import type { ScreenCaptureEventKind, ScreenCaptureScreen } from "@hereby/core/enums";
import type { AiNavigationAction } from "@hereby/core/schemas";
import {
  IMPERSONATION_ENDED_NOTICE,
  navigateAfterImpersonationExit,
  readImpersonationReturn,
  restoreImpersonationReturn,
  saveImpersonationReturn,
} from "@hereby/sdk";
import { navigateAdminGuidance } from "./ai/guidance-adapter";
import { observeAppAnalytics } from "./analytics";
/* ──────────────────────────────────────────────────────────
 * Admin App – Alpine.js component
 * Manages employees, teams, roles & permissions
 * ────────────────────────────────────────────────────────── */

import {
  type ApprovalReplacedDetail,
  type ApprovalStepsState,
  type ApprovalStepsStep,
  applyWardAssignmentChanges,
  askChoice,
  askConfirm,
  bindApprovalReplaceHost,
  createWardRow,
  decisionResultMessage,
  EMBED_RESET_SCROLL_MESSAGE,
  formatWorkLogMinutes,
  getIcon,
  handleDecisionError,
  hasWardAssignmentChanges,
  impersonationBannerState,
  lastSelectionKey,
  NURSE_WORK_TYPES,
  pickerMatches,
  pickerQuery,
  pickerRoles,
  printHtmlDocument,
  type RemovedWardRow,
  readLastSelection,
  registerEmployeeCardActions,
  registerEscLayer,
  removeWardRow,
  requestApprovalReplace,
  resetScrollPositions,
  type ScheduleGroupOption,
  scheduleGroupOptionsForRow,
  showAlert,
  showError,
  showPrompt,
  suggestWardId,
  syncRowScheduleGroup,
  syncRowWard,
  type WardOption,
  type WardRow,
  type WardSchedulerNote,
  wardOptionsForRow,
  wardRowBadge,
  wardRowsError,
  wardRowsFromAssignments,
  wardSchedulerHint,
  withOveruseConfirm,
  workLogAxisTicks,
  workLogBarStyle,
  workLogKindLabel,
  workLogRangeLabel,
  workLogStatusLabel,
  writeLastSelection,
} from "@hereby/components";
import type {
  ApprovalRowState,
  ApprovalStateLike,
  CertificateRequestFieldKey,
} from "@hereby/core/constants";
import {
  APPROVAL_LINE_TYPE_LABEL,
  APPROVAL_TYPE_CATALOG,
  type ApprovalLineType,
  type ApprovalVisibility,
  ATTENDANCE_DEFAULT_CHECK_IN,
  ATTENDANCE_DEFAULT_CHECK_OUT,
  allowModeOf,
  allowValueOf,
  applicableDayKeys,
  applySummary,
  applyTargets,
  approvalDecisionHint,
  approvalNextLabel,
  approvalOthersTurnMessage,
  approvalProgressLabel,
  approvalRowActionable,
  approvalRowState,
  approverIdsFromSelection,
  attachJosa,
  CERTIFICATE_REQUEST_FIELD_CATALOG,
  type ChangeLogView,
  candidatesToPickerEmployees,
  canEnterAdminFull,
  changeLogLine,
  compactTerminology,
  compareTeamOrder,
  computeWageHours,
  DEFAULT_CERTIFICATE_REQUEST_FIELDS,
  DEFAULT_LEAVE_REASON_PRESETS,
  DEFAULT_REQUEST_TIME_STEP_MINUTES,
  DOCUMENT_TEMPLATE_PRESETS,
  deriveMonthlyStandardHours,
  describeShiftSchedule,
  EMPLOYEE_HOME_NOTICE_KEEP_DAYS_DEFAULT,
  EMPLOYEE_HOME_NOTICE_KEEP_DAYS_MAX,
  EMPLOYEE_HOME_NOTICE_KEEP_DAYS_MIN,
  EMPLOYEE_HOME_NOTICE_LIMIT_DEFAULT,
  EMPLOYEE_HOME_NOTICE_LIMIT_MAX,
  EMPLOYEE_HOME_NOTICE_LIMIT_MIN,
  EMPLOYEE_HOME_SECTION_LABELS,
  type EmployeeHomeLayout,
  type EmployeeHomeLayoutConfig,
  type EntityPickerEmployee,
  type EntityPickerSelection,
  FEATURE_BUNDLE_GROUP_LABELS,
  FEATURE_PERMISSION_BUNDLES,
  type FeaturePermissionBundle,
  featureBundleById,
  filterOverviewRows,
  formatDayRange,
  HR_ACTION_CATALOG,
  hrActionKindByFormTemplateCode,
  IMPERSONATION_DEFAULT_TTL_MINUTES,
  IMPERSONATION_TTL_OPTIONS,
  isCertificateTemplateCode,
  isDepartmentTeam,
  JOB_CATEGORY_LABELS,
  LEAVE_REASON_PRESET_MAX,
  leaveCancelPlanLabel,
  monthlyHoursFromDaily,
  moveSibling,
  normalizeRoleOrgScope,
  type OverviewFilter,
  overviewAllowOptions,
  overviewAllowTip,
  overviewCellText,
  overviewTypes,
  parseApprovalLineEnabled,
  pickerSelectionOf,
  pickRoleScopeId,
  planLeaveCancellation,
  REQUEST_TIME_STEP_LABELS,
  REQUEST_TIME_STEP_OPTIONS,
  type RequestTimeStepMinutes,
  ROLE_SCOPE_UNSET,
  type RoleScopeOption,
  type RoleScopeOptionGroup,
  type RoleScopeTeam,
  resolveApprovalReminderHours,
  resolveApprovalVisibility,
  resolveEmployeeHomeLayout,
  resolveLeaveOveruseMode,
  resolveRequestTimeStepMinutes,
  resolveScreenCaptureProtection,
  resolveSimplifiedOvertimeType,
  roleDepartmentOptions,
  roleMatchesOrgFilter,
  roleScopeTeamLabel,
  roleTeamOptionGroups,
  SHIFT_ASSIGNMENT_SOURCE_LABELS,
  SHIFT_CODE_COLORS,
  SHIFT_RESOLUTION_SOURCE_LABELS,
  type ShiftScheduleSource,
  type ShiftScheduleText,
  type TeamApprovalContextView,
  type AllowMode as TeamLineAllowMode,
  type TeamOverviewRowView,
  type TeamOverviewView,
  WORK_PATTERN_DERIVED_FIELD_LABELS,
} from "@hereby/core/constants";
import { formatHoursHM, parseHoursHM } from "@hereby/core/date";
import type {
  ApprovalRequestType,
  EmployeeHomeSection,
  LeaveDeductionMode,
} from "@hereby/core/enums";
import {
  AnnualLeavePromotionSelectionMode,
  AttendanceRateRoundingMode,
  JobCategory,
  NotificationType,
  NurseWorkType,
} from "@hereby/core/enums";
import { getLocaleBundle } from "@hereby/core/locales";
import { fetchAllPages, type HerebyClient } from "@hereby/sdk";
import Alpine from "alpinejs";
import {
  annualLeavePromotionBatchStatusLabel,
  annualLeavePromotionPeriodKey,
  annualLeavePromotionRowAction,
  annualLeavePromotionStatusLabel,
  annualLeavePromotionTemplateEmptyNotice,
} from "./attendance/annual-leave-promotion";
import { resolveDatePresetRange } from "./attendance/date-preset";
import {
  attendanceRowRed,
  attnDateLabel,
  attnEditChanges,
  checkOutDayOffset,
} from "./attendance/display";
import {
  editHoursSum,
  expectedHoursFromTimes,
  scheduledSplitFromTimes,
} from "./attendance/edit-hours";
import { embedUrl } from "./attendance/embed-url";
import { coerceSelectBoolean } from "./attendance/forms";
import { monthRange, shiftMonth } from "./attendance/month-nav";
import {
  offsiteConfirmedByDate,
  offsiteRowSelectable,
  offsiteSelectAllIds,
  offsiteSelectedStep,
} from "./attendance/offsite";
import { checkoutGuess, matchSecomEvent } from "./attendance/secom-match";
import {
  buildDocTemplateRows,
  copyTemplateName,
  type DocTemplateRow,
  hasDuplicateTemplateName,
  newCertificateCode,
  type StoredDocTemplate,
} from "./doc-template-catalog";
import {
  type DocVariableGroup,
  unfilledPlaceholders,
  variableGroupsFor,
} from "./doc-template-variables";
import {
  employeeListStateKey,
  readEmployeeListState,
  writeEmployeeListState,
} from "./employee-filter-persist";

/** attendance.workLog 응답의 화면용 최소 형태 — 서버 타입과 구조적으로 호환(추가 필드는 무시). */
type WorkLogSegmentView = {
  kind: string;
  id: string;
  startMin: number | null;
  endMin: number | null;
  hours: number;
  counted: boolean;
  status: string;
  detail: string | null;
  isEarlyLeave?: boolean;
  isLate?: boolean;
  manualOverride?: boolean;
  breakHours?: number;
};
type WorkLogHoursView = {
  scheduled: number;
  overtime: number;
  offsite: number;
  total: number;
  pendingOvertime: number;
  pendingOffsite: number;
};
type WorkLogDayView = {
  date: string;
  weekday: number;
  isWeekend: boolean;
  isHoliday: boolean;
  shift: {
    id: string | null;
    name: string | null;
    scheduledStartMin: number | null;
    scheduledEndMin: number | null;
  } | null;
  leave: { name: string; code: string | null } | null;
  segments: WorkLogSegmentView[];
  hours: WorkLogHoursView;
  warnings: string[];
};
type WorkLogView = { days: WorkLogDayView[]; totals: WorkLogHoursView; timezone: string };

import { positionFloatingMenu as positionFloatingMenuUtil } from "@hereby/components";
import {
  ensureHolidayYears,
  getNationalHolidayName,
  invalidateHolidayYear,
} from "./data/kr-holidays";
import {
  appAdoptionFinishedKey,
  appStatusTooltip,
  currentOnboardingPhaseLabel,
  isAppAdoptionFinished,
  onboardingBannerKey,
  shouldShowOnboardingBanner,
  stageLabelClass,
} from "./onboarding";
import {
  applyBundle,
  applyCellScope,
  applyResourceMax,
  type BundleCheck,
  type BundleRequirementLike,
  buildPermissionMatrix,
  type CellScope,
  cellKey,
  cellMaxScope,
  cellScope,
  checkBundle,
  clearResource,
  diffAgainst,
  filterMatrix,
  groupMatrixResources,
  MATRIX_ACTIONS,
  type MatrixAction,
  type MatrixCell,
  type MatrixResource,
  type PermissionGroup,
  type PermissionMatrix,
  type PermissionRowLike,
  removeBundle,
  SCOPE_ORDER,
} from "./role-permission-matrix";
import {
  FOCUS_EVENT as SCHEDULED_CHANGES_FOCUS_EVENT,
  OPEN_SHEET_EVENT as SCHEDULED_CHANGES_OPEN_SHEET_EVENT,
} from "./scheduled-changes";
import { assignmentCellView, getAssignmentLabels, toDateStr } from "./scheduler";
import {
  AI_ASSISTANT_OPEN_EVENT,
  dateRange,
  entryCellValue,
  FLOATING_PANEL_OPEN_EVENT,
  filterRosterByName,
  nextTeamWardPop,
  TEAM_WARD_PANEL_ID,
  type TeamWardPopKind,
  type TeamWardRosterRow,
} from "./team-ward-preview";

/** 연차/휴가 결재 상세 팝오버의 떠 있는 패널 id — 팀 관리 병동 팝오버 · 채팅과 같은 자리라 서로 하나만 뜬다 */
const LEAVE_DETAIL_PANEL_ID = "admin:leave-detail";
/** 결재선 › 병원 기본 결재선 편집 팝오버의 떠 있는 패널 id */
const APPROVAL_TEMPLATE_PANEL_ID = "admin:approval-template";
/** 결재선 › 팀별 결재선 편집 팝오버의 떠 있는 패널 id */
const TEAM_LINE_PANEL_ID = "admin:team-approval-line";

import {
  applyOrgSettingsSearch,
  ORG_SETTINGS_FILTERED_EVENT,
  pickActiveSection,
} from "./org-settings-anchors";
import {
  readIncludeSubteams,
  rosterIncludeSubteamsKey,
  writeIncludeSubteams,
} from "./team-roster-prefs";
import {
  destroyDocumentEditor,
  getContent,
  highlightPlaceholders,
  initDocumentEditor,
  insertAtCursor,
  setEditorHeight,
} from "./utils/document-editor";
import { ESIGN_POLLING_INTERVAL_MS, shouldSkipESignPolling } from "./utils/esignPolling";
import { esignSyncCandidateIds } from "./utils/esignSync";
import { excelCellToDateStr } from "./utils/excel-date";

/** 산정 기준 변경 이력 한 행 — listBasisChanges 응답을 화면용으로 정규화한 형태 */
/** 팀 이동 모달의 대상 한 명(MED-912) */
interface TeamMoveTarget {
  id: string;
  name: string;
  employeeNumber: string;
  memberships: { teamId: string; teamName: string; roleName: string | null; isPrimary: boolean }[];
}

interface LeaveBasisChangeView {
  id: string;
  basis: "fiscal_year" | "hire_date";
  effectiveFrom: string;
  fiscalYearStart: string | null;
  newHireMode:
    | "prorate_in_hire_year"
    | "monthly_then_retroactive"
    | "monthly_then_prorate_at_anniversary"
    | null;
  note: string | null;
  createdAt: string;
  createdByName: string | null;
  cancelledAt: string | null;
  cancelledByName: string | null;
  appliedAt: string | null;
}

interface LeaveImportPreview {
  format: "hereby" | "ecount";
  totalRows: number;
  parseErrorCount: number;
  parseErrors: { row: number; message: string }[];
  warningRows: number;
  dateRange: { from: string; to: string } | null;
  leaveTypes: { label: string; count: number; suggestedLeaveTypeId: string | null }[];
  leaveReasons: { id: string; name: string; deductsAnnualBalance: boolean }[];
  employees: {
    matched: number;
    unknownCount: number;
    unknownSample: { employeeNumber: string; employeeName: string | null; rows: number }[];
  };
}

interface LeaveImportSummary {
  totalRows: number;
  ledgerRows: number;
  skippedRows: number;
  warningRows: number;
  employeesToCreate: number;
  errorCount: number;
  skippedErrorRows: number;
}

interface LeaveImportBatchView {
  id: string;
  createdAt: string | Date;
  fileName: string;
  format: string;
  rowCount: number;
  ledgerPostedCount: number;
  createdEmployeeCount: number;
  skippedErrorRows: number;
  status: "applied" | "reverted";
  uploadedByName: string | null;
}

/** 파일 → base64 (tRPC 는 JSON 이라 파일을 base64 로 보낸다). 12k행 CSV 도 2MB 안팎 */
/**
 * 요소 하나만 브라우저 전체 화면으로 — 이미 전체 화면이면 나온다. 상태는 fullscreenchange 로 onChange 에 알린다
 * (ESC · 브라우저 버튼으로 나가도 따라온다). 자격증 파일 보기 · 문서 양식 전체 화면이 쓴다(10/21).
 */
function toggleElementFullscreen(
  el: HTMLElement | undefined,
  onChange: (on: boolean) => void,
): void {
  if (document.fullscreenElement) {
    document.exitFullscreen().catch(() => {
      /* 이미 나온 상태 — 무시 */
    });
    return;
  }
  if (!el || typeof el.requestFullscreen !== "function") {
    return;
  }
  const handle = () => {
    const on = document.fullscreenElement === el;
    onChange(on);
    if (!on) {
      document.removeEventListener("fullscreenchange", handle);
    }
  };
  document.addEventListener("fullscreenchange", handle);
  el.requestFullscreen().catch(() => {
    // 브라우저 · 정책이 거부
    document.removeEventListener("fullscreenchange", handle);
    onChange(false);
  });
}

function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function normalizeLeaveBasisChange(c: {
  id: string;
  basis: string;
  effectiveFrom: string | Date;
  fiscalYearStart: string | null;
  newHireMode: string | null;
  note: string | null;
  createdAt: string | Date;
  createdByName: string | null;
  cancelledAt: string | Date | null;
  cancelledByName: string | null;
  appliedAt: string | Date | null;
}): LeaveBasisChangeView {
  const day = (v: string | Date | null): string | null =>
    v ? (typeof v === "string" ? v : v.toISOString()).slice(0, 10) : null;
  return {
    id: c.id,
    basis: c.basis === "fiscal_year" ? "fiscal_year" : "hire_date",
    effectiveFrom: day(c.effectiveFrom) ?? "",
    fiscalYearStart: c.fiscalYearStart,
    newHireMode:
      c.newHireMode === "monthly_then_retroactive" ||
      c.newHireMode === "monthly_then_prorate_at_anniversary"
        ? c.newHireMode
        : c.newHireMode
          ? "prorate_in_hire_year"
          : null,
    note: c.note,
    createdAt: day(c.createdAt) ?? "",
    createdByName: c.createdByName,
    cancelledAt: day(c.cancelledAt),
    cancelledByName: c.cancelledByName,
    appliedAt: day(c.appliedAt),
  };
}

/* ══════ 연차 정책 (org.settings.leavePolicy) ══════
   정본 스키마·기본값: packages/core/src/schemas/leave-policy.schema.ts
   (@hereby/core 루트 export는 zod/pino를 끌고 오므로 브라우저 번들에서는 import하지 않고
    값을 미러링한다 — 스키마의 default가 바뀌면 이 상수도 함께 고칠 것.)
   기본값은 전부 permissive = 플래그 도입 이전 동작 그대로다.

   UI는 플래그를 성격별로 셋으로 나눈다. 같은 체크박스로 늘어놓으면 전부
   "끄면 아무 일도 안 일어난다"로 읽히는데, 계산 방식 축은 OFF에도 다른 규칙이
   적용되므로 사실이 아니다.
     · CHOICES — OFF/ON 모두 규칙이다 → 양쪽 결과를 적은 2지선다 라디오
     · TOGGLES — OFF면 검사 자체를 안 한다 → 진짜 on/off 토글
     · PENDING — 아직 계산에 연결되지 않았다 → 비활성 + 안내 */
/** 페이지 크기 "전체" = 서버 paginationSchema 의 limit 상한. 이 위는 한 번에 보여주지 않는다. */
const PAGE_SIZE_ALL = 1000;
/** 「직원 배정」 미리보기 요청 순번 — 늦게 온 옛 응답이 새 결과를 덮지 않게. 반응형 상태에 두면 미리보기를 부르는
 *  x-effect 가 이 값을 읽고 써서 자기 자신을 끝없이 다시 부른다 — 그래서 컴포넌트 밖 모듈 변수 */
let wardAssignPreviewSeq = 0;
/** 「직원 배정」 검토 영역 자동 스크롤 — 같은 클릭에서 오는 여러 신호를 모아 한 번만(nudgeWardAssignReview) */
let wardAssignNudgeTimer: ReturnType<typeof setTimeout> | undefined;
/** 새로 생긴 검토 영역을 맞추는 중인지 — 미리보기 문장이 늦게 도착해 상자가 아래로 자라므로, 도착했을 때 한 번 더 맞춘다 */
let wardAssignNudgePending = false;
/**
 * 트리 연결선 — 평면 목록이라 CSS 만으로는 부모·형제 관계를 몰라 행마다 선 조각을 계산한다.
 * 좌표는 `.org-tree__item` 기준 px: 단계 들여쓰기 14 · 행 왼쪽 여백 22 · 셰브론 12 · 가로선 높이 14.
 * 단계 0 세로선은 맨 위 행(조직 · 「전체 병동」) 아이콘 아래(8), 그 아래 단계는 부모 셰브론 가운데를 지난다.
 * 팀 관리 트리(여러 단계)와 병동 관리 목록(한 단계)이 같이 쓴다 — 두 화면의 선이 어긋나지 않게 한 곳에 둔다.
 */
function treeLineColumn(level: number): number {
  return level === 0 ? 8 : level * 14 + 14;
}

/**
 * 한 행이 그릴 선 조각들.
 * @param guides 윗단계마다 「아직 형제가 남았는지」 — 남았으면 그 단계 세로선이 이 행을 지나간다
 * @param hasChildren 셰브론이 있는 행인지 — 없으면 가로선이 빈 셰브론 자리를 지나 아이콘 앞까지 간다
 */
function treeLineStyles(
  depth: number,
  guides: boolean[],
  isFirst: boolean,
  isLast: boolean,
  hasChildren: boolean,
): Record<string, string>[] {
  const lines: Record<string, string>[] = [];
  for (const [level, continues] of guides.entries()) {
    if (continues) {
      lines.push({
        left: `${treeLineColumn(level)}px`,
        top: "0px",
        bottom: "-2px",
        width: "1px",
      });
    }
  }
  const left = `${treeLineColumn(depth)}px`;
  // 자기 세로선 — 첫 하위 행은 부모 셰브론 바로 아래부터, 마지막 형제는 가로선에서 멈춘다
  const top = isFirst && depth > 0 ? -10 : 0;
  lines.push(
    isLast
      ? { left, top: `${top}px`, height: `${14 - top}px`, width: "1px" }
      : { left, top: `${top}px`, bottom: "-2px", width: "1px" },
  );
  const end = depth * 14 + 22 + (hasChildren ? 0 : 14);
  lines.push({
    left,
    top: "14px",
    width: `${end - treeLineColumn(depth)}px`,
    height: "1px",
  });
  return lines;
}

/** 배정 해제 되돌리기 줄을 거두는 타이머 — 반응형 상태에 두면 x-effect 가 매 틱 다시 돈다 */
let wardUndoTimer: ReturnType<typeof setTimeout> | undefined;
/** 건너뛴 뒤 줄 강조를 걷는 타이머 — 반응형 상태에 두면 x-effect 가 매 틱 다시 돈다 */
let focusEmployeeTimer: ReturnType<typeof setTimeout> | undefined;
/** 강조한 줄로 스크롤하는 타이머 — 목록이 다시 그려진 뒤에 한 번만 움직인다 */
let focusScrollTimer: ReturnType<typeof setTimeout> | undefined;
/** 병동 관리 표 안 수정 폼의 빈 값 — 숫자 칸(병상 수 · 순서)은 입력 그대로 문자열로 들고 저장할 때 검사한다 */
/** 스케줄러(스케줄 그룹) — nurseScheduler.listScheduleGroups 항목. 병동 관리 표 · 팀 관리 · 직원 팝업의 병동 배정 줄이 같은 목록을 쓴다 */
type ScheduleGroupItem = ScheduleGroupOption & {
  wardName: string;
  code: string | null;
  location: string | null;
  description: string | null;
  displayOrder: number;
  jobCategories: JobCategory[];
};

/** 스케줄러 추가/수정 폼 — 숫자 칸은 입력 그대로 문자열. 배열이 들어 있어 복사마다 새로 만든다(spread 는 배열을 공유한다) */
function emptySchedulerForm() {
  return {
    id: "",
    wardId: "",
    name: "",
    code: "",
    location: "",
    description: "",
    displayOrder: "",
    isActive: true,
    isDefault: false,
    jobCategories: [] as string[],
  };
}

const EMPTY_WARD_INLINE_FORM = {
  code: "",
  name: "",
  description: "",
  linkedTeamId: "",
  wardCareType: "",
  bedCount: "",
  displayOrder: "",
  isActive: true,
};
/** DS 규칙(pagination.md #page-size)대로 네 개 고정 — 목록마다 다른 조합을 만들지 않는다. */
const PAGE_SIZE_OPTIONS: { value: number; label: string }[] = [
  { value: 30, label: "30명씩" },
  { value: 50, label: "50명씩" },
  { value: 100, label: "100명씩" },
  { value: PAGE_SIZE_ALL, label: "전체" },
];
/** 같은 네 개 — 사람이 아니라 건(결재 · 신청 · 요청)을 세는 목록용 라벨(근태 현황 결재 · 외근 · 정정 요청). */
const PAGE_SIZE_COUNT_OPTIONS: { value: number; label: string }[] = PAGE_SIZE_OPTIONS.map((o) => ({
  value: o.value,
  label: o.value >= PAGE_SIZE_ALL ? o.label : `${o.value}건씩`,
}));
/** 목록별로 기억해 둔 페이지 크기 — 없거나 옵션 밖이면 기본 30. */
/**
 * 팀 관리 명단(team.roster) 호출 합치기 — 변경 뒤 여러 곳이 같은 틱에 `loadUnassignedMembers()` 와
 * `refreshOrgTeamCompositions()` 를 함께 부르므로 한 요청으로 묶는다. 이미 **보내진** 요청에는 끼지 않고
 * 끝난 뒤 한 번 더 읽는다 — 변경 직후 부른 쪽이 변경 전 응답을 받지 않게. Alpine 데이터에 Promise 를 두면
 * 프록시로 감싸져 `then` 이 깨지므로 모듈 변수로 둔다.
 */
let rosterPending: Promise<void> | null = null;
let rosterInflight: Promise<void> | null = null;
let rosterQueued: Promise<void> | null = null;

function readPageSize(tab: string): number {
  try {
    const raw = Number(localStorage.getItem(`admin:pageSize:${tab}`));
    return PAGE_SIZE_OPTIONS.some((o) => o.value === raw) ? raw : 30;
  } catch {
    return 30;
  }
}

const LEAVE_POLICY_DEFAULTS: Record<string, boolean | string> = {
  // 초과 사용 처리(MED-968) — allow(경고만) · block(차단) · approver_confirm(결재자 확인 뒤 허용).
  // enforceBalanceLimit · allowNegativeBalance 는 저장 때 이 값에서 파생해 하위호환으로 함께 쓴다
  overuseMode: "allow",
  carryNegativeBalance: true,
  enforceBalanceLimit: false,
  blockOverlappingRequests: false,
  excludeWeekendsHolidays: false,
  applyLeaveTypeDeduction: false,
  deductNonAnnualFromBalance: true,
  enableHourlyLeave: false,
  allowNegativeBalance: true,
  shiftHolidayByDuty: false,
};

const LEAVE_POLICY_CHOICES: {
  key: string;
  title: string;
  /** 이 선택을 발효시키는 상위 플래그 — 꺼져 있으면 안내를 띄운다 */
  requires?: string;
  requiresText?: string;
  options: { value: boolean | string; label: string; desc: string }[];
}[] = [
  {
    key: "overuseMode",
    title: "남은 연차보다 많이 신청하면?",
    options: [
      {
        value: "allow",
        label: "경고만 하고 허용",
        desc: "신청서에 「남은 연차보다 N일 많아요」 경고만 보이고 접수·승인돼요. 잔여가 마이너스가 될 수 있어요.",
      },
      {
        value: "approver_confirm",
        label: "결재자가 확인한 뒤 허용",
        desc: "접수는 되지만 승인할 때 결재자에게 초과분이 보이고 「초과 승인」을 눌러야 승인돼요.",
      },
      {
        value: "block",
        label: "차단",
        desc: "남은 연차보다 많으면 접수도 승인도 막아요.",
      },
    ],
  },
  {
    key: "excludeWeekendsHolidays",
    title: "연차를 며칠로 계산할까요?",
    options: [
      {
        value: false,
        label: "신청 기간의 달력일 전부",
        desc: "주말·공휴일이 끼어 있어도 그대로 차감해요. 4인 이하 사업장만 선택할 수 있어요.",
      },
      {
        value: true,
        label: "실제 근무일만",
        desc: "근무 유형의 적용 요일이 아닌 날과 공휴일은 빼요. 교대(듀티) 근무자도 공휴일은 빼요.",
      },
    ],
  },
  {
    key: "applyLeaveTypeDeduction",
    title: "휴가 유형마다 차감량을 다르게 할까요?",
    options: [
      {
        value: false,
        label: "모든 유형을 하루 1일로",
        desc: "「휴가/연차 유형」에 배수를 설정해도 반영되지 않아요.",
      },
      {
        value: true,
        label: "유형에 설정한 배수대로",
        desc: "「휴가/연차 유형」의 차감 배수(종일 1 · 반차 0.5 · 반반차 0.25)를 적용해요.",
      },
    ],
  },
  {
    key: "deductNonAnnualFromBalance",
    title: "병가·경조사도 연차 잔여에서 깎을까요?",
    requires: "applyLeaveTypeDeduction",
    requiresText:
      "위 「휴가 유형마다 차감량을 다르게 할까요?」에서 '유형에 설정한 배수대로'를 골라야 이 선택이 적용돼요.",
    options: [
      {
        value: true,
        label: "전부 연차에서 차감",
        desc: "휴가 종류와 상관없이 연차 잔여가 줄어요.",
      },
      {
        value: false,
        label: "유형별 설정을 따름",
        desc: "「휴가/연차 유형」의 '연차 잔여 차감' 값이 그대로 적용돼요.",
      },
    ],
  },
];

const LEAVE_POLICY_TOGGLES: { key: string; label: string; desc: string }[] = [
  {
    key: "carryNegativeBalance",
    label: "마이너스 잔여를 다음 발생 때 자동 상계",
    desc: "지난 기간에 더 쓴 연차만큼 새 기간 연차에서 빼요(20 발생 − 5 초과 = 15). 연차 대장에 「지난 기간 초과 사용 상계」로 남고 직원에게 알려요.",
  },
  {
    key: "blockOverlappingRequests",
    label: "기간 중복 신청 차단",
    desc: "같은 직원이 이미 신청·승인된 기간과 겹치게 신청하면 막아요.",
  },
];

const LEAVE_POLICY_PENDING: { key: string; label: string; desc: string }[] = [
  // enableHourlyLeave는 여기서 뺐다 — 시간 단위 유형을 저장하면 확인 팝업을 거쳐 자동으로
  // 켜지고(saveLeaveReason), 차감은 신청 시간을 항상 따르므로 수동 토글이 필요 없다.
  {
    key: "shiftHolidayByDuty",
    label: "교대 근무 휴일을 듀티 기준으로",
    desc: "3교대의 주말·오프 판정을 근무표 배정일 기준으로 해요. 공휴일은 이미 항상 차감에서 빠져요.",
  },
];

/** 시프트 목록 항목 — SDK(tRPC) 응답에서 추론. 서버 출력이 바뀌면 컴파일이 잡는다. */
type ShiftItem = Awaited<ReturnType<HerebyClient["shift"]["list"]>>["items"][number];
type ShiftGroupItem = Awaited<ReturnType<HerebyClient["shiftGroup"]["list"]>>["items"][number];
type JobDutyItem = Awaited<ReturnType<HerebyClient["jobDuty"]["list"]>>["items"][number];
type WorkPatternItem = Awaited<ReturnType<HerebyClient["workPattern"]["list"]>>["items"][number];
type JobDutyReapplyItem = Awaited<
  ReturnType<HerebyClient["jobDuty"]["reapplyDefaultShifts"]>
>["items"][number];
type WorkPatternImportItem = Awaited<
  ReturnType<HerebyClient["workPattern"]["importFromShiftGroups"]>
>["items"][number];
type WorkPatternRecomputeItem = Awaited<
  ReturnType<HerebyClient["workPattern"]["recompute"]>
>["items"][number];
type RoleItem = Awaited<ReturnType<HerebyClient["role"]["list"]>>["items"][number];
/** 병원 설정 「결재선」 카드 — 정책 · 종류별 템플릿 · 종류별 후보(결재선 MED-900 5단계) */
type ApprovalTemplateContext = Awaited<ReturnType<HerebyClient["approvalFlow"]["templates"]>>;
/** 병동 관리 「직원 배정」 미리보기 — 서버가 실행과 같은 규칙으로 계산한 직원별 결과(nurseScheduler.ward.assignPreview) */
type WardAssignPreview = Awaited<ReturnType<HerebyClient["nurseScheduler"]["previewWardAssign"]>>;
/** 필터 없는 전체 팀 목록(allTeams) 한 줄 — 역할 부서·팀 규칙이 쓰는 모양 + 연결 병동 · 상태 */
type OrgTeamItem = RoleScopeTeam & {
  wardId?: string | null;
  isActive?: boolean;
  /** 팀 대표 근무(옵셔널) — 직원 등록 시 기본 선택되는 근무 그룹 > 근무형태 */
  shiftGroupId?: string | null;
  shiftId?: string | null;
};
type EntitlementRuleItem = Awaited<
  ReturnType<HerebyClient["leaveRequest"]["listEntitlementRules"]>
>["items"][number];
type LeaveBalanceDetailInfo = Awaited<
  ReturnType<HerebyClient["leaveRequest"]["balanceRuleDetail"]>
>;
type LeaveAdjustmentItem = Awaited<
  ReturnType<HerebyClient["leaveRequest"]["listBalanceAdjustments"]>
>["items"][number];
type ESignHealthViewState = {
  status?: unknown;
  label?: string;
  lastCheckedAt?: unknown;
  lastReceivedAt?: unknown;
  lastSucceededAt?: unknown;
  lastFailedAt?: unknown;
  lastErrorMessage?: unknown;
};
type ESignIntegrationHealthState = {
  signApi: ESignHealthViewState;
  webhook: ESignHealthViewState;
};
type ESignIntegrationStatusState = {
  status: "unlinked" | "provisioning" | "linked" | "error" | "revoked";
  ownerEmailMasked: string | null;
  linkedAt: string | Date | null;
  maskedApiKey: string | null;
  errorMessage: string | null;
  /** Hereby Sign 템플릿 관리 화면(새 창). 연동 전이거나 주소를 알 수 없으면 null → 버튼 숨김. */
  templateManageUrl?: string | null;
};

/** 문서 양식 팝업 폼 — docTemplateForm 과 같은 모양 */
interface DocTemplateFormState {
  code: string;
  isActive: boolean;
  name: string;
  description: string;
  bodyTemplate: string;
  cssTemplate: string;
  attachmentRequired: boolean;
  requestFields: {
    key: string;
    label: string;
    use: boolean;
    required: boolean;
    alwaysOn: boolean;
  }[];
}

export function adminAppComponent() {
  // 빠르게 탭을 바꿔도 이전 요청이 새 화면의 로딩 표시·선택 복원을 끝내지 않게 한다.
  let dataLoadVersion = 0;
  let employeeLoadVersion = 0;
  return {
    /* ── state ── */
    /* Shared save-confirm for data-entry (form) modals. attemptClose("showXModal")
       opens the confirm; confirmClose() (닫기) closes the target modal,
       dismissClose() (계속편집) cancels. A form's X / Esc / 취소 all route here so
       closing never silently loses typed input. Mirrors team-manager app.ts.
       Target may also name a closer method (e.g. "closeDocTemplateForm") for
       modals that need teardown beyond flipping a state var. */
    closeConfirm: { open: false, target: "" } as { open: boolean; target: string },
    /* 유형·기준 통합 관리 팝업(type-manager) 자식 폼이 닫힌 시각(ms).
       ESC 연타/키 리피트 시 첫 ESC가 자식 폼을 닫은 직후 두 번째 ESC가 컨테이너까지
       닫아버리는 문제를 막기 위해, 자식이 막 닫힌 직후 짧은 구간 동안 컨테이너 ESC를 무시한다. */
    tmChildClosedAt: 0,
    /* Pristine-close guard: a baseline of each modal's form state is captured
       when it opens ($watch in init). While the form is unchanged, X/취소/Esc
       close directly — the save-confirm only appears once something was typed.
       문서 양식은 별도의 정규화된 본문 기준값으로 변경 여부를 판단한다. */
    closeGuardForms: {
      showAddMember: ["addMemberSelectedIds"],
      showWardAssign: ["wardAssign"],
      showEditRole: ["roleForm", "rolePermissionIds"],
      showCertModal: ["certForm", "certFieldVals"],
      showShiftGroupModal: ["shiftGroupForm"],
      // 「대표로 쓰는 팀」·「기본으로 쓰는 직무」 칩은 shiftTypeForm 밖(팀·직무 쪽에 저장)이라 따로 넣는다 —
      // 없으면 칩만 바꿨을 때 저장 버튼이 비활성으로 남고 닫기 확인도 뜨지 않는다(2026-09-21 실측).
      showShiftTypeModal: ["shiftTypeForm", "typeFormTeamIds", "typeFormJobDutyIds"],
      showJobDutyModal: ["jobDutyForm"],
      showWorkPatternModal: ["workPatternForm"],
      showOffsiteTypeModal: ["offsiteTypeForm"],
      showOvertimeTypeModal: ["overtimeTypeForm"],
      showLeaveReasonModal: ["leaveReasonForm"],
      showEditRule: ["ruleForm"],
      showLeaveBasisChange: ["leaveBasisForm"],
      showLeaveShiftAssign: ["leaveShiftAssignForm"],
      showGradeModal: ["gradeForm"],
      showEditTeam: ["teamForm"],
      showOrgLeaders: ["orgLeaderForm"],
      showItemCodeModal: ["itemCodeForm"],
      orgParameterShowForm: ["orgParameterForm"],
      wardShowForm: ["wardForm"],
      schedulerShowForm: ["schedulerForm"],
      showESignSendModal: ["esignForm"],
    } as Record<string, string[]>,
    closeGuardBaselines: {} as Record<string, string>,
    closeGuardState(target: string): string {
      const keys: string[] = this.closeGuardForms[target] ?? [];
      return JSON.stringify(keys.map((k: string) => (this as Record<string, unknown>)[k]));
    },
    /**
     * closeGuard baseline(폼이 열린 시점 상태) 대비 변경 여부.
     * 생성 모드는 빈 폼이 baseline이라 입력 시 true, 수정 모드는 원본과 달라야 true.
     * 변경이 없으면 저장 버튼을 비활성화하는 데 사용한다. (병동/역할 폼 공용)
     */
    formDirty(target: string): boolean {
      const baseline = this.closeGuardBaselines[target];
      return baseline !== undefined && baseline !== this.closeGuardState(target);
    },
    attemptClose(stateVar: string) {
      // 소스 보기를 포함한 최신 본문을 반영한 뒤, 열 때의 정규화된 HTML과 비교한다.
      if (stateVar === "closeDocTemplateForm") {
        this.syncFromEditor();
        if (!this.docTemplateDirty()) {
          this.performClose(stateVar);
          return;
        }
        this.closeConfirm = { open: true, target: stateVar };
        return;
      }
      const baseline = this.closeGuardBaselines[stateVar];
      if (baseline !== undefined && baseline === this.closeGuardState(stateVar)) {
        this.performClose(stateVar);
        return;
      }
      this.closeConfirm = { open: true, target: stateVar };
    },
    performClose(target: string) {
      const member = (this as Record<string, unknown>)[target];
      if (typeof member === "function") {
        (member as () => void).call(this);
      } else {
        (this as Record<string, unknown>)[target] = false;
      }
    },
    confirmClose() {
      const target = this.closeConfirm.target;
      this.closeConfirm = { open: false, target: "" };
      if (target) {
        this.performClose(target);
      }
    },
    dismissClose() {
      this.closeConfirm = { open: false, target: "" };
    },

    dataReady: false,
    loadingData: false,
    accessDenied: false,
    sdk: Alpine.store("hereby").sdk,

    activeTab: "employees",
    get tabs() {
      const t = (key: string) => Alpine.store("hereby").t(key) || key;
      return [
        { id: "employees", label: t("admin.tab_employees"), icon: "ic-employee" },
        { id: "organization", label: "팀 관리", icon: "ic-organization" },
        { id: "roles", label: t("admin.tab_roles"), icon: "ic-id-card" },
        { id: "wards", label: t("admin.tab_wards"), icon: "ic-hospital" },
        { id: "attendance", label: "근태 현황", icon: "ic-clock" },
        { id: "leave", label: "연차 관리", icon: "ic-calendar-check" },
        { id: "offsite", label: "외근 관리", icon: "ic-map-pin" },
        { id: "esign", label: t("admin.tab_esign"), icon: "ic-signature" },
        { id: "payroll", label: "급여관리", icon: "ic-money" },
        { id: "scheduler", label: "스케줄러", icon: "ic-calendar-clock" },
        { id: "parameters", label: "글로벌 파라미터", icon: "ic-setting" },
        { id: "onboarding", label: "직원 앱 도입", icon: "ic-list-check" },
        { id: "scheduled-changes", label: "예약된 변경", icon: "ic-calendar-clock" },
        { id: "chat-recovery", label: "대화 복구", icon: "ic-shield-check" },
        { id: "settings", label: t("admin.tab_settings"), icon: "ic-setting" },
        { id: "embed", label: "임베드", icon: "" },
      ];
    },

    /* PageHeader bindings: shared title bar reads these for the current native tab.
       Embed tab provides its own title/icon via embedTitleFor/embedIconFor. */
    get activeTabLabel(): string {
      if (this.activeTab === "documents") {
        return "문서 결재";
      }
      // 도입이 끝난 뒤의 「직원 앱 도입」은 설정 페이지의 한 탭(내 정보 옆)으로 보인다 — 헤더도 「설정」
      if (this.activeTab === "onboarding" && this.appAdoptionFinished) {
        return "설정";
      }
      if (this.activeTab === "settings") {
        // 조직 설정·내 정보는 '설정' 한 페이지의 2탭 — 탭을 옮겨도 글로벌 헤더
        // 타이틀은 항상 '설정'으로 고정한다(탭 바가 조직 설정/내 정보를 표기).
        if (this.settingsSubTab === "org" || this.settingsSubTab === "myinfo") {
          return "설정";
        }
        return (
          this.settingsGroupLabels[this.settingsSubTab] ||
          this.settingsSubLabels[this.settingsSubTab] ||
          "설정"
        );
      }
      const tab = this.tabs.find((t: { id: string }) => t.id === this.activeTab);
      return tab?.label || "";
    },
    get activeTabIcon(): string {
      if (this.activeTab === "documents") {
        return "ic-clipboard";
      }
      if (this.activeTab === "onboarding" && this.appAdoptionFinished) {
        return "ic-setting";
      }
      // 설정 서브탭은 LNB 항목 아이콘과 동일하게 (헤더-LNB 아이콘 통일)
      if (this.activeTab === "settings") {
        // org·myinfo는 글로벌 헤더가 '설정'이라 아이콘도 ic-setting(기본값)으로 통일.
        const subIcons: Record<string, string> = {
          shifts: "ic-sliders",
          jobDuties: "ic-id-card",
          workPatterns: "ic-sliders",
          docTemplates: "ic-file-text",
          approvalLines: "ic-tree",
        };
        return subIcons[this.settingsSubTab] || "ic-setting";
      }
      const tab = this.tabs.find((t: { id: string }) => t.id === this.activeTab);
      return tab?.icon || "";
    },

    positionFloatingMenu(
      triggerEl: HTMLElement | null | undefined,
      menuEl: HTMLElement | null | undefined,
    ) {
      positionFloatingMenuUtil(triggerEl, menuEl);
    },

    /** 표 맨 오른쪽 「관리」 열의 ⋯ 메뉴 — 위아래는 공용 배치를 쓰고, 좌우만 버튼 오른쪽 끝에 맞춘다.
     *  공용 배치는 메뉴 왼쪽을 버튼 왼쪽에 맞추는데, 오른쪽 끝 열에서는 그러면 메뉴가 화면 밖으로 나간다. */
    positionRowMenu(
      triggerEl: HTMLElement | null | undefined,
      menuEl: HTMLElement | null | undefined,
    ) {
      if (!(triggerEl && menuEl)) {
        return;
      }
      positionFloatingMenuUtil(triggerEl, menuEl);
      const right = triggerEl.getBoundingClientRect().right;
      menuEl.style.left = `${Math.max(8, right - menuEl.offsetWidth)}px`;
    },

    /** 병동 페이지 관리자로 들어왔는가 — 시스템 관리자가 아닌데 ward:manage 만 가진 경우(PM-01) */
    isWardOnlyAdmin: false,
    /** 조직 시스템 관리자 계정인가 — 대화 복구 메뉴는 이 계정에만 그린다 */
    get isSystemAdminUser(): boolean {
      const user = Alpine.store("hereby").currentUser as { isSystemAdmin?: boolean } | null;
      return Boolean(user?.isSystemAdmin);
    },
    /** 병동 관리자가 쓸 수 있는 탭 — 이 밖으로는 가지 못한다 */
    wardAdminTabs: ["wards", "scheduler"] as string[],
    /**
     * 결재자로 들어왔는가 — 시스템 관리자가 아닌데 결재 승인 권한(휴가·초과근무·외근·인센티브, 팀 이상)이
     * 있는 경우(2026-10-06 사용자 결정: 결재자도 admin 만 쓴다). LNB 에는 결재 메뉴만 그린다.
     */
    isApproverAdmin: false,
    /** 결재선(MED-900) — 내가 결재자인 대기 건수(종류별). LNB 결재 메뉴 배지 · 결재자 제한 입장의 첫 화면 */
    approvalInbox: { total: 0, byType: {} as Record<string, number> },
    /** 상세 모달의 결재선 패널 데이터(approvalFlow.steps) — 연차·외근 상세가 같은 패널을 쓴다 */
    detailApproval: null as ApprovalStepsState | null,
    detailApprovalLoading: false,
    /** 지금 열린 상세의 문서 — 결재자 바꾸기 모달이 어느 문서인지 안다 */
    detailApprovalRef: null as { requestType: "leave" | "offsite_work"; requestId: string } | null,
    /** 결재자 바꾸기 모달이 열려 있나 — 상세 모달의 ESC 가드(모달 위 모달). hereby:approval-replace-state 로 받는다 */
    approvalReplaceOpen: false,
    approvalReplaceBound: false,
    /** 승인·반려 요청 중인 문서 id — 응답 전 재클릭 무시 */
    approvalBusyIds: [] as string[],
    /** 결재자가 열 수 있는 결재 화면(embed 키) — 이 밖으로는 가지 못한다 */
    approverEmbeds: [] as string[],

    /** 병동 관리자·결재자처럼 쓸 수 있는 화면만 보이는 입장인가 */
    get isRestrictedAdmin(): boolean {
      return this.isWardOnlyAdmin || this.isApproverAdmin;
    },

    /** 제한 입장에서 갈 수 있는 탭 */
    get restrictedAdminTabs(): string[] {
      return [
        ...(this.isWardOnlyAdmin ? this.wardAdminTabs : []),
        ...(this.isApproverAdmin ? ["embed"] : []),
      ];
    },

    /**
     * 결재자 메뉴 — 서버 teamApproverProcedure 와 같은 기준(그 종류의 승인 권한이 팀 이상 범위).
     * 「관리」 권한은 서버가 승인으로 풀어 내려 주므로 `overtime:approve.all` 로 들어온다.
     * 문서 결재는 아직 팀 범위 승인 권한이 없어 결재 권한이 하나라도 있으면 함께 보인다(지금 규칙 그대로).
     */
    approverMenuItems(): { id: string; label: string; icon: string; badge?: number }[] {
      const raw = Alpine.store("hereby").currentUser?.permissions;
      const perms: string[] = Array.isArray(raw) ? raw : [];
      const can = (resource: string) =>
        perms.includes(`${resource}:approve.team`) || perms.includes(`${resource}:approve.all`);
      // 결재선(MED-900) 결재함 — 내가 현 단계 결재자인 대기 건수를 메뉴 배지로
      const badge = (requestType: string): number | undefined => {
        const n = this.approvalInbox.byType[requestType] ?? 0;
        return n > 0 ? n : undefined;
      };
      const items: { id: string; label: string; icon: string; badge?: number }[] = [];
      if (can("leave")) {
        items.push({
          id: "embed:leave",
          label: "연차/휴가 결재",
          icon: "ic-clipboard-check",
          badge: badge("leave"),
        });
      }
      if (can("overtime")) {
        items.push({
          id: "embed:overtime-approval",
          label: "연장근무 결재",
          icon: "ic-clipboard-check",
          badge: badge("overtime"),
        });
      }
      if (can("offsiteWork")) {
        items.push({
          id: "embed:offsite-approval",
          label: "외근 결재",
          icon: "ic-clipboard",
          badge: badge("offsite_work"),
        });
      }
      if (can("incentive")) {
        items.push({
          id: "embed:incentive",
          label: "인센티브",
          icon: "ic-money",
          badge: badge("incentive"),
        });
      }
      if (items.length > 0) {
        items.push({ id: "embed:doc-mgmt", label: "문서 결재", icon: "ic-clipboard" });
      }
      return items;
    },

    /** 제한 입장에서 막혔을 때 안내 — 어떤 권한으로 들어왔는지 알려 준다 */
    restrictedAdminNotice(): string {
      if (this.isWardOnlyAdmin && this.isApproverAdmin) {
        return "권한이 있는 병동 관리·결재 메뉴만 사용할 수 있어요.";
      }
      return this.isApproverAdmin
        ? "결재 권한으로 들어왔어요. 결재 메뉴만 사용할 수 있어요."
        : "병동 관리 권한으로 들어왔어요. 병동 관리만 사용할 수 있어요.";
    },

    /* ── LNB menu config (consumed by the shared lnbSidebar component) ──
       Composite ids:
         - "settings:<sub>" → switch to settings tab + sub-tab
         - "embed:<key>"    → switch to embed tab + load registry entry
         - plain ids        → switch to that tab directly                       */
    get lnbConfig() {
      // 병동 페이지 관리자는 병동 관리와 근무표만 쓴다 — 나머지 메뉴는 아예 그리지 않는다.
      // (막힌 메뉴를 보여 주고 누르면 거절하는 대신, 쓸 수 있는 것만 보여 준다 — PM-01)
      // 결재자도 같은 방식 — 승인 권한이 있는 결재 화면만(2026-10-06).
      if (this.isRestrictedAdmin) {
        const sections: { label: string; items: { id: string; label: string; icon: string }[] }[] =
          [];
        if (this.isApproverAdmin) {
          sections.push({ label: "결재", items: this.approverMenuItems() });
        }
        if (this.isWardOnlyAdmin) {
          sections.push({
            label: "병동",
            items: [
              { id: "wards", label: "병동 관리", icon: "ic-hospital" },
              { id: "scheduler", label: "스케줄러", icon: "ic-calendar-clock" },
            ],
          });
        }
        return { sections };
      }
      return {
        sections: [
          // 도입이 끝나면(목표 달성·완료 처리) 이 섹션은 빠지고 설정 › 「직원 앱 도입」 탭에서 다시 본다
          ...(this.appAdoptionFinished
            ? []
            : [
                {
                  // 직원 앱 배포 온보딩(MED-795) — 안내 발송·설치/로그인 현황·재안내를 한 화면의 체크리스트로.
                  // 도입 초기에 가장 먼저 할 일이라 인사 관리에서 떼어 맨 위 섹션으로 둔다.
                  // 이름은 「온보딩」·「배포」를 쓰지 않는다 — 인사 시스템에서 온보딩은 신규 입사 절차로 읽히고,
                  // 배포는 개발자 말이다. 관리자가 하는 일은 직원들이 직원 앱을 쓰기 시작하게 만드는 것(=도입).
                  label: "시작하기",
                  items: [{ id: "onboarding", label: "직원 앱 도입", icon: "ic-list-check" }],
                },
              ]),
          {
            label: "인사 관리",
            items: [
              { id: "organization", label: "팀 관리", icon: "ic-organization" },
              // 「역할」은 병원마다 다르게 부른다(설정 › 조직 설정 › 용어 표시). lnbSidebar 가 config 를 init 때
              // 스냅샷하므로 낱말을 바꾼 뒤 메뉴 라벨은 새로고침 후에 반영된다(표·안내문은 즉시).
              {
                id: "roles",
                label: `${Alpine.store("hereby").term("role")} 관리`,
                icon: "ic-id-card",
              },
              { id: "wards", label: "병동 관리", icon: "ic-hospital" },
              { id: "employees", label: "직원 관리", icon: "ic-employee" },
              // 결재선(MED-900 5단계) — 병원 정책(직접 고르기 · 역할 제한)과 종류별 기본 결재선. 설정 서브탭을
              // 문서 양식처럼 LNB 항목으로 노출한다(사용자 결정 10/7: 조직 설정 카드가 아니라 「인사 관리」 메뉴).
              { id: "settings:approvalLines", label: "결재선", icon: "ic-tree" },
              {
                // 문서 관리 → 문서 결재로 명칭 변경 (LNB 개편 시안)
                label: "문서 결재",
                icon: "ic-approval",
                children: [
                  // 문서 결재(team-manager iframe) + 자격증/면허(admin native)을 한 페이지 두 서브탭으로 통합
                  {
                    id: "documents",
                    label: "문서 결재",
                    icon: "ic-clipboard",
                    badge: this.docApprovalPending > 0 ? this.docApprovalPending : undefined,
                  },
                  {
                    // 문서 설정 → 문서 양식으로 명칭 변경 (LNB 개편 시안)
                    id: "settings:docTemplates",
                    label: "문서 양식",
                    icon: "ic-file-text",
                  },
                ],
              },
            ],
          },
          {
            // 급여 개편 작업 중 — 시트 외 메뉴(급여대장·인센티브·급여관리 설정)는
            // 시트 기반으로 대체 예정이라 LNB에서 제거 (WIP 라벨 단계를 거쳐 확정).
            // 페이지·embed 레지스트리·설정 서브탭은 현행 유지.
            label: "급여 관리",
            items: [
              { id: "embed:sheets", label: "시트", icon: "ic-table" },
              // 예약된 변경(MED-827) — 계약 시행일 변경·시트 칸 예약과 이 달 시행 원천 변경을 한 곳에서
              { id: "scheduled-changes", label: "예약된 변경", icon: "ic-calendar-clock" },
            ],
          },
          {
            label: "근태 관리",
            // 1-depth 재편(근태 현황 구조 변경): 스케줄러 / 근태 현황(상단 탭 4개) 2개로 단순화.
            //  - 기존 근태 현황(team-manager 홈 iframe)·연차 관리·연장근무 현황/결재·외근 관리는
            //    근태 현황 페이지의 상단 탭(근무/연차/연장근무/외근근무 현황)으로 흡수.
            //  - 유형·기준 설정(근무 설정/연장근무·외근 유형/연차 부여 기준/휴가 유형)은
            //    '근태 설정' 패널(type-manager)로 모으고, 진입은 근태 현황 nav 우측 「근태 설정」 버튼.
            items: [
              { id: "scheduler", label: "스케줄러", icon: "ic-calendar-clock" },
              { id: "attendance", label: "근태 현황", icon: "ic-clock" },
            ],
          },
          // 총무(general-affairs) 워크스페이스 병합 — 앱의 기존 LNB 5개 메뉴를
          // embed 항목으로 1:1 매핑 (LNB 개편 디자인 시안).
          // 대시보드·총무·관리는 아직 준비 중이라 "준비중"(DS 라벨
          // light-gray-dark·solid·square·xs) 배지 + disabled 처리. LNB 레일이
          // 어두워(neutral-900) 다크 서피스용 tone을 쓴다.
          // 참고: embed 라우트(embed:ga-*) 자체는 막지 않는다 — 현재는 테스트
          // 유저가 메뉴로 접근만 안 하면 되므로 LNB 비활성화로 충분(의도).
          // URL 직접 접근 차단은 필요해지면 후속으로 라우트 가드 추가.
          {
            label: "총무",
            items: [
              {
                id: "embed:ga-dashboard",
                label: "대시보드",
                icon: "ic-gauge",
                badge: {
                  text: "준비중",
                  tone: "light-gray-dark",
                  size: "xs",
                  shape: "square",
                },
                disabled: true,
              },
              {
                id: "embed:ga-training",
                label: "총무",
                icon: "ic-cap",
                badge: {
                  text: "준비중",
                  tone: "light-gray-dark",
                  size: "xs",
                  shape: "square",
                },
                disabled: true,
              },
              {
                id: "embed:ga-tasks",
                label: "관리",
                icon: "ic-list-check",
                badge: {
                  text: "준비중",
                  tone: "light-gray-dark",
                  size: "xs",
                  shape: "square",
                },
                disabled: true,
              },
              { id: "embed:ga-notices", label: "공지", icon: "ic-bell" },
              // 총무 '설정'(앱 푸시 알림)은 하단 '설정 > 기본정보 상단'으로 이관 → LNB에서 숨김
              // { id: "embed:ga-settings", label: "설정", icon: "ic-setting" },
            ],
          },
          {
            label: "기타",
            items: [
              { id: "esign", label: "전자 서명", icon: "ic-signature" },
              { id: "embed:mgmt-dashboard", label: "경영 대시보드", icon: "ic-chart-up" },
              { id: "embed:drive", label: "드라이브", icon: "ic-folder" },
              // 대화 복구(MED-927 E11) — 대화 복구 권한(chatRecovery:manage, 시스템 관리자는 통과)이 있을 때만(서버도 같은 기준)
              ...(Alpine.store("hereby").hasPermission("chatRecovery:manage")
                ? [{ id: "chat-recovery", label: "대화 복구", icon: "ic-shield-check" }]
                : []),
            ],
          },
          {
            label: "설정",
            // 기본 정보 → 설정으로 명칭 변경 (LNB 개편 시안)
            items: [{ id: "settings:org", label: "설정", icon: "ic-setting" }],
          },
        ],
      };
    },

    /* ── dashboard stats ── */
    stats: {
      totalEmployees: 0,
      activeEmployees: 0,
      inactiveEmployees: 0,
      onLeaveEmployees: 0,
      terminatedEmployees: 0,
      totalTeams: 0,
      totalRoles: 0,
    },

    /** 목록별 「첫 응답이 왔다(성공 · 실패 무관)」 — 오기 전엔 그 화면이 스켈레톤을 그리고 빈 상태 문구를 숨긴다(DS skeleton). */
    loadedOnce: {
      roles: false,
      wards: false,
      offsite: false,
      closings: false,
      shiftGroups: false,
      jobDuties: false,
      workPatterns: false,
      corrections: false,
      esign: false,
      esignStatus: false,
    },
    /** 직원 관리 통계 핀(전체 · 재직 · 팀 · 역할 수)을 처음 다 센 뒤 true — 그 전엔 숫자 자리가 스켈레톤. */
    statsReady: false,
    /** 직원 목록 첫 응답이 왔는가 — 오기 전엔 표가 스켈레톤 행을 그린다(빈 상태 문구 대신). 실패해도 true(빈 상태로 넘어간다). */
    employeesLoaded: false,

    /* ── data arrays ── */
    employees: [] as Record<string, unknown>[],
    /** Org-wide team-less employees ("미소속"). Loaded independently of the
     *  paginated/filtered `employees` table so the org tree always surfaces
     *  unassigned members regardless of the current page or active filters. */
    unassignedMembers: [] as Record<string, unknown>[],
    /** Org-wide member roster (every employee, member-table shape). Backs the
     *  workspace(최상위 조직) detail view, which shows all members instead of a
     *  team list. Loaded like `unassignedMembers` (full roster) but unfiltered. */
    allMembers: [] as Record<string, unknown>[],
    /** employeeId → active team ids, from the full org roster. Lets the tree
     *  badge count DISTINCT employees per subtree (dedup across teams) without
     *  loading each team's composition. Populated alongside `allMembers`. */
    rosterTeamMap: {} as Record<string, string[]>,
    /** 직원별 재직 상태 — 팀 관리 인원 배지를 재직 + 휴직만 세려고 쓴다(퇴사 · 비활성 제외). */
    rosterStatusMap: {} as Record<string, string>,
    // 퇴사자가 예전에 있던 팀(소속이 끝난 기록) — 팀별 「퇴사」 인원
    rosterLeftTeamMap: {} as Record<string, string[]>,
    /** 명단 첫 요청이 끝났는가(성공 · 실패 무관) — 팀 관리 명단 스켈레톤을 거두는 기준. */
    rosterSettled: false,
    /** team.roster 를 한 번이라도 받았는지 — 받은 뒤에는 팀별 명단(teamMembers)을 로스터에서 파생하므로
     *  getComposition 을 부르지 않는다(받기 전 · 실패 시에만 예전 경로로 폴백). */
    rosterLoaded: false,
    wardRefreshTimer: null as ReturnType<typeof setTimeout> | null,
    /** employeeId 집합 — 이번 세션에 팀/병원에 "추가"된 직원(신규 등록·기존 멤버
     *  추가 무관). 멤버 테이블(워크스페이스·팀)에서 사번 우측 blue '신규' 라벨로 표시.
     *  세션 한정 — 새로고침하면 비워진다(서버 저장 없음). markRecentlyAdded로 채운다. */
    recentlyAddedEmpIds: [] as string[],
    teams: [] as Record<string, unknown>[],
    /** 팀 목록 첫 응답이 왔는가 — 오기 전엔 팀 관리 트리가 스켈레톤을 그린다. 실패해도 true. */
    teamsLoaded: false,
    roles: [] as RoleItem[],
    shifts: [] as ShiftItem[],
    permissions: [] as Record<string, unknown>[],
    employeeStatusCounts: {} as Record<string, number>,

    /* ── pagination ── */
    paging: {
      employees: { page: 1, limit: readPageSize("employees"), totalCount: 0, totalPages: 0 },
      // 조직 탭 팀 목록은 트리 렌더링 — 계층 유지를 위해 전체 로드 (페이지네이션 비대상)
      teams: { page: 1, limit: 1000, totalCount: 0, totalPages: 0 },
      roles: { page: 1, limit: 30, totalCount: 0, totalPages: 0 },
      esign: { page: 1, limit: 30, totalCount: 0, totalPages: 0 },
    } as Record<string, { page: number; limit: number; totalCount: number; totalPages: number }>,
    /** 잔여 연차 첫 응답이 왔는가 — 오기 전엔 표 · 요약 숫자가 스켈레톤. 실패해도 true. */
    leaveBalancesLoaded: false,

    /* ── leave balance data ── */
    leaveBalances: [] as Record<string, unknown>[],
    /* basis: "" = 조직 적용 기준, 그 외는 그 기준으로 본다(적용 기준과 다르면 서버가 참고 환산) */
    leaveFilter: {
      search: "",
      year: new Date().getFullYear(),
      teamIds: [] as string[],
      basis: "" as "" | "hire_date" | "fiscal_year",
    },
    /** listBalances 응답의 기준 정보 — reference=true 면 목록이 참고 환산값 */
    leaveBalancesBasis: null as { applied: string; shown: string; reference: boolean } | null,
    annualLeavePromotions: [] as Record<string, unknown>[],
    annualLeavePromotionSelectedIds: [] as string[],
    annualLeavePromotionAllMatchingSelected: false,
    annualLeavePromotionTemplates: [] as Record<string, unknown>[],
    annualLeavePromotionTemplatesLoading: false,
    // 서식이 하나도 없을 때 [서식 추가하기]가 새 창으로 여는 Hereby Sign 템플릿 관리 화면.
    // 서버가 null 을 주면(미설정·내부 주소) 버튼을 숨기고 안내문만 남긴다(CS-LOC-260914-0002).
    annualLeavePromotionTemplateManageUrl: null as string | null,
    annualLeavePromotionRetryingId: "",
    annualLeavePromotionBatches: [] as Record<string, unknown>[],
    annualLeavePromotionBatchModal: {
      open: false,
      batchId: "",
      batch: null as Record<string, unknown> | null,
      loading: false,
    },
    annualLeavePromotionBatchPollTimer: null as ReturnType<typeof setInterval> | null,
    annualLeavePromotionSecondModal: {
      open: false,
      promotionId: "",
      employeeId: "",
      planItems: [{ date: "", minutes: 480, halfDay: false }] as {
        date: string;
        minutes: number;
        halfDay: boolean;
      }[],
      submitting: false,
    },
    annualLeavePromotionModal: {
      open: false,
      employeeId: "",
      reissuePromotionId: "",
      selectionMode: AnnualLeavePromotionSelectionMode.EXPLICIT,
      selectionSearch: "",
      selectionTeamIds: [] as string[],
      selectionSignature: "",
      herebyTemplateId: 0,
      templateFingerprint: "",
      responseDurationDays: 10,
      submitting: false,
    },
    /* 연차 현황 클라이언트 페이지네이션 (전체 직원 → 30개/페이지) */
    leaveBalancePage: 1,
    leaveBalanceLimit: readPageSize("leave-balances"),

    /* ── 출근율 (전년도 출근율) 서브탭 ── */
    attendanceRates: [] as Record<string, unknown>[],
    attendanceRateDefaults: { fullYearWorkDays: 0, dailyStandard: 8 },
    // 기본 산정 연도 = 직전 연도
    attendanceRateFilter: {
      search: "",
      year: new Date().getFullYear() - 1,
      teamIds: [] as string[],
      result: "all" as "all" | "meet" | "unmeet",
    },
    attendanceRateLoading: false,
    attendanceRatePage: 1,
    attendanceRateLimit: readPageSize("attendance-rates"),
    // 소정근로일수 설정 모달
    showAttendanceRateModal: false,
    attendanceRateModalMode: "bulk" as "bulk" | "row",
    attendanceRateSaving: false,
    arForm: {
      days: 0,
      hours: 0,
      minutes: 0,
      roundingMode: AttendanceRateRoundingMode.ROUND as AttendanceRateRoundingMode,
      precision: 3,
      reason: "",
    },
    // 계산 설정 select 옵션
    arRoundingOptions: [
      { value: AttendanceRateRoundingMode.CEIL, label: "올림" },
      { value: AttendanceRateRoundingMode.ROUND, label: "반올림" },
      { value: AttendanceRateRoundingMode.FLOOR, label: "버림" },
    ],
    arPrecisionOptions: [
      { value: 1, label: "소수 첫째(0.1%)" },
      { value: 2, label: "소수 둘째(0.01%)" },
      { value: 3, label: "소수 셋째(0.001%)" },
      { value: 4, label: "소수 넷째(0.0001%)" },
    ],
    // 행 [수정] 대상 직원(변경 불가 정보 표시용)
    arRowEmployee: null as Record<string, unknown> | null,
    // 일괄 팝업 적용 대상 검색(팀 멤버 추가와 유사)
    arPickerEmployees: [] as Record<string, unknown>[],
    arPickerSearch: "",
    arPickerSelectedIds: [] as string[],
    arPickerLoading: false,
    arPickerSort: { by: "hireDate" as string, order: "asc" as "asc" | "desc" },
    /* ── 연차 현황 [상세]/[조정] (관리 열) ── */
    showLeaveBalanceDetail: false,
    leaveBalanceDetailTab: "rule" as "rule" | "usage" | "adjust",
    /** 상세/조정 대상 — 연차 현황(listBalances) 행 */
    leaveDetailTarget: null as Record<string, unknown> | null,
    leaveDetailInfo: null as LeaveBalanceDetailInfo | null,
    leaveDetailUsage: [] as Record<string, unknown>[],
    leaveDetailAdjustments: [] as LeaveAdjustmentItem[],
    leaveDetailLoading: false,
    showLeaveAdjustModal: false,
    /** 조정 폼 — 일+시간 분리 입력(직원 소정근로시간=1일, 1시간 단위) */
    leaveAdjustForm: { days: 0, hours: 0, reason: "" },
    leaveAdjustStd: 8,
    leaveAdjustSaving: false,

    /* ── 연차/휴가 결재 (네이티브 탭) ── */
    leaveApprovals: [] as Record<string, unknown>[],
    // search: 사번/이름/이메일 통합 검색 — 근태 현황 4개 페이지 공통 검색창(연차 현황과 동일 규약)
    leaveApprovalFilter: {
      status: "" as string,
      dateFrom: "" as string,
      dateTo: "" as string,
      search: "" as string,
    },
    /** 연차/휴가 결재 기간 프리셋 active 키. 수동 변경·초기화 시 "". */
    leaveApprovalDatePreset: "" as string,
    /** 신청일(createdAt) 정렬 방향 — 사번 좌측 신청일 컬럼 헤더로 토글. 기본 내림차순(최신 신청 상단). */
    leaveApprovalSortOrder: "desc" as "asc" | "desc",
    leaveApprovalPaging: {
      page: 1,
      limit: readPageSize("leave-approvals"),
      totalCount: 0,
      totalPages: 0,
    },
    showLeaveDetail: false,
    leaveDetailPopListenersBound: false,
    leaveDetailRecord: null as Record<string, unknown> | null,
    // 승인된 휴가의 '승인 취소' 확인 모달 (휴가 상세 위 z-60)
    showLeaveCancelApproval: false,
    showLeaveReject: false,
    leaveRejectId: "" as string,
    leaveRejectReason: "" as string,
    /* 연차 관리 단일 페이지의 4개 서브탭(공통UX §2.1·§2.2): 연차 현황/부여 기준/휴가 유형/연차·휴가 결재.
       기본 진입 서브탭 = 연차 현황(탭 순서상 첫 번째). */
    leaveSubTab: "balances" as "balances" | "rules" | "reasons" | "attendance-rate" | "approval",
    /* Tabs for the 연차 관리 page, consumed by the shared tabNav component. */
    get leaveTabs() {
      const t = (key: string) => Alpine.store("hereby").t(key) || key;
      // 연차 부여 기준·휴가 유형은 탭에서 분리 → 탭 우측 진입 버튼이 유형·기준 관리 패널을 연다.
      return [
        { id: "balances", label: t("leave.leave_status") },
        { id: "attendance-rate", label: "출근율" },
        { id: "approval", label: "연차/휴가 결재" },
      ];
    },

    /* ── 근태 현황 컨테이너 (activeTab==='attendance') ──
       기존에 LNB에 흩어져 있던 근태 화면을 상단 탭 4개로 묶은 단일 페이지.
       각 탭은 기존 화면을 그대로 품는다(2-레벨 탭):
         work     = 근무 현황   → team-manager 출퇴근 테이블만(iframe, att=1)
         leave    = 연차 현황   → leave-management.html (현황/연차·휴가 결재 서브탭)
         overtime = 연장근무 현황 → team-manager /work 외근 서브탭 제거(iframe, noOffsite=1)
         offsite  = 외근근무 현황 → offsite-management.html (현황/외근 결재 서브탭)
       유형·기준 설정은 탭이 아니라 nav 우측 「근태 설정」 버튼 → openTypeManager 로 분리. */
    attendanceTab: "work" as "work" | "leave" | "overtime" | "offsite" | "closing" | "correction",
    get attendanceTabs() {
      return [
        { id: "work", label: "근무" },
        { id: "leave", label: "연차" },
        { id: "overtime", label: "연장근무" },
        { id: "offsite", label: "외근" },
        { id: "closing", label: "근로시간 마감" },
        // 출퇴근 정정 요청 — 근태 승인 권한(팀 이상)이 있어야 서버가 목록을 준다. 대기 건수를 배지로.
        ...(Alpine.store("hereby").hasPermission("attendance:approve")
          ? [
              {
                id: "correction",
                label: "정정 요청",
                badge: this.correctionPendingCount > 0 ? this.correctionPendingCount : undefined,
              },
            ]
          : []),
      ];
    },
    /* Switch the 근태 현황 top tab. 네이티브 탭(leave/offsite)은 진입 시 데이터를 lazy-load,
       iframe 탭(work/overtime)은 :src 게이팅으로 보일 때만 로드된다. */
    switchAttendanceTab(tab: "work" | "leave" | "overtime" | "offsite" | "closing" | "correction") {
      this.attendanceTab = tab;
      if (tab === "leave") {
        this.loadLeaveBalances();
        this.loadEntitlementRules();
        this.loadLeaveReasons();
        // 기본 서브탭은 '연차 현황'(loadLeaveBalances)이지만, 결재 목록도 진입 시 미리 로드.
        this.loadLeaveApprovals();
      } else if (tab === "offsite") {
        this.loadOffsiteRecords();
        this.loadOffsiteTypes();
      } else if (tab === "closing") {
        this.loadWorkTimeClosings();
      } else if (tab === "correction") {
        this.loadCorrections();
      }
    },

    /* ══ 출퇴근 정정 요청 (근태 현황 > 정정 요청) ══
       직원 앱 근무 기록에서 보낸 「시각 고치기 · 빠진 기록 추가」 요청을 승인 · 반려한다.
       승인하면 서버가 관리자 근태 수정과 같은 계산(근무시간 재계산 · 수정 이력)으로 그날 기록을 바꾼다.
       급여 확정 · 근로시간 마감된 날은 서버가 거절한다(문구를 그대로 보여 준다). 시안: 캔버스 8-10~8-12. */
    corrections: [] as Record<string, unknown>[],
    correctionFilter: { status: "pending", kind: "", search: "" },
    correctionPaging: { page: 1, limit: readPageSize("corrections"), totalCount: 0, totalPages: 0 },
    correctionPendingCount: 0,
    correctionLoading: false,
    /** 검토 팝업 — 줄을 누르면 열린다. 승인하면 바뀔 기록 · 근무시간을 서버가 미리 계산해 준다(preview). */
    showCorrectionReview: false,
    correctionReview: null as Awaited<
      ReturnType<HerebyClient["attendanceCorrection"]["preview"]>
    > | null,
    correctionReviewLoading: false,
    showCorrectionReject: false,
    correctionRejectTarget: null as Record<string, unknown> | null,
    correctionRejectReason: "",

    async loadCorrections() {
      this.correctionLoading = true;
      try {
        const f = this.correctionFilter;
        const result = await this.sdk.attendanceCorrection.list({
          ...(f.status ? { status: f.status as "pending" } : {}),
          ...(f.kind ? { kind: f.kind as "fix_time" } : {}),
          ...(f.search.trim() ? { search: f.search.trim() } : {}),
          pagination: { page: this.correctionPaging.page, limit: this.correctionPaging.limit },
        });
        this.corrections = (result?.items ?? []) as Record<string, unknown>[];
        this.correctionPaging.totalCount = result?.total ?? 0;
        this.correctionPaging.totalPages = Math.ceil(
          this.correctionPaging.totalCount / this.correctionPaging.limit,
        );
      } catch (_e) {
        this.corrections = [];
        this.correctionPaging.totalCount = 0;
        this.correctionPaging.totalPages = 0;
      } finally {
        this.correctionLoading = false;
        this.loadedOnce.corrections = true;
      }
      this.loadCorrectionPendingCount();
    },

    /** 탭 배지 — 검토 대기 건수만(한 건만 읽고 total 을 쓴다). 권한이 없으면 0. */
    async loadCorrectionPendingCount() {
      if (!Alpine.store("hereby").hasPermission("attendance:approve")) {
        this.correctionPendingCount = 0;
        return;
      }
      try {
        const result = await this.sdk.attendanceCorrection.list({
          status: "pending" as const,
          pagination: { page: 1, limit: 1 },
        });
        this.correctionPendingCount = result?.total ?? 0;
      } catch (_e) {
        this.correctionPendingCount = 0;
      }
    },

    applyCorrectionFilter() {
      this.correctionPaging.page = 1;
      this.loadCorrections();
    },

    correctionGoPage(page: number) {
      if (page < 1 || page > this.correctionPaging.totalPages) {
        return;
      }
      this.correctionPaging.page = page;
      this.loadCorrections();
    },

    correctionPageRange(): number[] {
      return this.pageWindow(this.correctionPaging.page, this.correctionPaging.totalPages);
    },

    /** "08:55 · 기록 없음" — 출근 · 퇴근 두 칸. ISO 시각을 조직 시간대로. */
    correctionPair(inIso: unknown, outIso: unknown): string {
      const store = Alpine.store("hereby");
      const t = (v: unknown) => (v ? store.formatTime(v as string) : "기록 없음");
      return `${t(inIso)} · ${t(outIso)}`;
    },

    /** 승인하면 바뀔 기록 — 요청하지 않은 칸은 지금 값 그대로. */
    correctionAfter(r: Record<string, unknown>): {
      in: string;
      out: string;
      inChanged: boolean;
      outChanged: boolean;
    } {
      const store = Alpine.store("hereby");
      const t = (v: unknown) => (v ? store.formatTime(v as string) : "기록 없음");
      return {
        in: t(r.requestedCheckIn ?? r.originalCheckIn),
        out: t(r.requestedCheckOut ?? r.originalCheckOut),
        inChanged: !!r.requestedCheckIn,
        outChanged: !!r.requestedCheckOut,
      };
    },

    correctionKindLabel(kind: unknown): string {
      return kind === "add_missing" ? "기록 추가" : "시각 수정";
    },

    correctionStatusLabel(status: unknown): string {
      return (
        (
          { pending: "대기", approved: "승인", rejected: "반려", cancelled: "취소" } as Record<
            string,
            string
          >
        )[String(status)] ?? String(status)
      );
    },

    correctionStatusBadge(status: unknown): string {
      return (
        (
          {
            pending: "ds-label--yellow",
            approved: "ds-label--green",
            rejected: "ds-label--red",
            cancelled: "ds-label--gray",
          } as Record<string, string>
        )[String(status)] ?? "ds-label--gray"
      );
    },

    approveCorrection(r: Record<string, unknown>) {
      this.confirmTitle = "정정 요청 승인";
      this.confirmMessage = `${r.employeeName || "직원"}님의 ${Alpine.store("hereby").formatDate(r.date as string)} 출퇴근 기록을 요청대로 바꿀까요? 근무시간이 다시 계산돼요.`;
      this.confirmLabel = "승인";
      this.confirmAction = async () => {
        try {
          const res = await this.sdk.attendanceCorrection.approve(r.id as string);
          // 결재선이 있는 요청은 중간 단계면 「n단계 승인 · 다음 결재자」, 최종이면 반영 안내 — 서버 문구 그대로
          this.notify(res?.message ?? "승인했어요. 출퇴근 기록에 반영됐어요.");
          await this.loadCorrections();
        } catch (err) {
          showError(err instanceof Error ? err.message : String(err));
          await this.loadCorrections();
        }
      };
      this.showConfirm = true;
    },

    async openCorrectionReview(r: Record<string, unknown>) {
      this.correctionReview = null;
      this.correctionReviewLoading = true;
      this.showCorrectionReview = true;
      try {
        this.correctionReview = await this.sdk.attendanceCorrection.preview(r.id as string);
      } catch (err) {
        this.showCorrectionReview = false;
        showError(err instanceof Error ? err.message : String(err));
      } finally {
        this.correctionReviewLoading = false;
      }
    },

    /** 검토 팝업의 시각 칸 — 없으면 「기록 없음」. */
    correctionTime(v: unknown): string {
      return v ? Alpine.store("hereby").formatTime(v as string) : "기록 없음";
    },

    /** "7시간 12분 (소정 초과 3시간 12분)" — 출퇴근이 다 있어야 계산된다. */
    correctionHours(h: { totalHours: number | null; excessHours: number }): string {
      if (h.totalHours === null) {
        return "계산 전";
      }
      const ko = (hours: number) => {
        const m = Math.round(hours * 60);
        const hh = Math.floor(m / 60);
        const mm = m % 60;
        return hh && mm ? `${hh}시간 ${mm}분` : hh ? `${hh}시간` : `${mm}분`;
      };
      return h.excessHours > 0
        ? `${ko(h.totalHours)} (소정 초과 ${ko(h.excessHours)})`
        : ko(h.totalHours);
    },

    /** 검토 팝업 → 승인 / 반려 — 팝업을 닫고 줄의 동작과 같은 흐름으로 넘긴다. */
    reviewApprove() {
      const item = this.correctionReview?.item;
      if (!item) {
        return;
      }
      this.showCorrectionReview = false;
      this.approveCorrection(item as unknown as Record<string, unknown>);
    },

    reviewReject() {
      const item = this.correctionReview?.item;
      if (!item) {
        return;
      }
      this.showCorrectionReview = false;
      this.openCorrectionReject(item as unknown as Record<string, unknown>);
    },

    openCorrectionReject(r: Record<string, unknown>) {
      this.correctionRejectTarget = r;
      this.correctionRejectReason = "";
      this.showCorrectionReject = true;
    },

    async confirmCorrectionReject() {
      const r = this.correctionRejectTarget;
      const reason = this.correctionRejectReason.trim();
      if (!(r && reason)) {
        return;
      }
      try {
        await this.sdk.attendanceCorrection.reject(r.id as string, reason);
        this.showCorrectionReject = false;
        this.correctionRejectTarget = null;
        await this.loadCorrections();
      } catch (err) {
        showError(err instanceof Error ? err.message : String(err));
      }
    },

    /* ══ 근로시간 마감 (근태 현황 > 근로시간 마감) ══
       마감 내역(list) ↔ 개인별 마감 상태(individual) 두 서브탭. */
    closingSubTab: "list" as "list" | "individual",
    get closingTabs() {
      return [
        { id: "list", label: "마감 내역" },
        { id: "individual", label: "개인별 마감 상태" },
      ];
    },
    switchClosingTab(tab: "list" | "individual") {
      this.closingSubTab = tab;
      if (tab === "list") {
        this.loadWorkTimeClosings();
      } else if (tab === "individual") {
        // 개인별 진입 시 선택된 마감 건이 없으면 최신 건을 기본 선택
        if (!this.selectedClosingId && this.workTimeClosings.length > 0) {
          this.selectedClosingId = this.workTimeClosings[0].id as string;
        }
        this.loadClosingTargets();
      }
    },
    workTimeClosings: [] as Record<string, unknown>[],
    // 마감 추가/수정 모달
    showClosingModal: false,
    editingClosingId: "" as string,
    closingForm: {
      closingDate: "",
      confirmDeadline: "",
      autoClose: false,
      notifyEmail: false,
    },
    // 개인별 마감 상태
    selectedClosingId: "" as string,
    closingDetail: null as Record<string, unknown> | null,
    closingTargets: [] as Record<string, unknown>[],
    closingTargetStatusFilter: "all" as "all" | "unconfirmed" | "confirmed",
    closingTargetSearch: "" as string,
    selectedTargetEmployeeIds: [] as string[],

    async loadWorkTimeClosings() {
      try {
        const res = await this.sdk.workTimeClosing.list();
        this.workTimeClosings = (res as Record<string, unknown>[]) || [];
      } catch {
        this.workTimeClosings = [];
      } finally {
        this.loadedOnce.closings = true;
      }
    },

    /** 현재 서브탭 재로드 (새로고침 버튼). */
    refreshWorkTimeClosingView() {
      this.loadWorkTimeClosings();
      if (this.closingSubTab === "individual" && this.selectedClosingId) {
        this.loadClosingTargets();
      }
    },

    closingStatusLabel(row: Record<string, unknown>): string {
      return row.status === "closed" ? "마감" : "진행중";
    },

    openAddClosing() {
      this.editingClosingId = "";
      // 기본값: 이번 달 시작일을 마감일로, 말일을 확정 기한으로 제안
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      const pad = (n: number) => String(n).padStart(2, "0");
      const toISO = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      this.closingForm = {
        closingDate: toISO(firstDay),
        confirmDeadline: toISO(lastDay),
        autoClose: false,
        notifyEmail: false,
      };
      // 대상자: 기본 '재직 전원', 직접 선택 옵션용 직원 목록 로드
      this.closingTargetMode = "all";
      this.closingSelectedEmployeeIds = [];
      this.loadClosingSelectableEmployees();
      this.showClosingModal = true;
    },

    openEditClosing(row: Record<string, unknown>) {
      this.editingClosingId = row.id as string;
      this.closingForm = {
        closingDate: String(row.closingDate || "").slice(0, 10),
        confirmDeadline: row.confirmDeadline ? String(row.confirmDeadline).slice(0, 10) : "",
        autoClose: !!row.autoClose,
        notifyEmail: !!row.notifyEmail,
      };
      // 수정 시 대상자 재설정은 지원하지 않음(대상 잠금 보존) — 기한/플래그만 수정
      this.closingTargetMode = "all";
      this.showClosingModal = true;
    },

    async saveClosing() {
      if (!this.closingForm.closingDate) {
        return;
      }
      try {
        if (this.editingClosingId) {
          await this.sdk.workTimeClosing.update({
            id: this.editingClosingId,
            closingDate: this.closingForm.closingDate,
            confirmDeadline: this.closingForm.confirmDeadline || null,
            autoClose: this.closingForm.autoClose,
            notifyEmail: this.closingForm.notifyEmail,
          });
        } else {
          // 직접 선택 모드면 선택 직원, 아니면 미지정(서버가 재직 전원)
          const customIds =
            this.closingTargetMode === "custom" ? this.closingSelectedEmployeeIds : undefined;
          if (this.closingTargetMode === "custom" && (customIds?.length ?? 0) === 0) {
            showAlert("직접 선택 모드에서는 대상자를 1명 이상 선택해 주세요.");
            return;
          }
          await this.sdk.workTimeClosing.create({
            closingDate: this.closingForm.closingDate,
            confirmDeadline: this.closingForm.confirmDeadline || null,
            autoClose: this.closingForm.autoClose,
            notifyEmail: this.closingForm.notifyEmail,
            employeeIds: customIds,
          });
        }
        this.showClosingModal = false;
        await this.loadWorkTimeClosings();
        this.notify("저장되었습니다");
      } catch (err) {
        showError(`저장 실패: ${err instanceof Error ? err.message : String(err)}`);
      }
    },

    /** 마감 건 삭제 — 확정 대상 있으면 서버가 차단(DS 확인 모달). */
    deleteClosing(row: Record<string, unknown>) {
      this.confirmTitle = "삭제";
      this.confirmMessage = "이 마감 건을 삭제할까요? 확정된 대상자가 있으면 삭제할 수 없어요.";
      this.confirmLabel = this._t("common.confirm");
      this.confirmHideCancel = false;
      this.confirmAction = async () => {
        try {
          await this.sdk.workTimeClosing.delete(row.id as string);
          await this.loadWorkTimeClosings();
          this.notify("삭제되었습니다");
        } catch (err) {
          showError(`삭제 실패: ${err instanceof Error ? err.message : String(err)}`);
        }
      };
      this.showConfirm = true;
    },

    /** 마감 내역 행 클릭/[보기] → 개인별 마감 상태로 이동(해당 건 필터). */
    openClosingTargets(row: Record<string, unknown>) {
      this.selectedClosingId = row.id as string;
      this.closingSubTab = "individual";
      this.closingTargetStatusFilter = "all";
      this.closingTargetSearch = "";
      this.selectedTargetEmployeeIds = [];
      this.loadClosingTargets();
    },

    async loadClosingTargets() {
      if (!this.selectedClosingId) {
        this.closingTargets = [];
        this.closingDetail = null;
        return;
      }
      try {
        const res = (await this.sdk.workTimeClosing.listTargets({
          closingId: this.selectedClosingId,
          status: this.closingTargetStatusFilter,
          search: this.closingTargetSearch || undefined,
        })) as { closing?: Record<string, unknown>; items?: Record<string, unknown>[] };
        this.closingDetail = res?.closing || null;
        this.closingTargets = res?.items || [];
        this.selectedTargetEmployeeIds = [];
      } catch {
        this.closingTargets = [];
      }
    },

    closingTargetStatusLabel(status: unknown): string {
      return status === "confirmed" ? "마감" : "미마감";
    },

    /** 현재 마감 건 요약(개인별 상태 헤더/드롭다운). */
    get selectedClosingRow(): Record<string, unknown> | null {
      return this.workTimeClosings.find((c) => c.id === this.selectedClosingId) || null;
    },

    /** 개인별 마감 상태 → 엑셀 다운로드(현재 필터 결과). */
    async downloadClosingTargets() {
      if (this.closingTargets.length === 0) {
        return;
      }
      const xlsx = await import("xlsx");
      const header = ["사번", "이름", "소속", "상태", "확정일"];
      const rows = this.closingTargets.map((t) => [
        (t.employeeNumber as string) || "",
        (t.employeeName as string) || "",
        (t.teamName as string) || "",
        this.closingTargetStatusLabel(t.status),
        t.confirmedAt ? this.formatDateDot(t.confirmedAt as string) : "",
      ]);
      const ws = xlsx.utils.aoa_to_sheet([header, ...rows]);
      ws["!cols"] = header.map(() => ({ wch: 16 }));
      const wb = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(wb, ws, "개인별 마감 상태");
      const dateLabel = this.closingDetail?.closingDate
        ? String(this.closingDetail.closingDate).slice(0, 10)
        : "";
      xlsx.writeFile(wb, `근로시간마감_${dateLabel}.xlsx`);
    },

    /* ── 마감 추가 대상자 선택 ── */
    closingTargetMode: "all" as "all" | "custom",
    closingSelectableEmployees: [] as Record<string, unknown>[],
    closingSelectedEmployeeIds: [] as string[],

    /** 직접 선택용 재직 직원 목록 (모달 진입 시 1회 로드). */
    async loadClosingSelectableEmployees() {
      if (this.closingSelectableEmployees.length > 0) {
        return;
      }
      try {
        const result = await this.sdk.employee.getEmployees({
          // biome-ignore lint/suspicious/noExplicitAny: EmployeeStatus string value
          status: "ACTIVE" as any,
          page: 1,
          limit: 500,
        });
        const r = result as { items?: Record<string, unknown>[] };
        this.closingSelectableEmployees = (r?.items || []).map((e) => ({
          id: e.id,
          employeeNumber: e.employeeNumber,
          name: e.name || `${e.lastName ?? ""}${e.firstName ?? ""}`,
        }));
      } catch {
        this.closingSelectableEmployees = [];
      }
    },

    toggleAllClosingEmployees(event: Event) {
      const checked = (event.target as HTMLInputElement).checked;
      this.closingSelectedEmployeeIds = checked
        ? this.closingSelectableEmployees.map((e) => e.id as string)
        : [];
    },

    toggleAllTargets(event: Event) {
      const checked = (event.target as HTMLInputElement).checked;
      this.selectedTargetEmployeeIds = checked
        ? this.closingTargets.map((t) => t.employeeId as string)
        : [];
    },

    /**
     * 행 클릭 = 체크박스 선택 토글 (외근·연장근무 결재 표와 동일한 조작감).
     * 이 표는 확정/확정 취소 양방향이 모두 선택 기반이라 확정된 행도 선택 대상 —
     * 결재 표들과 달리 선택 제한 조건이 없어 모든 행이 클릭 가능하다.
     */
    toggleClosingTargetSelect(t: Record<string, unknown>) {
      const id = t.employeeId as string;
      const idx = this.selectedTargetEmployeeIds.indexOf(id);
      if (idx >= 0) {
        this.selectedTargetEmployeeIds.splice(idx, 1);
      } else {
        this.selectedTargetEmployeeIds.push(id);
      }
    },

    /** 선택 대상 확정(마감). 확인 후 실행. */
    confirmSelectedTargets() {
      const n = this.selectedTargetEmployeeIds.length;
      if (n === 0) {
        return;
      }
      this.confirmTitle = "근로 시간 확정";
      this.confirmMessage = `${n}명의 마감일까지 근로 시간을 확정할까요? 확정하면 해당 기간 근태를 수정할 수 없어요.`;
      this.confirmLabel = this._t("common.confirm");
      this.confirmHideCancel = false;
      this.confirmAction = async () => {
        try {
          await this.sdk.workTimeClosing.confirmTargets({
            closingId: this.selectedClosingId,
            employeeIds: this.selectedTargetEmployeeIds,
          });
          await this.loadClosingTargets();
          await this.loadWorkTimeClosings();
          this.notify("확정되었습니다");
        } catch (err) {
          showError(`확정 실패: ${err instanceof Error ? err.message : String(err)}`);
        }
      };
      this.showConfirm = true;
    },

    /** 선택 대상 확정 취소(해제). */
    unconfirmSelectedTargets() {
      const n = this.selectedTargetEmployeeIds.length;
      if (n === 0) {
        return;
      }
      this.confirmTitle = "확정 취소";
      this.confirmMessage = `${n}명의 근로 시간 확정을 취소할까요?`;
      this.confirmLabel = this._t("common.confirm");
      this.confirmHideCancel = false;
      this.confirmAction = async () => {
        try {
          await this.sdk.workTimeClosing.unconfirmTargets({
            closingId: this.selectedClosingId,
            employeeIds: this.selectedTargetEmployeeIds,
          });
          await this.loadClosingTargets();
          await this.loadWorkTimeClosings();
          this.notify("확정을 취소했어요");
        } catch (err) {
          showError(`확정 취소 실패: ${err instanceof Error ? err.message : String(err)}`);
        }
      };
      this.showConfirm = true;
    },

    /* ── 외근 관리 (offsite work) — 현황·결재를 통합한 단일 조직 전체 화면.
       외근 유형은 탭 우측 진입 버튼 → 유형·기준 관리 패널(외근 섹션)로 분리. */
    offsiteRecords: [] as Record<string, unknown>[],
    // search: 사번/이름/이메일 통합 검색 — 근태 현황 4개 페이지 공통 검색창(연차 현황과 동일 규약)
    offsiteRecFilter: { status: "", dateFrom: "", dateTo: "", teamIds: [] as string[], search: "" },
    /** 외근 현황 기간 프리셋 active 키. 수동 변경·초기화 시 "". */
    offsiteRecDatePreset: "" as string,
    offsitePaging: {
      page: 1,
      limit: readPageSize("offsite-records"),
      totalCount: 0,
      totalPages: 0,
    },
    /** 집계 — 필터 조건 전체의 외근 시간 합계 (기획서 §2.8) */
    offsiteTotalHours: 0,
    offsiteTypes: [] as {
      id: string;
      name: string;
      description?: string | null;
      sortOrder?: number;
      isActive?: boolean;
      usageCount?: number;
    }[],
    /** 유형 관리 상태 필터 (기획서 유형관리 §1): all | active | inactive */
    offsiteTypeFilter: "all" as "all" | "active" | "inactive",
    showOffsiteTypeModal: false,
    editingOffsiteTypeId: "" as string,
    offsiteTypeForm: { name: "", description: "", isActive: true },

    /* ── 연장근무 유형 관리 (기획서 「유형 관리(탭)」) ── */
    overtimeTypes: [] as {
      id: string;
      name: string;
      description?: string | null;
      multiplier?: number;
      sortOrder?: number;
      isActive?: boolean;
      usageCount?: number;
    }[],
    overtimeTypeFilter: "all" as "all" | "active" | "inactive",
    showOvertimeTypeModal: false,
    editingOvertimeTypeId: "" as string,
    // multiplier는 빈 입력(placeholder 노출)을 허용하기 위해 number | string. 저장 가드가 >0을 강제.
    overtimeTypeForm: {
      name: "",
      description: "",
      multiplier: "" as number | string,
      isActive: true,
    },

    /* ── 유형·기준 통합 관리 팝업 (type-manager.html) ──
       근무 설정 / 연장근무 유형 / 외근 유형 / 연차 부여 기준 / 휴가 유형을 좌측 메뉴로 모아
       한 팝업에서 관리한다. 진입은 기존 LNB 항목(handleLnbSelect)에서 openTypeManager로 연결.
       PoC: 연장근무 유형 섹션만 실제 동작(나머지는 준비 중 placeholder). */
    showTypeManagerModal: false,
    typeManagerSection: "overtime" as
      | "shift"
      | "jobDuty"
      | "workPattern"
      | "overtime"
      | "offsite"
      | "rule"
      | "reason",
    get typeManagerMenu() {
      // 아이콘은 각 항목의 도메인이 LNB에서 쓰는 정체성 아이콘과 동일하게 맞춘다.
      // 근무 설정=ic-sliders, 연장근무=ic-clock-plus(연장근무 관리 도메인), 외근=ic-map-pin,
      // 연차(부여기준/휴가유형)=달력 계열.
      return [
        { id: "shift", label: "근무 설정", icon: getIcon("ic-sliders") },
        { id: "jobDuty", label: "직무 설정", icon: getIcon("ic-id-card") },
        { id: "workPattern", label: "근로형태", icon: getIcon("ic-sliders") },
        { id: "overtime", label: "연장근무 유형", icon: getIcon("ic-clock-plus") },
        { id: "offsite", label: "외근 유형", icon: getIcon("ic-map-pin") },
        { id: "rule", label: "연차 부여 기준", icon: getIcon("ic-calendar-check") },
        { id: "reason", label: "휴가/연차 유형", icon: getIcon("ic-calendar") },
        { id: "policy", label: "연차 정책", icon: getIcon("ic-sliders") },
        { id: "holiday", label: "공휴일", icon: getIcon("ic-calendar") },
      ] as { id: string; label: string; icon: string }[];
    },
    /* 팝업 위에 중첩으로 뜨는 도메인 폼/확인창이 하나라도 열려 있는지.
       컨테이너 ESC 가드에 사용 — 자식이 열려 있으면 컨테이너는 닫지 않는다(자식만 먼저 닫힘).
       showConfirm(삭제·비활성·템플릿 확인)·closeConfirm(저장확인)도 자식 오버레이이므로 포함해
       include(window 리스너 등록) 순서와 무관하게 "직전에 열린 팝업만" 닫히도록 한다.
       새 섹션 폼을 추가하면 그 show 플래그를 여기에 함께 넣을 것. */
    get typeManagerChildOpen() {
      return (
        this.showOvertimeTypeModal ||
        this.showOffsiteTypeModal ||
        this.showShiftGroupModal ||
        this.showShiftTypeModal ||
        this.showJobDutyModal ||
        this.showWorkPatternModal ||
        this.showWorkPatternRecompute ||
        this.showWorkPatternImport ||
        this.showJobDutyReapply ||
        this.showEditRule ||
        this.showLeaveReasonModal ||
        this.showHolidayModal ||
        this.showLeavePolicyImpact ||
        this.showConfirm ||
        this.closeConfirm.open
      );
    },
    /* 저장 버튼 disabled 판정 — reactive 게터.
       주의: 인라인 :disabled 식에 formDirty() 함수 호출을 직접 넣으면 Alpine 이펙트가
       name 변경에 재평가되지 않는(이펙트가 죽는) 문제가 있어, 게터로 분리한다.
       ADD는 필수값 충족 시 활성, EDIT는 변경(formDirty)까지 있어야 활성. */
    get overtimeSaveDisabled(): boolean {
      const f = this.overtimeTypeForm;
      if (!String(f.name ?? "").trim()) {
        return true;
      }
      if (!(Number(f.multiplier) > 0)) {
        return true;
      }
      if (this.editingOvertimeTypeId && !this.formDirty("showOvertimeTypeModal")) {
        return true;
      }
      return false;
    },
    get offsiteSaveDisabled(): boolean {
      const f = this.offsiteTypeForm;
      if (!String(f.name ?? "").trim()) {
        return true;
      }
      if (this.editingOffsiteTypeId && !this.formDirty("showOffsiteTypeModal")) {
        return true;
      }
      return false;
    },
    // 근무 그룹/근무 유형·휴가 유형도 같은 이유(인라인 formDirty가 name 변경 재평가를 죽임)로 게터 분리.
    get shiftGroupSaveDisabled(): boolean {
      const f = this.shiftGroupForm;
      if (this.shiftGroupSaving) {
        return true;
      }
      if (!String(f.name ?? "").trim()) {
        return true;
      }
      if (this.editingGroupId && !this.formDirty("showShiftGroupModal")) {
        return true;
      }
      return false;
    },
    get shiftTypeSaveDisabled(): boolean {
      const f = this.shiftTypeForm;
      if (this.shiftTypeSaving) {
        return true;
      }
      if (!String(f.name ?? "").trim()) {
        return true;
      }
      // 코드는 그룹 '스케줄러 연동' ON일 때만 필수 (OFF면 코드 없이 저장 가능).
      if (this.typeFormGroupSchedulerEnabled && !String(f.code ?? "").trim()) {
        return true;
      }
      if (this.editingShiftId && !this.formDirty("showShiftTypeModal")) {
        return true;
      }
      return false;
    },
    /**
     * 코드 길이 초과 안내 — 비어 있으면 정상.
     *
     * 입력을 maxlength 로 잘라내지 않고 "넘치면 알려주고 저장을 막는" 방식이다.
     * 이유: (1) 잘라내면 4번째 글자가 그냥 사라져 왜 안 들어가는지 알 수 없고,
     * (2) 상한 도입 전에 저장된 긴 코드를 편집으로 열었을 때 저장 버튼만 비활성화되고
     * 이유가 안 보이면 막다른 길이 된다. 두 경우 모두 같은 문구로 설명된다.
     * 취소(모달 닫기)는 leaveReasons 를 건드리지 않으므로 기존 긴 코드는 그대로 남는다.
     */
    get leaveReasonCodeError(): string {
      const code = String(this.leaveReasonForm.code ?? "");
      if (code.length > this.leaveReasonCodeMax) {
        return `코드는 최대 ${this.leaveReasonCodeMax}글자예요. ${code.length}글자를 입력했어요.`;
      }
      // 중복 — 같은 코드가 두 유형에 걸리면 근무표 셀에서 구분이 안 된다(색만 다름).
      // 공백·대소문자를 무시해 비교하고, 편집 중인 자기 자신은 제외한다.
      const dup = this.leaveReasonDuplicateCodeOwner(code);
      return dup ? `'${dup}' 유형이 이미 이 코드를 쓰고 있어요. 다른 코드를 입력해 주세요.` : "";
    },

    /** 입력 코드를 이미 쓰는 다른 휴가 유형의 이름. 없으면 null. */
    leaveReasonDuplicateCodeOwner(code: string): string | null {
      const key = code.trim().toLowerCase();
      if (!key) {
        return null;
      }
      const owner = this.leaveReasons.find(
        (r) =>
          r.id !== this.editingLeaveReasonId &&
          String(r.code ?? "")
            .trim()
            .toLowerCase() === key,
      );
      return owner ? owner.name : null;
    },

    /**
     * 휴가 유형의 코드 배지 정보 — 연차 목록의 「유형」 열과 상세 팝업이 이름 옆에 코드를 붙일 때 쓴다.
     * 코드가 없거나 레거시 enum 값이면 null(배지 없음).
     */
    leaveReasonCodeBadge(type: unknown): { code: string; color: string } | null {
      const found = this.leaveReasons.find((r) => r.id === String(type ?? ""));
      const code = String(found?.code ?? "").trim();
      if (!(found && code)) {
        return null;
      }
      return { code: this.leaveReasonCodeLabel(code), color: found.color || "" };
    },

    get leaveReasonSaveDisabled(): boolean {
      const f = this.leaveReasonForm;
      if (!String(f.name ?? "").trim()) {
        return true;
      }
      // 코드는 선택 입력(2026-09-16) — 없으면 스케줄러 배정 목록에만 안 오른다(폼에 안내).
      // 상한 초과·중복 코드는 저장 불가 — 신규 등록도, 긴 코드를 열어 다시 저장하는 것도 막힌다
      if (this.leaveReasonCodeError) {
        return true;
      }
      // 시간 설정 — 30분 스텝, 30분~8시간(8시간=1일)
      const um = Number(f.usageMinutes);
      if (!Number.isFinite(um) || um < 30 || um > 480 || um % 30 !== 0) {
        return true;
      }
      // 개인 정보 설정 Y면 대체 표시명 필수 — 없으면 제3자 화면에 뭘 보여줄지 정의되지 않는다
      if (f.privacyMasked && !String(f.maskedName ?? "").trim()) {
        return true;
      }
      // 휴게 시간 — 시·분 숫자 유효성 (허용 토글과 무관하게 값 자체는 항상 저장)
      const bh = Number(f.breakHoursPart);
      const bm = Number(f.breakMinutesPart);
      if (!Number.isInteger(bh) || bh < 0 || bh > 8 || !Number.isInteger(bm) || bm < 0 || bm > 59) {
        return true;
      }
      if (this.editingLeaveReasonId && !this.formDirty("showLeaveReasonModal")) {
        return true;
      }
      return false;
    },
    openTypeManager(section = "overtime") {
      this.showTypeManagerModal = true;
      this.selectTypeManagerSection(section);
    },
    selectTypeManagerSection(section: string) {
      this.typeManagerSection = section as typeof this.typeManagerSection;
      // 섹션 진입 시 해당 데이터 로드(전 섹션 구현 완료).
      if (section === "overtime") {
        this.loadOvertimeTypes();
      } else if (section === "offsite") {
        this.loadOffsiteTypes();
      } else if (section === "shift" || section === "jobDuty" || section === "workPattern") {
        // 근무 설정·직무 설정: 그룹+유형+직무 (설정 탭과 동일하게 셋 다 로드 — 근무 유형 폼의 「기본으로 쓰는 직무」와
        // 직무 매핑표가 서로의 목록을 쓴다)
        this.loadShifts();
        this.loadShiftGroups();
        this.loadJobDuties();
        this.loadWorkPatterns();
        // 임베드에서 바로 열어도 근무 그룹·형태의 대표 팀을 표시한다.
        this.loadAllTeams();
      } else if (section === "rule") {
        this.loadEntitlementRules();
      } else if (section === "reason") {
        this.loadLeaveReasons();
        // 사용 가능 근무유형 선택지 + 유형 설정 발효 여부 안내에 필요
        this.loadShiftGroups();
        this.loadLeavePolicy();
      } else if (section === "policy") {
        this.loadLeavePolicy();
      } else if (section === "holiday") {
        this.loadHolidays();
        // 공휴일 미반영 경고의 판정 기준(excludeWeekendsHolidays 실효값) — 정책을 안 읽으면
        // 기본값(false)으로 판정해 5인 이상(강제 ON) 조직에도 거짓 경고가 뜬다
        this.loadLeavePolicy();
      }
    },

    /* ── e-sign (Hereby integration) ── */
    esignRecords: [] as Record<string, unknown>[],
    esignTemplates: [] as Record<string, unknown>[],
    esignPollingTimer: null as ReturnType<typeof setInterval> | null,
    esignPollingInFlight: false,
    esignForm: { employeeId: "", herebyTemplateId: 0 } as {
      employeeId: string;
      herebyTemplateId: number;
    },
    esignSending: false,
    // 서명 요청 보내기 모달(직원·템플릿 선택 → 전송). 툴바 인라인 폼에서 분리(CS-LOC-260911-0005).
    showESignSendModal: false,
    // 계약서 템플릿 목록 모달(뷰어) — "지금 보낼 수 있는 계약서가 무엇인지"를 보여 준다(CS-LOC-260911-0004).
    showESignTemplatesModal: false,
    esignTemplatesLoading: false,
    // 모달에서 고른 직원 원본 — 수신 이메일 안내·이메일 없음 경고에 쓴다. esignForm.employeeId 와 함께 움직인다.
    esignSelectedEmployee: null as Record<string, unknown> | null,
    // Per-record loading state for "열기" button — keyed by ESignRecord.id.
    // Guards against rapid re-clicks during the issue-redirect-token round-trip.
    esignOpening: {} as Record<string, boolean>,
    esignFilter: { search: "", filterStatus: "" } as { search: string; filterStatus: string },
    esignIntegrationHealth: {
      signApi: { status: "waiting", label: "전자서명 연결 확인 중" },
      webhook: { status: "waiting", label: "상태 수신 대기" },
    } as ESignIntegrationHealthState,
    esignIntegrationStatus: {
      status: "unlinked",
      ownerEmailMasked: null,
      linkedAt: null,
      maskedApiKey: null,
      errorMessage: null,
      templateManageUrl: null,
    } as ESignIntegrationStatusState,
    esignProvisioning: false,
    esignFilterLabel: {
      writing: "작성 중",
      reserved: "예약",
      failed: "실패",
      waiting: "대기 중",
      completed: "체결",
      inactive: "비활성",
    } as Record<string, string>,
    esignLocalStatusLabel: {
      sending: "전송 중",
      sent: "전송됨",
      signed: "서명 완료",
      rejected: "거절됨",
      expired: "만료됨",
      error: "오류",
    } as Record<string, string>,
    // Combobox state for the template picker
    esignTemplateQuery: "",
    esignTemplateOpen: false,
    esignTemplateHighlight: 0,
    // Combobox state for the employee picker — server-driven so search
    // covers all employees, not just the current employees-tab page.
    esignEmployeeOptions: [] as Record<string, unknown>[],
    esignEmployeeQuery: "",
    esignEmployeeOpen: false,
    esignEmployeeHighlight: 0,
    esignEmployeeLoading: false,

    /* ── entitlement rules ── */
    entitlementRules: [] as EntitlementRuleItem[],
    /* 조직의 연차 산정 기준 — 이력표(leave_accrual_basis_changes)가 정본. 관리자가 적용 시작일을 정해
       예약하고, 코드가 날짜로 기준을 바꾸지 않는다(2026-09-14 결정). 행이 없으면 입사일 기준. */
    leaveBasis: {
      today: "",
      current: {
        basis: "hire_date" as "fiscal_year" | "hire_date",
        fiscalStart: "01-01",
        newHireMode: "prorate_in_hire_year" as
          | "prorate_in_hire_year"
          | "monthly_then_retroactive"
          | "monthly_then_prorate_at_anniversary",
        effectiveFrom: null as string | null,
      },
      pending: null as LeaveBasisChangeView | null,
      history: [] as LeaveBasisChangeView[],
    },
    leaveBasisHistoryOpen: false,
    /* 과거 연차 내역 일괄 업로드 — 연차 현황 「업로드」. 파일은 base64 로 서버에 보내 파싱·검증·저장한다 */
    showLeaveImport: false,
    leaveImport: {
      fileName: "",
      fileBase64: "",
      fileError: "",
      loading: false,
      loadingLabel: "",
      preview: null as LeaveImportPreview | null,
      mapping: {} as Record<string, string>,
      terminatedPolicy: "create" as "create" | "skip" | "error",
      /** 오류 행을 건너뛰고 나머지만 저장 (기본 All-or-Nothing) */
      skipInvalidRows: false,
      validated: false,
      errors: [] as { row: number; message: string }[],
      summary: null as LeaveImportSummary | null,
      saving: false,
    },
    leaveImportBatches: [] as LeaveImportBatchView[],
    showLeaveBasisChange: false,
    leaveBasisSaving: false,
    /** 연차 현황 근무 미배정 배지 → 근무 배정 모달(leave-shift-assign.html). 대상은 배지를 누른 직원 한 명. */
    showLeaveShiftAssign: false,
    leaveShiftAssignSaving: false,
    leaveShiftAssignTarget: null as {
      employeeId: string;
      employeeName: string;
      employeeNumber: string;
      dailyStandardHours: number;
    } | null,
    leaveShiftAssignForm: { shiftGroupId: "", shiftId: "" },
    leaveBasisForm: {
      basis: "fiscal_year" as "fiscal_year" | "hire_date",
      effectiveFrom: "",
      newHireMode: "prorate_in_hire_year" as
        | "prorate_in_hire_year"
        | "monthly_then_retroactive"
        | "monthly_then_prorate_at_anniversary",
      note: "",
    },
    /* 부여 방식(기준) 탭 — 회계년도/입사일 규칙 리스트를 각각 관리한다. 열 때 조직 기준 탭으로 맞춘다 */
    ruleBasisTab: "hire_date" as "fiscal_year" | "hire_date",
    /* 회계일(settings.fiscalYearStart "MM-DD") — datepicker 바인딩용 풀 날짜(YYYY-MM-DD) */
    ruleFiscalStartDate: "",
    ruleFiscalStartSaved: "01-01",
    showEditRule: false,
    editingRule: null as Record<string, unknown> | null,
    ruleForm: {
      name: "",
      basis: "fiscal_year" as "fiscal_year" | "hire_date",
      isActive: true,
      yearsOfServiceMin: 0,
      yearsOfServiceMax: null as number | null,
      entitledDays: 15,
      accrualType: "annual" as "monthly" | "annual",
      maxDays: null as number | null,
      description: "",
      sortOrder: 0,
    },

    /* ── settings tab ── */
    settingsSubTab: "org" as string,
    settingsLoaded: false,
    /* In-page settings sub-nav is hidden: the main LNB (2-depth) is now the
       single navigation surface. Flip to true to restore the inline sidebar. */
    settingsSidebarVisible: false,
    /* Settings sub-tab → 2-depth (item) label. Used as the fallback PageHeader
       title for items that have no parent group (leaves, org). Mirrors the main
       LNB item labels; the in-page section header shows the same text. */
    settingsSubLabels: {
      // LNB 개편 시안 명칭 반영: 기본 정보→설정, 근무 시간→근무 설정, 문서 설정→문서 양식
      org: "설정",
      myinfo: "내 정보",
      shifts: "근무 설정",
      jobDuties: "직무 설정",
      workPatterns: "근로형태",
      grades: "호봉표",
      codes: "항목 코드",
      docTemplates: "문서 양식",
      approvalLines: "결재선",
    } as Record<string, string>,
    /* Settings sub-tab → 1-depth (group) label. The PageHeader title shows the
       group name for depth menus (e.g. 수당/인센티브 설정 → "급여관리 설정"),
       while the in-page section header shows the 2-depth item name. Sub-tabs
       absent here have no group, so the title falls back to settingsSubLabels. */
    settingsGroupLabels: {
      // docTemplates는 그룹명(문서 결재) 대신 항목명(문서 양식)을 그대로 타이틀로 노출
      grades: "급여관리 설정",
    } as Record<string, string>,

    /* ── documents tab (문서 결재 iframe + 자격증/면허 native, 통합 페이지) ── */
    docsTab: "approval" as "approval" | "certs",
    /* 탭 클릭 시 확인할 권한(resource:action). 시스템 관리자는 우회.
       ⚠️ 이 프론트 체크는 UX 안내(팝업)일 뿐 — 백엔드 권한 강제는 별도(후속). */
    docsTabPerm: {
      approval: "document:read",
      certs: "employee:read",
    } as Record<string, string>,
    docsPermPopup: { open: false, label: "" } as { open: boolean; label: string },
    /* Tabs for the documents page, consumed by the shared tabNav component.
       Each item also supports optional leadingIcon/trailingIcon, leadingText/
       trailingText, updateDot, badge — add fields here when needed. */
    get documentsTabs() {
      return [
        { id: "approval", label: "문서 결재" },
        { id: "certs", label: "자격증/면허" },
      ];
    },

    /* 기간 프리셋 공용 항목 — SubTab(sm · line) 항목.
       id는 resolveDatePresetRange()가 받는 프리셋 키와 같아야 한다.
       "선택 없음"(=기간 조건 없음)이 정상 상태이므로 활성 id가 ""여도 된다 — SubTab은
       세그먼트와 달리 "항상 하나 선택"을 강제하지 않는다. */
    get datePresetTabs() {
      const t = (key: string) => Alpine.store("hereby").t(key) || key;
      return [
        { id: "thisWeek", label: t("attendance.this_week") },
        { id: "lastWeek", label: t("attendance.last_week") },
        { id: "thisMonth", label: t("attendance.this_month") },
        { id: "lastMonth", label: t("attendance.last_month") },
        { id: "last3Months", label: t("attendance.last_3_months") },
        { id: "all", label: t("common.all") },
      ];
    },

    /* 자격증/면허 신고 기한 기간 프리셋 — 항목은 공용 datePresetTabs와 동일(setCertDatePreset 경유). */
    get certDatePresetTabs() {
      return this.datePresetTabs;
    },

    /* ── embed tab (iframe-based LNB items pointing at hr-system / team-manager) ── */
    currentEmbed: "" as string,
    /* Bumped on refresh; appended to iframe URL as `&_=<n>` so changing
       it forces the iframe to re-fetch even when other params are unchanged. */
    embedReloadKey: 0 as number,
    /* True while the iframe is reloading (between refreshCurrentEmbed
       and the iframe's `@load`). Drives the PageHeader's refresh-button
       disabled+spinner state so rapid double-clicks can't kick off
       multiple parallel reloads — each iframe instance fires its own
       SDK init / token-refresh chain, and stacking them races the
       refresh-token-rotation server-side and silently logs the user
       out. Per 2026-06-19 user report.

       Belt + suspenders: combined with the cross-instance token-adoption
       fix in HerebyClient, the SDK layer also tolerates the race; this
       guard simply prevents it from happening in the first place. */
    embedRefreshing: false as boolean,

    /* iframe 문서가 아직 뜨지 않은 구간 표시.
       임베드된 앱은 자기 코드가 실행되기 전까지 아무것도 그릴 수 없어, 그동안
       임베드 영역이 통째로 비어 보인다(자식 쪽에서는 손댈 수 없는 구간).
       그 자리를 admin 이 스피너로 채운다. iframe 의 `@load` 에서 내린다. */
    embedLoading: true as boolean,

    /** 임베드 대상/리로드 키가 바뀌면 다시 로딩 상태로 되돌린다(x-effect 로 호출). */
    markEmbedLoading(_currentEmbed: string, _reloadKey: number): void {
      this.embedLoading = true;
    },

    /* ── team filter (shared across team-manager iframe pages) ── */
    myTeams: [] as { id: string; name: string; memberCount?: number }[],
    selectedTeamId: "" as string,
    orgInfo: null as Record<string, unknown> | null,
    orgSettings: { selfPasswordReset: false } as Record<string, unknown>,
    salaryGrades: [] as Record<string, unknown>[],
    salaryGradeFilter: { categoryId: "" as string, year: 0 },

    /* ── ward management ── */
    wardList: [] as Record<string, unknown>[],
    /* ── 병동 관리 = 목록 + 상세(2026-09-22) — 왼쪽 병동 목록에서 고른 것. "all" = 전체 병동 개요, "ward" = 병동 상세.
       해시(#wards/<id> · #wards/all) + localStorage 로 기억한다(팀 관리 트리 선택과 같은 방식). ── */
    wardSelected: { type: "all" as "all" | "ward", id: "" },
    wardSelectionRestored: false,
    /** <768 에서 병동 목록 서랍 열림(팀 관리 트리 서랍과 같은 방식) */
    wardListOpen: false,
    /** 병동 상세 하위 탭 — 인력(배정 인원) · 병동 정보 · 스케줄러 */
    wardDetailTab: "people" as "people" | "info" | "schedulers",
    /** 병동 정보 「책임자」 수정 — 직원 id 목록. 표 안 수정(wardInlineEdit)과 따로 둔다(개요 표엔 책임자 칸이 없다).
     *  wardId 가 wardInlineEdit.id 와 같을 때만 유효하다 */
    wardHeadEdit: { wardId: "", selected: [] as string[], original: [] as string[] },
    /* ── 스케줄러(스케줄 그룹) — 병동 안에서 근무표를 나누는 단위. 병동 관리 표의 「스케줄러」 칸에서 관리한다 ── */
    /** 조직의 스케줄러 목록(병동 이름 포함) — 병동 목록과 함께 받는다. 팀 관리 · 직원 팝업의 병동 배정 줄도 이 목록을 쓴다 */
    scheduleGroupList: [] as ScheduleGroupItem[],
    /** 한 병동의 스케줄러 목록 팝업 */
    wardSchedulers: { open: false, wardId: "" },
    /** 스케줄러 추가/수정 폼(목록 팝업 위) — closeGuardForms.schedulerShowForm 에 등록돼 저장확인을 거친다 */
    schedulerShowForm: false,
    schedulerSaving: false,
    schedulerFormMode: "create" as "create" | "edit",
    schedulerForm: emptySchedulerForm(),
    schedulerFormOriginal: emptySchedulerForm(),
    /** 포함 직군 체크박스 선택지 — JobCategory 전부, 표시명은 core 의 JOB_CATEGORY_LABELS */
    jobCategoryOptions: Object.values(JobCategory).map((value) => ({
      value,
      label: JOB_CATEGORY_LABELS[value],
    })),
    wardShowForm: false,
    wardSaving: false,
    wardFormMode: "create" as "create" | "edit",
    // wardCareType: 병동 종류(배치 기준 표 선택 키). "" = 미지정 — <select> x-model 호환.
    wardForm: {
      id: "",
      code: "",
      name: "",
      description: "",
      isActive: true,
      wardCareType: "",
      linkedTeamId: "",
      bedCount: "",
      displayOrder: "",
    },
    // 수정 모달의 [저장] 활성화 조건(수정 내역 있을 때만) 판단용 원본 스냅샷
    wardFormOriginal: {
      name: "",
      description: "",
      isActive: true,
      wardCareType: "",
      linkedTeamId: "",
    },
    // 병동 관리 표 안 수정 — 한 번에 한 줄(id). 원본(original)과 비교해 [저장]을 켠다
    wardInlineEdit: {
      id: "",
      form: { ...EMPTY_WARD_INLINE_FORM },
      original: { ...EMPTY_WARD_INLINE_FORM },
    },

    /* ── leave reason custom list (org.settings.leaveReasons) ── */
    leaveReasons: [] as {
      id: string;
      name: string;
      code?: string;
      color?: string;
      description?: string;
      deductionKind?: "rate" | "hourly";
      deductionRate?: number;
      defaultMinutes?: number;
      minUnitMinutes?: number;
      minHours?: number;
      maxHours?: number;
      payKind?: "paid" | "unpaid";
      deductsAnnualBalance?: boolean;
      allowedShiftIds?: string[];
      allowedTeamIds?: string[];
      allowOnOffDay?: boolean;
      schedulerEnabled?: boolean;
      status?: "active" | "inactive";
      /* 2026-07 개편 필드 — 정본 스키마: @hereby/core leave-policy.schema.ts */
      usageMinutes?: number;
      countsAsAttendance?: boolean;
      paidHours?: boolean;
      breakChoiceAllowed?: boolean;
      breakMinutes?: number;
      specialLeave?: boolean;
      privacyMasked?: boolean;
      maskedName?: string;
      evidenceRequired?: boolean;
      /** 신청 사유 추천 리스트 — undefined = 미관리(기본 칩 노출), [] = 관리 후 비움(칩 없음) */
      reasonPresets?: string[];
      /** @deprecated 레거시 — deductionRate로 대체. 미마이그레이션 조직 표시용 */
      hours?: number;
      /** @deprecated 레거시 — deductionRate로 대체 */
      deductionDays?: number;
    }[],
    showLeaveReasonModal: false,
    editingLeaveReasonId: "" as string,
    /** 휴가 유형 코드 최대 길이 — 근무표 셀·배지 폭에 맞춘 상한.
     *  입력 검증·저장 차단·목록 말줄임이 전부 이 값을 본다(숫자를 흩뿌리지 않는다). */
    leaveReasonCodeMax: 3,
    /* 2026-07 개편 폼 — 차감 방식(rate/hourly)·유급·근무유형·적용팀 축은 UI에서 제거.
       연차 차감 Y/N + 시간 설정(분, 8h=1일)이 새 축이고, 저장 시 deductionRate를 파생해
       기존 차감 엔진(leave-deduction.calc)과 정합을 유지한다. */
    leaveReasonForm: {
      name: "" as string,
      code: "" as string,
      color: "" as string,
      description: "" as string,
      /** 연차 차감 Y/N — 저장 필드는 deductsAnnualBalance */
      annualDeduction: true,
      /** 신청 단위 — "day": 시간 설정값만큼 차감(저장 deductionKind=rate) /
       *  "hourly": 직원이 신청 때 사용 시간을 30분 단위로 고름(저장 deductionKind=hourly).
       *  2026-07 개편 때 UI 에서 빠져 항상 rate 로 저장되던 것을 2026-09-14 되살림 —
       *  works 시간 단위 신청 UI(사용 시간·"2시간 = 0.25일" 미리보기)는 hourly 유형에만 뜬다. */
      requestUnit: "day" as "day" | "hourly",
      /** 시간 설정(분) — 8시간(480)=1일. hourly 면 신청 폼 초기값(defaultMinutes) */
      usageMinutes: 480,
      allowOnOffDay: false,
      schedulerEnabled: true,
      countsAsAttendance: true,
      paidHours: true,
      breakChoiceAllowed: false,
      /** 휴게 시간 — 시·분 분리 입력(저장은 분 단위 breakMinutes) */
      breakHoursPart: 0,
      breakMinutesPart: 30,
      specialLeave: false,
      privacyMasked: false,
      maskedName: "" as string,
      evidenceRequired: false,
      status: "active" as "active" | "inactive",
      /** 신청 사유 추천 리스트 — Works 휴가 신청 폼의 사유 퀵 선택 칩(저장 reasonPresets).
       *  미관리(undefined) 유형은 열 때 DEFAULT_LEAVE_REASON_PRESETS를 프리필한다 —
       *  직원이 지금 보고 있는 칩과 편집 시작값을 일치시키기 위해서다. 비우고 저장하면
       *  []가 저장되고 칩이 사라진다(undefined와 []의 의미가 다르다). */
      reasonPresets: [] as string[],
    },
    /** 추천 사유 입력 버퍼 — [추가]로 reasonPresets에 옮긴다 */
    leaveReasonPresetDraft: "" as string,
    /** 추가 설정 아코디언 열림 상태 — 폼을 열 때마다 닫힘으로 초기화 */
    leaveReasonExtraOpen: false,
    // 휴가 유형 상태 필터 (전체/활성/비활성)
    leaveReasonFilter: { status: "" as "" | "active" | "inactive" },

    /* ── 연차 정책 플래그 (org.settings.leavePolicy) ──
       기본값·항목 정의는 파일 상단 LEAVE_POLICY_* 상수. 병원이 바꾼 값만 override로 저장된다.
       allowNegativeBalance는 enforceBalanceLimit와 같은 축의 반대 표현이라 UI에서 빼고
       (저장 시 !enforceBalanceLimit로 동기화) 스키마 필드는 하위호환으로만 남긴다 —
       백엔드가 `enforceBalanceLimit || !allowNegativeBalance`로 읽기 때문에
       둘이 어긋나면 라벨상 모순인 조합이 만들어진다. */
    leavePolicyForm: { ...LEAVE_POLICY_DEFAULTS },
    /** 마지막으로 서버에 저장된 값 — "차단을 새로 켰는가" 판정 기준 */
    leavePolicySaved: { ...LEAVE_POLICY_DEFAULTS },
    leavePolicySaving: false,
    /** 상시근로자 5인 이상 여부(settings.fiveOrMore, 기본 true) — true면
     *  excludeWeekendsHolidays가 법정 강제 ON이라 화면에서도 끌 수 없다(근기법 §55②). */
    leavePolicyFiveOrMore: true,
    /** 재직 직원 수 — "5인 미만" 설정인데 실제 5인 이상이면 경고 표시용 */
    leavePolicyActiveEmployeeCount: 0,
    leavePolicyChoices: LEAVE_POLICY_CHOICES,
    leavePolicyToggles: LEAVE_POLICY_TOGGLES,
    leavePolicyPending: LEAVE_POLICY_PENDING,

    /* 직원 앱 민감 화면 캡처 기록(MED-973) — 설정 탭 「캡처 기록」 모달 */
    showScreenCaptureLog: false,
    screenCaptureLogLoading: false,
    screenCaptureLogFilter: { from: "", to: "", screen: "", kind: "", search: "" },
    screenCaptureLog: { items: [], totalCount: 0, page: 1, limit: 50 } as {
      items: {
        id: string;
        employeeId: string;
        employeeName: string;
        employeeNumber: string;
        kind: string;
        screen: string;
        occurredAt: string | Date;
        platform: string;
        osVersion: string | null;
        appVersion: string | null;
      }[];
      totalCount: number;
      page: number;
      limit: number;
    },

    /* 정책 저장 전 영향 안내 팝업 (previewPolicyImpact 결과) */
    showLeavePolicyImpact: false,
    leavePolicyImpact: null as {
      checks: ("overlap" | "balance")[];
      blocked: {
        id: string;
        employeeName: string;
        employeeNumber: string;
        startDate: string;
        endDate: string;
        leaveTypeLabel: string;
        reason: "overlap" | "balance";
        detail: string;
      }[];
      pendingTotal: number;
      truncated: boolean;
    } | null,

    /* ── 공휴일 (organization_holidays + 국가공휴일 병합) ── */
    holidayYear: new Date().getFullYear(),
    holidayItems: [] as {
      id: string | null;
      date: string;
      name: string;
      source: "national" | "organization";
      /** 이 조직에서 유급휴일로 적용 중인가 — 국가 공휴일만 false 가능(holidayOptOuts) */
      applied: boolean;
      /** 매년 반복(MM-DD) 미적용에 걸린 날짜인가 */
      recurringOptOut: boolean;
    }[],
    holidayLoading: false,
    showHolidayModal: false,
    holidayForm: { date: "", name: "" },
    holidaySaving: false,
    /** 연도별 준법 상태(complianceStatus) — 빈 달력 경고 배너용 */
    holidayCompliance: null as {
      year: number;
      nationalCount: number;
      organizationCount: number;
      emptyCalendarWarning: boolean;
    } | null,
    // 코드 라벨 색상 팔레트 (Hereby sign 기준 15색). 새 유형 생성 시 순차 자동 선택 후 순환.
    leaveTypeColors: [
      "#F66014",
      "#A3C75A",
      "#A0D3F9",
      "#F2C7E3",
      "#26426B",
      "#2463EB",
      "#826AED",
      "#E63224",
      "#0F6625",
      "#A2812D",
      "#F09C00",
      "#00A19D",
      "#B55239",
      "#713838",
      "#B8B8B8",
    ] as string[],
    salaryGradePage: 1,
    salaryGradePageSize: 20,
    salaryGradeYears: [] as number[],
    salaryGradeCategories: [] as { id: string; name: string }[],
    showCategoryModal: false,
    categoryModalMode: "add" as "add" | "edit",
    categoryForm: { id: "", name: "" },
    // 직종 관리 modal state
    showCategoryManagerModal: false,
    categoryManagerList: [] as {
      id: string;
      name: string;
      isNew?: boolean;
      editing?: boolean;
      editName?: string;
    }[],
    categoryManagerNewName: "",
    categoryManagerDirty: false,
    allowanceItemCodes: [] as Record<string, unknown>[],
    deductionItemCodes: [] as Record<string, unknown>[],

    // Certifications
    certifications: [] as Record<string, unknown>[],
    certSummary: [] as Record<string, unknown>[],
    certFilter: {
      type: "" as string,
      teamIds: [] as string[],
      search: "" as string,
      deadlineFrom: "" as string,
      deadlineTo: "" as string,
    },
    certDatePreset: "" as string,

    showCertModal: false,
    editingCertId: "" as string,
    /** 수정 모달용 직원 표시 라벨 — 직원이 비활성/필터로 목록에 없어도 이름이 보이도록. */
    certEditEmployeeLabel: "" as string,
    certForm: {
      employeeId: "",
      type: "nursing" as string,
      registeredAt: "",
      notes: "",
    },
    /* 유형 동적 필드 값 { [fieldKey]: value } — 기본·커스텀 필드 공용 단일 모델.
       저장 시 기본 필드(license_name 등)는 고정 컬럼으로, 커스텀 필드는 fieldValues로 분리된다. */
    certFieldVals: {} as Record<string, string>,
    /* 파일형 필드의 표시용 파일명 { [fieldKey]: fileName } (업로드 시 file.name, 기존 건은 basename) */
    certFileNames: {} as Record<string, string>,
    certSaving: false,
    certFileUploading: false,
    // 제출 토글(미등록 시 노출). certForm 밖에 둬 토글 변경이 formDirty(수정 버튼)에 영향을 주지 않게 한다.
    certSubmitted: false,
    // 수정 모달에서 이미 등록된 자격증/면허인지. true면 토글 대신 '편집·삭제' 버튼 노출 + 상세필드 읽기전용.
    certRegistered: false,
    // 편집(연필) 클릭 후 잠금해제(편집 중) 상태. true면 토글 대신 '삭제'만 노출(토글 OFF는 변경이 아니므로 숨김).
    certEditing: false,
    // 자격유형 select의 직전 확정값 — 등록된 자격증에서 유형 변경을 취소할 때 원래 값으로 되돌리는 데 쓴다.
    certTypePrev: "",
    // 문서 결재 미처리 건수(LNB 뱃지) = pending 문서 + pending 증명서
    docApprovalPending: 0,

    employeeHistory: { leaves: [], resignations: [] } as {
      leaves: Record<string, unknown>[];
      resignations: Record<string, unknown>[];
    },

    // Employee attendance modal — 해당 월 단위 조회 (근태관리모달 §2.2).
    // Secom 원시 이벤트는 표에서 히든이지만 출퇴근 지문 대조 툴팁용으로 계속 로드한다.
    showAttendanceModal: false,
    attendanceEmployee: null as Record<string, unknown> | null,
    /** 근태 수정 창 직원의 오늘 근무형태(서버 판정) — 듀티 표시 판정용 */
    attendanceEffectiveShift: null as {
      leaveDeductionMode?: string | null;
      shiftName?: string | null;
      sourceLabel?: string | null;
    } | null,
    attendanceRecords: [] as Record<string, unknown>[],
    secomEvents: [] as Record<string, unknown>[],
    // 근태 조회 중 여부 — 새로고침 아이콘 회전 모션 토글에 사용
    attendanceLoading: false,
    // 월 선택 팝오버 열림 상태 — 모달 Esc 핸들러가 "팝오버 먼저 닫기"를 판단하려면
    // 컴포넌트 스코프에 있어야 한다(팝오버 내부 x-data에 두면 모달 루트에서 못 읽음).
    // → Esc 1번: 팝오버만 닫힘 / 팝오버 닫힌 뒤 Esc: 모달 닫힘.
    mpOpen: false,
    attendanceMonth: "" as string, // "YYYY-MM"
    attendanceFilter: { dateFrom: "", dateTo: "" },
    // 승인된 외근 기록 (workDate "YYYY-MM-DD" 키) — 근태에서는 확인만 제공 (외근 기획서 §2.8, 2차)
    attendanceOffsiteByDate: {} as Record<string, Record<string, unknown>>,
    /**
     * 근태 관리 모달 보기 — 일별 근태표(table) / 전체 근무기록(log).
     * 전체 근무기록은 attendance.workLog 가 출퇴근·연장근무·외근을 일별 타임라인으로 합친 결과
     * (온보딩 9/16: 조기 퇴근 + 외근 시 그날 총 근무시간을 한 화면에서). 월을 바꾸면 같이 다시 불러온다.
     */
    attendanceView: "table" as "table" | "log",
    attendanceWorkLog: null as WorkLogView | null,
    attendanceWorkLogLoading: false,

    // 근태 수정 모달 (§2.3.2) — 날짜 1건 기준, 탭 2개(출퇴근 수정 / 연장·야간 조회)
    showAttendanceEditModal: false,
    attendanceEditRecord: null as Record<string, unknown> | null,
    attendanceEditTab: "times" as "times" | "history",
    attendanceEditForm: {
      checkIn: "",
      checkOut: "",
      reason: "",
      hours: {
        regular: 0,
        overtime: 0,
        night: 0,
        nightOvertime: 0,
        holidayRegular: 0,
        holidayOvertime: 0,
        holidayNight: 0,
        holidayNightOvertime: 0,
        break: 0,
      } as Record<string, number>,
    },
    attendanceEditOvertimes: [] as Record<string, unknown>[],
    attendanceEditSaving: false,

    // Org edit mode — 수정은 묶음 단위(orgEditSection = 그 묶음 id). 저장은 폼 전체를 보내지만
    // 폼은 지금 값으로 채워지므로 다른 묶음 값은 그대로 다시 저장된다.
    orgEditMode: false,
    orgEditSection: "",
    // 묶음 접기(id → 접힘). 기본은 모두 펼침 — 접힌 묶음에서 「수정」을 누르면 펼친다(startOrgEdit)
    orgSectionCollapsed: {} as Record<string, boolean>,
    // 조직 설정 검색(앵커 줄 오른쪽) — 맞는 행만 남긴다(applyOrgSettingsSearch). 결과가 없으면 빈 안내
    orgSettingsQuery: "",
    orgSearchEmpty: false,
    orgForm: {
      name: "",
      legalName: "",
      taxId: "",
      phone: "",
      email: "",
      fax: "",
      website: "",
      representativeName: "",
      sealImage: "",
      logo: "",
    },
    orgSettingsForm: {
      payDay: 25,
      cutoffDay: 21,
      /** 시트 — 신규 입사자 자동 추가(기본 꺼짐) */
      sheetsAutoAddNewEmployees: false,
      sheetsScheduledChangeApply: "confirm" as "confirm" | "auto",
      /** 직원 앱 AI 어시스턴트(MED-800) 켬/끔 · 마스터 플래그(플랫폼) 여부 — 꺼져 있으면 토글 비활성 */
      employeeAppAi: false,
      aiMasterEnabled: false,
      weeklyLimit: 52,
      dailyStandard: 8,
      weeklyLimitAlert: true,
      attendanceMethod: "app" as "app" | "secom" | "manual",
      /** 세콤 출근·퇴근 버튼 태그만 근태로(MED-970) */
      secomPunchTypesOnly: false,
      allowedIpsText: "",
      defaultCheckIn: ATTENDANCE_DEFAULT_CHECK_IN,
      defaultCheckOut: ATTENDANCE_DEFAULT_CHECK_OUT,
      // 신청 시간 단위(MED-901) — 직원 앱 연장·외근 신청/보고·시간 단위 휴가의 시각 선택 간격
      requestTimeStepMinutes: DEFAULT_REQUEST_TIME_STEP_MINUTES as RequestTimeStepMinutes,
      /** 연장 유형 단순화(MED-965) */
      simplifiedOvertimeType: false,
      // 직원 앱 화면 캡처 제한 — 기본 켬(설정 키가 없으면 켠 것으로 본다)
      screenCaptureProtection: true,
      signName: "",
      signEmail: "",
      messageSenderName: "",
      messageSenderEmail: "",
      selfPasswordReset: false,
      twoFactorRequired: false,
      // 비밀번호 정책 — 임시 비밀번호 발급 형식·길이 (system-admin 프로비저닝과 동일 포맷).
      pwFormat: "mixed" as "numbers" | "lower_numbers" | "mixed" | "fixed",
      pwFixedValue: "",
      // 임시 비밀번호 유효기간(일, 1~90) — 비우면 기한 없음(MED-922)
      pwValidityDays: "" as string | number,
      pwLength: 8,
      // 일반(직접 설정) 비밀번호 정책 — 비밀번호 변경 시 서버 검증 규칙.
      userPwMinLength: 8,
      userPwUpper: true,
      userPwLower: true,
      userPwNumber: true,
      userPwSpecial: true,
      selfEditableFields: {
        phone: false,
        address: true,
        emergencyContact: true,
        bankAccount: false,
        profilePicture: true,
      } as Record<string, boolean>,
      // 메신저 연락처 프로필에 전화번호를 보일지(병원 일괄) — settings.contactSharing.phone, 기본 꺼짐.
      sharePhone: false,
      // 직원 앱 첫화면 레이아웃 — 편집 진입 시 resolveEmployeeHomeLayout 로 채운다(전부 노출이 기본).
      employeeHome: resolveEmployeeHomeLayout(undefined) as EmployeeHomeLayout,
      /** 용어 표시 — 「역할」을 부르는 이름. 빈 문자열 = 기본 */
      termRole: "",
      // 시트 식별 열의 근로계약 값 — 한 달에 계약이 둘일 때 합산 기준(MED-703). 기본 prorate
      sheetContractBasis: "prorate" as "prorate" | "last_contract",
    },
    orgSaving: false,
    defaultShiftId: "" as string,

    // 자격증/면허 유형 커스텀 설정 (문서 양식 > 자격증/면허 탭)
    certTypeConfigs: [] as Record<string, unknown>[],
    showCertTypeModal: false,
    editingCertTypeId: "" as string,
    certTypeForm: {
      name: "",
      description: "",
      isActive: true,
      // 동적 필드: { key, name, type: text|date|file, description, required, defaultValue? }
      fields: [] as Record<string, unknown>[],
    },

    // Document templates
    docTemplates: [] as Record<string, unknown>[],
    /** 목록을 한 번이라도 받아 왔는가 — 받기 전엔 모든 결재 서식이 「기본 서식 사용 중」으로 잘못 보이므로 줄을 그리지 않는다 */
    docTemplatesLoaded: false,
    docTemplateVariables: [] as { key: string; label: string; example: string }[],
    expandedDocTemplate: "" as string,
    showDocTemplateForm: false,
    editingDocTemplateId: "" as string,
    docTemplatePreviewMode: false,
    docTemplatePreviewHtml: "",
    /* 양식 팝업 종류 — addCert: 새 증명서(시작 고르기 → 작성) · edit: 저장된 양식 수정 ·
       changePreset: 목록의 「기본 서식 사용 중」 줄을 우리 병원 서식으로 저장(코드는 기본 서식 그대로) */
    docTemplateMode: "addCert" as "addCert" | "edit" | "changePreset",
    docTemplateStep: "write" as "start" | "write",
    /** 「어떤 증명서에서 시작할까요?」 고른 값 — "blank" 또는 목록 줄 key */
    docTemplateStartKey: "blank" as string,
    /** 양식 팝업 전체화면 — 카드가 화면을 꽉 채우고 편집기가 화면 높이만큼 늘어난다. 종료는 화면 상단 가운데 버튼 · Esc */
    docTemplateModalFull: false,
    /** 작성 화면 왼쪽 패널 — 정보(이름 · 설명 · 첨부) · 자동 정보 · 신청 항목(증명서만)을 위아래로 이어 두고,
        위 탭은 그 묶음으로 스크롤하는 앵커. 이 값은 지금 보고 있는 묶음(스크롤 스파이) */
    docTemplatePanelTab: "info" as "info" | "vars" | "fields",
    /** 탭을 눌러 스크롤하는 동안은 스파이가 켜진 탭을 바꾸지 않는다 — 짧은 마지막 묶음이 맨 위까지 못 올라와도 누른 탭이 켜진 채로 */
    docPanelPinned: false,
    docTemplateVarSearch: "",
    /** 2단계 부제에 쓰는 시작 증명서 이름(고른 순간 고정 — 목록이 다시 불러와져도 바뀌지 않게) */
    docTemplateStartName: "" as string,
    docTemplateForm: {
      code: "",
      // 증명서: 직원 앱 공개 여부(끄면 비공개). 결재 서식은 늘 true
      isActive: true as boolean,
      name: "",
      description: "",
      bodyTemplate: "",
      cssTemplate: "",
      // 결재 서식 전용(사직서·휴직신청서·인사발령품의서): 직원 앱 제출 시 첨부 필수 여부(MED-902)
      attachmentRequired: true as boolean,
      // 증명서 양식 전용: 신청 시 받을 항목 — 카탈로그 전 항목을 편집행(사용/필수)으로 든다
      requestFields: [] as {
        key: string;
        label: string;
        use: boolean;
        required: boolean;
        alwaysOn: boolean;
      }[],
    },
    // Dirty-check baselines captured when the modal opens: meta fields
    // (name/description/requestFields) and the editor body (normalized). See docTemplateDirty().
    docTemplateFormSnapshot: "" as string,
    docTemplateBodyBaseline: "" as string,
    docEditorReady: false,
    docEditorFullscreen: false,

    // Item code modal
    showItemCodeModal: false,
    itemCodeType: "allowance" as "allowance" | "deduction",
    editingItemCodeId: "" as string,
    itemCodeForm: { code: "", name: "", displayOrder: 0 },
    itemCodeSaving: false,

    // Salary grade modal
    showGradeModal: false,
    editingGradeId: "" as string,
    gradeForm: {
      categoryId: "" as string,
      grade: 1,
      baseSalary: 0,
      hobongAmount: 0,
      totalSalary: 0,
      annualSalary: 0,
      effectiveYear: new Date().getFullYear(),
      isActive: true,
      notes: "",
    },
    gradeSaving: false,

    // ── 근무 설정: 근무 그룹(ShiftGroup) → 근무 유형(Shift) ──
    shiftGroups: [] as ShiftGroupItem[],
    // 근무 설정 목록 상태 필터 — 기본 「활성」(역할 관리 PR 665 와 같은 방식, CS-LOC-260915-0002).
    // 병원 설정 > 근무시간 서브탭과 근태 현황 > 유형·기준 관리 모달 > 근무 설정 섹션이 같은 필터를 쓴다.
    shiftStatusFilter: "ACTIVE" as "" | "ACTIVE" | "INACTIVE",

    // 근무 그룹 모달
    showShiftGroupModal: false,
    editingGroupId: "" as string,
    shiftGroupForm: {
      name: "",
      description: "",
      schedulerEnabled: false,
      isActive: true,
    },
    shiftGroupSaving: false,

    // ── 직무 마스터(job_duties) — 설정 › 직무 설정(근태 설정 팝업 「직무 설정」도 같은 파셜). 직무마다 기본 근무(그룹 › 형태)를 잇는다.
    //    근무 설정(근무 그룹·형태 = 시간 정의)과 축이 달라 화면을 따로 둔다 — 여기는 「누가 어떤 근무를 쓰나」.
    jobDuties: [] as JobDutyItem[],
    jobDutiesUnassignedCount: 0,
    // 직무 설정 자체의 상태 필터 — 근무 설정의 shiftStatusFilter 와 별개(화면이 다르다). 기본 「활성」.
    jobDutyStatusFilter: "ACTIVE" as "" | "ACTIVE" | "INACTIVE",
    // 「목록 | 매핑표」 — 목록은 직군별로 묶어 훑고, 매핑표는 행마다 근로형태(그룹) › 근무형태를 골라 바로 저장한다(배포 후 xlsx 세팅용).
    jobDutyView: "list" as "list" | "matrix",
    // 매핑표에서 저장 중인 직무 id — 그 행의 select 를 잠근다(연타 방지)
    jobDutyMatrixSaving: "" as string,
    showJobDutyModal: false,
    editingJobDutyId: "" as string,
    jobDutyForm: {
      name: "",
      description: "",
      jobCategory: "" as string,
      shiftGroupId: "" as string,
      shiftId: "" as string,
      // 기본 근로형태(job_duties.defaultWorkPatternId) — 직원 등록에서 이 직무를 고르면 먼저 선택된다
      defaultWorkPatternId: "" as string,
      isActive: true as boolean | string,
    },
    jobDutySaving: false,
    /**
     * 근무 유형 폼 「기본으로 쓰는 직무」 — 이 형태를 기본 근무로 쓰는 직무 id. 「대표로 쓰는 팀」과 같은 이유로 shiftTypeForm 밖에 둔다
     * (저장 대상이 job_duties 쪽). Initial 은 열었을 때 값 — 바뀐 경우에만 setDefaultShift 를 부른다.
     */
    typeFormJobDutyIds: [] as string[],
    typeFormJobDutyIdsInitial: [] as string[],
    // ── 근로형태 마스터(work_patterns) — 설정 › 근로형태(근태 설정 팝업 「근로형태」도 같은 파셜). 포괄임금의 월 시간 기준
    //    (월 소정 · 고정연장 · 고정야간 → 임금산정시간). 근무형태(하루의 출퇴근 시각)·직무(누가 어떤 근무를 쓰나)와 축이 다르다.
    workPatterns: [] as WorkPatternItem[],
    workPatternMultipliers: { overtime: 1.5, night: 0.5, holiday: 1.5 },
    workPatternStatusFilter: "ACTIVE" as "" | "ACTIVE" | "INACTIVE",
    showWorkPatternModal: false,
    editingWorkPatternId: "" as string,
    workPatternForm: {
      name: "",
      description: "",
      jobDutyId: "" as string,
      shiftGroupId: "" as string,
      shiftId: "" as string,
      // 월 소정근로가 근무 유형 기준 자동값인가 — 그룹/형태를 바꾸면 자동값으로 따라가고, 직접 고치면 false 로 굳는다(MED-704)
      standardAuto: true as boolean,
      // 숫자 input 은 문자열로 온다 — 저장 때 Number() 로 바꾼다
      monthlyStandardHours: "" as string | number,
      monthlyOvertimeHours: "" as string | number,
      monthlyNightHours: "" as string | number,
      monthlyHolidayHours: "" as string | number,
      dailyOvertimeHours: "" as string | number,
      dailyNightHours: "" as string | number,
      monthlyWorkingDays: "" as string | number,
      isActive: true as boolean | string,
    },
    workPatternSaving: false,
    // 파생값 재계산 모달 — 미리보기(dryRun) 결과를 담는다. 근로형태 시간을 고친 뒤와 목록의 재계산 버튼에서 연다
    showWorkPatternRecompute: false,
    workPatternRecompute: {
      workPatternId: "" as string,
      title: "",
      loading: false,
      applying: false,
      total: 0,
      changedCount: 0,
      noContractCount: 0,
      items: [] as WorkPatternRecomputeItem[],
    },

    // 근무 유형 모달
    showShiftTypeModal: false,
    editingShiftId: "" as string,
    typeFormGroupId: "" as string,
    typeFormGroupName: "" as string,
    // 이 근무 유형이 속한 그룹의 '스케줄러 연동' 여부 — 코드·라벨색 노출/필수 판정.
    typeFormGroupSchedulerEnabled: false,
    /**
     * 근무 유형 폼 「대표로 쓰는 팀」 — 이 형태를 대표 근무로 쓰는 팀 id. shiftTypeForm 과 따로 두는 이유:
     * shiftTypeForm 은 통째로 shift.create/update 에 펼쳐 보내는데, 이 값은 팀 쪽(teams.shiftId)에 저장된다.
     * typeFormTeamIdsInitial 은 열었을 때 값 — 바뀐 경우에만 저장 호출을 보낸다.
     */
    typeFormTeamIds: [] as string[],
    typeFormTeamIdsInitial: [] as string[],
    shiftTypeForm: {
      code: "",
      codeColor: SHIFT_CODE_COLORS[0] as string,
      name: "",
      description: "",
      startTime: "09:00",
      endTime: "18:00",
      isOvernight: false,
      workHours: 8,
      breakHours: 1,
      overtimeBreakHours: 0,
      isActive: true,
      applicableDays: {
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: false,
        sunday: false,
      } as Record<string, boolean>,
      /**
       * 요일별 시간 — 기본 시간과 다른 요일만 담는다(월수금 09:00–18:00 / 화목 09:00–15:00).
       * 키가 있으면 그 요일은 「다른 시간」이고, 값은 네 필드를 모두 가진 완전한 한 벌이다
       * (부분 덮어쓰기는 폼에서 만들지 않는다 — 화면에서 무엇이 상속인지 읽기 어렵다).
       */
      dayOverrides: {} as Record<
        string,
        { startTime: string; endTime: string; workHours: number; breakHours: number }
      >,
      worksOnHolidays: false,
      leaveDeductionMode: "fixed_days" as "fixed_days" | "duty",
    },
    shiftTypeSaving: false,
    /** 코드 라벨 색상 팔레트 (UI 칩) */
    shiftCodeColors: SHIFT_CODE_COLORS as readonly string[],

    /* ── 직원 앱 배포 온보딩 배너(MED-795) ── */
    onboardingBanner: { visible: false, done: 0, total: 0, rate: 0, targetRate: 80, step: "" },
    /** 직원 앱 도입이 끝났는가(목표 달성·완료 처리) — LNB 「시작하기」 숨김 · 설정 탭 노출을 가른다 */
    appAdoptionFinished: false,

    /* ── filters ── */
    employeeFilter: {
      search: "",
      statuses: [] as string[],
      teamIds: [] as string[],
      roleIds: [] as string[],
      // 근무형태 필터 — 그룹 체크(그룹 전체) + 형태 체크. 근무 설정 「직원 N명」 링크도 여기로 온다.
      shiftGroupIds: [] as string[],
      shiftIds: [] as string[],
      hireDateFrom: "",
      hireDateTo: "",
      // 직무 · 병동 — 팀 관리 명단과 같은 기준("__unset" = 직무 미입력, "__none" = 병동 미배정)
      jobDuties: [] as string[],
      wardIds: [] as string[],
      // 앱 상태(온보딩 단계) — 직원 앱 배포 온보딩 화면의 「미완료자 보기」 도 여기로 온다
      appStages: [] as string[],
      // 근무형태 드롭다운의 판정 필터 — 활성 소속 줄의 근무 그룹이 2개 이상(MED-924) · 판정 근무 ≠ 근로형태/직무 기본(MED-923)
      multipleShiftGroups: false,
      shiftDiffersFromDefault: false,
    },
    /* ── sort (server-side via pagination.sortBy/sortOrder) ── */
    employeeSort: {
      by: "" as "" | "employeeNumber" | "lastName" | "email" | "hireDate" | "status" | "jobDuty",
      order: "asc" as "asc" | "desc",
    },
    teamFilter: { search: "", status: "", teamId: "", roleId: "" },

    /* ── organization (team master-detail) ── */
    orgSelected: { type: null as null | "team" | "workspace" | "ward", id: null as string | null },
    /** 이 세션에서 팀 관리 선택이 (복원이든 클릭이든) 정해졌는가 — 처음 진입 때만 저장값을 복원한다. */
    orgSelectionRestored: false,
    /** 팀장 없는 팀의 단톡방 입장 대기 — 선택한 팀 것만, 시스템 관리자에게만 채워진다(서버가 그 밖에는 빈 목록). */
    orgRoomJoin: {
      teamId: null as string | null,
      conversationId: null as string | null,
      pending: [] as { employeeId: string; name: string }[],
    },

    /** 트리뷰 너비 (px) — localStorage 로 영속. 사용자가 드래그 핸들로 조절. */
    orgTreeWidth: ((): number => {
      try {
        const v = Number.parseInt(localStorage.getItem("admin:orgTreeWidth") || "", 10);
        return Number.isFinite(v) && v >= 180 && v <= 480 ? v : 240;
      } catch {
        return 240;
      }
    })(),
    /** <768(md 미만) 에서 트리를 서랍으로 열었는지 — 넓은 화면에서는 트리가 늘 옆에 있어 쓰이지 않는다.
     *  항목을 고르면(selectOrg*) 닫힌다. LNB 드로어(lnbDrawerOpen)와 같은 방식. */
    orgTreeOpen: false,
    /** 모달(.ds-modal-backdrop)이 떠 있는가 — 서랍의 Esc 양보 판정용.
     *  서랍 안 「팀 추가」로 모달을 띄우고 Esc 를 누르면 모달과 서랍이 함께 닫혔다. 서랍 핸들러를
     *  **캡처 단계**에서 돌려 모달이 닫히기 전에 이 값을 읽고, 떠 있으면 서랍은 그대로 둔다
     *  (버블 단계면 모달 쪽 핸들러가 먼저 닫아 버려 「모달 없음」으로 읽힌다). */
    anyModalOpen(): boolean {
      return Array.from(document.querySelectorAll<HTMLElement>(".ds-modal-backdrop")).some(
        (el) => getComputedStyle(el).display !== "none",
      );
    },
    orgTreeResizing: false,
    /** Drag 중에 등록·해제할 mousemove/mouseup 핸들러 — leak 방지용 참조. */
    orgTreeMoveHandler: null as ((e: MouseEvent) => void) | null,
    orgTreeUpHandler: null as (() => void) | null,

    startOrgTreeResize(initialEvent: MouseEvent) {
      // Capture starting baselines so the drag math is stable even if Alpine
      // re-renders happen mid-drag.
      const startX = initialEvent.clientX;
      const startWidth = this.orgTreeWidth;
      this.orgTreeResizing = true;
      const onMove = (e: MouseEvent) => {
        const dx = e.clientX - startX;
        const next = Math.max(180, Math.min(480, startWidth + dx));
        this.orgTreeWidth = next;
      };
      const onUp = () => {
        this.orgTreeResizing = false;
        try {
          localStorage.setItem("admin:orgTreeWidth", String(this.orgTreeWidth));
        } catch {
          /* ignore */
        }
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
        this.orgTreeMoveHandler = null;
        this.orgTreeUpHandler = null;
      };
      this.orgTreeMoveHandler = onMove;
      this.orgTreeUpHandler = onUp;
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
      initialEvent.preventDefault();
    },

    /** 트리뷰 우클릭 메뉴 — 한 번에 하나만 열림. team===null 이면 닫힌 상태. */
    orgCtxMenu: {
      open: false,
      x: 0,
      y: 0,
      team: null as Record<string, unknown> | null,
    },

    openOrgCtxMenu(team: Record<string, unknown>, e: MouseEvent) {
      e.preventDefault();
      e.stopPropagation();
      // 화면 밖으로 메뉴가 잘리지 않도록 좌표를 viewport 안으로 클램프.
      const menuWidth = 140;
      const menuHeight = 76;
      const x = Math.min(e.clientX, window.innerWidth - menuWidth - 8);
      const y = Math.min(e.clientY, window.innerHeight - menuHeight - 8);
      this.orgCtxMenu = { open: true, x, y, team };
    },

    closeOrgCtxMenu() {
      // escape.window 는 메뉴가 닫혀 있어도 들어온다 — 닫힌 상태에서 객체를 재대입하면
      // 바인딩만 헛돌게 하므로 조기 반환.
      if (!this.orgCtxMenu.open) {
        return;
      }
      this.orgCtxMenu = { open: false, x: 0, y: 0, team: null };
    },

    /* ── bulk selection ── */
    selectedEmployees: [] as string[],
    selectAllEmployees: false,
    bulkStatus: "",
    bulkTeamId: "",
    bulkRoleId: "",
    // 근무 배정 일괄 변경 — 그룹만 고르면 형태 미지정으로, 형태를 고르면 그 형태의 그룹으로 맞춰진다.
    bulkShiftGroupId: "",
    bulkShiftId: "",

    /* ── edit employee modal ── */
    showEditEmployee: false,
    showAdminConfirm: false,
    editingEmployee: null as Record<string, unknown> | null,
    editMemberships: [] as {
      memberId: string;
      teamId: string;
      teamName: string;
      roleId: string;
      originalRoleId: string;
    }[],
    showAddTeamRow: false,
    addTeamForm: { teamId: "", roleId: "" },
    editForm: {
      lastName: "",
      firstName: "",
      email: "",
      phone: "",
      countryCode: "+82",
      employeeNumber: "",
      hireDate: "",
      birthDate: "",
      residentNumber: "",
      status: "",
      shiftId: "",
      attendanceMethod: "" as "" | "app" | "secom" | "manual",
      isSystemAdmin: false,
      phoneOtpEnabled: false,
      newPassword: "",
      confirmPassword: "",
      passwordMode: "sms" as "sms" | "manual",
    },

    /* ── edit team modal ── */
    showEditTeam: false,
    /** 총괄 팀장(조직 전체, MED-946) — 팀 관리 워크스페이스 헤더가 보이고, 모달로 지정한다 */
    showOrgLeaders: false,
    orgLeaderIds: [] as string[],
    orgLeaderForm: { leaderIds: [] as string[] },
    orgLeaderSaving: false,
    editingTeam: null as Record<string, unknown> | null,
    editTeamMembers: [] as Record<string, unknown>[],
    teamForm: {
      name: "",
      description: "",
      /** 팀장 전원(대표 먼저) — 한 팀에 여러 명(MED-946). 저장은 leaderIds 로 보낸다. */
      leaderIds: [] as string[],
      isActive: true,
      parentTeamId: "",
      // 연결 병동 — 비우면 연결 없음. 병동 하나에 팀 하나.
      wardId: "",
      // 대표 근무(옵셔널) — 직원 등록에서 이 팀을 고르면 기본 선택되는 근무 그룹 > 근무형태. 형태는 그룹 하위만.
      shiftGroupId: "",
      shiftId: "",
      /** 허용 근무 유형(MED-969) — 소속 직원이 배정 그룹 밖에서도 고를 수 있는 근무 유형 */
      allowedShiftIds: [] as string[],
    },

    /* ── 병동 관리 「직원 배정」 팝업(2026-09-22 P2) — 이미 등록된 직원을 병동에 배정한다.
       옵션 두 개(연결 팀에도 추가 · 다른 병동 배정은 그대로 = 겸임)는 기본 켬(사용자 결정). 저장해야 반영되는 폼 값이라
       스위치가 아니라 체크박스(DS 규칙). 겸임을 끄면 이동 — 지금 / 예약(기본 = 다음 근무 주기 시작일). ── */
    showWardAssign: false,
    /** 저장할 값 — closeGuard 가 이 객체 하나로 「바뀐 게 있는지」를 본다 */
    wardAssign: {
      /** 배정(이 병동에 사람을 고른다) · 이동(고른 사람을 어느 병동으로 보낼지 고른다) — 서버 규칙은 같다(이동 = 겸임 끔) */
      mode: "assign" as "assign" | "move",
      /** 이동에서 떠나는 병동 — 제목과 대상 목록에서 빼는 데 쓴다 */
      fromWardId: "",
      wardId: "",
      selectedIds: [] as string[],
      scheduleGroupId: "",
      workType: NurseWorkType.THREE_SHIFT as string,
      joinLinkedTeam: true,
      keepOtherWards: true,
      timing: "now" as "now" | "scheduled",
      effectiveDate: "",
      /** 이동할 때 직접 지정한 근무(manual)도 새 병동 기준 근무로 바꿀지 — 기본은 그대로(서버 keepManualShift) */
      changeManualShift: false,
    },
    wardAssignSearch: "",
    /** 배정 상태 필터 — 전체 · 미배정 · 다른 병동 배정 · 이미 이 병동 */
    wardAssignStatusFilter: "all" as "all" | "unassigned" | "other" | "here",
    wardAssignJobFilter: [] as string[],
    wardAssignSaving: false,
    wardAssignPreview: null as WardAssignPreview | null,
    wardAssignPreviewLoading: false,
    /** 이동 팝업에서 본문 아래에 아직 볼 내용이 남았는지 — 「스크롤 하여 변경 내용 확인」 버튼을 띄울지 */
    wardMoveMoreBelow: false,
    /** 지금 내용을 끝까지 봤는지 — 이동 버튼은 이게 켜져야 눌린다(안 보고 옮기는 것을 막는다) */
    wardMoveReviewed: false,
    /** 본문을 한 번이라도 내렸는지 — 그 뒤로는 플로팅 버튼을 다시 띄우지 않는다 */
    wardMoveScrolled: false,
    /** 미리보기를 못 받아 왔는지 — 빈 상자 대신 이유를 보여 주고 이동을 막는다 */
    wardAssignPreviewFailed: false,
    /** 방금 끝낸 배정 해제 — 명단 위에 잠깐 떠서 되돌릴 수 있게 한다(토스트가 아니다: 토스트는 답을 요구하지 않을 때만 쓴다) */
    wardUndo: null as { profileIds: string[]; text: string } | null,

    /** 건너뛴 뒤 강조할 직원 — 도착 화면의 명단이 이 사람 줄로 스크롤하고 잠깐 표시한다.
     *  건너뛴 것을 따로 알리는 줄은 두지 않는다(강조로 충분하다, 2026-09-23 사용자 결정). */
    focusEmployeeId: "",

    /* ── add team member modal ── */
    showAddMember: false,
    addMemberTeamId: "",
    /** 병동 모드에서 연결 팀에도 넣을지(기본 켬) — 끄면 병동 배정만 만든다. 연결 팀이 없는 병동은 늘 병동 배정만 */
    addMemberJoinTeam: true,
    /* 팀 멤버 추가 모달 — 전체 ACTIVE 직원을 한 번에 로드해 다중 선택으로 일괄 추가.
       페이지네이션 없이 스크롤 테이블 + 검색 필터. 이미 이 팀 소속인 직원은 행 비활성. */
    addMemberEmployees: [] as Record<string, unknown>[],
    addMemberSearch: "",
    /** 직무 필터(다중) — 직무가 빈 직원은 "__unset"(화면 「미입력」). 팀 관리 명단 직무 필터와 같은 규칙 */
    addMemberJobFilter: [] as string[],
    /** 팀 필터(다중) — 지금 소속 팀 기준, 소속이 없는 직원은 "__none"(화면 「소속 없음」) */
    addMemberTeamFilter: [] as string[],
    addMemberSelectedIds: [] as string[],
    addMemberLoading: false,
    addMemberSaving: false,
    /** 헤더 정렬 상태 — 컬럼별 클릭 토글로 asc/desc/none을 순환한다.
     *  기본은 입사일 오름차순(가장 오래된 입사자 상단). `by: ""` 이면 클라이언트 정렬 미적용
     *  (API 응답 순서 그대로 = createdAt DESC). */
    addMemberSort: {
      by: "hireDate" as "" | "hireDate" | "employeeNumber" | "fullName" | "teamName" | "jobDuty",
      order: "asc" as "asc" | "desc",
    },

    /* ── edit role modal ── */
    showEditRole: false,
    editingRole: null as Record<string, unknown> | null,
    roleForm: {
      name: "",
      code: "",
      description: "",
      isActive: true,
      departmentTeamIds: [] as string[],
      teamIds: [] as string[],
    },
    // 「중복 부서」·「중복 팀(병동)」 체크 — 켜면 여러 개를 고르고 고른 것을 DS 라벨로 보여 준다.
    // 저장 값이 아니라 입력 방식이라 roleForm 밖에 둔다(체크만 바꿔서는 저장 확인창이 뜨지 않게).
    roleScopeMulti: { department: false, team: false },
    // 필터 없는 전체 팀 목록(loadAllTeams — loadTeams 끝에서 함께 갱신). 역할 부서·팀, 병동 「연결 팀」처럼
    // 팀 관리 화면 필터(검색·상태)와 무관해야 하는 곳이 쓴다. 필터가 걸린 `teams` 를 쓰면 가려진 팀이
    // 저장 때 조용히 빠지거나 「연결 안 함」으로 잘못 보인다.
    allTeams: [] as OrgTeamItem[],
    // 역할 목록 부서·팀 필터의 「미지정」 값 — HTML 에 문자열을 직접 쓰지 않도록 상수를 노출한다.
    roleScopeUnset: ROLE_SCOPE_UNSET,
    rolePermissionIds: [] as string[],
    /* ── 권한 매트릭스(리소스 × 동작 × 범위) — 규칙은 ./role-permission-matrix ──
       permMatrix 는 permissions 에서 열 때 다시 만든다(라벨이 로케일·용어를 따르므로 캐시는 모달 단위). */
    permMatrix: null as PermissionMatrix | null,
    /** 리소스별 검색 보조 문구(ko·en 라벨) — 매트릭스와 함께 만든다. */
    permSearchLabels: {} as Record<string, string>,
    permMatrixFilter: { query: "", resource: "", scope: "" as CellScope, selectedOnly: false },
    /** 「다른 역할과 비교」 — 고른 역할 id 와 그 역할의 권한 id(고를 때 한 번 펼친다). */
    permCompareRoleId: "",
    permCompareIds: [] as string[],
    permCompareDiffOnly: false,
    /** 리소스 그룹 펼침(그룹 id → 열림, 적지 않은 그룹은 펼침). 필터·검색 중에는 전부 펼친다. */
    permGroupOpen: {} as Record<string, boolean>,
    /** 역할 목록 카드 펼침 — 권한 없는 리소스도 보기(역할 id → 켬). 기본은 켠 권한이 있는 리소스만. */
    roleShowAllPerms: {} as Record<string, boolean>,
    /** 이번에 손으로 바꾼 칸(cellKey) — 묶음 체크를 풀어도 이 칸은 그대로 둔다. */
    permManualCells: [] as string[],
    /** 칸의 선택지 순서 — 없음 · 본인 · 팀 · 전체. */
    permScopeOptions: ["", "self", "team", "all"] as CellScope[],
    // Snapshot taken when the edit modal opens — used to enable [저장] only
    // when the role has actually changed (수정 내역 있을 때).
    roleFormSnapshot: "" as string,

    /* ── add employee modal (패널 폼) ── */
    // Add Employee 패널은 공유 컴포넌트다 (addEmployeePanel)
    // Triggered via: $dispatch('add-employee-open')

    /* ── confirm dialog ── */
    showConfirm: false,
    confirmTitle: "",
    confirmMessage: "",
    confirmLabel: "",
    confirmAction: null as (() => Promise<void>) | null,
    // 취소(취소 버튼/X/Esc) 시 실행할 콜백 — 예: x-model로 이미 바뀐 선택을 직전 값으로 되돌리기.
    confirmCancelAction: null as (() => void) | null,
    // 선택형 중간 버튼(있을 때만 노출) — 취소도 확인도 아닌 제3 선택지(예: '내용 유지').
    // 라벨이 비어있으면 버튼 자체가 숨겨진다. 클릭 시 콜백 실행 후 닫히며 취소 콜백은 실행하지 않는다.
    confirmSecondaryLabel: "",
    confirmSecondaryAction: null as (() => Promise<void> | void) | null,
    // 알림(에러) 전용 모드: 취소 버튼 숨김 + 다른 모달 위에 표시(z-60)
    confirmHideCancel: false,
    confirmTop: false,

    /* ── expanded rows ── */
    expandedTeams: {} as Record<string, boolean>,
    expandedRoles: {} as Record<string, boolean>,
    teamMembers: {} as Record<string, Record<string, unknown>[]>,
    /** 팀 관리 병동 요약 캐시(teamId → team.wardSummary 결과) — 선택한 팀과 하위 팀 중 병동이 연결된 팀들. */
    wardSummaries: {} as Record<string, Record<string, unknown>[]>,

    /* ── 팀 관리 헤더의 연결 병동 팝오버(인력 · 스케줄러 미리보기) — 읽기 전용, 한 번에 하나만 ── */
    teamWardPop: "" as TeamWardPopKind,
    teamWardPopWardId: "",
    /** 인력 팝오버 이름 검색어 — 팝오버를 열 때마다 비운다 */
    teamWardRosterQuery: "",
    teamWardPopListenersBound: false,
    /** 스케줄러 미리보기 — 이번 달 근무표(병동 기본 스케줄러). 응답이 늦게 오면 wardId 로 대조해 버린다 */
    teamWardSched: {
      loading: false,
      failed: false,
      wardId: "",
      exists: false,
      year: 0,
      month: 0,
      dates: [] as string[],
      rows: [] as { employeeId: string; name: string }[],
      cells: {} as Record<string, string>,
      shifts: [] as { id: string; code?: string; codeColor?: string; name: string }[],
      labels: {} as Record<string, string>,
    },

    /** Which employee row currently has its "소속 추가" picker open.
     *  Hoisted to the app scope so opening one picker auto-closes any
     *  other row's picker — only one floating menu is ever visible. */
    openAffiliationPickerEmpId: "" as string,

    /** Per-team collapsed state for the team-picker tree.
     *  Hydrated from localStorage so the open/closed pattern survives
     *  modal closes, tab switches, and full page reloads. */
    teamPickerCollapsed: ((): Record<string, boolean> => {
      try {
        const raw = localStorage.getItem("admin:teamPickerCollapsed");
        return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
      } catch {
        return {};
      }
    })(),

    toggleTeamPickerCollapsed(teamId: string) {
      this.teamPickerCollapsed = {
        ...this.teamPickerCollapsed,
        [teamId]: !this.teamPickerCollapsed[teamId],
      };
      try {
        localStorage.setItem("admin:teamPickerCollapsed", JSON.stringify(this.teamPickerCollapsed));
      } catch {
        // localStorage unavailable (private window / quota) — keep in-memory only.
      }
    },

    /** Per-team collapsed state for the *organization tab* tree (independent
     *  of the picker so collapsing in the side aside doesn't hide options
     *  inside the team-add modal). Click a team name in the org tree to
     *  toggle. Default = fully expanded on every entry: we intentionally do
     *  NOT restore this from localStorage, so accessing 팀 관리 always shows
     *  the tree expanded (see resetOrgTreeCollapsed / switchTab). In-session
     *  collapses are kept in memory only. */
    orgTreeCollapsed: {} as Record<string, boolean>,

    /** Re-expand the whole org tree. Called when entering the 팀 관리 tab so
     *  the tree is always presented expanded on access. */
    resetOrgTreeCollapsed() {
      this.orgTreeCollapsed = {};
    },

    toggleOrgTreeCollapsed(teamId: string) {
      this.orgTreeCollapsed = {
        ...this.orgTreeCollapsed,
        [teamId]: !this.orgTreeCollapsed[teamId],
      };
    },

    /** 트리에서 팀 행을 클릭했을 때의 일괄 처리.
     *  1) orgSelected 갱신 → 우측 패널이 해당 팀으로 전환
     *  2) (펼침/접힘은 하지 않는다 — 셰브론 클릭 · → / ← 키 전용, toggleOrgTreeCollapsed.
     *     선택된 팀을 다시 눌러도 접히지 않는다 — 2026-09-14 사용자 요청. develop #680 의
     *     「선택된 팀 재클릭 = 토글」은 머지 때 사용자 결정으로 따르지 않았다.)
     *  3) 멤버 목록 캐시(teamMembers[teamId]) 가 비어 있으면 composition 을 가져와 채운다.
     *  (B2 추가 이전에는 toggleTeamExpand 가 3) 을 겸했지만, 트리 펼침 토글과 분리하면서
     *   leaf 패널 진입 시 멤버가 표시되지 않던 회귀를 막기 위해 명시적으로 호출한다.) */
    async selectOrgTeam(teamId: string, _hasChildren?: boolean) {
      // 선택만 한다 — 펼침/접힘은 트리의 셰브론 클릭(또는 → / ← 키)에서만(이름을 눌러 접히지 않게, 2026-09-14 사용자 요청).
      // 두 번째 인자는 예전 호출부(병동 관리 · 병동 현황 표에서 팀으로 이동) 호환용으로만 남는다.
      this.orgTreeOpen = false;
      this.orgSelected = { type: "team", id: teamId };
      this.rememberOrgSelection();
      const team = (this.teams as Record<string, unknown>[]).find((t) => t.id === teamId);
      // 병동 요약은 헤더의 「연결 병동」 줄(배정 N명 · 수간호사)이 쓰는 팀 — 자기 병동이 있는 팀 — 만 바로 읽는다.
      // 부서(하위 팀의 병동만 있는 팀)는 접힌 「병동 현황」을 펼칠 때 읽는다(ensureWardSummary, MED-914).
      if (team?.wardId) {
        this.loadWardSummary(teamId);
      }
      // 단톡방 입장 대기 — 서버가 시스템 관리자 · 팀장 없는 팀일 때만 목록을 주므로 그 조건에서만 부른다
      // (팀을 못 찾았으면 예전처럼 서버에 맡긴다).
      const store = Alpine.store("hereby");
      if (!team || (!team.leaderId && store.hasPermission("team:update"))) {
        this.loadOrgRoomJoinRequests(teamId);
      } else if (this.orgRoomJoin.teamId !== teamId) {
        this.orgRoomJoin = { teamId, conversationId: null, pending: [] };
      }
      // 팀별 명단은 team.roster 에서 파생돼 있다(fetchTeamRoster) — 팀을 골라도 서버를 부르지 않는다.
      // 로스터를 아직 못 받았으면(첫 진입 전 · 실패) 예전 경로로 그 팀만 읽는다.
      if (!this.rosterLoaded) {
        try {
          const result = await this.sdk.team.getComposition(teamId);
          this.teamMembers[teamId] = result?.members || [];
        } catch {
          this.teamMembers[teamId] = [];
        }
      }
    },

    /** 팀장 없는 팀의 단톡방 입장 대기 — 시스템 관리자가 아니거나 실패하면 빈 목록(안내가 안 뜬다). */
    async loadOrgRoomJoinRequests(teamId: string) {
      let next = {
        teamId,
        conversationId: null as string | null,
        pending: [] as { employeeId: string; name: string }[],
      };
      try {
        const result = await this.sdk.conversation.teamRoomJoinRequests(teamId);
        next = {
          teamId,
          conversationId: result?.conversationId ?? null,
          pending: (result?.pending ?? []).map((p) => ({ employeeId: p.employeeId, name: p.name })),
        };
      } catch {
        // 권한 없음 등 — 안내만 숨긴다
      }
      // 기다리는 사이 다른 팀을 골랐으면 버린다
      if (this.orgSelected.id === teamId) {
        this.orgRoomJoin = next;
      }
    },

    /** 입장 대기 승인 · 거절 — 서버가 팀장 없는 팀 · 시스템 관리자인지 다시 확인한다. */
    async decideOrgRoomJoin(employeeId: string, approve: boolean) {
      const { teamId, conversationId, pending } = this.orgRoomJoin;
      if (!(teamId && conversationId)) {
        return;
      }
      if (!approve) {
        const name = pending.find((p) => p.employeeId === employeeId)?.name ?? "";
        if (!(await askConfirm(`${name}님의 팀 단톡방 입장 요청을 거절할까요?`))) {
          return;
        }
      }
      try {
        if (approve) {
          await this.sdk.conversation.approveMember(conversationId, employeeId);
        } else {
          await this.sdk.conversation.removeMember(conversationId, employeeId);
        }
        this.notify(approve ? "입장을 승인했어요" : "입장 요청을 거절했어요");
      } catch (err) {
        showError(err instanceof Error ? err.message : String(err));
      }
      await this.loadOrgRoomJoinRequests(teamId);
    },

    /** 병동 요약 — 선택한 팀(과 하위 팀) 중 병동이 연결된 팀마다 인원 · 직군 · 근무 형태 · 병동 정보.
     *  scopeKey 가 "workspace" 면 조직 전체 병동. 실패하면 빈 목록으로 두어 병동 영역만 숨긴다(명단은 그대로). */
    async loadWardSummary(scopeKey: string) {
      try {
        // scopeKey: "workspace" = 조직 전체, "ward:<id>" = 병동 하나(트리 병동 노드), 그 밖 = 팀 id
        const result = scopeKey.startsWith("ward:")
          ? await this.sdk.team.wardSummaryForWard(scopeKey.slice("ward:".length))
          : await this.sdk.team.wardSummary(scopeKey === "workspace" ? undefined : scopeKey);
        this.wardSummaries = {
          ...this.wardSummaries,
          [scopeKey]: (result?.wards || []) as unknown as Record<string, unknown>[],
        };
      } catch {
        this.wardSummaries = { ...this.wardSummaries, [scopeKey]: [] };
      }
    },

    /** 병동 현황 새로 고침 — 캐시를 비우고 지금 보고 있는 화면(+ 최상위)만 다시 부른다.
     *  팀 추가 · 수정(병동 연결) · 멤버 변경 뒤에 숫자가 예전 값으로 남지 않게. */
    refreshWardSummaries() {
      // 팀 목록 · 전체 명단 새로 고침이 한꺼번에 부르는 경우가 많아 짧게 모아서 한 번만 부른다.
      if (this.wardRefreshTimer) {
        clearTimeout(this.wardRefreshTimer);
      }
      this.wardRefreshTimer = setTimeout(() => {
        this.wardRefreshTimer = null;
        // 이미 읽어 둔 범위만 다시 읽는다 — 아직 펼치지 않은 병동 현황(최상위 · 부서)은 펼칠 때 읽으면 되고(MED-914),
        // 병동 관리 상세 · 지금 보는 팀의 헤더 병동 줄은 이미 키가 있어 함께 새로 고쳐진다.
        const keys = new Set(Object.keys(this.wardSummaries));
        // 병동 관리는 왼쪽 목록 배지 · 개요 표가 조직 전체 요약을 늘 쓴다
        if (this.activeTab === "wards") {
          keys.add("workspace");
        }
        for (const key of keys) {
          this.loadWardSummary(key);
        }
      }, 150);
    },

    /** 최상위(조직 전체) 선택 — 병동 현황은 접힌 표를 펼칠 때 읽는다(ensureWardSummary, MED-914) */
    selectOrgWorkspace() {
      this.orgTreeOpen = false;
      this.orgSelected = { type: "workspace", id: "workspace" };
      this.rememberOrgSelection();
    },

    /** 병동 현황 표를 펼칠 때 — 아직 안 읽은 범위만 읽는다(한 범위에 한 번). 다시 읽기는 refreshWardSummaries 가 맡는다 */
    ensureWardSummary(scopeKey: string) {
      if (!scopeKey || this.wardSummaries[scopeKey] !== undefined) {
        return;
      }
      this.loadWardSummary(scopeKey);
    },

    /** 이 팀과 하위 팀에 연결된 병동 수 — 병동 현황 표의 머리(요약을 아직 안 읽었을 때도 「하위 병동 N곳」을 보인다) */
    orgSubtreeWardCount(teamId: string): number {
      const ids = new Set([teamId, ...this.descendantTeamIds(teamId)]);
      return (this.teams as Record<string, unknown>[]).filter(
        (t) => ids.has(t.id as string) && t.wardId,
      ).length;
    },

    /* ── 팀 관리 선택 기억 — URL 해시(#organization/<type>:<id>) + localStorage ──
       새로고침하면 최상위로 돌아가 2W 병동(162명) 같은 큰 화면을 매번 다시 찾아 들어가야
       했다(온보딩 9/16 현장 요청). 해시가 있으면 해시가 우선, 없으면(LNB 로 다시 들어옴·
       루트 진입) localStorage. 저장된 병동이 삭제·비활성·미연결이면 최상위로 폴백한다. */

    orgSelectionStorageKey(): string {
      const orgId = Alpine.store("hereby").currentUser?.organizationId as string | undefined;
      return lastSelectionKey("admin", "orgSelected", orgId);
    },

    /** `{type,id}` → `ward:<id>` · `team:<id>` · `workspace`. 선택이 없으면 "". */
    encodeOrgSelection(sel: { type: string | null; id: string | null }): string {
      if (sel.type === "workspace") {
        return "workspace";
      }
      if ((sel.type === "ward" || sel.type === "team") && sel.id) {
        return `${sel.type}:${sel.id}`;
      }
      return "";
    },

    decodeOrgSelection(raw: string): { type: "team" | "workspace" | "ward"; id: string } | null {
      if (raw === "workspace") {
        return { type: "workspace", id: "workspace" };
      }
      const m = /^(ward|team):([0-9a-f-]{36})$/i.exec(raw);
      return m ? { type: m[1] as "ward" | "team", id: m[2] } : null;
    },

    /** 선택이 바뀔 때마다 — localStorage 에 쓰고, 팀 관리 탭이 열려 있으면 해시도 바꾼다(replace). */
    rememberOrgSelection() {
      this.orgSelectionRestored = true;
      const encoded = this.encodeOrgSelection(this.orgSelected);
      if (!encoded) {
        return;
      }
      writeLastSelection(this.orgSelectionStorageKey(), encoded);
      if (this.activeTab === "organization") {
        Alpine.store("router").navigateTab("organization", encoded, { replace: true });
      }
    },

    /**
     * 저장된 선택을 트리에 대고 검증한 뒤 확정한다. 트리(teams·wardList)가 로드된 뒤에
     * 불러야 한다 — 병동은 연결 팀 아래 「선택 가능한 병동」 줄로 실제 나타나야 유효하고
     * (wardList 만 보면 비활성·미연결 병동도 통과한다), 팀은 팀 목록에 있어야 한다.
     * 무효면 최상위로 폴백하고, 그 폴백도 저장해 다음 진입에서 같은 검증이 반복되지 않게 한다.
     */
    restoreOrgSelection() {
      if (this.orgSelectionRestored) {
        return;
      }
      const router = Alpine.store("router");
      const raw = router.orgSub || readLastSelection<string>(this.orgSelectionStorageKey()) || "";
      const sel = this.decodeOrgSelection(raw);
      // 병동은 더 이상 팀 관리에서 열지 않는다(P5) — 예전에 기억해 둔 병동 선택은 워크스페이스로 되돌린다.
      // 병동으로 보내지 않는 이유: 팀 관리를 열었을 뿐인데 다른 화면으로 튀면 뒤로 가기와 싸운다.
      if (sel?.type === "team") {
        if ((this.teams as Array<{ id: string }>).some((t) => t.id === sel.id)) {
          this.selectOrgTeam(sel.id);
          return;
        }
      }
      this.selectOrgWorkspace();
    },

    /** 지금 선택 화면의 병동 요약 행 — 최상위는 조직 전체, 팀은 그 팀과 하위 팀 */
    wardRowsCurrent(): Record<string, unknown>[] {
      return this.wardSummaries[this.orgWardSummaryKey()] || [];
    },

    /** 지금 선택 화면의 병동 요약 캐시 키 — 팀 = 팀 id(그 팀과 하위 팀), 병동 = "ward:<id>", 그 밖 = 조직 전체 */
    orgWardSummaryKey(): string {
      const { type, id } = this.orgSelected;
      if (type === "team" && id) {
        return id;
      }
      if (type === "ward" && id) {
        return `ward:${id}`;
      }
      return "workspace";
    },

    /**
     * 트리의 병동 노드(연결 팀 아래) · 병동 현황 표의 줄 — 병동 관리로 보낸다(2026-09-23 P5).
     *
     * 예전에는 팀 관리 안에 병동 화면(헤더 · 병동 요약 · 배정 기준 명단)이 따로 있었다. 병동 관리 상세가
     * 같은 화면을 더 갖춘 형태로 만들어지면서 같은 화면이 두 곳이 됐고, 한쪽만 고치면 다른 쪽이 뒤처졌다.
     * 이제 팀 관리는 팀만 다루고, 병동을 누르면 병동 관리의 그 병동으로 간다.
     */
    selectOrgWard(wardId: string) {
      this.orgTreeOpen = false;
      this.selectWard(wardId);
      this.switchTab("wards");
    },

    /* ── 병동 관리(목록 + 상세, 2026-09-22) 선택 — 팀 관리 트리 선택과 같은 기억 규칙(해시 #wards/<id> + localStorage) ── */

    wardSelectionStorageKey(): string {
      const orgId = Alpine.store("hereby").currentUser?.organizationId as string | undefined;
      return lastSelectionKey("admin", "wardSelected", orgId);
    },

    /** `all`(전체 병동 개요) 또는 병동 id — 해시 `#wards/<값>` 에 실린다 */
    encodeWardSelection(): string {
      return this.wardSelected.type === "ward" && this.wardSelected.id
        ? this.wardSelected.id
        : "all";
    },

    rememberWardSelection() {
      this.wardSelectionRestored = true;
      const encoded = this.encodeWardSelection();
      writeLastSelection(this.wardSelectionStorageKey(), encoded);
      if (this.activeTab === "wards") {
        Alpine.store("router").navigateTab("wards", encoded, { replace: true });
      }
    },

    /** 「전체 병동」 개요 — 병동 현황 표(조직 전체) + 병동 목록 · 설정 표 */
    selectWardOverview() {
      this.wardListOpen = false;
      this.wardSelected = { type: "all", id: "" };
      this.rememberWardSelection();
      this.loadWardSummary("workspace");
    },

    /** 병동 하나 — 오른쪽에 상세(인력 · 병동 정보 · 스케줄러). 다른 병동을 표 안 수정 중이면 접는다 */
    selectWard(wardId: string) {
      this.wardListOpen = false;
      if (this.wardInlineEdit.id && this.wardInlineEdit.id !== wardId) {
        this.cancelWardInfoEdit();
      }
      this.wardSelected = { type: "ward", id: wardId };
      this.rememberWardSelection();
      this.loadWardSummary(`ward:${wardId}`);
    },

    /** 저장된 선택을 병동 목록에 대고 검증한 뒤 확정 — 삭제된 병동이면 개요로. loadWards 뒤에 부른다 */
    restoreWardSelection() {
      if (this.wardSelectionRestored) {
        return;
      }
      const router = Alpine.store("router");
      const raw = router.wardSub || readLastSelection<string>(this.wardSelectionStorageKey()) || "";
      if (
        /^[0-9a-f-]{36}$/i.test(raw) &&
        (this.wardList as { id: string }[]).some((w) => w.id === raw)
      ) {
        this.selectWard(raw);
        return;
      }
      this.selectWardOverview();
    },

    /** 해시(#wards/<id> · #wards/all)가 밖에서 바뀌면(뒤로/앞으로 · 주소 입력) 화면 선택을 따라 맞춘다.
     *  없으면 해시만 다른 병동을 가리키고 화면은 이전 병동에 남아, 그 화면의 수정이 엉뚱한 병동에 저장된다.
     *  선택 함수가 쓴 해시는 이미 같은 값이라 여기서는 아무 일도 하지 않는다(되부름 없음). */
    syncWardSelectionFromHash(sub: string) {
      if (this.activeTab !== "wards" || !this.wardSelectionRestored || !sub) {
        return;
      }
      if (sub === this.encodeWardSelection()) {
        return;
      }
      if (sub !== "all" && (this.wardList as { id: string }[]).some((w) => w.id === sub)) {
        this.selectWard(sub);
      } else {
        // 「all」 또는 없는(삭제된) 병동 주소 — 개요로 가고 해시도 #wards/all 로 고친다
        this.selectWardOverview();
      }
    },

    /** 병동 관리로 탭을 바꿀 때 해시에 실을 값. 해시가 이미 병동을 가리키면(주소 입력 · 뒤로 가기로 들어옴) 그 값을 지킨다 —
     *  고르던 병동으로 덮으면 다른 메뉴에서 #wards/<id> 로 들어와도 예전 병동이 뜬다. 메뉴 클릭처럼 해시가 다른 탭이면
     *  고르던 병동(또는 개요), 아직 복원 전이면 비운다(restoreWardSelection 이 채운다). */

    /** 상세 화면의 병동(병동 목록 기준) */
    wardDetail(): Record<string, unknown> | null {
      const id = this.wardSelected.type === "ward" ? this.wardSelected.id : "";
      return id
        ? ((this.wardList as Record<string, unknown>[]).find((w) => w.id === id) ?? null)
        : null;
    },

    /** 상세 화면의 병동 요약 행(team.wardSummary — 병동 배정 기준). 아직 못 읽었으면 null */
    wardDetailSummary(): Record<string, unknown> | null {
      const id = this.wardSelected.type === "ward" ? this.wardSelected.id : "";
      return id ? ((this.wardSummaries[`ward:${id}`] || [])[0] ?? null) : null;
    },

    /** 전체 병동 개요 표의 행 — 조직 전체(연결 병동 + 미연결 운영 병동) */
    wardOverviewRows(): Record<string, unknown>[] {
      return this.wardSummaries.workspace || [];
    },

    /** 왼쪽 병동 목록 배지 — 병동 배정 인원(재직 + 휴직). 개요를 아직 못 읽었거나 비활성 병동이면 null(숨김) */
    wardHeadcountOf(wardId: string): number | null {
      const row = this.wardOverviewRows().find((w) => w.wardId === wardId);
      return row ? Number((row.headcount as { total: number }).total) : null;
    },

    /** 왼쪽 병동 목록 — 이름 · 코드로 거르고, 운영 중 병동(목록 순서 그대로) 뒤에 비활성 병동 */
    wardListItems(search: string): Record<string, unknown>[] {
      const q = (search || "").trim().toLowerCase();
      const list = (this.wardList as Record<string, unknown>[]).filter(
        (w) =>
          !q ||
          String(w.name || "")
            .toLowerCase()
            .includes(q) ||
          String(w.code || "")
            .toLowerCase()
            .includes(q),
      );
      const ordered = [
        ...list.filter((w) => w.isActive !== false),
        ...list.filter((w) => w.isActive === false),
      ];
      // 연결선 — 「전체 병동」 아래 한 단계라 팀 관리 트리의 최상위 팀 줄과 같은 모양이다(같은 계산기를 쓴다)
      return ordered.map((w, i) => ({
        ...w,
        treeLines: treeLineStyles(0, [], i === 0, i === ordered.length - 1, false),
      }));
    },

    /** 상세 헤더 · 개요 표의 연결 팀 → 팀 관리의 그 팀 화면 */
    goToWardLinkedTeam(wardId: string) {
      const team = this.wardLinkedTeam(wardId);
      if (!team) {
        return;
      }
      this.switchTab("organization");
      Alpine.nextTick(() => this.selectOrgTeam(team.id, this.teamIsBranch(team.id)));
    },

    /** 상태를 바꾸면 무엇이 달라지는지 — 개요 표 상태 칩 전환과 병동 정보 저장이 같은 설명을 쓴다 */
    wardStatusChangeNote(toInactive: boolean): string {
      return toInactive
        ? "스케줄러의 병동 목록에서 빠져 근무표를 열 수 없어요. 배정된 인원과 근무표 기록은 그대로예요."
        : "스케줄러의 병동 목록에 다시 나타나요.";
    },

    /** 상태 전환은 확인창을 거친다 — 비활성이면 스케줄러 병동 목록에서 빠져 근무표를 열 수 없다(실수 한 번에 사라지던 문제) */
    async confirmToggleWardStatus(wardId: string) {
      const ward = (this.wardList as Record<string, unknown>[]).find((w) => w.id === wardId);
      if (!ward) {
        return;
      }
      const name = String(ward.name || "병동");
      const toInactive = ward.isActive !== false;
      const ok = await askConfirm({
        message: `「${name}」 상태를 ${toInactive ? "비활성" : "활성"}으로 바꿀까요?\n${this.wardStatusChangeNote(toInactive)}`,
        confirmLabel: toInactive ? "비활성으로 전환" : "활성으로 전환",
      });
      if (!ok) {
        return;
      }
      await this.toggleWardStatus(wardId);
    },

    /* ── 병동 책임자 — 따로 저장되는 값이 아니라 그 병동 배정(간호 프로필)의 「수간호사」 체크다.
       병동 정보 탭에서 사람 단위로 고르고, 저장할 때 그 사람의 이 병동 배정 줄마다 체크를 켜고 끈다.
       한 병동에 여러 명도 된다(서버 제한 없음 · 기존 데이터 유지). ── */

    /** 이 병동의 배정 줄 — 한 사람이 같은 병동의 스케줄러 두 곳에 있으면 줄이 둘이다 */
    wardAssignmentsOf(
      member: Record<string, unknown>,
      wardId: string,
    ): { profileId: string; isHeadNurse: boolean }[] {
      const wards =
        (member.wards as { profileId?: string; wardId: string; isHeadNurse?: boolean }[]) ?? [];
      return wards
        .filter((w) => w.wardId === wardId && w.profileId)
        .map((w) => ({ profileId: String(w.profileId), isHeadNurse: !!w.isHeadNurse }));
    },

    /** 책임자 후보 — 이 병동에 배정된 재직 · 휴직 인원(헤더 「책임자」와 같은 기준 · 퇴직은 빠진다).
     *  수정을 시작할 때의 책임자가 맨 위, 나머지는 이름순 — 드롭다운을 열면 지금 책임자가 바로 보이게 */
    wardHeadCandidates(
      wardId: string,
    ): { employeeId: string; name: string; jobDuty: string; onLeave: boolean }[] {
      const current = new Set(
        this.wardHeadEdit.wardId === wardId ? this.wardHeadEdit.original : [],
      );
      return this.allMembers
        .filter(
          (m) =>
            (m.status === "ACTIVE" || m.status === "ON_LEAVE") &&
            this.wardAssignmentsOf(m, wardId).length > 0,
        )
        .map((m) => ({
          employeeId: String(m.employeeId),
          name: String(m.employeeName || ""),
          jobDuty: String(m.jobDuty || ""),
          onLeave: m.status === "ON_LEAVE",
        }))
        .sort(
          (a, b) =>
            Number(current.has(b.employeeId)) - Number(current.has(a.employeeId)) ||
            a.name.localeCompare(b.name, "ko"),
        );
    },

    /** 지금 책임자(직원 id) — 이 병동 배정 줄 중 하나라도 수간호사 체크면 책임자 */
    wardHeadIdsOf(wardId: string): string[] {
      const candidateIds = new Set(this.wardHeadCandidates(wardId).map((c) => c.employeeId));
      return this.allMembers
        .filter(
          (m) =>
            candidateIds.has(String(m.employeeId)) &&
            this.wardAssignmentsOf(m, wardId).some((a) => a.isHeadNurse),
        )
        .map((m) => String(m.employeeId));
    },

    /** 수정 폼 「책임자」 드롭다운 표시 — 첫 사람 이름(+N 은 배지가 따로 그린다) */
    wardHeadEditLabel(): string {
      const first = this.wardHeadEdit.selected[0];
      if (!first) {
        return "";
      }
      const hit = this.allMembers.find((m) => m.employeeId === first);
      return String(hit?.employeeName || "");
    },

    wardHeadDirty(): boolean {
      const { wardId, selected, original } = this.wardHeadEdit;
      if (!wardId || wardId !== this.wardInlineEdit.id) {
        return false;
      }
      return [...selected].sort().join(",") !== [...original].sort().join(",");
    },

    /** 병동 정보 [저장] 활성 조건 — 병동 칸 또는 책임자가 바뀌었을 때 */
    wardInfoDirty(): boolean {
      return this.wardInlineDirty() || this.wardHeadDirty();
    },

    /** 병동 정보 「수정」 — 표 안 수정 상태를 열고 책임자 선택을 지금 값으로 채운다 */
    async startWardInfoEdit(wardId: string) {
      await this.startInlineWardEdit(wardId);
      if (this.wardInlineEdit.id !== wardId) {
        return; // 다른 병동 수정 중이라 사용자가 「계속 수정」을 고른 경우
      }
      const ids = this.wardHeadIdsOf(wardId);
      this.wardHeadEdit = { wardId, selected: [...ids], original: [...ids] };
    },

    cancelWardInfoEdit() {
      this.cancelInlineWardEdit();
      this.wardHeadEdit = { wardId: "", selected: [], original: [] };
    },

    /** 책임자 저장 — 선택이 바뀐 사람(새로 고름 · 뺌)만, 그 사람의 이 병동 배정 줄마다 수간호사 체크를 맞춘다.
     *  그대로 둔 사람과 후보 밖(퇴직)의 남은 체크는 건드리지 않는다. 성공이면 true */
    async saveWardHeadNurses(
      wardId: string,
      selected: string[],
      original: string[],
    ): Promise<boolean> {
      const after = new Set(selected);
      const before = new Set(original);
      const updates: { id: string; isHeadNurse: boolean }[] = [];
      for (const m of this.allMembers) {
        const id = String(m.employeeId);
        const isHead = after.has(id);
        if (isHead === before.has(id)) {
          continue;
        }
        for (const a of this.wardAssignmentsOf(m, wardId)) {
          if (a.isHeadNurse !== isHead) {
            updates.push({ id: a.profileId, isHeadNurse: isHead });
          }
        }
      }
      try {
        await Promise.all(updates.map((u) => this.sdk.nurseScheduler.updateProfile(u)));
        return true;
      } catch {
        this.showCommonError();
        return false;
      } finally {
        // 일부만 저장됐어도 화면이 서버와 맞도록 명단(병동 배정)과 병동 요약을 다시 읽는다
        if (updates.length > 0) {
          await this.loadUnassignedMembers();
        }
      }
    },

    /** 병동 상세 「병동 정보」 저장 — 상태를 바꿨으면 그 방향(활성→비활성 / 비활성→활성)에 맞는 확인창을 먼저 띄운다.
     *  취소하면 저장하지 않고 수정 중인 내용을 그대로 둔다. 상태 말고 다른 칸만 바꿨으면 바로 저장한다.
     *  책임자를 먼저 저장하고 병동 칸을 저장한다 — 병동 칸 저장이 끝나면 폼이 닫히기 때문이다. */
    async saveWardInfo() {
      const { id, form, original } = this.wardInlineEdit;
      if (!id || this.wardSaving || !this.wardInfoDirty()) {
        return;
      }
      if (form.isActive !== original.isActive) {
        const toInactive = !form.isActive;
        const name = original.name || "병동";
        const ok = await askConfirm({
          message: `「${name}」 상태를 ${toInactive ? "비활성" : "활성"}으로 바꿔 저장할까요?\n${this.wardStatusChangeNote(toInactive)}`,
          confirmLabel: toInactive ? "비활성으로 저장" : "활성으로 저장",
        });
        if (!ok) {
          return;
        }
      }
      if (this.wardHeadDirty()) {
        this.wardSaving = true;
        const saved = await this.saveWardHeadNurses(
          id,
          this.wardHeadEdit.selected,
          this.wardHeadEdit.original,
        );
        this.wardSaving = false;
        if (!saved) {
          return;
        }
        // 책임자는 저장됨 — 병동 칸 저장이 실패해 폼이 열려 있어도 책임자는 바뀐 것으로 본다
        this.wardHeadEdit = { ...this.wardHeadEdit, original: [...this.wardHeadEdit.selected] };
      }
      if (this.wardInlineDirty()) {
        if (!(await this.saveInlineWardEdit())) {
          return;
        }
      }
      this.cancelWardInfoEdit();
    },

    /** 상세 「스케줄러」 탭의 추가 — 목록 팝업을 열지 않고 그 병동의 스케줄러 추가 폼을 바로 연다 */
    openCreateSchedulerFor(wardId: string) {
      this.wardSchedulers = { ...this.wardSchedulers, wardId };
      this.openCreateScheduler();
    },

    /** 트리 「미연결 병동」 아래 병동 줄 — 연결 팀이 없어 병동 화면 대신 연결할 팀을 설정하라는 확인창(닫기 / 병동 설정). */
    async openUnlinkedWard(wardId: string, wardName: string) {
      const ok = await askConfirm({
        message: `「${wardName}」 병동은 아직 어느 팀에도 연결되지 않았어요.\n병동과 연결할 팀을 설정해 주세요.`,
        confirmLabel: "병동 설정",
        cancelLabel: "닫기",
      });
      if (!ok) {
        return;
      }
      // 병동 관리 「전체 병동」 개요의 병동 목록 표에서 그 줄을 표 안 수정 상태로 연다(연결 팀 칸) — 상세가 열려 있으면 개요로 먼저
      this.switchTab("wards");
      this.selectWardOverview();
      Alpine.nextTick(async () => {
        await this.startInlineWardEdit(wardId);
        Alpine.nextTick(() => {
          document
            .querySelector(`[data-testid="ward-row"][data-id="${wardId}"]`)
            ?.scrollIntoView({ block: "center" });
        });
      });
    },

    /** 병동 현황 표의 직무 열 — 병동들에 나오는 직무를 인원 많은 순으로(미입력은 맨 뒤) */
    wardJobColumns(rows: Record<string, unknown>[]): string[] {
      const totals: Record<string, number> = {};
      for (const w of rows) {
        for (const j of (w.byJob as { jobDuty: string; count: number }[]) || []) {
          totals[j.jobDuty] = (totals[j.jobDuty] || 0) + j.count;
        }
      }
      return Object.keys(totals).sort(
        (a, b) =>
          Number(a === "미입력") - Number(b === "미입력") ||
          totals[b] - totals[a] ||
          a.localeCompare(b, "ko"),
      );
    },

    wardJobCount(w: Record<string, unknown>, job: string): number {
      const hit = ((w.byJob as { jobDuty: string; count: number }[]) || []).find(
        (j) => j.jobDuty === job,
      );
      return hit?.count || 0;
    },

    wardSum(
      rows: Record<string, unknown>[],
      pick: (w: Record<string, unknown>) => unknown,
    ): number {
      return rows.reduce((acc, w) => acc + (Number(pick(w)) || 0), 0);
    },

    /** 병동 책임자(수간호사 체크) — 겸임이면 다른 병동을 괄호로 */
    wardHeadNurseText(w: Record<string, unknown>): string {
      const heads = (w.headNurses as { name: string; otherWardNames: string[] }[]) || [];
      return heads
        .map(
          (h) =>
            h.name + (h.otherWardNames.length ? ` (${h.otherWardNames.join(" · ")} 겸임)` : ""),
        )
        .join(", ");
    },

    /**
     * 팀 헤더의 연결 병동 줄 — 이 팀에 직접 연결된 병동의 요약 행(배정 인원 · 수간호사).
     * 요약이 아직 안 왔으면 null(헤더는 병동 이름만 보인다). 하위 팀의 병동은 병동 현황 표가 맡는다.
     */
    orgTeamLinkedWardRow(wardId: string): Record<string, unknown> | null {
      if (!wardId) {
        return null;
      }
      return this.wardRowsCurrent().find((w) => w.wardId === wardId) || null;
    },

    /**
     * 헤더의 「배정 N명」 · 「스케줄러 미리보기」 — 같은 자리(채팅 패널 자리)에 뜨는 팝오버 둘 중 하나를 연다.
     * 같은 것을 다시 누르면 닫고, 다른 것을 누르면 바꾼다. 열 때 채팅 · 떠 있는 AI 창을 접게 알린다(겹침 방지).
     */
    toggleTeamWardPop(kind: Exclude<TeamWardPopKind, "">, wardId: string) {
      const next = nextTeamWardPop(this.teamWardPop, kind);
      this.teamWardPop = next;
      this.teamWardPopWardId = next ? wardId : "";
      this.teamWardRosterQuery = "";
      if (!next) {
        return;
      }
      this.syncTeamWardPopTop();
      window.dispatchEvent(
        new CustomEvent(FLOATING_PANEL_OPEN_EVENT, { detail: TEAM_WARD_PANEL_ID }),
      );
      if (next === "schedule") {
        this.loadTeamWardSchedule(wardId);
      }
    },

    /**
     * 팝오버를 페이지 헤더 바로 아래에 두려고 열 때마다 헤더의 실제 아래 끝을 잰다 — 헤더 높이가 바뀌어도 따라가고,
     * 헤더의 공지 · 알림 · 프로필을 덮지 않는다. 숨은 헤더(다른 탭 것)는 건너뛰고, 못 재면 CSS 기본값(64px)을 쓴다.
     */
    syncTeamWardPopTop(popSelector = '[data-testid="org-team-ward-pop"]') {
      const header = [...document.querySelectorAll<HTMLElement>(".ds-page-header")].find(
        (el) => el.getBoundingClientRect().height > 0,
      );
      const pop = document.querySelector<HTMLElement>(popSelector);
      if (!(header && pop)) {
        return;
      }
      pop.style.setProperty(
        "--org-ward-pop-header",
        `${Math.round(header.getBoundingClientRect().bottom)}px`,
      );
    },

    /** 스케줄러 미리보기를 쓸 수 있는지 — 근무표(nurse-scheduler:read) · 인력(schedule:read)을 둘 다 읽을 수 있어야 한다.
     *  없으면 버튼을 두지 않는다(눌렀는데 「불러오지 못했어요」만 뜨는 버튼이 되지 않게) */
    canPreviewWardSchedule(): boolean {
      const store = Alpine.store("hereby");
      return store.hasPermission("nurse-scheduler:read") && store.hasPermission("schedule:read");
    },

    closeTeamWardPop() {
      this.teamWardPop = "";
      this.teamWardPopWardId = "";
    },

    /**
     * 한 번만 — ESC(공용 ESC 층: 화면에서 가장 위 하나만 닫힘) · 채팅/AI 가 열리면 닫기.
     * init() 에서만 부른다 — 루트가 아닌 곳(팀 헤더 버튼 등)에서 부르면 $watch 가 그 요소와 함께 사라진다.
     */
    bindTeamWardPopListeners() {
      if (this.teamWardPopListenersBound) {
        return;
      }
      this.teamWardPopListenersBound = true;
      // 다른 팀 · 다른 메뉴로 가면 닫는다 — 팝오버는 고른 팀의 연결 병동 것이라 남아 있으면 엉뚱한 병동을 보여 준다
      // biome-ignore lint/suspicious/noExplicitAny: Alpine magic property
      (this as any).$watch("orgSelected.id", () => this.closeTeamWardPop());
      // biome-ignore lint/suspicious/noExplicitAny: Alpine magic property
      (this as any).$watch("activeTab", () => this.closeTeamWardPop());
      registerEscLayer(
        () =>
          this.teamWardPop
            ? document.querySelector<HTMLElement>('[data-testid="org-team-ward-pop"]')
            : null,
        () => this.closeTeamWardPop(),
      );
      window.addEventListener(FLOATING_PANEL_OPEN_EVENT, (event: Event) => {
        if ((event as CustomEvent).detail !== TEAM_WARD_PANEL_ID) {
          this.closeTeamWardPop();
        }
      });
      // AI 는 도크로 열려도(채팅과 겹치지 않아 위 이벤트를 안 보낸다) 따로 알린다 — 어디서 열었든 같은 신호
      window.addEventListener(AI_ASSISTANT_OPEN_EVENT, () => this.closeTeamWardPop());
      // 헤더의 알림 · 프로필 메뉴를 누르면 닫는다 — 그 메뉴가 뜨는 동안 팝오버가 같이 떠 있지 않게.
      // 「불편·기능 신고」는 **빼고 둔다** — 신고 스크린샷에 지금 보고 있는 팝오버가 찍혀야 한다
      // (captureFilter 는 .ds-fab-layer 를 빼지 않는다). 도구 영역은 8개 앱 공용 파셜이라 거기에 신호를
      // 넣지 않고 여기서 누름을 본다(capture — 메뉴 쪽 핸들러가 전파를 끊어도 받는다)
      document.addEventListener(
        "click",
        (event) => {
          const target = event.target as Element | null;
          if (
            this.teamWardPop &&
            target?.closest?.(
              '.ds-page-header__toolbar [x-data="notificationBell"], .ds-page-header__toolbar .ds-page-header__profile',
            )
          ) {
            this.closeTeamWardPop();
          }
        },
        true,
      );
    },

    /**
     * 인력 팝오버 명단 — 서버 병동 요약이 「배정 N명」을 셀 때 쓴 **바로 그 사람들**(roster).
     * 화면이 다른 목록(allMembers)에서 따로 거르면 숫자와 명단이 조용히 어긋나서 같은 응답을 쓴다.
     */
    teamWardRoster(): TeamWardRosterRow[] {
      const row = this.orgTeamLinkedWardRow(this.teamWardPopWardId);
      return (row?.roster as TeamWardRosterRow[] | undefined) ?? [];
    },

    /** 인력 팝오버에 보이는 줄 — 이름 검색어로 거른 명단(머리의 인원 수는 거르기 전 전체) */
    teamWardRosterVisible(): TeamWardRosterRow[] {
      return filterRosterByName(this.teamWardRoster(), this.teamWardRosterQuery);
    },

    /**
     * 스케줄러 미리보기 — 오늘이 속한 달의 근무표(병동 기본 스케줄러)를 읽는다. 스케줄러의 「근무표 보기」와 같은 달.
     * 행은 그 스케줄러의 인력, 칸은 근무표 항목. 쓰는 호출은 없다.
     */
    async loadTeamWardSchedule(wardId: string) {
      const [y, m] = (Alpine.store("hereby").todayLocal() as string).split("-").map(Number);
      const s = this.teamWardSched;
      Object.assign(s, {
        loading: true,
        failed: false,
        wardId,
        exists: false,
        year: y,
        month: m,
        dates: [],
        rows: [],
        cells: {},
      });
      try {
        const [sched, shiftList] = await Promise.all([
          this.sdk.nurseScheduler.findScheduleByPeriod({ wardId, year: y, month: m }),
          // 근무 유형은 열 때마다 새로 읽는다 — 설정에서 색 · 코드를 바꾸면 다음 열기에 바로 맞는다.
          // 실패해도 근무표는 띄운다(연동 근무 칸만 코드 없이 보인다).
          this.sdk.shift
            .list({ schedulerOnly: true })
            .catch(() => null),
        ]);
        if (this.teamWardSched.wardId !== wardId) {
          return;
        }
        if (shiftList) {
          s.shifts = (shiftList.items || []).map((sh) => ({
            id: sh.id,
            code: sh.code,
            codeColor: sh.codeColor ?? undefined,
            name: sh.name,
          }));
        }
        s.labels = getAssignmentLabels({
          // biome-ignore lint/style/useNamingConvention: getAssignmentLabels 가 읽는 Alpine 컨텍스트 모양($store)
          $store: { hereby: Alpine.store("hereby") },
          availableShifts: s.shifts,
        });
        if (!sched) {
          return;
        }
        const profiles = await fetchAllPages<{
          employeeId: string;
          employee?: { firstName?: string; lastName?: string };
        }>(
          (page, limit) =>
            this.sdk.nurseScheduler.listProfiles(
              { scheduleGroupId: sched.scheduleGroupId },
              { page, limit },
            ),
          500,
        );
        if (this.teamWardSched.wardId !== wardId) {
          return;
        }
        const cells: Record<string, string> = {};
        for (const e of sched.entries || []) {
          cells[`${e.employeeId}:${toDateStr(e.date)}`] = entryCellValue(e);
        }
        Object.assign(s, {
          exists: true,
          // 기간이 비어 있는 옛 근무표는 날짜 칸 없이 「근무표가 없어요」와 같게 보인다
          dates:
            sched.startDate && sched.endDate
              ? dateRange(toDateStr(sched.startDate), toDateStr(sched.endDate))
              : [],
          rows: profiles.map((p) => ({
            employeeId: p.employeeId,
            name: `${p.employee?.lastName || ""}${p.employee?.firstName || ""}`,
          })),
          cells,
        });
      } catch {
        // 권한 · 네트워크 실패 — 팝오버에 「불러오지 못했어요」를 띄우고 스케줄러로 가는 버튼은 그대로 둔다
        if (this.teamWardSched.wardId === wardId) {
          s.failed = true;
        }
      } finally {
        if (this.teamWardSched.wardId === wardId) {
          s.loading = false;
        }
      }
    },

    /** 미리보기 칸 하나 — 스케줄러 그리드와 같은 라벨 · 색 규칙(assignmentCellView) */
    teamWardCell(employeeId: string, date: string): { label: string; cls: string; style: string } {
      const s = this.teamWardSched;
      return assignmentCellView(s.cells[`${employeeId}:${date}`] || "", s.labels, s.shifts);
    },

    /** 미리보기 머리 — 일(日) · 요일 · 주말 여부 */
    teamWardDay(date: string): { day: number; dow: string; weekend: boolean } {
      const d = new Date(`${date}T00:00:00`).getDay();
      return {
        day: Number(date.slice(8, 10)),
        dow: ["일", "월", "화", "수", "목", "금", "토"][d],
        weekend: d === 0 || d === 6,
      };
    },

    /** 병동 현황 표 합계와 비교하는 전체 인원 —최상위는 조직 전체, 팀은 그 팀과 하위 팀(트리 배지와 같은 값) */
    wardBaseTotal(): number {
      if (this.orgSelected.type === "team" && this.orgSelected.id) {
        return this.orgTeamHeadcounts()[this.orgSelected.id]?.total ?? 0;
      }
      return this.orgWorkspaceHeadcount().total;
    },

    /** 인원 집계 — 전체 = 재직 + 휴직(비활성 제외). 퇴사는 전체에 넣지 않고 따로 센다.
     *  팀 관리 트리 · 헤더 · 직원 관리 통계 공통 기준. */
    countHeadcount(statuses: unknown[]): {
      total: number;
      active: number;
      onLeave: number;
      terminated: number;
    } {
      let active = 0;
      let onLeave = 0;
      let terminated = 0;
      for (const s of statuses) {
        if (s === "ACTIVE") {
          active++;
        } else if (s === "ON_LEAVE") {
          onLeave++;
        } else if (s === "TERMINATED") {
          terminated++;
        }
      }
      return { total: active + onLeave, active, onLeave, terminated };
    },

    /** 조직 전체 인원(재직 + 휴직, 퇴사 따로) — 트리 맨 위 배지 · 최상위 헤더 */
    orgWorkspaceHeadcount(): {
      total: number;
      active: number;
      onLeave: number;
      terminated: number;
    } {
      return this.countHeadcount(Object.values(this.rosterStatusMap));
    },

    /** 인원 배지 툴팁 — 「전체 N · 재직 N · 휴직 N · 퇴사 N」 */
    orgHeadcountTitle(
      c: { total: number; active: number; onLeave: number; terminated?: number } | null,
    ): string {
      return c
        ? `전체 ${c.total} · 재직 ${c.active} · 휴직 ${c.onLeave} · 퇴직 ${c.terminated ?? 0}`
        : "";
    },

    /** 팀 퇴사자 수 — 이 팀(하위 포함이면 하위 팀까지)에 소속돼 있던 퇴사자. 퇴사하면서 소속이
     *  끝난 사람도 예전 소속 기록으로 센다(병원 명단엔 퇴사자 소속이 없어 기존 기록을 쓴다). */
    orgTeamTerminated(teamId: string, includeSubteams: boolean): number {
      if (includeSubteams) {
        return this.orgTeamHeadcounts()[teamId]?.terminated ?? 0;
      }
      let count = 0;
      for (const [employeeId, status] of Object.entries(this.rosterStatusMap)) {
        if (status !== "TERMINATED") {
          continue;
        }
        const ids = [
          ...(this.rosterTeamMap[employeeId] || []),
          ...(this.rosterLeftTeamMap[employeeId] || []),
        ];
        if (ids.includes(teamId)) {
          count++;
        }
      }
      return count;
    },

    /** 병동 열 표시값 — 직원의 병동 배정과 소속 팀의 연결 병동으로 겸임 · 불일치 · 수간호사를 판단한다.
     *  (팀 관리 명단과 같은 규칙: 불일치 = 소속 팀에 연결 병동이 있는데 그 병동이 아닌 곳에만 배정) */
    employeeWardInfo(emp: Record<string, unknown>): {
      label: string;
      concurrent: boolean;
      mismatch: boolean;
      isHeadNurse: boolean;
    } {
      const wards =
        (emp.wards as { wardId: string; wardName: string; isHeadNurse: boolean }[]) || [];
      const memberships = (emp.memberships as { teamId: string }[]) || [];
      const teams = this.teams as Record<string, unknown>[];
      const linked = memberships
        .map((m) => teams.find((t) => t.id === m.teamId)?.wardId as string | undefined)
        .filter((id): id is string => !!id);
      return {
        label: wards.map((w) => w.wardName).join(" · "),
        concurrent: wards.length > 1,
        mismatch:
          linked.length > 0 && wards.length > 0 && !wards.some((w) => linked.includes(w.wardId)),
        isHeadNurse: wards.some((w) => w.isHeadNurse),
      };
    },

    /** 직원 관리 직무 필터 선택지 — 조직 전체 명단의 직무(가나다순) + 비어 있는 직원이 있으면 「미입력」 */
    employeeJobOptions(): string[] {
      const set = new Set(
        (this.allMembers as Record<string, unknown>[]).map(
          (m) => (m.jobDuty as string | null) || "__unset",
        ),
      );
      return [...set].sort(
        (a, b) => Number(a === "__unset") - Number(b === "__unset") || a.localeCompare(b, "ko"),
      );
    },

    /** 직원 관리 병동 필터 선택지 — 활성 병동(표시 순서 · 코드 순) + 「미배정」.
     *  listWards 는 비활성 병동도 돌려주므로(병동 관리 화면이 상태별로 보여야 해서) 여기서 뺀다. */
    employeeWardFilterOptions(): { id: string; name: string }[] {
      const wards = (this.wardList as Record<string, unknown>[])
        .filter((w) => w.isActive !== false)
        .sort(
          (a, b) =>
            ((a.displayOrder as number) || 0) - ((b.displayOrder as number) || 0) ||
            String(a.code || "").localeCompare(String(b.code || ""), "ko", { numeric: true }),
        )
        .map((w) => ({ id: w.id as string, name: w.name as string }));
      return [...wards, { id: "__none", name: "미배정" }];
    },

    /** 멤버 행별 [팀 관리] 모달 상태.
     *  current: 현재 소속 팀 (memberId 기준 — 제거할 때 필요)
     *  removeIds: 저장 시 제거할 memberId
     *  addIds: 저장 시 추가할 teamId */
    showMemberTeamsModal: false,
    memberTeamsEmployee: null as { id: string; name: string; employeeNumber: string } | null,
    memberTeamsCurrent: [] as {
      memberId: string;
      teamId: string;
      teamName: string;
    }[],
    memberTeamsRemoveIds: new Set<string>(),
    memberTeamsAddIds: new Set<string>(),
    memberTeamsSearch: "",
    // 병동 배정(단일 모드) — 직원 정보 수정 팝업과 같은 줄 편집(@hereby/components ward-assignment-rows)
    memberTeamsWards: [] as WardRow[],
    memberTeamsWardsRemoved: [] as RemovedWardRow[],
    memberTeamsWardSeq: 0,
    // 교대 형태 선택지 — 템플릿에서 모듈 상수를 못 읽어 상태로 노출
    memberTeamsWorkTypes: NURSE_WORK_TYPES,

    /** 일괄 팀 관리 모달 상태 (여러 멤버 동시 팀 추가·가산).
     *  bulk=true 면 '현재 소속 팀' 섹션을 숨기고 팀 추가(가산)만 노출한다.
     *  bulkMemberIds/Source 는 저장 시 empId 해석에 사용한다. */
    memberTeamsBulk: false,
    memberTeamsBulkMembers: [] as { employeeId: string; name: string }[],
    memberTeamsBulkMemberIds: [] as string[],
    memberTeamsBulkSource: "" as string,

    /** 선택한 여러 멤버를 대상으로 일괄 팀 관리(가산) 모달을 연다.
     *  memberIds 는 선택 행(TeamMember.id) 리스트. source 는 현재 뷰
     *  (workspace 전체 구성원 또는 특정 팀)로, 저장 시 empId 해석에 쓴다. */
    openBulkMemberTeamsManager(memberIds: string[]) {
      if (!memberIds || memberIds.length === 0) {
        return;
      }
      const sourceTeamId = (this.orgSelected.id as string) || "workspace";
      // 행 id 해석은 캐시 전체에서 — 하위 조직 포함 뷰의 행은 sourceTeamId 의 컴포지션에 없다.
      const members = this.resolveMemberRefs(memberIds)
        .map((ref) => ({
          employeeId: ref.employeeId,
          name: (ref.row.employeeName as string) || (ref.row.name as string) || "",
        }))
        .filter((m) => m.employeeId);
      if (members.length === 0) {
        return;
      }
      this.memberTeamsBulk = true;
      this.memberTeamsBulkMembers = members;
      this.memberTeamsBulkMemberIds = [...memberIds];
      this.memberTeamsBulkSource = sourceTeamId;
      this.memberTeamsEmployee = null;
      this.memberTeamsCurrent = [];
      this.memberTeamsRemoveIds = new Set<string>();
      this.memberTeamsAddIds = new Set<string>();
      this.memberTeamsSearch = "";
      this.showMemberTeamsModal = true;
    },

    /** 팀원 목록의 [직원 정보 수정] 진입점 — 직원 관리 탭의 편집 아이콘과 동일 UX.
     *  행 shape이 membership(fullName·employeeName 등 표기용) 이라 raw employee 필드가
     *  없으므로, 현재 페이지 로스터에서 찾은 뒤 없으면 서버에서 fresh 로 가져와 패널에 전달. */
    async openMemberEmployeeEdit(employeeId: string) {
      if (!this.canEditMemberEmployee()) {
        return;
      }
      const found =
        (this.employees as Record<string, unknown>[]).find((e) => e.id === employeeId) ||
        (this.unassignedMembers as Record<string, unknown>[]).find((e) => e.id === employeeId);
      let emp: Record<string, unknown> | undefined = found;
      if (!(emp && "lastName" in emp)) {
        try {
          const detail = await this.sdk.employee.getEmployee(employeeId);
          if (detail) {
            emp = detail as unknown as Record<string, unknown>;
          }
        } catch {
          // ignore — 아래에서 emp 미존재 처리.
        }
      }
      if (!emp) {
        return;
      }
      // biome-ignore lint/suspicious/noExplicitAny: Alpine dispatch magic
      (this as any).$dispatch("edit-employee-open", emp);
    },

    async openMemberTeamsManager(employeeId: string) {
      // 행 출처가 employees(페이지 단위)·allMembers·unassignedMembers 중 무엇이든
      // 찾을 수 있게 전체 로스터에서 폴백 조회한다(org 전체 뷰 행은 현재 페이지에
      // 없을 수 있음). 이름/사번 표기용이며, 실제 소속은 아래 getEmployee 로 조회.
      const emp =
        (this.employees as Record<string, unknown>[]).find((e) => e.id === employeeId) ||
        (this.allMembers as Record<string, unknown>[]).find((e) => e.employeeId === employeeId) ||
        (this.unassignedMembers as Record<string, unknown>[]).find((e) => e.id === employeeId);
      if (!emp) {
        return;
      }
      this.memberTeamsEmployee = {
        id: employeeId,
        name:
          (emp.name as string) || (emp.employeeName as string) || (emp.fullName as string) || "",
        employeeNumber: (emp.employeeNumber as string) || "",
      };
      this.memberTeamsCurrent = [];
      this.memberTeamsRemoveIds = new Set<string>();
      this.memberTeamsAddIds = new Set<string>();
      this.memberTeamsSearch = "";
      this.memberTeamsBulk = false;
      this.memberTeamsBulkMembers = [];
      this.memberTeamsBulkMemberIds = [];
      this.memberTeamsBulkSource = "";
      this.memberTeamsWards = [];
      this.memberTeamsWardsRemoved = [];
      this.showMemberTeamsModal = true;
      try {
        const detail = await this.sdk.employee.getEmployee(employeeId);
        const teams = (detail?.teams || []) as Record<string, unknown>[];
        this.memberTeamsCurrent = teams.map((t) => ({
          memberId: (t.memberId as string) || "",
          teamId: (t.id as string) || "",
          teamName: (t.name as string) || "",
        }));
        this.memberTeamsWards = wardRowsFromAssignments(detail?.wards);
        if (this.canEditMemberWards()) {
          await this.ensureWardOptions(true);
        }
      } catch (err) {
        this.notify(err instanceof Error ? err.message : String(err));
      }
    },

    closeMemberTeamsManager() {
      this.showMemberTeamsModal = false;
      this.memberTeamsEmployee = null;
      this.memberTeamsCurrent = [];
      this.memberTeamsRemoveIds = new Set<string>();
      this.memberTeamsAddIds = new Set<string>();
      this.memberTeamsSearch = "";
      this.memberTeamsBulk = false;
      this.memberTeamsBulkMembers = [];
      this.memberTeamsBulkMemberIds = [];
      this.memberTeamsBulkSource = "";
      this.memberTeamsWards = [];
      this.memberTeamsWardsRemoved = [];
    },

    /**
     * 팀 관리 명단의 [직원 정보 수정]을 보여 줄지 — 서버 employee.update 와 같은 기준(직원 수정 권한).
     * 본인 범위(update.self)만 있으면 남의 정보는 저장이 막히므로 팀 · 전체 범위일 때만 보여 준다.
     */
    canEditMemberEmployee(): boolean {
      const store = Alpine.store("hereby");
      return (
        store.hasPermission("employee:update.all") || store.hasPermission("employee:update.team")
      );
    },

    /** 병동 배정을 고칠 수 있는지 — 간호 스케줄 등록 · 수정 · 삭제 권한이 모두 있어야 한다(없으면 읽기 전용) */
    canEditMemberWards(): boolean {
      const store = Alpine.store("hereby");
      return ["create", "update", "delete"].every((action) =>
        store.hasPermission(`nurse-scheduler:${action}`),
      );
    },

    /** 저장 후 남을 팀(제거 예약 제외 + 추가 선택)에 연결된 병동 — 「소속 팀 연결」 라벨 · 새 줄 기본값 */
    memberTeamsLinkedWardIds(): string[] {
      const teamIds = [
        ...this.memberTeamsCurrent
          .filter((t) => !this.memberTeamsRemoveIds.has(t.memberId))
          .map((t) => t.teamId),
        ...Array.from(this.memberTeamsAddIds),
      ];
      return teamIds
        .map(
          (id) =>
            (this.teams as unknown as Record<string, unknown>[]).find((t) => t.id === id)?.wardId as
              | string
              | undefined,
        )
        .filter((id): id is string => !!id);
    },

    memberTeamsWardOptions(): WardOption[] {
      return (this.wardList as Record<string, unknown>[]).map((w) => ({
        id: w.id as string,
        name: (w.name as string) || "",
        isActive: w.isActive !== false,
      }));
    },

    addMemberTeamsWardRow() {
      this.memberTeamsWardSeq += 1;
      const wardId = suggestWardId(this.memberTeamsWards, this.memberTeamsLinkedWardIds());
      this.memberTeamsWards.push(
        createWardRow(
          `ward-new-${this.memberTeamsWardSeq}`,
          wardId,
          this.memberTeamsWardOptions(),
          this.scheduleGroupList,
        ),
      );
    },

    removeMemberTeamsWardRow(index: number) {
      removeWardRow(this.memberTeamsWards, index, this.memberTeamsWardsRemoved);
    },

    /** 병동을 바꾸면 스케줄러는 그 병동의 기본으로 되돌린다 */
    onMemberTeamsWardChange(row: WardRow) {
      syncRowWard(row, this.memberTeamsWardOptions(), this.scheduleGroupList);
    },

    onMemberTeamsGroupChange(row: WardRow) {
      syncRowScheduleGroup(row, this.scheduleGroupList);
    },

    memberTeamsWardSelectOptions(row: WardRow): { id: string; name: string }[] {
      return wardOptionsForRow(
        row,
        this.memberTeamsWards,
        this.memberTeamsWardOptions(),
        this.scheduleGroupList,
      );
    },

    /** 이 줄의 스케줄러 선택지 — 고른 병동의 운영 중인 스케줄러 중 다른 줄이 안 쓰는 것 */
    memberTeamsGroupSelectOptions(
      row: WardRow,
    ): { id: string; name: string; isDefault: boolean }[] {
      return scheduleGroupOptionsForRow(row, this.memberTeamsWards, this.scheduleGroupList);
    },

    memberTeamsWardBadge(row: WardRow): string {
      return wardRowBadge(row, this.memberTeamsWards, this.memberTeamsLinkedWardIds());
    },

    memberTeamsWardError(): string {
      return wardRowsError(this.memberTeamsWards);
    },

    memberTeamsHasWardChanges(): boolean {
      return hasWardAssignmentChanges(this.memberTeamsWards, this.memberTeamsWardsRemoved);
    },

    toggleMemberTeamRemove(memberId: string) {
      if (this.memberTeamsRemoveIds.has(memberId)) {
        this.memberTeamsRemoveIds.delete(memberId);
      } else {
        this.memberTeamsRemoveIds.add(memberId);
      }
      // Alpine 반응성 트리거를 위해 새 Set 으로 재할당.
      this.memberTeamsRemoveIds = new Set(this.memberTeamsRemoveIds);
    },

    toggleMemberTeamAdd(teamId: string) {
      const autoKey = `ward-auto-${teamId}`;
      if (this.memberTeamsAddIds.has(teamId)) {
        this.memberTeamsAddIds.delete(teamId);
        // 이 팀 때문에 넣어 둔 병동 줄이 아직 저장 전이면 함께 뺀다
        const idx = this.memberTeamsWards.findIndex((w) => w.key === autoKey && !w.profileId);
        if (idx >= 0) {
          this.memberTeamsWards.splice(idx, 1);
        }
      } else {
        this.memberTeamsAddIds.add(teamId);
        // 단일 모드 — 병동이 연결된 팀이면 그 병동 줄을 미리 넣어 보여 준다(저장하면 함께 반영, 필요 없으면 지우면 됨)
        const wardId = (this.teams as unknown as Record<string, unknown>[]).find(
          (t) => t.id === teamId,
        )?.wardId as string | undefined;
        if (
          !this.memberTeamsBulk &&
          wardId &&
          this.canEditMemberWards() &&
          !this.memberTeamsWards.some((w) => w.wardId === wardId)
        ) {
          this.memberTeamsWards.push(
            createWardRow(autoKey, wardId, this.memberTeamsWardOptions(), this.scheduleGroupList),
          );
        }
      }
      this.memberTeamsAddIds = new Set(this.memberTeamsAddIds);
    },

    /** 모달에서 선택 가능한 팀 후보 = 전체 팀 − 현재 소속(제거 표시되지 않은 것).
     *  검색어가 있으면 이름 부분 일치로 필터링. */
    // biome-ignore lint/suspicious/noExplicitAny: dynamic team rows
    memberTeamsAddCandidates(): any[] {
      const currentTeamIds = new Set(
        this.memberTeamsCurrent
          .filter((t) => !this.memberTeamsRemoveIds.has(t.memberId))
          .map((t) => t.teamId),
      );
      const q = (this.memberTeamsSearch || "").toLowerCase().trim();
      return (this.teams as unknown as Record<string, unknown>[])
        .filter((t) => !currentTeamIds.has(t.id as string))
        .filter((t) => !q || ((t.name as string) || "").toLowerCase().includes(q))
        .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
    },

    async saveMemberTeamsManager() {
      if (this.memberTeamsBulk) {
        await this.saveBulkMemberTeams();
        return;
      }
      if (!this.memberTeamsEmployee) {
        return;
      }
      const empId = this.memberTeamsEmployee.id;
      const toRemove = this.memberTeamsCurrent.filter((t) =>
        this.memberTeamsRemoveIds.has(t.memberId),
      );
      const toAdd = Array.from(this.memberTeamsAddIds);
      if (this.memberTeamsWardError()) {
        return;
      }
      try {
        // 제거 → 추가 두 묶음으로 나눠 각 묶음 안에서는 동시에 보낸다. SDK 의 tRPC
        // httpBatchLink 가 같은 틱의 호출을 한 HTTP 왕복으로 묶으므로, 팀 수만큼
        // 직렬 왕복하던 것이 2회로 줄어든다. (같은 팀을 제거 후 다시 추가한 경우
        // 재활성화가 제거보다 먼저 실행되면 안 되므로 두 묶음은 순서를 지킨다.)
        await Promise.all(toRemove.map((m) => this.sdk.team.removeMember(m.teamId, m.memberId)));
        // 병동 배정은 이 팝업의 「병동 배정」 줄이 정본 — 편집할 수 있으면 서버 자동 배정을 끈다(none)
        const wardAssignment = this.canEditMemberWards() ? ("none" as const) : ("auto" as const);
        const joined = await Promise.all(
          toAdd.map((teamId) =>
            this.sdk.team.addMember({ teamId, employeeId: empId, wardAssignment }),
          ),
        );
        // 병동 배정 — 간호 프로필 삭제 → 수정 → 추가(스케줄러 · 근무표에 바로 반영)
        if (this.canEditMemberWards() && this.memberTeamsHasWardChanges()) {
          await applyWardAssignmentChanges(
            this.sdk.nurseScheduler,
            empId,
            this.memberTeamsWards,
            this.memberTeamsWardsRemoved,
          );
        }
        // 목록·팀 트리·변경된 팀의 composition 을 한 묶음으로 재조회 — 현재 보고
        // 있는 팀의 멤버 행 표시가 즉시 반영된다.
        const changedTeamIds = [...toRemove.map((m) => m.teamId), ...toAdd];
        await Promise.all([
          this.loadEmployees(),
          this.loadTeams(),
          this.refreshOrgTeamCompositions(changedTeamIds),
        ]);
        // 팀 관리 탭 전체 로스터(allMembers·미소속)는 화면을 막지 않게 뒤에서 갱신.
        this.loadUnassignedMembers();
        this.notify("팀 소속을 저장했습니다");
        this.notifyWardScheduler(joined.map((j) => j.wardAssignment.scheduler));
        this.closeMemberTeamsManager();
      } catch (err) {
        this.confirmTitle = "저장 실패";
        this.confirmMessage = err instanceof Error ? err.message : String(err);
        this.confirmLabel = this._t("common.confirm");
        this.confirmHideCancel = true;
        this.confirmAction = null;
        this.showConfirm = true;
      }
    },

    /** 일괄 모달 저장 — 선택 멤버들을 선택한 팀들에 가산(기존 소속 유지).
     *  모달의 저장 버튼이 곧 확인이므로 별도 confirm 없이 바로 커밋한다.
     *  이미 소속된 팀에 대한 addMember 오류는 무시하고 계속한다(가산 정책). */
    async saveBulkMemberTeams() {
      const toAdd = Array.from(this.memberTeamsAddIds);
      if (toAdd.length === 0) {
        return;
      }
      const source = this.memberTeamsBulkSource;
      // 행 id 해석은 캐시 전체에서(openBulkMemberTeamsManager 와 같은 규칙) — 하위 조직 포함
      // 뷰의 행은 source 팀의 컴포지션에 없어서, source 목록만 뒤지면 그 행은 조용히 빠졌다.
      const refs = this.resolveMemberRefs(this.memberTeamsBulkMemberIds);
      const empIds = [...new Set(refs.map((ref) => ref.employeeId).filter(Boolean))];
      if (empIds.length === 0) {
        return;
      }
      try {
        // 팀 × 인원 조합을 전부 동시에 보낸다 — httpBatchLink 가 한 틱의 호출을
        // 10건 단위 HTTP 요청으로 묶어 병렬 전송하므로, 직렬 왕복(팀 수 × 인원 수)이
        // 사라진다. 이미 소속 등의 개별 실패는 무시하고 계속한다(가산 정책).
        const joined = await Promise.all(
          toAdd.flatMap((teamId) =>
            empIds.map((empId) =>
              this.sdk.team.addMember({ teamId, employeeId: empId }).catch(() => undefined),
            ),
          ),
        );
        // 행이 실제로 속한 팀(하위 조직 포함 뷰면 하위 팀)도 같이 — 병동 연결 팀에 넣으면
        // 병동 배정이 생겨 그 행의 병동 열이 바뀐다.
        const changedTeamIds = [
          ...toAdd,
          ...(source !== "workspace" ? [source] : []),
          ...refs.map((ref) => ref.teamId),
        ];
        // 목록·팀 트리·전체 로스터·변경된 팀 composition 을 한 묶음으로 재조회 —
        // 현재 보고 있는 팀의 멤버 행 표시가 즉시 반영된다.
        await Promise.all([
          this.loadEmployees(),
          this.loadTeams(),
          this.loadUnassignedMembers(),
          this.refreshOrgTeamCompositions(changedTeamIds),
        ]);
        this.notify(`${empIds.length}명을 ${toAdd.length}개 팀에 배정했습니다`);
        this.notifyWardScheduler(joined.map((j) => j?.wardAssignment.scheduler));
        // 툴바의 로컬 선택 상태(selectedMemberIds)를 초기화하도록 알린다.
        window.dispatchEvent(new CustomEvent("bulk-member-teams-applied"));
        this.closeMemberTeamsManager();
      } catch (err) {
        this.confirmTitle = "저장 실패";
        this.confirmMessage = err instanceof Error ? err.message : String(err);
        this.confirmLabel = this._t("common.confirm");
        this.confirmHideCancel = true;
        this.confirmAction = null;
        this.showConfirm = true;
      }
    },

    /** Shorthand for $store.hereby.t(key) with fallback */
    _t(key: string): string {
      return Alpine.store("hereby").t(key) || key;
    },

    /** 조직별 낱말 — _term("role") → "역할" 또는 병원이 정한 낱말(설정 › 조직 설정 › 용어 표시) */
    _term(key: "role"): string {
      return Alpine.store("hereby").term(key);
    },

    /** 서버 오류를 사람이 읽을 문장으로 — tRPC 의 message 는 이미 한국어 안내문이다. */
    _errMessage(err: unknown): string {
      return err instanceof Error ? err.message : String(err);
    },

    /**
     * "미소속" members — employees that belong to no team (empty memberships).
     * The org UI surfaces these as a special "미소속" group (member list).
     * Backed by `unassignedMembers` (a dedicated org-wide load), NOT the
     * paginated/filtered `employees` table — otherwise unassigned members on
     * another page or excluded by a filter would make the group vanish.
     */
    get noTeamMembers(): Record<string, unknown>[] {
      return this.unassignedMembers;
    },

    /**
     * Design-system toast feedback. Thin wrapper over the global `toast`
     * store so CRUD methods can give consistent add/delete feedback.
     */
    notify(message: string, variant: "success" | "error" | "info" = "success", autoClose?: number) {
      Alpine.store("toast")[variant](message, autoClose);
    },

    /** 자동 병동 배정이 스케줄러가 여럿인 병동의 기본 스케줄러로 들어갔으면 알린다.
     *  이 화면들엔 스케줄러가 안 보여서, 알리지 않으면 다른 스케줄러 소속이어야 할 간호사가 기본 스케줄러 인력에 조용히 남는다.
     *  문구가 길어 기본 3초로는 못 읽어 6초 둔다. */
    notifyWardScheduler(notes: ReadonlyArray<WardSchedulerNote | null | undefined>) {
      const hint = wardSchedulerHint(notes);
      if (hint) {
        this.notify(hint, "info", 6000);
      }
    },

    /* ──────────── init ──────────── */
    async init() {
      this.bindApprovalReplaceEvents();
      observeAppAnalytics(this, () =>
        this.dataReady
          ? this.activeTab === "settings"
            ? `settings_${this.settingsSubTab === "docTemplates" ? "doc_templates" : this.settingsSubTab}`
            : this.activeTab === "embed"
              ? `embed_${this.currentEmbed}`
              : this.activeTab
          : undefined,
      );
      if (this.dataReady) {
        return;
      }
      this.registerEmployeeCard();
      // init() 은 두 번 불릴 수 있다(Alpine 자동 호출 + x-init) — 리스너가 둘이면 단축키가 두 번 토글돼 아무 일도 안 일어난다
      if (!this.fullscreenListenersBound) {
        this.fullscreenListenersBound = true;
        document.addEventListener("fullscreenchange", () => this.onBrowserFullscreenChange());
        // capture 단계 — 스케줄러 그리드처럼 자기 키를 잡고 전파를 끊는 핸들러보다 먼저 받아야 단축키가 어디서나 듣는다
        window.addEventListener("keydown", (e) => this.handleFullscreenShortcut(e), true);
        // 알림 벨 → 출퇴근 정정 요청 알림은 근태 현황 「정정 요청」 탭으로. 공용 벨의 목적지 표는 직원 앱
        // 기준(결재 › 근무)이라 admin 은 알림 종류(type)로 직접 가른다. 처리했으면 preventDefault —
        // 안 하면 벨이 「내부 결재에서 확인해 주세요」 안내를 띄운다.
        window.addEventListener("navigate-section", (e: Event) => {
          const detail = (e as CustomEvent<{ type?: string }>).detail;
          if (detail?.type !== NotificationType.ATTENDANCE_CORRECTION) {
            return;
          }
          e.preventDefault();
          this.switchTab("attendance");
          this.switchAttendanceTab("correction");
        });
        // 알림 벨 → 예약 변경 알림(실패·확인 필요·건너뜀·예고·취소·이관)은 「예약된 변경」 페이지의 그 한 건으로.
        // 처리했으면 preventDefault — 안 하면 벨이 「관리자에서 확인해 주세요」 안내를 띄운다.
        window.addEventListener("navigate-reference", (e: Event) => {
          const detail = (e as CustomEvent<{ referenceType?: string; referenceId?: string }>)
            .detail;
          if (detail?.referenceType !== "scheduled_action" || !detail.referenceId) {
            return;
          }
          e.preventDefault();
          this.openScheduledChanges({ id: detail.referenceId });
        });
        // 「예약된 변경」 행의 「시트에서 열기」 → 시트 임베드를 그 시트로
        window.addEventListener(SCHEDULED_CHANGES_OPEN_SHEET_EVENT, (e: Event) => {
          const id = (e as CustomEvent<{ sheetViewId?: string }>).detail?.sheetViewId;
          if (id) {
            this.openSheetInEmbed(id);
          }
        });
      }
      // 팀 헤더 연결 병동 팝오버의 닫기 감시는 여기(루트)에서 — $watch 는 부른 요소에 묶여서, 버튼(x-if 안)에서
      // 처음 등록하면 팀을 바꿀 때 그 요소와 함께 감시가 지워져 팝오버가 남는다(QA MED-97)
      this.bindTeamWardPopListeners();
      this.bindLeaveDetailPopListeners();
      this.bindTeamLinePopListeners();
      // Pristine-close guard: snapshot each form modal's state the moment it
      // opens (Alpine flushes watchers after the open fn's sync populate).
      for (const showVar of Object.keys(this.closeGuardForms)) {
        // biome-ignore lint/suspicious/noExplicitAny: Alpine magic property
        (this as any).$watch(showVar, (open: boolean) => {
          if (open) {
            this.closeGuardBaselines[showVar] = this.closeGuardState(showVar);
          } else {
            // 자식 폼이 닫힌 순간 기록 — 컨테이너 ESC 연타 가드(아래 type-manager 핸들러)에서 사용.
            this.tmChildClosedAt = Date.now();
          }
        });
      }
      const store = Alpine.store("hereby");
      const router = Alpine.store("router");
      // supportMode: 플랫폼 스태프의 grant 세션 — admin 게이트 통과 (meta account
      // Phase B). 실제 권한은 서버가 grant 스코프로 강제하므로 게이트는 UX일 뿐.
      // 단, 조직 시스템 관리자의 권한 미리보기(ORG_DEBUG impersonation)는 대상 직원 그대로
      // 판정한다(isSystemAdmin 만) — 그 직원이 실제로 보는 「접근 불가」를 재현해야 한다(MED-713).
      const isSupportSession =
        (store.currentUser as Record<string, unknown> | null)?.supportMode === true &&
        !this.isOrgDebugImpersonation();
      // 병동 페이지 관리자(ward:manage) — 시스템 관리자가 아니어도 들어온다.
      // 다만 LNB 는 「병동 관리」와 스케줄러만 보이고(lnbConfig), 들어오는 첫 화면도 병동 관리다.
      // 실제 차단은 서버가 하므로 이 게이트는 화면 안내일 뿐이다(PM-01 / V-08).
      // 권한 미리보기(ORG_DEBUG) 중에는 store 가 대상 직원의 권한을 들고 있으므로,
      // 대상이 병동 페이지 관리자면 미리보기도 그 사람이 보는 화면 그대로 재현된다.
      // 전체 입장 = 시스템 관리자(검사 생략) · 지원 세션 · 조직 전체 범위 관리 권한(core canEnterAdminFull, 2026-10-21)
      const fullEntry =
        store.currentUser?.isSystemAdmin ||
        isSupportSession ||
        canEnterAdminFull(store.currentUser?.permissions as string[] | undefined);
      const restricted = !fullEntry;
      this.isWardOnlyAdmin = restricted && store.hasPermission("ward:manage");
      // 결재자(비 시스템 관리자) — 승인 권한이 있는 결재 화면만 쓰러 들어온다(2026-10-06).
      this.approverEmbeds = restricted
        ? this.approverMenuItems().map((item) => item.id.slice("embed:".length))
        : [];
      this.isApproverAdmin = this.approverEmbeds.length > 0;
      if (this.isApproverAdmin) {
        // 결재자의 첫 화면은 「대기가 있는 첫 종류」 — 결재함 숫자를 먼저 읽는다(실패하면 0)
        await this.loadApprovalInbox();
      }
      if (!(fullEntry || this.isRestrictedAdmin)) {
        this.accessDenied = true;
        return;
      }

      // 병동 페이지 관리자는 쓸 수 있는 버튼만 보인다(나머지는 LNB 에도 없다).
      // 그래도 화면 껍데기가 배경으로 읽는 것 중 권한 밖이 남을 수 있는데, 그 403 이
      // 공용 「권한 요청」 팝업을 띄우면 **아무것도 누르지 않았는데** 팝업이 뜬다.
      // SDK 가 열어 둔 취소 훅으로 그 자동 팝업만 막는다 — 사용자가 직접 누른 버튼은
      // 애초에 권한 있는 것만 보이므로 여기서 잃는 안내가 없다(PM-01 검증).
      if (this.isRestrictedAdmin) {
        window.addEventListener("hereby:permission-error", (ev) => {
          ev.preventDefault();
          // 다만 **조용히 삼키지는 않는다** — 눌러서 난 거절이면 왜 안 됐는지 알아야 한다.
          // 배경 조회의 거절은 같은 토스트가 한 번 스쳐 지나갈 뿐이라 화면을 막지 않는다.
          const detail = (ev as CustomEvent).detail as { message?: string } | undefined;
          Alpine.store("toast")?.error?.(detail?.message || "권한이 없어 처리하지 못했어요.");
        });
      }

      this.accessDenied = false;
      // 제한 입장의 첫 화면 — 결재자는 첫 결재 메뉴, 병동 관리자는 병동 관리
      const restrictedHome = this.isApproverAdmin ? "embed" : "wards";
      this.activeTab = router.tab || (this.isRestrictedAdmin ? restrictedHome : "employees");
      // 쓸 수 없는 탭으로 해시가 남아 있을 수 있다 — 첫 화면으로 되돌린다
      if (this.isRestrictedAdmin && !this.restrictedAdminTabs.includes(this.activeTab)) {
        this.activeTab = restrictedHome;
        // 결재 화면은 아래 임베드 복원이 주소를 맞춘다 — 병동 관리는 여기서 맞춘다
        if (restrictedHome !== "embed") {
          router.navigateTab(restrictedHome, undefined, { replace: true });
        }
      }
      if (this.activeTab === "settings" && router.settingsSub) {
        this.settingsSubTab = router.settingsSub;
      }
      // Restore the selected embed from the URL hash (#embed/<key>) so
      // browser refresh keeps the user on the same iframe page. Per
      // 2026-06 user report: "after refresh, the page is not loading".
      if (this.activeTab === "embed" && router.embedSub) {
        this.currentEmbed = router.embedSub;
      }
      // 결재자는 승인 권한이 있는 결재 화면만 — 해시가 다른 화면이면 첫 결재 화면으로
      if (
        this.isApproverAdmin &&
        this.activeTab === "embed" &&
        !this.approverEmbeds.includes(this.currentEmbed)
      ) {
        this.currentEmbed = this.firstApproverEmbed();
        router.navigateTab("embed", this.currentEmbed);
      }
      // 팀 관리 트리 선택 복원(#organization/ward:<id> 등, 없으면 localStorage). 트리가
      // 아직 없어 표시만 먼저 맞추고, loadData 뒤 restoreOrgSelection 이 검증·확정한다.
      if (this.activeTab === "organization") {
        const remembered = this.decodeOrgSelection(
          router.orgSub || readLastSelection<string>(this.orgSelectionStorageKey()) || "",
        );
        if (remembered) {
          this.orgSelected = remembered;
        }
      }
      // 병동 관리도 같은 방식 — 해시(#wards/<id>) · localStorage 의 선택을 먼저 표시하고, loadData 뒤 restoreWardSelection 이 검증한다
      if (this.activeTab === "wards") {
        const raw =
          router.wardSub || readLastSelection<string>(this.wardSelectionStorageKey()) || "";
        if (/^[0-9a-f-]{36}$/i.test(raw)) {
          this.wardSelected = { type: "ward", id: raw };
        }
      }
      // 직원 관리 필터·정렬 복원 — 새로고침해도 걸어 둔 필터가 남게(CS-OKBEST-260923-0001).
      // loadData 앞이어야 첫 조회부터 그 필터로 나간다.
      this.restoreEmployeeListState();
      this.dataReady = true;
      await this.loadData();
      // Team list is used by the PageHeader team filter on team-manager iframes.
      // Fire-and-forget — failure shouldn't block the dashboard.
      this.loadMyTeams();
      // 조직 정보는 LNB 브랜드 영역(병원 로고)이 매 화면에서 읽는다. 예전에는 설정 탭
      // 로더에서만 불러서, 설정을 한 번도 안 연 화면에서는 로고가 없는 것처럼 보였다.
      // 실패해도 hereby 심볼로 폴백하므로 대시보드를 막지 않는다(fire-and-forget).
      this.loadOrgInfo();
      // 문서 결재 미처리(pending) 건수 → LNB 뱃지
      this.loadDocApprovalPending();
      // 결재선 결재함 숫자 → 결재 메뉴 뱃지(결재자 제한 입장)
      this.loadApprovalInbox();
      if (this.activeTab === "leave") {
        this.loadLeaveBalances();
        this.loadEntitlementRules();
        this.loadLeaveReasons();
      }
      if (this.activeTab === "esign") {
        this.startESignPolling();
      }
      if (this.activeTab === "settings") {
        await this.loadSettingsData();
        await this.loadSettingsSubTabData(this.settingsSubTab);
      }
      if (this.activeTab === "parameters") {
        this.loadOrganizationParameters();
      }
      if (this.activeTab === "onboarding") {
        window.dispatchEvent(new Event("app-onboarding-refresh"));
      }
      if (this.activeTab === "wards") {
        this.loadWards();
      }
      // 온보딩 진행률 배너 — 발송·완료 처리 뒤에도 다시 읽는다
      this.loadOnboardingBanner();
      window.addEventListener("app-guide-sent", () => this.loadOnboardingBanner());
      window.addEventListener("app-onboarding-changed", () => this.loadOnboardingBanner());
    },

    /* ──────────── data loading ──────────── */
    async loadData() {
      const tab = this.activeTab;
      const version = ++dataLoadVersion;
      this.loadingData = true;
      try {
        // 화면의 목록뿐 아니라 필터·편집창이 읽는 데이터까지 함께 준비한다.
        // 전체 직원 명부는 조직 트리에만, 권한 카탈로그는 역할 화면에만 필요하다.
        // 임베드·스케줄러는 각각 자신의 컴포넌트에서 데이터를 조회한다.
        const loaders: Record<string, (() => Promise<void>)[]> = {
          employees: [
            this.loadEmployees,
            this.loadTeams,
            this.loadRoles,
            this.loadShifts,
            this.loadShiftGroups,
          ],
          organization: [
            this.loadEmployees,
            this.loadUnassignedMembers,
            this.loadTeams,
            this.loadRoles,
            this.loadShifts,
            this.loadShiftGroups,
          ],
          teams: [
            this.loadEmployees,
            this.loadUnassignedMembers,
            this.loadTeams,
            this.loadRoles,
            this.loadShifts,
            this.loadShiftGroups,
          ],
          roles: [this.loadRoles, this.loadPermissions, this.loadAllTeams],
          // 병동 관리 = 목록 + 상세 — 상세 「인력」 명단은 팀 관리와 같은 전체 로스터(allMembers)를 병동 배정으로 거른다
          wards: [
            this.loadWards,
            this.loadTeams,
            this.loadUnassignedMembers,
            this.loadShifts,
            this.loadShiftGroups,
          ],
          attendance: [this.loadTeams, this.loadCorrectionPendingCount],
          leave: [this.loadTeams],
          offsite: [this.loadTeams],
          documents: [this.loadEmployees, this.loadTeams],
          settings: [this.loadEmployees, this.loadAllTeams],
        };
        const tasks = (loaders[tab] || []).map((load) => load.call(this));
        // Refresh e-sign data only when the tab is active to avoid
        // unnecessary Hereby API calls on every global refresh.
        if (tab === "esign") {
          tasks.push(this.loadESignIntegrationStatus());
        }
        await Promise.allSettled(tasks);
        this.computeStats();
        // 통계 핀은 팀 · 역할 수까지 다 센 뒤에 보인다 — 직원 목록만 먼저 오면 「팀 0 · 역할 0」이 잠깐 보였다
        this.statsReady = true;
        // 팀·병동 선택지는 loadTeams가 모두 준비한 뒤 검증한다. 그사이 다른 탭으로
        // 이동했거나 사용자가 직접 선택했다면 현재 선택을 덮어쓰지 않는다.
        if (
          version === dataLoadVersion &&
          this.activeTab === "organization" &&
          tab === "organization"
        ) {
          this.restoreOrgSelection();
        }
        // 병동 관리도 같은 규칙 — 병동 목록이 온 뒤 저장된 선택(해시 · localStorage)을 검증해 확정한다
        if (version === dataLoadVersion && this.activeTab === "wards" && tab === "wards") {
          this.restoreWardSelection();
        }
      } finally {
        if (version === dataLoadVersion) {
          this.loadingData = false;
        }
      }
    },

    /** 직원 관리 필터·정렬 저장 키 — 조직·사용자별(한 브라우저에서 워크스페이스·계정을 오간다). */
    employeeListStateKey(): string {
      const user = Alpine.store("hereby").currentUser as
        | { id?: string; organizationId?: string }
        | null
        | undefined;
      return employeeListStateKey(user?.organizationId, user?.id);
    },

    /** 저장된 필터·정렬을 되살린다. 없거나 모양이 아니면 그대로 둔다. */
    restoreEmployeeListState() {
      const saved = readEmployeeListState(this.employeeListStateKey());
      if (!saved) {
        return;
      }
      Object.assign(this.employeeFilter, saved.filter);
      this.employeeSort.by = saved.sort.by;
      this.employeeSort.order = saved.sort.order;
    },

    /** 목록을 조회할 때마다 지금 필터·정렬을 기억한다 — 필터를 바꾸는 모든 경로가 loadEmployees 로 모인다. */
    rememberEmployeeListState() {
      writeEmployeeListState(this.employeeListStateKey(), {
        filter: { ...this.employeeFilter },
        sort: { ...this.employeeSort },
      });
    },

    async loadEmployees() {
      const version = ++employeeLoadVersion;
      this.rememberEmployeeListState();
      try {
        const p = this.paging.employees;
        const filter: Record<string, unknown> = {
          page: p.page,
          limit: p.limit,
        };
        if (this.employeeFilter.search) {
          filter.search = this.employeeFilter.search;
        }
        if (this.employeeFilter.statuses && this.employeeFilter.statuses.length > 0) {
          filter.statuses = this.employeeFilter.statuses;
        }
        if (this.employeeFilter.teamIds && this.employeeFilter.teamIds.length > 0) {
          filter.teamIds = this.employeeFilter.teamIds;
        }
        if (this.employeeFilter.roleIds && this.employeeFilter.roleIds.length > 0) {
          filter.roleIds = this.employeeFilter.roleIds;
        }
        if (this.employeeFilter.shiftGroupIds.length > 0) {
          filter.shiftGroupIds = this.employeeFilter.shiftGroupIds;
        }
        if (this.employeeFilter.shiftIds.length > 0) {
          filter.shiftIds = this.employeeFilter.shiftIds;
        }
        if (this.employeeFilter.hireDateFrom) {
          filter.hireDateFrom = this.employeeFilter.hireDateFrom;
        }
        if (this.employeeFilter.hireDateTo) {
          filter.hireDateTo = this.employeeFilter.hireDateTo;
        }
        if (this.employeeFilter.jobDuties.length > 0) {
          filter.jobDuties = this.employeeFilter.jobDuties;
        }
        if (this.employeeFilter.wardIds.length > 0) {
          filter.wardIds = this.employeeFilter.wardIds;
        }
        if (this.employeeFilter.appStages.length > 0) {
          filter.appStages = this.employeeFilter.appStages;
        }
        if (this.employeeFilter.multipleShiftGroups) {
          filter.multipleShiftGroups = true;
        }
        if (this.employeeFilter.shiftDiffersFromDefault) {
          filter.shiftDiffersFromDefault = true;
        }
        if (this.employeeSort.by) {
          filter.sortBy = this.employeeSort.by;
          filter.sortOrder = this.employeeSort.order;
        }
        const result = await this.sdk.employee.getEmployees(filter);
        // 화면 진입 요청보다 나중에 시작한 검색·저장 후 갱신 결과를 우선한다.
        if (version !== employeeLoadVersion) {
          return;
        }
        this.employees = (result?.items || []).map((e: Record<string, unknown>) => ({
          ...e,
          fullName: `${e.lastName || ""}${e.firstName || ""}`.trim() || e.email,
        }));
        p.totalCount = result?.totalCount || 0;
        p.totalPages = result?.totalPages || 0;
        this.employeeStatusCounts = result?.statusCounts || {};
        this.employeesLoaded = true;
      } catch {
        if (version === employeeLoadVersion) {
          this.employees = [];
          this.employeesLoaded = true;
        }
      }
    },

    /**
     * 팀 관리 명단 한 번 읽기(team.roster) — 조직 전체 직원 + 활성 소속을 가벼운 한 응답으로 받아
     * 미소속(unassignedMembers) · 전체 구성원(allMembers) · 트리 인원 지도(rosterTeamMap …) ·
     * **팀별 명단(teamMembers)** 을 전부 여기서 파생한다(MED-914). 예전에는 employee.list 를 1000건씩
     * 끝까지 읽고 팀을 고를 때마다 getComposition 을 따로 불렀다. 같은 틱의 중복 호출은 한 요청으로 합치고,
     * 이미 보낸 요청이 있으면 끝난 뒤 한 번 더 읽는다(모듈 변수 rosterPending/Inflight/Queued).
     * 실패하면 이전 값을 그대로 둔다.
     */
    loadUnassignedMembers(): Promise<void> {
      if (rosterPending) {
        return rosterPending;
      }
      if (rosterInflight) {
        if (!rosterQueued) {
          rosterQueued = rosterInflight.then(() => {
            rosterQueued = null;
            return this.loadUnassignedMembers();
          });
        }
        return rosterQueued;
      }
      const pending: Promise<void> = Promise.resolve().then(() => {
        rosterPending = null;
        rosterInflight = this.fetchTeamRoster().finally(() => {
          rosterInflight = null;
        });
        return rosterInflight;
      });
      rosterPending = pending;
      return pending;
    },

    /** team.roster 를 실제로 읽어 파생 상태를 모두 갱신한다 — loadUnassignedMembers 가 합쳐서 부른다. */
    async fetchTeamRoster() {
      try {
        if (!this.sdk) {
          return;
        }
        const roster = await this.sdk.team.roster();
        const employees = roster?.employees ?? [];
        const memberships = roster?.memberships ?? [];
        type RosterEmployee = (typeof employees)[number];
        type RosterMembership = (typeof memberships)[number];

        const byEmployee = new Map<string, RosterMembership[]>();
        for (const m of memberships) {
          byEmployee.set(m.employeeId, [...(byEmployee.get(m.employeeId) ?? []), m]);
        }
        const membershipsOf = (e: RosterEmployee) =>
          (byEmployee.get(e.id) ?? []).map((m) => ({
            memberId: m.memberId,
            teamId: m.teamId,
            teamName: m.teamName || null,
            roleId: m.roleId,
            roleName: m.roleName,
            isPrimary: m.isPrimary,
          }));

        // 미소속 — 활성 소속이 하나도 없는 직원. 가벼운 행이라 lastName 같은 직원 상세 키는 두지 않는다
        // (openMemberEmployeeEdit 가 「상세가 아니다」로 보고 getEmployee 를 따로 읽게).
        this.unassignedMembers = employees
          .filter((e) => !byEmployee.has(e.id))
          .map((e) => ({
            id: e.id,
            employeeId: e.id,
            fullName: e.name,
            employeeName: e.name,
            employeeNumber: e.employeeNumber,
            email: e.email,
            status: e.status,
            hireDate: e.hireDate,
            isSystemAdmin: e.isSystemAdmin,
            jobDuty: e.jobDuty,
            wards: e.wards,
            memberships: [],
          }));
        // workspace(최상위 조직) 전체 구성원 뷰 — 멤버 테이블과 같은 row shape 으로 정규화(id/employeeId 모두 employeeId:
        // org 스코프엔 팀별 membership 이 없어 일괄작업 헬퍼가 row id 를 employeeId 로 처리).
        this.allMembers = employees.map((e) => {
          const ms = membershipsOf(e);
          const tns = [...new Set(ms.map((m) => m.teamName).filter(Boolean))] as string[];
          return {
            id: e.id,
            employeeId: e.id,
            employeeName: e.name,
            employeeEmail: e.email,
            employeeNumber: e.employeeNumber,
            status: e.status,
            hireDate: e.hireDate,
            isSystemAdmin: e.isSystemAdmin,
            // 소속 팀명 목록 — 리스트의 '팀' 컬럼에 표시(여러 팀이면 콤마 결합).
            teamNames: tns,
            // 정렬용 대표 팀명 — 여러 팀이면 첫 팀명, 없으면 빈 문자열(정렬 시 하단으로 밀림).
            teamName: tns[0] ?? "",
            // 팀+역할 쌍(활성 소속) — 전체 목록의 '소속·역할' 병합 칩 뷰에 사용.
            memberships: ms,
            // 역할은 팀 스코프 값 — org 전체 뷰에선 시스템 역할만 표기(미소속 뷰와 동일).
            role: e.isSystemAdmin ? "시스템 관리자" : "-",
            roleId: "",
            // 직무 · 병동 배정 — 팀 화면 명단과 같은 열을 조직 전체 명단에서도 보여 준다.
            jobDuty: e.jobDuty ?? null,
            wards: e.wards ?? [],
          };
        });
        // 직원별 활성 소속 팀 목록 — 트리 배지의 distinct 인원 계산에 사용.
        const teamMap: Record<string, string[]> = {};
        for (const e of employees) {
          teamMap[e.id] = (byEmployee.get(e.id) ?? []).map((m) => m.teamId);
        }
        this.rosterTeamMap = teamMap;
        this.rosterStatusMap = Object.fromEntries(
          employees.map((e) => [e.id, String(e.status || "")]),
        );
        this.rosterLeftTeamMap = Object.fromEntries(
          employees.filter((e) => e.leftTeamIds.length > 0).map((e) => [e.id, e.leftTeamIds]),
        );

        // 팀별 명단 — getComposition 이 주던 행 모양 그대로(id = 소속 id). 팀을 골라도 서버를 다시 부르지 않는다.
        // 로스터의 소속은 활성만이라 끝난 소속 행이 명단에 남지 않는다. 입사일 · 시스템 관리자도 행에 실어
        // 화면이 페이지 단위 직원 목록(employees)을 뒤지지 않게 한다.
        const employeeById = new Map(employees.map((e) => [e.id, e]));
        const compositions: Record<string, Record<string, unknown>[]> = {};
        for (const m of memberships) {
          const e = employeeById.get(m.employeeId);
          if (!e) {
            continue;
          }
          const list = compositions[m.teamId] ?? [];
          list.push({
            id: m.memberId,
            employeeId: e.id,
            employeeName: e.name,
            employeeNumber: e.employeeNumber,
            employeeEmail: e.email,
            role: m.roleName || "MEMBER",
            roleId: m.roleId,
            status: e.status,
            allocation: 100,
            joinedAt: m.joinedAt,
            jobDuty: e.jobDuty,
            wards: e.wards,
            hireDate: e.hireDate,
            isSystemAdmin: e.isSystemAdmin,
            isActive: m.isActive,
            isPrimary: m.isPrimary,
            shiftGroupId: m.shiftGroupId,
            shiftId: m.shiftId,
          });
          compositions[m.teamId] = list;
        }
        // 예전에 읽어 둔 팀(지금은 소속이 없는 팀)도 빈 목록으로 덮어 옛 행이 남지 않게
        for (const tid of Object.keys(this.teamMembers)) {
          if (!compositions[tid]) {
            compositions[tid] = [];
          }
        }
        this.teamMembers = compositions;
        this.rosterLoaded = true;
        // 명단이 바뀌면(추가 · 상태 변경 · 팀 이동) 병동 현황 숫자도 다시 센다.
        this.refreshWardSummaries();
      } catch {
        // keep prior value on failure
      } finally {
        // 성공 · 실패와 무관하게 「한 번은 답이 왔다」 — 스켈레톤을 거둔다(rosterLoaded 는 재시도 판단용이라 건드리지 않는다)
        this.rosterSettled = true;
      }
    },

    async loadTeams() {
      try {
        const p = this.paging.teams;
        // 조직 트리/팀 피커는 페이지 단위 표시가 아니라 전체 목록이 필요 —
        // 서버 limit 상한(100)을 그대로 두고 fetchAllPages 로 끝까지 페이지네이션.
        const filter: Record<string, unknown> = {};
        if (this.teamFilter.search) {
          filter.search = this.teamFilter.search;
        }
        if (this.teamFilter.status) {
          filter.isActive = this.teamFilter.status === "active";
        }
        if (this.teamFilter.teamId) {
          filter.teamId = this.teamFilter.teamId;
        }
        if (this.teamFilter.roleId) {
          filter.roleId = this.teamFilter.roleId;
        }
        const items = await fetchAllPages<Record<string, unknown>>(
          (page, limit) => this.sdk.team.list({ ...filter, page, limit }),
          100,
        );
        this.teams = items;
        // 팀 추가 · 수정(병동 연결) · 멤버 변경 뒤에는 늘 팀 목록을 다시 부른다 — 병동 현황도 같이 새로 고친다
        this.refreshWardSummaries();
        // 필터 없는 전체 목록(allTeams)도 함께 — 병동 「연결 팀」 · 역할 부서·팀이 쓴다. 필터가 없으면 지금 받은
        // 목록이 곧 전체 목록이라 같은 요청을 두 번 보내지 않는다(MED-914).
        // 병동 목록도 다시 — 트리 맨 아래 「미연결 병동」이 새로 만든 병동을 바로 보이게
        const unfiltered = Object.keys(filter).length === 0;
        if (unfiltered) {
          this.allTeams = items as unknown as OrgTeamItem[];
        }
        await Promise.allSettled([
          unfiltered ? Promise.resolve() : this.loadAllTeams(),
          this.ensureWardOptions(true),
          this.loadOrgLeaders(),
        ]);
        p.totalCount = items.length;
        p.totalPages = 1;
      } catch {
        this.teams = [];
      } finally {
        this.teamsLoaded = true;
      }
    },

    /** 필터 없는 전체 팀 목록(allTeams). 실패하면 이전 목록을 그대로 둔다 — 비우면 역할 부서·팀 정리가 전부 지운다. */
    async loadAllTeams() {
      try {
        this.allTeams = await fetchAllPages<OrgTeamItem>(
          (page, limit) => this.sdk.team.list({ page, limit }),
          100,
        );
      } catch {
        /* keep previous list */
      }
    },

    async loadShifts() {
      try {
        if (!this.sdk) {
          return;
        }
        const result = await this.sdk.shift.list();
        this.shifts = result.items;
      } catch {
        this.shifts = [];
      }
    },

    async loadLeaveBalances() {
      try {
        const filter: Record<string, unknown> = {
          asOfDate: `${this.leaveFilter.year}-12-31`,
        };
        if (this.leaveFilter.search) {
          filter.search = this.leaveFilter.search;
        }
        if (this.leaveFilter.teamIds.length > 0) {
          filter.teamIds = this.leaveFilter.teamIds;
        }
        if (this.leaveFilter.basis) {
          filter.basis = this.leaveFilter.basis;
        }
        const result = await this.sdk.leaveRequest.listBalances(filter);
        this.leaveBalances = result?.items || [];
        this.leaveBalancesBasis = result?.basis ?? null;
        // 행이 그려지는 순간 스켈레톤을 거둔다(아래 촉진 목록까지 기다리면 실제 행과 스켈레톤이 함께 보인다)
        this.leaveBalancesLoaded = true;
        const promotionResult = await this.sdk.annualLeavePromotion.list({
          periodKey: annualLeavePromotionPeriodKey(this.leaveFilter.year),
        });
        this.annualLeavePromotions = (promotionResult?.items || []) as unknown as Record<
          string,
          unknown
        >[];
        this.leaveBalancePage = 1;
      } catch {
        this.leaveBalances = [];
        this.leaveBalancePage = 1;
      } finally {
        this.leaveBalancesLoaded = true;
      }
    },

    annualLeavePromotionFor(employeeId: string): Record<string, unknown> | undefined {
      return this.annualLeavePromotions.find((item) => item.employeeId === employeeId);
    },

    annualLeavePromotionStatus(employeeId: string): string {
      return annualLeavePromotionStatusLabel(
        String(this.annualLeavePromotionFor(employeeId)?.businessStatus || "not_notified"),
      );
    },

    annualLeavePromotionAction(employeeId: string) {
      const promotion = this.annualLeavePromotionFor(employeeId);
      return annualLeavePromotionRowAction(
        promotion,
        Boolean(promotion?.id && this.annualLeavePromotionRetryingId === String(promotion.id)),
      );
    },

    async handleAnnualLeavePromotionAction(employeeId: string) {
      const promotion = this.annualLeavePromotionFor(employeeId);
      const action = this.annualLeavePromotionAction(employeeId);
      if (action.disabled) {
        return;
      }
      if (action.kind === "retry" && promotion) {
        await this.retryAnnualLeavePromotion(promotion);
        return;
      }
      if (action.kind === "remind" && promotion) {
        await this.remindAnnualLeavePromotion(promotion);
        return;
      }
      if (action.kind === "reissue" && promotion) {
        await this.reissueAnnualLeavePromotion(promotion);
        return;
      }
      await this.openAnnualLeavePromotion(employeeId);
    },

    annualLeavePromotionPageEmployeeIds(): string[] {
      return Array.from(
        new Set(
          this.leaveBalancesPageItems()
            .map((item) => String(item.employeeId || ""))
            .filter(Boolean),
        ),
      );
    },

    annualLeavePromotionPageSelectionState(): "none" | "some" | "all" {
      const pageIds = this.annualLeavePromotionPageEmployeeIds();
      if (pageIds.length === 0) {
        return "none";
      }
      const selectedIds = new Set(this.annualLeavePromotionSelectedIds);
      const selectedCount = pageIds.filter((id) => selectedIds.has(id)).length;
      if (selectedCount === 0) {
        return "none";
      }
      return selectedCount === pageIds.length ? "all" : "some";
    },

    annualLeavePromotionHasMultiplePages(): boolean {
      return this.leaveBalanceTotalPages() > 1;
    },

    annualLeavePromotionSelectionBarVisible(): boolean {
      return (
        this.annualLeavePromotionAllMatchingSelected ||
        (this.annualLeavePromotionHasMultiplePages() &&
          this.annualLeavePromotionPageSelectionState() === "all")
      );
    },

    annualLeavePromotionHasActiveFilters(): boolean {
      return Boolean(this.leaveFilter.search.trim() || this.leaveFilter.teamIds.length);
    },

    annualLeavePromotionSelectionSignature(): string {
      return JSON.stringify({
        year: this.leaveFilter.year,
        search: this.leaveFilter.search.trim(),
        teamIds: [...new Set(this.leaveFilter.teamIds)].sort(),
      });
    },

    toggleAnnualLeavePromotionPageSelection(checked?: boolean) {
      const pageIds = this.annualLeavePromotionPageEmployeeIds();
      const selectedIds = new Set(this.annualLeavePromotionSelectedIds);
      const shouldSelect = checked ?? this.annualLeavePromotionPageSelectionState() !== "all";
      this.annualLeavePromotionAllMatchingSelected = false;
      for (const employeeId of pageIds) {
        if (shouldSelect) {
          selectedIds.add(employeeId);
        } else {
          selectedIds.delete(employeeId);
        }
      }
      this.annualLeavePromotionSelectedIds = [...selectedIds];
    },

    resetAnnualLeavePromotionSelection() {
      this.annualLeavePromotionSelectedIds = [];
      this.annualLeavePromotionAllMatchingSelected = false;
    },

    async reloadLeaveBalancesForFilter() {
      this.resetAnnualLeavePromotionSelection();
      await this.loadLeaveBalances();
    },

    toggleAnnualLeavePromotionSelection(employeeId: string) {
      if (this.annualLeavePromotionAllMatchingSelected) {
        this.annualLeavePromotionSelectedIds = [];
      }
      if (this.annualLeavePromotionSelectedIds.includes(employeeId)) {
        this.annualLeavePromotionSelectedIds = this.annualLeavePromotionSelectedIds.filter(
          (id) => id !== employeeId,
        );
      } else {
        this.annualLeavePromotionSelectedIds = [
          ...this.annualLeavePromotionSelectedIds,
          employeeId,
        ];
      }
      this.annualLeavePromotionAllMatchingSelected = false;
    },

    selectAllMatchingAnnualLeavePromotionTargets() {
      if (
        !this.annualLeavePromotionHasMultiplePages() ||
        this.annualLeavePromotionPageSelectionState() !== "all"
      ) {
        return;
      }
      this.annualLeavePromotionSelectedIds = [];
      this.annualLeavePromotionAllMatchingSelected = true;
    },

    async openAnnualLeavePromotion(employeeId = "") {
      // 목록이 비어 있으면 열 때마다 다시 읽는다 — 서식을 만들고 돌아온 경우가 그 경우다.
      if (
        !(
          this.annualLeavePromotionTemplates.length ||
          (await this.loadAnnualLeavePromotionTemplates())
        )
      ) {
        return;
      }
      const firstTemplate = this.annualLeavePromotionTemplates[0];
      const selectionSearch = this.leaveFilter.search.trim();
      const selectionTeamIds = [...new Set(this.leaveFilter.teamIds)];
      this.annualLeavePromotionModal = {
        ...this.annualLeavePromotionModal,
        open: true,
        employeeId,
        reissuePromotionId: "",
        selectionMode: employeeId
          ? AnnualLeavePromotionSelectionMode.EXPLICIT
          : this.annualLeavePromotionAllMatchingSelected
            ? AnnualLeavePromotionSelectionMode.ALL_ELIGIBLE_IN_SCOPE
            : AnnualLeavePromotionSelectionMode.EXPLICIT,
        selectionSearch,
        selectionTeamIds,
        selectionSignature: this.annualLeavePromotionSelectionSignature(),
        herebyTemplateId: Number(firstTemplate?.templateId || 0),
        templateFingerprint: String(firstTemplate?.templateFingerprint || ""),
        submitting: false,
      };
    },

    closeAnnualLeavePromotion() {
      if (!this.annualLeavePromotionModal.submitting) {
        this.annualLeavePromotionModal.open = false;
      }
    },

    /**
     * 촉진 통지에 쓸 수 있는 Sign 서식(P1 호환) 목록을 읽는다. 실패하면 오류를 띄우고 false.
     * Sign 미연동 조직은 templates() 가 던진다 — 여기서 잡지 않으면 Alpine 표현식 에러로
     * 콘솔에만 남고 버튼이 무반응으로 보인다 (2026-09-03 QA). 목록이 비면 "서식 없음" 안내의
     * [서식 추가하기]에 쓸 관리 화면 URL 을 함께 읽는다(CS-LOC-260914-0002).
     */
    async loadAnnualLeavePromotionTemplates(): Promise<boolean> {
      this.annualLeavePromotionTemplatesLoading = true;
      try {
        this.annualLeavePromotionTemplates =
          (await this.sdk.annualLeavePromotion.templates()) as unknown as Record<string, unknown>[];
      } catch (error) {
        showError(error instanceof Error ? error.message : "Sign 서식 목록을 불러오지 못했어요.");
        return false;
      } finally {
        this.annualLeavePromotionTemplatesLoading = false;
      }
      if (!this.annualLeavePromotionTemplates.length) {
        try {
          this.annualLeavePromotionTemplateManageUrl = (
            await this.sdk.annualLeavePromotion.templateManageUrl()
          ).url;
        } catch {
          // 안내문만으로도 경로는 전달된다 — URL 조회 실패로 모달을 막지 않는다.
          this.annualLeavePromotionTemplateManageUrl = null;
        }
      }
      return true;
    },

    annualLeavePromotionTemplateEmptyNotice(): string {
      return annualLeavePromotionTemplateEmptyNotice(this.annualLeavePromotionTemplateManageUrl);
    },

    /** "서식 없음" 안내의 [새로고침] — 새로 만든 서식이 생겼으면 첫 서식을 기본 선택한다. */
    async refreshAnnualLeavePromotionTemplates() {
      if (this.annualLeavePromotionTemplatesLoading) {
        return;
      }
      if (!(await this.loadAnnualLeavePromotionTemplates())) {
        return;
      }
      const modal = this.annualLeavePromotionModal;
      const selected = this.annualLeavePromotionTemplates.find(
        (template) => Number(template.templateId) === modal.herebyTemplateId,
      );
      const template = selected || this.annualLeavePromotionTemplates[0];
      modal.herebyTemplateId = Number(template?.templateId || 0);
      modal.templateFingerprint = String(template?.templateFingerprint || "");
    },

    openAnnualLeavePromotionTemplateManage() {
      const url = this.annualLeavePromotionTemplateManageUrl;
      if (!url) {
        return;
      }
      window.open(url, "_blank", "noopener");
    },

    async submitAnnualLeavePromotion() {
      const modal = this.annualLeavePromotionModal;
      if (!modal.herebyTemplateId) {
        showAlert("사용할 Sign 서식을 선택해 주세요.");
        return;
      }
      const employeeIds = modal.employeeId
        ? [modal.employeeId]
        : this.annualLeavePromotionSelectedIds;
      if (
        modal.selectionMode === AnnualLeavePromotionSelectionMode.EXPLICIT &&
        employeeIds.length === 0
      ) {
        showAlert("발송할 직원을 선택해 주세요.");
        return;
      }
      if (
        !modal.employeeId &&
        modal.selectionSignature !== this.annualLeavePromotionSelectionSignature()
      ) {
        this.resetAnnualLeavePromotionSelection();
        modal.open = false;
        showAlert("조회 조건이 변경됐어요. 발송 대상을 다시 선택해 주세요.");
        return;
      }
      modal.submitting = true;
      try {
        const periodKey = annualLeavePromotionPeriodKey(this.leaveFilter.year);
        const periodStart = new Date(`${this.leaveFilter.year}-01-01T00:00:00.000Z`);
        const periodEnd = new Date(`${this.leaveFilter.year}-12-31T23:59:59.999Z`);
        if (modal.reissuePromotionId) {
          await this.sdk.annualLeavePromotion.reissue({
            promotionId: modal.reissuePromotionId,
            employeeId: modal.employeeId,
            periodKey,
            periodStart,
            periodEnd,
            herebyTemplateId: modal.herebyTemplateId,
            templateFingerprint: modal.templateFingerprint || undefined,
            responseDurationSeconds: modal.responseDurationDays * 24 * 60 * 60,
            operationKey: `annual-leave-promotion:reissue:${modal.reissuePromotionId}:${crypto.randomUUID()}`,
          });
          modal.open = false;
          await this.loadLeaveBalances();
          return;
        }
        if (modal.employeeId) {
          await this.sdk.annualLeavePromotion.firstNotice({
            employeeId: modal.employeeId,
            periodKey,
            periodStart,
            periodEnd,
            herebyTemplateId: modal.herebyTemplateId,
            templateFingerprint: modal.templateFingerprint || undefined,
            responseDurationSeconds: modal.responseDurationDays * 24 * 60 * 60,
            operationKey: `annual-leave-promotion:first:${modal.employeeId}:${crypto.randomUUID()}`,
          });
          modal.open = false;
          await this.loadLeaveBalances();
          return;
        }
        const preflight = await this.sdk.annualLeavePromotion.batchPreflight({
          periodKey,
          periodStart,
          periodEnd,
          selectionMode: modal.selectionMode,
          employeeIds:
            modal.selectionMode === AnnualLeavePromotionSelectionMode.EXPLICIT
              ? employeeIds
              : undefined,
          search:
            modal.selectionMode === AnnualLeavePromotionSelectionMode.ALL_ELIGIBLE_IN_SCOPE
              ? modal.selectionSearch || undefined
              : undefined,
          teamIds:
            modal.selectionMode === AnnualLeavePromotionSelectionMode.ALL_ELIGIBLE_IN_SCOPE
              ? modal.selectionTeamIds
              : undefined,
        });
        if (!preflight.count) {
          showAlert("발송 가능한 직원이 없어요.");
          return;
        }
        const batch = await this.sdk.annualLeavePromotion.batchCreate({
          periodKey,
          periodStart,
          periodEnd,
          selectionMode: modal.selectionMode,
          employeeIds:
            modal.selectionMode === AnnualLeavePromotionSelectionMode.EXPLICIT
              ? employeeIds
              : undefined,
          search:
            modal.selectionMode === AnnualLeavePromotionSelectionMode.ALL_ELIGIBLE_IN_SCOPE
              ? modal.selectionSearch || undefined
              : undefined,
          teamIds:
            modal.selectionMode === AnnualLeavePromotionSelectionMode.ALL_ELIGIBLE_IN_SCOPE
              ? modal.selectionTeamIds
              : undefined,
          selectionToken: preflight.selectionToken,
          herebyTemplateId: modal.herebyTemplateId,
          templateFingerprint: modal.templateFingerprint || undefined,
          responseDurationSeconds: modal.responseDurationDays * 24 * 60 * 60,
          operationKey: `annual-leave-promotion:${periodKey}:${crypto.randomUUID()}`,
        });
        modal.open = false;
        this.annualLeavePromotionSelectedIds = [];
        this.annualLeavePromotionAllMatchingSelected = false;
        await this.openAnnualLeavePromotionBatchHistory(String(batch.id || ""));
        await this.loadLeaveBalances();
      } catch (error) {
        showError(error instanceof Error ? error.message : "연차 촉진 발송에 실패했어요.");
      } finally {
        modal.submitting = false;
      }
    },

    async remindAnnualLeavePromotion(promotion: Record<string, unknown>) {
      const promotionId = String(promotion.id || "");
      const attemptId = String(promotion.currentSignAttemptId || "");
      if (!(promotionId && attemptId)) {
        showAlert("재안내할 서명 요청을 찾을 수 없어요.");
        return;
      }
      const confirmed = await askConfirm(
        "제출 기한은 변경되지 않아요. 현재 서명 요청을 다시 안내할까요?",
      );
      if (!confirmed) {
        return;
      }
      try {
        await this.sdk.annualLeavePromotion.reminder({
          promotionId,
          attemptId,
          operationKey: `annual-leave-promotion:reminder:${promotionId}:${crypto.randomUUID()}`,
        });
        await this.loadLeaveBalances();
      } catch (error) {
        showError(error instanceof Error ? error.message : "재안내에 실패했어요.");
      }
    },

    async retryAnnualLeavePromotion(promotion: Record<string, unknown>) {
      const promotionId = String(promotion.id || "");
      if (!promotionId || promotion.canRetryFirstNotice !== true) {
        showAlert("재시도할 수 있는 실패 안내를 찾을 수 없어요.");
        return;
      }
      const confirmed = await askConfirm("실패한 요청과 동일한 내용으로 다시 발송할까요?");
      if (!confirmed) {
        return;
      }
      this.annualLeavePromotionRetryingId = promotionId;
      try {
        await this.sdk.annualLeavePromotion.retryFirstNotice(promotionId);
        await this.loadLeaveBalances();
      } catch (error) {
        showError(error instanceof Error ? error.message : "안내 재시도에 실패했어요.");
      } finally {
        if (this.annualLeavePromotionRetryingId === promotionId) {
          this.annualLeavePromotionRetryingId = "";
        }
      }
    },

    async reissueAnnualLeavePromotion(promotion: Record<string, unknown>) {
      await this.openAnnualLeavePromotion(String(promotion.employeeId || ""));
      if (!this.annualLeavePromotionModal.open) {
        return;
      }
      this.annualLeavePromotionModal.reissuePromotionId = String(promotion.id || "");
    },

    openAnnualLeavePromotionSecondDesignation(promotion: Record<string, unknown>) {
      this.annualLeavePromotionSecondModal = {
        open: true,
        promotionId: String(promotion.id || ""),
        employeeId: String(promotion.employeeId || ""),
        planItems: [{ date: "", minutes: 480, halfDay: false }],
        submitting: false,
      };
    },

    closeAnnualLeavePromotionSecondDesignation() {
      if (!this.annualLeavePromotionSecondModal.submitting) {
        this.annualLeavePromotionSecondModal.open = false;
      }
    },

    addAnnualLeavePromotionPlanItem() {
      this.annualLeavePromotionSecondModal.planItems.push({
        date: "",
        minutes: 480,
        halfDay: false,
      });
    },

    removeAnnualLeavePromotionPlanItem(index: number) {
      if (this.annualLeavePromotionSecondModal.planItems.length <= 1) {
        return;
      }
      this.annualLeavePromotionSecondModal.planItems.splice(index, 1);
    },

    async submitAnnualLeavePromotionSecondDesignation() {
      const modal = this.annualLeavePromotionSecondModal;
      const validItems = modal.planItems.filter((item) => item.date && Number(item.minutes) > 0);
      if (!(modal.promotionId && modal.employeeId && validItems.length)) {
        showAlert("사용시기를 1개 이상 입력해 주세요.");
        return;
      }
      modal.submitting = true;
      try {
        const periodKey = annualLeavePromotionPeriodKey(this.leaveFilter.year);
        await this.sdk.annualLeavePromotion.designateSecond({
          promotionId: modal.promotionId,
          employeeId: modal.employeeId,
          periodKey,
          periodStart: new Date(`${this.leaveFilter.year}-01-01T00:00:00.000Z`),
          periodEnd: new Date(`${this.leaveFilter.year}-12-31T23:59:59.999Z`),
          reason: "제출 기한 내 서명·계획서 미완료",
          planItems: validItems.map((item) => ({
            date: new Date(`${item.date}T00:00:00.000Z`),
            minutes: Number(item.minutes),
            halfDay: !!item.halfDay,
          })),
          operationKey: `annual-leave-promotion:second:${modal.promotionId}:${crypto.randomUUID()}`,
        });
        modal.open = false;
        await this.loadLeaveBalances();
      } catch (error) {
        showError(error instanceof Error ? error.message : "사용시기 지정에 실패했어요.");
      } finally {
        modal.submitting = false;
      }
    },

    annualLeavePromotionBatchStatus(status: unknown): string {
      return annualLeavePromotionBatchStatusLabel(String(status || ""));
    },

    annualLeavePromotionBatchItemStatus(status: unknown): string {
      switch (String(status || "")) {
        case "pending":
          return "대기";
        case "claimed":
          return "처리 중";
        case "succeeded":
          return "완료";
        case "failed":
          return "실패";
        case "cancelled":
          return "중지";
        case "skipped":
          return "제외";
        default:
          return "대기";
      }
    },

    async loadAnnualLeavePromotionBatches() {
      try {
        const periodKey = annualLeavePromotionPeriodKey(this.leaveFilter.year);
        this.annualLeavePromotionBatches = (await this.sdk.annualLeavePromotion.batchList({
          periodKey,
        })) as unknown as Record<string, unknown>[];
      } catch {
        this.annualLeavePromotionBatches = [];
      }
    },

    async openAnnualLeavePromotionBatchHistory(batchId = "") {
      this.annualLeavePromotionBatchModal.open = true;
      this.annualLeavePromotionBatchModal.batchId = batchId;
      await this.loadAnnualLeavePromotionBatches();
      const targetId = batchId || String(this.annualLeavePromotionBatches[0]?.id || "");
      this.annualLeavePromotionBatchModal.batchId = targetId;
      await this.refreshAnnualLeavePromotionBatch();
      this.clearAnnualLeavePromotionBatchPolling();
      this.annualLeavePromotionBatchPollTimer = setInterval(async () => {
        if (this.annualLeavePromotionBatchModal.open) {
          await this.refreshAnnualLeavePromotionBatch();
        }
      }, 5000);
    },

    closeAnnualLeavePromotionBatchHistory() {
      this.annualLeavePromotionBatchModal.open = false;
      this.clearAnnualLeavePromotionBatchPolling();
    },

    clearAnnualLeavePromotionBatchPolling() {
      if (this.annualLeavePromotionBatchPollTimer) {
        clearInterval(this.annualLeavePromotionBatchPollTimer);
        this.annualLeavePromotionBatchPollTimer = null;
      }
    },

    async refreshAnnualLeavePromotionBatch() {
      const batchId = this.annualLeavePromotionBatchModal.batchId;
      if (!batchId) {
        return;
      }
      this.annualLeavePromotionBatchModal.loading = true;
      try {
        this.annualLeavePromotionBatchModal.batch =
          (await this.sdk.annualLeavePromotion.batchStatus(batchId)) as unknown as Record<
            string,
            unknown
          >;
      } catch (error) {
        showError(error instanceof Error ? error.message : "발송 내역을 불러오지 못했어요.");
      } finally {
        this.annualLeavePromotionBatchModal.loading = false;
      }
    },

    async cancelAnnualLeavePromotionBatch() {
      const batchId = this.annualLeavePromotionBatchModal.batchId;
      if (!batchId) {
        return;
      }
      const confirmed = await askConfirm(
        "아직 시작하지 않은 직원의 발송을 중지할까요? 이미 발송된 Sign 요청은 취소하지 않아요.",
      );
      if (!confirmed) {
        return;
      }
      try {
        await this.sdk.annualLeavePromotion.batchCancel(batchId);
        await this.refreshAnnualLeavePromotionBatch();
      } catch (error) {
        showError(error instanceof Error ? error.message : "발송 중지에 실패했어요.");
      }
    },

    async retryAnnualLeavePromotionBatch() {
      const batchId = this.annualLeavePromotionBatchModal.batchId;
      if (!batchId) {
        return;
      }
      try {
        await this.sdk.annualLeavePromotion.batchRetryFailed(batchId);
        await this.refreshAnnualLeavePromotionBatch();
      } catch (error) {
        showError(error instanceof Error ? error.message : "실패 건 재시도에 실패했어요.");
      }
    },

    /* ── 연차 현황 [상세] 팝업 — 적용된 규칙 / 사용 내역 / 조정 내역 탭 ── */

    async openLeaveBalanceDetail(b: Record<string, unknown>) {
      this.leaveDetailTarget = b;
      this.leaveBalanceDetailTab = "rule";
      this.leaveDetailInfo = null;
      this.leaveDetailUsage = [];
      this.leaveDetailAdjustments = [];
      this.showLeaveBalanceDetail = true;
      await this._loadLeaveBalanceDetail(String(b.employeeId));
    },

    async _loadLeaveBalanceDetail(employeeId: string) {
      this.leaveDetailLoading = true;
      try {
        const [info, usage, adjustments] = await Promise.all([
          this.sdk.leaveRequest.balanceRuleDetail(employeeId),
          // 사용 내역 — 이 직원의 신청 전부(최신순). 상태별(취소/승인/반려/대기) 라벨은 목록 공용 헬퍼 재사용
          this.sdk.leaveRequest.list(
            // biome-ignore lint/suspicious/noExplicitAny: filter accepts employeeId (leaveRequestFilterSchema)
            { employeeId } as any,
            { page: 1, limit: 100, sortBy: "createdAt", sortOrder: "desc" },
          ),
          this.sdk.leaveRequest.listBalanceAdjustments(employeeId),
        ]);
        this.leaveDetailInfo = info;
        this.leaveDetailUsage = (usage?.items || []) as Record<string, unknown>[];
        this.leaveDetailAdjustments = adjustments?.items || [];
      } catch (err) {
        this.notify(
          `상세 정보를 불러오지 못했어요: ${err instanceof Error ? err.message : String(err)}`,
          "error",
        );
      } finally {
        this.leaveDetailLoading = false;
      }
    },

    /** 상세 팝업 「적용된 규칙」 행 — 규칙 이름(없으면 법정 기준 폴백 표기) */
    get leaveDetailRuleName(): string {
      const applied = this.leaveDetailInfo?.applied;
      if (!applied) {
        return "-";
      }
      return applied.rule?.name || "법정 기준 (근로기준법)";
    },

    /** 일수(소수) → "N일" / "N일 M시간" — 조정 내역·조정 폼 표기 공용 (직원 소정근로시간=1일 환산) */
    leaveDaysLabel(value: number | null | undefined): string {
      if (value == null || Number.isNaN(Number(value))) {
        return "-";
      }
      const std = Number(this.leaveDetailInfo?.dailyStandardHours) || this.leaveAdjustStd || 8;
      const totalHours = Math.round(Number(value) * std);
      const days = Math.floor(totalHours / std);
      const hours = totalHours - days * std;
      return hours === 0 ? `${days}일` : `${days}일 ${hours}시간`;
    },

    /** 상세 팝업 「적용된 규칙」 — 직원 소정근로시간과 출처 한 줄 ("11.5시간 · 근무 형태 「주간」") */
    get leaveDetailStandardLabel(): string {
      const info = this.leaveDetailInfo;
      if (!info) {
        return "-";
      }
      const hours = Number(info.dailyStandardHours) || 8;
      const source = info.dailyStandardSource;
      const name = info.dailyStandardSourceName;
      const origin =
        source === "shift"
          ? `근무 형태 「${name ?? "-"}」`
          : source === "shift_group"
            ? `근무 그룹 「${name ?? "-"}」`
            : info.shiftAssigned
              ? "조직 기본값 (근무 그룹 안 근무 형태마다 시간이 달라요)"
              : "조직 기본값 (근무 그룹·근무 형태 미배정)";
      return `${hours}시간 · ${origin}`;
    },

    /** 상세 팝업 사용 내역의 차감 표기 — 시간 단위 신청은 직원 소정근로시간으로 일수 환산을 덧붙인다
     *  ("2시간 (0.17일)"). 결재 목록 공용 leaveDeductionLabel은 직원별 기준을 몰라 시간만 적는다. */
    leaveDetailDeductionLabel(item: Record<string, unknown>): string {
      // 이관 행은 파일의 일수 원값(importedDays)이 곧 차감량이다
      if (item.source === "import" && item.importedDays != null) {
        return `${Number(item.importedDays)}일`;
      }
      const base = this.leaveDeductionLabel(item);
      const mins = Number(item.requestedMinutes) || 0;
      const std = Number(this.leaveDetailInfo?.dailyStandardHours) || 0;
      if (mins <= 0 || std <= 0) {
        return base;
      }
      // 여러 날 시간 단위 신청은 하루당 시간 × 달력 일수(쉬는 날이 빠지면 실제는 더 적다)
      const s = new Date(String(item.startDate).slice(0, 10));
      const e = new Date(String(item.endDate).slice(0, 10));
      const span =
        Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())
          ? 1
          : Math.floor((e.getTime() - s.getTime()) / 86400000) + 1;
      const days = Math.round(((mins * span) / (std * 60)) * 100) / 100;
      return `${base} (${days}일)`;
    },

    /* ── 연차 [조정] 팝업 — 잔여를 목표값으로 맞추는 수동 조정 ── */

    async openLeaveAdjust(b?: Record<string, unknown>) {
      const target = b ?? this.leaveDetailTarget;
      if (!target) {
        return;
      }
      this.leaveDetailTarget = target;
      const employeeId = String(target.employeeId);
      try {
        // 기본값 = 서버 기준 현재 잔여 (상세가 이미 로드돼 있으면 재사용)
        const info =
          this.leaveDetailInfo && this.leaveDetailInfo.employeeId === employeeId
            ? this.leaveDetailInfo
            : await this.sdk.leaveRequest.balanceRuleDetail(employeeId);
        this.leaveDetailInfo = info;
        const std = Number(info?.dailyStandardHours) || 8;
        this.leaveAdjustStd = std;
        const totalHours = Math.round(Number(info?.balance?.remaining ?? 0) * std);
        this.leaveAdjustForm = {
          days: Math.floor(totalHours / std),
          hours: ((totalHours % std) + std) % std,
          reason: "",
        };
        this.showLeaveAdjustModal = true;
      } catch (err) {
        this.notify(
          `현재 잔여를 불러오지 못했어요: ${err instanceof Error ? err.message : String(err)}`,
          "error",
        );
      }
    },

    /** 조정 후 잔여(일, 소수) — 일 + 시간/소정근로시간 */
    get leaveAdjustTargetDays(): number {
      const std = this.leaveAdjustStd || 8;
      return (
        (Number(this.leaveAdjustForm.days) || 0) + (Number(this.leaveAdjustForm.hours) || 0) / std
      );
    },

    get leaveAdjustSaveDisabled(): boolean {
      const f = this.leaveAdjustForm;
      const days = Number(f.days);
      const hours = Number(f.hours);
      if (!Number.isInteger(days) || days < 0 || days > 365) {
        return true;
      }
      if (!Number.isInteger(hours) || hours < 0 || hours >= (this.leaveAdjustStd || 8)) {
        return true;
      }
      if (!String(f.reason ?? "").trim()) {
        return true;
      }
      return this.leaveAdjustSaving;
    },

    async saveLeaveAdjust() {
      const target = this.leaveDetailTarget;
      if (!target || this.leaveAdjustSaveDisabled) {
        return;
      }
      this.leaveAdjustSaving = true;
      try {
        const res = await this.sdk.leaveRequest.adjustBalance({
          employeeId: String(target.employeeId),
          targetDays: this.leaveAdjustTargetDays,
          reason: this.leaveAdjustForm.reason.trim(),
        });
        this.showLeaveAdjustModal = false;
        this.notify(
          `연차를 조정했어요. ${this.leaveDaysLabel(res.beforeDays)} → ${this.leaveDaysLabel(res.afterDays)}`,
        );
        // 목록·상세를 서버 상태로 갱신 (상세 팝업이 열려 있으면 조정 내역 탭까지)
        await this.loadLeaveBalances();
        if (this.showLeaveBalanceDetail) {
          await this._loadLeaveBalanceDetail(String(target.employeeId));
        }
      } catch (err) {
        this.notify(
          `연차 조정에 실패했어요: ${err instanceof Error ? err.message : String(err)}`,
          "error",
        );
      } finally {
        this.leaveAdjustSaving = false;
      }
    },

    async loadEntitlementRules() {
      try {
        const result = await this.sdk.leaveRequest.listEntitlementRules();
        this.entitlementRules = result?.items || [];
      } catch {
        this.entitlementRules = [];
      }
      // 회계일(settings.fiscalYearStart "MM-DD") — 실패해도 리스트는 보인다
      try {
        const org = (await this.sdk.organization.getCurrent()) as {
          settings?: { fiscalYearStart?: string };
        };
        this.ruleFiscalStartSaved = org?.settings?.fiscalYearStart || "01-01";
      } catch {
        this.ruleFiscalStartSaved = "01-01";
      }
      await this.loadLeaveBasis();
      // 규칙 탭은 조직이 실제로 쓰는 기준으로 연다 — 반대 탭 규칙은 적용되지 않는다는 배너가 뜬다
      this.ruleBasisTab = this.leaveBasis.current.basis;
      // datepicker는 풀 날짜가 필요 — 올해 + 저장된 MM-DD로 표기(연도는 의미 없음, 매년 반복)
      this.ruleFiscalStartDate = `${new Date().getFullYear()}-${this.ruleFiscalStartSaved}`;
    },

    /** 현재 기준 탭의 규칙만 — 회계년도/입사일 리스트는 각각 관리된다.
     *  basis 없는 레거시 행은 회계년도 탭에 노출(마이그레이션 전 데이터 안전망). */
    entitlementRulesForBasis(): EntitlementRuleItem[] {
      return this.entitlementRules.filter(
        (r) => ((r as { basis?: string }).basis || "fiscal_year") === this.ruleBasisTab,
      );
    },

    /* ── 과거 연차 내역 일괄 업로드 ── */

    async openLeaveImport() {
      this.leaveImport = {
        fileName: "",
        fileBase64: "",
        fileError: "",
        loading: false,
        loadingLabel: "",
        preview: null,
        mapping: {},
        terminatedPolicy: "create",
        skipInvalidRows: false,
        validated: false,
        errors: [],
        summary: null,
        saving: false,
      };
      this.showLeaveImport = true;
      await this.loadLeaveImportBatches();
    },

    async loadLeaveImportBatches() {
      try {
        this.leaveImportBatches =
          (await this.sdk.leaveImport.listBatches()) as LeaveImportBatchView[];
      } catch {
        this.leaveImportBatches = [];
      }
    },

    /** hereby 템플릿 — 헤더는 서버 파서(HEREBY_LEAVE_IMPORT_HEADERS)와 같은 순서·표기여야 형식이 판별된다 */
    async downloadLeaveImportTemplate() {
      const xlsx = await import("xlsx");
      const headers = ["사번 *", "사원명", "근태종류 *", "시작일 *", "종료일", "차감 *", "사유"];
      const sample = ["A001", "홍길동", "연차", "2026-03-02", "2026-03-03", "2", "개인 사유"];
      const ws = xlsx.utils.aoa_to_sheet([headers, sample]);
      const wb = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(wb, ws, "연차내역");
      xlsx.writeFile(wb, "과거_연차_내역_템플릿.xlsx");
    },

    leaveImportMappingComplete(): boolean {
      const p = this.leaveImport.preview;
      if (!p) {
        return false;
      }
      return p.leaveTypes.every((t) => Boolean(this.leaveImport.mapping[t.label]));
    },

    async handleLeaveImportFile(file: File | undefined) {
      if (!file) {
        return;
      }
      const ext = file.name.split(".").pop()?.toLowerCase();
      if (!(ext && ["xlsx", "xls", "csv"].includes(ext))) {
        this.leaveImport.fileError = "엑셀(.xlsx/.xls) 또는 CSV 파일만 올릴 수 있어요.";
        return;
      }
      this.leaveImport.fileError = "";
      this.leaveImport.validated = false;
      this.leaveImport.errors = [];
      this.leaveImport.summary = null;
      this.leaveImport.preview = null;
      this.leaveImport.fileName = file.name;
      this.leaveImport.loading = true;
      this.leaveImport.loadingLabel = "파일을 읽는 중이에요…";
      try {
        const buf = await file.arrayBuffer();
        this.leaveImport.fileBase64 = arrayBufferToBase64(buf);
        const preview = (await this.sdk.leaveImport.preview({
          fileBase64: this.leaveImport.fileBase64,
          fileName: file.name,
        })) as LeaveImportPreview;
        this.leaveImport.preview = preview;
        const mapping: Record<string, string> = {};
        for (const t of preview.leaveTypes) {
          mapping[t.label] = t.suggestedLeaveTypeId ?? "";
        }
        this.leaveImport.mapping = mapping;
      } catch (err) {
        this.leaveImport.fileError = err instanceof Error ? err.message : String(err);
        this.leaveImport.fileBase64 = "";
      } finally {
        this.leaveImport.loading = false;
      }
    },

    async runLeaveImport(dryRun: boolean) {
      const li = this.leaveImport;
      if (!(li.fileBase64 && li.preview)) {
        return null;
      }
      return (await this.sdk.leaveImport.run({
        fileBase64: li.fileBase64,
        fileName: li.fileName,
        mapping: li.mapping,
        terminatedPolicy: li.terminatedPolicy,
        skipInvalidRows: li.skipInvalidRows,
        dryRun,
      })) as {
        ok: boolean;
        errors: { row: number; message: string }[];
        summary: LeaveImportSummary;
        batchId: string | null;
      };
    },

    async validateLeaveImport() {
      this.leaveImport.loading = true;
      this.leaveImport.loadingLabel = "검증하는 중이에요…";
      try {
        const res = await this.runLeaveImport(true);
        if (!res) {
          return;
        }
        this.leaveImport.errors = res.errors;
        this.leaveImport.summary = res.summary;
        this.leaveImport.validated = true;
      } catch (err) {
        this.leaveImport.fileError = err instanceof Error ? err.message : String(err);
        this.leaveImport.validated = false;
      } finally {
        this.leaveImport.loading = false;
      }
    },

    async confirmLeaveImport() {
      const li = this.leaveImport;
      if (
        li.saving ||
        !li.validated ||
        (li.errors.length > 0 && !li.skipInvalidRows) ||
        (li.summary?.totalRows ?? 0) === 0
      ) {
        return;
      }
      this.leaveImport.saving = true;
      try {
        const res = await this.runLeaveImport(false);
        if (!res) {
          return;
        }
        if (!res.ok) {
          this.leaveImport.errors = res.errors;
          this.leaveImport.summary = res.summary;
          return;
        }
        this.notify(
          `과거 연차 내역 ${res.summary.totalRows.toLocaleString()}행을 옮겼어요. (현재 기간 차감 ${res.summary.ledgerRows.toLocaleString()}행${
            res.summary.skippedErrorRows > 0
              ? `, 오류 ${res.summary.skippedErrorRows.toLocaleString()}행 건너뜀`
              : ""
          })`,
        );
        this.showLeaveImport = false;
        await Promise.all([this.loadLeaveImportBatches(), this.loadLeaveBalances()]);
      } catch (err) {
        showError(err instanceof Error ? err.message : String(err));
      } finally {
        this.leaveImport.saving = false;
      }
    },

    async revertLeaveImportBatch(b: LeaveImportBatchView) {
      const ok = await askConfirm({
        message: `「${b.fileName}」 이관(${b.rowCount.toLocaleString()}행)을 되돌릴까요? 차감 원장은 상쇄되고 이관 행은 취소 처리돼요.`,
        tone: "danger",
      });
      if (!ok) {
        return;
      }
      try {
        const r = await this.sdk.leaveImport.revert(b.id);
        this.notify(
          `이관을 되돌렸어요. (신청 ${r.requests}행 취소, 원장 ${r.ledgerReversed}건 상쇄)`,
        );
        await Promise.all([this.loadLeaveImportBatches(), this.loadLeaveBalances()]);
      } catch (err) {
        showError(err instanceof Error ? err.message : String(err));
      }
    },

    /** 산정 기준 현재값·예약·이력 — 부여 기준 팝업 「적용 기준」 영역과 설정 카드가 같이 쓴다 */
    async loadLeaveBasis() {
      // 연차는 병동 페이지 관리자에게 열지 않는다 — 부르면 403 이 나고 그 403 이
      // 공용 권한 요청 팝업을 띄워, 로그인하자마자 쓸 일 없는 팝업이 뜬다(PM-01 검증).
      if (this.isRestrictedAdmin) {
        return;
      }
      try {
        const r = await this.sdk.leaveRequest.listBasisChanges();
        this.leaveBasis = {
          today: r.today,
          current: {
            basis: r.current.basis === "fiscal_year" ? "fiscal_year" : "hire_date",
            fiscalStart: r.current.fiscalStart || "01-01",
            newHireMode:
              r.current.newHireMode === "monthly_then_retroactive" ||
              r.current.newHireMode === "monthly_then_prorate_at_anniversary"
                ? r.current.newHireMode
                : "prorate_in_hire_year",
            effectiveFrom: r.current.effectiveFrom
              ? String(r.current.effectiveFrom).slice(0, 10)
              : null,
          },
          pending: r.pending ? normalizeLeaveBasisChange(r.pending) : null,
          history: r.history.map(normalizeLeaveBasisChange),
        };
      } catch {
        // 권한이 없거나 실패하면 기본(입사일)로 두고 화면은 그대로 보인다
      }
    },

    leaveBasisLabel(basis: string): string {
      return basis === "fiscal_year" ? "회계년도" : "입사일";
    },

    /** 연차 현황이 참고 환산으로 보이는 중일 때의 안내 문구 — 적용 기준이면 "" */
    get leaveBalancesReferenceNotice(): string {
      const b = this.leaveBalancesBasis;
      if (!b?.reference) {
        return "";
      }
      const shown = this.leaveBasisLabel(b.shown);
      const applied = this.leaveBasisLabel(b.applied);
      return `${shown} 기준으로 환산한 참고값이에요. 정본은 ${applied} 기준이고, 참고값은 각 기간 안에 시작한 사용분만 세요. 퇴사 정산은 입사일 기준 잔여를 써요.`;
    },

    /** 상세 팝업 「적용된 규칙」 — 적용 중이 아닌 기준으로 본 잔여 한 줄(퇴사 정산은 입사일 기준) */
    get leaveDetailReferenceLabel(): string {
      const ref = this.leaveDetailInfo?.reference;
      if (!ref) {
        return "";
      }
      const label = this.leaveBasisLabel(String(ref.policy));
      const period = `${this.formatDateDot(ref.applied.periodStart)} ~ ${this.formatDateDot(ref.applied.periodEnd)}`;
      const tail = ref.policy === "hire_date" ? " — 퇴사 정산 기준값" : "";
      return `${label} 기준 · 기간 ${period} · 부여 ${ref.totalEntitled}일 · 사용 ${ref.used}일 · 잔여 ${ref.remaining}일${tail}`;
    },

    leaveBasisNewHireLabel(mode: string | null | undefined): string {
      if (mode === "monthly_then_retroactive") {
        return "입사 첫해 월별 발생 → 다음 회계일 소급 부여";
      }
      if (mode === "monthly_then_prorate_at_anniversary") {
        return "입사 첫해 월별 발생 → 만 1년 시점 남은 개월 비례 부여";
      }
      return "입사 연도에 바로 비례 부여";
    },

    /** 기준 변경 예약 모달 — 기본값은 현재와 반대 기준, 회계년도면 다음 회계일로 시작일을 채운다 */
    openLeaveBasisChange() {
      const next = this.leaveBasis.current.basis === "fiscal_year" ? "hire_date" : "fiscal_year";
      this.leaveBasisForm = {
        basis: next,
        effectiveFrom: next === "fiscal_year" ? this.nextFiscalStartDate() : "",
        newHireMode: "prorate_in_hire_year",
        note: "",
      };
      this.showLeaveBasisChange = true;
    },

    /**
     * 연차 현황 근무 미배정 배지 클릭 — 그 직원의 근무 배정 모달을 연다(CS-LOC-260915-0004).
     * 직원 관리로 건너가지 않고 이 자리에서 배정한다. 드롭다운 원본(shifts·shiftGroups)은 부팅 loadData 가
     * 채우지만, 근태 탭으로 곧장 진입해 비어 있을 수 있어 비었으면 다시 불러온다.
     */
    openLeaveShiftAssign(b: Record<string, unknown>) {
      this.leaveShiftAssignTarget = {
        employeeId: String(b.employeeId ?? ""),
        employeeName: String(b.employeeName ?? ""),
        employeeNumber: String(b.employeeNumber ?? ""),
        dailyStandardHours: Number(b.dailyStandardHours) || 8,
      };
      this.leaveShiftAssignForm = { shiftGroupId: "", shiftId: "" };
      this.showLeaveShiftAssign = true;
      if (!this.leaveShiftAssignHasOptions()) {
        this.loadShiftGroups();
        this.loadShifts();
      }
    },

    /** 고를 수 있는 활성 근무 그룹·형태가 하나라도 있는지 — 없으면 모달이 근무 설정으로 안내한다. */
    leaveShiftAssignHasOptions(): boolean {
      return (
        (this.shiftGroups as ShiftGroupItem[]).some((g) => g.isActive !== false) ||
        (this.shifts as ShiftItem[]).some((s) => s.isActive !== false)
      );
    },

    /** 그룹을 바꾸면 다른 그룹의 형태가 남지 않게 형태 선택을 비운다(일괄 배정 onBulkShiftGroupChange 와 동일). */
    onLeaveShiftAssignGroupChange() {
      const form = this.leaveShiftAssignForm;
      const cur = (this.shifts as ShiftItem[]).find((s) => s.id === form.shiftId);
      if (cur && form.shiftGroupId && cur.groupId !== form.shiftGroupId) {
        form.shiftId = "";
      }
    },

    /**
     * 배정 실행 — employee.bulkAssignShift 를 직원 한 명에게 쓴다(그룹만이면 형태 미지정, 형태면 그룹은 서버가 맞춤).
     * 끝나면 연차 현황을 다시 불러 배지가 사라지고, 직원 목록·근무 설정의 직원 수도 맞춘다.
     */
    async submitLeaveShiftAssign() {
      const target = this.leaveShiftAssignTarget;
      const { shiftGroupId, shiftId } = this.leaveShiftAssignForm;
      if (this.leaveShiftAssignSaving || !target || !(shiftGroupId || shiftId)) {
        return;
      }
      this.leaveShiftAssignSaving = true;
      try {
        await this.sdk.employee.bulkAssignShift(
          [target.employeeId],
          shiftGroupId || null,
          shiftId || null,
        );
        this.showLeaveShiftAssign = false;
        this.notify(
          `${target.employeeName} 직원의 근무를 「${this.bulkShiftAssignLabel(shiftGroupId, shiftId)}」(으)로 배정했어요.`,
        );
        await Promise.all([this.loadLeaveBalances(), this.loadEmployees(), this.loadShiftGroups()]);
      } catch (err) {
        showError(`근무 배정 실패: ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        this.leaveShiftAssignSaving = false;
      }
    },

    /** 오늘 이후 첫 회계일(YYYY-MM-DD) — 회계년도 기준은 회계일부터만 시작할 수 있다 */
    nextFiscalStartDate(): string {
      const monthDay = this.ruleFiscalStartSaved || "01-01";
      const today = this.leaveBasis.today || new Date().toISOString().slice(0, 10);
      const year = Number(today.slice(0, 4));
      const candidate = `${year}-${monthDay}`;
      return candidate >= today ? candidate : `${year + 1}-${monthDay}`;
    },

    /** 기준 라디오를 바꾸면 회계년도는 회계일로 시작일을 스냅하고, 입사일은 비운다 */
    onLeaveBasisFormBasisChange() {
      if (this.leaveBasisForm.basis === "fiscal_year") {
        this.leaveBasisForm.effectiveFrom = this.nextFiscalStartDate();
      }
    },

    async submitLeaveBasisChange() {
      if (this.leaveBasisSaving || !this.leaveBasisForm.effectiveFrom) {
        return;
      }
      this.leaveBasisSaving = true;
      try {
        await this.sdk.leaveRequest.scheduleBasisChange({
          basis: this.leaveBasisForm.basis,
          effectiveFrom: this.leaveBasisForm.effectiveFrom,
          newHireMode:
            this.leaveBasisForm.basis === "fiscal_year"
              ? this.leaveBasisForm.newHireMode
              : undefined,
          note: this.leaveBasisForm.note.trim() || undefined,
        });
        this.showLeaveBasisChange = false;
        await this.loadLeaveBasis();
        this.ruleBasisTab = this.leaveBasis.current.basis;
        this.notify(
          `${this.leaveBasisForm.effectiveFrom}부터 ${this.leaveBasisLabel(this.leaveBasisForm.basis)} 기준으로 바뀌도록 예약했어요.`,
        );
        if (this.leaveSubTab === "balances") {
          await this.loadLeaveBalances();
        }
      } catch (err) {
        showError(err instanceof Error ? err.message : String(err));
      } finally {
        this.leaveBasisSaving = false;
      }
    },

    async cancelLeaveBasisChange(id: string) {
      const pending = this.leaveBasis.pending;
      if (!pending) {
        return;
      }
      const ok = await askConfirm(
        `${pending.effectiveFrom}부터 ${this.leaveBasisLabel(pending.basis)} 기준으로 바뀌는 예약을 취소할까요?`,
      );
      if (!ok) {
        return;
      }
      try {
        await this.sdk.leaveRequest.cancelBasisChange(id);
        await this.loadLeaveBasis();
        this.notify("기준 변경 예약을 취소했어요.");
      } catch (err) {
        showError(err instanceof Error ? err.message : String(err));
      }
    },

    /** 회계일 저장 — datepicker에서 고른 날짜의 월·일만 저장한다(매년 반복). */
    async saveRuleFiscalStart() {
      const picked = this.ruleFiscalStartDate;
      if (!picked || picked.length < 10) {
        // 삭제(빈 값)는 기본 1월 1일로 되돌린다
        this.ruleFiscalStartDate = `${new Date().getFullYear()}-01-01`;
      }
      const monthDay = this.ruleFiscalStartDate.slice(5, 10);
      if (monthDay === this.ruleFiscalStartSaved) {
        return;
      }
      try {
        await this.sdk.organization.updateSettings({ fiscalYearStart: monthDay });
        this.ruleFiscalStartSaved = monthDay;
        this.notify("회계일을 저장했어요.");
      } catch (err) {
        this.notify(
          `${this._t("admin.save_failed")}: ${err instanceof Error ? err.message : String(err)}`,
          "error",
        );
        this.ruleFiscalStartDate = `${new Date().getFullYear()}-${this.ruleFiscalStartSaved}`;
      }
    },

    openAddRule() {
      this.editingRule = null;
      this.ruleForm = {
        name: "",
        basis: this.ruleBasisTab,
        isActive: true,
        yearsOfServiceMin: 0,
        yearsOfServiceMax: null,
        entitledDays: 15,
        accrualType: "annual",
        maxDays: null,
        description: "",
        sortOrder: this.entitlementRulesForBasis().length,
      };
      this.showEditRule = true;
    },

    /** 근속 구간으로부터 규칙 이름 파생 (이름 미지정 레거시 규칙/기본 규칙용) */
    _ruleBandName(min: unknown, max: unknown): string {
      const mn = Number(min ?? 0);
      const mx = max == null ? null : Number(max);
      if (mn === 0 && mx === 1) {
        return "입사 1년 미만";
      }
      if (mx == null) {
        return `${mn}년 이상 근속`;
      }
      return `${mn}~${mx}년 근속`;
    },

    /** 리스트 표시용 규칙 이름 — 지정 이름 우선, 없으면 근속 구간 파생명 */
    ruleDisplayName(rule: Record<string, unknown>): string {
      const n = ((rule.name as string) || "").trim();
      return n || this._ruleBandName(rule.yearsOfServiceMin, rule.yearsOfServiceMax);
    },

    openEditRule(rule: Record<string, unknown>) {
      this.editingRule = rule;
      this.ruleForm = {
        // 이름 미지정 레거시 규칙은 파생명으로 미리 채워 필수값 충족을 돕는다
        name: ((rule.name as string) || "").trim() || this.ruleDisplayName(rule),
        basis: ((rule.basis as string) || "fiscal_year") as "fiscal_year" | "hire_date",
        isActive: rule.isActive !== false,
        yearsOfServiceMin: (rule.yearsOfServiceMin as number) || 0,
        yearsOfServiceMax: (rule.yearsOfServiceMax as number) ?? null,
        entitledDays: Number(rule.entitledDays) || 15,
        accrualType: (rule.accrualType as "monthly" | "annual") || "annual",
        maxDays: rule.maxDays != null ? Number(rule.maxDays) : null,
        description: (rule.description as string) || "",
        sortOrder: (rule.sortOrder as number) || 0,
      };
      this.showEditRule = true;
    },

    async saveRule() {
      if (!this.ruleForm.name?.trim()) {
        this.notify("규칙 이름을 입력해 주세요.", "error");
        return;
      }
      try {
        // FIXME(타입 복구 중 발견): upsertEntitlementRule 입력의 id는 string, maxDays/description은 undefined 기반 타입인데 UI form은 unknown/null 값을 포함함. 입력 스키마 정렬 후 any 제거.
        await this.sdk.leaveRequest.upsertEntitlementRule({
          id: (this.editingRule?.id as string | undefined) || undefined,
          name: this.ruleForm.name.trim(),
          basis: this.ruleForm.basis,
          isActive: this.ruleForm.isActive,
          yearsOfServiceMin: this.ruleForm.yearsOfServiceMin,
          yearsOfServiceMax: this.ruleForm.yearsOfServiceMax,
          entitledDays: this.ruleForm.entitledDays,
          accrualType: this.ruleForm.accrualType,
          maxDays: this.ruleForm.maxDays,
          description: this.ruleForm.description || null,
          sortOrder: this.ruleForm.sortOrder,
          // biome-ignore lint/suspicious/noExplicitAny: SDK input payload type mismatch during getter migration
        } as any);
        const wasEditing = !!this.editingRule;
        this.showEditRule = false;
        await this.loadEntitlementRules();
        this.notify(wasEditing ? "휴가 규칙을 수정했어요." : "휴가 규칙을 추가했어요.");
      } catch (err) {
        this.notify(
          `${this._t("admin.save_failed")}: ${err instanceof Error ? err.message : String(err)}`,
          "error",
        );
      }
    },

    /** 부여 기준 상태 토글 (활성↔비활성). 비활성 규칙은 연차 산정에서 제외된다.
     *  중복 구간은 백엔드 산정 시 sortOrder가 빠른(먼저 적용된) 활성 규칙만 반영. */
    async toggleRuleActive(rule: Record<string, unknown>) {
      try {
        await this.sdk.leaveRequest.upsertEntitlementRule({
          id: rule.id as string,
          name: (rule.name as string) ?? null,
          isActive: rule.isActive === false,
          yearsOfServiceMin: rule.yearsOfServiceMin as number,
          yearsOfServiceMax: (rule.yearsOfServiceMax as number) ?? null,
          entitledDays: Number(rule.entitledDays),
          accrualType: rule.accrualType as "monthly" | "annual",
          maxDays: rule.maxDays != null ? Number(rule.maxDays) : null,
          description: (rule.description as string) || null,
          sortOrder: rule.sortOrder as number,
          // biome-ignore lint/suspicious/noExplicitAny: SDK input payload type mismatch during getter migration
        } as any);
        await this.loadEntitlementRules();
      } catch (err) {
        this.notify(
          `${this._t("admin.save_failed")}: ${err instanceof Error ? err.message : String(err)}`,
          "error",
        );
      }
    },

    /** 부여 기준 삭제 — 상태별 분기 (연차 관리 기획 부여 기준 §4②).
     *  활성: 삭제 불가(확인만). 비활성+연결: 재계산 경고. 비활성+미연결: 일반 삭제. */
    async deleteRule(rule: Record<string, unknown>) {
      const ruleName = (rule.name as string) || `${rule.yearsOfServiceMin}년 기준`;
      // 유형·기준 관리 패널(z-50) 안에서 호출되므로 확인창을 그 위(z-60)에 띄운다.
      this.confirmTop = true;
      if (rule.isActive !== false) {
        this.confirmTitle = "삭제";
        this.confirmMessage =
          "부여 기준을 삭제할 수 없어요. 먼저 비활성 상태로 변경한 뒤 삭제할 수 있어요.";
        this.confirmLabel = this._t("common.confirm");
        this.confirmHideCancel = true;
        this.confirmAction = null;
        this.showConfirm = true;
        return;
      }
      const hasLinked = Number(rule.linkedEmployeeCount || 0) > 0;
      this.confirmTitle = "삭제";
      this.confirmMessage = hasLinked
        ? `이 유형을 부여한 연차가 있어요. 삭제하면 ${ruleName} 기준으로 부여된 연차는 다시 계산돼요.`
        : `${ruleName} 연차 부여 기준을 삭제할까요?`;
      this.confirmLabel = this._t("common.confirm");
      this.confirmAction = async () => {
        try {
          await this.sdk.leaveRequest.deleteEntitlementRule(rule.id as string);
          await this.loadEntitlementRules();
          this.notify("휴가 규칙을 삭제했어요.");
        } catch (err) {
          this.notify(
            `${this._t("admin.delete_failed")}: ${err instanceof Error ? err.message : String(err)}`,
            "error",
          );
        }
      };
      this.showConfirm = true;
    },

    async loadRoles() {
      try {
        // 역할은 적은 수의 바운디드 집합 — 전체를 로드해 상태 필터/검색이
        // 페이지에 걸쳐 누락 없이 동작하도록 한다(팀 목록과 동일한 패턴).
        const items = await fetchAllPages<RoleItem>(
          (page, limit) => this.sdk.role.list({ includePermissions: true, page, limit }),
          100,
        );
        this.roles = items;
        const p = this.paging.roles;
        p.totalCount = items.length;
        p.totalPages = 1;
        p.page = 1;
      } catch {
        this.roles = [];
      } finally {
        this.loadedOnce.roles = true;
      }
    },

    async loadPermissions() {
      try {
        // FIXME(타입 복구 중 발견): role.listPermissions 응답 타입에 items fallback이 없어 레거시 응답 호환 로직과 맞지 않음. 응답 DTO 정리 후 any 제거.
        // biome-ignore lint/suspicious/noExplicitAny: SDK response type mismatch during getter migration
        const result: any = await this.sdk.role.listPermissions();
        this.permissions = Array.isArray(result) ? result : result?.items || [];
      } catch {
        this.permissions = [];
      }
      this.permMatrix = null;
    },

    computeStats() {
      const sc = this.employeeStatusCounts;
      this.stats = {
        // 전체 = 재직 + 휴직 — 팀 관리 · 병동 현황과 같은 기준. 퇴사 · 비활성은 옆 칸에 따로 보인다.
        totalEmployees: (sc.ACTIVE || 0) + (sc.ON_LEAVE || 0),
        activeEmployees: sc.ACTIVE || 0,
        inactiveEmployees: sc.INACTIVE || 0,
        onLeaveEmployees: sc.ON_LEAVE || 0,
        terminatedEmployees: sc.TERMINATED || 0,
        totalTeams: this.paging.teams.totalCount || this.teams.length,
        totalRoles: this.paging.roles.totalCount || this.roles.length,
      };
    },

    /* ──────────── employee actions ──────────── */
    // Add Employee 패널은 공유 컴포넌트로 옮겨졌다 (addEmployeePanel).
    // Open via: $dispatch('add-employee-open')

    /** 직원 관리 「앱 상태」 셀 — 라벨 톤·툴팁(onboarding.ts 순수 함수) */
    appStageLabelClass(stage: string): string {
      return stageLabelClass(stage);
    },
    appStatusTooltip(app: Parameters<typeof appStatusTooltip>[0]): string {
      return appStatusTooltip(app, (d) => Alpine.store("hereby").formatDateTime(d));
    },

    /** 직원 앱 배포 온보딩 화면의 바로가기 — 「앱 상태」 필터만 걸고 목록을 다시 읽는다 */
    applyEmployeeAppFilter(stages: string[]) {
      this.employeeFilter.appStages = [...stages];
      this.resetPage("employees");
      this.loadEmployees();
      this.computeStats();
    },

    /** 온보딩 배너 — 시스템 관리자만, 목표 달성·완료 처리·최근 닫음이면 숨긴다 */
    async loadOnboardingBanner() {
      const store = Alpine.store("hereby");
      const user = store.currentUser as {
        id?: string;
        organizationId?: string;
        isSystemAdmin?: boolean;
      } | null;
      // 「도입 끝남」(LNB 「시작하기」 숨김 · 설정 탭)은 이 화면을 쓸 수 있는 관리자 모두에게 적용한다 —
      // summary 는 employee:read 면 열린다. 배너만 시스템 관리자 전용으로 둔다.
      // 권한이 없으면 부르지 않는다: FORBIDDEN 응답은 SDK 가 권한 거부 팝업을 띄운다.
      if (this.isRestrictedAdmin || !store.hasPermission("employee:read")) {
        this.onboardingBanner.visible = false;
        return;
      }
      const finishedKey = appAdoptionFinishedKey(user?.organizationId);
      try {
        // 서버 응답 전에는 지난번 값으로 먼저 그린다(새로고침 때 메뉴 깜빡임 방지)
        this.appAdoptionFinished = localStorage.getItem(finishedKey) === "1";
      } catch {
        // 사생활 모드 — 서버 응답을 기다린다
      }
      try {
        const summary = await this.sdk.appOnboarding.summary({});
        let dismissedAt: string | null = null;
        try {
          dismissedAt = localStorage.getItem(onboardingBannerKey(user?.organizationId, user?.id));
        } catch {
          dismissedAt = null;
        }
        this.appAdoptionFinished = isAppAdoptionFinished(summary);
        try {
          localStorage.setItem(finishedKey, this.appAdoptionFinished ? "1" : "0");
        } catch {
          // 사생활 모드 — 다음 새로고침에서 한 번 깜빡일 뿐이다
        }
        this.onboardingBanner = {
          // 도입 배너는 직원 관리(employee:manage)를 가진 관리자에게(시스템 관리자는 통과)
          visible:
            Alpine.store("hereby").hasPermission("employee:manage") &&
            shouldShowOnboardingBanner(summary, dismissedAt),
          done: summary.progress.done,
          total: summary.progress.total,
          rate: summary.counts.rate,
          targetRate: summary.targetRate,
          // 도입 화면이 체크리스트 7단계 대신 묶음 흐름으로 바뀌어(10/13) 띠도 「지금 할 일」 묶음 이름으로 말한다
          step: currentOnboardingPhaseLabel(summary),
        };
      } catch {
        this.onboardingBanner.visible = false;
      }
    },

    dismissOnboardingBanner() {
      const user = Alpine.store("hereby").currentUser as {
        id?: string;
        organizationId?: string;
      } | null;
      try {
        localStorage.setItem(
          onboardingBannerKey(user?.organizationId, user?.id),
          new Date().toISOString(),
        );
      } catch {
        // 사생활 모드 — 이번 세션만 닫는다
      }
      this.onboardingBanner.visible = false;
    },

    resetEmployeeFilter() {
      this.employeeFilter.search = "";
      this.employeeFilter.statuses = [];
      this.employeeFilter.teamIds = [];
      this.employeeFilter.roleIds = [];
      this.employeeFilter.shiftGroupIds = [];
      this.employeeFilter.shiftIds = [];
      this.employeeFilter.hireDateFrom = "";
      this.employeeFilter.hireDateTo = "";
      this.employeeFilter.jobDuties = [];
      this.employeeFilter.wardIds = [];
      this.employeeFilter.appStages = [];
      this.employeeFilter.multipleShiftGroups = false;
      this.employeeFilter.shiftDiffersFromDefault = false;
      this.employeeSort.by = "";
      this.employeeSort.order = "asc";
      this.resetPage("employees");
      this.loadEmployees();
      this.computeStats();
    },

    /**
     * Toggle sort for an employee column.
     * First click on a column → sort ascending; subsequent click → flip to
     * descending; third click on a different column → switch field, asc.
     */
    toggleEmployeeSort(
      field: "employeeNumber" | "lastName" | "email" | "hireDate" | "status" | "jobDuty",
    ) {
      if (this.employeeSort.by === field) {
        this.employeeSort.order = this.employeeSort.order === "asc" ? "desc" : "asc";
      } else {
        this.employeeSort.by = field;
        this.employeeSort.order = "asc";
      }
      this.resetPage("employees");
      this.loadEmployees();
    },

    /** Email format validation */
    isValidEmail(email: string): boolean {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },

    async openEditEmployee(emp: Record<string, unknown>) {
      // Set form data BEFORE editingEmployee so x-if renders with correct values
      this.editForm = {
        lastName: (emp.lastName as string) || "",
        firstName: (emp.firstName as string) || "",
        email: (emp.email as string) || "",
        phone: (emp.phone as string) || "",
        countryCode: (emp.countryCode as string) || "+82",
        employeeNumber: (emp.employeeNumber as string) || "",
        hireDate: emp.hireDate ? new Date(emp.hireDate as string).toISOString().split("T")[0] : "",
        birthDate: emp.birthDate
          ? new Date(emp.birthDate as string).toISOString().split("T")[0]
          : "",
        residentNumber: (emp.residentNumber as string) || "",
        status: (emp.status as string) || "ACTIVE",
        shiftId: (emp.shiftId as string) || "",
        attendanceMethod: ((emp.attendanceMethod as string) || "") as
          | ""
          | "app"
          | "secom"
          | "manual",
        isSystemAdmin: !!(emp.isSystemAdmin as boolean),
        phoneOtpEnabled: Array.isArray(emp.authMethods)
          ? (emp.authMethods as string[]).includes("phone_otp")
          : false,
        newPassword: "",
        confirmPassword: "",
        passwordMode: "sms" as "sms" | "manual",
      };
      this.editMemberships = [];
      this.showAddTeamRow = false;
      this.addTeamForm = { teamId: "", roleId: "" };
      this.employeeHistory = { leaves: [], resignations: [] };
      this.editingEmployee = emp;
      this.showEditEmployee = true;
      try {
        const detail = await this.sdk.employee.getEmployee(emp.id as string);
        // Update form with detailed data (may have fields the list didn't include)
        if (detail) {
          this.editForm.phone = (detail.phone as string) || this.editForm.phone;
          this.editForm.shiftId = (detail.shiftId as string) || this.editForm.shiftId;
          if (detail.attendanceMethod !== undefined && detail.attendanceMethod !== null) {
            this.editForm.attendanceMethod = detail.attendanceMethod as
              | ""
              | "app"
              | "secom"
              | "manual";
          }
        }
        const teams = (detail?.teams || []) as Record<string, unknown>[];
        this.editMemberships = teams.map((t) => ({
          memberId: (t.memberId as string) || "",
          teamId: (t.id as string) || "",
          teamName: (t.name as string) || "",
          roleId: (t.roleId as string) || "",
          roleName: (t.roleName as string) || (t.role as string) || "",
          originalRoleId: (t.roleId as string) || "",
        }));
        // Load history in parallel
        this.sdk.employee.statusHistory(emp.id as string).then((h: Record<string, unknown>) => {
          if (h) {
            this.employeeHistory = h as typeof this.employeeHistory;
          }
        });
      } catch {
        this.editMemberships = [];
      }
    },

    /** Re-fetch memberships for the currently-editing employee (after inline team/role changes). */
    async loadEditMemberships() {
      if (!this.editingEmployee) {
        return;
      }
      try {
        const detail = await this.sdk.employee.getEmployee(this.editingEmployee.id as string);
        const teams = (detail?.teams || []) as Record<string, unknown>[];
        this.editMemberships = teams.map((t) => ({
          memberId: (t.memberId as string) || "",
          teamId: (t.id as string) || "",
          teamName: (t.name as string) || "",
          roleId: (t.roleId as string) || "",
          roleName: (t.roleName as string) || (t.role as string) || "",
          originalRoleId: (t.roleId as string) || "",
        }));
      } catch {
        /* keep existing data */
      }
    },

    async saveEmployee() {
      if (!this.editingEmployee) {
        return;
      }
      try {
        const emp = this.editingEmployee;
        // FIXME(타입 복구 중 발견): updateEmployee 입력은 구체 Employee update 스키마를 기대하지만 UI diff 객체는 Record 기반 unknown 값을 누적함. 입력 스키마 정렬 후 any 제거.
        // biome-ignore lint/suspicious/noExplicitAny: SDK input type mismatch during getter migration
        const update: any = { id: emp.id as string };
        const fields: [string, string, unknown][] = [
          ["lastName", this.editForm.lastName, emp.lastName],
          ["firstName", this.editForm.firstName, emp.firstName],
          ["email", this.editForm.email, emp.email],
          ["phone", this.editForm.phone, emp.phone],
          ["employeeNumber", this.editForm.employeeNumber, emp.employeeNumber],
          ["status", this.editForm.status, emp.status],
        ];
        for (const [key, newVal, oldVal] of fields) {
          if (newVal && newVal !== (oldVal || "")) {
            update[key] = newVal;
          }
        }
        if (this.editForm.shiftId !== (emp.shiftId || "")) {
          update.shiftId = this.editForm.shiftId || undefined;
        }
        if (this.editForm.hireDate) {
          const oldDate = emp.hireDate
            ? new Date(emp.hireDate as string).toISOString().split("T")[0]
            : "";
          if (this.editForm.hireDate !== oldDate) {
            update.hireDate = this.editForm.hireDate;
          }
        }
        if (this.editForm.birthDate) {
          const oldBirth = emp.birthDate
            ? new Date(emp.birthDate as string).toISOString().split("T")[0]
            : "";
          if (this.editForm.birthDate !== oldBirth) {
            update.birthDate = this.editForm.birthDate;
          }
        }
        if (this.editForm.residentNumber !== (emp.residentNumber || "")) {
          update.residentNumber = this.editForm.residentNumber || undefined;
        }
        // Attendance method override (empty string = use org default = null)
        const oldMethod = (emp.attendanceMethod as string) || "";
        if (this.editForm.attendanceMethod !== oldMethod) {
          update.attendanceMethod = this.editForm.attendanceMethod || null;
        }
        // System admin toggle
        if (this.editForm.isSystemAdmin !== !!(emp.isSystemAdmin as boolean)) {
          update.isSystemAdmin = this.editForm.isSystemAdmin;
        }
        // Phone OTP login toggle — patches employee.authMethods
        const currentMethods: string[] = Array.isArray(emp.authMethods)
          ? (emp.authMethods as string[])
          : [];
        const currentlyHasOtp = currentMethods.includes("phone_otp");
        if (this.editForm.phoneOtpEnabled !== currentlyHasOtp) {
          let nextMethods = currentMethods.filter((m) => m !== "phone_otp");
          if (this.editForm.phoneOtpEnabled) {
            nextMethods.push("phone_otp");
          }
          // authMethods has min(1) — fall back to email if disabling otp would empty the list
          if (nextMethods.length === 0) {
            nextMethods = ["email"];
          }
          update.authMethods = nextMethods;
        }
        await this.sdk.employee.updateEmployee(update);
        // Save role changes on team memberships — 소속별 호출을 같은 틱에 보내
        // 한 왕복(httpBatchLink)으로 묶는다. 하나가 실패해도 나머지는 저장하고,
        // 실패는 알린다(역할 관리 권한이 없으면 거절돼 — 조용히 넘기면 저장된 것처럼 보인다).
        const roleResults = await Promise.allSettled(
          this.editMemberships
            .filter((m) => m.roleId && m.roleId !== m.originalRoleId)
            .map((m) => this.sdk.team.updateMember(m.teamId, m.memberId, { roleId: m.roleId })),
        );
        const roleFailure = roleResults.find(
          (r): r is PromiseRejectedResult => r.status === "rejected",
        );
        this.showEditEmployee = false;
        await this.loadEmployees();
        this.computeStats();
        if (roleFailure) {
          const reason = roleFailure.reason;
          showError(
            `직원 정보는 저장했지만 ${attachJosa(this._term("role"), "은/는")} 저장하지 못했어요: ${reason instanceof Error ? reason.message : String(reason)}`,
          );
        } else {
          this.notify("직원 정보를 저장했습니다");
        }
      } catch (err) {
        showError(
          `${this._t("admin.save_failed")}: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    },

    async removeTeamMembership(idx: number) {
      const m = this.editMemberships[idx];
      if (!m) {
        return;
      }
      if (!this.editingEmployee) {
        return;
      }
      if (!(await askConfirm(this._t("admin.confirm_remove_team").replace("{team}", m.teamName)))) {
        return;
      }
      try {
        await this.sdk.team.removeMember(m.teamId, m.memberId);
        this.editMemberships.splice(idx, 1);
        this.notify("팀에서 제외했습니다");
        // Removing a membership may leave the employee team-less → refresh 미소속.
        this.loadUnassignedMembers();
      } catch (err) {
        showError(
          `${this._t("admin.team_remove_failed")}: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    },

    async addEmployeeToTeam() {
      if (!this.editingEmployee) {
        return;
      }
      if (!this.addTeamForm.teamId) {
        return;
      }
      try {
        const memberInput = {
          teamId: this.addTeamForm.teamId,
          employeeId: this.editingEmployee.id as string,
        };
        const joined = await this.sdk.team.addMember(memberInput);
        // Refresh memberships
        const detail = await this.sdk.employee.getEmployee(this.editingEmployee.id as string);
        const teams = (detail?.teams || []) as Record<string, unknown>[];
        this.editMemberships = teams.map((t) => ({
          memberId: (t.memberId as string) || "",
          teamId: (t.id as string) || "",
          teamName: (t.name as string) || "",
          roleId: (t.roleId as string) || "",
          roleName: (t.roleName as string) || (t.role as string) || "",
          originalRoleId: (t.roleId as string) || "",
        }));
        this.addTeamForm = { teamId: "", roleId: "" };
        this.showAddTeamRow = false;
        this.notify("팀에 추가했습니다");
        this.notifyWardScheduler([joined.wardAssignment.scheduler]);
        // Employee now has a team → drop from 미소속.
        this.loadUnassignedMembers();
      } catch (err) {
        showError(
          `${this._t("admin.team_add_failed")}: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    },

    async resetEmployeePassword() {
      if (!this.editingEmployee) {
        return;
      }
      if (!(await askConfirm(this._t("admin.confirm_password_reset")))) {
        return;
      }
      try {
        // FIXME(타입 복구 중 발견): adminResetPassword 응답 타입에 temporaryPassword 필드가 없지만 수동 전달 fallback UI가 해당 값을 사용함. 응답 DTO 복구 후 any 제거.
        // biome-ignore lint/suspicious/noExplicitAny: SDK response type mismatch during getter migration
        const result: any = await this.sdk.auth.adminResetPassword(
          this.editingEmployee.id as string,
        );
        if (result.smsSent) {
          showAlert(this._t("admin.temp_password_sent"));
        } else if (result.temporaryPassword) {
          showAlert(
            this._t("admin.temp_password_manual_msg").replace("{pw}", result.temporaryPassword),
          );
        } else {
          showAlert(result.message || this._t("admin.password_reset_done"));
        }
      } catch (err) {
        showError(
          `${this._t("admin.password_reset_failed")}: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    },

    /**
     * Add or replace a membership via the cascading picker.
     * If editMemberId is set, the old membership is removed first (edit flow).
     * If empty, it's a pure add.
     */
    async changeMembership(
      empId: string,
      editMemberId: string,
      teamId: string,
      roleId: string | null,
    ) {
      let addedInBatch = false;
      // 병동 배정 처리 — 기본은 새 팀 병동 자동 배정, 병동 팀끼리 옮길 땐 아래 확인창 선택을 따른다.
      // 역할은 소속마다 따로 — 고른 역할은 이번에 추가하는 소속에만 붙인다(다른 소속 역할은 그대로).
      let addInput: {
        teamId: string;
        employeeId: string;
        roleId?: string;
        wardAssignment?: "auto" | "move" | "none";
        fromTeamId?: string;
      } = { teamId, employeeId: empId, ...(roleId ? { roleId } : {}) };
      let joinedScheduler: WardSchedulerNote | undefined;
      try {
        // Remove old membership if editing
        if (editMemberId) {
          // employees 는 페이지 단위 목록이라 다른 페이지의 직원은 없을 수 있다 —
          // 전체 로스터(allMembers, 팀 관리 탭용 org-wide 로드)로 폴백한다.
          const editingEmp =
            this.employees.find((e) => e.id === empId) ||
            (this.allMembers as Record<string, unknown>[]).find((e) => e.employeeId === empId);
          const oldMembership = (
            (editingEmp?.memberships as { memberId: string; teamId: string }[] | undefined) || []
          ).find((m) => m.memberId === editMemberId);
          if (oldMembership) {
            if (oldMembership.teamId !== teamId) {
              const decision = await this.askWardMove(editingEmp, oldMembership.teamId, teamId);
              if (decision === "cancel") {
                return;
              }
              if (decision === "move") {
                addInput = {
                  ...addInput,
                  wardAssignment: "move",
                  fromTeamId: oldMembership.teamId,
                };
              } else if (decision === "keep") {
                addInput = { ...addInput, wardAssignment: "none" };
              }
            }
            // 제거 실패(이미 사라진 소속)는 무시한다.
            const remove = this.sdk.team
              .removeMember(oldMembership.teamId, editMemberId)
              .catch(() => undefined);
            if (oldMembership.teamId === teamId) {
              // 같은 팀으로의 재배정은 제거가 끝난 뒤 추가해야 재활성화가 꼬이지 않는다.
              await remove;
            } else {
              // 다른 팀이면 제거·추가를 같은 틱에 보내 한 왕복(httpBatchLink)으로 처리.
              const [, joined] = await Promise.all([remove, this.sdk.team.addMember(addInput)]);
              joinedScheduler = joined.wardAssignment.scheduler;
              addedInBatch = true;
            }
          }
        }
        // Add new membership
        if (!addedInBatch) {
          const joined = await this.sdk.team.addMember(addInput);
          joinedScheduler = joined.wardAssignment.scheduler;
        }
        // 직원 목록과 전체 로스터(미소속 반영)를 같은 틱에 시작 — 목록만 기다리고
        // 로스터는 화면을 막지 않게 뒤에서 채운다.
        this.loadUnassignedMembers();
        await this.loadEmployees();
        this.notifyWardScheduler([joinedScheduler]);
      } catch (err) {
        showError(
          `소속 ${editMemberId ? "변경" : "추가"} 실패: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    },

    /**
     * 팀 이동 확인 — 병동이 연결된 팀에서 다른 병동 팀으로 옮기고, 직원이 예전 팀 병동에 배정돼 있으면
     * 병동 배정도 함께 옮길지 묻는다. 그 밖에는 묻지 않고 자동 배정(auto)으로 둔다.
     * move = 병동도 함께 옮기기 · keep = 팀만 옮기기(병동 배정은 그대로) · cancel = Esc(이동 취소)
     */
    async askWardMove(
      emp: Record<string, unknown> | undefined,
      fromTeamId: string,
      toTeamId: string,
    ): Promise<"auto" | "move" | "keep" | "cancel"> {
      const teams = this.teams as Record<string, unknown>[];
      const from = teams.find((t) => t.id === fromTeamId);
      const to = teams.find((t) => t.id === toTeamId);
      const fromWardId = from?.wardId as string | undefined;
      const toWardId = to?.wardId as string | undefined;
      const wards = (emp?.wards as { wardId: string }[] | undefined) || [];
      // 두 팀 모두 서로 다른 병동에 연결돼 있어야 묻는다
      if (!(fromWardId && toWardId) || fromWardId === toWardId) {
        return "auto";
      }
      // 예전 병동에 배정돼 있고 새 병동엔 아직 없을 때만 — 그 밖엔 서버 자동 배정으로 충분하다
      const hasFrom = wards.some((w) => w.wardId === fromWardId);
      const hasTo = wards.some((w) => w.wardId === toWardId);
      if (!hasFrom || hasTo) {
        return "auto";
      }
      const name = ((emp?.fullName || emp?.employeeName || "") as string).trim();
      const toName = (to?.name as string) || "";
      const choice = await askChoice({
        message: `${name}님을 ${(from?.name as string) || ""}에서 ${toName} 팀으로 옮겨요.\n${toName}에 연결된 병동으로 병동 배정도 함께 옮길까요?`,
        emphasis: [name, toName].filter(Boolean),
        confirmLabel: "병동도 함께 옮기기",
        altLabel: "팀만 옮기기",
      });
      if (choice === "confirm") {
        return "move";
      }
      return choice === "alt" ? "keep" : "cancel";
    },

    /** 팀 관리 탭에서 보이는 팀 컴포지션 캐시(teamMembers)를 갱신한다.
     *  전달된 팀 + 현재 선택된 팀을 새로 조회 — 인라인 팀/역할 편집 후
     *  멤버 행 표시가 즉시 반영되도록. 실패 시 기존 캐시를 유지한다. */
    async refreshOrgTeamCompositions(teamIds: string[] = []) {
      // 팀별 명단은 team.roster 에서 파생된다 — 어느 팀이 바뀌었든 로스터 한 번으로 모든 팀이 같이 새로워진다
      // (같은 틱에 loadUnassignedMembers 를 함께 부르는 호출부와 한 요청으로 합쳐진다). 로스터를 아직
      // 못 받은 상태(첫 진입 전 · 실패)에서만 예전처럼 바뀐 팀의 getComposition 을 읽는다.
      if (this.rosterLoaded) {
        await this.loadUnassignedMembers();
        return;
      }
      // "workspace" 는 팀이 아니라 org 스코프 표시값 — 컴포지션 조회 대상이 아니다.
      const ids = new Set(teamIds.filter((id) => id && id !== "workspace"));
      const sel = this.orgSelected as { type?: string; id?: string };
      if (sel?.type === "team" && sel.id) {
        ids.add(sel.id);
      }
      await Promise.all(
        [...ids].map(async (tid) => {
          try {
            const result = await this.sdk.team.getComposition(tid);
            this.teamMembers[tid] = result?.members || [];
          } catch {
            /* keep prior cache */
          }
        }),
      );
    },

    // biome-ignore lint/suspicious/noExplicitAny: dynamic employee data from SDK
    isTeamAssigned(emp: any, teamId: string): boolean {
      const memberships = (emp?.memberships || []) as { teamId: string }[];
      return memberships.some((m) => m.teamId === teamId);
    },

    // biome-ignore lint/suspicious/noExplicitAny: dynamic employee data from SDK
    isRoleAssigned(emp: any, roleId: string): boolean {
      const memberships = (emp?.memberships || []) as { roleId?: string | null }[];
      return memberships.some((m) => m.roleId === roleId);
    },

    quickTeamChange(empId: string, teamId: string) {
      const team = this.teams.find((t: Record<string, unknown>) => t.id === teamId);
      const emp = this.employees.find((e: Record<string, unknown>) => e.id === empId);
      const teamName = (team?.name as string | undefined) || teamId;
      const empName = (emp?.fullName as string | undefined) || empId;
      // biome-ignore lint/suspicious/noExplicitAny: dynamic employee data
      const memberships = ((emp as any)?.memberships || []) as {
        memberId: string;
        teamId: string;
      }[];
      const existing = memberships.find((m) => m.teamId === teamId);

      // Toggle semantics: click an already-assigned team → remove; otherwise add.
      if (existing) {
        this.confirmMessage = this._t("admin.confirm_team_exclude")
          .replace("{emp}", empName)
          .replace("{team}", teamName);
        this.confirmAction = async () => {
          try {
            await this.sdk.team.removeMember(existing.teamId, existing.memberId);
            await this.loadEmployees();
          } catch (err) {
            showError(
              `${this._t("admin.team_exclude_failed")}: ${err instanceof Error ? err.message : String(err)}`,
            );
          }
        };
      } else {
        this.confirmMessage = this._t("admin.confirm_team_add")
          .replace("{emp}", empName)
          .replace("{team}", teamName);
        this.confirmAction = async () => {
          try {
            const joined = await this.sdk.team.addMember({ teamId, employeeId: empId });
            await this.loadEmployees();
            this.notifyWardScheduler([joined.wardAssignment.scheduler]);
          } catch (err) {
            showError(
              `${this._t("admin.team_add_failed")}: ${err instanceof Error ? err.message : String(err)}`,
            );
          }
        };
      }
      this.showConfirm = true;
    },

    quickRoleChange(
      empId: string,
      roleId: string,
      m?: { memberId: string; teamId: string; roleId?: string | null },
    ) {
      // 역할은 소속마다 따로 — 이 소속(m)의 역할만 바꾸거나 해제한다(다른 소속 역할은 그대로).
      if (!m) {
        return;
      }
      const role = this.roles.find((r) => r.id === roleId);
      // employees 는 페이지 단위 — 팀 관리 탭 행은 전체 로스터(allMembers)로 폴백.
      const emp =
        this.employees.find((e: Record<string, unknown>) => e.id === empId) ||
        (this.allMembers as Record<string, unknown>[]).find((e) => e.employeeId === empId);
      const roleName = (role?.name as string | undefined) || roleId;
      const empName =
        (emp?.fullName as string | undefined) || (emp?.employeeName as string | undefined) || empId;
      const alreadyAssigned = m.roleId === roleId;

      // Toggle semantics: click the current role → clear; otherwise assign.
      if (alreadyAssigned) {
        this.confirmMessage = this._t("admin.confirm_role_remove")
          .replace("{emp}", empName)
          .replace("{role}", roleName);
        this.confirmAction = async () => {
          try {
            await this.sdk.team.updateMember(m.teamId, m.memberId, { roleId: null });
            // 팀 관리 탭 뷰(전체 로스터·팀 컴포지션)도 같은 틱에 시작해 함께 갱신 —
            // 목록만 기다리고 나머지는 뒤에서 채운다.
            this.loadUnassignedMembers();
            this.refreshOrgTeamCompositions([m.teamId]);
            await this.loadEmployees();
          } catch (err) {
            showError(
              `${this._t("admin.role_remove_failed")}: ${err instanceof Error ? err.message : String(err)}`,
            );
          }
        };
      } else {
        this.confirmMessage = this._t("admin.confirm_role_change")
          .replace("{emp}", empName)
          .replace("{role}", roleName);
        this.confirmAction = async () => {
          try {
            await this.sdk.team.updateMember(m.teamId, m.memberId, { roleId });
            // 팀 관리 탭 뷰(전체 로스터·팀 컴포지션)도 같은 틱에 시작해 함께 갱신 —
            // 목록만 기다리고 나머지는 뒤에서 채운다.
            this.loadUnassignedMembers();
            this.refreshOrgTeamCompositions([m.teamId]);
            await this.loadEmployees();
          } catch (err) {
            showError(
              `${this._t("admin.role_change_failed")}: ${err instanceof Error ? err.message : String(err)}`,
            );
          }
        };
      }
      this.showConfirm = true;
    },

    /* ──────────── 근태 관리 ──────────── */
    async openAttendanceModal(emp: Record<string, unknown>) {
      this.attendanceEmployee = emp;
      this.attendanceRecords = [];
      this.secomEvents = [];
      this.attendanceWorkLog = null;
      // 듀티(3교대) 판정 — 서버의 근무형태 판정(근무표 → 직원 배정 → 그룹 → 기본 근무 → 조직 기본)을 그대로 쓴다.
      // 예전엔 직원 배정 형태만 보고 화면이 자체 판정해 그룹만 배정된 3교대 직원이 듀티로 안 잡혔다.
      this.attendanceEffectiveShift = null;
      const empId = emp.id as string | undefined;
      if (empId && this.sdk) {
        try {
          this.attendanceEffectiveShift = await this.sdk.attendance.getEffectiveShift(empId);
        } catch {
          this.attendanceEffectiveShift = null;
        }
      }
      // 기본 기간 = 해당 월 (§2.2). store.todayLocal()이 org 타임존 기준 오늘을 준다.
      const todayIso = Alpine.store("hereby").todayLocal() || new Date().toISOString().slice(0, 10);
      this.showAttendanceModal = true;
      await this.setAttendanceMonth(todayIso.slice(0, 7));
    },

    /** 월 단위 조회 — attendanceMonth("YYYY-MM")에서 1일~말일 범위를 파생해 로드 */
    async setAttendanceMonth(yearMonth: string) {
      const range = monthRange(yearMonth);
      if (!range) {
        return;
      }
      this.attendanceMonth = yearMonth;
      this.attendanceFilter.dateFrom = range.from;
      this.attendanceFilter.dateTo = range.to;
      await this.loadEmployeeAttendance();
    },

    /** ◀ ▶ 월 이동 */
    shiftAttendanceMonth(delta: number) {
      this.setAttendanceMonth(shiftMonth(this.attendanceMonth, delta));
    },

    get attendanceWarningCount(): number {
      return this.attendanceRecords.filter(
        // biome-ignore lint/suspicious/noExplicitAny: dynamic record
        (r: any) => r.needsOvertimeReport === true,
      ).length;
    },

    /* ── 일별 표 표시 헬퍼 ── */

    /** 국가 공휴일 이름 ("" = 국가 공휴일 아님) — 서버 동기화 캐시(holiday-calendar) 기준.
     *  loadHolidays()가 해당 연도를 ensure한 뒤에 유효하다. */
    krHolidayName(date: unknown): string {
      return getNationalHolidayName(String(date || ""));
    },

    /** 날짜 라벨 — YYYY.MM.DD.(요일). 근태 표는 예외적으로 24시간/한국식 날짜 표기. */
    attnDateLabel(date: unknown): string {
      return attnDateLabel(date);
    },

    /**
     * 듀티(3교대) 직원 여부 — 근무 형태 설정(연차 차감 판정 = 듀티 기준)대로 판정한다.
     * 듀티 직군은 주말·공휴일이 일반 근무이므로 근태 관리 팝업에서 휴일 4칸(휴일일반·
     * 휴일연장·휴일야간·휴일야간연장)을 일반·연장·야간·야간연장에 합산해 표시하고
     * 칼럼 자체를 숨긴다. (신규 기록은 서버가 이미 일반 버킷으로 분류 — 이 통합은
     * 과거 기록 표시 정합용)
     */
    get attendanceDutyMode(): boolean {
      return this.attendanceEffectiveShift?.leaveDeductionMode === "duty";
    },

    /** 표시용 버킷 값 — 듀티면 휴일 짝 칼럼을 흡수해 합산한 수치 */
    attnBucketNum(r: Record<string, unknown>, base: string, holidayPair: string): number {
      const merged =
        (Number(r[base]) || 0) + (this.attendanceDutyMode ? Number(r[holidayPair]) || 0 : 0);
      return Math.round(merged * 10) / 10;
    },

    /** 표시용 버킷 텍스트 (소수점 1자리) */
    attnBucket(r: Record<string, unknown>, base: string, holidayPair: string): string {
      // h:mm 표기 (실근로시간 정산 규칙, 2026-07)
      return formatHoursHM(this.attnBucketNum(r, base, holidayPair));
    },

    /** 월별 합계 — 지정한 시간 구분 컬럼의 합(소수점 1자리). */
    attnSum(field: string): string {
      const total = this.attendanceRecords.reduce(
        // biome-ignore lint/suspicious/noExplicitAny: dynamic record
        (s: number, r: any) => s + (Number(r[field]) || 0),
        0,
      );
      return formatHoursHM(total);
    },

    /** 월별 합계(듀티 병합) — 듀티면 휴일 짝 칼럼 합계를 흡수 */
    attnSumMerged(base: string, holidayPair: string): string {
      const total = this.attendanceRecords.reduce(
        // biome-ignore lint/suspicious/noExplicitAny: dynamic record
        (s: number, r: any) => s + this.attnBucketNum(r, base, holidayPair),
        0,
      );
      return formatHoursHM(total);
    },

    /** 날짜 빨간 톤 — 주말·공휴일(한국)·OFF(휴무 근무) 포함 */
    attendanceRowRed(r: Record<string, unknown>): boolean {
      return attendanceRowRed(r, (d) => this.krHolidayName(d));
    },

    /** 야간 퇴근 표시 — 퇴근이 다음날이면 +N (예: "06:00 (+1)") */
    checkOutDayOffset(r: Record<string, unknown>): number {
      const store = Alpine.store("hereby");
      return checkOutDayOffset(r, (v) => store.formatDateISO(v));
    },

    /** 소정 근무(근무 열)가 자정을 넘겨 끝나는가 — 소정 퇴근일이 소정 출근일보다 다음 날.
     *  buildScheduledTimes 가 야간 근무의 scheduledCheckOut 을 익일로 굴려 저장하므로
     *  두 소정 시각의 로컬 날짜를 비교한다. */
    scheduledNextDay(r: Record<string, unknown>): boolean {
      const store = Alpine.store("hereby");
      const cin = r.scheduledCheckIn;
      const cout = r.scheduledCheckOut;
      if (!(cin && cout)) {
        return false;
      }
      const inIso = store.formatDateISO(cin as string);
      const outIso = store.formatDateISO(cout as string);
      return !!(inIso && outIso) && outIso > inIso;
    },

    /* ── 지문 ±5분 대조 (출퇴근 §3·§4) ──
       기록 방식이 '지문 단말기(secom)'인 직원만 지문 대조를 제공한다.
       앱/관리자 수동은 제공하지 않음. eventDatetime은 Seoul 로컬 wall-clock(YYYYMMDDHHmmss),
       출퇴근 시각은 UTC로 저장되므로 둘 다 org-로컬 분 단위로 변환해 차이를 비교한다. */

    /** 직원의 출퇴근 기록 방식 ("app" | "secom" | "manual" | "") */
    attnMethod(): string {
      return String(this.attendanceEmployee?.attendanceMethod || "");
    },

    /** 지문 대조 포맷터 주입 — attendance/secom-match.ts 순수 함수용. */
    _secomDeps() {
      const store = Alpine.store("hereby");
      return {
        formatDateISO: (v: string) => store.formatDateISO(v),
        formatTimeOnly: (v: string) => this.formatTimeOnly(v),
      };
    },

    /** 해당 행의 출근/퇴근 시각과 ±5분 내 지문 기록(가장 가까운 1건)을 찾는다. */
    secomMatchEvent(
      r: Record<string, unknown>,
      which: "in" | "out",
    ): Record<string, unknown> | null {
      return matchSecomEvent(r, which, this.secomEvents, this._secomDeps());
    },

    /** 지문 단말기 직원 + ±5분 매칭 지문이 있는가 → 상세 기록 툴팁 제공 (§3 ①) */
    secomShowMatch(r: Record<string, unknown>, which: "in" | "out"): boolean {
      if (this.attnMethod() !== "secom") {
        return false;
      }
      return this.secomMatchEvent(r, which) !== null;
    },

    /** 지문 단말기 직원인데 기록된 시각에 ±5분 매칭 지문이 없는가 → `!` 경고 (§3 ②) */
    secomShowNoMatch(r: Record<string, unknown>, which: "in" | "out"): boolean {
      if (this.attnMethod() !== "secom") {
        return false;
      }
      const target =
        which === "in"
          ? (r.actualCheckIn as string) || (r.checkIn as string)
          : (r.actualCheckOut as string) || (r.checkOut as string);
      if (!target) {
        return false;
      }
      return this.secomMatchEvent(r, which) === null;
    },

    /** 매칭 지문 상세 툴팁 — "지문 인식: 2026.06.24. 16:25" (§3 ① 예시 · 화면 날짜 형식 통일) */
    secomMatchTip(r: Record<string, unknown>, which: "in" | "out"): string {
      const ev = this.secomMatchEvent(r, which);
      if (!ev) {
        return "";
      }
      const dt = String(ev.eventDatetime || "");
      if (dt.length < 12) {
        return "";
      }
      const stamp = `${dt.slice(0, 4)}.${dt.slice(4, 6)}.${dt.slice(6, 8)}. ${dt.slice(8, 10)}:${dt.slice(10, 12)}`;
      return `지문 인식: ${stamp}${ev.terminalId ? ` · 단말 ${ev.terminalId}` : ""}`;
    },

    /** 퇴근 미입력 + 소정 퇴근시각 ±5분 지문이 있는가 (§4) — 추정 퇴근 안내 */
    secomCheckoutGuess(r: Record<string, unknown>): Record<string, unknown> | null {
      if (this.attnMethod() !== "secom") {
        return null;
      }
      return checkoutGuess(r, this.secomEvents, this._secomDeps());
    },

    /** 추정 퇴근 툴팁 (§4) — 정확하지 않을 수 있음 안내 포함 */
    secomCheckoutGuessTip(r: Record<string, unknown>): string {
      const ev = this.secomCheckoutGuess(r);
      const base =
        "지문 인식 기반의 퇴근 기록이 있어요.(다만, 실제 퇴근 시간과 완전히 일치하지 않을 수 있으니 확인해 주세요.)";
      if (!ev) {
        return base;
      }
      const dt = String(ev.eventDatetime || "");
      const time = dt.length >= 12 ? ` — ${dt.slice(8, 10)}:${dt.slice(10, 12)}` : "";
      return `${base}${time}`;
    },

    /* ── 근태 수정 모달 (§2.3.2) ── */

    openAttendanceEditModal(r: Record<string, unknown>) {
      this.attendanceEditRecord = r;
      this.attendanceEditTab = "times";
      const n = (v: unknown) => Number(v) || 0;
      this.attendanceEditForm = {
        checkIn: this.timeInputValue((r.checkIn as string) || (r.actualCheckIn as string)),
        checkOut: this.timeInputValue((r.checkOut as string) || (r.actualCheckOut as string)),
        reason: "",
        hours: {
          regular: n(r.regularHours),
          overtime: n(r.overtimeHours),
          night: n(r.nightHours),
          nightOvertime: n(r.nightOvertimeHours),
          holidayRegular: n(r.holidayRegularHours),
          holidayOvertime: n(r.holidayOvertimeHours),
          holidayNight: n(r.holidayNightHours),
          holidayNightOvertime: n(r.holidayNightOvertimeHours),
          break: n(r.breakHours),
        },
      };
      this.showAttendanceEditModal = true;
    },

    /** 8개 구분 시간의 합(휴게 제외, 소수점 1자리). 근태 관리 합계와 동일. */
    attnEditSum(): number {
      return editHoursSum(this.attendanceEditForm.hours);
    },

    /** 출퇴근 시간으로부터 기대 근무시간(휴게 제외). 미입력이면 null. */
    attnEditExpectedHours(): number | null {
      const f = this.attendanceEditForm;
      return expectedHoursFromTimes(f.checkIn, f.checkOut, f.hours.break || 0);
    },

    /** 퇴근이 출근보다 이른가(=익일 퇴근) — 익일 퇴근 체크박스의 활성/체크 상태.
     *  시간 계산(edit-hours)이 동일 규칙(checkOut <= checkIn → +24h)으로 굴러가므로
     *  체크박스는 그 해석을 사용자에게 확인시켜 주는 표시다. */
    attnEditIsNextDay(): boolean {
      const f = this.attendanceEditForm;
      return !!(f.checkIn && f.checkOut) && f.checkOut <= f.checkIn;
    },

    /** 템플릿용 h:mm 표기 (8.5 → "8:30"). 실근로시간 정산 표기 규칙(2026-07). */
    fmtHM(hours: unknown): string {
      return formatHoursHM(hours);
    },

    /** 템플릿용 h:mm 입력 파싱 ("8:30"/"8.5" → 8.5). fmtHM 와 쌍 — h:mm 입력칸의 @change 에서 사용. */
    parseHM(raw: unknown): number {
      return parseHoursHM(raw);
    },

    /** 근무 시간 계산 입력칸 — "h:mm"/소수("8.5") 둘 다 받아 소수 시간으로 저장.
     *  hours 객체를 재할당해 :value 가 정규화된 h:mm 로 다시 그려지게 한다. */
    attnEditSetHours(key: string, raw: string): void {
      const hours = this.attendanceEditForm.hours as Record<string, number>;
      this.attendanceEditForm.hours = { ...hours, [key]: parseHoursHM(raw) };
    },

    /** 합계가 출퇴근 시간과 맞지 않는가 (0.1h 이상 차이). 둘 다 입력됐을 때만 검사. */
    attnEditMismatch(): boolean {
      const expected = this.attnEditExpectedHours();
      if (expected === null) {
        return false;
      }
      return Math.abs(this.attnEditSum() - expected) > 0.1;
    },

    /** 저장 활성: 수정 사유 입력만 있으면 활성.
     *  출퇴근 미입력(퇴근 누락 등)·합계 불일치는 경고만 표시하고 저장은 허용 —
     *  관리자가 사유를 기재해 의도적으로 수정/저장할 수 있게 한다. */
    attnEditCanSave(): boolean {
      return this.attendanceEditForm.reason.trim().length > 0;
    },

    /** 출퇴근 시간 변경 시 소정 4종(일반/야간/휴일일반/휴일야간) 자동 계산. 초과 4종은 유지. */
    attnEditRecalcFromTimes() {
      const f = this.attendanceEditForm;
      const split = scheduledSplitFromTimes(
        f.checkIn,
        f.checkOut,
        f.hours.break || 0,
        Boolean(this.attendanceEditRecord?.isHoliday),
      );
      if (!split) {
        return;
      }
      Object.assign(f.hours, split);
    },

    /** 수정 내역 히스토리 (최신순) — attendance.metadata.editHistory */
    attendanceEditHistory(): Record<string, unknown>[] {
      // biome-ignore lint/suspicious/noExplicitAny: jsonb metadata
      const meta = this.attendanceEditRecord?.metadata as any;
      const list = (meta?.editHistory || []) as Record<string, unknown>[];
      return [...list].reverse();
    },

    /** 수정 일시 — YYYY.MM.DD. HH:MM (24시간) */
    attnEditHistoryWhen(iso: unknown): string {
      const s = String(iso || "");
      if (!s) {
        return "-";
      }
      // formatDateTime는 org 타임존 기준. 24시간 표기를 위해 store 사용.
      // biome-ignore lint/suspicious/noExplicitAny: Alpine store access in component method
      return (this as any).$store.hereby.formatDateTime(s);
    },

    /** 변경 내용 diff — 출퇴근 + 8개 구분 + 휴게 중 변경된 항목만 */
    attnEditChanges(
      h: Record<string, unknown>,
    ): { label: string; before: string; after: string }[] {
      return attnEditChanges(h, (v) => this.formatTimeOnly(v as string));
    },

    overtimeCategoryLabel(c: unknown): string {
      const map: Record<string, string> = {
        weekday: "평일 연장",
        saturday: "주말",
        holiday: "휴일",
        night: "야간",
      };
      return map[String(c || "")] || String(c || "-");
    },

    overtimeStatusLabel(s: unknown): string {
      const map: Record<string, string> = {
        pending: "대기",
        approved: "승인",
        rejected: "반려",
        cancelled: "취소",
        report_pending: "보고 대기",
        report_approved: "보고 승인",
        report_rejected: "보고 반려",
      };
      return map[String(s || "")] || String(s || "-");
    },

    /** 연장근무 결재 화면으로 이동 (결재 없음 경고의 링크) */
    goToOvertimeApproval() {
      this.showAttendanceEditModal = false;
      this.showAttendanceModal = false;
      this.handleLnbSelect("embed:overtime-approval");
    },

    async saveAttendanceEdit() {
      const r = this.attendanceEditRecord;
      if (!r?.id) {
        return;
      }
      if (!this.attnEditCanSave()) {
        return;
      }
      // 합계가 출퇴근 시간과 맞지 않으면 저장 전 확인 팝업([취소]/[저장]). 근태 수정 모달(z-60) 위에 표시.
      if (this.attnEditMismatch()) {
        this.confirmTitle = "합계가 출퇴근 시간과 맞지 않아요.";
        this.confirmMessage = "이대로 저장할까요?";
        this.confirmLabel = this._t("common.save");
        this.confirmTop = true;
        this.confirmAction = async () => {
          await this._doSaveAttendanceEdit();
        };
        this.showConfirm = true;
        return;
      }
      await this._doSaveAttendanceEdit();
    },

    /** 실제 근태 저장 — 불일치 확인 팝업을 통과했거나 불일치가 없을 때 호출. */
    async _doSaveAttendanceEdit() {
      const r = this.attendanceEditRecord;
      if (!r?.id) {
        return;
      }
      const f = this.attendanceEditForm;
      this.attendanceEditSaving = true;
      try {
        await this.sdk.attendance.saveManualHours({
          id: r.id as string,
          checkInTime: f.checkIn || null,
          checkOutTime: f.checkOut || null,
          hours: {
            regular: f.hours.regular || 0,
            overtime: f.hours.overtime || 0,
            night: f.hours.night || 0,
            nightOvertime: f.hours.nightOvertime || 0,
            holidayRegular: f.hours.holidayRegular || 0,
            holidayOvertime: f.hours.holidayOvertime || 0,
            holidayNight: f.hours.holidayNight || 0,
            holidayNightOvertime: f.hours.holidayNightOvertime || 0,
            break: f.hours.break || 0,
          },
          reason: f.reason.trim(),
        });
        await this.loadEmployeeAttendance();
        // 저장 후 갱신된 레코드로 교체 → 수정 내역·재계산 즉시 반영
        const updated = this.attendanceRecords.find((x: Record<string, unknown>) => x.id === r.id);
        if (updated) {
          this.attendanceEditRecord = updated;
        }
        this.showAttendanceEditModal = false;
      } catch (err) {
        showError(
          `${this._t("admin.attendance_update_failed")}: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
      this.attendanceEditSaving = false;
    },

    async loadEmployeeAttendance(showSpin = false) {
      if (!this.attendanceEmployee) {
        return;
      }
      const empId = this.attendanceEmployee.id as string;
      // 새로고침 버튼으로 직접 호출할 때만 회전 모션을 표시한다.
      // (월 이동·초기 로드 등 내부 호출은 회전하지 않음)
      if (showSpin) {
        this.attendanceLoading = true;
      }
      try {
        const [att, sec, offsite] = await Promise.all([
          this.sdk.attendance.list({
            filter: {
              employeeId: empId,
              dateRange: {
                // FIXME(타입 복구 중 발견): attendance.list 입력은 Date를 기대하지만 관리자 필터는 yyyy-mm-dd 문자열임. SDK 입력 스키마 정렬 후 any 제거.
                // biome-ignore lint/suspicious/noExplicitAny: SDK input type mismatch during getter migration
                from: this.attendanceFilter.dateFrom as any,
                // FIXME(타입 복구 중 발견): attendance.list 입력은 Date를 기대하지만 관리자 필터는 yyyy-mm-dd 문자열임. SDK 입력 스키마 정렬 후 any 제거.
                // biome-ignore lint/suspicious/noExplicitAny: SDK input type mismatch during getter migration
                to: this.attendanceFilter.dateTo as any,
              },
            },
            pagination: { page: 1, limit: 200, sortBy: "date", sortOrder: "desc" },
          }),
          this.sdk.secomEvent.listEvents({
            employeeId: empId,
            dateFrom: this.attendanceFilter.dateFrom,
            dateTo: this.attendanceFilter.dateTo,
            limit: 500,
          }),
          // 외근은 확인만 — 결재는 외근 관리에서 진행, 근태와 승인 절차를 연결하지 않는다
          this.sdk.offsiteWork
            .list(
              {
                employeeId: empId,
                dateRange: {
                  // FIXME(타입 복구 중 발견): offsiteWork.list 입력은 Date를 기대하지만 관리자 필터는 yyyy-mm-dd 문자열임. SDK 입력 스키마 정렬 후 any 제거.
                  // biome-ignore lint/suspicious/noExplicitAny: SDK input type mismatch during getter migration
                  from: this.attendanceFilter.dateFrom as any,
                  // FIXME(타입 복구 중 발견): offsiteWork.list 입력은 Date를 기대하지만 관리자 필터는 yyyy-mm-dd 문자열임. SDK 입력 스키마 정렬 후 any 제거.
                  // biome-ignore lint/suspicious/noExplicitAny: SDK input type mismatch during getter migration
                  to: this.attendanceFilter.dateTo as any,
                },
              },
              { page: 1, limit: 100, sortBy: "workDate", sortOrder: "asc" },
            )
            .catch(() => ({ items: [] })),
        ]);
        // FIXME(타입 복구 중 발견): attendance/secom/offsite 조회 입력은 Date를 기대하지만 관리자 필터는 yyyy-mm-dd 문자열이고, 응답 row 타입은 UI Record 배열과 맞지 않음. DTO 정렬 후 any 제거.
        this.attendanceRecords = (att?.items || []) as unknown as Record<string, unknown>[];
        this.secomEvents = (sec?.items || []) as unknown as Record<string, unknown>[];

        // 승인 이후 상태의 외근만 날짜별로 매핑 (대기/반려/취소 건은 근태에 노출하지 않음)
        this.attendanceOffsiteByDate = offsiteConfirmedByDate(
          (offsite?.items || []) as Record<string, unknown>[],
        );
        if (this.attendanceView === "log") {
          await this.loadAttendanceWorkLog();
        }
      } catch (err) {
        showError(
          `${this._t("admin.attendance_query_failed")}: ${err instanceof Error ? err.message : String(err)}`,
        );
      } finally {
        if (showSpin) {
          this.attendanceLoading = false;
        }
      }
    },

    /** 근태 관리 모달 보기 전환 — 전체 근무기록은 처음 들어갈 때(또는 월이 바뀐 뒤) 불러온다. */
    async switchAttendanceView(view: "table" | "log") {
      this.attendanceView = view;
      if (view === "log" && !this.attendanceWorkLog) {
        await this.loadAttendanceWorkLog();
      }
    },

    /** 전체 근무기록 조회 — 근태표와 같은 월 범위(attendanceFilter)로 attendance.workLog 를 부른다. */
    async loadAttendanceWorkLog() {
      const empId = this.attendanceEmployee?.id as string | undefined;
      if (!(empId && this.attendanceFilter.dateFrom && this.attendanceFilter.dateTo)) {
        return;
      }
      this.attendanceWorkLogLoading = true;
      try {
        this.attendanceWorkLog = await this.sdk.attendance.workLog({
          employeeId: empId,
          from: this.attendanceFilter.dateFrom,
          to: this.attendanceFilter.dateTo,
        });
      } catch (err) {
        this.attendanceWorkLog = null;
        showError(`전체 근무기록 조회 실패: ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        this.attendanceWorkLogLoading = false;
      }
    },

    /** 타임라인 눈금(00:00 ~ 익일 06:00, 6시간 간격) — 공용 헬퍼 그대로. */
    get workLogTicks(): { label: string; leftPct: number }[] {
      return workLogAxisTicks();
    },

    workLogBarStyle(startMin: number | null, endMin: number | null): string {
      return workLogBarStyle(startMin, endMin);
    },

    /** 세그먼트 막대 색 — 출퇴근(소정)=orange · 연장=purple · 외근=blue, 합산 안 되는 건은 흐리게. */
    workLogSegmentClass(seg: WorkLogSegmentView): string {
      const color =
        seg.kind === "clock"
          ? "bg-orange-400"
          : seg.kind === "overtime"
            ? "bg-purple-400"
            : "bg-blue-400";
      return seg.counted ? color : `${color} opacity-40`;
    },

    workLogKindLabel(kind: string): string {
      return workLogKindLabel(kind);
    },

    workLogRangeLabel(seg: WorkLogSegmentView): string {
      return workLogRangeLabel(seg.startMin, seg.endMin);
    },

    /** 세그먼트 한 줄 부가 표기 — 결재 단계(연장·외근) 또는 출퇴근 플래그(조기 퇴근·지각·직접 수정). */
    workLogSegmentNote(seg: WorkLogSegmentView): string {
      if (seg.kind !== "clock") {
        return workLogStatusLabel(seg.kind, seg.status);
      }
      const flags: string[] = [];
      if (seg.isEarlyLeave) {
        flags.push("조기 퇴근");
      }
      if (seg.isLate) {
        flags.push("지각");
      }
      if (seg.manualOverride) {
        flags.push("직접 수정");
      }
      return flags.join(" · ");
    },

    /** 막대 hover 툴팁(여러 줄) — 종류·시간·합산 시간·상태. */
    workLogSegmentTip(seg: WorkLogSegmentView): string {
      const lines = [
        `${workLogKindLabel(seg.kind)} ${workLogRangeLabel(seg.startMin, seg.endMin)}`,
      ];
      const hours = `${seg.counted ? "합산" : "참고"} ${this.fmtHM(seg.hours)}`;
      lines.push(
        seg.kind === "clock" && seg.breakHours
          ? `${hours} · 휴게 ${this.fmtHM(seg.breakHours)}`
          : hours,
      );
      if (seg.detail) {
        lines.push(seg.detail);
      }
      const note = this.workLogSegmentNote(seg);
      if (note) {
        lines.push(seg.counted ? note : `${note} (합산 안 함)`);
      }
      return lines.join("\n");
    },

    /** 소정 근무 창 라벨 — 근무 열에 「09:00~18:00」. */
    workLogShiftLabel(day: WorkLogDayView): string {
      if (!day.shift || day.shift.scheduledStartMin === null) {
        return "";
      }
      return `${formatWorkLogMinutes(day.shift.scheduledStartMin)}~${formatWorkLogMinutes(day.shift.scheduledEndMin)}`;
    },

    /** 해당 근태 행 날짜의 확정 외근 기록 (없으면 null) */
    attendanceOffsiteFor(r: Record<string, unknown>): Record<string, unknown> | null {
      const key = String(r.date || "").slice(0, 10);
      return this.attendanceOffsiteByDate[key] || null;
    },

    /** 외근 칩 호버 툴팁 — 시간·장소·유형·진행 단계 */
    attendanceOffsiteTip(r: Record<string, unknown>): string {
      const ow = this.attendanceOffsiteFor(r);
      if (!ow) {
        return "";
      }
      const stageLabels: Record<string, string> = {
        approved: "승인 (보고 전)",
        report_pending: "결과 보고 대기",
        report_approved: "보고 완료",
      };
      const report = ow.report as Record<string, unknown> | null;
      const hours = Number(report?.actualHours ?? ow.workHours ?? 0);
      const lines = [
        `외근 ${hours.toFixed(1)}시간`,
        `장소: ${ow.location || "-"}`,
        ow.typeName ? `유형: ${ow.typeName}` : "",
        `단계: ${stageLabels[String(ow.status)] || String(ow.status)}`,
      ];
      return lines.filter(Boolean).join("\n");
    },

    formatTimeOnly(val: string | Date | undefined | null): string {
      if (!val) {
        return "-";
      }
      const d = new Date(val as string);
      if (Number.isNaN(d.getTime())) {
        return "-";
      }
      return d.toLocaleTimeString("ko-KR", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: "Asia/Seoul",
      });
    },

    /** "HH:MM" (24h, org-local) for an <input type="time"> value, or "" when empty. */
    timeInputValue(val: string | Date | undefined | null): string {
      if (!val) {
        return "";
      }
      const d = new Date(val as string);
      if (Number.isNaN(d.getTime())) {
        return "";
      }
      return d.toLocaleTimeString("ko-KR", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: "Asia/Seoul",
      });
    },

    /** DS 라벨 색 모디파이어(ds-label--*)를 반환. 근무 시간 설정표의 유형 배지와
     *  동일한 색 매핑(야간→blue, 오후→aqua, 주간→yellow, 그 외→gray)이라
     *  직원관리 근무형태·근태 모달 배지가 같은 ds-label 모양으로 통일된다. */
    shiftBadgeClass(shiftName: string | undefined): string {
      if (!shiftName) {
        return "ds-label--gray";
      }
      const name = shiftName.toLowerCase();
      // Match by Korean shift name keywords
      if (name.includes("야간") || name.includes("night") || name.includes("n")) {
        return "ds-label--blue";
      }
      if (name.includes("오후") || name.includes("evening") || name.includes("e")) {
        return "ds-label--aqua";
      }
      if (
        name.includes("주간") ||
        name.includes("day") ||
        name.includes("d") ||
        name.includes("일반")
      ) {
        return "ds-label--yellow";
      }
      return "ds-label--gray";
    },

    /** Resolve a shiftId to its display name from the loaded `shifts` list.
     *  Employee list rows carry only shiftId (not the name), so the roster
     *  table maps it here. Returns "" when unassigned or shifts aren't loaded. */
    shiftNameById(id: string | undefined | null): string {
      if (!id) {
        return "";
      }
      const match = this.shifts.find((s) => s.id === id);
      return match?.name || "";
    },
    /**
     * 직원 목록 근무형태 칸 — 개별 배정과 직무(근로형태) 기본 근무를 함께 판정한다(2026-09-22 결함: 직무가 있는데 근무가 비어 보였다).
     *  - assigned: 개별 배정 = 기본과 같거나 기본이 없음 → 배정 이름
     *  - mismatch: 개별 배정이 기본과 다름 → 배정 이름 + 「직무 기본과 다름」 표시
     *  - default: 배정이 비어 있거나 그룹만인데 기본 근무가 있음 → 기본 근무 이름(근태는 이 값으로 판정된다) + 「기본」 표시
     *  - none: 아무것도 없음
     * title 의 첫 줄은 배지에 보이는 **전체 이름**이다 — 칸(160px)에서 말줄임되므로 툴팁이 전체를 보여 준다
     * (CS-OKBEST-260922-0004). 둘째 줄부터가 출처·차이 설명이고, 줄바꿈이 있어 멀티라인 툴팁으로 뜬다.
     */
    employeeShiftCell(emp: {
      shiftId?: string | null;
      shiftGroupId?: string | null;
      shiftAssignedVia?: string | null;
      defaultShift?: { shiftGroupId: string | null; shiftId: string | null; source: string } | null;
    }): { kind: "assigned" | "mismatch" | "default" | "none"; name: string; title: string } {
      const cell = this.employeeShiftCellDetail(emp);
      return {
        ...cell,
        title: cell.name ? (cell.title ? `${cell.name}\n${cell.title}` : cell.name) : cell.title,
      };
    },
    /** employeeShiftCell 의 판정 본체 — title 은 이름 없이 출처·차이 설명만(이름 줄은 employeeShiftCell 이 붙인다). */
    employeeShiftCellDetail(emp: {
      shiftId?: string | null;
      shiftGroupId?: string | null;
      shiftAssignedVia?: string | null;
      defaultShift?: { shiftGroupId: string | null; shiftId: string | null; source: string } | null;
    }): { kind: "assigned" | "mismatch" | "default" | "none"; name: string; title: string } {
      const own = this.shiftNameById(emp.shiftId);
      const ownGroup = this.shiftGroupNameById(emp.shiftGroupId);
      const d = emp.defaultShift ?? null;
      const dName = d
        ? d.shiftId
          ? this.shiftNameById(d.shiftId)
          : `${this.shiftGroupNameById(d.shiftGroupId)} (그룹만)`
        : "";
      const dSource =
        d?.source === "work_pattern_default" ? "근로형태 기본 근무" : "직무 기본 근무";
      const via = this.shiftAssignedViaLabel(emp.shiftAssignedVia);
      if (own) {
        if (
          d &&
          (d.shiftId !== (emp.shiftId ?? null) || d.shiftGroupId !== (emp.shiftGroupId ?? null))
        ) {
          return {
            kind: "mismatch",
            name: own,
            title: `${dSource} 「${dName}」과 달라요 — 근태는 직원 근무 배정 「${own}」으로 판정돼요${via ? ` (${via})` : ""}`,
          };
        }
        return { kind: "assigned", name: own, title: via ? `배정 출처: ${via}` : "" };
      }
      if (ownGroup) {
        // 그룹만 배정 — 판정 순서상 기본 근무보다 **그룹의 요일별 형태 자동 선택**이 먼저다(MED-923: 예전 툴팁은
        // 「기본 근무로 판정돼요」라고 잘못 말했다). 기본 근무의 그룹과 다르면 「≠ 직무 기본」.
        const judged = `근무 그룹 「${ownGroup}」만 배정 — 근태는 그 그룹의 요일별 형태로 판정돼요(근무 그룹 자동 선택)`;
        const name = `${ownGroup} (그룹만)`;
        if (d && d.shiftGroupId !== (emp.shiftGroupId ?? null)) {
          return {
            kind: "mismatch",
            name,
            title: `${dSource} 「${dName}」과 달라요 — ${judged}${via ? ` (${via})` : ""}`,
          };
        }
        return {
          kind: "assigned",
          name,
          title: d
            ? `${judged}. ${dSource} 「${dName}」으로 채우려면 「기본 근무 재적용」을 쓰세요.`
            : via
              ? `배정 출처: ${via}`
              : "",
        };
      }
      if (d) {
        return {
          kind: "default",
          name: dName,
          title: `근무 미배정 — 근태는 ${dSource} 「${dName}」으로 판정돼요. 「기본 근무 재적용」으로 채울 수 있어요.`,
        };
      }
      return { kind: "none", name: "", title: "" };
    },

    attendanceStatusLabel(status: string): string {
      const keyMap: Record<string, string> = {
        SCHEDULED: "admin.att_status_scheduled",
        CHECKED_IN: "admin.att_status_checked_in",
        CHECKED_OUT: "admin.att_status_checked_out",
        ABSENT: "admin.att_status_absent",
        LATE: "admin.att_status_late",
        EARLY_LEAVE: "admin.att_status_early_leave",
        HOLIDAY: "admin.att_status_holiday",
        LEAVE: "admin.att_status_leave",
        SICK_LEAVE: "admin.att_status_sick_leave",
      };
      return keyMap[status] ? this._t(keyMap[status]) : status;
    },

    secomEventTypeLabel(type: string): string {
      const keyMap: Record<string, string> = {
        check_in: "admin.secom_check_in",
        check_out: "admin.secom_check_out",
        unknown: "admin.secom_unknown",
      };
      return keyMap[type] ? this._t(keyMap[type]) : type;
    },

    secomStatusLabel(status: string): string {
      const keyMap: Record<string, string> = {
        pending: "admin.secom_pending",
        processed: "admin.secom_processed",
        error: "admin.secom_error",
        ignored: "admin.secom_ignored",
      };
      return keyMap[status] ? this._t(keyMap[status]) : status;
    },

    toggleSelectEmployee(id: string) {
      const idx = this.selectedEmployees.indexOf(id);
      if (idx >= 0) {
        this.selectedEmployees.splice(idx, 1);
      } else {
        this.selectedEmployees.push(id);
      }
    },

    toggleSelectAllEmployees() {
      if (this.selectAllEmployees) {
        this.selectedEmployees = this.employees.map((e: Record<string, unknown>) => e.id as string);
      } else {
        this.selectedEmployees = [];
      }
    },

    /**
     * 일괄 변경 「적용」 — 상태·팀·역할을 확인 모달 하나로 묶어 순서대로 실행한다.
     *
     * 예전에는 버튼이 bulkUpdateStatus()/bulkAssignTeam()/bulkAssignRole() 세
     * 함수를 연달아 호출했는데, 셋 다 같은 슬롯(confirmMessage/confirmAction/
     * showConfirm)에 확인 모달을 세팅하기 때문에 뒤 호출이 앞 호출을 덮어썼다.
     * → 드롭다운을 2개 이상 고르면 마지막 하나만 반영되고 나머지는 성공 토스트와
     * 함께 조용히 누락됐다. 그래서 진입점을 이 함수 하나로 합쳤다.
     *
     * 실행 순서(상태 → 팀 → 역할)는 의미가 있다: 역할 일괄 부여는 "활성 팀
     * 멤버십"에 적용되므로, 새로 배정한 팀 멤버십이 먼저 만들어져 있어야 그
     * 멤버십에도 역할이 붙는다.
     */
    async applyBulkChanges() {
      const ids = [...this.selectedEmployees];
      const status = this.bulkStatus;
      const teamId = this.bulkTeamId;
      const roleId = this.bulkRoleId;
      const shiftGroupId = this.bulkShiftGroupId;
      const shiftId = this.bulkShiftId;
      const hasShiftChange = Boolean(shiftGroupId || shiftId);
      if (ids.length === 0 || !(status || teamId || roleId || hasShiftChange)) {
        return;
      }

      const team = teamId
        ? this.teams.find((t: Record<string, unknown>) => t.id === teamId)
        : undefined;
      const teamName = ((team as Record<string, unknown>)?.name as string) || "";
      const role = roleId ? this.roles.find((r) => r.id === roleId) : undefined;
      const roleName = (role?.name as string) || "";

      // 확인 모달 — 고른 변경을 전부 나열한다(한 건만 골라도 같은 형식).
      const statusLabel = this._t("common.status");
      const teamLabel = this._t("team.team");
      const roleLabel = this._t("role.role");
      const lines: string[] = [];
      if (status) {
        lines.push(`· ${statusLabel} → ${this.statusLabel(status)}`);
      }
      if (teamId) {
        lines.push(`· ${teamLabel} → ${teamName}`);
      }
      if (roleId) {
        lines.push(`· ${roleLabel} → ${roleName}`);
        lines.push(this._t("admin.bulk_role_note"));
      }
      if (hasShiftChange) {
        lines.push(`· 근무 배정 → ${this.bulkShiftAssignLabel(shiftGroupId, shiftId)}`);
        lines.push("  (주 소속 줄의 근무를 바꿔요 — 근무 그룹을 하나 더 추가하는 게 아니에요)");
      }
      this.confirmMessage = this._t("admin.confirm_bulk_apply")
        .replace("{n}", String(ids.length))
        .replace("{changes}", lines.join("\n"));

      this.confirmAction = async () => {
        // 축별 실패 건수 — 예전 코드는 직원 단위 실패를 통째로 삼켰다.
        // 부분 실패를 조용히 넘기지 않도록 모아서 마지막에 알린다.
        const failures: string[] = [];

        if (status) {
          let failed = 0;
          for (const id of ids) {
            try {
              await this.sdk.employee.updateEmployee({ id, status });
            } catch {
              failed++;
            }
          }
          if (failed > 0) {
            failures.push(`${statusLabel} ${failed}건`);
          }
        }

        const schedulerNotes: (WardSchedulerNote | undefined)[] = [];
        if (teamId) {
          let failed = 0;
          for (const id of ids) {
            try {
              const joined = await this.sdk.team.addMember({ teamId, employeeId: id });
              schedulerNotes.push(joined.wardAssignment.scheduler);
            } catch {
              failed++;
            }
          }
          if (failed > 0) {
            failures.push(`${teamLabel} ${failed}건`);
          }
          delete this.teamMembers[teamId];
        }

        if (roleId) {
          try {
            await this.sdk.employee.bulkAssignRole(ids, roleId);
          } catch (err) {
            showError(
              `${this._t("admin.permission_change_failed")}: ${err instanceof Error ? err.message : String(err)}`,
            );
            failures.push(`${roleLabel} ${ids.length}건`);
          }
        }

        if (hasShiftChange) {
          try {
            await this.sdk.employee.bulkAssignShift(ids, shiftGroupId || null, shiftId || null);
          } catch (err) {
            showError(`근무 배정 변경 실패: ${err instanceof Error ? err.message : String(err)}`);
            failures.push(`근무 배정 ${ids.length}건`);
          }
        }

        this.selectedEmployees = [];
        this.selectAllEmployees = false;
        this.bulkStatus = "";
        this.bulkTeamId = "";
        this.bulkRoleId = "";
        this.bulkShiftGroupId = "";
        this.bulkShiftId = "";

        const reloads: Promise<unknown>[] = [this.loadEmployees()];
        if (teamId) {
          reloads.push(this.loadTeams());
        }
        if (hasShiftChange) {
          // 근무 설정의 「직원 N명」 카운트도 같이 갱신
          reloads.push(this.loadShiftGroups());
        }
        await Promise.all(reloads);
        if (teamId) {
          this.loadUnassignedMembers();
        }
        this.computeStats();

        const applied = [
          status ? statusLabel : "",
          teamId ? teamLabel : "",
          roleId ? roleLabel : "",
        ].filter(Boolean);
        this.notify(`${ids.length}명에 ${applied.join("·")} 변경을 적용했습니다`);
        this.notifyWardScheduler(schedulerNotes);
        if (failures.length > 0) {
          showError(`일부 변경에 실패했습니다: ${failures.join(", ")}`);
        }
      };
      this.showConfirm = true;
    },

    /* ──────────── team actions ──────────── */
    /**
     * 팀장 후보 — 조직 전체 직원(퇴사 제외). 팀장 지정은 소속과 독립이다(MED-945): 하위 팀 소속이든 다른 팀
     * 소속이든 어느 팀의 팀장도 될 수 있다. 명단은 팀 관리의 로스터(allMembers, 전 직원)에서 뽑고, 로스터가
     * 아직 없으면 직원 목록(현재 페이지)으로 대신한다.
     */
    leaderCandidates(query: string): { id: string; name: string; number: string }[] {
      const q = (query || "").trim().toLowerCase();
      const seen = new Set<string>();
      const out: { id: string; name: string; number: string }[] = [];
      const roster = this.allMembers as Record<string, unknown>[];
      const source: { id: string; name: string; number: string; status: string }[] =
        roster.length > 0
          ? roster.map((m) => ({
              id: String(m.employeeId || m.id || ""),
              name: String(m.employeeName || ""),
              number: String(m.employeeNumber || ""),
              status: String(m.status || ""),
            }))
          : (this.employees as Record<string, unknown>[]).map((e) => ({
              id: String(e.id || ""),
              name: String(e.fullName || ""),
              number: String(e.employeeNumber || ""),
              status: String(e.status || ""),
            }));
      for (const c of source) {
        if (!c.id || seen.has(c.id) || c.status === "TERMINATED") {
          continue;
        }
        if (q && !(c.name.toLowerCase().includes(q) || c.number.toLowerCase().includes(q))) {
          continue;
        }
        seen.add(c.id);
        out.push({ id: c.id, name: c.name, number: c.number });
      }
      return out;
    },

    /** 팀장 추가 — 이미 있는 사람은 무시. 첫 사람이 대표(teams.leaderId)다. */
    addTeamLeader(id: string) {
      if (id && !this.teamForm.leaderIds.includes(id)) {
        this.teamForm.leaderIds = [...this.teamForm.leaderIds, id];
      }
    },

    removeTeamLeader(id: string) {
      this.teamForm.leaderIds = this.teamForm.leaderIds.filter((x: string) => x !== id);
    },

    /** 팀장 이름 나열 — 팀 관리 헤더(「팀장 김철수, 이영희」) */
    teamLeaderNames(team: Record<string, unknown> | null | undefined): string[] {
      if (!team) {
        return [];
      }
      const ids = Array.isArray(team.leaderIds)
        ? (team.leaderIds as string[])
        : team.leaderId
          ? [team.leaderId as string]
          : [];
      return ids.map((id) => this.orgEmployeeName(id)).filter(Boolean);
    },

    /** 이 직원이 팀의 팀장(대표·공동) 인가 */
    isTeamLeaderOf(team: Record<string, unknown> | null | undefined, employeeId: string): boolean {
      if (!(team && employeeId)) {
        return false;
      }
      const ids = Array.isArray(team.leaderIds) ? (team.leaderIds as string[]) : [];
      return ids.includes(employeeId) || team.leaderId === employeeId;
    },

    /** 팀장 선택칸 표시 — 「이름 (사번)」 */
    leaderCandidateLabel(id: string): string {
      const c = this.leaderCandidates("").find((x) => x.id === id);
      if (c) {
        return c.number ? `${c.name} (${c.number})` : c.name;
      }
      const name = this.orgEmployeeName(id);
      return name || "";
    },

    /* ──────────── 총괄 팀장(조직 전체, MED-946) ──────────── */
    async loadOrgLeaders() {
      try {
        const res = await this.sdk.team.orgLeaders();
        this.orgLeaderIds = Array.isArray(res?.leaderIds) ? [...res.leaderIds] : [];
      } catch {
        /* 권한이 없거나 실패하면 이전 값 유지 */
      }
    },

    /** 워크스페이스 헤더 「총괄 팀장 A, B」 */
    orgLeaderNames(): string[] {
      return this.orgLeaderIds.map((id: string) => this.orgEmployeeName(id)).filter(Boolean);
    },

    /** 이 직원이 총괄 팀장인가 — 명단 배지 */
    isOrgLeaderOf(employeeId: string): boolean {
      return !!employeeId && this.orgLeaderIds.includes(employeeId);
    },

    /** 모달을 열기 전에 서버 값을 다시 읽는다 — 탭 진입 직후(목록이 아직 안 온 때)나 다른 관리자가 바꾼 뒤 저장이 덮어쓰지 않게 */
    async openOrgLeaders() {
      await this.loadOrgLeaders();
      this.orgLeaderForm = { leaderIds: [...this.orgLeaderIds] };
      this.showOrgLeaders = true;
      this.closeGuardBaselines.showOrgLeaders = this.closeGuardState("showOrgLeaders");
    },

    addOrgLeader(id: string) {
      if (id && !this.orgLeaderForm.leaderIds.includes(id)) {
        this.orgLeaderForm.leaderIds = [...this.orgLeaderForm.leaderIds, id];
      }
    },

    removeOrgLeader(id: string) {
      this.orgLeaderForm.leaderIds = this.orgLeaderForm.leaderIds.filter((x: string) => x !== id);
    },

    async saveOrgLeaders() {
      if (this.orgLeaderSaving) {
        return;
      }
      this.orgLeaderSaving = true;
      try {
        const res = await this.sdk.team.setOrgLeaders([...this.orgLeaderForm.leaderIds]);
        this.orgLeaderIds = Array.isArray(res?.leaderIds) ? [...res.leaderIds] : [];
        this.showOrgLeaders = false;
        this.notify("총괄 팀장을 저장했습니다");
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        this.notify(msg || "총괄 팀장을 저장하지 못했습니다", "error");
      } finally {
        this.orgLeaderSaving = false;
      }
    },

    openCreateTeam(prefillParentTeamId?: string) {
      this.editingTeam = null;
      this.editTeamMembers = [];
      // If no explicit parent provided, infer from the org tree selection:
      //   - team selected → make it the parent team (creating a sub-team)
      let parentTeamId = prefillParentTeamId || "";
      if (!parentTeamId) {
        const sel = this.orgSelected as { type?: string; id?: string } | undefined;
        if (sel?.type === "team" && sel.id) {
          // 선택된 팀을 부모로 삼아 하위 팀을 만든다.
          // 깊이/하위 개수 제한 없음 — 팀을 원하는 만큼 중첩·확장할 수 있다.
          parentTeamId = sel.id;
        }
      }
      this.teamForm = {
        name: "",
        description: "",
        leaderIds: [],
        isActive: true,
        parentTeamId,
        wardId: "",
        shiftGroupId: "",
        shiftId: "",
        allowedShiftIds: [],
      };
      this.teamFormError = { name: "", general: "" };
      this.showEditTeam = true;
      this.ensureWardOptions();
    },

    /** 팀 추가/수정 팝업의 연결 병동 선택지 — 병동 목록이 없으면 조용히 불러온다.
     *  (loadWards 는 실패 시 오류 팝업을 띄워서, 스케줄러 권한이 없는 사용자에게 팀 팝업을 열 때마다 뜬다.)
     *  force — 이미 있어도 다시 읽는다. 다른 탭 · 앱에서 만든 병동이 팀 관리 트리의 「미연결 병동」과
     *  팀 관리 팝업 병동 선택지에 바로 보이게(한 번 읽은 목록을 계속 쓰면 새 병동이 「(삭제된 병동)」으로 보였다). */
    async ensureWardOptions(force = false) {
      if (!force && this.wardList.length > 0) {
        return;
      }
      try {
        // 팀 모달의 선택지를 채우는 배경 조회 — 권한이 없으면 팝업 없이 「연결 안 함」만 남긴다
        const result = await this.sdk.nurseScheduler.listWards(undefined, { background: true });
        const items = Array.isArray(result) ? result : result?.items || [];
        // loadWards 와 같은 순서로 — 병동 관리 왼쪽 목록이 팀 목록 재조회 때마다 뒤바뀌지 않게
        this.wardList = this.sortWardList(items);
        // 병동 배정 줄의 스케줄러 선택지도 함께 — 실패해도 조용히(권한 없는 사용자에게 팝업이 뜨지 않게)
        await this.loadScheduleGroups();
      } catch {
        // 병동 목록을 못 읽으면 「연결 안 함」만 보인다
      }
    },

    /** 연결 병동 선택지 — 운영 중인 병동(표시 순서 · 코드 순). 지금 연결된 병동은 비활성이어도 남긴다. */
    teamWardOptions(): Record<string, unknown>[] {
      const current = this.teamForm.wardId;
      return (this.wardList as Record<string, unknown>[])
        .filter((w) => w.isActive !== false || w.id === current)
        .slice()
        .sort(
          (a, b) =>
            ((a.displayOrder as number) || 0) - ((b.displayOrder as number) || 0) ||
            String(a.code || "").localeCompare(String(b.code || ""), "ko", { numeric: true }),
        );
    },

    /** 팀 대표 근무의 그룹이 바뀌면 다른 그룹의 형태가 남지 않게 형태를 비운다(일괄 배정 2단계와 같은 규칙). */
    onTeamFormShiftGroupChange() {
      const cur = (this.shifts as ShiftItem[]).find((x) => x.id === this.teamForm.shiftId);
      if (!this.teamForm.shiftGroupId || (cur && cur.groupId !== this.teamForm.shiftGroupId)) {
        this.teamForm.shiftId = "";
      }
    },
    /**
     * 팀 「대표 근무」 그룹 선택지 — 활성 그룹만. 이미 지정된 값이 비활성이 됐으면 그 값도 남겨
     * select 가 빈칸으로 보이며 저장 시 조용히 해제되는 일을 막는다. 정렬은 근무 설정 화면과 같은 축.
     */
    teamShiftGroupOptions(): ShiftGroupItem[] {
      const current = this.teamForm.shiftGroupId;
      return (this.shiftGroups as ShiftGroupItem[])
        .filter((g) => g.isActive !== false || g.id === current)
        .slice()
        .sort(
          (a, b) =>
            ((a.sortOrder as number) || 0) - ((b.sortOrder as number) || 0) ||
            String(a.name || "").localeCompare(String(b.name || ""), "ko"),
        );
    },

    /** 이 병동이 (지금 편집 중인 팀이 아닌) 다른 팀에 연결돼 있으면 그 팀 이름 — 병동 하나에 팀 하나 */
    wardLinkedTeamName(wardId: string): string {
      const selfId = (this.editingTeam as Record<string, unknown> | null)?.id;
      // 필터 없는 전체 팀 기준 — 팀 관리 검색·상태 필터에 가려진 팀의 연결도 알려야 고른 뒤 서버에서 막히지 않는다
      const linked = this.allTeams.find((t) => t.wardId === wardId && t.id !== selfId);
      return linked?.name || "";
    },

    /**
     * 팀 폼 「연결 병동」 잠금 — 저장 뒤 이 팀이 부서(최상위 + 하위 팀 있음)면 병동을 새로 연결할 수 없다(서버와 같은 규칙).
     * 폼에서 고른 상위 팀으로 판단하고, 새 팀은 하위 팀이 없어 잠기지 않는다.
     */
    teamFormWardLocked(): boolean {
      const selfId = (this.editingTeam as Record<string, unknown> | null)?.id as string | undefined;
      if (!selfId) {
        return false;
      }
      const after = this.allTeams.map((t) =>
        t.id === selfId ? { ...t, parentTeamId: this.teamForm.parentTeamId || null } : t,
      );
      return isDepartmentTeam(after, selfId);
    },

    async openEditTeam(team: Record<string, unknown>) {
      this.editingTeam = team;
      this.editTeamMembers = [];
      const leaderIds = Array.isArray(team.leaderIds)
        ? (team.leaderIds as string[])
        : team.leaderId
          ? [team.leaderId as string]
          : [];
      const wardId = (team.wardId as string) || "";
      const shiftGroupId = (team.shiftGroupId as string) || "";
      const shiftId = (team.shiftId as string) || "";
      this.teamForm = {
        name: (team.name as string) || "",
        description: (team.description as string) || "",
        leaderIds: [...leaderIds],
        isActive: team.isActive !== false,
        parentTeamId: (team.parentTeamId as string) || "",
        // 선택지(병동 목록)가 그려진 뒤에 넣는다 — 아래 $nextTick
        wardId: "",
        // 근무 그룹·형태 선택지도 마찬가지 — 옵션이 그려진 뒤에 넣어야 select 가 값을 고른다
        shiftGroupId: "",
        shiftId: "",
        allowedShiftIds: Array.isArray(team.allowedShiftIds)
          ? [...(team.allowedShiftIds as string[])]
          : [],
      };
      this.teamFormError = { name: "", general: "" };
      this.showEditTeam = true;
      try {
        const result = await this.sdk.team.getComposition(team.id as string);
        const members = (result?.members || []) as Record<string, unknown>[];
        // 팀장 선택은 조직 전체 명단(leaderCandidates)에서 하므로 팀원 목록에 팀장을 끼워 넣을 필요가 없다(MED-945·946)
        this.editTeamMembers = members;
      } catch {
        this.editTeamMembers = [];
      }
      // 연결 병동 선택지(병동 목록)도 채운 뒤에 값을 넣어야 select 가 그 값을 고른다.
      await this.ensureWardOptions();
      // Set select values after options are populated so Alpine x-model picks the right option.
      Alpine.nextTick(() => {
        this.teamForm.wardId = wardId;
        this.teamForm.shiftGroupId = shiftGroupId;
        // 형태 옵션은 그룹 값이 들어간 뒤에 그려지므로 한 틱 더 미룬다
        Alpine.nextTick(() => {
          this.teamForm.shiftId = shiftId;
          this.closeGuardBaselines.showEditTeam = this.closeGuardState("showEditTeam");
        });
        // The late leaderId fill is part of the modal's initial state — refresh
        // the pristine-close baseline so an untouched form still closes directly.
        this.closeGuardBaselines.showEditTeam = this.closeGuardState("showEditTeam");
      });
    },

    /** 팀 헤더 「병동 설정」 — 팀 수정 팝업을 열고 「연결 병동」 칸으로 바로 옮긴다(편집 아이콘을 거치지 않게). */
    async openTeamWardSetting(team: Record<string, unknown>) {
      await this.openEditTeam(team);
      // openEditTeam 이 $nextTick 에서 값을 채운 뒤에 옮겨야 포커스가 초기값 반영에 밀리지 않는다
      Alpine.nextTick(() => {
        const select = document.getElementById("team-form-ward") as HTMLSelectElement | null;
        select?.scrollIntoView({ block: "center" });
        select?.focus();
      });
    },

    /** 이 팀이 부서(최상위 + 하위 팀 있음)인지 — 부서에는 병동을 연결할 수 없어 헤더 「병동 설정」를 숨긴다(서버와 같은 규칙). */
    orgTeamIsDepartment(teamId: string): boolean {
      return isDepartmentTeam(this.allTeams, teamId);
    },

    /** 팀 추가/수정 모달의 필드별 인라인 에러. 빈 문자열이면 에러 없음 상태. */
    teamFormError: { name: "", general: "" } as { name: string; general: string },

    async saveTeam() {
      // Reset previous errors
      this.teamFormError = { name: "", general: "" };
      if (!this.teamForm.name) {
        return;
      }
      // 클라이언트측 사전 중복 검사 — 즉시 빨간 안내 문구. 서버 단일 진실 (API
      // 가 race-safe 한 LOWER(name) 검사) 가 최종이지만, 네트워크 절약 + 즉시
      // 피드백을 위해 한 번 더 확인한다.
      const dupTeam = (this.teams as unknown as Record<string, unknown>[]).find(
        (t) =>
          (t.name as string)?.toLowerCase() === this.teamForm.name.toLowerCase() &&
          (!this.editingTeam || t.id !== (this.editingTeam as Record<string, unknown>).id),
      );
      if (dupTeam) {
        this.teamFormError.name = "이미 있는 팀명이에요. 다른 이름을 사용해 주세요.";
        return;
      }
      try {
        if (this.editingTeam) {
          await this.sdk.team.update(this.editingTeam.id as string, {
            name: this.teamForm.name,
            description: this.teamForm.description || undefined,
            leaderIds: [...this.teamForm.leaderIds],
            status: this.teamForm.isActive ? "ACTIVE" : "INACTIVE",
            parentTeamId: this.teamForm.parentTeamId || null,
            wardId: this.teamForm.wardId || null,
            shiftGroupId: this.teamForm.shiftGroupId || null,
            shiftId: this.teamForm.shiftId || null,
            allowedShiftIds: [...this.teamForm.allowedShiftIds],
          });
        } else {
          await this.sdk.team.create({
            name: this.teamForm.name,
            type: "PERMANENT",
            status: this.teamForm.isActive ? "ACTIVE" : "INACTIVE",
            description: this.teamForm.description || undefined,
            leaderIds: [...this.teamForm.leaderIds],
            parentTeamId: this.teamForm.parentTeamId || undefined,
            wardId: this.teamForm.wardId || undefined,
            shiftGroupId: this.teamForm.shiftGroupId || undefined,
            shiftId: this.teamForm.shiftId || undefined,
            allowedShiftIds: [...this.teamForm.allowedShiftIds],
          });
        }
        const wasEditing = !!this.editingTeam;
        this.showEditTeam = false;
        await this.loadTeams();
        this.notify(wasEditing ? "팀을 수정했습니다" : "팀을 추가했습니다");
      } catch (err) {
        // 서버 에러 메시지를 필드별로 분류 — 팀명 중복은 인라인, 나머지는 일반 모달 알림.
        const msg = err instanceof Error ? err.message : String(err);
        if (msg.includes("팀명") || msg.includes("이미 있는")) {
          this.teamFormError.name = "이미 있는 팀명이에요. 다른 이름을 사용해 주세요.";
        } else if (
          msg.includes("팀 깊이") ||
          msg.includes("팀은 최대") ||
          msg.includes("병동") ||
          // 상위 팀 · 팀장 검증 안내(서버 문구 그대로) — 하위 팀 아래로 이동, 없는 상위 팀, 팀장 자격
          msg.includes("상위 팀") ||
          msg.includes("하위 팀 아래로") ||
          msg.includes("팀장으로 지정") ||
          msg.includes("퇴사한 직원")
        ) {
          this.teamFormError.general = msg;
        } else {
          this.teamFormError.general = "알 수 없는 에러가 발생했어요. 잠시 후 다시 시도해 주세요.";
        }
      }
    },

    async deleteTeam(teamId: string) {
      const team = (this.teams as unknown as Record<string, unknown>[]).find(
        (t) => t.id === teamId,
      );
      const teamName = (team?.name as string) || "";
      // 1차 확인 — "{팀명}팀을 삭제하시겠어요?" (기획서 §1)
      this.confirmTitle = "팀 삭제";
      this.confirmMessage = `${teamName}팀을 삭제하시겠어요?`;
      this.confirmLabel = this._t("common.confirm");
      this.confirmAction = async () => {
        try {
          await this.sdk.team.delete(teamId);
          await this.loadTeams();
          this.notify("팀을 삭제했습니다");
        } catch (err) {
          // 서버가 "하위 팀 또는 팀원이 있어서…" 메시지를 던지면 차단 안내 모달로 재오픈.
          // executeConfirm 이 _resetConfirm 으로 현재 팝업을 닫으므로, 다음 틱에 재오픈한다.
          const msg = err instanceof Error ? err.message : String(err);
          const isBlocked = msg.includes("하위 팀") || msg.includes("팀원이 있어서");
          Alpine.nextTick(() => {
            this.confirmTitle = "팀 삭제 불가";
            this.confirmMessage = isBlocked
              ? "하위 팀 또는 팀원이 있어서 삭제할 수 없어요."
              : "알 수 없는 에러가 발생했어요. 잠시 후 다시 시도해 주세요.";
            this.confirmLabel = this._t("common.confirm");
            this.confirmHideCancel = true;
            this.confirmAction = null;
            this.showConfirm = true;
          });
        }
      };
      this.showConfirm = true;
    },

    // Nesting depth (top-level team = 1, each sub-team +1). Backend caps the
    // chain at 50 (MAX_TEAM_DEPTH); the guard below only breaks cycles, and is
    // set above the cap so depth stays accurate up to the limit (used to gate
    // the "팀 추가" button: a child is allowed only while depth < 50).
    teamDepth(teamId: string): number {
      const list = this.teams as Record<string, unknown>[];
      let depth = 1;
      let current = list.find((t) => t.id === teamId);
      while (current?.parentTeamId) {
        depth++;
        if (depth > 60) {
          break; // safety against cycles
        }
        const parentId = current.parentTeamId as string;
        current = list.find((t) => t.id === parentId);
      }
      return depth;
    },

    // Count direct sub-teams under a given parent.
    teamChildCount(parentId: string): number {
      return (this.teams as unknown as Record<string, unknown>[]).filter(
        (t) => t.parentTeamId === parentId,
      ).length;
    },

    /** 팀 계층 경로를 "간호부 > 1병동 > A팀 > 간호조무사" 형식으로 반환.
     *  [직원 관리] 소속·역할 열에서 leaf 팀명만 노출하던 것을 상위 팀부터 전체
     *  체인을 보여주도록 확장 (기획서: TO-BE). 팀 트리는 `this.teams` 로컬 캐시에서
     *  parentTeamId 로 거슬러 올라가며 조립한다. */
    teamBreadcrumb(teamId: string): string {
      if (!teamId) {
        return "";
      }
      const list = this.teams as Record<string, unknown>[];
      const byId = new Map<string, Record<string, unknown>>();
      for (const t of list) {
        byId.set(t.id as string, t);
      }
      const chain: string[] = [];
      let current = byId.get(teamId);
      let safety = 0;
      while (current && safety < 60) {
        chain.unshift((current.name as string) || "");
        const parentId = (current.parentTeamId as string) || "";
        if (!parentId) {
          break;
        }
        current = byId.get(parentId);
        safety++;
      }
      return chain.filter(Boolean).join(" > ");
    },

    /**
     * Left org-tree rows — flattened depth-first over the WHOLE team tree
     * (no depth cap; replaces the old hardcoded 3-level template nesting).
     * Each row carries `_depth` for indentation. When `search` is set,
     * matches plus their ancestors are kept so the tree stays connected.
     */
    /**
     * 팀 관리 트리의 형제 순서 — 관리자가 드래그로 정한 순서(sortOrder)가 먼저(MED-953), 지정이 없는 팀은
     * 예전 규칙대로 병동이 연결된 팀을 먼저(병동 코드 순: 1W → 2W → 3W), 나머지는 이름순.
     */
    compareOrgTeams(a: Record<string, unknown>, b: Record<string, unknown>): number {
      const sortKey = (t: Record<string, unknown>) =>
        t.wardId
          ? ((t.ward as Record<string, unknown> | null)?.code as string) || String(t.name || "")
          : String(t.name || "");
      return compareTeamOrder(
        a as { name?: string; sortOrder?: number | null },
        b as { name?: string; sortOrder?: number | null },
        () => {
          const aw = a.wardId ? 0 : 1;
          const bw = b.wardId ? 0 : 1;
          if (aw !== bw) {
            return aw - bw;
          }
          return sortKey(a).localeCompare(sortKey(b), "ko", { numeric: true });
        },
      );
    },

    /* ── 팀 순서 드래그(MED-953) — 같은 상위 팀 안의 형제 사이에서만 ── */
    teamDrag: { id: "", parentId: "", overId: "", position: "" as "" | "before" | "after" },

    /** 팀 순서를 끌어 바꿀 수 있는가 — 팀 수정 권한(team:update) 또는 시스템 관리자 */
    canReorderTeams(): boolean {
      const store = Alpine.store("hereby");
      return !!(store.currentUser?.isSystemAdmin || store.hasPermission("team:update"));
    },

    onTeamDragStart(event: DragEvent, team: { id: string; parentTeamId?: string | null }) {
      if (!this.canReorderTeams() || this.memberDrag.ids.length > 0) {
        event.preventDefault();
        return;
      }
      this.teamDrag = {
        id: team.id,
        parentId: (team.parentTeamId as string) || "",
        overId: "",
        position: "",
      };
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", `team:${team.id}`);
      }
    },

    onTeamDragEnd() {
      this.teamDrag = { id: "", parentId: "", overId: "", position: "" };
    },

    /** 트리 행 위로 끌고 있을 때 — 팀 드래그면 놓을 자리(위/아래)를, 직원 드래그면 기존 팀 이동 하이라이트를 */
    onOrgTreeDragOver(
      event: DragEvent,
      team: { id: string; parentTeamId?: string | null; isActive?: boolean },
    ) {
      if (!this.teamDrag.id) {
        this.onMemberDragOverTeam(event, team);
        return;
      }
      const sameParent = ((team.parentTeamId as string) || "") === this.teamDrag.parentId;
      if (team.id === this.teamDrag.id || !sameParent) {
        this.teamDrag.overId = "";
        this.teamDrag.position = "";
        if (event.dataTransfer) {
          event.dataTransfer.dropEffect = "none";
        }
        return;
      }
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = "move";
      }
      const el = event.currentTarget as HTMLElement | null;
      const rect = el?.getBoundingClientRect();
      const below = rect ? event.clientY - rect.top > rect.height / 2 : false;
      this.teamDrag.overId = team.id;
      this.teamDrag.position = below ? "after" : "before";
    },

    onOrgTreeDragLeave(team: { id: string }) {
      if (this.teamDrag.id) {
        if (this.teamDrag.overId === team.id) {
          this.teamDrag.overId = "";
          this.teamDrag.position = "";
        }
        return;
      }
      this.onMemberDragLeaveTeam(team);
    },

    async onOrgTreeDrop(team: { id: string; parentTeamId?: string | null; isActive?: boolean }) {
      if (!this.teamDrag.id) {
        this.onMemberDropOnTeam(team);
        return;
      }
      const { id: movedId, parentId, overId, position } = this.teamDrag;
      this.onTeamDragEnd();
      if (!overId || overId !== team.id || !position) {
        return;
      }
      await this.reorderSiblingTeams(parentId, movedId, overId, position);
    },

    /**
     * 형제 순서 저장 — 지금 트리 순서에서 끈 팀을 대상 앞/뒤로 옮긴 id 배열을 만들어 로컬에 먼저 반영(sortOrder 를
     * 0부터 다시 매김)하고 서버(team.reorder)에 보낸다. 실패하면 목록을 다시 읽어 서버 순서로 되돌린다.
     */
    async reorderSiblingTeams(
      parentId: string,
      movedId: string,
      targetId: string,
      position: "before" | "after",
    ) {
      const list = this.teams as Record<string, unknown>[];
      const siblings = list
        .filter((t) => ((t.parentTeamId as string) || "") === parentId)
        .sort((a, b) => this.compareOrgTeams(a, b));
      const current = siblings.map((t) => t.id as string);
      const next = moveSibling(current, movedId, targetId, position);
      if (next.every((id, i) => id === current[i])) {
        return;
      }
      const previous = new Map(siblings.map((t) => [t.id as string, t.sortOrder as number | null]));
      for (const [index, id] of next.entries()) {
        const row = list.find((t) => t.id === id);
        if (row) {
          row.sortOrder = index;
        }
      }
      try {
        await this.sdk.team.reorder({ parentTeamId: parentId || null, orderedIds: next });
      } catch (error) {
        for (const [id, sortOrder] of previous) {
          const row = list.find((t) => t.id === id);
          if (row) {
            row.sortOrder = sortOrder ?? null;
          }
        }
        Alpine.store("toast").error(
          error instanceof Error ? error.message : "팀 순서를 저장하지 못했어요.",
        );
        await this.loadTeams();
      }
    },

    // biome-ignore lint/suspicious/noExplicitAny: dynamic team rows
    orgTreeFlat(search?: string): any[] {
      const list = this.teams as Record<string, unknown>[];
      const q = (search || "").toLowerCase().trim();
      const byId = new Map<string, Record<string, unknown>>();
      for (const t of list) {
        byId.set(t.id as string, t);
      }
      // Restrict to search matches + their ancestor chain (keeps tree intact).
      let visible: Set<string> | null = null;
      if (q) {
        visible = new Set<string>();
        for (const t of list) {
          if (((t.name as string) || "").toLowerCase().includes(q)) {
            let cur: Record<string, unknown> | undefined = t;
            let safety = 0;
            while (cur && safety < 50) {
              visible.add(cur.id as string);
              cur = byId.get((cur.parentTeamId as string) || "");
              safety++;
            }
          }
        }
      }
      const vis = visible;
      const inView = vis ? list.filter((t) => vis.has(t.id as string)) : list;
      const inViewIds = new Set(inView.map((t) => t.id as string));
      const byParent = new Map<string, Record<string, unknown>[]>();
      for (const t of inView) {
        const pid = (t.parentTeamId as string) || "";
        if (!byParent.has(pid)) {
          byParent.set(pid, []);
        }
        byParent.get(pid)?.push(t);
      }
      for (const arr of byParent.values()) {
        arr.sort((a, b) => this.compareOrgTeams(a, b));
      }
      // 인원 배지 — 로스터가 있으면 재직 + 휴직(퇴사 따로)을 한 번에 집계, 로드 전에는 배지를 숨긴다.
      const counts = Object.keys(this.rosterTeamMap).length > 0 ? this.orgTeamHeadcounts() : null;
      // biome-ignore lint/suspicious/noExplicitAny: dynamic team rows
      const out: any[] = [];
      // When the user types in the search box, force-expand the matched
      // ancestor chain so results are always visible.
      const forceExpand = !!q;
      // 병동 줄은 트리에 두지 않는다(2026-09-23 P5 사용자 결정) — 팀 관리 트리는 팀만 보인다.
      // 병동은 오른쪽 「병동 현황」 표와 병동 관리에서 본다.
      const walk = (
        t: Record<string, unknown>,
        depth: number,
        guides: boolean[],
        isFirst: boolean,
        isLast: boolean,
      ) => {
        const children = byParent.get(t.id as string) || [];
        const childCount = children.length;
        const hasChildren = childCount > 0;
        const collapsed = !forceExpand && !!this.orgTreeCollapsed[t.id as string];
        // 모든 단계가 인원 배지를 보인다 — 자기 + 하위 팀 전체(직원 기준 중복 제거), 재직 + 휴직.
        // 명단을 다 읽기 전에는 배지를 숨긴다(null) — 소속 수로 어림한 숫자가 잠깐 보였다가 바뀌지 않게.
        const headcount = counts
          ? counts[t.id as string] || { total: 0, active: 0, onLeave: 0, terminated: 0 }
          : null;
        out.push({
          ...t,
          treeDepth: depth,
          hasChildren,
          collapsed,
          headcount,
          rollupSize: headcount ? headcount.total : this.teamRollupSize(t.id as string),
          treeLines: treeLineStyles(depth, guides, isFirst, isLast, hasChildren),
        });
        if (collapsed) {
          return;
        }
        const childGuides = [...guides, !isLast];
        for (const [i, c] of children.entries()) {
          walk(c, depth + 1, childGuides, i === 0, i === childCount - 1);
        }
      };
      // Roots = teams whose parent is not itself in view (covers true roots
      // and any node whose ancestor was filtered out by search).
      // 최상위도 하위와 같은 규칙으로 정렬한다 — 지정 순서(sortOrder) 먼저, 나머지는 이름순(MED-953).
      // 하위 팀 묶음(byParent)만 정렬하고 최상위는 서버 목록 순서 그대로 두어, 최상위 팀 사이의 드래그가
      // 저장은 되는데 화면이 바뀌지 않았다(2026-10-07 QA).
      const roots = inView
        .filter((t) => {
          const pid = (t.parentTeamId as string) || "";
          return !(pid !== "" && inViewIds.has(pid));
        })
        .sort((a, b) => this.compareOrgTeams(a, b));
      for (const [i, t] of roots.entries()) {
        walk(t, 0, [], i === 0, i === roots.length - 1);
      }
      return out;
    },

    // Check if a team has any (active) members loaded into teamMembers cache.
    // The org tree loads composition lazily; treat "no entry" as 0.
    teamMemberCount(teamId: string): number {
      const members = this.teamMembers[teamId];
      if (!Array.isArray(members)) {
        // Fall back to the currentSize summary from the teams list so the
        // branch/leaf UI doesn't flicker before composition is fetched.
        const team = (this.teams as unknown as Record<string, unknown>[]).find(
          (t) => t.id === teamId,
        );
        return (team?.currentSize as number) || 0;
      }
      return members.filter((m: Record<string, unknown>) => m.isActive !== false).length;
    },

    // Rollup headcount for a team: DISTINCT employees in the team + every
    // descendant team. Counts unique employees from the loaded org roster
    // (`rosterTeamMap`) so someone belonging to both a parent and a child team
    // is counted once — matching the deduped team-detail "총 N명" and list.
    // Falls back to summing each team's `currentSize` (may overcount cross-team
    // members) only until the roster finishes loading.
    teamRollupSize(teamId: string): number {
      const subtree = new Set<string>([teamId, ...this.descendantTeamIds(teamId)]);
      const roster = this.rosterTeamMap;
      const empIds = Object.keys(roster);
      if (empIds.length > 0) {
        let count = 0;
        for (const eid of empIds) {
          if ((roster[eid] || []).some((tid) => subtree.has(tid))) {
            count++;
          }
        }
        return count;
      }
      // Roster not loaded yet — approximate with the per-team currentSize sum.
      const byId = new Map<string, Record<string, unknown>>();
      for (const t of this.teams as Record<string, unknown>[]) {
        byId.set(t.id as string, t);
      }
      let sum = 0;
      for (const tid of subtree) {
        sum += (byId.get(tid)?.currentSize as number) || 0;
      }
      return sum;
    },

    /** 팀별 인원(자기 + 하위 팀 전체, 직원 기준 중복 제거) — 전체 = 재직 + 휴직(비활성 제외),
     *  퇴사는 따로 센다(지금 소속 + 퇴사하면서 끝난 예전 소속).
     *  로스터를 한 번 훑어 모든 팀을 같이 센다: 직원마다 소속 팀과 그 상위 팀들을 올라가며 더한다. */
    orgTeamHeadcounts(): Record<
      string,
      { total: number; active: number; onLeave: number; terminated: number }
    > {
      const parentOf = new Map<string, string>();
      for (const t of this.teams as Record<string, unknown>[]) {
        parentOf.set(t.id as string, (t.parentTeamId as string) || "");
      }
      const reachFrom = (teamIds: string[]) => {
        const reached = new Set<string>();
        for (const teamId of teamIds) {
          let cur = teamId;
          // 상위 팀 순환이 있어도 멈추도록 이미 지난 팀이면 끝낸다
          while (cur && !reached.has(cur)) {
            reached.add(cur);
            cur = parentOf.get(cur) || "";
          }
        }
        return reached;
      };
      const out: Record<
        string,
        { total: number; active: number; onLeave: number; terminated: number }
      > = {};
      const bump = (teamId: string) => {
        const c = out[teamId] || { total: 0, active: 0, onLeave: 0, terminated: 0 };
        out[teamId] = c;
        return c;
      };
      for (const [employeeId, status] of Object.entries(this.rosterStatusMap)) {
        const current = this.rosterTeamMap[employeeId] || [];
        if (status === "TERMINATED") {
          const ids = [...current, ...(this.rosterLeftTeamMap[employeeId] || [])];
          for (const teamId of reachFrom(ids)) {
            bump(teamId).terminated++;
          }
          continue;
        }
        if (status !== "ACTIVE" && status !== "ON_LEAVE") {
          continue;
        }
        for (const teamId of reachFrom(current)) {
          const c = bump(teamId);
          c.total++;
          if (status === "ON_LEAVE") {
            c.onLeave++;
          } else {
            c.active++;
          }
        }
      }
      return out;
    },

    /** 명단 「하위 팀 포함」의 시작값 — 기본 켬, 사용자가 바꾼 값은 조직·사용자별로 기억(team-roster-prefs) */
    rosterIncludeSubteamsDefault(): boolean {
      const user = Alpine.store("hereby").currentUser as Record<string, unknown> | null;
      return readIncludeSubteams(
        rosterIncludeSubteamsKey(
          (user?.organizationId as string | undefined) ?? null,
          (user?.id as string | undefined) ?? null,
        ),
      );
    },

    rememberRosterIncludeSubteams(value: boolean) {
      const user = Alpine.store("hereby").currentUser as Record<string, unknown> | null;
      writeIncludeSubteams(
        rosterIncludeSubteamsKey(
          (user?.organizationId as string | undefined) ?? null,
          (user?.id as string | undefined) ?? null,
        ),
        value,
      );
    },

    teamIsBranch(teamId: string): boolean {
      return this.teamChildCount(teamId) > 0;
    },

    teamIsLeaf(teamId: string): boolean {
      return this.teamChildCount(teamId) === 0;
    },

    /**
     * Flat depth-first list of all teams (roots → children → grandchildren),
     * with display metadata. Used by team-picker menus to render the same
     * hierarchy as the org tab while keeping branch (folder) teams
     * non-selectable — members live on leaf teams only.
     */
    // biome-ignore lint/suspicious/noExplicitAny: dynamic team rows
    teamTreeFlat(): any[] {
      const list = this.teams as Record<string, unknown>[];
      const byParent = new Map<string, Record<string, unknown>[]>();
      for (const t of list) {
        const pid = (t.parentTeamId as string) || "";
        if (!byParent.has(pid)) {
          byParent.set(pid, []);
        }
        byParent.get(pid)?.push(t);
      }
      for (const arr of byParent.values()) {
        arr.sort((a, b) => this.compareOrgTeams(a, b));
      }
      // biome-ignore lint/suspicious/noExplicitAny: dynamic team rows
      const out: any[] = [];
      const walk = (parentId: string, depth: number) => {
        const kids = byParent.get(parentId) || [];
        for (const t of kids) {
          const childCount = (byParent.get(t.id as string) || []).length;
          out.push({
            ...t,
            treeDepth: depth,
            treeIsLeaf: childCount === 0,
            treeChildCount: childCount,
            treeCollapsed: false,
          });
          // 전체 목록은 항상 펼친다. 접기 반영은 피커 전용 teamTreePicker() 가 맡는다.
          if (childCount > 0) {
            walk(t.id as string, depth + 1);
          }
        }
      };
      walk("", 0);
      return out;
    },

    /** 팀 피커(드롭다운 트리) 전용 — 접힌 부모의 하위 팀과 비활성 팀은 제외한 목록.
     *  `query` 가 있으면 검색 모드(계층·접기 무시, 이름 일치만 평평하게), `keepTeamId` 는
     *  지금 소속의 팀이라 비활성이어도 남긴다.
     *
     *  `teamTreeFlat()`은 항상 전체를 돌려준다(일괄 팀 변경 select · `teamLeavesFlat()` 처럼
     *  모든 팀이 보여야 하는 곳이 쓴다). 피커는 이 메서드를 써서 접기 상태를 반영한다.
     *  접힌 부모 행 자체는 항상 남고 chevron 도 그대로라, 저장된 접기 상태가 오래돼도
     *  팀이 영구히 사라지는 일은 없다 — 부모를 펼치면 언제든 다시 나온다.
     *  (2026-08 QA: 접기 버튼이 눌려도 아무 변화가 없던 것은, 상태·핸들러는 남았는데
     *  트리 생성이 접기를 무시하도록 고정돼 있었기 때문이다.) */
    // biome-ignore lint/suspicious/noExplicitAny: dynamic team rows
    teamTreePicker(query = "", keepTeamId = ""): any[] {
      const q = pickerQuery(query);
      // 비활성 팀은 고를 수 없으니 빼되, 지금 이 소속의 팀(keepTeamId)은 비활성이라도 남긴다
      // biome-ignore lint/suspicious/noExplicitAny: dynamic team rows
      const shown = (t: any) => t.isActive !== false || t.id === keepTeamId;
      if (q) {
        // 검색 중엔 접기·계층을 무시하고 이름이 맞는 팀만 평평하게 — 어느 깊이에 있든 찾힌다
        return (
          this.teamTreeFlat()
            // biome-ignore lint/suspicious/noExplicitAny: dynamic team rows
            .filter((t: any) => shown(t) && pickerMatches(t.name, q))
            // biome-ignore lint/suspicious/noExplicitAny: dynamic team rows
            .map((t: any) => ({
              ...t,
              treeDepth: 0,
              treeIsLeaf: true,
              treeChildCount: 0,
              treeCollapsed: false,
            }))
        );
      }
      const collapsed = this.teamPickerCollapsed || {};
      const hidden = new Set<string>();
      // teamTreeFlat 은 전위 순회(부모가 자식보다 먼저)라 한 번의 통과로 숨김을 전파할 수 있다.
      // biome-ignore lint/suspicious/noExplicitAny: dynamic team rows
      return this.teamTreeFlat().flatMap((t: any) => {
        const pid = (t.parentTeamId as string) || "";
        if (pid && hidden.has(pid)) {
          hidden.add(t.id as string);
          return [];
        }
        if (!shown(t)) {
          // 비활성 팀은 하위 팀까지 함께 숨긴다(접기와 같은 전파)
          hidden.add(t.id as string);
          return [];
        }
        const isCollapsed = !t.treeIsLeaf && !!collapsed[t.id as string];
        if (isCollapsed) {
          hidden.add(t.id as string);
        }
        return [{ ...t, treeCollapsed: isCollapsed }];
      });
    },

    /** 역할 피커 목록 — 비활성 역할은 빼고(지금 소속의 역할 keepRoleId 만 예외) 검색어로 거른다 */
    // biome-ignore lint/suspicious/noExplicitAny: dynamic role rows
    rolePickerList(query = "", keepRoleId = ""): any[] {
      // biome-ignore lint/suspicious/noExplicitAny: dynamic role rows
      return pickerRoles(this.roles as any[], query, keepRoleId);
    },

    /** Only leaf teams, ordered by tree traversal with depth metadata. */
    // biome-ignore lint/suspicious/noExplicitAny: dynamic team rows
    teamLeavesFlat(): any[] {
      // biome-ignore lint/suspicious/noExplicitAny: dynamic team rows
      return this.teamTreeFlat().filter((t: any) => t.treeIsLeaf);
    },

    /** All descendant team ids of `teamId` (any depth, excludes the team itself). */
    descendantTeamIds(teamId: string): string[] {
      const list = this.teams as Record<string, unknown>[];
      const out: string[] = [];
      // Guard against a parentTeamId cycle in the data — without it the walk never
      // ends and the page hangs. Depth cap matches teamDepth()'s 60 (backend max 50).
      const visited = new Set<string>([teamId]);
      const walk = (pid: string, depth: number) => {
        if (depth > 60) {
          return; // safety against cycles
        }
        for (const t of list) {
          const id = t.id as string;
          if (t.parentTeamId === pid && !visited.has(id)) {
            visited.add(id);
            out.push(id);
            walk(id, depth + 1);
          }
        }
      };
      walk(teamId, 1);
      return out;
    },

    /** Ensure `teamMembers` is populated for `teamId` and every descendant team —
     *  backs the "하위 조직 멤버 포함" toggle in the team member view. */
    async ensureSubtreeMembers(teamId: string): Promise<void> {
      // 로스터를 받았으면 모든 팀의 명단이 이미 teamMembers 에 있다(소속이 없는 팀 = 빈 목록) — 서버를 부르지 않는다
      if (this.rosterLoaded) {
        return;
      }
      const ids = [teamId, ...this.descendantTeamIds(teamId)];
      await Promise.all(
        ids.map(async (tid) => {
          if (!this.teamMembers[tid]) {
            try {
              const result = await this.sdk.team.getComposition(tid);
              this.teamMembers[tid] = result?.members || [];
            } catch {
              this.teamMembers[tid] = [];
            }
          }
        }),
      );
    },

    /** Members of `teamId` plus all descendant teams, deduped by employee. Each
     *  row carries `_teamId` (its real source team) so row-level team actions
     *  (팀원 제거 등) target the correct team even in the aggregated view. */
    subtreeMembers(teamId: string): Record<string, unknown>[] {
      const ids = [teamId, ...this.descendantTeamIds(teamId)];
      const seen = new Set<string>();
      const out: Record<string, unknown>[] = [];
      for (const tid of ids) {
        for (const m of (this.teamMembers[tid] || []) as Record<string, unknown>[]) {
          const eid = m.employeeId as string;
          if (seen.has(eid)) {
            continue;
          }
          seen.add(eid);
          out.push({ ...m, srcTeamId: tid });
        }
      }
      return out;
    },

    /** Build a "Path > Like > This" label for a team using parent names. */
    teamPathLabel(teamId: string): string {
      const list = this.teams as Record<string, unknown>[];
      const parts: string[] = [];
      let current = list.find((t) => t.id === teamId);
      let safety = 0;
      while (current && safety < 10) {
        parts.unshift(String(current.name || ""));
        const pid = current.parentTeamId as string | undefined;
        if (!pid) {
          break;
        }
        current = list.find((t) => t.id === pid);
        safety++;
      }
      return parts.join(" › ");
    },

    async loadAllTeamMembers() {
      for (const team of this.teams as Record<string, unknown>[]) {
        const tid = team.id as string;
        if (!this.teamMembers[tid]) {
          try {
            const result = await this.sdk.team.getComposition(tid);
            this.teamMembers[tid] = result?.members || [];
          } catch {
            this.teamMembers[tid] = [];
          }
        }
      }
    },

    async toggleTeamExpand(teamId: string) {
      if (this.expandedTeams[teamId]) {
        this.expandedTeams[teamId] = false;
        return;
      }
      try {
        const result = await this.sdk.team.getComposition(teamId);
        this.teamMembers[teamId] = result?.members || [];
        this.expandedTeams[teamId] = true;
      } catch {
        this.teamMembers[teamId] = [];
        this.expandedTeams[teamId] = true;
      }
    },

    /** 병동에 연결된 팀 — allTeams(필터 없는 전체 팀)의 wardId 기준 */
    wardLinkedTeam(wardId: string): OrgTeamItem | undefined {
      return this.allTeams.find((t) => t.wardId === wardId);
    },

    /** 병동 배정을 직접 만들 수 있는지 — 「팀에도 추가」를 끄거나 연결 팀이 없는 병동에 넣을 때 필요하다(nurseScheduler.createProfile) */
    canCreateWardAssignment(): boolean {
      return Alpine.store("hereby").hasPermission("nurse-scheduler:create");
    },

    /** 병동 화면 「신규 직원 추가」가 등록 패널에 넘기는 값 — 연결 팀(있으면 소속에 미리 채움) · 병동(저장 시 배정).
     *  병동을 함께 넘기므로 연결 팀이 없는 병동에서 등록해도 그 병동 명단에 들어온다(2026-09-23 P4). */
    addEmployeeDetailForWard(wardId: string | null): {
      teamId?: string;
      teamName?: string;
      wardId?: string;
      wardName?: string;
    } {
      if (!wardId) {
        return {};
      }
      const team = this.wardLinkedTeam(wardId);
      return {
        ...(team ? { teamId: team.id, teamName: team.name ?? "" } : {}),
        wardId,
        wardName: this.wardNameOf(wardId),
      };
    },

    /* ── 병동 관리 「직원 배정」(2026-09-22 P2) ──
       후보 = 전체 명단(allMembers)의 재직 · 휴직 직원. 결과 미리보기는 서버(ward.assignPreview)가 실행과 같은 규칙으로
       계산한다 — 화면이 규칙을 따로 흉내 내면 미리보기와 실제 결과가 조용히 어긋난다. */

    /** 「직원 배정」을 쓸 수 있는지 — 병동 배정 만들기 권한(서버 assignEmployees 의 기본 권한) */
    canAssignToWard(): boolean {
      return Alpine.store("hereby").hasPermission("nurse-scheduler:create");
    },

    /** 옵션별 추가 권한 — 팀 소속 바꾸기(team:update) · 지금 이동(배정 삭제) · 예약 이동(배정 수정). 없으면 그 옵션을 잠근다 */
    wardAssignAllowed(kind: "team" | "moveNow" | "reserve"): boolean {
      const store = Alpine.store("hereby");
      if (kind === "team") {
        // 병동 페이지 관리자에게는 team:update 를 주지 않는다. 대신 서버가 「그 병동의 연결 팀」
        // 하나로 좁혀 합류·이탈만 받는다(PM-01) — 이 팝업이 바꾸는 팀이 바로 그 연결 팀이므로 켠다.
        return store.hasPermission("team:update") || store.hasPermission("ward:manage");
      }
      return store.hasPermission(
        kind === "moveNow" ? "nurse-scheduler:delete" : "nurse-scheduler:update",
      );
    },

    openWardAssign(wardId: string) {
      const groups = this.wardScheduleGroups(wardId).filter((g) => g.isActive !== false);
      const linked = this.wardLinkedTeam(wardId);
      // 값을 다 채운 뒤에 연다 — closeGuard 가 여는 순간의 값을 「안 바뀐 상태」로 잡는다
      this.wardAssign = {
        mode: "assign",
        fromWardId: "",
        wardId,
        selectedIds: [],
        scheduleGroupId: (groups.find((g) => g.isDefault) ?? groups[0])?.id ?? "",
        workType: NurseWorkType.THREE_SHIFT,
        joinLinkedTeam: !!linked && this.wardAssignAllowed("team"),
        keepOtherWards: true,
        timing: this.wardAssignAllowed("moveNow") ? "now" : "scheduled",
        effectiveDate: "",
        changeManualShift: false,
      };
      this.wardAssignSearch = "";
      this.wardAssignStatusFilter = "all";
      this.wardAssignJobFilter = [];
      this.wardAssignPreview = null;
      wardAssignNudgePending = false;
      this.showWardAssign = true;
    },

    /** 후보 한 줄 — 현재 배정(병동 · 교대 형태)과 「이미 이 병동」 여부 */
    wardAssignCandidates(): {
      id: string;
      name: string;
      number: string;
      jobDuty: string;
      teams: string;
      onLeave: boolean;
      here: boolean;
      otherWards: string;
      assignmentLabel: string;
    }[] {
      const wardId = this.wardAssign.wardId;
      const t = (k: string) => Alpine.store("hereby").t(k);
      return this.allMembers
        .filter((m) => m.status === "ACTIVE" || m.status === "ON_LEAVE")
        .map((m) => {
          const wards =
            (m.wards as { wardId: string; wardName: string; workType?: string }[] | undefined) ??
            [];
          const here = wards.some((w) => w.wardId === wardId);
          const others = wards.filter((w) => w.wardId !== wardId);
          return {
            id: String(m.employeeId),
            name: String(m.employeeName || ""),
            number: String(m.employeeNumber || ""),
            jobDuty: String(m.jobDuty || ""),
            teams: ((m.teamNames as string[] | undefined) ?? []).join(", "),
            onLeave: m.status === "ON_LEAVE",
            here,
            otherWards: others.map((w) => w.wardName).join(" · "),
            assignmentLabel: here
              ? "이미 이 병동"
              : others.length
                ? others
                    .map(
                      (w) =>
                        w.wardName + (w.workType ? ` · ${t(`nurse.work_type_${w.workType}`)}` : ""),
                    )
                    .join(" / ")
                : "미배정",
          };
        });
    },

    /** 검색 · 배정 상태 · 직무 필터를 건 후보 — 고를 수 있는 사람이 위, 그 안은 이름순 */
    wardAssignFiltered() {
      const q = this.wardAssignSearch.trim().toLowerCase();
      const status = this.wardAssignStatusFilter;
      const jobs = this.wardAssignJobFilter;
      return this.wardAssignCandidates()
        .filter((c) => !q || c.name.toLowerCase().includes(q) || c.number.toLowerCase().includes(q))
        .filter(
          (c) =>
            status === "all" ||
            (status === "here" && c.here) ||
            (status === "other" && !c.here && !!c.otherWards) ||
            (status === "unassigned" && !c.here && !c.otherWards),
        )
        .filter((c) => jobs.length === 0 || jobs.includes(c.jobDuty || "__unset"))
        .sort((a, b) => Number(a.here) - Number(b.here) || a.name.localeCompare(b.name, "ko"));
    },

    wardAssignJobOptions(): string[] {
      const set = new Set(this.wardAssignCandidates().map((c) => c.jobDuty || "__unset"));
      return [...set].sort(
        (a, b) => Number(a === "__unset") - Number(b === "__unset") || a.localeCompare(b, "ko"),
      );
    },

    toggleWardAssignSelect(id: string) {
      const ids = this.wardAssign.selectedIds;
      this.wardAssign.selectedIds = ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
    },

    /** 모두 선택 — 지금 보이는 행 중 고를 수 있는(이미 이 병동이 아닌) 사람 */
    wardAssignAllSelected(): boolean {
      const open = this.wardAssignFiltered().filter((c) => !c.here);
      return open.length > 0 && open.every((c) => this.wardAssign.selectedIds.includes(c.id));
    },
    toggleWardAssignSelectAll() {
      const open = this.wardAssignFiltered()
        .filter((c) => !c.here)
        .map((c) => c.id);
      const selected = new Set(this.wardAssign.selectedIds);
      if (this.wardAssignAllSelected()) {
        this.wardAssign.selectedIds = this.wardAssign.selectedIds.filter(
          (id) => !open.includes(id),
        );
      } else {
        for (const id of open) {
          selected.add(id);
        }
        this.wardAssign.selectedIds = [...selected];
      }
    },

    /** 고른 사람 중 다른 병동 배정이 있는 사람이 있는지 — 없으면 겸임/이동 옵션은 의미가 없어 감춘다 */
    wardAssignHasMovers(): boolean {
      const byId = new Map(this.wardAssignCandidates().map((c) => [c.id, c]));
      return this.wardAssign.selectedIds.some((id) => !!byId.get(id)?.otherWards);
    },

    /** 예약 이동을 고른 상태인지(겸임 끔 + 예약 + 옮길 사람 있음) — 팀 옵션을 잠근다(팀 소속은 예약이 안 된다) */
    wardAssignScheduled(): boolean {
      return (
        !this.wardAssign.keepOtherWards &&
        this.wardAssign.timing === "scheduled" &&
        this.wardAssignHasMovers()
      );
    },

    /** 서버 입력 — 미리보기와 실행이 같은 값을 보낸다 */
    wardAssignInput() {
      const a = this.wardAssign;
      return {
        wardId: a.wardId,
        scheduleGroupId: a.scheduleGroupId || undefined,
        workType: a.workType as NurseWorkType,
        employeeIds: a.selectedIds,
        // 예약 이동이면 팀 체크박스가 잠기고 꺼진 모습이다 — 함께 고른 다른 직원도 팀에 넣지 않아 화면과 같게 보낸다
        joinLinkedTeam: a.joinLinkedTeam && !this.wardAssignScheduled(),
        keepOtherWards: a.keepOtherWards,
        timing: a.timing,
        effectiveDate: a.timing === "scheduled" && a.effectiveDate ? a.effectiveDate : undefined,
        keepManualShift: !a.changeManualShift,
      };
    },

    /** 옵션 · 선택이 바뀔 때마다(팝업의 x-effect) 미리보기를 다시 받는다 — 250ms 모아서, 늦게 온 옛 응답은 버린다 */
    scheduleWardAssignPreview() {
      const seq = ++wardAssignPreviewSeq;
      if (this.wardAssign.selectedIds.length === 0 || !this.wardAssign.wardId) {
        this.wardAssignPreview = null;
        this.wardAssignPreviewLoading = false;
        return;
      }
      this.wardAssignPreviewLoading = true;
      setTimeout(async () => {
        if (seq !== wardAssignPreviewSeq || !this.showWardAssign) {
          return;
        }
        try {
          const preview = await this.sdk.nurseScheduler.previewWardAssign(this.wardAssignInput());
          if (seq !== wardAssignPreviewSeq) {
            return;
          }
          this.wardAssignPreview = preview;
          this.wardAssignPreviewFailed = false;
          // 새로 생긴 검토 영역을 맞추던 중이면, 문장이 채워져 상자가 자란 뒤 한 번 더 맞춘다(첫 스크롤 때는 「계산 중」 한 줄뿐이라 덜 내려갔다)
          if (wardAssignNudgePending) {
            wardAssignNudgePending = false;
            Alpine.nextTick(() => this.scrollWardAssignReview());
          }
          // 예약 적용일 기본값 = 다음 근무 주기 시작일(V-01: 이번 주기 기록이 원래 병동 근무표에서 사라지는 것을 피한다)
          if (!this.wardAssign.effectiveDate) {
            this.wardAssign.effectiveDate = preview.nextCycleStart;
          }
        } catch {
          if (seq === wardAssignPreviewSeq) {
            this.wardAssignPreview = null;
            this.wardAssignPreviewFailed = true;
          }
        } finally {
          if (seq === wardAssignPreviewSeq) {
            this.wardAssignPreviewLoading = false;
          }
        }
      }, 250);
    },

    /** 교대 형태 선택지 — 스케줄러 간호 정보와 같은 값 */
    nurseWorkTypeValues(): string[] {
      return NURSE_WORK_TYPES;
    },

    /** 예약 적용일로 고를 수 있는 첫날 — 조직 기준 오늘(서버 미리보기)의 다음 날. 서버도 「내일 이후」만 받는다 */
    wardAssignMinDate(): string {
      const today = this.wardAssignPreview?.today;
      if (!today) {
        return "";
      }
      const [y, m, d] = today.split("-").map(Number);
      const next = new Date(Date.UTC(y, m - 1, d + 1));
      return next.toISOString().slice(0, 10);
    },

    /** 이름 몇 개 — 「강선우」 / 「강선우 · 오주원」 / 「강선우 · 오주원 외 3명」 */
    wardAssignNames(names: string[]): string {
      return names.length <= 2
        ? names.join(" · ")
        : `${names.slice(0, 2).join(" · ")} 외 ${names.length - 2}명`;
    },

    /** 「10월 16일」 */
    wardAssignDateLabel(date: string): string {
      const [, m, d] = date.split("-").map(Number);
      return m && d ? `${m}월 ${d}일` : date;
    },

    /**
     * 결과 미리보기 문장 — 서버 미리보기(직원별)를 옵션 조합에 맞는 한 줄씩으로 묶는다. warn = 되돌리기 어렵거나 다른 화면에
     * 흔적이 남는 결과(이번 주기 근무표 기록이 안 보임 · 팀 관리 「불일치」).
     */
    wardAssignPreviewLines(): { text: string; warn: boolean }[] {
      // 못 받아 왔으면 빈 상자로 두지 않는다 — 무엇이 바뀌는지 모르는 채로 실행하게 된다
      if (this.wardAssignPreviewFailed) {
        return [
          { text: "바뀌는 내용을 계산하지 못했어요. 잠시 뒤 다시 시도해 주세요.", warn: true },
        ];
      }
      const p = this.wardAssignPreview;
      if (!p) {
        return [];
      }
      const selected = new Set(this.wardAssign.selectedIds);
      const rows = p.rows.filter((r) => selected.has(r.employeeId) && !r.alreadyAssigned);
      if (rows.length === 0) {
        return [{ text: "바뀌는 것이 없어요.", warn: false }];
      }
      const j = (word: string, pair: "은/는" | "과/와" | "으로/로") =>
        this.wardAssignJosa(word, pair);
      const names = (list: typeof rows) => this.wardAssignNames(list.map((r) => r.name));
      const lines: { text: string; warn: boolean }[] = [];
      const onLeave = rows.filter((r) => r.status === "ON_LEAVE").length;
      const workTypeLabel = Alpine.store("hereby").t(`nurse.work_type_${this.wardAssign.workType}`);
      const nowRows = rows.filter((r) => !r.reserve);
      const moving = this.wardAssign.mode === "move";
      lines.push({
        text:
          `${rows.length}명을 ${moving ? j(p.wardName, "으로/로") : `${p.wardName}에`} ${moving ? "옮겨요" : "배정해요"}` +
          (onLeave ? `(휴직 ${onLeave}명 포함)` : "") +
          (nowRows.length ? ` · ${p.scheduleGroupName} 스케줄러 · ${workTypeLabel}` : ""),
        warn: false,
      });

      // 병동 — 겸임 · 지금 이동 · 예약 이동
      const concurrent = rows.filter((r) => !r.move && r.otherWards.length > 0);
      for (const r of concurrent.slice(0, 3)) {
        lines.push({
          text: `${j(r.name, "은/는")} ${j(r.otherWards.map((w) => w.wardName).join(" · "), "과/와")} 겸임해요`,
          warn: false,
        });
      }
      if (concurrent.length > 3) {
        lines.push({ text: `그 밖에 ${concurrent.length - 3}명도 겸임해요`, warn: false });
      }
      for (const r of rows.filter((x) => x.move && !x.reserve)) {
        const from = r.otherWards.map((w) => w.wardName).join(" · ");
        const count = r.otherWards.reduce((sum, w) => sum + w.entryCount, 0);
        lines.push({
          text:
            `${j(r.name, "은/는")} ${from}에서 지금 옮겨요` +
            (count > 0
              ? ` — ${from} 근무표의 이번 주기 이후 기록 ${count}칸이 더는 보이지 않아요`
              : ""),
          warn: count > 0,
        });
      }
      const reserving = rows.filter((r) => r.reserve);
      if (reserving.length) {
        const date = this.wardAssign.effectiveDate || p.nextCycleStart;
        lines.push({
          text: `${j(names(reserving), "은/는")} ${this.wardAssignDateLabel(date)}부터 옮겨요. 그 전에 짜는 ${p.wardName} 근무표엔 아직 없어요`,
          warn: false,
        });
      }

      // 팀 — 옮기는 사람은 「A팀에서 B팀으로」, 새로 들어가기만 하는 사람은 「B팀에도(지금 소속 팀은 그대로)」
      const teamMovers = rows.filter((r) => r.joinTeamName && r.leaveTeamNames.length);
      for (const r of teamMovers) {
        lines.push({
          text: `${j(r.name, "은/는")} 팀도 ${r.leaveTeamNames.join(" · ")}에서 ${j(p.targetTeamName ?? "", "으로/로")} 옮겨요`,
          warn: false,
        });
      }
      const joiningOnly = rows.filter((r) => r.joinTeamName && !r.leaveTeamNames.length);
      if (joiningOnly.length) {
        lines.push({
          text: `${j(names(joiningOnly), "은/는")} ${p.targetTeamName}에도 들어가요(지금 소속 팀은 그대로)`,
          warn: false,
        });
      }
      if (teamMovers.length || joiningOnly.length) {
        lines.push({ text: "새로 들어간 팀은 팀장이 승인하면 팀 단톡방에 들어가요", warn: false });
      }
      const leavingOnly = rows.filter((r) => !r.joinTeamName && r.leaveTeamNames.length);
      for (const r of leavingOnly) {
        lines.push({
          text: `${r.name}의 ${r.leaveTeamNames.join(" · ")} 소속은 끝나요`,
          warn: false,
        });
      }
      const mismatch = rows.filter((r) => r.keptLinkedTeamNames.length);
      for (const r of mismatch.slice(0, 3)) {
        lines.push({
          text: `${r.name}의 ${r.keptLinkedTeamNames.join(" · ")} 소속이 그대로라 팀 관리에 「불일치」로 보여요`,
          warn: true,
        });
      }
      if (mismatch.length > 3) {
        lines.push({ text: `그 밖에 ${mismatch.length - 3}명도 「불일치」로 보여요`, warn: true });
      }

      // 근무 — 이동하는 사람만(겸임은 근무를 바꾸지 않는다)
      const movers = rows.filter((r) => r.move);
      for (const r of movers.filter((x) => x.shift.follow && x.shift.willChange)) {
        lines.push({
          text:
            `${r.name}의 근무가 ${r.shift.current?.label ?? "없음"}에서 ${j(r.shift.next?.label ?? "-", "으로/로")} 바뀌어요` +
            (r.reserve ? "(적용일에)" : ""),
          warn: false,
        });
      }
      const keptManual = movers.filter((r) => r.shift.manual && !r.shift.follow);
      if (keptManual.length) {
        lines.push({
          text: `${j(names(keptManual), "은/는")} 직접 지정한 근무라 그대로 둬요`,
          warn: false,
        });
      }
      const sameShift = movers.filter((r) => r.shift.follow && !r.shift.willChange);
      if (sameShift.length) {
        lines.push({ text: `${names(sameShift)}의 근무는 그대로예요`, warn: false });
      }
      return lines;
    },

    /** 받침에 맞는 조사 — 「강선우는」 · 「강태윤은」 · 「내과 병동과」 · 「외과 병동팀으로」. 한글로 끝나지 않으면 「은(는)」 */
    wardAssignJosa(word: string, pair: "은/는" | "과/와" | "으로/로"): string {
      const [withBatchim, without] = pair.split("/");
      const code = word.trim().slice(-1).charCodeAt(0) - 0xac00;
      if (Number.isNaN(code) || code < 0 || code > 11171) {
        return `${word}${withBatchim}(${without})`;
      }
      const jong = code % 28;
      if (pair === "으로/로") {
        // ㄹ 받침(8)은 「로」 — 「병동실로」
        return word + (jong === 0 || jong === 8 ? "로" : "으로");
      }
      return word + (jong ? withBatchim : without);
    },

    /** 이동하는 사람 중 직접 지정한 근무가 있는 사람 수 — 「직접 지정한 근무도 바꾸기」를 보일지 */
    wardAssignManualMovers(): number {
      const p = this.wardAssignPreview;
      if (!p || this.wardAssign.keepOtherWards) {
        return 0;
      }
      const selected = new Set(this.wardAssign.selectedIds);
      return p.rows.filter((r) => selected.has(r.employeeId) && r.move && r.shift.manual).length;
    },

    /**
     * 표에서 사람을 고르면 아래에 새로 생기는 옵션(겸임 · 이동 시점)과 노란 결과 미리보기가 팝업 본문 아래로 가려진 채
     * 「배정」이 켜진다 — 스크롤 없이 검토하지 않고 저장하기 쉽다(2026-09-22 사용자 지적). 미리보기가 가려져 있으면 본문을
     * 살짝 내려 미리보기 윗부분(두세 줄)이 화면에 걸치게 한다. 이미 보이면 움직이지 않는다.
     * 새로 생길 때만 부른다(0→1명 선택 · 겸임 옵션 등장, ward-assign.html x-init) — 일부러 위로 올려 둔 본문을 체크할 때마다 끌어내리지 않게.
     */
    nudgeWardAssignReview() {
      // 이동 모드는 맞추지 않는다 — 카드 높이가 고정이고 본문 맨 위가 「옮길 직원」 명단이라,
      // 내려 주면 지금 다루는 명단이 화면 밖으로 밀린다(2026-09-23 제목 줄이 안 보이던 원인).
      if (this.wardAssign.mode === "move") {
        return;
      }
      // 미리보기 문장이 도착하면 한 번 더 맞춘다(scheduleWardAssignPreview) — 새로 생긴 경우에만 켠다
      wardAssignNudgePending = true;
      this.scrollWardAssignReview();
    },

    /** 가려진 미리보기가 본문 아래 끝에 걸치게 스크롤만 한다(이미 보이면 그대로) */
    scrollWardAssignReview() {
      // 0→1명 선택과 겸임 옵션 등장이 같은 클릭에서 함께 온다 — 잠깐 모아 한 번만, 옵션이 다 그려진 뒤의 자리로 스크롤한다
      // (따로 부르면 두 번째 부드러운 스크롤이 첫 번째를 중간에 끊어 미리보기가 덜 보였다)
      clearTimeout(wardAssignNudgeTimer);
      wardAssignNudgeTimer = setTimeout(() => {
        const modal = document.querySelector('[data-testid="ward-assign-modal"]');
        const body = modal?.querySelector<HTMLElement>(".ds-modal__body");
        const preview = modal?.querySelector<HTMLElement>('[data-testid="ward-assign-preview"]');
        if (!(body && preview) || preview.offsetParent === null) {
          return;
        }
        const Peek = 72; // 본문 아래 끝에서 미리보기가 보일 높이
        const hidden =
          preview.getBoundingClientRect().top - (body.getBoundingClientRect().bottom - Peek);
        if (hidden > 0) {
          const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
          body.scrollTo({ top: body.scrollTop + hidden, behavior: reduce ? "auto" : "smooth" });
        }
      }, 60);
    },

    async saveWardAssign() {
      const a = this.wardAssign;
      if (a.selectedIds.length === 0 || this.wardAssignSaving) {
        return;
      }
      if (this.wardAssignScheduled() && !a.effectiveDate) {
        showAlert("적용 시작일을 골라 주세요.");
        return;
      }
      this.wardAssignSaving = true;
      try {
        const { results } = await this.sdk.nurseScheduler.assignEmployeesToWard(
          this.wardAssignInput(),
        );
        const ok = results.filter((r) => r.ok);
        const failed = results.filter((r) => !r.ok);
        const moving = a.mode === "move";
        // 「추가됨」 배지는 지금 보고 있는 병동에 들어온 사람 표시다 — 이동은 이 병동에서 나가는 것이라 달지 않는다
        if (!moving) {
          this.markRecentlyAdded(ok.map((r) => r.employeeId));
        }
        this.showWardAssign = false;
        window.dispatchEvent(new CustomEvent("bulk-member-teams-applied"));
        await Promise.all([this.loadUnassignedMembers(), this.loadTeams()]);
        const wardName = this.wardAssignPreview?.wardName ?? "";
        const did = moving ? "옮겼어요" : "배정했어요";
        if (failed.length === 0) {
          const where = moving ? this.wardAssignJosa(wardName, "으로/로") : `${wardName}에`;
          this.notify(`${ok.length}명을 ${where} ${did}`);
        } else {
          showAlert({
            title: moving ? "일부만 옮겼어요" : "일부만 배정했어요",
            message: `${results.length}명 중 ${ok.length}명을 ${did} ${failed.length}명은 ${moving ? "옮기지" : "배정하지"} 못했어요.`,
            details: failed.map((r) => `${r.name} — ${r.message ?? ""}`),
          });
        }
      } catch (err) {
        const code = (err as { data?: { code?: string } } | null)?.data?.code;
        if (err instanceof Error && (code === "BAD_REQUEST" || code === "FORBIDDEN")) {
          showAlert(err.message);
        } else {
          this.showCommonError();
        }
      } finally {
        this.wardAssignSaving = false;
      }
    },

    /* ── 병동 인력 — 다른 병동으로 이동 · 배정 해제 · 되돌리기(2026-09-23 P3) ───────────────
       이동은 「직원 배정」과 서버 규칙이 같다(겸임을 끈 배정 = 이동). 그래서 팝업을 두 벌 만들지 않고
       같은 팝업을 모드로 나눈다 — 배정은 병동이 고정이고 사람을 고르며, 이동은 사람이 고정이고 병동을 고른다. */

    /** 고른 사람들을 다른 병동으로 — 「직원 배정」 팝업을 이동 모드로 연다(대상 병동은 팝업에서 고른다) */
    openWardMove(fromWardId: string, employeeIds: string[]) {
      const ids = employeeIds.filter(Boolean);
      if (ids.length === 0) {
        return;
      }
      // 값을 다 채운 뒤에 연다 — closeGuard 가 여는 순간의 값을 「안 바뀐 상태」로 잡는다
      this.wardAssign = {
        mode: "move",
        fromWardId,
        wardId: "",
        selectedIds: ids,
        scheduleGroupId: "",
        workType: NurseWorkType.THREE_SHIFT,
        joinLinkedTeam: this.wardAssignAllowed("team"),
        // 이동 = 겸임 끔. 이동 모드에서는 이 값을 화면에 내보이지 않는다
        keepOtherWards: false,
        timing: this.wardAssignAllowed("moveNow") ? "now" : "scheduled",
        effectiveDate: "",
        changeManualShift: false,
      };
      this.wardAssignSearch = "";
      this.wardAssignStatusFilter = "all";
      this.wardAssignJobFilter = [];
      this.wardAssignPreview = null;
      wardAssignNudgePending = false;
      this.wardMoveMoreBelow = false;
      this.wardMoveReviewed = false;
      this.wardMoveScrolled = false;
      this.wardAssignPreviewFailed = false;
      this.showWardAssign = true;
    },

    /** 이동 대상 병동 — 운영 중인 병동에서 떠나는 병동만 뺀다(비활성 병동으로는 보내지 않는다) */
    wardMoveTargets(): { id: string; name: string }[] {
      return (this.wardList as Record<string, unknown>[])
        .filter((w) => w.id !== this.wardAssign.fromWardId && w.isActive !== false)
        .map((w) => ({ id: String(w.id), name: String(w.name || "") }));
    },

    /** 대상 병동을 고르면 그 병동의 기본 스케줄러 · 연결 팀 기본값으로 맞춘다 */
    onWardMoveTargetChange() {
      const groups = this.wardScheduleGroups(this.wardAssign.wardId).filter(
        (g) => g.isActive !== false,
      );
      this.wardAssign.scheduleGroupId = (groups.find((g) => g.isDefault) ?? groups[0])?.id ?? "";
      this.wardAssign.joinLinkedTeam =
        !!this.wardLinkedTeam(this.wardAssign.wardId) && this.wardAssignAllowed("team");
      this.wardAssign.effectiveDate = "";
    },

    /** 이동 모드에서 옮기는 사람들 — 배정 모드 후보 표와 같은 열(이름 · 직무 · 소속 팀 · 현재 배정) */
    wardMovePeople(): {
      id: string;
      name: string;
      number: string;
      jobDuty: string;
      teams: string;
      assignmentLabel: string;
    }[] {
      const byId = new Map(this.wardAssignCandidates().map((c) => [c.id, c]));
      return this.wardAssign.selectedIds.map((id) => {
        const c = byId.get(id);
        return {
          id,
          name: c?.name ?? "",
          number: c?.number ?? "",
          jobDuty: c?.jobDuty ?? "",
          teams: c?.teams ?? "",
          assignmentLabel: c?.assignmentLabel ?? "",
        };
      });
    },

    /** 이동 팝업에서 한 사람 빼기 — 팝업에서는 더 고를 수 없고 빼기만 된다(더 고르려면 닫고 명단에서 고른다) */
    removeWardMovePerson(employeeId: string) {
      this.wardAssign.selectedIds = this.wardAssign.selectedIds.filter((id) => id !== employeeId);
    },

    /* ── 이동 팝업 「스크롤 하여 변경 내용 확인」 ───────────────────────────────
       카드 높이가 고정이라 옵션 · 미리보기가 아래로 잘릴 때가 있다. 배정 모드처럼 본문을 저절로 내리지는
       않고(맨 위가 지금 다루는 명단이다), 아직 볼 것이 남았을 때만 버튼을 띄워 사용자가 직접 내리게 한다. */

    /** 본문 아래에 아직 볼 내용이 남았는지 — 스크롤 때 · 내용이 바뀔 때 다시 잰다 */
    syncWardMoveScrollHint(el: HTMLElement | null | undefined) {
      if (!el || this.wardAssign.mode !== "move") {
        this.wardMoveMoreBelow = false;
        return;
      }
      // 8px 여유 — 끝까지 내렸는데 소수점 때문에 바닥으로 안 쳐지는 것을 막는다
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= 8;
      // 바닥에 닿으면 「봤다」로 친다. 잘릴 내용이 아예 없으면(짧은 내용) 연 순간 바로 켜진다.
      // 한 번 켜지면 다시 올려도 꺼지지 않는다 — 다시 꺼지는 건 내용이 바뀔 때뿐이다(resetWardMoveReview).
      if (atBottom) {
        this.wardMoveReviewed = true;
      }
      // 버튼은 「아직 한 번도 안 내려봤을 때」만 띄운다. 직접 내렸든 버튼으로 내렸든 한 번 움직이면
      // 다시 나오지 않는다(2026-09-23 사용자 결정) — 같은 자리에 버튼이 다시 떠 손을 막는 일이 없다.
      this.wardMoveMoreBelow = !(atBottom || this.wardMoveScrolled);
    },

    /** 본문 스크롤 — 한 번이라도 움직이면 플로팅 버튼은 이 팝업이 닫힐 때까지 다시 뜨지 않는다 */
    onWardMoveScroll(el: HTMLElement | null | undefined) {
      if (el && this.wardAssign.mode === "move" && el.scrollTop > 0) {
        this.wardMoveScrolled = true;
      }
      this.syncWardMoveScrollHint(el);
    },

    /** 내용이 바뀌면(사람 · 옮길 병동 · 옵션) 다시 보게 한다 — 바뀐 결과를 못 본 채로 옮기지 않도록 */
    resetWardMoveReview(el: HTMLElement | null | undefined) {
      this.wardMoveReviewed = false;
      this.syncWardMoveScrollHint(el);
    },

    /** 버튼을 누르면 본문 끝(미리보기)까지 내리고 버튼은 사라진다 */
    scrollWardMoveToEnd(el: HTMLElement | null | undefined) {
      this.wardMoveScrolled = true;
      this.wardMoveMoreBelow = false;
      this.wardMoveReviewed = true;
      el?.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    },

    /** 병동 배정 해제 — 확인 뒤 끝내고, 명단 위에 5초 동안 되돌리기 줄을 남긴다 */
    async unassignFromWard(wardId: string, employeeIds: string[]) {
      const ids = employeeIds.filter(Boolean);
      if (!wardId || ids.length === 0) {
        return;
      }
      const wardName = this.wardNameOf(wardId);
      const names = this.wardPeopleNames(ids);
      const who = ids.length === 1 ? `${names[0]}님을` : `${ids.length}명을`;
      const heads = this.wardHeadNurseNames(wardId, ids);
      const lines = [
        `${who} ${wardName} 배정에서 해제할까요?`,
        "이 병동 배정에서만 빠져요. 소속 팀과 근무(근태 기준)는 그대로 두고, 지난 근무표 기록도 지워지지 않아요.",
      ];
      if (heads.length > 0) {
        lines.push(
          `${heads.join(" · ")}님은 이 병동의 수간호사(책임자)예요 — 해제하면 책임자가 없어져요.`,
        );
      }
      const ok = await askConfirm({
        message: lines.join("\n"),
        confirmLabel: "배정 해제",
        tone: "danger",
      });
      if (!ok) {
        return;
      }
      try {
        const { results } = await this.sdk.nurseScheduler.unassignEmployeesFromWard({
          wardId,
          employeeIds: ids,
        });
        const done = results.filter((r) => r.ok);
        const failed = results.filter((r) => !r.ok);
        window.dispatchEvent(new CustomEvent("bulk-member-teams-applied"));
        await Promise.all([this.loadUnassignedMembers(), this.loadTeams()]);
        if (failed.length > 0) {
          showAlert({
            title: "일부만 해제했어요",
            message: `${results.length}명 중 ${done.length}명을 해제했어요. ${failed.length}명은 해제하지 못했어요.`,
            details: failed.map((r) => `${r.name} — ${r.message ?? ""}`),
          });
        }
        const profileIds = done.map((r) => r.profileId).filter((id): id is string => !!id);
        if (profileIds.length > 0) {
          this.showWardUndo(
            profileIds,
            done.length === 1
              ? `${done[0].name}님을 ${wardName} 배정에서 해제했어요.`
              : `${done.length}명을 ${wardName} 배정에서 해제했어요.`,
          );
        }
      } catch (err) {
        const code = (err as { data?: { code?: string } } | null)?.data?.code;
        if (err instanceof Error && (code === "BAD_REQUEST" || code === "FORBIDDEN")) {
          showAlert(err.message);
        } else {
          this.showCommonError();
        }
      }
    },

    /* ── 직원 카드 · 교차 이동(2026-09-23 P6) ─────────────────────────────────
       카드 자체는 공용(@hereby/components employee-card — 모든 앱 목록 표의 이름 칸).
       admin 은 카드에 네 동작을 단다: 직원 정보 수정 · 그 팀 / 병동 / 스케줄러로 건너뛰기.
       건너뛴 화면에서는 그 사람 줄을 강조한다 — LNB 를 다시 누르고 다시 검색하지 않게 하려는 것. */

    /** 카드 동작 등록 — init() 에서 한 번 */
    registerEmployeeCard() {
      registerEmployeeCardActions({
        edit: (employeeId) => this.openMemberEmployeeEdit(employeeId),
        goTeam: (teamId, employeeId) => this.goToEmployeeTeam(teamId, employeeId),
        goWard: (wardId, employeeId) => this.goToEmployeeWard(wardId, employeeId),
        goScheduler: (wardId, employeeId) => this.goToEmployeeScheduler(wardId, employeeId),
      });
    },

    /** 도착 화면에서 그 줄을 잠깐 강조한다 — 2초 뒤 은은한 선택 상태만 남기고 표시는 걷는다 */
    focusEmployeeRow(employeeId: string) {
      // 강조를 먼저 켠다 — 목록의 「다른 페이지면 그 페이지로」 효과가 이 값을 보고 돈다
      this.focusEmployeeId = employeeId;
      if (focusEmployeeTimer) {
        clearTimeout(focusEmployeeTimer);
      }
      if (focusScrollTimer) {
        clearTimeout(focusScrollTimer);
      }
      // 목록이 다 그려진 **뒤에** 한 번만 스크롤한다. 바로 스크롤하면 뒤이은 재렌더가 위치를 되돌려
      // 덜컹거리다 제자리로 오고, 명단을 아직 불러오는 중이면 그 줄이 없어 아무 데도 못 간다(2026-09-23 지적).
      // 그래서 줄이 나타날 때까지 짧게 기다렸다가, 나타나면 그때 한 번 움직인다.
      let tries = 0;
      const scrollWhenReady = () => {
        // 탭이 x-show 로 둘 다 남아 있어 같은 직원 줄이 여럿이다 — 화면에 그려진 줄만 고른다
        const row = [...document.querySelectorAll(`tr[data-id="${employeeId}"]`)].find(
          (el) => el.getBoundingClientRect().height > 0,
        );
        if (row) {
          focusScrollTimer = undefined;
          row.scrollIntoView({ block: "center", behavior: "smooth" });
          return;
        }
        tries += 1;
        focusScrollTimer = tries < 16 ? setTimeout(scrollWhenReady, 150) : undefined;
      };
      focusScrollTimer = setTimeout(scrollWhenReady, 300);
      // 5초 — 건너뛴 것을 알리는 신호가 이 표시뿐이라, 눈이 표를 훑을 시간을 준다(2026-09-23 사용자 요청으로 늘림)
      focusEmployeeTimer = setTimeout(() => {
        this.focusEmployeeId = "";
        focusEmployeeTimer = undefined;
      }, 5000);
    },

    /** 카드 → 병동 관리의 그 병동 인력 탭, 그 사람 줄 강조 */
    goToEmployeeWard(wardId: string, employeeId: string) {
      this.wardDetailTab = "people";
      this.selectWard(wardId);
      this.switchTab("wards");
      this.focusEmployeeRow(employeeId);
    },

    /** 카드 → 팀 관리의 그 팀, 그 사람 줄 강조 */
    goToEmployeeTeam(teamId: string, employeeId: string) {
      this.selectOrgTeam(teamId);
      this.switchTab("organization");
      this.focusEmployeeRow(employeeId);
    },

    /** 카드 → 스케줄러의 그 병동, 그 사람 줄 강조.
     *  스케줄러는 격리 컴포넌트라 상태를 직접 못 넘긴다 — 해시에 병동·기간을 싣고
     *  강조할 사람만 sessionStorage 로 넘긴다(스케줄러가 인력을 읽은 뒤 한 번 꺼내 쓰고 지운다).
     *  기간은 오늘이 속한 주기 — 스케줄러가 해시의 년·월로 그 주기를 연다. */
    goToEmployeeScheduler(wardId: string, employeeId: string) {
      try {
        sessionStorage.setItem("admin:scheduler:focusEmployee", employeeId);
      } catch {
        // sessionStorage 를 못 쓰는 환경(사생활 보호 창 등) — 강조만 빠지고 이동은 된다
      }
      this.goToWardScheduler(wardId);
    },

    /** 스케줄러의 그 병동, 오늘이 속한 달 — 해시(#scheduler/<wardId>:<yyyy>-<m>)로 병동·월을 미리 고른다 */
    goToWardScheduler(wardId: string) {
      const today = Alpine.store("hereby").todayLocal() as string;
      const [y, m] = today.split("-");
      const router = Alpine.store("router");
      router.navigateTab("scheduler", `${wardId}:${y}-${Number(m)}`);
    },

    /** 병동 이름 — 목록에 없으면 빈 문자열 */
    wardNameOf(wardId: string): string {
      const ward = (this.wardList as Record<string, unknown>[]).find((w) => w.id === wardId);
      return String(ward?.name || "병동");
    },

    /** 고른 직원의 이름 — 전체 명단에서 찾는다(없으면 건너뛴다) */
    wardPeopleNames(employeeIds: string[]): string[] {
      const byId = new Map(
        (this.allMembers as Record<string, unknown>[]).map((m) => [
          String(m.employeeId),
          String(m.employeeName || ""),
        ]),
      );
      return employeeIds.map((id) => byId.get(id) || "").filter(Boolean);
    },

    /** 고른 사람 중 이 병동의 수간호사(책임자) — 해제 확인에서 따로 알린다 */
    wardHeadNurseNames(wardId: string, employeeIds: string[]): string[] {
      const heads = new Set(this.wardHeadIdsOf(wardId));
      return this.wardPeopleNames(employeeIds.filter((id) => heads.has(id)));
    },

    /** 되돌리기 줄 띄우기 — 5초 뒤 사라진다(그 뒤에도 「직원 배정」으로 다시 넣을 수 있다) */
    showWardUndo(profileIds: string[], text: string) {
      if (wardUndoTimer) {
        clearTimeout(wardUndoTimer);
      }
      this.wardUndo = { profileIds, text };
      wardUndoTimer = setTimeout(() => {
        this.wardUndo = null;
        wardUndoTimer = undefined;
      }, 5000);
    },

    dismissWardUndo() {
      if (wardUndoTimer) {
        clearTimeout(wardUndoTimer);
        wardUndoTimer = undefined;
      }
      this.wardUndo = null;
    },

    /** 되돌리기 — 방금 끝낸 배정을 그대로 되살린다(스케줄러 · 교대 형태 · 책임자 표시가 함께 돌아온다) */
    async undoWardUnassign() {
      const pending = this.wardUndo;
      if (!pending) {
        return;
      }
      this.dismissWardUndo();
      try {
        const { restored } = await this.sdk.nurseScheduler.restoreWardAssignments({
          profileIds: pending.profileIds,
        });
        await Promise.all([this.loadUnassignedMembers(), this.loadTeams()]);
        this.notify(
          restored === pending.profileIds.length
            ? "배정을 되돌렸어요"
            : `${restored}명만 되돌렸어요 — 나머지는 그사이 다시 배정됐어요`,
        );
      } catch {
        this.showCommonError();
      }
    },

    async openAddMember(teamId: string) {
      this.addMemberTeamId = teamId;
      this.addMemberSearch = "";
      this.addMemberJobFilter = [];
      this.addMemberTeamFilter = [];
      this.addMemberSelectedIds = [];
      this.addMemberEmployees = [];
      // 매 오픈마다 기본 정렬 상태로 리셋 (입사일 오름차순).
      this.addMemberSort = { by: "hireDate", order: "asc" };
      this.addMemberLoading = true;
      this.showAddMember = true;
      try {
        type EmployeeRow = Awaited<
          ReturnType<HerebyClient["employee"]["getEmployees"]>
        >["items"][number];
        const items = await fetchAllPages<EmployeeRow>(
          (page, limit) => this.sdk.employee.getEmployees({ page, limit }),
          1000,
        );
        // 팀 멤버 추가는 재직만 본다(퇴사자 · 휴직자는 서버가 막거나 팀 소속 대상이 아니다)
        this.addMemberEmployees = items
          .filter((e) => (e.status as string) === "ACTIVE")
          .map((e) => ({
            ...e,
            fullName: `${e.lastName || ""}${e.firstName || ""}`.trim() || e.email,
          }));
      } catch {
        this.addMemberEmployees = [];
      } finally {
        this.addMemberLoading = false;
      }
    },

    /** 검색어(이름/사번/이메일)로 거른 뒤 헤더 정렬 상태로 정렬한 후보 — 실시간 필터.
     *  by === "" 이면 클라이언트 정렬을 스킵(API 응답 순서 유지). */
    get addMemberFiltered(): Record<string, unknown>[] {
      const q = this.addMemberSearch.trim().toLowerCase();
      // 직무 · 팀 필터를 먼저 건 뒤 검색어로 거른다
      const base = this.addMemberEmployees.filter((e) => this.addMemberMatchesFilters(e));
      const filtered = q
        ? base.filter((e) => {
            const name = ((e.fullName as string) || "").toLowerCase();
            const email = ((e.email as string) || "").toLowerCase();
            const num = ((e.employeeNumber as string) || "").toLowerCase();
            const job = ((e.jobDuty as string) || "").toLowerCase();
            return name.includes(q) || email.includes(q) || num.includes(q) || job.includes(q);
          })
        : base;
      const by = this.addMemberSort.by;
      if (!by) {
        return this.addMemberSelectableFirst(filtered);
      }
      const dir = this.addMemberSort.order === "asc" ? 1 : -1;
      filtered.sort((a, b) => {
        // 팀은 배열이므로 대표 문자열(첫 팀명)로 정렬.
        const readCell = (r: Record<string, unknown>) => {
          if (by === "teamName") {
            const tn = r.teamNames as string[] | undefined;
            return tn && tn.length > 0 ? tn[0] : "";
          }
          return r[by];
        };
        const av = readCell(a);
        const bv = readCell(b);
        // 값이 비어 있는 행은 항상 하단(정렬 방향과 무관, 사용성 유지).
        const aEmpty = av === null || av === undefined || av === "";
        const bEmpty = bv === null || bv === undefined || bv === "";
        if (aEmpty && !bEmpty) {
          return 1;
        }
        if (!aEmpty && bEmpty) {
          return -1;
        }
        if (aEmpty && bEmpty) {
          return 0;
        }
        return String(av).localeCompare(String(bv)) * dir;
      });
      return this.addMemberSelectableFirst(filtered);
    },

    /** 헤더 클릭 정렬 토글 — asc → desc → none → asc 로 순환.
     *  다른 컬럼을 처음 눌렀을 때는 asc 로 시작. */
    toggleAddMemberSort(
      col: "" | "hireDate" | "employeeNumber" | "fullName" | "teamName" | "jobDuty",
    ) {
      if (!col) {
        return;
      }
      if (this.addMemberSort.by !== col) {
        this.addMemberSort = { by: col, order: "asc" };
        return;
      }
      if (this.addMemberSort.order === "asc") {
        this.addMemberSort = { by: col, order: "desc" };
      } else {
        // desc 상태에서 한 번 더 클릭 → 정렬 해제(none).
        this.addMemberSort = { by: "", order: "asc" };
      }
    },
    /** 직원이 이미 현재 팀(addMemberTeamId)의 멤버인지 — memberships.teamId로 판정 */
    addMemberIsExisting(emp: Record<string, unknown>): boolean {
      const memberships = (emp.memberships as { teamId?: string }[] | undefined) || [];
      return memberships.some((m) => m.teamId === this.addMemberTeamId);
    },
    /** 직무 · 팀 필터에 맞는 직원인지 — 둘 다 비어 있으면 모두 통과. 팀은 지금 소속 중 하나라도 고른 팀이면 통과 */
    addMemberMatchesFilters(emp: Record<string, unknown>): boolean {
      const jobs = this.addMemberJobFilter;
      if (jobs.length > 0 && !jobs.includes((emp.jobDuty as string) || "__unset")) {
        return false;
      }
      const picked = this.addMemberTeamFilter;
      if (picked.length === 0) {
        return true;
      }
      const teamIds = ((emp.memberships as { teamId?: string }[] | undefined) || [])
        .map((m) => m.teamId)
        .filter((id): id is string => !!id);
      return teamIds.length === 0
        ? picked.includes("__none")
        : teamIds.some((id) => picked.includes(id));
    },
    /** 직무 필터 선택지 — 후보에게 있는 직무(가나다순), 직무가 빈 직원이 있으면 「미입력」을 맨 뒤에 */
    get addMemberJobOptions(): string[] {
      const set = new Set(this.addMemberEmployees.map((e) => (e.jobDuty as string) || "__unset"));
      return [...set].sort(
        (a, b) => Number(a === "__unset") - Number(b === "__unset") || a.localeCompare(b, "ko"),
      );
    },
    /** 직무 필터를 보일지 — 후보에게 입력된 직무가 하나도 없으면(선택지가 「미입력」뿐이면) 골라도 결과가
     *  전체와 같아 의미가 없다. 그럴 때는 드롭다운을 감춘다. 팀 필터도 같은 규칙. */
    get addMemberShowJobFilter(): boolean {
      return this.addMemberJobOptions.some((j) => j !== "__unset");
    },
    get addMemberShowTeamFilter(): boolean {
      return this.addMemberTeamOptions.some((o) => o.id !== "__none");
    },
    /** 팀 필터 선택지 — 후보가 지금 소속된 팀(이름순, 같은 이름이면 「상위 팀 > 이름」),
     *  소속 팀이 없는 직원이 있으면 「소속 없음」을 맨 뒤에 */
    get addMemberTeamOptions(): { id: string; label: string }[] {
      const names = new Map<string, string>();
      let hasNone = false;
      for (const e of this.addMemberEmployees) {
        const memberships =
          (e.memberships as { teamId?: string; teamName?: string | null }[] | undefined) || [];
        if (memberships.length === 0) {
          hasNone = true;
        }
        for (const m of memberships) {
          if (m.teamId) {
            names.set(m.teamId, m.teamName || "");
          }
        }
      }
      const options = [...names].map(([id, name]) => ({
        id,
        label: roleScopeTeamLabel(this.allTeams, id) || name,
      }));
      options.sort((a, b) => a.label.localeCompare(b.label, "ko"));
      return hasNone ? [...options, { id: "__none", label: "소속 없음" }] : options;
    },
    /** 고를 수 없는 행 — 이미 이 팀의 팀원인 경우 */
    addMemberRowLocked(emp: Record<string, unknown>): boolean {
      return this.addMemberIsExisting(emp);
    },
    /** 고를 수 있는 직원을 위로 — 이미 소속된 행은 정렬 순서를 지킨 채 아래로 모은다 */
    addMemberSelectableFirst(list: Record<string, unknown>[]): Record<string, unknown>[] {
      const open = list.filter((e) => !this.addMemberRowLocked(e));
      return open.length === list.length
        ? list
        : [...open, ...list.filter((e) => this.addMemberRowLocked(e))];
    },
    /** 아직 이 팀에 없어 실제 추가 가능한 직원 수(검색 무관 전체 기준). */
    get addMemberRegisterableCount(): number {
      return this.addMemberEmployees.filter((e) => !this.addMemberRowLocked(e)).length;
    },
    /** 현재 검색 결과 중 추가 가능한(미소속) 직원 — 모두 선택 대상. */
    get addMemberSelectableFiltered(): Record<string, unknown>[] {
      return this.addMemberFiltered.filter((e) => !this.addMemberRowLocked(e));
    },
    /** 모두 선택 체크박스 상태 — 선택 가능한 직원이 있고 전부 선택됐을 때만 true. */
    get addMemberAllSelected(): boolean {
      const selectable = this.addMemberSelectableFiltered;
      return (
        selectable.length > 0 &&
        selectable.every((e) => this.addMemberSelectedIds.includes(e.id as string))
      );
    },
    toggleAddMemberSelect(id: string) {
      const i = this.addMemberSelectedIds.indexOf(id);
      if (i === -1) {
        this.addMemberSelectedIds.push(id);
      } else {
        this.addMemberSelectedIds.splice(i, 1);
      }
    },
    /** 모두 선택 ↔ 모두 해제 (검색 결과 내 미소속 직원 기준). */
    toggleAddMemberSelectAll() {
      const selectable = this.addMemberSelectableFiltered.map((e) => e.id as string);
      if (this.addMemberAllSelected) {
        const drop = new Set(selectable);
        this.addMemberSelectedIds = this.addMemberSelectedIds.filter((id) => !drop.has(id));
      } else {
        this.addMemberSelectedIds = Array.from(
          new Set([...this.addMemberSelectedIds, ...selectable]),
        );
      }
    },

    /** 특정 팀의 구성원 캐시를 서버에서 다시 불러온다. 위저드로 신규 직원을 그 팀에
     *  배정한 직후 목록에 즉시 반영하기 위해 사용(팀 멤버 추가의 재조회와 동일 사유). */
    async refreshTeamComposition(teamId: string) {
      if (!teamId) {
        return;
      }
      // 로스터를 받았으면 팀별 명단은 거기서 파생된다 — 같은 틱의 loadUnassignedMembers 와 한 요청으로 합쳐진다
      if (this.rosterLoaded) {
        await this.loadUnassignedMembers();
        return;
      }
      try {
        const result = await this.sdk.team.getComposition(teamId);
        this.teamMembers[teamId] = result?.members || [];
      } catch {
        delete this.teamMembers[teamId];
      }
    },

    /** 이번 세션 '신규' 표시 대상에 직원 id들을 더한다(중복 제거·빈값 무시).
     *  배열을 재할당해 Alpine 반응성을 확실히 트리거한다. */
    markRecentlyAdded(empIds: string[]) {
      const add = (empIds || []).filter(Boolean);
      if (add.length === 0) {
        return;
      }
      this.recentlyAddedEmpIds = Array.from(new Set([...this.recentlyAddedEmpIds, ...add]));
    },

    /** 한 명 추가 — 팀 소속만 만든다. 병동 연결 팀이면 서버가 병동 배정도 함께 만들고,
     *  스케줄러가 여럿인 병동의 기본 스케줄러로 들어갔으면 그 정보를 돌려준다(saveAddMember 가 모아 알린다). */
    async addOneMember(employeeId: string): Promise<WardSchedulerNote | undefined> {
      const joined = await this.sdk.team.addMember({
        teamId: this.addMemberTeamId,
        employeeId,
      });
      return joined.wardAssignment.scheduler;
    },

    async saveAddMember() {
      const ids = [...this.addMemberSelectedIds];
      if (ids.length === 0 || this.addMemberSaving) {
        return;
      }
      this.addMemberSaving = true;
      // 일괄 추가는 검증된 단일 addMember 를 인원수만큼 호출한다 — 단톡방 자동참여·
      // 중복(이미 멤버) 처리·소프트삭제 재활성화가 모두 보존된다. (bulkMemberOperation
      // ADD 핸들러는 이 처리들이 빠져 있어 사용하지 않음.) 호출은 같은 틱에 모두
      // 보내 httpBatchLink 가 10건 단위로 묶어 병렬 전송하게 한다 — 인원수만큼
      // 직렬 왕복하지 않는다. 부분 실패는 모아서 안내.
      const failed: string[] = [];
      const addedIds: string[] = [];
      const schedulerNotes: (WardSchedulerNote | undefined)[] = [];
      const results = await Promise.allSettled(
        ids.map((employeeId) => this.addOneMember(employeeId)),
      );
      results.forEach((r, i) => {
        const employeeId = ids[i];
        if (r.status === "fulfilled") {
          addedIds.push(employeeId);
          schedulerNotes.push(r.value);
        } else {
          const emp = this.addMemberEmployees.find((e) => e.id === employeeId);
          failed.push((emp?.fullName as string) || employeeId);
        }
      });
      // 방금 추가된 직원에 세션 '신규' 라벨 부여 (워크스페이스·팀 목록 모두).
      this.markRecentlyAdded(addedIds);
      // 추가한 팀의 composition 재조회와 팀 트리·직원 목록 갱신을 한 묶음으로.
      await Promise.all([
        this.refreshTeamComposition(this.addMemberTeamId),
        this.loadTeams(),
        this.loadEmployees(),
      ]);
      this.addMemberSaving = false;
      this.showAddMember = false;
      const added = ids.length - failed.length;
      if (failed.length > 0) {
        this.notify(
          `${added}명 추가 완료, ${failed.length}명 실패: ${failed.join(", ")}`,
          added > 0 ? "info" : "error",
        );
      } else {
        this.notify(`${added}명을 팀에 추가했습니다`);
      }
      this.notifyWardScheduler(schedulerNotes);
    },

    async removeMember(teamId: string, memberId: string) {
      this.confirmMessage = this._t("admin.confirm_remove_member");
      this.confirmAction = async () => {
        try {
          await this.sdk.team.removeMember(teamId, memberId, "관리자 제거");
          // 팀 관리 탭 전체 로스터(allMembers·미소속)는 뒤에서 갱신 — 인라인 × 제거 반영.
          this.loadUnassignedMembers();
          // composition 재조회(saveAddMember 와 동일 사유)·팀 트리·직원 목록을 한 묶음으로.
          await Promise.all([
            this.refreshTeamComposition(teamId),
            this.loadTeams(),
            this.loadEmployees(),
          ]);
          this.notify("팀원을 제외했습니다");
        } catch (err) {
          showError(
            `${this._t("admin.delete_failed")}: ${err instanceof Error ? err.message : String(err)}`,
          );
        }
      };
      this.showConfirm = true;
    },

    /**
     * 선택 툴바의 행 id → 그 행이 실제로 가리키는 멤버십(팀 + 멤버십 id + 직원).
     *
     * 팀 명단의 「하위 조직 멤버 포함」 뷰(하위 팀이 있는 팀에서는 기본값)는 고른 팀이 아니라
     * 하위 팀의 멤버십 행을 섞어 보여 준다. 그래서 화면에서 고른 팀 id 를 모든 행에 그대로 쓰면
     * 서버가 「팀원을 찾을 수 없어요.」로 거절한다(CS-OKBEST-260917-0004).
     * 팀별 컴포지션 캐시(teamMembers)에서 행 id 를 찾아 그 행이 실제로 속한 팀을 돌려주고,
     * 캐시에 없는 id 는 org 스코프(workspace 전체 구성원) 행이라 곧 employeeId 다(팀 멤버십 없음).
     */
    resolveMemberRefs(
      memberIds: string[],
    ): { memberId: string; teamId: string; employeeId: string; row: Record<string, unknown> }[] {
      type MemberRef = {
        memberId: string;
        teamId: string;
        employeeId: string;
        row: Record<string, unknown>;
      };
      const wanted = new Set(memberIds);
      const found = new Map<string, MemberRef>();
      for (const [teamId, list] of Object.entries(this.teamMembers)) {
        for (const m of list || []) {
          const id = m.id as string;
          if (wanted.has(id) && !found.has(id)) {
            found.set(id, {
              memberId: id,
              teamId,
              employeeId: (m.employeeId as string) || "",
              row: m,
            });
          }
        }
      }
      const roster = new Map(
        (this.allMembers as Record<string, unknown>[]).map((m) => [m.id as string, m]),
      );
      return memberIds.map(
        (id) =>
          found.get(id) ?? { memberId: id, teamId: "", employeeId: id, row: roster.get(id) ?? {} },
      );
    },

    /** resolveMemberRefs 의 직원 id 만(중복 제거) — 상태·근무처럼 직원 단위로 도는 일괄작업용. */
    resolveMemberEmployeeIds(memberIds: string[]): string[] {
      return [
        ...new Set(
          this.resolveMemberRefs(memberIds)
            .map((ref) => ref.employeeId)
            .filter(Boolean),
        ),
      ];
    },

    async bulkRemoveMembers(teamId: string, memberIds: string[]) {
      if (!memberIds.length) {
        return;
      }
      this.confirmMessage = `선택한 ${memberIds.length}명을 팀에서 제거하시겠습니까?`;
      this.confirmAction = async () => {
        // 행마다 실제 소속 팀으로 — 하위 조직 포함 뷰의 행은 고른 팀의 멤버십이 아니다.
        const refs = this.resolveMemberRefs(memberIds);
        const targets = refs.map((ref) => ({ ...ref, teamId: ref.teamId || teamId }));
        // 인원수만큼 직렬 왕복하지 않고 같은 틱에 보내 httpBatchLink 로 묶는다.
        // 하나가 실패해도 나머지는 제외하고, 실패한 수를 알린다(중간에 끊기면 절반만 반영된다).
        const results = await Promise.allSettled(
          targets.map((ref) =>
            this.sdk.team.removeMember(ref.teamId, ref.memberId, "관리자 일괄 제거"),
          ),
        );
        this.loadUnassignedMembers();
        // composition 재조회·팀 트리·직원 목록을 한 묶음으로 (펼침 상태는 유지).
        await Promise.all([
          this.refreshOrgTeamCompositions([...new Set(targets.map((ref) => ref.teamId))]),
          this.loadTeams(),
          this.loadEmployees(),
        ]);
        const failed = results.filter((r) => r.status === "rejected") as PromiseRejectedResult[];
        if (failed.length > 0) {
          showError(
            `${this._t("admin.delete_failed")}: ${memberIds.length}명 중 ${failed.length}명을 제외하지 못했어요. ${this._errMessage(failed[0].reason)}`,
          );
        } else {
          this.notify(`${memberIds.length}명을 팀에서 제외했습니다`);
        }
      };
      this.showConfirm = true;
    },

    /** 선택 툴바 [상태 변경] — memberIds(팀 컴포지션 행 id 또는 org 스코프 employeeId)를
     *  employeeId 로 해석해 상태를 일괄 변경한다.
     *
     *  팀 명단의 행은 teamMembers[팀 id] 컴포지션 캐시에서 그리고, 그 행의 status 는 서버가
     *  employee.status 를 복사해 준 값이다. 예전에는 직원 목록·전체 로스터만 다시 읽어서
     *  팀 화면의 상태 배지·헤더 인원이 새로고침 전까지 옛 값에 머물렀다(CS-OKBEST-260917-0007).
     *  행이 실제로 속한 팀(하위 조직 포함 뷰면 하위 팀)의 컴포지션까지 같이 재조회한다. */
    async bulkUpdateMemberStatus(memberIds: string[], status: string) {
      if (!(memberIds.length && status)) {
        return;
      }
      const refs = this.resolveMemberRefs(memberIds);
      const employeeIds = [...new Set(refs.map((ref) => ref.employeeId).filter(Boolean))];
      if (employeeIds.length === 0) {
        return;
      }
      // 같은 틱에 보내 httpBatchLink 로 묶고, 하나가 실패해도 나머지는 저장한다
      // (직렬 await 는 첫 실패에서 끊겨 일부만 반영된 채로 끝났다).
      const results = await Promise.allSettled(
        employeeIds.map((id) => this.sdk.employee.updateEmployee({ id, status })),
      );
      await Promise.all([
        this.loadEmployees(),
        this.loadUnassignedMembers(),
        this.refreshOrgTeamCompositions([...new Set(refs.map((ref) => ref.teamId))]),
      ]);
      const failed = results.filter((r) => r.status === "rejected") as PromiseRejectedResult[];
      if (failed.length > 0) {
        showError(
          `${this._t("admin.status_change_failed")}: ${employeeIds.length}명 중 ${failed.length}명의 상태를 바꾸지 못했어요. ${this._errMessage(failed[0].reason)}`,
        );
      }
    },

    /** 선택 툴바 [역할 변경] — 해당 팀 멤버십 단위(team.updateMember)로만 역할을 바꾼다.
     *  역할은 소속마다 따로라 행이 실제로 속한 팀으로 보낸다(하위 조직 포함 뷰 대응). */
    async bulkAssignMemberRole(teamId: string, memberIds: string[], roleId: string) {
      if (!(memberIds.length && roleId)) {
        return;
      }
      const refs = this.resolveMemberRefs(memberIds);
      const targets = refs.map((ref) => ({ ...ref, teamId: ref.teamId || teamId }));
      // 같은 틱에 보내 httpBatchLink 로 묶고, 하나가 실패해도 나머지는 저장한다
      // (직렬 await 는 첫 실패에서 끊겨 일부만 반영된 채로 끝났다).
      const results = await Promise.allSettled(
        targets.map((ref) => this.sdk.team.updateMember(ref.teamId, ref.memberId, { roleId })),
      );
      await this.refreshOrgTeamCompositions([...new Set(targets.map((ref) => ref.teamId))]);
      const failed = results.filter((r) => r.status === "rejected") as PromiseRejectedResult[];
      if (failed.length > 0) {
        showError(
          `${this._t("admin.role_change_failed")}: ${memberIds.length}명 중 ${failed.length}명의 ${attachJosa(this._term("role"), "을/를")} 바꾸지 못했어요. ${this._errMessage(failed[0].reason)}`,
        );
      } else {
        this.notify(
          `${memberIds.length}명의 ${attachJosa(this._term("role"), "을/를")} 변경했습니다`,
        );
      }
    },

    /** 선택 툴바 「적용」의 팀 배정 줄 — 확인 모달에 무엇이 바뀌는지 적는다(근무 배정 줄과 짝). */
    bulkTeamAssignLabel(targetTeamId: string): string {
      const target = (this.teams as unknown as Record<string, unknown>[]).find(
        (t) => t.id === targetTeamId,
      );
      return (target?.name as string) || "선택한 팀";
    },

    /**
     * 선택 툴바 [팀 배정] — selectedMemberIds 를 employeeId 로 매핑한 뒤 target 팀에
     * 추가한다(기존 소속 유지·가산). org 스코프(workspace)는 소스가 전체 로스터.
     *
     * 확인 모달은 여기서 열지 않는다 — 이 함수는 「적용」의 confirmAction 안에서 불리고,
     * executeConfirm 이 액션을 await 한 뒤 _resetConfirm() 으로 슬롯을 비우기 때문에
     * 여기서 세운 중첩 확인 모달은 그려지기도 전에 지워졌다(=팀 배정이 아무 일도 안 했다).
     * 확인은 바깥 「일괄 적용」 모달이 이미 받는다(배정 대상 팀명도 그 문구에 들어간다).
     */
    async bulkAddTeamMembersByMemberIds(
      sourceTeamId: string,
      selectedMemberIds: string[],
      targetTeamId: string,
    ): Promise<void> {
      if (!(targetTeamId && selectedMemberIds.length)) {
        return;
      }
      // 하위 조직 포함 뷰의 행은 sourceTeamId 의 컴포지션에 없다 — 캐시 전체에서 해석한다
      // (예전에는 조용히 빠져 "N명 배정" 의 N 이 고른 수보다 적었다).
      const refs = this.resolveMemberRefs(selectedMemberIds);
      const empIds = [...new Set(refs.map((ref) => ref.employeeId).filter(Boolean))];
      if (empIds.length === 0) {
        return;
      }
      // 인원수만큼 직렬 왕복하지 않고 같은 틱에 보내 httpBatchLink 로 묶는다.
      // 개별 실패(이미 소속 등)는 무시하고 계속.
      await Promise.all(
        empIds.map((empId) =>
          this.sdk.team
            .addMember({ teamId: targetTeamId, employeeId: empId })
            .catch(() => undefined),
        ),
      );
      // 행이 실제로 속한 팀(하위 조직 포함 뷰면 하위 팀)의 컴포지션도 — 병동 연결 팀에 넣으면
      // 병동 배정이 생겨 그 행의 병동 열이 바뀐다.
      await Promise.all([
        this.loadEmployees(),
        this.loadTeams(),
        this.loadUnassignedMembers(),
        this.refreshOrgTeamCompositions([
          targetTeamId,
          sourceTeamId,
          ...refs.map((ref) => ref.teamId),
        ]),
      ]);
      this.notify(`${empIds.length}명을 팀에 배정했습니다`);
    },

    // 직원 계정(Account) 초대 — 이메일 보유 + 미연결 직원에게 초대 메일 발송
    async sendEmployeeAccountInvite(emp: Record<string, unknown>) {
      const empName =
        (emp.fullName as string) ||
        `${(emp.lastName as string) || ""}${(emp.firstName as string) || ""}` ||
        "직원";
      this.confirmMessage = `"${empName}" 님에게 계정 초대 메일을 보내시겠습니까?`;
      this.confirmAction = async () => {
        try {
          await this.sdk.employee.sendAccountInvite(emp.id as string);
          this.notify("초대 메일을 발송했습니다");
        } catch (err) {
          showError(`초대 발송 실패: ${err instanceof Error ? err.message : String(err)}`);
        }
      };
      this.showConfirm = true;
    },

    // 조직 세션 → account 세션 전환 후 워크스페이스 허브로 이동
    /* ── 권한 미리보기 (MED-713) ─────────────────────────────────────────
       시스템 관리자가 직원 권한으로 실제 화면을 본다. 서버(impersonation.start)가 시간 제한 세션을 발급하고,
       원래 토큰은 hereby_impersonation_return 에 보관해 배너 [종료]·만료 시 되돌린다. */
    impersonationTtlOptions: IMPERSONATION_TTL_OPTIONS,
    impersonationEscBound: false,
    impersonation: {
      open: false,
      loading: false,
      starting: false,
      target: null as Record<string, unknown> | null,
      preview: null as {
        fullName: string;
        employeeNumber: string;
        roles: { code: string; name: string }[];
        permissionCount: number;
        isSystemAdmin: boolean;
        canAccessAdmin: boolean;
        canAccessTeamManager?: boolean;
      } | null,
      ttl: IMPERSONATION_DEFAULT_TTL_MINUTES as number,
      reason: "",
      app: "employee",
    },

    /** 지금 세션이 조직 시스템 관리자의 권한 미리보기(ORG_DEBUG)인가 — 게이트·배너 분기 */
    isOrgDebugImpersonation(): boolean {
      return impersonationBannerState(Alpine.store("hereby").currentUser, true).visible;
    },

    /** admin 자신의 주소 — 운영은 /admin/, dev 는 / (BASE_URL). 복귀 URL 도 이걸로 만든다 */
    impersonationAdminUrl(hash = ""): string {
      const base = import.meta.env.BASE_URL.replace(/\/?$/, "/");
      return `${window.location.origin}${base}${hash}`;
    },

    /** 앱 진입 URL — 운영·dev 모두 같은 출처의 경로(admin dev 서버가 임베드 앱을 프록시한다) */
    impersonationAppUrl(app: string): string {
      if (app === "admin") {
        return this.impersonationAdminUrl();
      }
      const origin = window.location.origin;
      const paths: Record<string, string> = {
        employee: "/employee-app/",
        "team-manager": "/team-manager/",
        "hr-system": "/hr-system/",
      };
      return `${origin}${paths[app] ?? paths.employee}`;
    },

    /* ── 팀 이동 (MED-912) ──────────────────────────────────────────────────
       직원을 다른 팀으로 옮기는 한 단계 동작. 서버(teamMove.execute)가 직원마다 자동 승인된 인사발령
       문서를 만들어 발령일이 오늘이면 바로, 미래면 그날 반영한다(문서가 전보 이력). 직원 관리 행·선택
       툴바·팀 관리 명단이 같은 모달을 연다(team-move-open 이벤트 또는 openTeamMove). */
    teamMove: {
      open: false,
      saving: false,
      targets: [] as TeamMoveTarget[],
      fromTeamId: "",
      toTeamId: "",
      mode: "move" as "move" | "add",
      /** "__keep" = 기존 역할 유지 · "__none" = 역할 없이 · 그 밖은 역할 id */
      roleId: "__keep",
      effectiveDate: "",
      reason: "",
    },

    /** 직원 행(직원 관리·팀 관리 명단·전체 로스터 어느 꼴이든) → 모달 대상 */
    teamMoveTargetFrom(emp: Record<string, unknown>): TeamMoveTarget {
      const memberships = ((emp.memberships as Record<string, unknown>[] | undefined) ?? []).map(
        (m) => ({
          teamId: String(m.teamId ?? ""),
          teamName: String(m.teamName ?? ""),
          roleName: (m.roleName as string | null | undefined) ?? null,
          isPrimary: m.isPrimary === true,
        }),
      );
      return {
        id: String(emp.employeeId ?? emp.id ?? ""),
        name:
          (emp.fullName as string | undefined) ||
          (emp.employeeName as string | undefined) ||
          `${(emp.lastName as string | undefined) ?? ""}${(emp.firstName as string | undefined) ?? ""}`.trim() ||
          (emp.name as string | undefined) ||
          "",
        employeeNumber: (emp.employeeNumber as string | undefined) ?? "",
        memberships,
      };
    },

    openTeamMove(
      targets: Record<string, unknown>[] | null | undefined,
      opts: { fromTeamId?: string; toTeamId?: string; mode?: "move" | "add" } = {},
    ) {
      const rows = (targets ?? []).filter((t) => t && (t.employeeId || t.id));
      if (rows.length === 0) {
        return;
      }
      const list = rows.map((t) => this.teamMoveTargetFrom(t));
      const single = list.length === 1 ? list[0] : null;
      const primary = single?.memberships.find((m) => m.isPrimary) ?? single?.memberships[0];
      this.teamMove = {
        ...this.teamMove,
        open: true,
        saving: false,
        targets: list,
        fromTeamId: opts.fromTeamId ?? primary?.teamId ?? "",
        toTeamId: opts.toTeamId ?? "",
        mode: opts.mode ?? "move",
        roleId: "__keep",
        effectiveDate: Alpine.store("hereby").todayLocal(),
        reason: "",
      };
    },

    /** 팀 이동 버튼·드래그를 보일지 — 팀 구성 변경 권한(team:update) 또는 시스템 관리자 */
    canMoveMembers(): boolean {
      const store = Alpine.store("hereby");
      return !!(store.currentUser?.isSystemAdmin || store.hasPermission("team:update"));
    },

    /**
     * 팀 관리 명단 행 id(소속 기록 id 또는 전체 뷰의 직원 id) → 팀 이동 모달. 행 해석은 resolveMemberRefs 하나로.
     * fromTeamId 를 주면 그 팀에서 옮기고(팀 보기), 비우면 각자 주 소속에서 옮긴다(전체 뷰·하위 조직 포함 뷰).
     */
    openTeamMoveForMembers(rowIds: string[], fromTeamId: string, opts: { toTeamId?: string } = {}) {
      const refs = this.resolveMemberRefs(rowIds) as Array<{
        employeeId: string;
        teamId: string;
        row?: Record<string, unknown> | null;
      }>;
      const seen = new Set<string>();
      const targets: Record<string, unknown>[] = [];
      for (const ref of refs) {
        if (!ref.employeeId || seen.has(ref.employeeId)) {
          continue;
        }
        seen.add(ref.employeeId);
        const teamIds = (this.rosterTeamMap as Record<string, string[]>)[ref.employeeId] ?? [];
        const memberships = teamIds.map((teamId) => ({
          teamId,
          teamName:
            ((this.teams as unknown as Record<string, unknown>[]).find((t) => t.id === teamId)
              ?.name as string | undefined) ?? "",
          roleName: null,
          isPrimary: fromTeamId ? teamId === fromTeamId : teamIds.length === 1,
        }));
        targets.push({
          employeeId: ref.employeeId,
          employeeName: ref.row?.employeeName,
          employeeNumber: ref.row?.employeeNumber,
          memberships,
        });
      }
      this.openTeamMove(targets, {
        ...(fromTeamId ? { fromTeamId } : {}),
        ...(opts.toTeamId ? { toTeamId: opts.toTeamId } : {}),
      });
    },

    /* 팀원 드래그 → 트리 팀에 놓기(MED-912). 놓으면 팀 이동 모달이 대상 팀이 채워진 채 열려 확인 창 구실을 한다.
       선택된 행을 끌면 선택 전체가 함께 간다. */
    memberDrag: { ids: [] as string[], fromTeamId: "", overTeamId: "" },

    /** 행을 끌기 시작 — 선택 상태는 명단 x-data 의 로컬 값이라 HTML 이 넘겨 준다. 한 명이면 그 행의 실제 팀에서 옮긴다. */
    onMemberDragStart(
      event: DragEvent,
      member: { id: string; srcTeamId?: string; teamId?: string },
      selectedIds: string[] = [],
    ) {
      const ids = selectedIds.includes(member.id) ? [...selectedIds] : [member.id];
      this.memberDrag.ids = ids;
      this.memberDrag.fromTeamId = ids.length === 1 ? member.srcTeamId || member.teamId || "" : "";
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", ids.join(","));
      }
    },

    onMemberDragEnd() {
      this.memberDrag.ids = [];
      this.memberDrag.fromTeamId = "";
      this.memberDrag.overTeamId = "";
    },

    onMemberDragOverTeam(event: DragEvent, team: { id: string; isActive?: boolean }) {
      if (this.memberDrag.ids.length === 0 || team.isActive === false) {
        return;
      }
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = "move";
      }
      this.memberDrag.overTeamId = team.id;
    },

    onMemberDragLeaveTeam(team: { id: string }) {
      if (this.memberDrag.overTeamId === team.id) {
        this.memberDrag.overTeamId = "";
      }
    },

    onMemberDropOnTeam(team: { id: string; isActive?: boolean }) {
      const { ids, fromTeamId } = this.memberDrag;
      this.onMemberDragEnd();
      if (ids.length === 0 || team.isActive === false || (fromTeamId && fromTeamId === team.id)) {
        return;
      }
      this.openTeamMoveForMembers(ids, fromTeamId, { toTeamId: team.id });
    },

    closeTeamMove() {
      if (this.teamMove.saving) {
        return;
      }
      this.teamMove.open = false;
    },

    teamMoveTargetsLabel(): string {
      const t = this.teamMove.targets;
      if (t.length === 0) {
        return "";
      }
      return t.length === 1 ? t[0].name : `${t[0].name} 외 ${t.length - 1}명`;
    },

    teamMoveCurrentTeamsLabel(target: TeamMoveTarget): string {
      if (target.memberships.length === 0) {
        return "소속 없음";
      }
      return target.memberships
        .map(
          (m) =>
            `${m.teamName}${m.isPrimary ? " (주 소속)" : ""}${m.roleName ? ` · ${m.roleName}` : ""}`,
        )
        .join(", ");
    },

    teamMoveIsFuture(): boolean {
      const today = Alpine.store("hereby").todayLocal();
      return !!this.teamMove.effectiveDate && this.teamMove.effectiveDate > today;
    },

    teamMoveValid(): boolean {
      const m = this.teamMove;
      if (!(m.targets.length > 0 && m.toTeamId && m.effectiveDate)) {
        return false;
      }
      if (m.mode === "move" && m.targets.length === 1 && m.fromTeamId === m.toTeamId) {
        return false;
      }
      return true;
    },

    /** 이동 실행 — 결과는 적용/예약/건너뜀/실패 건수로 알리고 직원 목록·팀 명단·트리를 다시 읽는다 */
    async submitTeamMove() {
      if (!this.teamMoveValid() || this.teamMove.saving) {
        return;
      }
      const m = this.teamMove;
      m.saving = true;
      try {
        const single = m.targets.length === 1;
        const result = await this.sdk.team.moveMembers({
          items: m.targets.map((t) => ({
            employeeId: t.id,
            fromTeamId: single && m.mode === "move" ? m.fromTeamId || null : null,
          })),
          toTeamId: m.toTeamId,
          mode: m.mode,
          roleMode: m.roleId === "__keep" ? "keep" : m.roleId === "__none" ? "none" : "set",
          ...(m.roleId !== "__keep" && m.roleId !== "__none" ? { roleId: m.roleId } : {}),
          effectiveDate: m.effectiveDate,
          ...(m.reason ? { reason: m.reason } : {}),
        });
        const parts: string[] = [];
        if (result.applied > 0) {
          parts.push(`${result.applied}명 ${m.mode === "add" ? "추가" : "이동"}`);
        }
        if (result.scheduled > 0) {
          parts.push(
            `${result.scheduled}명 ${Alpine.store("hereby").formatDate(result.effectiveDate)} 예약`,
          );
        }
        if (result.skipped > 0) {
          parts.push(`${result.skipped}명 건너뜀(이미 소속)`);
        }
        if (result.failed > 0) {
          parts.push(`${result.failed}명 실패`);
        }
        const failed = result.results.find((r) => r.status === "failed");
        this.notify(
          `${parts.join(" · ") || "변경 없음"}${failed?.message ? ` — ${failed.message}` : ""}`,
        );
        m.open = false;
        this.selectedEmployees = [];
        const touchedTeams = [
          m.toTeamId,
          ...result.results.map((r) => r.fromTeamId).filter((id): id is string => !!id),
        ];
        await Promise.allSettled([
          this.loadEmployees(),
          this.loadUnassignedMembers(),
          this.loadTeams(),
          this.refreshOrgTeamCompositions(touchedTeams),
        ]);
        this.computeStats();
      } catch (err) {
        this.notify(err instanceof Error ? err.message : String(err));
      } finally {
        m.saving = false;
      }
    },

    /* ── 인사발령 품의 (MED-779) ────────────────────────────────────────────
       관리자가 대상 직원 명의로 발령 문서를 만든다(document.createHrAction). 승인은 문서 결재에서. */
    appointment: {
      open: false,
      saving: false,
      target: null as Record<string, unknown> | null,
      action: "transfer" as "transfer" | "add_membership" | "end_membership" | "role_change",
      effectiveDate: "",
      fromTeamId: "",
      teamId: "",
      roleId: "",
      reason: "",
    },

    appointmentDefaultFromTeam(): string {
      const ms =
        (this.appointment.target?.memberships as Record<string, unknown>[] | undefined) ?? [];
      const primary = ms.find((m) => m.isPrimary) ?? ms[0];
      return (primary?.teamId as string) ?? "";
    },

    appointmentCurrentTeamsLabel(): string {
      const ms =
        (this.appointment.target?.memberships as Record<string, unknown>[] | undefined) ?? [];
      if (ms.length === 0) {
        return "소속 없음";
      }
      return ms.map((m) => `${m.teamName ?? ""}${m.roleName ? ` · ${m.roleName}` : ""}`).join(", ");
    },

    openAppointment(emp: Record<string, unknown> | null) {
      if (!emp?.id) {
        return;
      }
      this.appointment = {
        ...this.appointment,
        open: true,
        saving: false,
        target: emp,
        action: "transfer",
        effectiveDate: Alpine.store("hereby").todayLocal(),
        fromTeamId: "",
        teamId: "",
        roleId: "",
        reason: "",
      };
      this.appointment.fromTeamId = this.appointmentDefaultFromTeam();
    },

    closeAppointment() {
      if (this.appointment.saving) {
        return;
      }
      this.appointment.open = false;
    },

    appointmentValid(): boolean {
      const a = this.appointment;
      if (!(a.target?.id && a.effectiveDate)) {
        return false;
      }
      switch (a.action) {
        case "transfer":
          return !!(a.fromTeamId && a.teamId && a.fromTeamId !== a.teamId);
        case "add_membership":
          return !!a.teamId;
        case "end_membership":
          return !!a.fromTeamId;
        case "role_change":
          return !!(a.fromTeamId && a.roleId);
        default:
          return false;
      }
    },

    /** 품의 올리기 — 이름 사본(팀·역할)을 함께 담아 품의서가 나중 이름 변경에 흔들리지 않게 한다 */
    async submitAppointment() {
      if (!this.appointmentValid() || this.appointment.saving) {
        return;
      }
      const a = this.appointment;
      const teamName = (id: string) =>
        (this.teams as unknown as Record<string, unknown>[]).find((t) => t.id === id)?.name as
          | string
          | undefined;
      const roleName = (id: string) =>
        (this.roles as unknown as Record<string, unknown>[]).find((r) => r.id === id)?.name as
          | string
          | undefined;
      const change: Record<string, unknown> = { action: a.action };
      if (a.action === "transfer") {
        Object.assign(change, {
          fromTeamId: a.fromTeamId,
          fromTeamName: teamName(a.fromTeamId),
          teamId: a.teamId,
          teamName: teamName(a.teamId),
        });
      } else if (a.action === "add_membership") {
        Object.assign(change, { teamId: a.teamId, teamName: teamName(a.teamId) });
      } else {
        Object.assign(change, { teamId: a.fromTeamId, teamName: teamName(a.fromTeamId) });
      }
      if (a.roleId && a.action !== "end_membership") {
        Object.assign(change, { roleId: a.roleId, roleName: roleName(a.roleId) });
      }
      this.appointment.saving = true;
      try {
        await this.sdk.document.createHrAction({
          employeeId: a.target?.id as string,
          hrAction: {
            kind: "hr_appointment",
            fields: {
              effectiveDate: a.effectiveDate,
              reason: a.reason.trim() || undefined,
              changes: [change as never],
            },
          },
          notes: a.reason.trim() || undefined,
        });
        this.appointment.open = false;
        this.notify("인사발령 품의를 올렸어요. 문서 결재에서 승인하면 시행일에 반영돼요");
      } catch (err) {
        showError(
          `발령 품의를 올릴 수 없어요: ${err instanceof Error ? err.message : String(err)}`,
        );
      } finally {
        this.appointment.saving = false;
      }
    },

    async openImpersonation(emp: Record<string, unknown> | null) {
      if (!emp?.id) {
        return;
      }
      // ESC 는 가장 위 창만 닫는다 — 직원 수정 패널 위에서 열리면 패널의 window ESC 리스너가 같이
      // 돌아 패널까지 닫혔다. 공용 ESC 층(registerEscLayer)이 캡처 단계에서 이 팝업만 닫고 전파를 끊는다.
      if (!this.impersonationEscBound) {
        this.impersonationEscBound = true;
        registerEscLayer(
          () =>
            this.impersonation.open
              ? (document
                  .querySelector<HTMLElement>('[data-testid="impersonation-modal"]')
                  ?.closest<HTMLElement>(".ds-modal-backdrop") ?? null)
              : null,
          () => this.closeImpersonation(),
        );
      }
      this.impersonation = {
        ...this.impersonation,
        open: true,
        loading: true,
        starting: false,
        target: emp,
        preview: null,
        ttl: IMPERSONATION_DEFAULT_TTL_MINUTES,
        reason: "",
        app: "employee",
      };
      try {
        this.impersonation.preview = await this.sdk.impersonation.preview(emp.id as string);
        // 시작 화면 기본값 = 그 직원이 실제로 쓰는 화면. 병원 설정에 들어오는 사람은 병원 설정,
        // 결재자(팀 관리 입장 가능)는 팀 관리, 그 밖에는 직원 앱 — 매번 바꾸지 않아도 되게.
        const preview = this.impersonation.preview;
        this.impersonation.app = preview?.canAccessAdmin
          ? "admin"
          : preview?.canAccessTeamManager
            ? "team-manager"
            : "employee";
      } catch (err) {
        this.impersonation.open = false;
        showError(
          `권한 미리보기를 열 수 없어요: ${err instanceof Error ? err.message : String(err)}`,
        );
      } finally {
        this.impersonation.loading = false;
      }
    },

    closeImpersonation() {
      if (this.impersonation.starting) {
        return;
      }
      this.impersonation.open = false;
    },

    /** [시작] — 세션 발급 → 원래 토큰 보관 → 새 토큰 → 고른 앱으로 */
    async startImpersonation() {
      const target = this.impersonation.target;
      if (!target?.id || this.impersonation.starting) {
        return;
      }
      if (this.impersonation.app === "admin" && !this.impersonation.preview?.canAccessAdmin) {
        this.impersonation.app = "employee";
      }
      this.impersonation.starting = true;
      try {
        const result = await this.sdk.impersonation.start({
          employeeId: target.id as string,
          ttlMinutes: this.impersonation.ttl as 30 | 60 | 120 | 240,
          reason: this.impersonation.reason.trim() || undefined,
        });
        const client = this.sdk as {
          getAccessToken?: () => string | undefined;
          getRefreshToken?: () => string | undefined;
          setTokens: (at: string, rt?: string) => void;
        };
        const currentAt =
          client.getAccessToken?.() ?? localStorage.getItem("hereby_access_token") ?? "";
        const currentRt =
          client.getRefreshToken?.() ?? localStorage.getItem("hereby_refresh_token") ?? null;
        const meta = result.impersonation;
        saveImpersonationReturn({
          accessToken: currentAt,
          refreshToken: currentRt,
          returnUrl: this.impersonationAdminUrl("#employees"),
          grantId: meta.grantId,
          expiresAt: meta.expiresAt ? new Date(meta.expiresAt).toISOString() : null,
          targetName: meta.targetName,
          actorName: meta.actorName,
        });
        client.setTokens(result.accessToken, result.refreshToken);
        // replace — 토큰을 바꾼 이 관리자 화면은 뒤로가기 대상으로 남기지 않는다(MED-913). 남으면 bfcache 가
        // 「시작 중…」 모달이 열린 옛 상태를 그대로 되살려 멈춘 것처럼 보인다(SDK 의 pageshow 가드가 2차 방어).
        window.location.replace(this.impersonationAppUrl(this.impersonation.app));
      } catch (err) {
        this.impersonation.starting = false;
        showError(
          `권한 미리보기를 시작할 수 없어요: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    },

    /** 접근 불가 화면의 [미리보기 종료] — 배너와 같은 복귀 규칙 */
    async endImpersonationFromGate() {
      try {
        await this.sdk.impersonation.end();
      } catch {
        // grant 는 TTL 로 자연 만료 — 복귀는 계속한다
      }
      const saved = readImpersonationReturn();
      const outcome = restoreImpersonationReturn(this.sdk, IMPERSONATION_ENDED_NOTICE);
      // 접근 권한 없음 화면은 이미 `/admin/#employees` 라 href 대입으로는 다시 읽지 않는다 — 헬퍼가 reload
      navigateAfterImpersonationExit(
        outcome.restored
          ? saved?.returnUrl || this.impersonationAdminUrl("#employees")
          : this.impersonationAdminUrl(),
      );
    },

    async switchWorkspace() {
      try {
        await this.sdk.account.toAccountSession();
        // 전체 리로드로 컴포넌트 상태를 초기화 — init()이 account 토큰을
        // 감지해 허브(/workspaces)로 라우팅한다.
        window.location.href = `${import.meta.env.BASE_URL.replace(/\/$/, "")}/workspaces`;
      } catch (err) {
        showError(`워크스페이스 전환 실패: ${err instanceof Error ? err.message : String(err)}`);
      }
    },

    async deleteEmployee(emp: Record<string, unknown>) {
      const empName =
        (emp.fullName as string) ||
        `${(emp.lastName as string) || ""}${(emp.firstName as string) || ""}` ||
        "직원";
      this.confirmMessage = `"${empName}" 직원을 삭제하시겠습니까?`;
      this.confirmAction = async () => {
        try {
          await this.sdk.employee.deleteEmployee(emp.id as string);
          await this.loadEmployees();
          this.computeStats();
          this.notify("직원을 삭제했습니다");
        } catch (err) {
          showError(
            `${this._t("admin.delete_failed")}: ${err instanceof Error ? err.message : String(err)}`,
          );
        }
      };
      this.showConfirm = true;
    },

    /* ──────────── role actions ──────────── */
    openCreateRole() {
      this.editingRole = null;
      this.roleForm = {
        name: "",
        code: "",
        description: "",
        isActive: true,
        departmentTeamIds: [],
        teamIds: [],
      };
      this.roleScopeMulti = { department: false, team: false };
      this.rolePermissionIds = [];
      this._resetPermMatrixUi();
      this.roleFormSnapshot = this._roleSnapshot();
      this.showEditRole = true;
    },

    openEditRole(role: Record<string, unknown>) {
      this.editingRole = role;
      // 삭제된 팀·구조가 바뀐 팀은 여는 순간 정리해서, 화면에 보이는 값과 저장될 값을 맞춘다(정리 전 값으로 기준선을 잡지 않음).
      // 팀 목록을 못 불러왔으면 정리하지 않는다 — 빈 목록으로 정리하면 전부 지워진다.
      const rawScope = {
        departmentTeamIds: (role.departmentTeamIds as string[] | null) ?? [],
        teamIds: (role.teamIds as string[] | null) ?? [],
      };
      const scope =
        this.allTeams.length > 0 ? normalizeRoleOrgScope(this.allTeams, rawScope) : rawScope;
      this.roleForm = {
        name: (role.name as string) || "",
        code: (role.code as string) || "",
        description: (role.description as string) || "",
        isActive: role.isActive !== false,
        departmentTeamIds: scope.departmentTeamIds ?? [],
        teamIds: scope.teamIds ?? [],
      };
      this.roleScopeMulti = {
        department: this.roleForm.departmentTeamIds.length > 1,
        team: this.roleForm.teamIds.length > 1,
      };
      const perms = role.permissions as Record<string, unknown>[] | undefined;
      this.rolePermissionIds = perms ? perms.map((p) => p.id as string) : [];
      this._resetPermMatrixUi();
      this.roleFormSnapshot = this._roleSnapshot();
      this.showEditRole = true;
    },

    /** Stable serialization of the role form + selected permissions for dirty-checking. */
    _roleSnapshot(): string {
      return JSON.stringify({
        name: this.roleForm.name.trim(),
        description: (this.roleForm.description || "").trim(),
        isActive: this.roleForm.isActive,
        departmentTeamIds: [...this.roleForm.departmentTeamIds].sort(),
        teamIds: [...this.roleForm.teamIds].sort(),
        permissionIds: [...this.rolePermissionIds].sort(),
      });
    },

    /* ── 역할 부서 · 팀(병동) — 규칙은 @hereby/core/constants role-org-scope ── */
    roleDeptOptions(): RoleScopeOption[] {
      return roleDepartmentOptions(this.allTeams);
    },

    /** 팀(병동) 선택지 — 팝업은 고른 부서 아래로, 목록 필터는 필터에서 고른 부서 아래로 좁힌다(비면 전체). */
    roleTeamGroups(departmentIds: string[]): RoleScopeOptionGroup[] {
      return roleTeamOptionGroups(this.allTeams, departmentIds);
    },

    roleScopeLabel(teamId: string): string {
      return roleScopeTeamLabel(this.allTeams, teamId);
    },

    roleMatchesScopeFilter(role: RoleItem, departmentFilter: string, teamFilter: string): boolean {
      return roleMatchesOrgFilter(this.allTeams, role, departmentFilter, teamFilter);
    },

    /**
     * 부서·팀 드롭다운 표시값 맞추기(x-effect). 선택지가 x-for 로 나중에 다시 그려지면 `:value`/x-model 은
     * 새로 생긴 <option> 을 고르지 못해 「선택 안 함」으로 보인다 — 그려진 뒤(nextTick) 값을 다시 넣는다.
     * deps 는 선택지를 다시 그리게 하는 값(팀 목록·고른 부서)을 넘겨 의존성으로만 읽는다.
     */
    syncSelectValue(el: HTMLSelectElement, value: string, ..._deps: unknown[]) {
      Alpine.nextTick(() => {
        el.value = value;
      });
    },

    /** 팝업 드롭다운에서 하나 고름 — 중복 선택이면 추가, 아니면 교체. 부서가 바뀌면 그 부서 밖 팀은 뺀다. */
    pickRoleScope(kind: "department" | "team", id: string) {
      const key = kind === "department" ? "departmentTeamIds" : "teamIds";
      this.roleForm[key] = pickRoleScopeId(this.roleForm[key], id, this.roleScopeMulti[kind]);
      this._pruneRoleScope();
    },

    removeRoleScope(kind: "department" | "team", id: string) {
      const key = kind === "department" ? "departmentTeamIds" : "teamIds";
      this.roleForm[key] = this.roleForm[key].filter((v) => v !== id);
      this._pruneRoleScope();
    },

    /** 「중복 …」 체크. 끄면 먼저 고른 하나만 남긴다. */
    setRoleScopeMulti(kind: "department" | "team", on: boolean) {
      this.roleScopeMulti[kind] = on;
      if (!on) {
        const key = kind === "department" ? "departmentTeamIds" : "teamIds";
        this.roleForm[key] = this.roleForm[key].slice(0, 1);
        this._pruneRoleScope();
      }
    },

    _pruneRoleScope() {
      if (this.allTeams.length === 0) {
        return;
      }
      const scope = normalizeRoleOrgScope(this.allTeams, this.roleForm);
      this.roleForm.departmentTeamIds = scope.departmentTeamIds ?? [];
      this.roleForm.teamIds = scope.teamIds ?? [];
    },

    /** Whether the role form has unsaved changes vs. the snapshot taken on open. */
    roleDirty(): boolean {
      return this._roleSnapshot() !== this.roleFormSnapshot;
    },

    /** [저장] enable: name required; on edit also requires a change (수정 내역 있을 때). */
    canSaveRole(): boolean {
      if (!this.roleForm.name.trim()) {
        return false;
      }
      return this.editingRole ? this.roleDirty() : true;
    },

    /** Whether the role being edited is the locked super-admin role. */
    isEditingSuperAdmin(): boolean {
      return (this.editingRole?.code as string) === "SUPER_ADMIN";
    },

    /** 「모든 권한 켜기」 — 164개를 전부 가장 넓은 범위로 켜므로 한 번 확인한다. */
    async selectAllPermissions() {
      const t = Alpine.store("hereby").t;
      const ok = await askConfirm({
        message: `${t("role.perm_all_confirm", { n: this.permissions.length })}\n${t("role.perm_all_confirm_note")}`,
        emphasis: t("role.perm_all_confirm_note"),
        confirmLabel: t("role.perm_all_confirm_ok"),
        tone: "danger",
      });
      if (!ok) {
        return;
      }
      this.rolePermissionIds = this.permissions.map((p: Record<string, unknown>) => p.id as string);
    },

    /* ── 권한 매트릭스 — 순수 규칙은 ./role-permission-matrix, 여기는 상태 연결만 ── */

    /** 모달을 열 때 — 필터·비교를 비우고 매트릭스를 다시 만든다(라벨이 로케일·용어를 따른다). */
    _resetPermMatrixUi() {
      this.permMatrixFilter = { query: "", resource: "", scope: "", selectedOnly: false };
      this.permCompareRoleId = "";
      this.permCompareIds = [];
      this.permCompareDiffOnly = false;
      this.permGroupOpen = {};
      this.permManualCells = [];
      this.permMatrix = null;
    },

    _permMatrix(): PermissionMatrix {
      if (!this.permMatrix) {
        const rows = this.permissions as unknown as PermissionRowLike[];
        this.permMatrix = buildPermissionMatrix(rows, (r) => this._t(`perm.resource.${r}`));
        // 검색은 현재 로케일 라벨 + 반대 로케일 라벨도 맞춘다(「Attendance」 로 근태를 찾을 수 있게).
        const ko = getLocaleBundle("ko");
        const en = getLocaleBundle("en");
        const labels: Record<string, string> = {};
        for (const res of this.permMatrix.resources) {
          const key = `perm.resource.${res.resource}`;
          labels[res.resource] = [ko[key] ?? "", en[key] ?? ""].join(" ");
        }
        this.permSearchLabels = labels;
      }
      return this.permMatrix;
    },

    /** 행이 하나라도 있는 열(동작)만 — 고정 순서. */
    permMatrixActions(): MatrixAction[] {
      return this._permMatrix().actions;
    },

    /** 리소스 필터 선택지 — 매트릭스의 리소스 전부. */
    permMatrixResources(): MatrixResource[] {
      return this._permMatrix().resources;
    },

    /** 화면에 보일 리소스 줄 — 검색·리소스·범위·선택만·차이만 필터를 거친 결과. */
    visiblePermMatrix(): MatrixResource[] {
      const matrix = this._permMatrix();
      const f = this.permMatrixFilter;
      let resources = filterMatrix(matrix, {
        query: f.query,
        resource: f.resource,
        scope: f.scope,
        selectedOnly: f.selectedOnly,
        selectedIds: this.rolePermissionIds,
        labels: this.permSearchLabels,
      }).resources;
      if (this.permCompareRoleId && this.permCompareDiffOnly) {
        const diff = diffAgainst(this.rolePermissionIds, this.permCompareIds, matrix);
        resources = resources.filter((res) =>
          MATRIX_ACTIONS.some((a) => diff[cellKey(res.resource, a)]),
        );
      }
      return resources;
    },

    permActionLabel(action: MatrixAction): string {
      return this._t(`perm.action.${action}`);
    },

    permScopeLabel(scope: CellScope): string {
      return scope ? this._t(`perm.scope.${scope}`) : this._t("role.perm_none");
    },

    /* ── 기능별 필수 권한 묶음(MED-989) — 정본 FEATURE_PERMISSION_BUNDLES, 규칙은 role-permission-matrix ── */

    /** 묶음 목록 + 이 역할의 충족 상태 — 그룹(직원 앱 · 팀장 앱 · 급여 시트 · 관리) 순서대로. */
    permBundles(): { bundle: FeaturePermissionBundle; group: string; check: BundleCheck }[] {
      const matrix = this._permMatrix();
      return FEATURE_PERMISSION_BUNDLES.map((bundle) => ({
        bundle,
        group: FEATURE_BUNDLE_GROUP_LABELS[bundle.group],
        check: checkBundle(this.rolePermissionIds, matrix, bundle.requires),
      }));
    },

    permBundleStatusLabel(status: BundleCheck["status"]): string {
      return this._t(`role.perm_bundle_${status}`);
    },

    _permRequirementLabel(r: BundleRequirementLike): string {
      return `${this._t(`perm.resource.${r.resource}`)} ${this.permActionLabel(r.action)} · ${this.permScopeLabel(r.scope)}`;
    },

    /** 버튼 툴팁(여러 줄) — 설명 · 필요한 권한 전부 · 모자란 권한. */
    permBundleTip(entry: { bundle: FeaturePermissionBundle; check: BundleCheck }): string {
      const lines = [entry.bundle.description, "", `${this._t("role.perm_bundle_requires")}:`];
      for (const r of entry.bundle.requires) {
        lines.push(`· ${this._permRequirementLabel(r)}`);
      }
      const lacking = [...entry.check.missing, ...entry.check.unavailable];
      if (lacking.length > 0) {
        lines.push("", `${this._t("role.perm_bundle_missing")}:`);
        for (const r of lacking) {
          lines.push(`· ${this._permRequirementLabel(r)}`);
        }
      }
      return lines.join("\n");
    },

    /** 묶음 더하기 — 모자란 칸만 올린다. 이미 다 갖췄으면 안내만. */
    applyPermBundle(id: string) {
      if (this.isEditingSuperAdmin()) {
        return;
      }
      const bundle = featureBundleById(id);
      if (!bundle) {
        return;
      }
      const matrix = this._permMatrix();
      const before = checkBundle(this.rolePermissionIds, matrix, bundle.requires);
      if (before.status === "full") {
        this.notify(
          Alpine.store("hereby").t("role.perm_bundle_already", { name: bundle.label }),
          "info",
        );
        return;
      }
      this.rolePermissionIds = applyBundle(this.rolePermissionIds, matrix, bundle.requires);
      this.notify(
        Alpine.store("hereby").t("role.perm_bundle_added", {
          name: bundle.label,
          n: before.missing.length,
        }),
      );
      if (before.unavailable.length > 0) {
        this.notify(
          Alpine.store("hereby").t("role.perm_bundle_unavailable", {
            n: before.unavailable.length,
          }),
          "info",
        );
      }
    },

    /**
     * 묶음 체크박스 — 다 갖춘 묶음(full)을 누르면 풀고, 아니면(없음·일부) 채운다.
     * 풀 때는 다른 체크된 묶음이 같은 칸에 요구하는 범위까지만 내리고, 손으로 바꾼 칸은 남긴다.
     */
    togglePermBundle(id: string) {
      if (this.isEditingSuperAdmin()) {
        return;
      }
      const bundle = featureBundleById(id);
      if (!bundle) {
        return;
      }
      const matrix = this._permMatrix();
      if (checkBundle(this.rolePermissionIds, matrix, bundle.requires).status !== "full") {
        this.applyPermBundle(id);
        return;
      }
      const keep = FEATURE_PERMISSION_BUNDLES.filter(
        (b) =>
          b.id !== id && checkBundle(this.rolePermissionIds, matrix, b.requires).status === "full",
      ).flatMap((b) => b.requires);
      this.rolePermissionIds = removeBundle(
        this.rolePermissionIds,
        matrix,
        bundle.requires,
        keep,
        this.permManualCells,
      );
    },

    /** 일부만 갖춘 묶음의 안내 — 모자란 권한 하나(나머지는 개수). */
    permBundleMissingHint(check: BundleCheck): string {
      const lacking = [...check.missing, ...check.unavailable];
      if (lacking.length === 0) {
        return "";
      }
      const first = this._permRequirementLabel(lacking[0]);
      return lacking.length === 1
        ? Alpine.store("hereby").t("role.perm_bundle_missing_one", { name: first })
        : Alpine.store("hereby").t("role.perm_bundle_missing_many", {
            name: first,
            n: lacking.length - 1,
          });
    },

    /** 묶음 목록을 그룹(직원 앱 · 팀장 앱 · 급여 시트 · 관리)별로 */
    permBundleGroups(): {
      group: string;
      entries: { bundle: FeaturePermissionBundle; group: string; check: BundleCheck }[];
    }[] {
      const out: {
        group: string;
        entries: { bundle: FeaturePermissionBundle; group: string; check: BundleCheck }[];
      }[] = [];
      for (const entry of this.permBundles()) {
        const last = out[out.length - 1];
        if (last && last.group === entry.group) {
          last.entries.push(entry);
        } else {
          out.push({ group: entry.group, entries: [entry] });
        }
      }
      return out;
    },

    /** 리소스 필터 드롭다운 — 전체 리소스를 같은 그룹 순서로(optgroup). */
    permMatrixResourceGroups(): PermissionGroup[] {
      return groupMatrixResources(this._permMatrix().resources);
    },

    /** 리소스 그룹(인사 · 근태 · 급여 …) — 필터를 거친 줄을 고정 그룹 순서로. */
    permGroups(): PermissionGroup[] {
      return groupMatrixResources(this.visiblePermMatrix());
    },

    /** 그룹에서 켜진 권한 수 */
    permGroupSelectedCount(group: PermissionGroup): number {
      const selected = new Set(this.rolePermissionIds);
      return group.resources.reduce(
        (n: number, res: MatrixResource) => n + res.ids.filter((id) => selected.has(id)).length,
        0,
      );
    },

    /** 검색 · 필터 · 차이만 보기 중이면 걸러진 그룹을 전부 펼쳐 보인다. */
    get permFiltering(): boolean {
      const f = this.permMatrixFilter;
      return (
        !!f.query.trim() ||
        !!f.resource ||
        !!f.scope ||
        f.selectedOnly ||
        (!!this.permCompareRoleId && this.permCompareDiffOnly)
      );
    },

    /** 기본은 펼침(10/20 사용자 요청) — 사용자가 접은 그룹만 false 로 적어 둔다. */
    isPermGroupOpen(id: string): boolean {
      return this.permFiltering || this.permGroupOpen[id] !== false;
    },

    togglePermGroup(id: string) {
      this.permGroupOpen = { ...this.permGroupOpen, [id]: !this.isPermGroupOpen(id) };
    },

    /** 모두 펼치기 / 접기 */
    get permAllGroupsOpen(): boolean {
      return this.permGroups().every((g: PermissionGroup) => this.isPermGroupOpen(g.id));
    },

    setAllPermGroups(open: boolean) {
      const next: Record<string, boolean> = {};
      for (const g of groupMatrixResources(this._permMatrix().resources)) {
        next[g.id] = open;
      }
      this.permGroupOpen = next;
    },

    /** 「이 역할이 할 수 있는 일」 — 다 갖춘 기능 묶음 이름 + 그룹별 켜진 권한 수. */
    permSummary(): { bundles: string[]; groups: { label: string; n: number }[] } {
      return this._permSummaryFor(this.rolePermissionIds);
    },

    /** 요약 계산(팝업 · 역할 목록 카드 공용) — 다 갖춘 기능 묶음 이름 + 그룹별 켜진 권한 수 */
    _permSummaryFor(ids: readonly string[]): {
      bundles: string[];
      groups: { label: string; n: number }[];
    } {
      const matrix = this._permMatrix();
      const selected = new Set(ids);
      const bundles = FEATURE_PERMISSION_BUNDLES.filter(
        (b) => checkBundle(ids, matrix, b.requires).status === "full",
      ).map((b) => b.label);
      const groups = groupMatrixResources(matrix.resources)
        .map((g) => ({
          label: g.label,
          n: g.resources.reduce(
            (n: number, res: MatrixResource) => n + res.ids.filter((id) => selected.has(id)).length,
            0,
          ),
        }))
        .filter((g) => g.n > 0);
      return { bundles, groups };
    },

    /* ── 역할 목록 카드 펼침(보기 전용) — 팝업과 같은 그룹 · 열 · 범위로, 켠 권한이 있는 리소스만 ── */

    _roleIds(role: RoleItem): string[] {
      return ((role as { permissions?: { id: string }[] }).permissions ?? []).map((p) => p.id);
    },

    rolePermSummary(role: RoleItem): { bundles: string[]; groups: { label: string; n: number }[] } {
      return this._permSummaryFor(this._roleIds(role));
    },

    /** 그룹별 줄 — 기본은 켠 권한이 있는 리소스만, 「권한 없는 리소스도 보기」면 전부. 빈 그룹은 뺀다. */
    roleMatrixGroups(role: RoleItem): (PermissionGroup & { on: number })[] {
      const selected = new Set(this._roleIds(role));
      const showAll = this.roleShowAllPerms[role.id as string] === true;
      return groupMatrixResources(this._permMatrix().resources)
        .map((g) => ({
          ...g,
          on: g.resources.reduce(
            (n: number, res: MatrixResource) => n + res.ids.filter((id) => selected.has(id)).length,
            0,
          ),
          resources: g.resources.filter(
            (res: MatrixResource) => showAll || res.ids.some((id) => selected.has(id)),
          ),
        }))
        .filter((g) => g.resources.length > 0);
    },

    /**
     * 한 표로 합친 줄(A안) — 그룹 첫 줄에만 영역 칸(rowspan)을 둔다. 줄 키는 리소스, 영역 칸 정보는 첫 줄에만.
     */
    roleMatrixRows(role: RoleItem): {
      res: MatrixResource;
      groupId: string;
      groupLabel: string;
      groupOn: number;
      first: boolean;
      span: number;
    }[] {
      return this.roleMatrixGroups(role).flatMap((g) =>
        g.resources.map((res: MatrixResource, i: number) => ({
          res,
          groupId: g.id,
          groupLabel: g.label,
          groupOn: g.on,
          first: i === 0,
          span: g.resources.length,
        })),
      );
    },

    /** 켠 권한이 하나도 없는 리소스 수 — 「권한 없는 리소스도 보기」 옆에 */
    roleHiddenResourceCount(role: RoleItem): number {
      const selected = new Set(this._roleIds(role));
      return this._permMatrix().resources.filter(
        (res: MatrixResource) => !res.ids.some((id) => selected.has(id)),
      ).length;
    },

    /** 역할이 그 칸에 가진 범위(가장 넓은 것, 없으면 "") — 팝업 칸과 같은 값 */
    roleCellScope(role: RoleItem, cell: MatrixCell | undefined): CellScope {
      return cellScope(this._roleIds(role), cell);
    },

    /** 칸에 고른 범위(선택된 행 중 가장 넓은 것, 없으면 ""). */
    permCellScope(cell: MatrixCell | undefined): CellScope {
      return cellScope(this.rolePermissionIds, cell);
    },

    /** 그 범위의 행이 칸에 있는가 — 없으면 선택지를 잠근다(없음은 항상 고를 수 있다). */
    permCellHas(cell: MatrixCell | undefined, scope: CellScope): boolean {
      return scope === "" ? true : !!cell?.[scope];
    },

    /**
     * 칸 툴팁(여러 줄 title → DS 상세 툴팁) — 고를 수 있는 범위마다 그 권한 행의 이름과 설명(permissions.description).
     * 네이티브 select 의 option 엔 툴팁을 못 붙이므로 select 자체에 한 번에 보여 준다.
     */
    permCellTip(cell: MatrixCell | undefined): string {
      if (!cell) {
        return "";
      }
      const byId = this._permMatrix().byId;
      const lines: string[] = [];
      for (const scope of SCOPE_ORDER) {
        const id = cell[scope];
        const row = id ? byId[id]?.row : undefined;
        if (!row) {
          continue;
        }
        const name = row.name || row.code;
        const desc = (row.description || "").trim();
        lines.push(`${this.permScopeLabel(scope)} — ${name}${desc ? `: ${desc}` : ""}`);
      }
      return lines.join("\n");
    },

    /** 칸 드롭다운 — 범위를 바꾸고 「손으로 바꾼 칸」으로 적어 둔다(묶음 해제가 건드리지 않게). */
    setPermCellScope(res: MatrixResource, action: MatrixAction, scope: CellScope) {
      if (this.isEditingSuperAdmin()) {
        return;
      }
      this.rolePermissionIds = applyCellScope(this.rolePermissionIds, res.cells[action], scope);
      this._markPermManual(res, [action]);
    },

    _markPermManual(res: MatrixResource, actions: readonly MatrixAction[]) {
      for (const a of actions) {
        const key = cellKey(res.resource, a);
        if (res.cells[a] && !this.permManualCells.includes(key)) {
          this.permManualCells.push(key);
        }
      }
    },

    /** 칸 드롭다운 선택지 — 없음 + 그 칸에 실제로 있는 범위만. */
    permCellOptions(cell: MatrixCell | undefined): CellScope[] {
      return this.permScopeOptions.filter((s: CellScope) => s === "" || !!cell?.[s]);
    },

    /** 줄 머리 「모두 전체」 — 리소스의 모든 칸을 가장 넓은 범위로. */
    setPermResourceAll(res: MatrixResource) {
      if (this.isEditingSuperAdmin()) {
        return;
      }
      this.rolePermissionIds = applyResourceMax(this.rolePermissionIds, res);
      this._markPermManual(res, MATRIX_ACTIONS);
    },

    /** 줄 머리 「해제」 — 리소스의 행을 전부 뺀다. */
    clearPermResource(res: MatrixResource) {
      if (this.isEditingSuperAdmin()) {
        return;
      }
      this.rolePermissionIds = clearResource(this.rolePermissionIds, res);
      this._markPermManual(res, MATRIX_ACTIONS);
    },

    /** 리소스가 이미 전부 가장 넓은 범위인가(「모두 전체」 버튼을 숨길 조건). */
    permResourceAtMax(res: MatrixResource): boolean {
      return MATRIX_ACTIONS.every((a) => {
        const cell = res.cells[a];
        return !cell || cellScope(this.rolePermissionIds, cell) === cellMaxScope(cell);
      });
    },

    permResourceHasSelection(res: MatrixResource): boolean {
      return res.ids.some((id) => this.rolePermissionIds.includes(id));
    },

    /** 비교 대상 선택지 — 지금 편집하는 역할을 뺀 나머지(권한은 loadRoles 가 includePermissions 로 받아 둔다). */
    permCompareOptions(): RoleItem[] {
      const editingId = this.editingRole?.id as string | undefined;
      return this.roles.filter((r) => r.id !== editingId);
    },

    setPermCompareRole(roleId: string) {
      this.permCompareRoleId = roleId;
      const role = this.roles.find((r) => r.id === roleId);
      const perms = (role as { permissions?: { id: string }[] } | undefined)?.permissions ?? [];
      this.permCompareIds = perms.map((p) => p.id);
      if (!roleId) {
        this.permCompareDiffOnly = false;
      }
    },

    /** 비교 역할이 그 칸에 고른 범위. */
    permCompareScope(cell: MatrixCell | undefined): CellScope {
      return cellScope(this.permCompareIds, cell);
    },

    /** 비교 중이고 두 역할의 범위가 다른 칸인가 — 노란 바탕으로 띄운다. */
    permCellDiffers(cell: MatrixCell | undefined): boolean {
      return (
        !!this.permCompareRoleId &&
        !!cell &&
        cellScope(this.permCompareIds, cell) !== cellScope(this.rolePermissionIds, cell)
      );
    },

    async saveRole() {
      if (!this.roleForm.name) {
        showAlert(this._t("admin.role_name_required"));
        return;
      }
      try {
        if (this.editingRole) {
          await this.sdk.role.update(this.editingRole.id as string, {
            name: this.roleForm.name,
            description: this.roleForm.description || undefined,
            isActive: this.roleForm.isActive,
            departmentTeamIds: this.roleForm.departmentTeamIds,
            teamIds: this.roleForm.teamIds,
          });
          await this.sdk.role.updatePermissions(
            this.editingRole.id as string,
            this.rolePermissionIds,
          );
        } else {
          const code = this.roleForm.code || this.roleForm.name.replace(/\s+/g, "_").toUpperCase();
          const created = await this.sdk.role.create({
            name: this.roleForm.name,
            code,
            description: this.roleForm.description || undefined,
            isActive: this.roleForm.isActive,
            departmentTeamIds: this.roleForm.departmentTeamIds,
            teamIds: this.roleForm.teamIds,
          });
          if (this.rolePermissionIds.length > 0 && created?.id) {
            await this.sdk.role.updatePermissions(created.id as string, this.rolePermissionIds);
          }
        }
        const wasEditingRole = !!this.editingRole;
        this.showEditRole = false;
        await this.loadRoles();
        this.notify(
          `${attachJosa(this._term("role"), "을/를")} ${wasEditingRole ? "수정" : "추가"}했습니다`,
        );
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        if (
          msg.includes("already exists") ||
          msg.includes("duplicate") ||
          msg.includes("CONFLICT")
        ) {
          showAlert(this._t("admin.role_code_duplicate"));
        } else if (msg.includes("at least 1 character")) {
          showAlert(this._t("admin.role_code_name_required"));
        } else {
          showError(`${this._t("admin.save_failed")}: ${msg}`);
        }
      }
    },

    async deleteRole(roleId: string) {
      this.confirmTitle = "";
      this.confirmMessage = this._t("admin.confirm_delete_role");
      this.confirmLabel = this._t("common.delete");
      this.confirmAction = async () => {
        try {
          await this.sdk.role.delete(roleId);
          await this.loadRoles();
          this.notify(`${attachJosa(this._term("role"), "을/를")} 삭제했습니다`);
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          // 해당 역할을 가진 직원이 있어 삭제 불가 — 정책 안내 팝업 (확인 버튼만).
          // executeConfirm 이 _resetConfirm 으로 현재 팝업을 닫으므로, 다음 틱에 재오픈한다.
          const blocked = msg.includes("배정");
          setTimeout(() => {
            if (blocked) {
              this._showRoleDeleteBlocked();
            } else {
              this.showCommonError();
            }
          }, 0);
        }
      };
      this.showConfirm = true;
    },

    /** 정책: 해당 역할을 가진 직원이 있어 삭제할 수 없을 때 안내 (확인 버튼만 닫힘). */
    _showRoleDeleteBlocked() {
      this.confirmTitle = this._t("role.delete_blocked");
      this.confirmMessage = "";
      this.confirmLabel = this._t("common.confirm");
      this.confirmHideCancel = true;
      this.confirmAction = null;
      this.showConfirm = true;
    },

    toggleRoleExpand(roleId: string) {
      this.expandedRoles[roleId] = !this.expandedRoles[roleId];
    },

    /* ──────────── confirm dialog ──────────── */
    async executeConfirm() {
      if (this.confirmAction) {
        await this.confirmAction();
      }
      this._resetConfirm();
    },

    // 중간 버튼(예: '내용 유지') 클릭 — 제3 선택지 콜백을 실행하고 닫는다.
    // 취소가 아니므로 confirmCancelAction(되돌리기)은 실행하지 않는다.
    async executeConfirmSecondary() {
      if (this.confirmSecondaryAction) {
        await this.confirmSecondaryAction();
      }
      this._resetConfirm();
    },

    cancelConfirm() {
      // 취소/X/Esc 시 등록된 되돌리기 콜백을 먼저 실행(예: 자격유형 선택 복원)한 뒤 닫는다.
      if (this.confirmCancelAction) {
        this.confirmCancelAction();
      }
      this._resetConfirm();
    },

    _resetConfirm() {
      this.showConfirm = false;
      this.confirmAction = null;
      this.confirmCancelAction = null;
      this.confirmSecondaryLabel = "";
      this.confirmSecondaryAction = null;
      this.confirmTitle = "";
      this.confirmMessage = "";
      this.confirmLabel = "";
      this.confirmHideCancel = false;
      this.confirmTop = false;
    },
    /** 확인 다이얼로그가 파괴적(삭제·제거·제외)인지 — 확정 버튼 톤 결정용(파괴=lightor, 일반=dark).
       실행 액션이 있는 확인만 — "삭제할 수 없습니다" 같은 안내(confirmAction=null)는 제외. */
    confirmIsDestructive() {
      return !!this.confirmAction && /삭제|제거|제외/.test(this.confirmMessage || "");
    },
    /** 파괴적 확인의 확정 버튼 라벨 — "확인" 대신 동작 동사(삭제/제거/제외). 안내·커스텀 라벨은 그대로 유지. */
    confirmActionLabel() {
      const generic = this._t("common.confirm");
      if (this.confirmLabel && this.confirmLabel !== generic) {
        return this.confirmLabel;
      }
      if (this.confirmAction) {
        const m = this.confirmMessage || "";
        if (/삭제/.test(m)) {
          return this._t("common.delete");
        }
        if (/제거/.test(m)) {
          return this._t("common.remove");
        }
        if (/제외/.test(m)) {
          return this._t("common.exclude");
        }
      }
      return this.confirmLabel || generic;
    },

    /**
     * 공통 에러 안내 팝업 — 알 수 없는 서버 에러 발생 시 사용한다.
     * 확인/X 버튼 모두 팝업만 닫는다. 다른 모달 위에 표시(z-60).
     */
    showCommonError() {
      this.confirmTitle = "알 수 없는 에러가 발생했어요.";
      this.confirmMessage = "잠시 후 다시 시도해 주세요.";
      this.confirmLabel = this._t("common.confirm");
      this.confirmHideCancel = true;
      this.confirmTop = true;
      this.confirmAction = null;
      this.showConfirm = true;
    },

    /**
     * 조직 설정 편집(orgEditMode) 중 다른 페이지/탭으로 이탈 시 — 시스템 confirm 대신
     * 공통 confirm 다이얼로그로 확인한다. 확인하면 편집 모드를 끄고 onProceed(원래
     * 내비게이션)를 재실행한다(재실행 시 orgEditMode=false라 가드를 통과). 취소하면 머무른다.
     */
    confirmLeaveOrgEdit(onProceed: () => void) {
      this._resetConfirm();
      this.confirmTitle = "변경사항이 저장되지 않았습니다";
      this.confirmMessage = "저장하지 않고 이동하시겠습니까?";
      this.confirmLabel = "이동";
      this.confirmTop = true;
      this.confirmAction = async () => {
        this.orgEditMode = false;
        onProceed();
      };
      this.showConfirm = true;
    },

    /* ──────────── pagination ──────────── */
    async goToPage(tab: string, page: number) {
      const p = this.paging[tab];
      if (page < 1 || page > p.totalPages) {
        return;
      }
      p.page = page;
      if (tab === "employees") {
        await this.loadEmployees();
        this.computeStats();
      } else if (tab === "teams") {
        await this.loadTeams();
      } else if (tab === "roles") {
        await this.loadRoles();
      } else if (tab === "esign") {
        await this.loadESignRecords();
      }
    },

    resetPage(tab: string) {
      this.paging[tab].page = 1;
    },

    /** 페이지 크기 선택기 옵션 — DS 규칙(pagination.md #page-size)대로 네 개 고정. "전체"는 서버 상한 1,000. */
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    pageSizeCountOptions: PAGE_SIZE_COUNT_OPTIONS,

    /**
     * 근태 현황 탭 목록의 페이지 크기(DS 30 · 50 · 100 · 전체) — 1쪽으로 돌아가 다시 그린다. 선택은 목록별로 기억한다.
     * 연차 현황 · 출근율은 받아 둔 목록을 쪽으로 자르고, 휴가 결재 · 외근 · 정정 요청은 서버에서 다시 받는다.
     */
    setAttendancePageSize(
      key:
        | "leave-balances"
        | "attendance-rates"
        | "leave-approvals"
        | "offsite-records"
        | "corrections",
      raw: string | number,
    ) {
      const size = Number(raw);
      if (!PAGE_SIZE_OPTIONS.some((o) => o.value === size)) {
        return;
      }
      try {
        localStorage.setItem(`admin:pageSize:${key}`, String(size));
      } catch {
        /* localStorage unavailable — keep in-memory only */
      }
      if (key === "leave-balances") {
        this.leaveBalanceLimit = size;
        this.leaveBalancePage = 1;
      } else if (key === "attendance-rates") {
        this.attendanceRateLimit = size;
        this.attendanceRatePage = 1;
      } else if (key === "leave-approvals") {
        this.leaveApprovalPaging.limit = size;
        this.leaveApprovalGoPage(1);
      } else if (key === "offsite-records") {
        this.offsitePaging.limit = size;
        this.offsitePaging.page = 1;
        this.loadOffsiteRecords();
      } else {
        this.correctionPaging.limit = size;
        this.correctionPaging.page = 1;
        this.loadCorrections();
      }
    },

    /** 「전체」(1,000)를 골랐는데 총 건수가 더 많을 때만 요약 툴팁 문구 */
    attendancePageCapTitle(limit: number, total: number): string {
      return limit >= PAGE_SIZE_ALL && total > PAGE_SIZE_ALL
        ? `전체 ${total.toLocaleString()}건 중 최대 ${PAGE_SIZE_ALL.toLocaleString()}건까지만 표시돼요`
        : "";
    },

    /** 페이지 크기 변경 — 1페이지로 되돌리고 다시 불러온다. 선택은 목록별로 기억한다. */
    setPageSize(tab: string, raw: string | number, reload: () => void) {
      const size = Number(raw);
      if (!PAGE_SIZE_OPTIONS.some((o) => o.value === size)) {
        return;
      }
      this.paging[tab].limit = size;
      this.paging[tab].page = 1;
      try {
        localStorage.setItem(`admin:pageSize:${tab}`, String(size));
      } catch {
        /* localStorage unavailable — keep in-memory only */
      }
      reload();
    },

    /** "전체"를 골랐는데 총 건수가 상한을 넘을 때만 툴팁 문구를 돌려준다(그 외엔 빈 문자열 = 툴팁 없음). */
    pageSizeCapTitle(tab: string): string {
      const p = this.paging[tab];
      if (p.limit >= PAGE_SIZE_ALL && p.totalCount > PAGE_SIZE_ALL) {
        return `전체 ${p.totalCount.toLocaleString()}건 중 최대 ${PAGE_SIZE_ALL.toLocaleString()}건까지만 표시돼요`;
      }
      return "";
    },

    pageRange(tab: string): number[] {
      const p = this.paging[tab];
      return this.pageWindow(p.page, p.totalPages);
    },

    /* 클라이언트·서버 공통 10칸 페이지 윈도우 — 항상 최대 10개가 보이도록 양 끝에서 클램프 */
    pageWindow(current: number, totalPages: number): number[] {
      const end = Math.min(totalPages, Math.max(1, current - 4) + 9);
      const start = Math.max(1, end - 9);
      const range: number[] = [];
      for (let i = start; i <= end; i++) {
        range.push(i);
      }
      return range;
    },

    /* ── 연차 현황(leaveBalances) 클라이언트 페이지네이션 ── */
    leaveBalancesPageItems(): Record<string, unknown>[] {
      const start = (this.leaveBalancePage - 1) * this.leaveBalanceLimit;
      return this.leaveBalances.slice(start, start + this.leaveBalanceLimit);
    },
    leaveBalanceTotalPages(): number {
      return Math.max(1, Math.ceil(this.leaveBalances.length / this.leaveBalanceLimit));
    },
    leaveBalancePageRange(): number[] {
      return this.pageWindow(this.leaveBalancePage, this.leaveBalanceTotalPages());
    },

    /* ── 외근 현황(offsiteRecords) 서버 페이지네이션 ── */
    offsiteGoPage(page: number) {
      const p = this.offsitePaging;
      if (page < 1 || page > p.totalPages) {
        return;
      }
      p.page = page;
      this.loadOffsiteRecords();
    },
    offsitePageRange(): number[] {
      return this.pageWindow(this.offsitePaging.page, this.offsitePaging.totalPages);
    },

    /* ──────────── certifications ──────────── */
    async loadCertifications() {
      try {
        const filter: Record<string, unknown> = {};
        if (this.certFilter.type) {
          filter.type = this.certFilter.type;
        }
        if (this.certFilter.teamIds.length) {
          filter.teamIds = this.certFilter.teamIds;
        }
        if (this.certFilter.search.trim()) {
          filter.search = this.certFilter.search.trim();
        }
        if (this.certFilter.deadlineFrom) {
          filter.deadlineFrom = this.certFilter.deadlineFrom;
        }
        if (this.certFilter.deadlineTo) {
          filter.deadlineTo = this.certFilter.deadlineTo;
        }
        this.certifications = (await this.sdk.employeeCertification.list(filter)) || [];
      } catch {
        this.certifications = [];
      }
    },

    async loadCertSummary() {
      try {
        this.certSummary = (await this.sdk.employeeCertification.summary()) || [];
      } catch {
        this.certSummary = [];
      }
    },

    /* 문서 결재 미처리(pending) 건수 로드 → LNB "문서 결재" 뱃지. 실패해도 무시(0 유지). */
    async loadDocApprovalPending() {
      try {
        const d = await this.sdk.teamManager.dashboard();
        this.docApprovalPending = (d?.pendingDocument || 0) + (d?.pendingCertificate || 0);
      } catch {
        this.docApprovalPending = 0;
      }
    },

    /* 차기 면허 신고 기한이 임박(90일 이내)했거나 지난 경우 true → 경고색 강조(P2). */
    certDeadlineSoon(deadline: unknown): boolean {
      if (!deadline) {
        return false;
      }
      const d = new Date(String(deadline));
      if (Number.isNaN(d.getTime())) {
        return false;
      }
      const soonMs = 90 * 24 * 60 * 60 * 1000;
      return d.getTime() - Date.now() <= soonMs;
    },

    /* ── 자격 유형 동적 필드 (하이브리드: 기본 필드=고정 컬럼, 커스텀 필드=fieldValues) ── */

    /** 기본 필드 key → 대장 레코드 고정 컬럼(응답 객체 기준) 매핑 */
    certBaseFieldMap: {
      license_name: "name",
      license_number: "licenseNumber",
      issuing_authority: "issuingAuthority",
      issue_date: "issueDate",
      last_reported_date: "lastReportedDate",
      next_reporting_deadline: "nextReportingDeadline",
      file: "storagePath",
    } as Record<string, string>,

    /** 선택된 유형의 동적 필드 정의. 설정 미로딩/빈 배열이면 현행 기본 폼으로 폴백. */
    certActiveFields(): Record<string, unknown>[] {
      const cfg = this.certTypeConfigs.find((ct) => ct.code === this.certForm.type);
      const fields = Array.isArray(cfg?.fields) ? (cfg?.fields as Record<string, unknown>[]) : [];
      if (fields.length > 0) {
        return fields;
      }
      // 폴백(유형 미선택·미로딩): 현행 기본 폼과 동일
      return [
        { key: "license_name", name: "면허증명", type: "text", required: true },
        { key: "license_number", name: "면허번호", type: "text", required: true },
        { key: "issuing_authority", name: "발급기관", type: "text", required: true },
        { key: "issue_date", name: "발급일", type: "date", required: true },
        { key: "file", name: "원본 파일", type: "file", required: true },
        { key: "last_reported_date", name: "최근 면허 신고일", type: "date", required: false },
        { key: "next_reporting_deadline", name: "차기 면허 신고일", type: "date", required: false },
      ];
    },

    /** certFieldVals 초기화 — 레코드 값(수정) 또는 필드 defaultValue(신규)로 채운다. */
    initCertFieldVals(record?: Record<string, unknown>) {
      const vals: Record<string, string> = {};
      for (const f of this.certActiveFields()) {
        const key = String(f.key);
        const col = this.certBaseFieldMap[key];
        let v = "";
        if (record) {
          if (col && record[col] != null && record[col] !== "") {
            v = String(record[col]);
            if (f.type === "date") {
              v = v.slice(0, 10);
            }
          } else {
            const fv = (record.fieldValues as Record<string, string>) || {};
            v = fv[key] || "";
          }
        } else {
          v = f.defaultValue ? String(f.defaultValue) : "";
          // 발급기관 기본값(기존 UX 유지)
          if (!v && key === "issuing_authority") {
            v = "보건복지부";
          }
        }
        vals[key] = v;
      }
      this.certFieldVals = vals;
      this.certFileNames = {};
    },

    /** 필수 필드 미입력 여부 — 저장 버튼 활성화 판단 (제출 ON일 때만 호출) */
    certRequiredMissing(): boolean {
      return this.certActiveFields().some(
        (f) => f.required === true && !String(this.certFieldVals[String(f.key)] || "").trim(),
      );
    },

    async openAddCert() {
      if (this.employees.length === 0) {
        await this.loadEmployees();
      }
      this.editingCertId = "";
      this.certForm = { employeeId: "", type: "", registeredAt: "", notes: "" };
      this.certRegistered = false;
      this.certEditing = false;
      // 신규 등록은 제출 토글을 기본 ON으로 펼쳐 바로 상세 입력. 미제출로 두려면 토글을 끈다.
      this.certSubmitted = true;
      this.certEditEmployeeLabel = "";
      this.initCertFieldVals();
      this.certTypePrev = this.certForm.type;
      this.showCertModal = true;
    },

    openEditCert(c: Record<string, unknown>) {
      this.editingCertId = c.id as string;
      this.certForm = {
        employeeId: (c.employeeId as string) || "",
        type: (c.type as string) || "nursing",
        registeredAt: c.registeredAt ? String(c.registeredAt).slice(0, 10) : "",
        notes: (c.notes as string) || "",
      };
      this.initCertFieldVals(c);
      // 제출된 상세가 하나라도 있으면 등록 모드(읽기전용 + 삭제 버튼) — 이름(자동 기본값)은 제외.
      const fv = (c.fieldValues as Record<string, string>) || {};
      this.certRegistered = !!(
        c.licenseNumber ||
        c.issueDate ||
        c.lastReportedDate ||
        c.nextReportingDeadline ||
        c.storagePath ||
        Object.values(fv).some((v) => v)
      );
      this.certEditing = false;
      this.certSubmitted = this.certRegistered;
      this.certEditEmployeeLabel =
        ((c.employeeName as string) || "") + (c.employeeNumber ? ` (${c.employeeNumber})` : "");
      this.certTypePrev = this.certForm.type;
      this.showCertModal = true;
    },

    /* 날짜형 동적 필드 변경 훅 — 최근 면허 신고일 입력 시 차기 신고 기한 자동 계산
       (신고일 +3년 시점이 속한 해의 12/31). 수동 수정도 허용되므로 항상 덮어쓰되,
       신고일이 비면 자동값도 지우지 않는다(사용자 입력 보존). */
    onCertDynDateChange(key: string) {
      if (key !== "last_reported_date") {
        return;
      }
      const v = this.certFieldVals.last_reported_date;
      if (!v) {
        return;
      }
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) {
        return;
      }
      // 유형에 차기 신고일 필드가 있을 때만 자동 세팅
      if (this.certActiveFields().some((f) => f.key === "next_reporting_deadline")) {
        this.certFieldVals.next_reporting_deadline = `${d.getFullYear() + 3}-12-31`;
      }
    },

    /* 파일형 동적 필드 업로드 → 값(storage key) 저장. */
    async uploadCertFieldFile(ev: Event, key: string) {
      const input = ev.target as HTMLInputElement;
      const file = input.files?.[0];
      if (!file) {
        return;
      }
      this.certFileUploading = true;
      try {
        const { key: storageKey } = await this.sdk.fileStorage.uploadFile(file, "document");
        this.certFieldVals[key] = storageKey;
        this.certFileNames[key] = file.name;
        this.notify("파일을 업로드했습니다");
      } catch (err) {
        showError(`파일 업로드 실패: ${err instanceof Error ? err.message : String(err)}`);
      }
      this.certFileUploading = false;
    },

    async saveCert() {
      // 저장(수정/추가)은 절대 레코드를 삭제하지 않는다. 제출 토글 OFF로 저장해도
      // '미제출' 상태로 업데이트할 뿐, 삭제는 자격증 목록(대장)의 삭제 아이콘(deleteCert,
      // 확인 다이얼로그 포함)에서만 수행한다.
      if (!(this.certForm.employeeId && this.certForm.type)) {
        return;
      }
      // 제출 토글이 곧 '제출 여부'. OFF(미제출)면 상세 필드는 저장하지 않는다.
      // 입력하다 토글을 끈 내용은 화면엔 남아 보여도(재펼침 편의) 저장 페이로드에선 제외.
      const submitted = this.certSubmitted;
      // 동적 필드 값을 하이브리드 분리: 기본 필드 → 고정 컬럼, 커스텀 필드 → fieldValues
      const vals = this.certFieldVals;
      const customValues: Record<string, string> = {};
      for (const f of this.certActiveFields()) {
        const key = String(f.key);
        if (!this.certBaseFieldMap[key] && vals[key]) {
          customValues[key] = vals[key];
        }
      }
      // 자격증명(name)은 엔티티 필수 — 필드값 없으면 유형 표시명으로 폴백
      const certName =
        (submitted && (vals.license_name || "").trim()) ||
        this.certTypeDefaultName(this.certForm.type) ||
        this.certTypeLabel(this.certForm.type);
      this.certSaving = true;
      try {
        if (this.editingCertId) {
          await this.sdk.employeeCertification.update({
            id: this.editingCertId,
            type: this.certForm.type,
            name: certName,
            licenseNumber: submitted ? vals.license_number || null : null,
            issuingAuthority: submitted ? vals.issuing_authority || null : null,
            issueDate: submitted ? vals.issue_date || null : null,
            lastReportedDate: submitted ? vals.last_reported_date || null : null,
            nextReportingDeadline: submitted ? vals.next_reporting_deadline || null : null,
            registeredAt: this.certForm.registeredAt || null,
            storagePath: submitted ? vals.file || null : null,
            fieldValues: submitted ? customValues : {},
            notes: this.certForm.notes || null,
          });
        } else if (submitted) {
          await this.sdk.employeeCertification.create({
            employeeId: this.certForm.employeeId,
            type: this.certForm.type,
            name: certName,
            licenseNumber: vals.license_number || undefined,
            issuingAuthority: vals.issuing_authority || undefined,
            issueDate: vals.issue_date || undefined,
            lastReportedDate: vals.last_reported_date || undefined,
            nextReportingDeadline: vals.next_reporting_deadline || undefined,
            registeredAt: this.certForm.registeredAt || undefined,
            storagePath: vals.file || undefined,
            fieldValues: customValues,
            notes: this.certForm.notes || undefined,
          });
        } else {
          // 미제출 신규: 자격유형 + 이름만 생성(상세 없음).
          await this.sdk.employeeCertification.create({
            employeeId: this.certForm.employeeId,
            type: this.certForm.type,
            name: certName,
            registeredAt: this.certForm.registeredAt || undefined,
            notes: this.certForm.notes || undefined,
          });
        }
        const wasEditingCert = !!this.editingCertId;
        this.showCertModal = false;
        await Promise.allSettled([this.loadCertifications(), this.loadCertSummary()]);
        this.notify(wasEditingCert ? "자격증을 수정했습니다" : "자격증을 추가했습니다");
      } catch (err) {
        showError(
          `${this._t("admin.save_failed")}: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
      this.certSaving = false;
    },

    async deleteCert(c: string | Record<string, unknown>) {
      // 호출부 호환: id 문자열 또는 행 객체 모두 허용
      const id = typeof c === "string" ? c : String(c.id);
      const name = typeof c === "string" ? "" : String(c.employeeName || "");
      const certName = typeof c === "string" ? "" : String(c.name || "자격증/면허");
      this.confirmTitle = "삭제";
      this.confirmMessage = name
        ? `${name}님의 ${certName} 정보를 삭제할까요? 삭제하면 복구할 수 없어요.`
        : this._t("admin.confirm_delete_cert");
      this.confirmLabel = this._t("common.delete");
      this.confirmAction = async () => {
        try {
          await this.sdk.employeeCertification.delete(id);
          await Promise.allSettled([this.loadCertifications(), this.loadCertSummary()]);
          this.notify("자격증을 삭제했습니다");
        } catch (err) {
          showError(
            `${this._t("admin.delete_failed")}: ${err instanceof Error ? err.message : String(err)}`,
          );
        }
      };
      this.showConfirm = true;
    },

    /* 파일형 필드의 첨부 삭제 — confirm 후 값·파일명 비움(저장 시 null 반영).
       cert 모달 위에 띄우려고 confirmTop 사용. 삭제 후엔 '파일 선택' 버튼·안내문구가 표시됨. */
    removeCertFieldFile(key: string) {
      this.confirmTitle = "파일 삭제";
      this.confirmMessage = "첨부한 파일을 삭제하시겠습니까?";
      this.confirmLabel = "삭제";
      this.confirmTop = true;
      this.confirmAction = async () => {
        this.certFieldVals[key] = "";
        delete this.certFileNames[key];
      };
      this.showConfirm = true;
    },

    /* 편집 — 등록된 자격증의 기존 값을 유지한 채 상세 입력을 활성화한다.
       certRegistered 해제 → 필드 활성·필수검증 적용. certEditing=true 로 헤더는 토글 대신 '삭제'만 노출
       (편집 중 토글 OFF는 '변경'이 아니므로 숨긴다). 값은 그대로 두어 수정만 한다. */
    editCertUnlock() {
      this.certRegistered = false;
      this.certSubmitted = true;
      this.certEditing = true;
    },

    /* 등록된 자격증/면허 '삭제'(재등록) 버튼 — confirm 후 상세 항목을 모두 비우고 재등록 모드로 전환한다.
       실제 DB 삭제가 아니라 폼만 비우며, 새 값으로 '수정' 저장 시 update가 기존 레코드를 덮어써 재등록된다
       (모달을 그냥 닫으면 원본은 그대로 유지). cert 모달 위에 띄우려고 confirmTop 사용. */
    clearCertForReregister() {
      this.confirmTitle = "자격증/면허 삭제";
      this.confirmMessage = "등록된 자격증/면허 정보를 삭제하고 재등록합니다.";
      this.confirmLabel = "삭제";
      this.confirmTop = true;
      this.confirmAction = async () => {
        // 동적 필드 값 전체 초기화(기본값 재적용 없이 빈 폼으로)
        for (const k of Object.keys(this.certFieldVals)) {
          this.certFieldVals[k] = "";
        }
        this.certFileNames = {};
        // 등록·편집 모드 해제 → 토글이 다시 노출되고 상세 항목을 새로 입력(재등록)할 수 있다.
        this.certRegistered = false;
        this.certEditing = false;
        this.certSubmitted = true;
      };
      this.showConfirm = true;
    },

    /* 파일형 필드 표시용 이름: 업로드 시 저장한 file.name 우선, 없으면 값(storage key)의 basename. */
    certDisplayFileName(key: string): string {
      if (this.certFileNames[key]) {
        return this.certFileNames[key];
      }
      const p = this.certFieldVals[key] || "";
      return p.split("/").pop() || p;
    },

    /** 자격증/면허 파일을 팝업(cert-file-preview.html)으로 연다 — 전엔 새 탭이었다(10/21).
     *  blob 으로 받아 blob: 주소로 띄워 X-Frame-Options 에 막히지 않고, PDF 도 같은 출처라 인쇄된다. */
    async viewCertFile(storagePath: string, title?: string) {
      if (!storagePath) {
        return;
      }
      this.closeCertFilePreview();
      this.certFilePreview = {
        open: true,
        title: title || storagePath.split("/").pop() || "",
        blobUrl: "",
        kind: "file",
        loading: true,
        fs: false,
      };
      try {
        const { url } = await this.sdk.fileStorage.getDownloadUrl(storagePath);
        const res = await fetch(url);
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const blob = await res.blob();
        // 로딩 중 닫았으면 버린다
        if (!this.certFilePreview.open) {
          return;
        }
        const isImage =
          blob.type.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp)$/i.test(storagePath);
        this.certFilePreview.kind = isImage ? "image" : "file";
        this.certFilePreview.blobUrl = URL.createObjectURL(blob);
      } catch {
        this.closeCertFilePreview();
        showError(this._t("admin.file_load_failed"));
      } finally {
        this.certFilePreview.loading = false;
      }
    },

    closeCertFilePreview() {
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {
          /* 이미 나온 상태 — 무시 */
        });
      }
      if (this.certFilePreview.blobUrl) {
        URL.revokeObjectURL(this.certFilePreview.blobUrl);
      }
      this.certFilePreview = {
        open: false,
        title: "",
        blobUrl: "",
        kind: "file",
        loading: false,
        fs: false,
      };
    },

    /** 뷰어 칸만 브라우저 전체 화면 — 다시 누르거나 ESC 로 나온다(문서 결재 미리보기와 같은 규칙) */
    toggleCertFileFullscreen(el: HTMLElement | undefined) {
      toggleElementFullscreen(el, (on) => {
        this.certFilePreview.fs = on;
        if (!on) {
          this.certFileFsExitAt = Date.now();
        }
      });
    },

    /** 전체 화면을 나가는 ESC 가 팝업까지 닫지 않게 — 전체 화면 중이거나 막 나온 직후(0.5초)면 true */
    certFileFsEscGuard(): boolean {
      return Boolean(document.fullscreenElement) || Date.now() - this.certFileFsExitAt < 500;
    },

    /** 인쇄 — 이미지는 한 장짜리 HTML 로, PDF 는 띄운 뷰어(blob: 같은 출처)를 그대로 인쇄한다 */
    printCertFilePreview() {
      const { blobUrl, kind, title } = this.certFilePreview;
      if (!blobUrl) {
        return;
      }
      if (kind === "image") {
        // printHtmlDocument 는 쓰자마자 인쇄해 그림이 그려지기 전이면 빈 종이가 나온다 — 그림 decode 뒤 인쇄
        const iframe = document.createElement("iframe");
        iframe.style.cssText = "position:fixed;width:0;height:0;border:0;opacity:0";
        document.body.appendChild(iframe);
        const doc = iframe.contentDocument;
        const win = iframe.contentWindow;
        if (!(doc && win)) {
          iframe.remove();
          return;
        }
        doc.open();
        doc.write(
          '<!doctype html><html><head><meta charset="utf-8"><style>@page{margin:12mm}html,body{margin:0}img{display:block;max-width:100%;max-height:100vh;margin:0 auto;object-fit:contain}</style></head><body></body></html>',
        );
        doc.close();
        const img = doc.createElement("img");
        img.alt = title || "";
        img.src = blobUrl;
        doc.body.appendChild(img);
        img
          .decode()
          .catch(() => {
            /* decode 실패여도 인쇄창은 연다 */
          })
          .then(() => {
            win.focus();
            win.print();
            window.setTimeout(() => iframe.remove(), 5000);
          });
        return;
      }
      const frame = document.querySelector<HTMLIFrameElement>(
        '[data-testid="cert-file-preview-modal"] iframe',
      );
      try {
        frame?.contentWindow?.focus();
        frame?.contentWindow?.print();
      } catch {
        window.open(blobUrl, "_blank");
      }
    },

    /* ──────────── certificate issuance history (재직증명서 발급내역) ──────────── */
    /* ──────────── document templates ──────────── */
    async loadDocumentTemplates() {
      try {
        this.docTemplates = (await this.sdk.documentTemplate.list()) || [];
        this.docTemplatesLoaded = true;
        if (this.docTemplateVariables.length === 0) {
          this.docTemplateVariables =
            ((await this.sdk.documentTemplate.variables()) as typeof this.docTemplateVariables) ||
            [];
        }
      } catch {
        this.docTemplates = [];
      }
    },

    /* ──────────── 자격증/면허 유형 커스텀 설정 ──────────── */
    async loadCertTypeConfigs() {
      try {
        this.certTypeConfigs =
          ((await this.sdk.certificationType.list()) as typeof this.certTypeConfigs) || [];
      } catch {
        this.certTypeConfigs = [];
      }
    },

    openAddCertType() {
      this.editingCertTypeId = "";
      this.certTypeForm = { name: "", description: "", isActive: true, fields: [] };
      this.showCertTypeModal = true;
    },

    openEditCertType(ct: Record<string, unknown>) {
      this.editingCertTypeId = String(ct.id);
      // 필드는 깊은 복사(원본 목록 오염 방지). defaultValue 등 미노출 속성도 라운드트립으로 보존.
      const fields = Array.isArray(ct.fields)
        ? (ct.fields as Record<string, unknown>[]).map((f) => ({ ...f }))
        : [];
      this.certTypeForm = {
        name: String(ct.name || ""),
        description: String(ct.description || ""),
        isActive: ct.isActive !== false,
        fields,
      };
      this.showCertTypeModal = true;
    },

    addCertTypeField() {
      this.certTypeForm.fields.push({
        key: `f_${crypto.randomUUID().slice(0, 8)}`,
        name: "",
        type: "text",
        description: "",
        required: false,
      });
    },

    removeCertTypeField(idx: number) {
      this.certTypeForm.fields.splice(idx, 1);
    },

    async saveCertType() {
      const name = this.certTypeForm.name.trim();
      if (!name) {
        this.notify("이름을 입력해 주세요.", "error");
        return;
      }
      const description = this.certTypeForm.description.trim();
      // 상태 select은 x-model로 문자열("true"/"false")을 줄 수 있어 boolean으로 강제 변환
      const isActive =
        this.certTypeForm.isActive === true || (this.certTypeForm.isActive as unknown) === "true";
      // 이름 빈 필드는 저장 제외. 나머지 속성(defaultValue 등)은 보존.
      const fields = this.certTypeForm.fields
        .filter((f) => String(f.name || "").trim())
        .map((f) => ({
          key: String(f.key || `f_${crypto.randomUUID().slice(0, 8)}`),
          name: String(f.name).trim(),
          type: (["text", "date", "file"].includes(String(f.type)) ? f.type : "text") as
            | "text"
            | "date"
            | "file",
          description: String(f.description || "").trim() || undefined,
          required:
            f.required === true || (f.required as unknown) === "true" || f.required === "on",
          defaultValue: f.defaultValue ? String(f.defaultValue) : undefined,
        }));
      try {
        if (this.editingCertTypeId) {
          await this.sdk.certificationType.update({
            id: this.editingCertTypeId,
            name,
            description: description || null,
            isActive,
            fields,
          });
        } else {
          await this.sdk.certificationType.create({
            name,
            description: description || undefined,
            isActive,
            fields,
          });
        }
        this.showCertTypeModal = false;
        await this.loadCertTypeConfigs();
        this.notify("자격 유형을 저장했어요.");
      } catch (err) {
        this.notify(
          `저장에 실패했어요: ${err instanceof Error ? err.message : String(err)}`,
          "error",
        );
      }
    },

    deleteCertType(ct: Record<string, unknown>) {
      const name = String(ct.name || "");
      const isActive = ct.isActive !== false;
      const usageCount = Number(ct.usageCount || 0);
      this.confirmTitle = "삭제";
      this.confirmLabel = this._t("common.confirm");
      // ① 활성 상태 → 삭제 불가 (확인만)
      if (isActive) {
        this.confirmMessage = `${name} 유형을 삭제할 수 없어요. 먼저 비활성 상태로 변경한 뒤 삭제할 수 있어요.`;
        this.confirmHideCancel = true;
        this.confirmAction = null;
        this.showConfirm = true;
        return;
      }
      // ② 비활성 + 사용 이력 있음 → 삭제 불가 (확인만)
      if (usageCount > 0) {
        this.confirmMessage = `${name} 등록된 기록이 있어요. 등록된 기록이 있으면 삭제할 수 없어요.`;
        this.confirmHideCancel = true;
        this.confirmAction = null;
        this.showConfirm = true;
        return;
      }
      // ③ 비활성 + 사용 이력 없음 → 삭제 확인
      this.confirmMessage = `${name} 유형을 삭제할까요?`;
      this.confirmAction = async () => {
        try {
          await this.sdk.certificationType.delete(String(ct.id));
          await this.loadCertTypeConfigs();
          this.notify("자격 유형을 삭제했어요.");
        } catch (err) {
          this.notify(
            `삭제에 실패했어요: ${err instanceof Error ? err.message : String(err)}`,
            "error",
          );
        }
      };
      this.showConfirm = true;
    },

    toggleDocTemplateExpand(id: string) {
      this.expandedDocTemplate = this.expandedDocTemplate === id ? "" : id;
    },

    // 프리셋 단일 소스: packages/core/src/constants/document-templates.ts
    // (증명서 3종 + 결재/신청 서식 8종 — 시드·전자결재 렌더 폴백과 공유)
    docTemplatePresets: DOCUMENT_TEMPLATE_PRESETS,

    /** 신청 시간 단위 선택지(15분 · 30분 · 1시간) — 설정 편집 select */
    requestTimeStepChoices: REQUEST_TIME_STEP_OPTIONS.map((value) => ({
      value,
      label: REQUEST_TIME_STEP_LABELS[value],
    })),

    /** 보기 카드용 라벨 — 저장된 설정을 해석한 값(없으면 기본 30분) */
    requestTimeStepLabel(): string {
      return REQUEST_TIME_STEP_LABELS[resolveRequestTimeStepMinutes(this.orgSettings)];
    },

    /** 보기 카드용 — 연장 유형 단순화(MED-965) 켬/끔 */
    simplifiedOvertimeTypeLabel(): string {
      return resolveSimplifiedOvertimeType(this.orgSettings)
        ? "시간외 근무 하나로 신청(분류 자동)"
        : "직원이 유형을 고름";
    },

    /** 보기 카드용 — 저장된 설정을 해석한 값(키가 없으면 켬) */
    screenCaptureProtectionEnabled(): boolean {
      return resolveScreenCaptureProtection(this.orgSettings);
    },

    /** 임시 비밀번호 유효기간 입력 → 저장값. 1~90 정수만, 그 외(빈칸·0·범위 밖)는 null = 기한 없음 */
    pwValidityDaysPayload(): number | null {
      const d = Math.trunc(Number(this.orgSettingsForm.pwValidityDays));
      return Number.isFinite(d) && d >= 1 && d <= 90 ? d : null;
    },

    /** 증명서 양식(재직·경력·퇴직)인가 — 신청 입력 항목 섹션은 이 종류에만 보인다 */
    isCertificateDocTemplate(): boolean {
      return isCertificateTemplateCode(this.docTemplateForm.code || "");
    },

    /** 인사처리 결재 서식(사직서·휴직신청서·인사발령품의서)인가 — 「첨부 파일」 필수/선택 설정은 이 종류에만 보인다(MED-902) */
    isHrActionDocTemplate(): boolean {
      return hrActionKindByFormTemplateCode(this.docTemplateForm.code || "") !== null;
    },

    /** 서식 코드의 카탈로그 기본값(사직서 필수 · 휴직/발령 선택) — 저장값이 없을 때 라디오 초기값 */
    _docTemplateAttachmentDefault(code: string): boolean {
      const kind = hrActionKindByFormTemplateCode(code);
      return kind ? HR_ACTION_CATALOG[kind].requiresFile : false;
    },

    /** 저장된 설정(null = 기본값) → 편집행. 카탈로그 전 항목을 순서대로, 사용/필수 체크로 편집 */
    _requestFieldRows(config: { key: string; required: boolean }[] | null | undefined) {
      const source = config ?? DEFAULT_CERTIFICATE_REQUEST_FIELDS;
      return CERTIFICATE_REQUEST_FIELD_CATALOG.map((def) => {
        const c = source.find((x) => x.key === def.key);
        return {
          key: def.key,
          label: def.label,
          use: Boolean(def.alwaysOn) || Boolean(c),
          required: Boolean(def.alwaysOn) || Boolean(c?.required),
          alwaysOn: Boolean(def.alwaysOn),
        };
      });
    },

    /** 편집행 → 저장 페이로드(사용 항목만). 기본값과 같으면 null 로 저장해 "설정 없음"을 유지 */
    _requestFieldsPayload(): { key: CertificateRequestFieldKey; required: boolean }[] | null {
      const rows = this.docTemplateForm.requestFields || [];
      const chosen = rows
        .filter((r) => r.use)
        .map((r) => ({ key: r.key as CertificateRequestFieldKey, required: r.required }));
      const isDefault =
        chosen.length === DEFAULT_CERTIFICATE_REQUEST_FIELDS.length &&
        chosen.every((c, i) => {
          const d = DEFAULT_CERTIFICATE_REQUEST_FIELDS[i];
          return d.key === c.key && d.required === c.required;
        });
      return isDefault ? null : chosen;
    },

    /** 사용 체크를 끄면 필수도 함께 끈다(필수인데 안 받는 항목은 성립 불가) */
    toggleRequestFieldUse(row: { use: boolean; required: boolean; alwaysOn: boolean }) {
      if (row.alwaysOn) {
        return;
      }
      row.use = !row.use;
      if (!row.use) {
        row.required = false;
      }
    },

    /** 목록 줄 — 증명서 묶음(저장된 것 + 아직 저장 안 된 기본 증명서) · 결재 서식 9종 */
    docTemplateRows(): DocTemplateRow<StoredDocTemplate & Record<string, unknown>>[] {
      if (!this.docTemplatesLoaded) {
        return [];
      }
      return buildDocTemplateRows(
        this.docTemplates as unknown as (StoredDocTemplate & Record<string, unknown>)[],
        this.docTemplatePresets,
      );
    },

    /** 「어떤 증명서에서 시작할까요?」 선택지 — 직원이 지금 신청할 수 있는 증명서 그대로(빈 양식은 따로) */
    docTemplateStartSources(): {
      key: string;
      name: string;
      body: string;
      css: string;
      requestFields: unknown;
    }[] {
      return this.docTemplateRows()
        .filter((r) => r.section === "certificate")
        .map((r) =>
          r.source === "stored"
            ? {
                key: r.key,
                name: r.template.name,
                body: String(r.template.bodyTemplate ?? ""),
                css: String(r.template.cssTemplate ?? ""),
                requestFields: r.template.requestFields ?? null,
              }
            : {
                key: r.key,
                name: r.preset.name,
                body: r.preset.bodyTemplate,
                css: r.preset.cssTemplate || "",
                requestFields: null,
              },
        );
    },

    /** 같은 이름의 양식이 있는가 — 저장 버튼을 잠그고 이름 칸 아래에 안내한다 */
    docTemplateNameDuplicate(): boolean {
      return hasDuplicateTemplateName(
        this.docTemplateForm.name || "",
        this.docTemplates as unknown as { id: string; name: string }[],
        this.editingDocTemplateId,
      );
    },

    /** 팝업 제목 — 종류에 맞게(증명서 추가 · 증명서 수정 · 결재 서식 변경 · 결재 서식 수정) */
    docTemplateModalTitle(): string {
      const cert = this.isCertificateDocTemplate();
      if (this.docTemplateMode === "addCert") {
        return "증명서 추가";
      }
      if (this.docTemplateMode === "changePreset") {
        return `${cert ? "증명서 변경" : "결재 서식 변경"} · ${this.docTemplateForm.name}`;
      }
      return cert ? "증명서 수정" : "결재 서식 수정";
    },

    docTemplateModalSubtitle(): string {
      if (this.docTemplateMode === "addCert") {
        return this.docTemplateStep === "start"
          ? "직원이 앱에서 신청할 수 있는 증명서를 만들어요."
          : this.docTemplateStartName
            ? `${this.docTemplateStartName}에서 시작했어요`
            : "빈 양식에서 만들고 있어요";
      }
      if (this.docTemplateMode === "changePreset") {
        return "지금은 기본 서식을 쓰고 있어요. 저장하면 우리 병원 서식으로 바뀌어요.";
      }
      return "";
    },

    _resetDocTemplateForm(fields: Partial<DocTemplateFormState>) {
      this.docTemplatePreviewMode = false;
      this.docTemplatePanelTab = "info";
      this.docTemplateVarSearch = "";
      this._resetDocTemplatePanelScroll();
      this.docTemplateForm = {
        code: "",
        isActive: true,
        name: "",
        description: "",
        bodyTemplate: "",
        cssTemplate: "",
        attachmentRequired: false,
        requestFields: this._requestFieldRows(null),
        ...fields,
      };
      this.docTemplateFormSnapshot = this._docTemplateSnapshot();
      // Seed the body baseline so it isn't stale before the editor's onReady
      // refines it to the normalized form (avoids a transient "dirty" flash).
      this.docTemplateBodyBaseline = this.docTemplateForm.bodyTemplate;
      this.docEditorReady = false;
    },

    /** 「증명서 추가」 — 1단계(시작 고르기)부터. 편집기는 2단계로 넘어갈 때 띄운다. */
    openAddDocTemplate() {
      this.editingDocTemplateId = "";
      this.docTemplateMode = "addCert";
      this.docTemplateStep = "start";
      this.docTemplateStartKey = this.docTemplateStartSources()[0]?.key ?? "blank";
      this._resetDocTemplateForm({});
      this.showDocTemplateForm = true;
    },

    /** 1단계 → 2단계: 고른 증명서의 내용·신청 항목을 채우고 이름은 「… (복사본)」, 코드는 새로 만든다 */
    docTemplateNextStep() {
      const names = (this.docTemplates as unknown as { name: string }[]).map((t) => t.name);
      const codes = (this.docTemplates as unknown as { code: string }[]).map((t) => t.code);
      const source =
        this.docTemplateStartKey === "blank"
          ? undefined
          : (this.docTemplateStartSources().find((x) => x.key === this.docTemplateStartKey) ??
            this.docTemplateStartSources()[0]);
      this.docTemplateStartName = source?.name ?? "";
      const body = source ? source.body : "<h1>증명서</h1><p></p>";
      this._resetDocTemplateForm({
        code: newCertificateCode(codes),
        name: source ? copyTemplateName(source.name, names) : "",
        bodyTemplate: body,
        cssTemplate: source ? source.css : "",
        requestFields: this._requestFieldRows(
          (source?.requestFields as { key: string; required: boolean }[] | null) ?? null,
        ),
      });
      this.docTemplateStep = "write";
      Alpine.nextTick(() => this.initDocEditor(body));
    },

    /** 2단계 → 1단계: 작성한 내용이 있으면 사라진다고 확인한 뒤 돌아간다 */
    async docTemplatePrevStep() {
      this.syncFromEditor();
      if (this.docTemplateDirty()) {
        const ok = await askConfirm({
          message: "시작할 증명서를 다시 고를까요?\n지금 작성한 내용은 사라져요.",
          emphasis: "지금 작성한 내용은 사라져요.",
          confirmLabel: "다시 고르기",
        });
        if (!ok) {
          return;
        }
      }
      destroyDocumentEditor("#docTemplateEditor");
      this.docEditorFullscreen = false;
      this._resetDocTemplateForm({});
      this.docTemplateStep = "start";
    },

    /** 목록의 「기본 서식 사용 중」 줄 — 기본 서식 내용으로 열고, 저장하면 같은 코드로 우리 병원 서식이 된다 */
    openChangePresetDocTemplate(code: string) {
      const preset = this.docTemplatePresets.find((p) => p.code === code);
      if (!preset) {
        return;
      }
      this.editingDocTemplateId = "";
      this.docTemplateMode = "changePreset";
      this.docTemplateStep = "write";
      this._resetDocTemplateForm({
        code: preset.code,
        name: preset.name,
        description: preset.description,
        bodyTemplate: preset.bodyTemplate,
        cssTemplate: preset.cssTemplate || "",
        attachmentRequired: this._docTemplateAttachmentDefault(preset.code),
      });
      this.showDocTemplateForm = true;
      Alpine.nextTick(() => this.initDocEditor(preset.bodyTemplate));
    },

    /** 편집 칸 높이(카드에 맞추기 전 · 좁은 화면) — 팝업 높이(90vh · 전체화면이면 화면)에서 제목 · 탭 줄 · 편집 도구 줄 · 아래 버튼 줄 몫을 뺀다 */
    docTemplateEditorHeight(): number {
      const card = this.docTemplateModalFull
        ? window.innerHeight
        : Math.round(window.innerHeight * 0.9);
      return Math.max(300, card - 300);
    },

    /** 편집 칸 높이를 오른쪽 카드에 꼭 맞춘다(왼쪽 패널과 같은 높이 · 안에서 스크롤). 좁은 화면(위아래로 쌓임)은 고정 높이 */
    fitDocTemplateEditor() {
      const host = document.querySelector<HTMLElement>("[data-doc-editor-host]");
      const editor = host?.querySelector<HTMLElement>(".sun-editor");
      // .se-wrapper-inner 는 둘(편집 칸 · 숨은 코드 보기) — 보이는 것을 잰다
      const inner = Array.from(host?.querySelectorAll<HTMLElement>(".se-wrapper-inner") ?? []).find(
        (el) => el.offsetHeight > 0,
      );
      if (!(host && editor && inner)) {
        return;
      }
      if (!window.matchMedia("(min-width: 768px)").matches || host.clientHeight === 0) {
        setEditorHeight("#docTemplateEditor", this.docTemplateEditorHeight());
        return;
      }
      // 편집 도구 줄 등 편집 칸 밖 몫 = 편집기 전체 − 편집 칸
      const chrome = editor.offsetHeight - inner.offsetHeight;
      setEditorHeight("#docTemplateEditor", Math.max(200, host.clientHeight - chrome));
    },

    /** 카드 크기가 바뀌면(창 크기 · 전체화면) 다시 맞춘다 — 편집기를 만들 때 한 번 건다 */
    _watchDocTemplateEditorFit() {
      const host = document.querySelector<HTMLElement>("[data-doc-editor-host]");
      if (!host || host.dataset.fitWatched) {
        return;
      }
      host.dataset.fitWatched = "1";
      new ResizeObserver(() => this.fitDocTemplateEditor()).observe(host);
    },

    /** 이 문서에서 채워지는 자동 정보 — 묶음별(문서 종류에 맞는 것만, doc-template-variables.ts) */
    docTemplateVarGroups(): DocVariableGroup[] {
      return variableGroupsFor(this.docTemplateForm.code || "", this.docTemplateVariables);
    },

    /** 검색어로 거른 묶음(빈 묶음은 뺀다) */
    docTemplateVarGroupsShown(): DocVariableGroup[] {
      const q = this.docTemplateVarSearch.trim();
      const groups = this.docTemplateVarGroups();
      if (!q) {
        return groups;
      }
      return groups
        .map((g) => ({ ...g, variables: g.variables.filter((v) => v.label.includes(q)) }))
        .filter((g) => g.variables.length > 0);
    },

    /** 왼쪽 패널에 보이는 묶음 — 결재 서식 변경(이름 고정)에서 첨부도 없으면 「정보」 묶음은 비어 빠진다 */
    docTemplatePanelSections(): ("info" | "vars" | "fields")[] {
      const out: ("info" | "vars" | "fields")[] = [];
      if (
        this.docTemplateMode !== "changePreset" ||
        this.isCertificateDocTemplate() ||
        this.isHrActionDocTemplate()
      ) {
        out.push("info");
      }
      out.push("vars");
      if (this.isCertificateDocTemplate()) {
        out.push("fields");
      }
      return out;
    },

    /** 켜진 앵커 탭 — 지금 값이 빠진 묶음이면 첫 묶음 */
    docTemplatePanelActive(): "info" | "vars" | "fields" {
      const sections = this.docTemplatePanelSections();
      return sections.includes(this.docTemplatePanelTab) ? this.docTemplatePanelTab : sections[0];
    },

    /** 앵커 탭 — 그 묶음 머리가 패널 맨 위로 오게 스크롤 */
    scrollDocTemplatePanel(id: "info" | "vars" | "fields") {
      this.docTemplatePanelTab = id;
      const box = document.querySelector<HTMLElement>("[data-doc-panel-box]");
      const el = box?.querySelector<HTMLElement>(`[data-doc-panel-section="${id}"]`);
      if (!(box && el)) {
        return;
      }
      this.docPanelPinned = true;
      const top = el.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop;
      // 묶음 위 경계선이 패널 맨 위에 오게 — 첫 묶음은 맨 위로
      const first = this.docTemplatePanelSections()[0] === id;
      box.scrollTo({ top: first ? 0 : top, behavior: "smooth" });
    },

    /** 스크롤 스파이 — 사용자가 패널을 스크롤하면 보고 있는 묶음의 탭을 켠다 */
    onDocTemplatePanelScroll(box: HTMLElement) {
      if (this.docPanelPinned) {
        return;
      }
      const boxTop = box.getBoundingClientRect().top;
      const items = Array.from(box.querySelectorAll<HTMLElement>("[data-doc-panel-section]"))
        .filter((el) => el.getClientRects().length > 0)
        .map((el) => ({
          id: el.dataset.docPanelSection as string,
          top: el.getBoundingClientRect().top - boxTop,
        }));
      const atBottom = box.scrollTop + box.clientHeight >= box.scrollHeight - 2;
      const id = pickActiveSection(items, 40, atBottom);
      if (id) {
        this.docTemplatePanelTab = id as "info" | "vars" | "fields";
      }
    },

    /** 팝업을 열 때 패널을 맨 위로 */
    _resetDocTemplatePanelScroll() {
      this.docPanelPinned = false;
      document.querySelector<HTMLElement>("[data-doc-panel-box]")?.scrollTo({ top: 0 });
    },

    /** 본문에 있지만 이 문서에서 채워지지 않을 {{…}} */
    docTemplateUnfilled(): string[] {
      return unfilledPlaceholders(
        this.docTemplateForm.bodyTemplate || "",
        this.docTemplateVarGroups(),
      );
    },

    /** 편집 칸 안 {{…}} 강조를 다시 칠한다(채워지는 것 주황 · 아닌 것 빨강) */
    _refreshDocHighlight() {
      const bad = new Set(this.docTemplateUnfilled());
      highlightPlaceholders("#docTemplateEditor", (name) => !bad.has(name));
    },

    /** 양식 팝업 전체화면 켜고 끄기(시트 전체화면처럼 종료 버튼은 화면 상단 가운데) */
    setDocTemplateFull(on: boolean) {
      this.docTemplateModalFull = on;
      Alpine.nextTick(() => this.fitDocTemplateEditor());
    },

    /** 미리보기 팝업 열기/닫기 — 예시 직원 정보로 채운 결과를 양식 팝업 위 팝업에서 본다 */
    setDocTemplatePreview(on: boolean) {
      if (on) {
        this.syncFromEditor();
        this.generateDocTemplatePreview();
      }
      this.docTemplatePreviewMode = on;
    },

    /** 메타 정보는 이 스냅샷으로, 본문은 편집기의 초기 HTML과 현재 상태로 변경을 판단한다. */
    _docTemplateSnapshot(): string {
      return JSON.stringify({
        name: this.docTemplateForm.name.trim(),
        isActive: this.docTemplateForm.isActive,
        description: (this.docTemplateForm.description || "").trim(),
        attachmentRequired: this.docTemplateForm.attachmentRequired,
        requestFields: (this.docTemplateForm.requestFields || []).map((r) => [
          r.key,
          r.use,
          r.required,
        ]),
      });
    },

    /** Whether the doc-template form has unsaved changes vs. the baselines taken
     *  on open: meta fields (name/description) OR the editor body. Both compare
     *  reactive Alpine state (bodyTemplate is kept in sync by the editor's
     *  onChange), so the save/close bindings re-evaluate as the user edits. */
    docTemplateDirty(): boolean {
      const metaDirty = this._docTemplateSnapshot() !== this.docTemplateFormSnapshot;
      const bodyDirty = this.docTemplateForm.bodyTemplate !== this.docTemplateBodyBaseline;
      return metaDirty || bodyDirty;
    },

    /** 저장 버튼 비활성 여부. 모든 반응형 입력(name·editingId·dirty)을 **먼저 읽어** 의존성을
     *  항상 추적한다 — 인라인 `:disabled` 식에서 dirty() 호출이 add 모드에 단락되며 name 의존성이
     *  누락돼 버튼이 비활성으로 고정되던 버그 방지. (openAddDocTemplate의 폼 객체 교체와 결합된 문제) */
    docTemplateSaveDisabled(): boolean {
      const hasName = !!(this.docTemplateForm.name || "").trim();
      const isEdit = !!this.editingDocTemplateId;
      const dirty = this.docTemplateDirty();
      const duplicate = this.docTemplateNameDuplicate();
      return !(this.docEditorReady && hasName) || duplicate || (isEdit && !dirty);
    },

    /** 문서 양식(문서 제출 유형) 한 줄을 전체 화면으로 — 화면 밖에 둔 칸(settings.html x-ref docTplFs)에 문서를 넣고 그 칸을 띄운다.
     *  requestFullscreen 은 클릭 안에서 바로 불러야 해서 칸은 늘 DOM 에 있고, 전체 화면일 때만 UA 규칙이 화면 전체로 편다. */
    openDocTemplateFullscreen(
      row: {
        source: string;
        template?: { bodyTemplate: string; cssTemplate?: string | null; name?: string };
        preset?: { bodyTemplate: string; cssTemplate?: string | null; name?: string };
      },
      el: HTMLElement | undefined,
    ) {
      const src = row.source === "stored" ? row.template : row.preset;
      if (!src) {
        return;
      }
      this.docTplFs.html = this.buildDocPreviewHtml(src.bodyTemplate, src.cssTemplate || "");
      this.docTplFs.title = src.name || "";
      toggleElementFullscreen(el, (on) => {
        this.docTplFs.on = on;
        if (!on) {
          this.docTplFs.html = "";
        }
      });
    },

    buildDocPreviewHtml(body: string, css = "") {
      const examples: Record<string, string> = {};
      for (const v of this.docTemplateVariables) {
        examples[v.label] = v.example;
        examples[v.key] = v.example; // backward compat for old templates
      }
      const filled = body.replace(
        /\{\{([^}]+)\}\}/g,
        (_: string, key: string) => examples[key] || `{{${key}}}`,
      );
      return `<!DOCTYPE html><html lang="ko"><head><meta charset="UTF-8"><style>
@page{size:A4;margin:20mm}body{font-family:'Malgun Gothic',sans-serif;color:#111;margin:0;padding:40px}
.cert{max-width:700px;margin:0 auto}h1{text-align:center;font-size:28px;letter-spacing:12px;margin-bottom:40px}
table{width:100%;border-collapse:collapse;margin-bottom:30px}th,td{border:1px solid #333;padding:10px 14px;font-size:14px}
th{background:#f5f5f5;width:25%;text-align:left;font-weight:600}td{width:75%}
:where(table:has(> colgroup > col[style*="width:"])) > :where(thead,tbody,tfoot) > :where(tr) > :is(th,td){width:auto}
.statement{text-align:center;font-size:15px;line-height:1.8;margin:30px 0 50px}
.purpose{text-align:center;font-size:15px;margin:30px 0 50px}.purpose strong{font-size:16px}
.date{text-align:center;font-size:15px;margin:40px 0 50px}
.org-block{text-align:center;font-size:15px;line-height:2}.org-name-row{font-size:18px;font-weight:700}
.cert-number{text-align:right;font-size:12px;color:#666;margin-bottom:10px}
${css}</style></head><body><div class="cert">
<p class="cert-number">증명서 번호: CERT-20260402-EMP001</p>
${filled}
<div class="org-block"><div>서울시 강남구</div><div>사업자등록번호: 123-45-67890</div>
<div class="org-name-row">베스트재활의원&nbsp;&nbsp;대표 홍길동
<span style="display:inline-block;border:2px solid #c00;border-radius:50%;width:60px;height:60px;line-height:60px;text-align:center;color:#c00;font-weight:700;font-size:14px;margin-left:10px;vertical-align:middle;">직인</span>
</div></div></div></body></html>`;
    },

    generateDocTemplatePreview() {
      this.docTemplatePreviewHtml = this.buildDocPreviewHtml(
        this.docTemplateForm.bodyTemplate,
        this.docTemplateForm.cssTemplate || "",
      );
    },

    async initDocEditor(content: string) {
      if (!this.showDocTemplateForm) {
        return;
      }
      try {
        await initDocumentEditor("#docTemplateEditor", {
          initialContent: content,
          cssTemplate: this.docTemplateForm.cssTemplate || "",
          height: 400,
          onFullscreenChange: (fullscreen: boolean) => {
            this.docEditorFullscreen = fullscreen;
          },
          onReady: (normalized: string) => {
            // 정규화된 저장 HTML을 기준으로 삼아 열기만 한 양식은 변경으로 표시하지 않는다.
            this.docTemplateForm.bodyTemplate = normalized;
            this.docTemplateBodyBaseline = normalized;
            this.docEditorReady = true;
            this.fitDocTemplateEditor();
            this._watchDocTemplateEditorFit();
            this._refreshDocHighlight();
          },
          onChange: (html: string) => {
            this.docTemplateForm.bodyTemplate = html;
            this._refreshDocHighlight();
          },
        });
      } catch {
        this.notify("문서 편집기를 불러오지 못했어요. 창을 다시 열어 주세요.", "error");
      }
    },

    syncFromEditor() {
      // 편집기가 없으면 기존 값을 유지하고, 사용자가 지운 빈 본문은 그대로 반영한다.
      const content = getContent("#docTemplateEditor");
      if (content !== null) {
        this.docTemplateForm.bodyTemplate = content;
      }
    },

    insertDocTemplateVariable(key: string) {
      const v = this.docTemplateVariables.find(
        (item: { key: string; label: string }) => item.key === key,
      );
      insertAtCursor("#docTemplateEditor", `{{${v?.label || key}}}`);
    },

    closeDocTemplateForm() {
      this.docTemplateModalFull = false;
      destroyDocumentEditor("#docTemplateEditor");
      this.docEditorReady = false;
      this.docEditorFullscreen = false;
      this.showDocTemplateForm = false;
    },

    openEditDocTemplate(tmpl: Record<string, unknown>) {
      this.editingDocTemplateId = tmpl.id as string;
      this.docTemplateMode = "edit";
      this.docTemplateStep = "write";
      this.docTemplatePreviewMode = false;
      this.docTemplatePanelTab = "info";
      this.docTemplateVarSearch = "";
      this._resetDocTemplatePanelScroll();
      this.docTemplateForm = {
        code: (tmpl.code as string) || "",
        isActive: tmpl.isActive !== false,
        name: (tmpl.name as string) || "",
        description: (tmpl.description as string) || "",
        bodyTemplate: (tmpl.bodyTemplate as string) || "",
        cssTemplate: (tmpl.cssTemplate as string) || "",
        attachmentRequired:
          typeof tmpl.attachmentRequired === "boolean"
            ? tmpl.attachmentRequired
            : this._docTemplateAttachmentDefault((tmpl.code as string) || ""),
        requestFields: this._requestFieldRows(
          (tmpl.requestFields as { key: string; required: boolean }[] | null) ?? null,
        ),
      };
      this.docTemplateFormSnapshot = this._docTemplateSnapshot();
      // Seed the body baseline so it isn't stale before the editor's onReady
      // refines it to the normalized form (avoids a transient "dirty" flash).
      this.docTemplateBodyBaseline = this.docTemplateForm.bodyTemplate;
      this.docEditorReady = false;
      this.showDocTemplateForm = true;
      Alpine.nextTick(() => this.initDocEditor(this.docTemplateForm.bodyTemplate));
    },

    async saveDocTemplate() {
      if (!this.docEditorReady) {
        return;
      }
      const f = this.docTemplateForm;
      if (!f.code) {
        return;
      }
      if (!f.name) {
        return;
      }
      // 본문을 지운 상태에서 저장하면 입력이 필요하다는 안내를 표시한다.
      if (!f.bodyTemplate) {
        this.notify("문서 내용을 입력해 주세요.", "error");
        return;
      }
      // 이 문서에서 채워지지 않을 {{…}} 가 있으면 알린다 — 저장은 허용(그 자리는 발급 · 결재 문서에서 빈칸)
      const unfilled = this.docTemplateUnfilled();
      if (unfilled.length > 0) {
        const kind = this.isCertificateDocTemplate() ? "증명서" : "서식";
        const ok = await askConfirm({
          message: `이 ${kind}에서 채울 수 없는 정보가 있어요.\n${unfilled.map((n) => `{{${n}}}`).join(" · ")}\n저장하면 그 자리는 빈칸으로 나와요.`,
          emphasis: "저장하면 그 자리는 빈칸으로 나와요.",
          confirmLabel: "그래도 저장",
        });
        if (!ok) {
          return;
        }
      }
      const mode = this.docTemplateMode;
      const isCert = this.isCertificateDocTemplate();
      // 결재 서식은 공개 여부가 없다 — 비활성이면 기본 서식으로 돌아가 버려 「고친 서식」이 조용히 안 쓰인다
      const isActive = isCert ? f.isActive : true;
      try {
        // 신청 입력 항목은 증명서 양식에만 의미가 있다 — 결재 서식엔 null 로 보낸다
        const requestFields = isCert ? this._requestFieldsPayload() : null;
        // 첨부 필수 여부는 인사처리 결재 서식에만 의미가 있다 — 그 외엔 null(카탈로그 기본)
        const attachmentRequired = this.isHrActionDocTemplate() ? f.attachmentRequired : null;
        if (this.editingDocTemplateId) {
          await this.sdk.documentTemplate.update({
            id: this.editingDocTemplateId,
            name: f.name,
            bodyTemplate: f.bodyTemplate,
            cssTemplate: f.cssTemplate || null,
            description: f.description || null,
            requestFields,
            attachmentRequired,
            ...(isCert ? { isActive } : {}),
          });
        } else {
          await this.sdk.documentTemplate.create({
            code: f.code,
            name: f.name,
            bodyTemplate: f.bodyTemplate,
            cssTemplate: f.cssTemplate || undefined,
            description: f.description || undefined,
            requestFields,
            attachmentRequired,
            isActive,
          });
        }
        const name = f.name.trim();
        this.closeDocTemplateForm();
        await this.loadDocumentTemplates();
        if (mode === "addCert") {
          this.notify(
            isActive
              ? `「${name}」을(를) 추가했어요. 직원 앱 증명서 신청 목록에 바로 보여요.`
              : `「${name}」을(를) 비공개로 추가했어요. 공개하면 직원 앱에 보여요.`,
          );
        } else if (mode === "changePreset") {
          this.notify(`「${name}」을(를) 우리 병원 서식으로 바꿨어요.`);
        } else {
          this.notify(`「${name}」을(를) 저장했어요.`);
        }
      } catch (err) {
        showError(
          `${this._t("admin.save_failed")}: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    },

    /** 삭제 — 증명서는 직원 앱 목록에서도 사라지고, 고친 결재 서식은 기본 서식으로 되돌아간다 */
    async deleteDocTemplate(tmpl: Record<string, unknown>) {
      const id = String(tmpl.id);
      const name = String(tmpl.name || "양식");
      const code = String(tmpl.code || "");
      const isForm = !isCertificateTemplateCode(code);
      const hasPreset = this.docTemplatePresets.some((p) => p.code === code);
      const revert = isForm && hasPreset;
      const ok = await askConfirm(
        revert
          ? {
              message: `「${name}」을(를) 기본 서식으로 되돌릴까요?\n지금 고친 내용은 사라지고, 결재 문서에 기본 서식이 쓰여요.`,
              emphasis: "지금 고친 내용은 사라지고, 결재 문서에 기본 서식이 쓰여요.",
              confirmLabel: "되돌리기",
              tone: "danger",
            }
          : {
              message: `「${name}」을(를) 삭제할까요?\n${isForm ? "삭제하면 되돌릴 수 없어요." : "직원 앱 증명서 신청 목록에서도 사라져요."}`,
              emphasis: isForm
                ? "삭제하면 되돌릴 수 없어요."
                : "직원 앱 증명서 신청 목록에서도 사라져요.",
              confirmLabel: this._t("common.delete"),
              tone: "danger",
            },
      );
      if (!ok) {
        return;
      }
      try {
        await this.sdk.documentTemplate.delete(id);
        await this.loadDocumentTemplates();
        this.notify(
          revert ? `「${name}」을(를) 기본 서식으로 되돌렸어요.` : `「${name}」을(를) 삭제했어요.`,
        );
      } catch (err) {
        showError(
          `${this._t("admin.delete_failed")}: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    },

    /** 직원명부 다운로드 항목(컬럼) 선택 팝업 상태.
     *  라벨↔서버 key 매핑은 rosterExportItems 로 노출(HTML 이 순회하며 체크박스 렌더).
     *  기본값은 전부 체크 (기획서: default = 전부 체크). */
    showRosterExport: false,
    rosterExportSelected: {
      num: true,
      name: true,
      team: true,
      jobDuty: true,
      ward: true,
      status: true,
      hireDate: true,
      email: true,
      phone: true,
      shift: true,
    } as Record<
      | "num"
      | "name"
      | "team"
      | "jobDuty"
      | "ward"
      | "status"
      | "hireDate"
      | "email"
      | "phone"
      | "shift",
      boolean
    >,
    /** 렌더용 라벨 순서(기획서 표 순서 + 팀 · 직무 · 병동은 성명 뒤). */
    rosterExportItems: [
      { key: "num", label: "사번" },
      { key: "name", label: "성명" },
      { key: "team", label: "팀" },
      { key: "jobDuty", label: "직무" },
      { key: "ward", label: "병동" },
      { key: "status", label: "상태" },
      { key: "hireDate", label: "입사일" },
      { key: "email", label: "이메일" },
      { key: "phone", label: "전화번호" },
      { key: "shift", label: "근무형태" },
    ] as {
      key:
        | "num"
        | "name"
        | "team"
        | "jobDuty"
        | "ward"
        | "status"
        | "hireDate"
        | "email"
        | "phone"
        | "shift";
      label: string;
    }[],

    /** 팝업이 다운로드할 범위 — null 이면 직원 관리 탭(목록 필터 그대로),
     *  아니면 팀 관리 뷰에서 넘어온 범위(팀 · 상태 · 직무 · 검색어)를 그대로 쓴다. */
    rosterExportScope: null as {
      teamIds?: string[];
      statuses?: string[];
      jobDuties?: string[];
      search?: string;
    } | null,

    /** [직원명부/명단 다운로드] 버튼 → 컬럼 선택 팝업 오픈 (매번 전부 체크 상태로 리셋).
     *  scope 를 주면 해당 범위로 다운로드(팀 관리), 없으면 직원 관리 탭 필터 기준. */

    /* ── 직원 관리 「업로드」 — 신규 직원 대량 추가 · 기존 직원 대량 수정 (기획서 2026-07) ──
       공통: .xlsx/.xls, 최대 500행, All-or-Nothing. 업로드 즉시 dryRun 검증 → 오류 목록 표시,
       [확인] 은 오류 0건일 때만 활성화되어 커밋한다. */
    showUploadMenu: false,
    showBulkAddModal: false,
    showBulkUpdateModal: false,

    /** 대량 추가 상태 */
    bulkAdd: {
      fileName: "",
      fileError: "",
      rows: [] as Record<string, string | number>[],
      errors: [] as { row: number; message: string }[],
      validated: false,
      saving: false,
    },

    /** 대량 수정 상태 — 직원 선택은 모달 내부에서만 관리(목록 체크는 초기값으로만 사용). */
    bulkUpd: {
      candidates: [] as Record<string, unknown>[],
      loading: false,
      selectedIds: [] as string[],
      teamId: "",
      status: "",
      search: "",
      fileName: "",
      fileError: "",
      rows: [] as Record<string, string | number>[],
      errors: [] as { row: number; message: string }[],
      validated: false,
      saving: false,
    },

    /** 템플릿 헤더 정의 — 파싱 시 헤더 검증에도 사용 (헤더 변경 = 오류). */
    // 팀·직무·근무 그룹은 직원 등록 패널과 같이 필수다(근무 그룹은 비우면 팀 대표 근무 — 서버가 판정).
    // 뒤쪽 「직무」「근무 그룹」「근무 유형」은 열이 없는 예전 템플릿도 헤더 검사를 통과시키고,
    // 빠진 값은 서버가 행 오류로 안내한다(parseBulkFile 참고). 필수 표시 「 *」 유무도 헤더 검사에서 무시한다.
    bulkAddHeaders: [
      "성 *",
      "이름 *",
      "사번 *",
      "입사일 *",
      "생년월일",
      "이메일",
      "휴대폰 번호",
      "팀 *",
      "역할",
      "직무 *",
      "근무 그룹",
      "근무 유형",
      "근로형태",
    ],
    bulkUpdHeaders: [
      "사번 *",
      "이메일",
      "휴대폰 번호",
      "성 *",
      "이름 *",
      "입사일 *",
      "생년월일",
      "팀",
      "역할",
      "직무",
      "근로형태",
    ],

    openBulkAdd() {
      this.showUploadMenu = false;
      this.bulkAdd = {
        fileName: "",
        fileError: "",
        rows: [],
        errors: [],
        validated: false,
        saving: false,
      };
      this.showBulkAddModal = true;
    },

    async openBulkUpdate() {
      this.showUploadMenu = false;
      this.bulkUpd = {
        candidates: [],
        loading: true,
        // 직원 목록에서 체크된 직원을 초기 선택값으로 (이후 동기화하지 않음 — 기획서 2.3)
        selectedIds: [...(this.selectedEmployees as string[])],
        teamId: "",
        status: "",
        search: "",
        fileName: "",
        fileError: "",
        rows: [],
        errors: [],
        validated: false,
        saving: false,
      };
      this.showBulkUpdateModal = true;
      try {
        type Row = Awaited<ReturnType<HerebyClient["employee"]["getEmployees"]>>["items"][number];
        const items = await fetchAllPages<Row>(
          (page, limit) => this.sdk.employee.getEmployees({ page, limit }),
          1000,
        );
        this.bulkUpd.candidates = items.map((e) => ({
          ...e,
          fullName: `${e.lastName || ""}${e.firstName || ""}`.trim() || e.email,
        }));
      } catch {
        this.bulkUpd.candidates = [];
      } finally {
        this.bulkUpd.loading = false;
      }
    },

    /** 대량 수정 — 필터 적용된 후보 목록 */
    get bulkUpdFiltered(): Record<string, unknown>[] {
      const q = this.bulkUpd.search.trim().toLowerCase();
      return this.bulkUpd.candidates.filter((e) => {
        if (this.bulkUpd.status && e.status !== this.bulkUpd.status) {
          return false;
        }
        if (this.bulkUpd.teamId) {
          const ms = (e.memberships as { teamId: string }[] | undefined) || [];
          if (!ms.some((m) => m.teamId === this.bulkUpd.teamId)) {
            return false;
          }
        }
        if (!q) {
          return true;
        }
        return (
          String(e.fullName || "")
            .toLowerCase()
            .includes(q) ||
          String(e.employeeNumber || "")
            .toLowerCase()
            .includes(q) ||
          String(e.email || "")
            .toLowerCase()
            .includes(q)
        );
      });
    },

    bulkUpdToggle(id: string) {
      const i = this.bulkUpd.selectedIds.indexOf(id);
      if (i >= 0) {
        this.bulkUpd.selectedIds.splice(i, 1);
      } else if (this.bulkUpd.selectedIds.length >= 500) {
        this.notify("한 번에 최대 500명까지 선택할 수 있어요");
      } else {
        this.bulkUpd.selectedIds.push(id);
      }
    },

    /** 조건 전원 선택 — 현재 필터·검색 결과 전체 (최대 500명, 초과분은 안내) */
    bulkUpdSelectAllFiltered() {
      const ids = this.bulkUpdFiltered.map((e) => e.id as string);
      const merged = new Set(this.bulkUpd.selectedIds);
      for (const id of ids) {
        if (merged.size >= 500) {
          this.notify("한 번에 최대 500명까지 선택할 수 있어요. 초과분은 선택되지 않았어요.");
          break;
        }
        merged.add(id);
      }
      this.bulkUpd.selectedIds = [...merged];
    },

    /* ── 엑셀 유틸 (xlsx 동적 로드 — scheduler 내보내기와 동일 패턴) ── */

    /** 셀 값 → YYYY-MM-DD (엑셀 시리얼·문자열 모두 수용). 변환 규칙은 utils/excel-date — 시간대와 무관하게 입력한 날짜 그대로 */
    excelCellToDateStr(v: unknown, date1904 = false): string {
      return excelCellToDateStr(v, { date1904 });
    },

    async downloadBulkAddTemplate() {
      const xlsx = await import("xlsx");
      const ws = xlsx.utils.aoa_to_sheet([
        this.bulkAddHeaders.map((h: string) => this.bulkHeaderLabel(h)),
      ]);
      ws["!cols"] = this.bulkAddHeaders.map(() => ({ wch: 16 }));
      const wb = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(wb, ws, "신규 직원");
      xlsx.writeFile(wb, "신규직원_대량추가_템플릿.xlsx");
    },

    async downloadBulkUpdTemplate() {
      const xlsx = await import("xlsx");
      const sel = new Set(this.bulkUpd.selectedIds);
      const rows = this.bulkUpd.candidates
        .filter((e) => sel.has(e.id as string))
        .map((e) => [
          e.employeeNumber || "",
          e.email || "",
          e.phone || "",
          e.lastName || "",
          e.firstName || "",
          e.hireDate ? String(e.hireDate).slice(0, 10) : "",
          e.birthDate ? String(e.birthDate).slice(0, 10) : "",
          // 팀·역할은 현재 주 소속(활성 소속 중 첫 번째)을 미리 채운다 — 그대로 올리면 변경 없음
          this.bulkUpdPrimaryMembership(e).teamName,
          this.bulkUpdPrimaryMembership(e).roleName,
          (e.jobDuty as string | null) || "",
          this.workPatternNameById((e.workPatternId as string | null) || ""),
        ]);
      const ws = xlsx.utils.aoa_to_sheet([
        this.bulkUpdHeaders.map((h: string) => this.bulkHeaderLabel(h)),
        ...rows,
      ]);
      ws["!cols"] = this.bulkUpdHeaders.map(() => ({ wch: 16 }));
      const wb = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(wb, ws, "직원 수정");
      xlsx.writeFile(wb, "직원정보_대량수정_템플릿.xlsx");
    },

    /** 대량 수정 템플릿의 팀·역할 미리 채움 — 활성 소속 중 첫 번째(직원 명단 칩 순서와 동일). */
    bulkUpdPrimaryMembership(e: Record<string, unknown>): { teamName: string; roleName: string } {
      const ms =
        (e.memberships as { teamName?: string | null; roleName?: string | null }[] | undefined) ||
        [];
      const first = ms[0];
      return { teamName: first?.teamName || "", roleName: first?.roleName || "" };
    },

    /** 공통 파일 파싱 — 헤더 검증 + 행 상한 + 형식 검증. 실패 시 fileError, 성공 시 2차원 배열 반환. */
    /**
     * 템플릿 머리글에 보이는 낱말 — 「역할」 열은 병원이 정한 낱말(용어 표시)로 바꿔 내려준다.
     * 헤더 배열(bulkAddHeaders·bulkUpdHeaders)은 정본 낱말 그대로 두고 내보낼 때만 바꾼다.
     */
    bulkHeaderLabel(header: string): string {
      return header.replace(/^역할(?=\s*\*?$)/, this._term("role"));
    },

    async parseBulkFile(
      file: File,
      expectedHeaders: string[],
    ): Promise<
      | { error: string }
      | { rows: unknown[][]; hasJobDuty: boolean; hasWorkPattern: boolean; date1904: boolean }
    > {
      const name = file.name.toLowerCase();
      if (!(name.endsWith(".xlsx") || name.endsWith(".xls"))) {
        return { error: "엑셀 파일만 업로드할 수 있어요.\n지원 형식: .xlsx, .xls" };
      }
      const xlsx = await import("xlsx");
      const buf = await file.arrayBuffer();
      // 날짜 셀은 시리얼 숫자 그대로 받는다 — cellDates:true 는 KST 브라우저에서 하루 앞당겨진다(utils/excel-date)
      const wb = xlsx.read(buf, { cellDates: false });
      const date1904 = !!wb.Workbook?.WBProps?.date1904;
      const ws = wb.Sheets[wb.SheetNames[0]];
      if (!ws) {
        return { error: "엑셀 파일에서 시트를 찾을 수 없어요." };
      }
      const aoa = xlsx.utils.sheet_to_json(ws, { header: 1, defval: "" }) as unknown[][];
      const header = (aoa[0] || []).map((h) => String(h).trim());
      // 필수 표시(「 *」)는 비교에서 뺀다 — 표시가 없던 예전 템플릿(「팀」「직무」)도 같은 열로 본다.
      // 「역할」 열은 병원 낱말(용어 표시)로 내려가므로 그 낱말도, 낱말을 바꾸기 전 템플릿의 「역할」도 받는다.
      const roleTerm = this._term("role");
      const bare = (h: string | undefined) => {
        const stripped = (h ?? "").replace(/\s*\*$/, "");
        return stripped === roleTerm ? "역할" : stripped;
      };
      // 뒤쪽 선택 열(직무 · 근무 그룹 · 근무 유형)은 없어도(예전 템플릿) 통과시킨다
      const optionalTail = new Set(["직무", "근무 그룹", "근무 유형", "근로형태"]);
      const missing = expectedHeaders.some(
        (h, i) => bare(header[i]) !== bare(h) && !(optionalTail.has(bare(h)) && !header[i]),
      );
      const jobDutyIndex = expectedHeaders.findIndex((h) => bare(h) === "직무");
      const hasJobDuty = jobDutyIndex >= 0 && bare(header[jobDutyIndex]) === "직무";
      const workPatternIndex = expectedHeaders.findIndex((h) => bare(h) === "근로형태");
      const hasWorkPattern = workPatternIndex >= 0 && bare(header[workPatternIndex]) === "근로형태";
      if (missing) {
        return {
          error: "필수 컬럼을 확인할 수 없어요.\n다운로드한 템플릿의 헤더를 변경하지 말아 주세요.",
        };
      }
      const body = aoa.slice(1).filter((r) => r.some((c) => String(c ?? "").trim() !== ""));
      if (body.length === 0) {
        return { error: "업로드한 파일에 데이터 행이 없어요." };
      }
      if (body.length > 500) {
        return { error: "한 번에 최대 500명까지 처리할 수 있어요.\n파일의 행 수를 확인해 주세요." };
      }
      return { rows: body, hasJobDuty, hasWorkPattern, date1904 };
    },

    async handleBulkAddFile(file: File | null | undefined) {
      if (!file) {
        return;
      }
      this.bulkAdd.fileName = file.name;
      this.bulkAdd.fileError = "";
      this.bulkAdd.errors = [];
      this.bulkAdd.validated = false;
      this.bulkAdd.rows = [];
      const parsed = await this.parseBulkFile(file, this.bulkAddHeaders);
      if ("error" in parsed) {
        this.bulkAdd.fileError = parsed.error;
        return;
      }
      // 행 → 서버 입력 (엑셀 행 번호 = 헤더 1행 + index + 2)
      const rows = parsed.rows.map((r, i) => ({
        rowNumber: i + 2,
        lastName: String(r[0] ?? "").trim(),
        firstName: String(r[1] ?? "").trim(),
        employeeNumber: String(r[2] ?? "").trim(),
        hireDate: this.excelCellToDateStr(r[3], parsed.date1904),
        birthDate: this.excelCellToDateStr(r[4], parsed.date1904) || undefined,
        email: String(r[5] ?? "").trim() || undefined,
        phone: String(r[6] ?? "").trim() || undefined,
        teamName: String(r[7] ?? "").trim() || undefined,
        roleName: String(r[8] ?? "").trim() || undefined,
        jobDuty: String(r[9] ?? "").trim() || undefined,
        shiftGroupName: String(r[10] ?? "").trim() || undefined,
        shiftName: String(r[11] ?? "").trim() || undefined,
        // 근로형태(선택) — 비우면 직무의 기본 근로형태
        workPatternName: String(r[12] ?? "").trim() || undefined,
      }));
      this.bulkAdd.rows = rows as unknown as Record<string, string | number>[];
      try {
        const res = await this.sdk.employee.bulkCreateValidated({ rows, dryRun: true });
        this.bulkAdd.errors = res.errors;
        this.bulkAdd.validated = res.ok;
      } catch (err) {
        this.bulkAdd.fileError = err instanceof Error ? err.message : String(err);
      }
    },

    async confirmBulkAdd() {
      if (!this.bulkAdd.validated || this.bulkAdd.saving) {
        return;
      }
      this.bulkAdd.saving = true;
      try {
        const res = await this.sdk.employee.bulkCreateValidated({
          rows: this.bulkAdd.rows as never,
          dryRun: false,
        });
        if (!res.ok) {
          this.bulkAdd.errors = res.errors;
          this.bulkAdd.validated = false;
          return;
        }
        this.showBulkAddModal = false;
        this.notify(`${res.count}명의 직원을 등록했습니다`);
        await this.loadEmployees();
        this.computeStats();
      } catch (err) {
        this.bulkAdd.fileError = err instanceof Error ? err.message : String(err);
      } finally {
        this.bulkAdd.saving = false;
      }
    },

    async handleBulkUpdFile(file: File | null | undefined) {
      if (!file) {
        return;
      }
      this.bulkUpd.fileName = file.name;
      this.bulkUpd.fileError = "";
      this.bulkUpd.errors = [];
      this.bulkUpd.validated = false;
      this.bulkUpd.rows = [];
      const parsed = await this.parseBulkFile(file, this.bulkUpdHeaders);
      if ("error" in parsed) {
        this.bulkUpd.fileError = parsed.error;
        return;
      }
      const rows = parsed.rows.map((r, i) => ({
        rowNumber: i + 2,
        employeeNumber: String(r[0] ?? "").trim(),
        email: String(r[1] ?? "").trim() || undefined,
        phone: String(r[2] ?? "").trim() || undefined,
        lastName: String(r[3] ?? "").trim(),
        firstName: String(r[4] ?? "").trim(),
        hireDate: this.excelCellToDateStr(r[5], parsed.date1904),
        birthDate: this.excelCellToDateStr(r[6], parsed.date1904) || undefined,
        teamName: String(r[7] ?? "").trim() || undefined,
        roleName: String(r[8] ?? "").trim() || undefined,
        // 직무 열이 있는 파일만 반영 — 빈 칸은 직무를 지운다. 열이 없는 예전 파일은 건드리지 않는다.
        ...(parsed.hasJobDuty ? { jobDuty: String(r[9] ?? "").trim() } : {}),
        // 근로형태 열이 있는 파일만 반영 — 빈 칸은 해제, 열이 없는 예전 파일은 건드리지 않는다
        ...(parsed.hasWorkPattern ? { workPatternName: String(r[10] ?? "").trim() } : {}),
      }));
      this.bulkUpd.rows = rows as unknown as Record<string, string | number>[];
      try {
        const res = await this.sdk.employee.bulkUpdateValidated({
          selectedEmployeeIds: this.bulkUpd.selectedIds,
          rows,
          dryRun: true,
        });
        this.bulkUpd.errors = res.errors;
        this.bulkUpd.validated = res.ok;
      } catch (err) {
        this.bulkUpd.fileError = err instanceof Error ? err.message : String(err);
      }
    },

    async confirmBulkUpdate() {
      if (!this.bulkUpd.validated || this.bulkUpd.saving) {
        return;
      }
      this.bulkUpd.saving = true;
      try {
        const res = await this.sdk.employee.bulkUpdateValidated({
          selectedEmployeeIds: this.bulkUpd.selectedIds,
          rows: this.bulkUpd.rows as never,
          dryRun: false,
        });
        if (!res.ok) {
          this.bulkUpd.errors = res.errors;
          this.bulkUpd.validated = false;
          return;
        }
        this.showBulkUpdateModal = false;
        this.notify(`${res.count}명의 직원 정보를 수정했습니다`);
        await this.loadEmployees();
        this.computeStats();
      } catch (err) {
        this.bulkUpd.fileError = err instanceof Error ? err.message : String(err);
      } finally {
        this.bulkUpd.saving = false;
      }
    },

    openRosterExport(scope?: {
      teamIds?: string[];
      statuses?: string[];
      jobDuties?: string[];
      search?: string;
    }) {
      this.rosterExportSelected = {
        num: true,
        name: true,
        team: true,
        jobDuty: true,
        ward: true,
        status: true,
        hireDate: true,
        email: true,
        phone: true,
        shift: true,
      };
      this.rosterExportScope = scope ?? null;
      this.showRosterExport = true;
    },

    /** 팀 관리 — 특정 팀 명단 다운로드 진입점(컬럼 선택 팝업 경유).
     *  화면과 같은 명단: 하위 조직 포함이면 하위 팀까지, 상태 · 직무 필터 · 검색어도 그대로. */
    exportTeamRoster(
      teamId: string,
      opts: {
        statuses?: string[];
        jobDuties?: string[];
        search?: string;
        includeSubteams?: boolean;
      } = {},
    ) {
      this.openRosterExport({
        teamIds: opts.includeSubteams ? [teamId, ...this.descendantTeamIds(teamId)] : [teamId],
        statuses: opts.statuses?.length ? opts.statuses : undefined,
        jobDuties: opts.jobDuties?.length ? opts.jobDuties : undefined,
        search: opts.search || undefined,
      });
    },

    /** 팀 관리 — 워크스페이스(조직 전체) 명단 다운로드 진입점(컬럼 선택 팝업 경유). */
    exportAllMembers(opts: { statuses?: string[]; jobDuties?: string[]; search?: string } = {}) {
      this.openRosterExport({
        statuses: opts.statuses?.length ? opts.statuses : undefined,
        jobDuties: opts.jobDuties?.length ? opts.jobDuties : undefined,
        search: opts.search || undefined,
      });
    },

    /** 팝업 [다운로드] 버튼 — 선택된 컬럼만 서버로 전달해 xlsx 를 받는다. */
    async confirmRosterExport() {
      const columns = this.rosterExportItems
        .map((it) => it.key)
        .filter((k) => this.rosterExportSelected[k]);
      if (columns.length === 0) {
        return;
      }
      try {
        const filter: {
          statuses?: string[];
          teamIds?: string[];
          search?: string;
          roleIds?: string[];
          hireDateFrom?: Date;
          hireDateTo?: Date;
          jobDuties?: string[];
          wardIds?: string[];
          columns: typeof columns;
        } = { columns };
        const scope = this.rosterExportScope;
        if (scope) {
          if (scope.teamIds?.length) {
            filter.teamIds = scope.teamIds;
          }
          if (scope.statuses?.length) {
            filter.statuses = scope.statuses;
          }
          if (scope.jobDuties?.length) {
            filter.jobDuties = scope.jobDuties;
          }
          if (scope.search) {
            filter.search = scope.search;
          }
        } else {
          // 직원 관리 — 지금 목록에 걸린 필터 그대로(목록에 보이는 사람 = 다운로드 명단)
          const f = this.employeeFilter;
          if (f.statuses.length > 0) {
            filter.statuses = f.statuses;
          }
          if (f.teamIds.length > 0) {
            filter.teamIds = f.teamIds;
          }
          if (f.roleIds.length > 0) {
            filter.roleIds = f.roleIds;
          }
          if (f.search) {
            filter.search = f.search;
          }
          if (f.hireDateFrom) {
            filter.hireDateFrom = new Date(f.hireDateFrom);
          }
          if (f.hireDateTo) {
            filter.hireDateTo = new Date(f.hireDateTo);
          }
          if (f.jobDuties.length > 0) {
            filter.jobDuties = f.jobDuties;
          }
          if (f.wardIds.length > 0) {
            filter.wardIds = f.wardIds;
          }
        }
        const r = await this.sdk.employee.exportRoster(filter);
        const link = document.createElement("a");
        link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${r.data}`;
        link.download = r.filename;
        link.click();
        this.showRosterExport = false;
      } catch (err) {
        showError(
          `${this._t("admin.download_failed")}: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    },

    // exportTeamRoster / exportAllMembers 는 컬럼 선택 팝업 진입점으로 대체 —
    // 위의 openRosterExport 계열(rosterExportScope) 참고.

    async exportCertifications() {
      try {
        // 엑셀 = 지금 화면 필터 범위 (유형·소속·신고기한·검색 동일 적용)
        const filter: Record<string, unknown> = {};
        if (this.certFilter.type) {
          filter.type = this.certFilter.type;
        }
        if (this.certFilter.teamIds.length) {
          filter.teamIds = this.certFilter.teamIds;
        }
        if (this.certFilter.search.trim()) {
          filter.search = this.certFilter.search.trim();
        }
        if (this.certFilter.deadlineFrom) {
          filter.deadlineFrom = this.certFilter.deadlineFrom;
        }
        if (this.certFilter.deadlineTo) {
          filter.deadlineTo = this.certFilter.deadlineTo;
        }
        const r = await this.sdk.employeeCertification.export(filter);
        const link = document.createElement("a");
        link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${r.data}`;
        link.download = r.filename;
        link.click();
      } catch (err) {
        showError(
          `${this._t("admin.download_failed")}: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    },

    /* 자격유형 select 변경 핸들러. (등록·수정 공통)
       확인창은 「제출 펼침(토글 ON 또는 등록 잠금) + 실제 작성 내용 있음 + 최초 선택 아님」일 때만 띄운다.
         · 취소     → 유형을 직전 값으로 되돌린다(변경 없음).
         · 내용 유지 → 유형 + 자격증명(기본명)만 바꾸고 번호·날짜·파일은 그대로 둔다.
         · 변경(primary) → 상세 제출 정보를 리셋한다(등록 잠금 건은 잠금해제 + 제출란 활성화).
       그 외(토글 OFF=접힘 / 최초 유형 지정 / 자동 자격증명만 있고 실제 입력 없음)는 확인창 없이
       기본 이름만 갱신한다. 자동 채워지는 자격증명은 '작성한 내용'으로 보지 않는다(직전 유형의
       기본명과 다를 때만 사용자가 직접 고친 것으로 판정). x-model이 확인창보다 먼저 certForm.type 을
       바꾸므로 취소 시 복원이 반드시 필요하다. */
    onCertTypeChange() {
      const newType = this.certForm.type;
      const prevType = this.certTypePrev;
      const expanded = this.certRegistered || this.certSubmitted;
      const vals = this.certFieldVals;
      // 이전 유형 기준 "실제 작성 내용" 판정용 스냅샷(자동 기본값 제외)
      const prevDefaultName = prevType ? this.certTypeDefaultName(prevType) : "";
      const typedContent = Object.entries(vals).some(([k, v]) => {
        if (!v) {
          return false;
        }
        if (k === "license_name" && v === prevDefaultName) {
          return false; // 자동 채워진 자격증명은 작성 내용으로 안 봄
        }
        if (k === "issuing_authority" && v === "보건복지부") {
          return false; // 자동 기본값
        }
        return true;
      });
      const hasContent = this.certRegistered || typedContent;
      // 접힘(미제출) / 최초 유형 지정 / 실제 입력 없음 → 확인창 없이 새 유형 필드로 재구성.
      if (!(expanded && prevType && hasContent)) {
        this.initCertFieldVals();
        this.certTypePrev = newType;
        return;
      }
      this.confirmTitle = "자격유형 변경";
      this.confirmMessage =
        "자격유형을 변경하면 작성한 자격증/면허 내용이 리셋돼요. 자격유형을 변경할까요?";
      this.confirmLabel = "변경";
      this.confirmSecondaryLabel = "내용 유지";
      this.confirmTop = true;
      // 변경(primary): 새 유형 필드 구성으로 리셋(기본값 재적용).
      this.confirmAction = async () => {
        const wasRegistered = this.certRegistered;
        this.initCertFieldVals();
        // 등록된 잠금 건이었다면 잠금 해제 + 제출란 활성화(재입력 모드). 신규 건은 토글 상태 유지.
        if (wasRegistered) {
          this.certRegistered = false;
          this.certEditing = false;
          this.certSubmitted = true;
        }
        this.certTypePrev = newType;
      };
      // 내용 유지: 새 유형 필드 구성으로 바꾸되, 같은 key 값은 유지한다(잠금 상태도 유지).
      this.confirmSecondaryAction = () => {
        const kept = { ...vals };
        this.initCertFieldVals();
        for (const k of Object.keys(this.certFieldVals)) {
          if (kept[k] && k !== "license_name") {
            this.certFieldVals[k] = kept[k];
          }
        }
        this.certTypePrev = newType;
      };
      this.confirmCancelAction = () => {
        // 취소: 유형을 직전 값으로 되돌리고 상태를 그대로 유지한다.
        this.certForm.type = this.certTypePrev;
      };
      this.showConfirm = true;
    },

    /** 등록 모달의 자격 유형 옵션: 활성 유형 + (편집 중 비활성이라도 현재 선택값은 노출) */
    certTypeOptions(currentCode?: string) {
      const list = this.certTypeConfigs.filter(
        (ct) => ct.isActive !== false || ct.code === currentCode,
      );
      return list.map((ct) => ({ code: String(ct.code), name: String(ct.name) }));
    },

    certTypeDefaultName(type: string): string {
      const cfg = this.certTypeConfigs.find((ct) => ct.code === type);
      // 기본 면허증명 = 유형 필드 정의(fields)의 license_name.defaultValue (단일 출처)
      const fields = Array.isArray(cfg?.fields) ? (cfg?.fields as Record<string, unknown>[]) : [];
      const licenseField = fields.find((f) => f.key === "license_name");
      if (licenseField?.defaultValue) {
        return String(licenseField.defaultValue);
      }
      // 없으면 유형명만 반환(접미사 강제 없음)
      return cfg ? String(cfg.name || "") : type;
    },

    certTypeLabel(type: string): string {
      const cfg = this.certTypeConfigs.find((ct) => ct.code === type);
      return cfg ? String(cfg.name || type) : type;
    },

    /* ──────────── leave rule templates ──────────── */
    async applyLeaveRuleTemplates() {
      this.confirmTitle = "기본 연차규칙 등록";
      this.confirmMessage = "근로기준법 기본 연차규칙을 등록할까요? (기존 규칙은 유지돼요)";
      this.confirmLabel = this._t("common.confirm");
      this.confirmAction = async () => {
        await this._applyLeaveRuleTemplatesConfirmed();
      };
      // 유형·기준 관리 패널(z-50) 안에서 호출되므로 확인창을 그 위(z-60)에 띄운다.
      this.confirmTop = true;
      this.showConfirm = true;
    },

    async _applyLeaveRuleTemplatesConfirmed() {
      try {
        const templates = [
          {
            yearsOfServiceMin: 0,
            yearsOfServiceMax: 1,
            entitledDays: 11,
            accrualType: "monthly",
            maxDays: 11,
            description: "입사 1년 미만: 매월 1일 발생 (최대 11일)",
            sortOrder: 0,
          },
          {
            yearsOfServiceMin: 1,
            yearsOfServiceMax: 3,
            entitledDays: 15,
            accrualType: "annual",
            maxDays: null,
            description: "1~3년: 연 15일",
            sortOrder: 1,
          },
          {
            yearsOfServiceMin: 3,
            yearsOfServiceMax: 5,
            entitledDays: 16,
            accrualType: "annual",
            maxDays: null,
            description: "3~5년: 연 16일 (2년 초과 시 1일 가산)",
            sortOrder: 2,
          },
          {
            yearsOfServiceMin: 5,
            yearsOfServiceMax: 7,
            entitledDays: 17,
            accrualType: "annual",
            maxDays: null,
            description: "5~7년: 연 17일",
            sortOrder: 3,
          },
          {
            yearsOfServiceMin: 7,
            yearsOfServiceMax: 9,
            entitledDays: 18,
            accrualType: "annual",
            maxDays: null,
            description: "7~9년: 연 18일",
            sortOrder: 4,
          },
          {
            yearsOfServiceMin: 9,
            yearsOfServiceMax: 11,
            entitledDays: 19,
            accrualType: "annual",
            maxDays: null,
            description: "9~11년: 연 19일",
            sortOrder: 5,
          },
          {
            yearsOfServiceMin: 11,
            yearsOfServiceMax: 13,
            entitledDays: 20,
            accrualType: "annual",
            maxDays: null,
            description: "11~13년: 연 20일",
            sortOrder: 6,
          },
          {
            yearsOfServiceMin: 13,
            yearsOfServiceMax: 15,
            entitledDays: 21,
            accrualType: "annual",
            maxDays: null,
            description: "13~15년: 연 21일",
            sortOrder: 7,
          },
          {
            yearsOfServiceMin: 15,
            yearsOfServiceMax: 17,
            entitledDays: 22,
            accrualType: "annual",
            maxDays: null,
            description: "15~17년: 연 22일",
            sortOrder: 8,
          },
          {
            yearsOfServiceMin: 17,
            yearsOfServiceMax: 19,
            entitledDays: 23,
            accrualType: "annual",
            maxDays: null,
            description: "17~19년: 연 23일",
            sortOrder: 9,
          },
          {
            yearsOfServiceMin: 19,
            yearsOfServiceMax: 21,
            entitledDays: 24,
            accrualType: "annual",
            maxDays: null,
            description: "19~21년: 연 24일",
            sortOrder: 10,
          },
          {
            yearsOfServiceMin: 21,
            yearsOfServiceMax: null,
            entitledDays: 25,
            accrualType: "annual",
            maxDays: null,
            description: "21년 이상: 연 25일 (법정 최대)",
            sortOrder: 11,
          },
        ].map((t) => ({
          // 기본 규칙 이름 — 연차 부여 기준 정책 지정값(sortOrder 기준). 미지정 시 근속 구간 파생명.
          ...t,
          name:
            [
              "월차",
              "기본 연차",
              "가산 연차 (3년차)",
              "가산 연차 (5년차)",
              "가산 연차 (7년차)",
              "기본 연차(9년차)",
              "가산 연차 (11년차)",
              "가산 연차 (13년차)",
              "가산 연차 (15년차)",
              "기본 연차(17년차)",
              "가산 연차 (19년차)",
              "가산 연차 (21년 이상)",
            ][t.sortOrder] ?? this._ruleBandName(t.yearsOfServiceMin, t.yearsOfServiceMax),
        }));
        // 동일 근속 구간(min~max)이 이미 있으면 건너뜀 — 기존 규칙은 유지·덮어쓰지 않음.
        // (과거 unique 제약 완화 이후 upsert가 무조건 생성해 중복이 쌓이던 문제 방지)
        const sameBand = (
          a: { yearsOfServiceMin: number; yearsOfServiceMax: number | null },
          b: { yearsOfServiceMin: number; yearsOfServiceMax: number | null },
        ) =>
          Number(a.yearsOfServiceMin) === Number(b.yearsOfServiceMin) &&
          (a.yearsOfServiceMax ?? null) === (b.yearsOfServiceMax ?? null);
        let added = 0;
        for (const t of templates) {
          // 기본 규칙은 현재 기준 탭(회계년도/입사일) 리스트에만 등록·중복 검사한다
          const exists = this.entitlementRulesForBasis().some((r) =>
            sameBand(r as { yearsOfServiceMin: number; yearsOfServiceMax: number | null }, t),
          );
          if (exists) {
            continue;
          }
          try {
            // 이름은 위 .map()에서 정책 지정명(월차·기본 연차·가산 연차…)을 이미 t.name에 넣었으므로
            // 그대로 저장한다. (과거 구간명 override는 develop 정책명 갱신으로 제거 — 머지 잔재 방지)
            // FIXME(타입 복구 중 발견): 기본 휴가 규칙 템플릿은 accrualType 문자열/null 값을 포함해 upsertEntitlementRule 입력 타입과 맞지 않음. 입력 스키마 정렬 후 any 제거.
            await this.sdk.leaveRequest.upsertEntitlementRule({
              ...t,
              basis: this.ruleBasisTab,
              // biome-ignore lint/suspicious/noExplicitAny: SDK input type mismatch during getter migration
            } as any);
            added++;
          } catch {
            // skip on conflict
          }
        }
        await this.loadEntitlementRules();
        this.notify(
          added > 0 ? `기본 규칙 ${added}건을 등록했어요.` : "이미 등록된 기본 규칙이에요.",
        );
      } catch (err) {
        this.notify(
          `${this._t("admin.create_failed")}: ${err instanceof Error ? err.message : String(err)}`,
          "error",
        );
      }
    },

    /* ──────────── settings tab ──────────── */
    async loadSettingsData() {
      this.settingsLoaded = true;
      await Promise.allSettled([
        this.loadOrgInfo(),
        this.loadShifts(),
        this.loadSalaryGrades(),
        this.loadSalaryGradeCategories(),
        this.loadItemCodes(),
        this.loadCertifications(),
        this.loadCertSummary(),
        this.loadCertTypeConfigs(),
      ]);
    },

    async loadSettingsSubTabData(subTab: string) {
      if (subTab === "shifts" || subTab === "jobDuties" || subTab === "workPatterns") {
        // 근무 설정·직무 설정·근로형태는 서로의 목록을 쓴다(근무 유형 폼의 「기본으로 쓰는 직무」 · 직무 매핑표의 그룹 열 · 근로형태 폼의 직무/기본 근무)
        await Promise.all([
          this.loadShiftGroups(),
          this.loadShifts(),
          this.loadJobDuties(),
          this.loadWorkPatterns(),
          this.loadAllTeams(),
        ]);
        return;
      }
      if (subTab === "grades") {
        await Promise.allSettled([this.loadSalaryGrades(), this.loadSalaryGradeCategories()]);
        return;
      }
      if (subTab === "certs") {
        await Promise.allSettled([this.loadCertifications(), this.loadCertSummary()]);
        return;
      }
      if (subTab === "docTemplates") {
        await Promise.allSettled([this.loadDocumentTemplates(), this.loadCertTypeConfigs()]);
        return;
      }
      if (subTab === "approvalLines") {
        // 정책은 조직 설정(loadOrgInfo 가 템플릿·후보도 같이 읽는다), 역할 체크박스는 역할 목록
        await Promise.allSettled([
          this.loadOrgInfo(),
          this.roles.length > 0 ? Promise.resolve() : this.loadRoles(),
        ]);
        return;
      }
      if (subTab === "codes") {
        await this.loadItemCodes();
        return;
      }
      if (subTab === "org") {
        await this.loadOrgInfo();
        return;
      }
      if (subTab === "myinfo") {
        // 내 정보 pane hosts its own myInfoForm x-data; ask it to (re)load the
        // current user's employee record + shift options without opening a modal.
        window.dispatchEvent(new Event("load-my-info"));
        return;
      }
    },

    async loadOrgInfo() {
      try {
        const org = await this.sdk.organization.getCurrent();
        this.orgInfo = org as Record<string, unknown>;
        this.orgSettings = (org?.settings as Record<string, unknown>) || {};
        this.defaultShiftId = (org?.defaultShiftId as string) || "";
        // 용어 표시를 스토어에 반영 — 설정 저장 직후 메뉴·표 머리글이 새로고침 없이 바뀐다
        Alpine.store("hereby").setTerminology(
          this.orgSettings.terminology as { role?: string } | undefined,
        );
      } catch {
        this.orgInfo = null;
      }
      // 설정 카드 「연차 산정 기준」 — 이력표 기준 현재값
      await this.loadLeaveBasis();
      // 설정 카드 「결재선」 — 종류별 템플릿·후보(권한 없으면 null)
      await this.loadApprovalTemplates();
    },

    async loadSalaryGrades() {
      try {
        // Load available years if not yet loaded
        if (this.salaryGradeYears.length === 0) {
          const years = (await this.sdk.salaryGrade.availableYears()) as number[];
          this.salaryGradeYears = years || [];
          // Default to latest year if not set
          if (!this.salaryGradeFilter.year && years.length > 0) {
            this.salaryGradeFilter.year = years[0];
          }
        }

        const filter: Record<string, unknown> = {};
        if (this.salaryGradeFilter.categoryId) {
          filter.categoryId = this.salaryGradeFilter.categoryId;
        }
        if (this.salaryGradeFilter.year) {
          filter.effectiveYear = this.salaryGradeFilter.year;
        }
        // FIXME(타입 복구 중 발견): salaryGrade.list 응답 items가 SalaryGrade[]로 추론되지만 UI 상태는 Record<string, unknown>[]를 기대함. 응답 DTO 정렬 후 any 제거.
        const items = await fetchAllPages<Record<string, unknown>>(
          (page, limit) => this.sdk.salaryGrade.list(filter, { page, limit }),
          500,
        );
        this.salaryGrades = items;
        this.salaryGradePage = 1;
      } catch {
        this.salaryGrades = [];
      }
    },

    async loadItemCodes() {
      try {
        const [aCodes, dCodes] = await Promise.allSettled([
          this.sdk.itemCode.listAllowanceCodes(),
          this.sdk.itemCode.listDeductionCodes(),
        ]);
        // FIXME(타입 복구 중 발견): itemCode 목록 응답 타입이 UI Record 배열 상태와 직접 호환되지 않음. 응답 DTO 정렬 후 unknown 캐스트 제거.
        this.allowanceItemCodes =
          aCodes.status === "fulfilled"
            ? (aCodes.value as unknown as Record<string, unknown>[]) || []
            : [];
        this.deductionItemCodes =
          dCodes.status === "fulfilled"
            ? (dCodes.value as unknown as Record<string, unknown>[]) || []
            : [];
      } catch {
        this.allowanceItemCodes = [];
        this.deductionItemCodes = [];
      }
    },

    // Shift CRUD (settings)
    // ───────── 근무 그룹 ─────────
    async loadShiftGroups() {
      try {
        if (!this.sdk) {
          return;
        }
        const result = await this.sdk.shiftGroup.list();
        this.shiftGroups = result.items;
      } catch {
        this.shiftGroups = [];
      } finally {
        this.loadedOnce.shiftGroups = true;
      }
    },

    /** 근태 기록의 근무형태 판정 출처 라벨(attendance.shiftSource) — 근태 현황 행 아래 작은 글씨 */
    shiftSourceLabel(code: string | null | undefined): string {
      return code ? (SHIFT_RESOLUTION_SOURCE_LABELS as Record<string, string>)[code] || "" : "";
    },
    /** 직원 근무 배정 출처 라벨(employees.shiftAssignedVia) — 직원 목록 근무형태 배지 툴팁 */
    shiftAssignedViaLabel(code: string | null | undefined): string {
      return code ? (SHIFT_ASSIGNMENT_SOURCE_LABELS as Record<string, string>)[code] || "" : "";
    },
    /** isActive 가 undefined 면 활성으로 본다(배지 표기와 같은 기준). */
    isShiftItemActive(item: { isActive?: boolean | null }) {
      return item.isActive !== false;
    },

    shiftStatusMatches(item: { isActive?: boolean | null }) {
      if (!this.shiftStatusFilter) {
        return true;
      }
      const on = this.isShiftItemActive(item);
      return this.shiftStatusFilter === "ACTIVE" ? on : !on;
    },

    /** 「전체 상태」에서는 활성 → 비활성 순으로 정렬해 활성이 항상 위에 온다. */
    shiftActiveFirst<T extends { isActive?: boolean | null }>(list: T[]): T[] {
      if (this.shiftStatusFilter) {
        return list;
      }
      const on: T[] = [];
      const off: T[] = [];
      for (const item of list) {
        (this.isShiftItemActive(item) ? on : off).push(item);
      }
      return [...on, ...off];
    },

    /** 활성 필터: 활성 그룹만. 비활성 필터: 비활성 그룹 + 비활성 유형을 가진 활성 그룹. */
    filteredShiftGroups() {
      return this.shiftActiveFirst(
        this.shiftGroups.filter(
          (g) =>
            this.shiftStatusMatches(g) ||
            (this.shiftStatusFilter === "INACTIVE" && this.filteredShifts(g).length > 0),
        ),
      );
    },

    /** 비활성 그룹 안의 유형은 그룹 단위로 꺼진 것이라 필터와 무관하게 모두 보여 준다. */
    filteredShifts(group: ShiftGroupItem) {
      const shifts = group.shifts ?? [];
      if (!this.isShiftItemActive(group)) {
        return this.shiftActiveFirst(shifts);
      }
      return this.shiftActiveFirst(shifts.filter((s) => this.shiftStatusMatches(s)));
    },

    shiftGroupNameById(groupId?: string | null): string {
      if (!groupId) {
        return "";
      }
      return (this.shiftGroups as ShiftGroupItem[]).find((g) => g.id === groupId)?.name ?? "";
    },

    /**
     * 일괄 배정 「근무형태」 드롭다운 옵션 — 그룹을 골랐으면 그 그룹의 활성 형태만,
     * 안 골랐으면 활성 형태 전부를 「그룹 · 형태」로 보여 준다(형태를 고르면 그룹은 서버가 맞춘다).
     */
    bulkShiftOptions(groupId: string): Array<{ id: string; label: string }> {
      return (this.shifts as ShiftItem[])
        .filter((s) => s.isActive !== false && (!groupId || s.groupId === groupId))
        .map((s) => {
          const groupName = this.shiftGroupNameById(s.groupId);
          return {
            id: s.id,
            label: groupId || !groupName ? s.name : `${groupName} · ${s.name}`,
          };
        });
    },

    /** 허용 근무 유형 체크 목록(MED-969) — 조직의 활성 근무 전부를 「그룹 · 근무」로. 이미 체크된 비활성 근무는 남겨 보여 준다 */
    allowedShiftOptions(checked: string[]): Array<{ id: string; label: string }> {
      return (this.shifts as ShiftItem[])
        .filter((s) => s.isActive !== false || checked.includes(s.id))
        .map((s) => {
          const groupName = this.shiftGroupNameById(s.groupId);
          return {
            id: s.id,
            label: `${groupName ? `${groupName} · ` : ""}${s.name}${s.isActive === false ? " (비활성)" : ""}`,
          };
        });
    },

    toggleTeamAllowedShift(id: string) {
      const list = this.teamForm.allowedShiftIds;
      const i = list.indexOf(id);
      if (i >= 0) {
        list.splice(i, 1);
      } else {
        list.push(id);
      }
    },

    /** 그룹 드롭다운이 바뀌면 다른 그룹의 형태가 남지 않게 형태 선택을 비운다 (직원 상세 2단계와 동일). */
    onBulkShiftGroupChange() {
      const cur = (this.shifts as ShiftItem[]).find((s) => s.id === this.bulkShiftId);
      if (cur && this.bulkShiftGroupId && cur.groupId !== this.bulkShiftGroupId) {
        this.bulkShiftId = "";
      }
    },

    /** 확인 모달 한 줄용 — 「그룹 · 형태」 / 「그룹 (근무형태 미지정)」. */
    bulkShiftAssignLabel(groupId: string, shiftId: string): string {
      const shift = shiftId
        ? (this.shifts as ShiftItem[]).find((s) => s.id === shiftId)
        : undefined;
      const groupName = this.shiftGroupNameById(shift?.groupId ?? groupId);
      if (shift) {
        return groupName ? `${groupName} · ${shift.name}` : shift.name;
      }
      return groupName ? `${groupName} (근무형태 미지정)` : "";
    },

    /**
     * 직원 관리 「근무형태」 필터 옵션 — 그룹 행 + 그 아래 형태 행(들여쓰기). 활성만.
     * 그룹 체크는 그 그룹 전체(형태 미지정 포함), 형태 체크는 그 형태만.
     */
    shiftFilterOptions(): Array<{
      id: string;
      kind: "group" | "shift";
      name: string;
      depth: number;
    }> {
      const out: Array<{ id: string; kind: "group" | "shift"; name: string; depth: number }> = [];
      for (const g of this.shiftGroups as ShiftGroupItem[]) {
        if (g.isActive === false) {
          continue;
        }
        out.push({ id: g.id, kind: "group", name: g.name, depth: 0 });
        for (const s of g.shifts ?? []) {
          if (s.isActive === false) {
            continue;
          }
          out.push({ id: s.id, kind: "shift", name: s.name, depth: 1 });
        }
      }
      // 그룹 미지정(레거시) 형태는 맨 아래 최상위로
      for (const s of this.shifts as ShiftItem[]) {
        if (!s.groupId && s.isActive !== false) {
          out.push({ id: s.id, kind: "shift", name: s.name, depth: 0 });
        }
      }
      return out;
    },

    /** 근무형태 드롭다운 아래쪽 판정 필터(체크박스) — 라벨은 드롭다운 머리글·배지에도 쓴다 */
    shiftJudgmentFilterLabels: {
      multipleShiftGroups: "근무 그룹 2개 이상",
      shiftDiffersFromDefault: "기본값 ≠ 직무 기본",
    } as const,

    shiftFilterCount(): number {
      return (
        this.employeeFilter.shiftGroupIds.length +
        this.employeeFilter.shiftIds.length +
        (this.employeeFilter.multipleShiftGroups ? 1 : 0) +
        (this.employeeFilter.shiftDiffersFromDefault ? 1 : 0)
      );
    },

    shiftFilterLabel(): string {
      const groups = this.employeeFilter.shiftGroupIds;
      const shifts = this.employeeFilter.shiftIds;
      if (groups.length + shifts.length === 0) {
        if (this.employeeFilter.multipleShiftGroups) {
          return this.shiftJudgmentFilterLabels.multipleShiftGroups;
        }
        if (this.employeeFilter.shiftDiffersFromDefault) {
          return this.shiftJudgmentFilterLabels.shiftDiffersFromDefault;
        }
        return "근무형태 선택";
      }
      const first =
        groups.length > 0 ? this.shiftGroupNameById(groups[0]) : this.shiftNameById(shifts[0]);
      return first || "근무형태 선택";
    },

    clearShiftFilter() {
      this.employeeFilter.shiftGroupIds = [];
      this.employeeFilter.shiftIds = [];
      this.employeeFilter.multipleShiftGroups = false;
      this.employeeFilter.shiftDiffersFromDefault = false;
      this.resetPage("employees");
      this.loadEmployees();
      this.computeStats();
    },

    /** 근무 설정 「직원 N명」 → 직원 관리로 이동해 그 그룹/형태로 필터한다. */
    openEmployeesByShift(groupId: string | null, shiftId: string | null) {
      this.employeeFilter.shiftGroupIds = !shiftId && groupId ? [groupId] : [];
      this.employeeFilter.shiftIds = shiftId ? [shiftId] : [];
      this.resetPage("employees");
      this.loadEmployees();
      this.computeStats();
      this.switchTab("employees");
    },

    /**
     * 팀 관리 선택 툴바 [근무 배정] — member id 를 employeeId 로 매핑한 뒤 일괄 배정한다.
     * (매핑은 resolveMemberRefs: 팀 캐시에 없는 id 는 org 스코프 행이라 곧 employeeId.)
     */
    async bulkAssignShiftByMemberIds(memberIds: string[], groupId: string, shiftId: string) {
      if (!(memberIds.length && (groupId || shiftId))) {
        return;
      }
      const employeeIds = this.resolveMemberEmployeeIds(memberIds);
      try {
        await this.sdk.employee.bulkAssignShift(employeeIds, groupId || null, shiftId || null);
        await Promise.all([
          this.loadEmployees(),
          this.loadUnassignedMembers(),
          this.loadShiftGroups(),
        ]);
      } catch (err) {
        showError(`근무 배정 변경 실패: ${err instanceof Error ? err.message : String(err)}`);
      }
    },

    /**
     * 「부 그룹 제거」(MED-924) — 선택 직원의 주 소속이 아닌 소속 줄에서 근무 그룹·형태를 비운다.
     * 한 요청(한 트랜잭션)이라 부분 실패가 없다 — 실패하면 전부 그대로다. 주 소속 근무·근무표 배정은 서버가 건드리지 않는다.
     */
    async clearSecondaryShiftGroups() {
      const ids = [...this.selectedEmployees] as string[];
      if (ids.length === 0) {
        return;
      }
      const ok = await askConfirm({
        message: `선택한 ${ids.length}명의 주 소속이 아닌 소속에서 근무 그룹을 지울까요?\n주 소속의 근무와 근무표(스케줄러) 배정은 그대로이고, 당일 근태는 다시 판정돼요.`,
        confirmLabel: "부 그룹 제거",
        tone: "danger",
      });
      if (!ok) {
        return;
      }
      try {
        const r = await this.sdk.employee.clearSecondaryShiftGroups(ids);
        const untouched = r.employees - r.clearedEmployees;
        this.notify(
          `${r.clearedEmployees}명의 소속 ${r.clearedMemberships}줄에서 근무 그룹을 지웠어요.${
            untouched > 0 ? ` ${untouched}명은 지울 부 그룹이 없었어요.` : ""
          }`,
        );
        this.selectedEmployees = [];
        this.selectAllEmployees = false;
        await Promise.all([this.loadEmployees(), this.loadShiftGroups()]);
      } catch (err) {
        showError(
          `부 그룹 제거 실패(${ids.length}명 모두 그대로): ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    },

    // ───────── 직무 마스터 ─────────
    async loadJobDuties() {
      try {
        if (!this.sdk) {
          return;
        }
        const result = await this.sdk.jobDuty.list();
        this.jobDuties = result.items;
        this.jobDutiesUnassignedCount = result.unassignedCount;
      } catch {
        this.jobDuties = [];
        this.jobDutiesUnassignedCount = 0;
      } finally {
        this.loadedOnce.jobDuties = true;
      }
    },
    /** 직무 설정의 상태 필터 — 「전체 상태」에서는 활성이 먼저 온다(근무 설정과 같은 순서 규칙). */
    filteredJobDuties(): JobDutyItem[] {
      const f = this.jobDutyStatusFilter;
      const list = this.jobDuties.filter((d) => {
        if (!f) {
          return true;
        }
        return f === "ACTIVE" ? d.isActive !== false : d.isActive === false;
      });
      return [...list].sort((a, b) => Number(b.isActive !== false) - Number(a.isActive !== false));
    },
    /**
     * 목록 보기 — 직군(jobCategory)별로 묶는다. 순서는 JOB_CATEGORY_LABELS 정의 순, 미분류는 맨 뒤.
     * 23~25개 직무를 한 표에 늘어놓으면 훑기 어렵다는 피드백(2026-09-21)에서 나온 구조.
     */
    jobDutyGroups(): Array<{ key: string; label: string; items: JobDutyItem[] }> {
      const byKey = new Map<string, JobDutyItem[]>();
      for (const d of this.filteredJobDuties()) {
        const k = d.jobCategory || "";
        byKey.set(k, [...(byKey.get(k) ?? []), d]);
      }
      const order = [...Object.keys(JOB_CATEGORY_LABELS), ""];
      return order
        .filter((k) => byKey.has(k))
        .map((k) => ({
          key: k || "__none",
          label: k ? this.dutyCategoryLabel(k) : "미분류",
          items: byKey.get(k) ?? [],
        }));
    },
    /** 기본 근무가 비어 있는 활성 직무 수 — 직원 등록에서 자동 채움이 팀 대표로 넘어가는 직무. 목록 위 안내에 쓴다. */
    jobDutiesMissingDefaultCount(): number {
      return this.jobDuties.filter((d) => d.isActive !== false && !!this.jobDutyDefaultWarning(d))
        .length;
    },
    /**
     * 직무 기본 근무 검증 — 그룹이 없으면 「없음」, 그룹만이고 그 그룹이 스케줄러 연동이 아니면 「유형 미지정」
     * (3교대처럼 근무표가 날마다 형태를 정하는 그룹은 그룹만이 정상). 비면 문제 없음.
     */
    jobDutyDefaultWarning(duty: { shiftGroupId?: string | null; shiftId?: string | null }): string {
      if (!duty.shiftGroupId) {
        return "없음";
      }
      if (duty.shiftId) {
        return "";
      }
      const g = this.shiftGroups.find((x) => x.id === duty.shiftGroupId);
      return g?.schedulerEnabled ? "" : "유형 미지정";
    },
    // ── 직무 기본 근무 일괄 재적용 — 미리보기(dryRun) → 적용. 직무가 있는데 근무가 비었거나(그룹만) 비활성이거나 기본이 바뀐 직원만.
    showJobDutyReapply: false,
    jobDutyReapply: {
      jobDutyId: "" as string,
      title: "",
      loading: false,
      applying: false,
      total: 0,
      changedCount: 0,
      overriddenCount: 0,
      items: [] as JobDutyReapplyItem[],
    },
    jobDutyReapplyReasonLabel(reason: string): string {
      return (
        (
          {
            empty: "근무 없음",
            group_only: "그룹만",
            inactive: "비활성 근무",
            default_changed: "기본 바뀜",
          } as Record<string, string>
        )[reason] || reason
      );
    },
    async openJobDutyReapply(duty?: JobDutyItem | null) {
      this.jobDutyReapply = {
        jobDutyId: duty?.id ?? "",
        title: duty ? duty.name : "직무가 있는 직원 전체",
        loading: true,
        applying: false,
        total: 0,
        changedCount: 0,
        overriddenCount: 0,
        items: [],
      };
      this.showJobDutyReapply = true;
      try {
        const res = await this.sdk.jobDuty.reapplyDefaultShifts({
          ...(duty ? { jobDutyId: duty.id } : {}),
          dryRun: true,
        });
        this.jobDutyReapply.total = res.total;
        this.jobDutyReapply.changedCount = res.changedCount;
        this.jobDutyReapply.overriddenCount = res.overriddenCount;
        this.jobDutyReapply.items = res.items;
      } catch (err) {
        this.showJobDutyReapply = false;
        this.notify(
          `재적용 미리보기 실패: ${err instanceof Error ? err.message : String(err)}`,
          "error",
        );
      }
      this.jobDutyReapply.loading = false;
    },
    async applyJobDutyReapply() {
      const r = this.jobDutyReapply;
      if (r.applying || r.items.length === 0) {
        return;
      }
      r.applying = true;
      try {
        const res = await this.sdk.jobDuty.reapplyDefaultShifts({
          employeeIds: r.items.map((it) => it.employeeId),
          dryRun: false,
        });
        this.showJobDutyReapply = false;
        this.notify(`직원 ${res.changedCount}명의 근무를 직무 기본 근무로 맞췄습니다`);
        await Promise.all([this.loadJobDuties(), this.loadShiftGroups()]);
        if (this.activeTab === "employees") {
          this.loadEmployees();
        }
      } catch (err) {
        this.notify(`재적용 실패: ${err instanceof Error ? err.message : String(err)}`, "error");
      }
      r.applying = false;
    },
    /**
     * 매핑표의 근로형태(근무 그룹) 선택지 — 활성 그룹 이름순(+ 그 직무의 지금 값이 비활성이면 그 값도).
     * 직무 × 그룹 격자는 근로형태 33개에서 열 34개·폭 5,300px 의 희소 격자가 되어 버렸다(2026-09-21 실측) —
     * 직무 하나는 그룹 하나만 쓰므로 행마다 select 두 개(그룹 › 형태)로 바꿨다.
     */
    jobDutyMappingGroupOptions(current: string): ShiftGroupItem[] {
      return this.shiftGroups
        .filter((g) => g.isActive !== false || g.id === current)
        .slice()
        .sort((a, b) => String(a.name).localeCompare(String(b.name), "ko"));
    },
    /** 매핑표의 근무형태 선택지 — 그 직무 그룹의 활성 형태(+ 지금 값이 비활성이면 그 값도). */
    jobDutyMappingShiftOptions(duty: JobDutyItem): ShiftItem[] {
      if (!duty.shiftGroupId) {
        return [];
      }
      return this.shifts.filter(
        (x) => x.groupId === duty.shiftGroupId && (x.isActive !== false || x.id === duty.shiftId),
      );
    },
    /** 비고 — 형태가 여럿인 그룹에서 「그룹만」이면 등록 때 형태를 고른다는 뜻(3교대 D/E/N · 병동지원 A/B). */
    jobDutyMappingNote(duty: JobDutyItem): string {
      if (!duty.shiftGroupId) {
        return "직원 등록에서 팀 대표 근무로 채워져요";
      }
      const n = this.jobDutyMappingShiftOptions(duty).length;
      if (!duty.shiftId && n > 1) {
        return `형태 ${n}개 — 등록할 때 골라요`;
      }
      return "";
    },
    /** 매핑표 저장(공용) — 바로 저장하고 목록을 다시 읽는다. 실패하면 다시 읽어 select 가 원래 값으로 돌아간다. */
    async saveJobDutyMapping(
      duty: JobDutyItem,
      pair: { shiftGroupId: string | null; shiftId: string | null },
    ) {
      this.jobDutyMatrixSaving = duty.id;
      try {
        await this.sdk.jobDuty.update({ id: duty.id, ...pair });
        await this.loadJobDuties();
      } catch (err) {
        await this.loadJobDuties();
        this.notify(
          `${this._t("admin.save_failed")}: ${err instanceof Error ? err.message : String(err)}`,
          "error",
        );
      }
      this.jobDutyMatrixSaving = "";
    },
    /** 근로형태(그룹) 변경 — 형태는 「그룹만」으로 시작한다(다른 그룹의 형태가 남지 않게). */
    async setJobDutyGroup(duty: JobDutyItem, groupId: string) {
      if ((duty.shiftGroupId || "") === groupId) {
        return;
      }
      await this.saveJobDutyMapping(duty, { shiftGroupId: groupId || null, shiftId: null });
    },
    /** 근무형태 변경 — 그룹은 그대로, 빈 값이면 「그룹만」. */
    async setJobDutyShift(duty: JobDutyItem, shiftId: string) {
      if (!duty.shiftGroupId || (duty.shiftId || "") === shiftId) {
        return;
      }
      await this.saveJobDutyMapping(duty, {
        shiftGroupId: duty.shiftGroupId,
        shiftId: shiftId || null,
      });
    },
    /** 근무 그룹 헤더 「직무 N개」 — 이 그룹을 기본 근무로 둔 직무 수. */
    groupJobDutyCount(groupId: string): number {
      return this.jobDuties.filter((d) => d.shiftGroupId === groupId).length;
    },
    /** 근무 그룹 헤더 「직무 N개」 → 직무 설정 매핑표로. 설정 탭에서는 서브탭 전환, 근태 설정 팝업에서는 섹션 전환. */
    openJobDutiesForGroup(host: "settings" | "popup") {
      this.jobDutyView = "matrix";
      if (host === "popup") {
        this.selectTypeManagerSection("jobDuty");
      } else {
        this.switchSettingsSub("jobDuties");
      }
    },
    dutyCategoryOptions(): Record<string, string> {
      return JOB_CATEGORY_LABELS as Record<string, string>;
    },
    dutyCategoryLabel(code: string | null | undefined): string {
      return code ? (JOB_CATEGORY_LABELS as Record<string, string>)[code] || code : "-";
    },
    /** 기본 근무 표시 — 「그룹 › 형태」. 그룹만이면 그룹, 없으면 -. */
    jobDutyDefaultLabel(duty: JobDutyItem): string {
      if (!duty.shiftGroupName) {
        return "-";
      }
      return duty.shiftName ? `${duty.shiftGroupName} › ${duty.shiftName}` : duty.shiftGroupName;
    },
    /** 근무형태 분포 칩 — 「형태 N」(그룹만 배정이면 「그룹(그룹만) N」, 미배정이면 「미지정 N」). */
    jobDutyDistLabel(d: JobDutyItem["shiftDistribution"][number]): string {
      const name = d.shiftName ?? (d.shiftGroupName ? `${d.shiftGroupName}(그룹만)` : "미지정");
      return `${name} ${d.count}`;
    },
    /** 「N명」 → 직원 관리를 그 직무로 필터해 연다(근무 설정의 「직원 N명」과 같은 흐름). */
    openEmployeesByJobDuty(duty: JobDutyItem) {
      this.employeeFilter.jobDuties = [duty.name];
      this.employeeFilter.shiftGroupIds = [];
      this.employeeFilter.shiftIds = [];
      this.resetPage("employees");
      this.loadEmployees();
      this.computeStats();
      this.switchTab("employees");
    },
    /** 직무 폼 「기본 근로형태」 선택지 — 활성 근로형태 중 직무 미지정이거나 이 직무에 속한 것(+ 지금 값). */
    jobDutyFormWorkPatternOptions(): WorkPatternItem[] {
      const dutyId = this.editingJobDutyId;
      const current = this.jobDutyForm.defaultWorkPatternId;
      return this.workPatterns.filter(
        (w) =>
          w.id === current ||
          (w.isActive !== false && (!w.jobDutyId || (dutyId && w.jobDutyId === dutyId))),
      );
    },
    /** 직무 목록의 기본 근로형태 이름 — 없으면 빈 문자열. */
    jobDutyWorkPatternLabel(duty: JobDutyItem): string {
      return this.workPatternNameById(duty.defaultWorkPatternId || "");
    },

    // ───────── 근로형태 마스터 ─────────
    async loadWorkPatterns() {
      try {
        if (!this.sdk) {
          return;
        }
        const result = await this.sdk.workPattern.list();
        this.workPatterns = result.items;
        this.workPatternMultipliers = result.multipliers;
      } catch {
        this.workPatterns = [];
      } finally {
        this.loadedOnce.workPatterns = true;
      }
    },
    workPatternNameById(id: string): string {
      return id ? this.workPatterns.find((w) => w.id === id)?.name || "" : "";
    },
    /** 시간 표시 — 소수 둘째 자리까지, 끝 0 은 뗀다(233 · 5.75 · 30.75). */
    fmtHours(v: number | string | null | undefined): string {
      if (v === null || v === undefined || v === "") {
        return "-";
      }
      const n = Number(v);
      if (!Number.isFinite(n)) {
        return "-";
      }
      return String(Math.round(n * 100) / 100);
    },
    /** 상태 필터 — 「전체 상태」에서는 활성이 먼저(직무 설정과 같은 규칙). */
    filteredWorkPatterns(): WorkPatternItem[] {
      const f = this.workPatternStatusFilter;
      const list = this.workPatterns.filter((w) => {
        if (!f) {
          return true;
        }
        return f === "ACTIVE" ? w.isActive !== false : w.isActive === false;
      });
      return [...list].sort((a, b) => Number(b.isActive !== false) - Number(a.isActive !== false));
    },
    /** 목록 — 속한 직무별 묶음(직무 이름순), 직무 미지정은 맨 뒤. 병원 표가 직무군 단위라 같은 모양으로 훑는다. */
    workPatternGroups(): Array<{ key: string; label: string; items: WorkPatternItem[] }> {
      const byKey = new Map<string, { label: string; items: WorkPatternItem[] }>();
      for (const w of this.filteredWorkPatterns()) {
        const k = w.jobDutyId || "";
        const g = byKey.get(k) ?? { label: w.jobDutyName || "직무 미지정", items: [] };
        g.items.push(w);
        byKey.set(k, g);
      }
      return [...byKey.entries()]
        .sort(([ka, a], [kb, b]) => (ka ? (kb ? a.label.localeCompare(b.label, "ko") : -1) : 1))
        .map(([k, g]) => ({ key: k || "__none", label: g.label, items: g.items }));
    },
    /** 근무일 · 근무시간 · 휴게 한 줄 — 근무 유형에서 만든 문구(scheduleText), 근거가 없으면 옛 텍스트 칸, 그것도 없으면 -. */
    workPatternScheduleLabel(wp: WorkPatternItem): string {
      const st = wp.scheduleText;
      const parts = st?.workHours
        ? [st.workDays, st.workHours, st.breakTime]
        : [wp.workDaysText, wp.workHoursText, wp.breakTimeText];
      const shown = parts.filter(Boolean);
      return shown.length ? shown.join(" · ") : "-";
    },
    /**
     * 근무 칸의 줄들 — 형태 하나면 「주5일(월–금) · 09:00–18:00 · 휴게 1시간」 한 줄, 그룹만(3교대)이면 근무일 · 형태별 시간 한 줄씩 · 휴게.
     * 한 줄에 다 붙이면 3교대 근로형태에서 표 폭이 3,900px 까지 늘어났다(2026-09-23 실측) — 줄로 나눠 열 폭을 묶는다.
     */
    workPatternScheduleLines(wp: WorkPatternItem): string[] {
      const st = wp.scheduleText;
      const [days, hours, brk] = st?.workHours
        ? [st.workDays, st.workHours, st.breakTime]
        : [wp.workDaysText, wp.workHoursText, wp.breakTimeText];
      const brkLine = brk ? `휴게 ${brk}` : null;
      if (hours?.includes(" / ")) {
        return [days, ...hours.split(" / "), brkLine].filter((v): v is string => !!v);
      }
      const line = [days, hours, brkLine].filter(Boolean).join(" · ");
      return line ? [line] : [];
    },
    /** 기본 근무 표시 — 「그룹 › 형태」. */
    workPatternDefaultLabel(wp: WorkPatternItem): string {
      if (!wp.shiftGroupName) {
        return "-";
      }
      return wp.shiftName ? `${wp.shiftGroupName} › ${wp.shiftName}` : wp.shiftGroupName;
    },
    workPatternFieldLabel(field: string): string {
      return (WORK_PATTERN_DERIVED_FIELD_LABELS as Record<string, string>)[field] || field;
    },
    /** 재계산 표의 변경 칩 — 「포괄시간 230 → 233」. */
    workPatternChangeLabel(c: WorkPatternRecomputeItem["changes"][number]): string {
      const fmt = (v: number | string | null) =>
        v === null || v === "" ? "없음" : typeof v === "number" ? this.fmtHours(v) : String(v);
      return `${c.label} ${fmt(c.before)} → ${fmt(c.after)}`;
    },
    /** 폼의 직무 선택지 — 활성 직무(+ 지금 값). */
    workPatternFormDutyOptions(): JobDutyItem[] {
      const cur = this.workPatternForm.jobDutyId;
      return this.jobDuties.filter((d) => d.isActive !== false || d.id === cur);
    },
    workPatternFormShifts(): ShiftItem[] {
      const gid = this.workPatternForm.shiftGroupId;
      return this.shifts.filter((x) => x.isActive !== false && (gid ? x.groupId === gid : false));
    },
    onWorkPatternFormGroupChange() {
      const cur = this.shifts.find((x) => x.id === this.workPatternForm.shiftId);
      if (
        !this.workPatternForm.shiftGroupId ||
        (cur && cur.groupId !== this.workPatternForm.shiftGroupId)
      ) {
        this.workPatternForm.shiftId = "";
      }
      this.syncWorkPatternStandardAuto();
    },
    onWorkPatternFormShiftChange() {
      this.syncWorkPatternStandardAuto();
    },
    /** 폼의 기본 근무(그룹 › 형태)에서 만든 근무일 · 근무시간 · 휴게 문구 — 저장값·근로계약 문구와 같은 함수(@hereby/core describeShiftSchedule). */
    workPatternFormScheduleText(): ShiftScheduleText {
      const gid = this.workPatternForm.shiftGroupId;
      if (!gid) {
        return { workDays: null, workHours: null, breakTime: null };
      }
      return describeShiftSchedule({
        group: this.shiftGroups.find((g) => g.id === gid) ?? null,
        shifts: this.shifts.filter((x) => x.groupId === gid),
        shiftId: this.workPatternForm.shiftId || null,
      });
    },
    /** 근무 유형 기준 월 소정근로 기본값(주휴 포함, 주5일 8h = 209). 근거가 없으면 null. */
    workPatternFormStandardHours(): number | null {
      const gid = this.workPatternForm.shiftGroupId;
      if (!gid) {
        return null;
      }
      return deriveMonthlyStandardHours({
        shifts: this.shifts.filter((x) => x.groupId === gid),
        shiftId: this.workPatternForm.shiftId || null,
      });
    },
    /** 자동 모드면 그룹/형태가 바뀔 때 소정근로를 기준값으로 맞춘다(직접 고친 값은 두고 버튼으로만 되돌린다). */
    syncWorkPatternStandardAuto() {
      if (!this.workPatternForm.standardAuto) {
        return;
      }
      const auto = this.workPatternFormStandardHours();
      if (auto !== null) {
        this.workPatternForm.monthlyStandardHours = auto;
      }
    },
    applyWorkPatternStandardAuto() {
      const auto = this.workPatternFormStandardHours();
      if (auto !== null) {
        this.workPatternForm.monthlyStandardHours = auto;
        this.workPatternForm.standardAuto = true;
      }
    },
    /** 폼의 임금산정시간 — 입력 중 즉시 계산(조직 배율). 저장값과 같은 규칙(@hereby/core computeWageHours). */
    workPatternFormWageHours(): number {
      const f = this.workPatternForm;
      return computeWageHours(
        {
          monthlyStandardHours: f.monthlyStandardHours,
          monthlyOvertimeHours: f.monthlyOvertimeHours,
          monthlyNightHours: f.monthlyNightHours,
          monthlyHolidayHours: f.monthlyHolidayHours,
        },
        this.workPatternMultipliers,
      );
    },
    /** 「월 시간으로」 — 일 고정연장·야간 × 월 근무일수를 월 칸에 넣는다(비어 있는 일 값은 건너뛴다). */
    applyWorkPatternDaily() {
      const f = this.workPatternForm;
      const days = Number(f.monthlyWorkingDays);
      if (!(days > 0)) {
        this.notify("월 근무일수를 먼저 입력해 주세요.", "error");
        return;
      }
      if (f.dailyOvertimeHours !== "" && f.dailyOvertimeHours !== null) {
        f.monthlyOvertimeHours = monthlyHoursFromDaily(f.dailyOvertimeHours, days);
      }
      if (f.dailyNightHours !== "" && f.dailyNightHours !== null) {
        f.monthlyNightHours = monthlyHoursFromDaily(f.dailyNightHours, days);
      }
    },
    get workPatternSaveDisabled(): boolean {
      return this.workPatternSaving || !!this.workPatternSaveBlockedReason();
    },
    /**
     * 저장이 막힌 이유(빈 문자열 = 저장 가능) — 푸터에 그대로 보여 준다. 예전엔 버튼만 회색이라 소정근로 칸의 placeholder 「209」를
     * 값으로 알고 저장을 누른 뒤 「저장된 근로형태가 하나도 나오지 않는다」로 접수됐다(CS-OKBEST-260922-0005).
     */
    workPatternSaveBlockedReason(): string {
      const f = this.workPatternForm;
      const missing: string[] = [];
      if (!f.name.trim()) {
        missing.push("이름");
      }
      if (f.monthlyStandardHours === "" || !Number.isFinite(Number(f.monthlyStandardHours))) {
        missing.push("월 소정근로시간");
      }
      // 기본 근무는 필수 — 근무일·근무시간·휴게 문구와 직원 등록 자동 채움의 근거(MED-704)
      if (!f.shiftGroupId) {
        missing.push("기본 근무(근무 그룹)");
      }
      return missing.length ? `${missing.join("·")}을 입력하면 저장할 수 있어요.` : "";
    },
    openAddWorkPattern() {
      this.editingWorkPatternId = "";
      this.workPatternForm = {
        name: "",
        description: "",
        jobDutyId: "",
        shiftGroupId: "",
        shiftId: "",
        // 월 소정근로는 주5일 8시간 표준(209h)으로 열고, 기본 근무를 고르면 근무 유형 기준 자동값으로 바뀐다(직접 고치면 유지)
        standardAuto: true,
        monthlyStandardHours: "209",
        monthlyOvertimeHours: "",
        monthlyNightHours: "",
        monthlyHolidayHours: "",
        dailyOvertimeHours: "",
        dailyNightHours: "",
        monthlyWorkingDays: "",
        isActive: true,
      };
      this.showWorkPatternModal = true;
    },
    openEditWorkPattern(wp: WorkPatternItem) {
      this.editingWorkPatternId = wp.id;
      const n = (v: number | null | undefined) => (v === null || v === undefined ? "" : v);
      this.workPatternForm = {
        name: wp.name,
        description: wp.description || "",
        jobDutyId: wp.jobDutyId || "",
        shiftGroupId: wp.shiftGroupId || "",
        shiftId: wp.shiftId || "",
        // 저장된 값은 그대로 — 병원 표처럼 기준과 다른 값(나이트 149h)을 조용히 덮지 않는다(자동은 버튼으로만)
        standardAuto: false,
        monthlyStandardHours: wp.monthlyStandardHours,
        monthlyOvertimeHours: wp.monthlyOvertimeHours,
        monthlyNightHours: wp.monthlyNightHours,
        monthlyHolidayHours: wp.monthlyHolidayHours,
        dailyOvertimeHours: n(wp.dailyOvertimeHours),
        dailyNightHours: n(wp.dailyNightHours),
        monthlyWorkingDays: n(wp.monthlyWorkingDays),
        isActive: wp.isActive !== false,
      };
      this.showWorkPatternModal = true;
    },
    async saveWorkPattern() {
      if (this.workPatternSaveDisabled) {
        return;
      }
      this.workPatternSaving = true;
      try {
        const f = this.workPatternForm;
        const num = (v: string | number) => (v === "" || v === null ? 0 : Number(v));
        const opt = (v: string | number) =>
          v === "" || v === null || !Number.isFinite(Number(v)) ? null : Number(v);
        const payload = {
          name: f.name.trim(),
          description: f.description.trim() || null,
          jobDutyId: f.jobDutyId || null,
          shiftGroupId: f.shiftGroupId || null,
          shiftId: f.shiftGroupId ? f.shiftId || null : null,
          monthlyStandardHours: num(f.monthlyStandardHours),
          monthlyOvertimeHours: num(f.monthlyOvertimeHours),
          monthlyNightHours: num(f.monthlyNightHours),
          monthlyHolidayHours: num(f.monthlyHolidayHours),
          dailyOvertimeHours: opt(f.dailyOvertimeHours),
          dailyNightHours: opt(f.dailyNightHours),
          monthlyWorkingDays: opt(f.monthlyWorkingDays),
          isActive: coerceSelectBoolean(f.isActive),
        };
        const wasEditing = !!this.editingWorkPatternId;
        let affected = 0;
        let editedId = this.editingWorkPatternId;
        if (wasEditing) {
          const res = await this.sdk.workPattern.update({
            id: this.editingWorkPatternId,
            ...payload,
          });
          affected = res.derivationChanged ? res.affectedEmployees : 0;
        } else {
          const res = await this.sdk.workPattern.create(payload);
          editedId = res.id;
        }
        this.showWorkPatternModal = false;
        await this.loadWorkPatterns();
        this.notify(wasEditing ? "근로형태를 수정했습니다" : "근로형태를 추가했습니다");
        // 시간·근무일 텍스트가 바뀌었고 그 근로형태 직원이 있으면 파생값 재계산을 미리보기로 연다(바로 쓰지 않는다)
        if (affected > 0) {
          const wp = this.workPatterns.find((w) => w.id === editedId);
          if (wp) {
            await this.openWorkPatternRecompute(wp);
          }
        }
      } catch (err) {
        this.notify(
          `${this._t("admin.save_failed")}: ${err instanceof Error ? err.message : String(err)}`,
          "error",
        );
      }
      this.workPatternSaving = false;
    },
    deleteWorkPattern(wp: WorkPatternItem) {
      this.confirmTop = true;
      if (wp.isActive !== false || wp.employeeCount > 0) {
        this.confirmTitle = "삭제";
        this.confirmMessage =
          wp.isActive !== false
            ? "근로형태를 삭제할 수 없어요. 먼저 비활성 상태로 변경한 뒤 삭제할 수 있어요."
            : `직원 ${wp.employeeCount}명이 이 근로형태로 등록돼 있어 삭제할 수 없어요. 직원의 근로형태를 먼저 바꿔 주세요.`;
        this.confirmLabel = this._t("common.confirm");
        this.confirmHideCancel = true;
        this.confirmAction = null;
        this.showConfirm = true;
        return;
      }
      this.confirmTitle = "삭제";
      this.confirmMessage = `${wp.name} 근로형태를 삭제할까요?`;
      this.confirmLabel = this._t("common.confirm");
      this.confirmHideCancel = false;
      this.confirmAction = async () => {
        try {
          await this.sdk.workPattern.delete(wp.id);
          await this.loadWorkPatterns();
          this.notify("근로형태를 삭제했습니다");
        } catch (err) {
          this.notify(
            `${this._t("admin.delete_failed")}: ${err instanceof Error ? err.message : String(err)}`,
            "error",
          );
        }
      };
      this.showConfirm = true;
    },
    /** 파생값 재계산 미리보기 — 이 근로형태 직원 전부를 dryRun 으로 대조한다. */
    // ── 근무 그룹에서 가져오기 — 미리보기(dryRun) → 적용. 근무 그룹으로 이미 배정된 직원이 근로형태 목록에 보이게(CS-OKBEST-260922-0005 후속)
    showWorkPatternImport: false,
    workPatternImport: {
      loading: false,
      applying: false,
      total: 0,
      newCount: 0,
      employeeCount: 0,
      items: [] as WorkPatternImportItem[],
    },
    async openWorkPatternImport() {
      this.workPatternImport = {
        loading: true,
        applying: false,
        total: 0,
        newCount: 0,
        employeeCount: 0,
        items: [],
      };
      this.showWorkPatternImport = true;
      try {
        const res = await this.sdk.workPattern.importFromShiftGroups({ dryRun: true });
        this.workPatternImport.total = res.total;
        this.workPatternImport.newCount = res.newCount;
        this.workPatternImport.employeeCount = res.employeeCount;
        this.workPatternImport.items = res.items;
      } catch (err) {
        this.showWorkPatternImport = false;
        this.notify(`가져오기 미리보기 실패: ${this._errMessage(err)}`, "error");
      }
      this.workPatternImport.loading = false;
    },
    async applyWorkPatternImport() {
      const r = this.workPatternImport;
      if (r.applying || r.items.length === 0) {
        return;
      }
      r.applying = true;
      try {
        const res = await this.sdk.workPattern.importFromShiftGroups({ dryRun: false });
        this.showWorkPatternImport = false;
        this.notify(`근로형태 ${res.created}개를 만들고 직원 ${res.linked}명을 이었습니다`);
        await Promise.all([this.loadWorkPatterns(), this.loadJobDuties()]);
      } catch (err) {
        this.notify(`가져오기 실패: ${this._errMessage(err)}`, "error");
      }
      r.applying = false;
    },
    async openWorkPatternRecompute(wp: WorkPatternItem) {
      this.workPatternRecompute = {
        workPatternId: wp.id,
        title: wp.name,
        loading: true,
        applying: false,
        total: 0,
        changedCount: 0,
        noContractCount: 0,
        items: [],
      };
      this.showWorkPatternRecompute = true;
      try {
        const res = await this.sdk.workPattern.recompute({ workPatternId: wp.id, dryRun: true });
        this.workPatternRecompute.total = res.total;
        this.workPatternRecompute.changedCount = res.changedCount;
        this.workPatternRecompute.noContractCount = res.noContractCount;
        this.workPatternRecompute.items = res.items;
      } catch (err) {
        this.showWorkPatternRecompute = false;
        this.notify(
          `재계산 미리보기 실패: ${err instanceof Error ? err.message : String(err)}`,
          "error",
        );
      }
      this.workPatternRecompute.loading = false;
    },
    /** 미리보기에서 달라진 직원에게만 적용 — 직원마다 근로계약 새 버전이 만들어진다. */
    async applyWorkPatternRecompute() {
      const r = this.workPatternRecompute;
      if (r.applying || r.items.length === 0) {
        return;
      }
      r.applying = true;
      try {
        const res = await this.sdk.workPattern.recompute({
          employeeIds: r.items.map((it) => it.employeeId),
          dryRun: false,
        });
        this.showWorkPatternRecompute = false;
        this.notify(`직원 ${res.changedCount}명의 파생값을 근로형태 기준으로 맞췄습니다`);
        await this.loadWorkPatterns();
      } catch (err) {
        this.notify(
          `재계산 적용 실패: ${err instanceof Error ? err.message : String(err)}`,
          "error",
        );
      }
      r.applying = false;
    },

    /** 직무 폼 — 기본 그룹 하위 활성 형태(기본 형태·허용 목록의 선택지). */
    jobDutyFormShifts(): ShiftItem[] {
      const gid = this.jobDutyForm.shiftGroupId;
      return this.shifts.filter((x) => x.isActive !== false && (gid ? x.groupId === gid : false));
    },
    onJobDutyFormGroupChange() {
      const cur = this.shifts.find((x) => x.id === this.jobDutyForm.shiftId);
      if (
        !this.jobDutyForm.shiftGroupId ||
        (cur && cur.groupId !== this.jobDutyForm.shiftGroupId)
      ) {
        this.jobDutyForm.shiftId = "";
      }
    },
    get jobDutySaveDisabled(): boolean {
      return this.jobDutySaving || !this.jobDutyForm.name.trim();
    },
    openAddJobDuty() {
      this.editingJobDutyId = "";
      this.jobDutyForm = {
        name: "",
        description: "",
        jobCategory: "",
        shiftGroupId: "",
        shiftId: "",
        defaultWorkPatternId: "",
        isActive: true,
      };
      this.showJobDutyModal = true;
    },
    openEditJobDuty(duty: JobDutyItem) {
      this.editingJobDutyId = duty.id;
      this.jobDutyForm = {
        name: duty.name,
        description: duty.description || "",
        jobCategory: duty.jobCategory || "",
        shiftGroupId: duty.shiftGroupId || "",
        shiftId: duty.shiftId || "",
        defaultWorkPatternId: duty.defaultWorkPatternId || "",
        isActive: duty.isActive !== false,
      };
      this.showJobDutyModal = true;
    },
    async saveJobDuty() {
      if (this.jobDutySaveDisabled) {
        return;
      }
      this.jobDutySaving = true;
      try {
        const f = this.jobDutyForm;
        const payload = {
          name: f.name.trim(),
          description: f.description.trim() || null,
          // 직군 select 는 문자열 — 서버가 JobCategory enum 으로 검증한다
          jobCategory: (f.jobCategory || null) as JobCategory | null,
          shiftGroupId: f.shiftGroupId || null,
          shiftId: f.shiftGroupId ? f.shiftId || null : null,
          defaultWorkPatternId: f.defaultWorkPatternId || null,
          // 허용 근무형태(allowedShiftIds)는 폼에서 뺐다(정렬에만 쓰여 관리 비용 대비 효과가 작다) — 생략하면 서버가 기존 값을 유지한다
          isActive: coerceSelectBoolean(f.isActive),
        };
        if (this.editingJobDutyId) {
          await this.sdk.jobDuty.update({ id: this.editingJobDutyId, ...payload });
        } else {
          await this.sdk.jobDuty.create(payload);
        }
        const wasEditing = !!this.editingJobDutyId;
        this.showJobDutyModal = false;
        await this.loadJobDuties();
        this.notify(wasEditing ? "직무를 수정했습니다" : "직무를 추가했습니다");
      } catch (err) {
        this.notify(
          `${this._t("admin.save_failed")}: ${err instanceof Error ? err.message : String(err)}`,
          "error",
        );
      }
      this.jobDutySaving = false;
    },
    deleteJobDuty(duty: JobDutyItem) {
      // 유형·기준 관리 패널(z-50) 안에서 호출되므로 확인창을 그 위(z-60)에 띄운다.
      this.confirmTop = true;
      // 활성 직무·직원이 있는 직무는 삭제 불가 — 안내 전용(취소 버튼 숨김) DS 모달(근무 그룹과 같은 규칙).
      if (duty.isActive !== false || duty.employeeCount > 0) {
        this.confirmTitle = "삭제";
        this.confirmMessage =
          duty.isActive !== false
            ? "직무를 삭제할 수 없어요. 먼저 비활성 상태로 변경한 뒤 삭제할 수 있어요."
            : `직원 ${duty.employeeCount}명이 이 직무로 등록돼 있어 삭제할 수 없어요. 직원의 직무를 먼저 바꿔 주세요.`;
        this.confirmLabel = this._t("common.confirm");
        this.confirmHideCancel = true;
        this.confirmAction = null;
        this.showConfirm = true;
        return;
      }
      this.confirmTitle = "삭제";
      this.confirmMessage = `${duty.name} 직무를 삭제할까요?`;
      this.confirmLabel = this._t("common.confirm");
      this.confirmHideCancel = false;
      this.confirmAction = async () => {
        try {
          await this.sdk.jobDuty.delete(duty.id);
          await this.loadJobDuties();
          this.notify("직무를 삭제했습니다");
        } catch (err) {
          this.notify(
            `${this._t("admin.delete_failed")}: ${err instanceof Error ? err.message : String(err)}`,
            "error",
          );
        }
      };
      this.showConfirm = true;
    },

    openAddGroup() {
      this.editingGroupId = "";
      this.shiftGroupForm = {
        name: "",
        description: "",
        schedulerEnabled: false,
        isActive: true,
      };
      this.showShiftGroupModal = true;
    },

    // biome-ignore lint/suspicious/noExplicitAny: group item is tRPC-inferred
    openEditGroup(group: any) {
      this.editingGroupId = group.id as string;
      this.shiftGroupForm = {
        name: (group.name as string) || "",
        description: (group.description as string) || "",
        schedulerEnabled: Boolean(group.schedulerEnabled),
        isActive: group.isActive !== false,
      };
      this.showShiftGroupModal = true;
    },

    async saveGroup() {
      if (!this.shiftGroupForm.name) {
        return;
      }
      this.shiftGroupSaving = true;
      try {
        // 상태 select 가 문자열("false")로 들어올 수 있어 boolean 으로 강제 변환.
        const payload = {
          ...this.shiftGroupForm,
          isActive: coerceSelectBoolean(this.shiftGroupForm.isActive),
        };
        if (this.editingGroupId) {
          await this.sdk.shiftGroup.update({ id: this.editingGroupId, ...payload });
        } else {
          await this.sdk.shiftGroup.create(payload);
        }
        const wasEditing = !!this.editingGroupId;
        this.showShiftGroupModal = false;
        await this.loadShiftGroups();
        this.notify(wasEditing ? "근무 그룹을 수정했습니다" : "근무 그룹을 추가했습니다");
      } catch (err) {
        this.notify(
          `${this._t("admin.save_failed")}: ${err instanceof Error ? err.message : String(err)}`,
          "error",
        );
      }
      this.shiftGroupSaving = false;
    },

    // biome-ignore lint/suspicious/noExplicitAny: group item is tRPC-inferred
    deleteGroup(group: any) {
      // 유형·기준 관리 패널(z-50) 안에서 호출되므로 확인창을 그 위(z-60)에 띄운다.
      this.confirmTop = true;
      // 활성 그룹은 삭제 불가 — 안내 전용(취소 버튼 숨김) DS 모달.
      if (group.isActive !== false) {
        this.confirmTitle = "삭제";
        this.confirmMessage =
          "근무 그룹을 삭제할 수 없어요. 먼저 비활성 상태로 변경한 뒤 삭제할 수 있어요.";
        this.confirmLabel = this._t("common.confirm");
        this.confirmHideCancel = true;
        this.confirmAction = null;
        this.showConfirm = true;
        return;
      }
      this.confirmTitle = "삭제";
      this.confirmMessage = `삭제하면 ${group.name}에 포함된 모든 근무 유형도 함께 삭제돼요.`;
      this.confirmLabel = this._t("common.confirm");
      this.confirmHideCancel = false;
      this.confirmAction = async () => {
        try {
          await this.sdk.shiftGroup.delete(group.id as string);
          await this.loadShiftGroups();
          await this.loadShifts();
          this.notify("근무 그룹을 삭제했습니다");
        } catch (err) {
          this.notify(
            `${this._t("admin.delete_failed")}: ${err instanceof Error ? err.message : String(err)}`,
            "error",
          );
        }
      };
      this.showConfirm = true;
    },

    // ───────── 근무 유형 ─────────
    /** 그룹에서 이미 쓰인 색상 다음으로 팔레트에서 자동 선택. */
    nextCodeColor(group: { shifts?: Array<{ codeColor?: string | null }> } | null): string {
      const used = new Set(
        (group?.shifts || []).map((s) => (s.codeColor || "").toUpperCase()).filter(Boolean),
      );
      const free = this.shiftCodeColors.find((c) => !used.has(c.toUpperCase()));
      return free || this.shiftCodeColors[used.size % this.shiftCodeColors.length];
    },

    // biome-ignore lint/suspicious/noExplicitAny: group item is tRPC-inferred
    openAddType(group: any) {
      this.editingShiftId = "";
      this.typeFormTeamIds = [];
      this.typeFormTeamIdsInitial = [];
      this.typeFormJobDutyIds = [];
      this.typeFormJobDutyIdsInitial = [];
      if (this.allTeams.length === 0) {
        this.loadAllTeams();
      }
      this.typeFormGroupId = group.id as string;
      this.typeFormGroupName = (group.name as string) || "";
      this.typeFormGroupSchedulerEnabled = Boolean(group.schedulerEnabled);
      this.shiftTypeForm = {
        code: "",
        codeColor: this.nextCodeColor(group),
        name: "",
        description: "",
        startTime: "09:00",
        endTime: "18:00",
        isOvernight: false,
        workHours: 8,
        breakHours: 1,
        overtimeBreakHours: 0,
        isActive: true,
        applicableDays: {
          monday: true,
          tuesday: true,
          wednesday: true,
          thursday: true,
          friday: true,
          saturday: false,
          sunday: false,
        },
        dayOverrides: {},
        // 연차 차감의 비근무일 판정 (연차 원장 개편) — 기본은 요일 기준·공휴일 휴무
        worksOnHolidays: false,
        leaveDeductionMode: "fixed_days",
      };
      this.showShiftTypeModal = true;
    },

    /**
     * 저장된 요일별 시간 → 폼 모양. 저장값은 부분(시간만 있는 요일 등)일 수 있으므로
     * 빠진 필드는 그 근무 형태의 기본값으로 채워 폼에서는 늘 네 필드가 다 있게 한다.
     */
    // biome-ignore lint/suspicious/noExplicitAny: shift item is tRPC-inferred
    _hydrateShiftDayOverrides(shift: any) {
      const stored = (shift.dayOverrides as Record<string, Record<string, unknown>> | null) || {};
      const baseStart = ((shift.startTime as string) || "09:00:00").slice(0, 5);
      const baseEnd = ((shift.endTime as string) || "18:00:00").slice(0, 5);
      const baseWork = Number(shift.workHours) || 8;
      const baseBreak = Number(shift.breakHours) || 1;
      const out: Record<
        string,
        { startTime: string; endTime: string; workHours: number; breakHours: number }
      > = {};
      for (const [day, value] of Object.entries(stored)) {
        if (!value || Object.keys(value).length === 0) {
          continue;
        }
        out[day] = {
          startTime: String(value.startTime ?? baseStart).slice(0, 5),
          endTime: String(value.endTime ?? baseEnd).slice(0, 5),
          workHours: Number(value.workHours ?? baseWork),
          breakHours: Number(value.breakHours ?? baseBreak),
        };
      }
      return out;
    },

    /**
     * 「이 요일은 다른 시간」 토글 — 켜면 기본 시간을 복사해 그 요일의 한 벌을 만들고,
     * 끄면 키를 지워 기본 시간으로 되돌린다.
     */
    toggleShiftDayOverride(day: string) {
      const f = this.shiftTypeForm;
      if (f.dayOverrides[day]) {
        const { [day]: _removed, ...rest } = f.dayOverrides;
        f.dayOverrides = rest;
        return;
      }
      f.dayOverrides = {
        ...f.dayOverrides,
        [day]: {
          startTime: f.startTime,
          endTime: f.endTime,
          workHours: f.workHours,
          breakHours: f.breakHours,
        },
      };
    },

    /** 목록 배지 — 「화·목 다름」. 요일별 시간이 없으면 빈 문자열. */
    // biome-ignore lint/suspicious/noExplicitAny: shift item is tRPC-inferred
    shiftDayOverrideLabel(shift: any): string {
      const stored = (shift?.dayOverrides as Record<string, Record<string, unknown>> | null) || {};
      const labels: Record<string, string> = {
        monday: "월",
        tuesday: "화",
        wednesday: "수",
        thursday: "목",
        friday: "금",
        saturday: "토",
        sunday: "일",
      };
      const days = Object.keys(labels).filter((d) => {
        const v = stored[d];
        return v && Object.values(v).some((x) => x !== null && x !== undefined && x !== "");
      });
      return days.length > 0 ? `${days.map((d) => labels[d]).join("·")} 다름` : "";
    },

    /** 근무 유형의 적용 요일 — 「월–금」·「월·수·금」·「월–금·일」. 비어 있으면 근태 판정과 같이 월–금으로 본다. */
    shiftDaysLabel(shift: ShiftScheduleSource): string {
      return formatDayRange(applicableDayKeys(shift));
    },

    // biome-ignore lint/suspicious/noExplicitAny: group/shift items are tRPC-inferred
    openEditType(group: any, shift: any) {
      this.editingShiftId = shift.id as string;
      const representativeTeamIds = this.allTeams
        .filter((t) => t.shiftId === shift.id)
        .map((t) => t.id);
      this.typeFormTeamIds = [...representativeTeamIds];
      this.typeFormTeamIdsInitial = [...representativeTeamIds];
      // 기본으로 쓰는 직무 — 직무 마스터에서 이 형태를 기본 근무로 둔 직무(직무 설정의 같은 연결을 반대쪽에서 편집)
      const defaultDutyIds = this.jobDuties.filter((d) => d.shiftId === shift.id).map((d) => d.id);
      this.typeFormJobDutyIds = [...defaultDutyIds];
      this.typeFormJobDutyIdsInitial = [...defaultDutyIds];
      this.typeFormGroupId = group.id as string;
      this.typeFormGroupName = (group.name as string) || "";
      this.typeFormGroupSchedulerEnabled = Boolean(group.schedulerEnabled);
      const days = (shift.applicableDays as Record<string, boolean>) || {};
      this.shiftTypeForm = {
        code: (shift.code as string) || "",
        codeColor: (shift.codeColor as string) || this.shiftCodeColors[0],
        name: (shift.name as string) || "",
        description: (shift.description as string) || "",
        startTime: ((shift.startTime as string) || "09:00:00").slice(0, 5),
        endTime: ((shift.endTime as string) || "18:00:00").slice(0, 5),
        isOvernight: Boolean(shift.isOvernight),
        workHours: Number(shift.workHours) || 8,
        breakHours: Number(shift.breakHours) || 1,
        overtimeBreakHours: Number(shift.overtimeBreakHours) || 0,
        isActive: shift.isActive !== false,
        applicableDays: {
          monday: days.monday ?? true,
          tuesday: days.tuesday ?? true,
          wednesday: days.wednesday ?? true,
          thursday: days.thursday ?? true,
          friday: days.friday ?? true,
          saturday: days.saturday ?? false,
          sunday: days.sunday ?? false,
        },
        dayOverrides: this._hydrateShiftDayOverrides(shift),
        worksOnHolidays: Boolean(shift.worksOnHolidays),
        leaveDeductionMode:
          (shift.leaveDeductionMode as "fixed_days" | "duty" | undefined) || "fixed_days",
      };
      this.showShiftTypeModal = true;
    },

    /** 이 근무 그룹(또는 그 아래 형태)을 대표 근무로 둔 팀 — 근무 설정 그룹 카드 표시용. */
    groupRepresentativeTeams(groupId: string): OrgTeamItem[] {
      return this.allTeams.filter((t) => t.shiftGroupId === groupId && t.isActive !== false);
    },
    /** 「대표 팀: 통합병동(1W) 외 1」 */
    groupRepresentativeTeamsLabel(groupId: string): string {
      const teams = this.groupRepresentativeTeams(groupId);
      if (teams.length === 0) {
        return "";
      }
      return teams.length === 1
        ? `대표 팀: ${teams[0].name}`
        : `대표 팀: ${teams[0].name} 외 ${teams.length - 1}`;
    },
    /** 툴팁 — 팀마다 한 줄, 형태까지 대표면 형태 이름을 붙인다. */
    groupRepresentativeTeamsTitle(groupId: string): string {
      return this.groupRepresentativeTeams(groupId)
        .map((t) => {
          const shift = t.shiftId
            ? (this.shifts as ShiftItem[]).find((x) => x.id === t.shiftId)
            : null;
          return shift ? `${t.name} · ${shift.name}` : `${t.name} (근무형태 미지정)`;
        })
        .join("\n");
    },
    /** 「대표로 쓰는 팀」 선택지 — 활성 팀, 이름순. */
    typeFormTeamOptions(): OrgTeamItem[] {
      return this.allTeams
        .filter((t) => t.isActive !== false)
        .slice()
        .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "ko"));
    },
    typeFormTeamName(id: string): string {
      return this.allTeams.find((t) => t.id === id)?.name || "(삭제된 팀)";
    },
    /** 이 팀에 지금 다른 대표 근무가 있으면 그 이름 — 같은 그룹의 「그룹만 대표」는 교체가 아니라 구체화라 빈 문자열. */
    typeFormTeamCurrentRepresentative(team: OrgTeamItem): string {
      const groupId = (team.shiftGroupId as string | null) || "";
      const shiftId = (team.shiftId as string | null) || "";
      if (!groupId || (this.editingShiftId && shiftId === this.editingShiftId)) {
        return "";
      }
      if (!shiftId && groupId === this.typeFormGroupId) {
        return "";
      }
      return this.bulkShiftAssignLabel(groupId, shiftId);
    },
    typeFormTeamOptionLabel(team: OrgTeamItem): string {
      const current = this.typeFormTeamCurrentRepresentative(team);
      const name = team.name || "";
      return current ? `${name} — 현재 대표: ${current}` : name;
    },
    /** 팀 추가 — 이미 다른 대표 근무가 있는 팀은 바꿀지 먼저 묻는다(저장은 근무 유형 저장 때). */
    async addTypeFormTeam(id: string) {
      if (!id || this.typeFormTeamIds.includes(id)) {
        return;
      }
      const team = this.allTeams.find((t) => t.id === id);
      const current = team ? this.typeFormTeamCurrentRepresentative(team) : "";
      if (current) {
        const ok = await askConfirm({
          message: `「${team?.name}」 팀의 대표 근무는 지금 「${current}」예요.\n저장하면 이 근무 유형으로 바뀌어요. 바꿀까요?`,
          confirmLabel: "바꾸기",
        });
        if (!ok) {
          return;
        }
      }
      this.typeFormTeamIds.push(id);
    },
    removeTypeFormTeam(id: string) {
      this.typeFormTeamIds = this.typeFormTeamIds.filter((x) => x !== id);
    },
    /** 「기본으로 쓰는 직무」 선택지 — 활성 직무, 이름순. */
    typeFormJobDutyOptions(): JobDutyItem[] {
      return this.jobDuties
        .filter((d) => d.isActive !== false)
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name, "ko"));
    },
    typeFormJobDutyName(id: string): string {
      return this.jobDuties.find((d) => d.id === id)?.name || "(삭제된 직무)";
    },
    /** 이 직무에 지금 다른 기본 근무가 있으면 그 이름 — 같은 그룹의 「그룹만 기본」은 교체가 아니라 구체화라 빈 문자열. */
    typeFormJobDutyCurrentDefault(duty: JobDutyItem): string {
      const groupId = duty.shiftGroupId || "";
      const shiftId = duty.shiftId || "";
      if (!groupId || (this.editingShiftId && shiftId === this.editingShiftId)) {
        return "";
      }
      if (!shiftId && groupId === this.typeFormGroupId) {
        return "";
      }
      return this.bulkShiftAssignLabel(groupId, shiftId);
    },
    typeFormJobDutyOptionLabel(duty: JobDutyItem): string {
      const current = this.typeFormJobDutyCurrentDefault(duty);
      return current ? `${duty.name} — 현재 기본: ${current}` : duty.name;
    },
    /** 직무 추가 — 이미 다른 기본 근무가 있는 직무는 바꿀지 먼저 묻는다(저장은 근무 유형 저장 때). */
    async addTypeFormJobDuty(id: string) {
      if (!id || this.typeFormJobDutyIds.includes(id)) {
        return;
      }
      const duty = this.jobDuties.find((d) => d.id === id);
      const current = duty ? this.typeFormJobDutyCurrentDefault(duty) : "";
      if (current) {
        const ok = await askConfirm({
          message: `「${duty?.name}」 직무의 기본 근무는 지금 「${current}」예요.\n저장하면 이 근무 유형으로 바뀌어요. 바꿀까요?`,
          confirmLabel: "바꾸기",
        });
        if (!ok) {
          return;
        }
      }
      this.typeFormJobDutyIds.push(id);
    },
    removeTypeFormJobDuty(id: string) {
      this.typeFormJobDutyIds = this.typeFormJobDutyIds.filter((x) => x !== id);
    },

    async saveType() {
      if (!this.shiftTypeForm.name) {
        return;
      }
      // 코드는 그룹 '스케줄러 연동' ON일 때만 필수.
      if (this.typeFormGroupSchedulerEnabled && !this.shiftTypeForm.code) {
        return;
      }
      this.shiftTypeSaving = true;
      try {
        // 상태 select 가 문자열("false")로 들어올 수 있어 boolean 으로 강제 변환.
        // leaveDeductionMode는 서버가 LeaveDeductionMode enum으로 받는다 — 값은 같지만
        // TS enum은 문자열 리터럴과 nominal하게 다르므로 경계에서 캐스트한다.
        // 적용 요일에서 빠진 요일의 오버라이드는 버린다 — 근무하지 않는 요일의 시간은 뜻이 없고,
        // 남겨 두면 목록의 「요일별 다름」 배지만 켜진다.
        const dayOverrides = Object.fromEntries(
          Object.entries(this.shiftTypeForm.dayOverrides).filter(
            ([day]) => this.shiftTypeForm.applicableDays[day],
          ),
        );
        const payload = {
          ...this.shiftTypeForm,
          dayOverrides,
          isActive: coerceSelectBoolean(this.shiftTypeForm.isActive),
          leaveDeductionMode: this.shiftTypeForm.leaveDeductionMode as LeaveDeductionMode,
          groupId: this.typeFormGroupId,
        };
        let shiftId = this.editingShiftId;
        if (this.editingShiftId) {
          await this.sdk.shift.update({ id: this.editingShiftId, ...payload });
        } else {
          const created = await this.sdk.shift.create(payload);
          shiftId = (created as { id?: string } | null)?.id ?? "";
        }
        const wasEditing = !!this.editingShiftId;
        // 대표로 쓰는 팀 — 근무 유형이 저장된 뒤(새 유형은 id 가 생긴 뒤)에, 바뀐 경우에만 한 번.
        // 이 호출만 실패하면 근무 유형은 이미 저장됐으므로 따로 알린다.
        const sortedIds = (ids: string[]) => JSON.stringify([...ids].sort());
        let teamSaveError = "";
        if (shiftId && sortedIds(this.typeFormTeamIds) !== sortedIds(this.typeFormTeamIdsInitial)) {
          try {
            await this.sdk.team.setRepresentativeShift(shiftId, this.typeFormTeamIds);
            await this.loadAllTeams();
          } catch (err) {
            teamSaveError = err instanceof Error ? err.message : String(err);
          }
        }
        // 기본으로 쓰는 직무 — 같은 규칙(저장 뒤 · 바뀐 경우에만 · 실패는 따로 알림)
        let dutySaveError = "";
        if (
          shiftId &&
          sortedIds(this.typeFormJobDutyIds) !== sortedIds(this.typeFormJobDutyIdsInitial)
        ) {
          try {
            await this.sdk.jobDuty.setDefaultShift(shiftId, this.typeFormJobDutyIds);
            await this.loadJobDuties();
          } catch (err) {
            dutySaveError = err instanceof Error ? err.message : String(err);
          }
        }
        this.showShiftTypeModal = false;
        await this.loadShiftGroups();
        await this.loadShifts();
        if (teamSaveError || dutySaveError) {
          const what = [teamSaveError && "대표로 쓰는 팀", dutySaveError && "기본으로 쓰는 직무"]
            .filter(Boolean)
            .join("·");
          this.notify(
            `근무 유형은 저장했지만 ${what}은 저장하지 못했어요: ${teamSaveError || dutySaveError}`,
            "error",
          );
        } else {
          this.notify(wasEditing ? "근무 유형을 수정했습니다" : "근무 유형을 추가했습니다");
        }
      } catch (err) {
        this.notify(
          `${this._t("admin.save_failed")}: ${err instanceof Error ? err.message : String(err)}`,
          "error",
        );
      }
      this.shiftTypeSaving = false;
    },

    // biome-ignore lint/suspicious/noExplicitAny: shift item is tRPC-inferred
    deleteType(shift: any) {
      // 유형·기준 관리 패널(z-50) 안에서 호출되므로 확인창을 그 위(z-60)에 띄운다.
      this.confirmTop = true;
      // 활성 유형은 삭제 불가 — 안내 전용(취소 버튼 숨김) DS 모달.
      if (shift.isActive !== false) {
        this.confirmTitle = "삭제";
        this.confirmMessage =
          "근무 유형을 삭제할 수 없어요. 먼저 비활성 상태로 변경한 뒤 삭제할 수 있어요.";
        this.confirmLabel = this._t("common.confirm");
        this.confirmHideCancel = true;
        this.confirmAction = null;
        this.showConfirm = true;
        return;
      }
      this.confirmTitle = "삭제";
      this.confirmMessage = `${shift.name} 근무 유형을 삭제할까요?`;
      this.confirmLabel = this._t("common.confirm");
      this.confirmHideCancel = false;
      this.confirmAction = async () => {
        try {
          await this.sdk.shift.delete(shift.id as string);
          await this.loadShiftGroups();
          await this.loadShifts();
          this.notify("근무 유형을 삭제했습니다");
        } catch (err) {
          this.notify(
            `${this._t("admin.delete_failed")}: ${err instanceof Error ? err.message : String(err)}`,
            "error",
          );
        }
      };
      this.showConfirm = true;
    },

    formatPhone(val: string): string {
      return val.replace(/\D/g, "").slice(0, 11);
    },

    formatRrn(val: string): string {
      const digits = val.replace(/\D/g, "").slice(0, 13);
      if (digits.length <= 6) {
        return digits;
      }
      return `${digits.slice(0, 6)}-${digits.slice(6)}`;
    },

    deptStatusLabel(status: string): string {
      const keyMap: Record<string, string> = {
        ACTIVE: "admin.dept_status_active",
        INACTIVE: "admin.dept_status_inactive",
        RESTRUCTURING: "admin.dept_status_restructuring",
      };
      return keyMap[status] ? this._t(keyMap[status]) : (status ?? "-");
    },

    shiftTypeLabel(type: string): string {
      const keyMap: Record<string, string> = {
        day: "admin.shift_type_day",
        night: "admin.shift_type_night",
        evening: "admin.shift_type_evening",
        weekend_day: "admin.shift_type_weekend_day",
        weekend_night: "admin.shift_type_weekend_night",
        holiday: "admin.shift_type_holiday",
        flexible: "admin.shift_type_flexible",
        on_call: "admin.shift_type_on_call",
        off: "admin.shift_type_off",
      };
      return keyMap[type] ? this._t(keyMap[type]) : type;
    },

    /** Label for the 소정/연장 classification. */
    shiftClassificationLabel(classification: string): string {
      return classification === "overtime"
        ? this._t("settings.shift_classification_overtime")
        : this._t("settings.shift_classification_regular");
    },

    applicableDaysText(days: Record<string, boolean> | null): string {
      if (!days) {
        return "-";
      }
      const keyMap: Record<string, string> = {
        monday: "admin.day_mon",
        tuesday: "admin.day_tue",
        wednesday: "admin.day_wed",
        thursday: "admin.day_thu",
        friday: "admin.day_fri",
        saturday: "admin.day_sat",
        sunday: "admin.day_sun",
      };
      const order = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
      return order
        .filter((d) => days[d])
        .map((d) => this._t(keyMap[d]))
        .join(" ");
    },

    async loadSalaryGradeCategories() {
      try {
        const cats = await this.sdk.trpc.salaryGrade.categories.query();
        this.salaryGradeCategories = (cats || []).map((c) => ({
          id: c.id as string,
          name: c.name as string,
        }));
      } catch {
        this.salaryGradeCategories = [];
      }
    },

    categoryName(id: string): string {
      return (
        this.salaryGradeCategories.find((c: { id: string; name: string }) => c.id === id)?.name ||
        id
      );
    },

    openAddCategory() {
      this.categoryModalMode = "add";
      this.categoryForm = { id: "", name: "" };
      this.showCategoryModal = true;
    },

    openEditCategory(catId: string) {
      const cat = this.salaryGradeCategories.find(
        (c: { id: string; name: string }) => c.id === catId,
      );
      this.categoryModalMode = "edit";
      this.categoryForm = { id: catId, name: cat?.name || "" };
      this.showCategoryModal = true;
    },

    async saveCategory() {
      if (!this.categoryForm.name.trim()) {
        return;
      }
      try {
        if (this.categoryModalMode === "edit" && this.categoryForm.id) {
          await this.sdk.trpc.salaryGrade.updateCategory.mutate({
            id: this.categoryForm.id,
            name: this.categoryForm.name.trim(),
          });
        } else {
          const result = await this.sdk.trpc.salaryGrade.createCategory.mutate({
            name: this.categoryForm.name.trim(),
          });
          // Select the new category
          this.salaryGradeFilter.categoryId = result.id;
        }
        const wasEditingCat = this.categoryModalMode === "edit";
        this.showCategoryModal = false;
        await this.loadSalaryGradeCategories();
        await this.loadSalaryGrades();
        this.notify(wasEditingCat ? "직종을 수정했습니다" : "직종을 추가했습니다");
      } catch (err) {
        showError(`저장 실패: ${err instanceof Error ? err.message : String(err)}`);
      }
    },

    async deleteCategory() {
      if (!this.categoryForm.id) {
        return;
      }
      const categoryId = this.categoryForm.id;
      const categoryName = this.categoryForm.name;
      this.confirmMessage = `"${categoryName}" 직종의 모든 호봉 데이터가 삭제됩니다. 계속하시겠습니까?`;
      this.confirmAction = async () => {
        try {
          await this.sdk.trpc.salaryGrade.deleteCategory.mutate({ id: categoryId });
          this.showCategoryModal = false;
          if (this.salaryGradeFilter.categoryId === categoryId) {
            this.salaryGradeFilter.categoryId = "";
          }
          await this.loadSalaryGradeCategories();
          await this.loadSalaryGrades();
          this.notify("직종을 삭제했습니다");
        } catch (err) {
          showError(`삭제 실패: ${err instanceof Error ? err.message : String(err)}`);
        }
      };
      this.showConfirm = true;
    },

    // ── 직종 관리 modal ──
    openCategoryManager() {
      this.categoryManagerList = this.salaryGradeCategories.map(
        (c: { id: string; name: string }) => ({
          id: c.id,
          name: c.name,
          isNew: false,
          editing: false,
          editName: c.name,
        }),
      );
      this.categoryManagerNewName = "";
      this.categoryManagerDirty = false;
      this.showCategoryManagerModal = true;
    },

    categoryManagerAddItem() {
      const name = this.categoryManagerNewName.trim();
      if (!name) {
        return;
      }
      this.categoryManagerList.push({
        id: `new_${Date.now()}`,
        name,
        isNew: true,
        editing: false,
        editName: name,
      });
      this.categoryManagerNewName = "";
      this.categoryManagerDirty = true;
    },

    categoryManagerStartEdit(idx: number) {
      const item = this.categoryManagerList[idx];
      if (!item) {
        return;
      }
      item.editing = true;
      item.editName = item.name;
    },

    categoryManagerConfirmEdit(idx: number) {
      const item = this.categoryManagerList[idx];
      if (!item?.editName?.trim()) {
        return;
      }
      item.name = item.editName.trim();
      item.editing = false;
      this.categoryManagerDirty = true;
    },

    categoryManagerRemove(idx: number) {
      this.categoryManagerList.splice(idx, 1);
      this.categoryManagerDirty = true;
    },

    categoryManagerCancel() {
      if (this.categoryManagerDirty) {
        this.attemptClose("showCategoryManagerModal");
        return;
      }
      this.showCategoryManagerModal = false;
    },

    async categoryManagerSave() {
      try {
        const existing = new Set(this.salaryGradeCategories.map((c: { id: string }) => c.id));

        // Delete removed categories
        for (const c of this.salaryGradeCategories) {
          if (!this.categoryManagerList.find((item: { id: string }) => item.id === c.id)) {
            await this.sdk.trpc.salaryGrade.deleteCategory.mutate({ id: c.id });
            if (this.salaryGradeFilter.categoryId === c.id) {
              this.salaryGradeFilter.categoryId = "";
            }
          }
        }

        // Create new / update existing
        for (const item of this.categoryManagerList) {
          if (item.isNew) {
            await this.sdk.trpc.salaryGrade.createCategory.mutate({
              name: item.name,
            });
          } else if (existing.has(item.id)) {
            const orig = this.salaryGradeCategories.find(
              (c: { id: string; name: string }) => c.id === item.id,
            );
            if (orig && orig.name !== item.name) {
              await this.sdk.trpc.salaryGrade.updateCategory.mutate({
                id: item.id,
                name: item.name,
              });
            }
          }
        }

        this.showCategoryManagerModal = false;
        await this.loadSalaryGradeCategories();
        await this.loadSalaryGrades();
      } catch (err) {
        showError(`저장 실패: ${err instanceof Error ? err.message : String(err)}`);
      }
    },

    gradeCategory(cat: string): string {
      const keyMap: Record<string, string> = {
        rehabilitation: "admin.grade_cat_rehabilitation",
        functional: "admin.grade_cat_functional",
        nursing: "admin.grade_cat_nursing",
      };
      return keyMap[cat] ? this._t(keyMap[cat]) : cat;
    },

    get salaryGradeTotalPages(): number {
      return Math.ceil(this.salaryGrades.length / this.salaryGradePageSize) || 1;
    },

    get salaryGradesPage(): Record<string, unknown>[] {
      const start = (this.salaryGradePage - 1) * this.salaryGradePageSize;
      return this.salaryGrades.slice(start, start + this.salaryGradePageSize);
    },

    // --- Org edit ---
    /** 조직 설정 검색 적용 — 묶음 영역(el)의 행을 거르고 결과 없음 여부를 남긴다 */
    applyOrgSearch(el: HTMLElement, query: string) {
      const shown = applyOrgSettingsSearch(el, query);
      this.orgSearchEmpty = query.trim() !== "" && shown === 0;
      // 앵커 탭에 알린다 — 결과 없는 묶음 탭을 흐리게, 켜진 탭을 남은 첫 묶음으로(org-settings-anchors.ts)
      window.dispatchEvent(new CustomEvent(ORG_SETTINGS_FILTERED_EVENT));
    },

    /** 조직 설정 묶음 접기/펼치기 — 화면 안 상태(새로 고치면 다시 모두 펼침) */
    toggleOrgSection(section: string) {
      this.orgSectionCollapsed = {
        ...this.orgSectionCollapsed,
        [section]: !this.orgSectionCollapsed[section],
      };
    },

    startOrgEdit(section = "") {
      if (!this.orgInfo) {
        return;
      }
      this.orgEditSection = section;
      if (section && this.orgSectionCollapsed[section]) {
        this.orgSectionCollapsed = { ...this.orgSectionCollapsed, [section]: false };
      }
      const o = this.orgInfo;
      this.orgForm = {
        name: (o.name as string) || "",
        legalName: (o.legalName as string) || "",
        taxId: (o.taxId as string) || "",
        phone: (o.phone as string) || "",
        email: (o.email as string) || "",
        fax: (o.fax as string) || "",
        website: (o.website as string) || "",
        representativeName: (o.representativeName as string) || "",
        sealImage: (o.sealImage as string) || "",
        logo: (o.logo as string) || "",
      };
      // biome-ignore lint/suspicious/noExplicitAny: nested JSONB settings
      const s = this.orgSettings as any;
      this.orgSettingsForm = {
        payDay: s?.payroll?.payDay || 25,
        cutoffDay: s?.payroll?.cutoffDay || 21,
        sheetsAutoAddNewEmployees: s?.sheets?.autoAddNewEmployees === true,
        // 셀 예약 자동 적용(MED-825, D4) — 미설정 = confirm
        sheetsScheduledChangeApply: s?.sheets?.scheduledChangeApply === "auto" ? "auto" : "confirm",
        // 직원 앱 AI 어시스턴트(MED-800) — 켬/끔은 별도 프로시저(ai.updateEmployeeAppSettings)로 저장한다.
        // updateSettings 의 최상위 병합으로 settings.ai 를 통째로 보내면 플랫폼이 정한 한도·모델을 덮어쓴다.
        employeeAppAi: s?.ai?.modules?.employeeApp?.enabled === true,
        aiMasterEnabled: s?.ai?.enabled === true,
        weeklyLimit: s?.workHours?.weeklyLimit || 52,
        dailyStandard: s?.workHours?.dailyStandard || 8,
        // 주당 근무시간 초과 알림 — 기본 on(undefined=on)
        weeklyLimitAlert: s?.workHours?.weeklyLimitAlert !== false,
        attendanceMethod: (s?.attendance?.defaultMethod as "app" | "secom" | "manual") || "app",
        secomPunchTypesOnly: s?.attendance?.secomPunchTypesOnly === true,
        allowedIpsText: ((s?.attendance?.allowedIpAddresses as string[]) || []).join("\n"),
        defaultCheckIn: (s?.attendance?.defaultCheckIn as string) || ATTENDANCE_DEFAULT_CHECK_IN,
        defaultCheckOut: (s?.attendance?.defaultCheckOut as string) || ATTENDANCE_DEFAULT_CHECK_OUT,
        requestTimeStepMinutes: resolveRequestTimeStepMinutes(s),
        simplifiedOvertimeType: resolveSimplifiedOvertimeType(s),
        screenCaptureProtection: resolveScreenCaptureProtection(s),
        signName: (s?.sign?.name as string) || "",
        signEmail: (s?.sign?.email as string) || "",
        messageSenderName: (s?.messageSender?.name as string) || "",
        messageSenderEmail: (s?.messageSender?.email as string) || "",
        selfPasswordReset: !!s?.selfPasswordReset,
        twoFactorRequired: !!s?.security?.twoFactorRequired,
        pwFormat:
          (s?.passwordPolicy?.format as "numbers" | "lower_numbers" | "mixed" | "fixed") || "mixed",
        pwFixedValue: (s?.passwordPolicy?.fixedValue as string) || "",
        pwValidityDays: (s?.passwordPolicy?.temporaryValidityDays as number | undefined) ?? "",
        pwLength: (s?.passwordPolicy?.length as number) || 8,
        userPwMinLength: (s?.userPasswordPolicy?.minLength as number) || 8,
        userPwUpper: s?.userPasswordPolicy?.requireUppercase !== false,
        userPwLower: s?.userPasswordPolicy?.requireLowercase !== false,
        userPwNumber: s?.userPasswordPolicy?.requireNumber !== false,
        userPwSpecial: s?.userPasswordPolicy?.requireSpecial !== false,
        selfEditableFields: {
          phone: !!s?.selfEditableFields?.phone,
          address: s?.selfEditableFields?.address !== false,
          emergencyContact: s?.selfEditableFields?.emergencyContact !== false,
          bankAccount: !!s?.selfEditableFields?.bankAccount,
          profilePicture: s?.selfEditableFields?.profilePicture !== false,
        },
        sharePhone: s?.contactSharing?.phone === true,
        employeeHome: resolveEmployeeHomeLayout(s?.employeeHome),
        // 용어 표시 — 비우면 기본 낱말(역할)
        termRole: (s?.terminology?.role as string) || "",
        sheetContractBasis:
          (s?.sheetContractBasis as string) === "last_contract" ? "last_contract" : "prorate",
      };
      this.orgEditMode = true;
    },

    /* ── 결재선(MED-900 5단계) — LNB 「인사 관리 › 결재선」(설정 서브탭 approvalLines) ─────────────
       보기 = 정책(설정값) + 종류별 템플릿(approvalFlow.templates) 알약 요약. 편집 = 체크박스 · 역할 체크박스 ·
       종류별 「결재자 고르기」(공용 대상 선택기, 후보 = 조직 전체 범위 승인자). 저장은 정책을 updateSettings 로,
       템플릿은 바뀐 종류만 saveTemplate/clearTemplate 로 보낸다(조직 설정 저장과는 별개). */
    /** 결재선 페인의 보기 — 병원 기본·정책(네이티브) / 팀 기본 결재선(팀장 앱 iframe, 결재선 후속 ③) */
    approvalLinePane: "org" as "org" | "teams",
    get approvalLinePaneTabs(): { id: string; label: string }[] {
      return [
        { id: "org", label: "병원 기본 결재선" },
        { id: "teams", label: "팀별 결재선" },
      ];
    },
    /* 2026-10-08 개편 — 수정/저장 모드 없이 바로 저장: 직원 직접 지정 스위치 · 역할 고르기 모달 · 종류별 표의 칸 편집. */
    approvalLineSaving: false,
    approvalRolePicker: { open: false, query: "", selected: [] as string[] },
    approvalOrgHistory: {
      open: false,
      loading: false,
      rows: [] as { id: string; when: string; who: string; what: string; detail: string }[],
    },
    /** 지금 정책 — 직원 직접 지정 · 결재자 지정 가능 역할 · 종류별 「결재선 사용」(병원이 적어 둔 것만) */
    approvalPolicy(): {
      allowCustom: boolean;
      roleIds: string[];
      roleNames: string[];
      lineEnabled: Record<string, boolean>;
      reminderHours: number;
      visibility: ApprovalVisibility;
    } {
      const s = this.orgSettings as {
        approvalLine?: {
          allowCustom?: boolean;
          allowedRoleIds?: string[];
          lineEnabled?: unknown;
          reminderHours?: unknown;
          visibility?: unknown;
        };
      };
      const roleIds = Array.isArray(s?.approvalLine?.allowedRoleIds)
        ? (s.approvalLine?.allowedRoleIds as string[])
        : [];
      return {
        allowCustom: s?.approvalLine?.allowCustom !== false,
        roleIds,
        roleNames: roleIds.map((id) => this.roles.find((r) => r.id === id)?.name ?? "역할"),
        lineEnabled: parseApprovalLineEnabled(s?.approvalLine?.lineEnabled),
        reminderHours: resolveApprovalReminderHours(s?.approvalLine?.reminderHours),
        visibility: resolveApprovalVisibility(s?.approvalLine?.visibility),
      };
    },
    /** 결재 문서 열람 범위(MED-960) — 고르는 즉시 저장 */
    async saveApprovalVisibility(raw: string) {
      const next = resolveApprovalVisibility(raw);
      const policy = this.approvalPolicy();
      if (next === policy.visibility) {
        return;
      }
      await this.saveApprovalPolicy(
        { allowCustom: policy.allowCustom, allowedRoleIds: [...policy.roleIds], visibility: next },
        next === "line"
          ? "결재선에 든 결재자와 신청자만 결재 문서를 봐요."
          : "승인 권한 범위(팀 · 하위 팀)의 결재 문서를 모두 봐요.",
      );
    },
    /** 확인 안 된 결재 다시 알림 시간(MED-964) — 입력칸에서 벗어날 때 저장. 0 = 끔 */
    async saveApprovalReminderHours(raw: string | number) {
      const n = resolveApprovalReminderHours(Number(raw));
      const policy = this.approvalPolicy();
      if (n === policy.reminderHours) {
        return;
      }
      await this.saveApprovalPolicy(
        { allowCustom: policy.allowCustom, allowedRoleIds: [...policy.roleIds], reminderHours: n },
        n > 0
          ? `결재자가 ${n}시간 동안 확인하지 않으면 한 번 다시 알려요.`
          : "확인 안 된 결재를 다시 알리지 않아요.",
      );
    },
    /**
     * 결재 문서 종류 전부(core 카탈로그 순) + 이 병원의 「결재선 사용」. 끈 종류는 단계 없이 승인 권한자가 바로 처리하고
     * (예전 방식), 병원 · 팀 결재선 표에서 빠진다. 초기값은 카탈로그(지금 동작 그대로).
     */
    get approvalRequestTypes(): {
      id: string;
      label: string;
      enabled: boolean;
      defaultOn: boolean;
      fallbackLabel: string;
    }[] {
      const saved = this.approvalPolicy().lineEnabled;
      return APPROVAL_TYPE_CATALOG.map((t) => ({
        id: t.type,
        label: t.label,
        enabled: typeof saved[t.type] === "boolean" ? saved[t.type] : t.lineEnabledByDefault,
        defaultOn: t.lineEnabledByDefault,
        fallbackLabel: t.fallbackLabel,
      }));
    },
    /** 종류별 「결재선 사용」 스위치 — 끌 때는 영향(단계 없이 바로 처리 · 저장된 결재선은 두되 쓰지 않음)을 묻는다 */
    async toggleApprovalLineType(type: string, on: boolean) {
      const info = this.approvalRequestTypes.find((t) => t.id === type);
      if (!info) {
        return;
      }
      if (!on) {
        const ok = await askConfirm({
          message: `${info.label} 신청에 결재선을 쓰지 않을까요?\n${info.fallbackLabel}이 단계 없이 바로 승인 · 반려해요. 저장된 병원 · 팀 · 직원 결재선은 지우지 않고 쓰지 않아요. 이미 올린 문서는 바뀌지 않아요.`,
          confirmLabel: "결재선 안 씀",
        });
        if (!ok) {
          await this.loadOrgInfo();
          return;
        }
      }
      const policy = this.approvalPolicy();
      await this.saveApprovalPolicy(
        {
          allowCustom: policy.allowCustom,
          allowedRoleIds: [...policy.roleIds],
          lineEnabled: { ...policy.lineEnabled, [type]: on },
        },
        on
          ? `${info.label} 신청에 결재선을 써요. 다음 신청부터 적용돼요.`
          : `${info.label} 신청은 ${info.fallbackLabel}이 바로 처리해요.`,
      );
      if (this.approvalTemplatePicker.kind === type) {
        this.approvalTemplatePicker.kind = null;
      }
      // 팀별 결재선 표의 열이 달라진다
      this.teamLineOverview = null;
    },
    async saveApprovalPolicy(
      next: {
        allowCustom: boolean;
        allowedRoleIds: string[];
        lineEnabled?: Record<string, boolean>;
        reminderHours?: number;
        visibility?: ApprovalVisibility;
      },
      done: string,
    ) {
      this.approvalLineSaving = true;
      try {
        // updateSettings 는 approvalLine 을 통째로 바꾼다 — 안 넘긴 lineEnabled · reminderHours 는 지금 값을 유지
        await this.sdk.organization.updateSettings({
          approvalLine: {
            ...next,
            lineEnabled: next.lineEnabled ?? this.approvalPolicy().lineEnabled,
            reminderHours: next.reminderHours ?? this.approvalPolicy().reminderHours,
            visibility: next.visibility ?? this.approvalPolicy().visibility,
          },
        });
        this.notify(done, "success");
        await this.loadOrgInfo();
      } catch (err) {
        showError(
          `${this._t("admin.save_failed")}: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
      this.approvalLineSaving = false;
    },
    /** 스위치 — 끌 때는 직원 쪽 영향(나의 결재선을 쓰지 않음)을 묻는다 */
    async toggleApprovalAllowCustom(on: boolean) {
      const policy = this.approvalPolicy();
      if (!on) {
        const ok = await askConfirm({
          message:
            "직원이 결재자를 직접 고르지 못하게 할까요?\n신청에는 팀 결재선 · 병원 기본 결재선 · 자동(팀장 → 인사 관리자)만 붙고, 직원의 「나의 결재선」은 쓰지 않아요(지우지는 않아요).",
          confirmLabel: "직접 지정 불가 설정",
        });
        if (!ok) {
          await this.loadOrgInfo();
          return;
        }
      }
      await this.saveApprovalPolicy(
        { allowCustom: on, allowedRoleIds: [...policy.roleIds] },
        on ? "직원이 결재자를 직접 고를 수 있어요." : "직원은 결재자를 고를 수 없어요.",
      );
    },
    async openApprovalRolePicker() {
      if (this.roles.length === 0) {
        await this.loadRoles();
      }
      this.approvalRolePicker = {
        open: true,
        query: "",
        selected: [...this.approvalPolicy().roleIds],
      };
    },
    approvalRoleOptions(): { id: string; name: string }[] {
      const q = this.approvalRolePicker.query.trim().toLowerCase();
      return (this.roles as { id: string; name: string; isActive?: boolean }[])
        .filter((r) => r.isActive !== false && (!q || r.name.toLowerCase().includes(q)))
        .map((r) => ({ id: r.id, name: r.name }));
    },
    toggleApprovalRole(id: string) {
      const ids = this.approvalRolePicker.selected;
      this.approvalRolePicker.selected = ids.includes(id)
        ? ids.filter((x) => x !== id)
        : [...ids, id];
    },
    async saveApprovalRoles() {
      await this.saveApprovalPolicy(
        {
          allowCustom: this.approvalPolicy().allowCustom,
          allowedRoleIds: [...this.approvalRolePicker.selected],
        },
        this.approvalRolePicker.selected.length
          ? `결재자 지정 가능 역할을 ${this.approvalRolePicker.selected.length}개로 설정했습니다.`
          : "결재자 지정 가능 역할 제한을 해제했습니다.",
      );
      this.approvalRolePicker.open = false;
    },
    approvalTemplateCtx: null as ApprovalTemplateContext | null,
    approvalTemplatePicker: {
      kind: null as string | null,
      selected: [] as EntityPickerSelection[],
      /** 같은 결재선을 함께 넣을 다른 신청 종류 */
      alsoTypes: [] as string[],
      saving: false,
    },
    /** 템플릿·후보 읽기 — setting:update 가 없으면 서버가 거절하므로 null(카드는 정책만 보인다) */
    async loadApprovalTemplates() {
      try {
        this.approvalTemplateCtx = await this.sdk.approvalFlow.templates();
      } catch {
        this.approvalTemplateCtx = null;
      }
    },
    approvalTypeLabel(id: string | null): string {
      return this.approvalRequestTypes.find((t) => t.id === id)?.label ?? "";
    },
    /** 읽어 둔 템플릿을 편집 폼 모양(종류 → 결재자 id 배열)으로 */
    approvalTemplatesFromCtx(): Record<string, string[]> {
      const out: Record<string, string[]> = {};
      const templates = (this.approvalTemplateCtx?.templates ?? {}) as Record<
        string,
        { approverIds: string[] } | null
      >;
      for (const t of this.approvalRequestTypes) {
        out[t.id] = [...(templates[t.id]?.approverIds ?? [])];
      }
      return out;
    },
    /** 「김지현 팀장」 — 후보 목록에서, 없으면(권한이 바뀐 사람) 저장된 이름으로 */
    approvalCandidateName(type: string, id: string): string {
      const ctx = this.approvalTemplateCtx;
      const candidates =
        (
          ctx?.candidates as
            | Record<string, { employeeId: string; name: string; title: string | null }[]>
            | undefined
        )?.[type] ?? [];
      const c = candidates.find((x) => x.employeeId === id);
      if (c) {
        return c.title ? `${c.name} ${c.title}` : c.name;
      }
      const templates = (ctx?.templates ?? {}) as Record<
        string,
        { approvers: { employeeId: string; name: string; title: string | null }[] } | null
      >;
      const saved = templates[type]?.approvers.find((a) => a.employeeId === id);
      return saved ? (saved.title ? `${saved.name} ${saved.title}` : saved.name) : "결재자";
    },
    /** 병원 기본 결재선 표의 행 — 종류 · 「결재선 사용」 · 결재자 줄 · 단계 수 · 권한이 바뀐 분이 섞였는지 */
    approvalTemplateRows(): {
      id: string;
      label: string;
      enabled: boolean;
      defaultOn: boolean;
      fallbackLabel: string;
      line: string;
      steps: number;
      invalid: boolean;
    }[] {
      const templates = (this.approvalTemplateCtx?.templates ?? {}) as Record<
        string,
        {
          approvers: { employeeId: string; name: string; title: string | null; valid: boolean }[];
        } | null
      >;
      return this.approvalRequestTypes.map((t) => {
        const approvers = templates[t.id]?.approvers ?? [];
        return {
          ...t,
          line: approvers.map((a) => (a.title ? `${a.name} ${a.title}` : a.name)).join(" → "),
          steps: approvers.length,
          invalid: approvers.some((a) => !a.valid),
        };
      });
    },
    /** 대상 선택기 후보 — 지금 열린 종류의 조직 전체 범위 승인자(이름 · 팀 · 직책, 사번 없음) */
    get approvalTemplatePickerEmployees(): EntityPickerEmployee[] {
      const kind = this.approvalTemplatePicker.kind;
      const candidates =
        (
          this.approvalTemplateCtx?.candidates as
            | Record<
                string,
                { employeeId: string; name: string; teams: string[]; title: string | null }[]
              >
            | undefined
        )?.[kind ?? ""] ?? [];
      return candidates.map((c) => ({
        id: c.employeeId,
        name: c.name,
        employeeNumber: null,
        teamIds: [],
        teamNames: c.teams,
        jobDuty: c.title,
      }));
    },
    /** 줄 → 편집 팝오버(팀 관리 팝오버와 같은 자리). 같은 줄을 다시 누르면 닫는다 */
    openApprovalTemplatePicker(type: string) {
      if (this.approvalTemplatePicker.kind === type) {
        this.closeApprovalTemplatePicker();
        return;
      }
      // 「결재선 사용」을 끈 종류는 편집할 것이 없다(줄은 스위치만)
      if (!this.approvalRequestTypes.find((t) => t.id === type)?.enabled) {
        return;
      }
      this.syncTeamWardPopTop('[data-testid="approval-template-modal"]');
      window.dispatchEvent(
        new CustomEvent(FLOATING_PANEL_OPEN_EVENT, { detail: APPROVAL_TEMPLATE_PANEL_ID }),
      );
      const ids = this.approvalTemplatesFromCtx()[type] ?? [];
      this.approvalTemplatePicker = {
        kind: type,
        selected: ids.map((id) => ({
          kind: "employee" as const,
          id,
          name: this.approvalCandidateName(type, id),
        })),
        alsoTypes: [],
        saving: false,
      };
    },
    closeApprovalTemplatePicker() {
      if (!this.approvalTemplatePicker.saving) {
        this.approvalTemplatePicker.kind = null;
      }
    },
    toggleApprovalAlsoType(type: string) {
      const list = this.approvalTemplatePicker.alsoTypes;
      this.approvalTemplatePicker.alsoTypes = list.includes(type)
        ? list.filter((t) => t !== type)
        : [...list, type];
    },
    /** 이 종류에 병원 기본 결재선이 있는가 — 모달의 「지우기」 */
    approvalTemplateHas(type: string | null): boolean {
      return !!type && (this.approvalTemplatesFromCtx()[type] ?? []).length > 0;
    },
    /** 저장 — 고른 순서가 곧 단계. 편집 중인 종류 + 「다른 신청 종류에도」 고른 종류에 같은 결재선. 곳마다 서버가 후보를 검사한다 */
    async confirmApprovalTemplatePicker() {
      const kind = this.approvalTemplatePicker.kind;
      if (!kind) {
        return;
      }
      const ids = [
        ...new Set(
          this.approvalTemplatePicker.selected
            .filter((sel) => sel.kind === "employee")
            .map((sel) => sel.id),
        ),
      ];
      const types = [kind, ...this.approvalTemplatePicker.alsoTypes.filter((t) => t !== kind)];
      this.approvalTemplatePicker.saving = true;
      const failures: string[] = [];
      for (const t of types) {
        try {
          await this.sdk.approvalFlow.saveTemplate(t as ApprovalRequestType, ids);
        } catch (err) {
          failures.push(
            `${this.approvalTypeLabel(t)}: ${err instanceof Error ? err.message : "저장하지 못했어요."}`,
          );
        }
      }
      this.approvalTemplatePicker.saving = false;
      const ok = types.length - failures.length;
      if (ok > 0) {
        this.notify(
          ok === 1
            ? "병원 기본 결재선을 저장했어요."
            : `${ok}개 신청 종류에 병원 기본 결재선을 저장했어요.`,
          "success",
        );
        this.approvalTemplatePicker.kind = null;
      }
      if (failures.length > 0) {
        showAlert({ message: "일부 신청 종류는 저장하지 못했어요.", details: failures });
      }
      await this.loadApprovalTemplates();
    },
    /** 지우기 — 그 종류는 자동(팀장 → 인사 관리자). 팀 결재선이 있는 팀은 그대로 */
    async clearApprovalTemplate(type: string) {
      const ok = await askConfirm({
        message: `${this.approvalTypeLabel(type)} 병원 기본 결재선을 지울까요?\n팀 결재선이 없는 팀의 신청은 자동(팀장 → 인사 관리자)으로 붙어요. 이미 올린 문서는 바뀌지 않아요.`,
        confirmLabel: "지우기",
        tone: "danger",
      });
      if (!ok) {
        return;
      }
      try {
        await this.sdk.approvalFlow.clearTemplate(type as ApprovalRequestType);
        this.notify(`${this.approvalTypeLabel(type)} 병원 기본 결재선을 지웠어요.`, "success");
      } catch (err) {
        showError(err instanceof Error ? err.message : "결재선을 지우지 못했어요.");
      }
      this.approvalTemplatePicker.kind = null;
      await this.loadApprovalTemplates();
    },
    /** 병원 결재선 변경 이력 — 병원 기본 결재선 · 정책 사건만(팀 것은 팀별 결재선 탭의 이력) */
    async openApprovalOrgHistory() {
      this.approvalOrgHistory = { open: true, loading: true, rows: [] };
      const Event: Record<string, string> = {
        org_template_saved: "병원 기본 결재선 저장",
        org_template_cleared: "병원 기본 결재선 지움",
        org_policy_changed: "직원 직접 지정 · 지정 가능 역할",
      };
      try {
        const logs = (await this.sdk.approvalFlow.orgChangeLogs({ limit: 200 })) as {
          id: string;
          createdAt: string | Date;
          event: string;
          requestType: string | null;
          actorName: string | null;
          after: Record<string, unknown> | null;
        }[];
        const names = new Map<string, string>();
        const ctx = this.approvalTemplateCtx as {
          candidates?: Record<string, { employeeId: string; name: string }[]>;
        } | null;
        for (const list of Object.values(ctx?.candidates ?? {})) {
          for (const c of list) {
            names.set(c.employeeId, c.name);
          }
        }
        const store = Alpine.store("hereby");
        this.approvalOrgHistory.rows = logs
          .filter((l) => l.event in Event)
          .map((l) => {
            const ids = (l.after as { approverIds?: string[] } | null)?.approverIds;
            const after = l.after as { allowCustom?: boolean; allowedRoleIds?: string[] } | null;
            const detail = Array.isArray(ids)
              ? ids.map((id) => names.get(id) ?? "결재자").join(" → ")
              : l.event === "org_policy_changed" && after
                ? `직접 지정 ${after.allowCustom === false ? "막음" : "허용"} · 지정 가능 역할 ${after.allowedRoleIds?.length ? `${after.allowedRoleIds.length}개` : "제한 없음"}`
                : "";
            return {
              id: l.id,
              when: store.formatDateTime?.(l.createdAt) ?? String(l.createdAt),
              who: l.actorName ?? "시스템",
              what: `${l.requestType ? `${this.approvalTypeLabel(l.requestType)} ` : ""}${Event[l.event]}`,
              detail,
            };
          });
      } catch {
        this.approvalOrgHistory.rows = [];
      }
      this.approvalOrgHistory.loading = false;
    },

    /* ── 결재선 › 「팀별 결재선」(2026-10-08 — 팀장 앱 iframe 에서 관리자 네이티브로). 팀이 행, 신청 종류가 열인 표.
       칸을 누르면 팀 관리 「스케줄러 미리보기」와 같은 팝오버(.org-ward-pop 자리 · 페이지 헤더 바로 아래)에서 편집한다 — iframe 안에서는
       탭 바 위로 띄울 수 없어 옮겼다. 규칙·문구는 @hereby/core/constants team-approval-lines(팀장 앱 「결재선 설정」과 공용),
       서버는 approvalLine 라우터(teamOverview · teamContext · saveTeam*). */
    teamLineTeams: [] as { id: string; name: string }[],
    teamLineOverview: null as TeamOverviewView | null,
    teamLineLoading: false,
    teamLineError: "",
    teamLineQuery: "",
    teamLineFilter: "all" as OverviewFilter,
    /** 직원 직접 지정을 저장 중인 팀 */
    teamLineAllowBusy: "" as string,
    teamLineEditor: {
      open: false,
      teamId: "",
      type: "leave" as ApprovalLineType,
      ctx: null as TeamApprovalContextView | null,
      loading: false,
      saving: false,
      selected: [] as EntityPickerSelection[],
      alsoTypes: [] as ApprovalLineType[],
      alsoTeamIds: [] as string[],
      teamQuery: "",
      shareOpen: false,
      /** 팝오버 위쪽 자리(px) — 표가 시작하는 높이에 맞춘다(헤더 · 필터를 가리지 않게) */
      top: 16,
    },
    teamLineHistory: {
      open: false,
      teamId: "",
      loading: false,
      rows: [] as { id: string; when: string; who: string; what: string; detail: string }[],
    },

    async loadTeamLineTeams() {
      const teams = (await this.sdk.approvalFlow.managedTeams()) as { id: string; name: string }[];
      this.teamLineTeams = teams;
    },

    async loadTeamLineTab() {
      this.teamLineLoading = true;
      this.teamLineError = "";
      try {
        this.teamLineOverview = (await this.sdk.approvalFlow.teamOverview()) as TeamOverviewView;
        this.teamLineTeams = this.teamLineOverview.teams.map((t) => ({
          id: t.id,
          name: t.name,
        }));
      } catch (e) {
        this.teamLineError = (e as Error)?.message || "결재선 설정을 불러오지 못했어요.";
      } finally {
        this.teamLineLoading = false;
      }
    },

    get teamLineRows(): TeamOverviewRowView[] {
      return filterOverviewRows(
        this.teamLineOverview?.teams ?? [],
        this.teamLineQuery,
        this.teamLineFilter,
      );
    },

    /** 표의 열 — 병원이 「결재선 사용」을 켠 종류만(서버 teamOverview.types) */
    teamLineTypes(): { id: ApprovalLineType; label: string }[] {
      return overviewTypes(this.teamLineOverview);
    },

    teamLineTypeLabel(type: ApprovalLineType | null): string {
      return type ? APPROVAL_LINE_TYPE_LABEL[type] : "";
    },

    teamLineCell(row: TeamOverviewRowView, type: ApprovalLineType) {
      return overviewCellText(row.cells[type], this.teamLineOverview?.names ?? {});
    },

    teamLineAllowOptions(row: TeamOverviewRowView) {
      return overviewAllowOptions(row);
    },

    teamLineAllowTip(row: TeamOverviewRowView): string {
      return overviewAllowTip(row);
    },

    teamLineTeamLineAllowMode(row: TeamOverviewRowView): TeamLineAllowMode {
      return allowModeOf({ approvalAllowCustom: row.allowCustom });
    },

    /** 행의 「직원 직접 지정」 셀렉트 — 바로 저장. 병원이 막았으면 셀렉트가 꺼져 있다 */
    async setTeamLineAllow(row: TeamOverviewRowView, mode: TeamLineAllowMode) {
      if (mode === this.teamLineTeamLineAllowMode(row)) {
        return;
      }
      this.teamLineAllowBusy = row.id;
      try {
        await this.sdk.approvalFlow.saveTeamSettings(row.id, allowValueOf(mode));
        this.notify(
          `${row.name} — ${mode === "inherit" ? "상위 설정대로 적용해요." : mode === "on" ? "직원이 결재선을 고를 수 있어요." : "직원은 결재선을 고를 수 없어요."}`,
        );
      } catch (e) {
        showAlert({ message: (e as Error)?.message || "설정을 저장하지 못했어요." });
      } finally {
        this.teamLineAllowBusy = "";
      }
      await this.loadTeamLineTab();
    },

    /* ── 편집 모달 ── */
    teamLineEditorTeam(): TeamOverviewRowView | null {
      return this.teamLineOverview?.teams.find((t) => t.id === this.teamLineEditor.teamId) ?? null;
    },

    /** 모달 맨 위 「지금 적용 중」 — 출처 라벨 · 결재자 줄 · 이 팀이 정한 것인지 */
    teamLineEditorCurrent(): { source: string; line: string; own: boolean } | null {
      const row = this.teamLineEditorTeam();
      if (!row) {
        return null;
      }
      const c = this.teamLineCell(row, this.teamLineEditor.type);
      return {
        source: c.inherited ? c.source : "이 팀 결재선",
        line: c.line,
        own: !c.inherited,
      };
    },

    get teamLineEditorEmployees(): EntityPickerEmployee[] {
      return candidatesToPickerEmployees(this.teamLineEditor.ctx, this.teamLineEditor.type);
    },

    /** 「다른 팀에도」 목록 — 편집 중인 팀은 빼고, 검색어로 거른다 */
    teamLineShareTeams(): TeamOverviewRowView[] {
      const q = this.teamLineEditor.teamQuery.trim().toLowerCase();
      return (this.teamLineOverview?.teams ?? []).filter(
        (t) => t.id !== this.teamLineEditor.teamId && (!q || t.name.toLowerCase().includes(q)),
      );
    },

    teamLineEditorHasOwn(): boolean {
      const row = this.teamLineEditorTeam();
      return row?.cells[this.teamLineEditor.type]?.source === "own";
    },

    teamLineEditorTargetCount(): number {
      return applyTargets({
        teamId: this.teamLineEditor.teamId,
        type: this.teamLineEditor.type,
        alsoTypes: this.teamLineEditor.alsoTypes,
        alsoTeamIds: this.teamLineEditor.alsoTeamIds,
      }).length;
    },

    /** 칸 → 편집 팝오버. 같은 칸을 다시 누르면 닫고, 다른 칸이면 내용만 바꾼다 */
    async openTeamLineEditor(row: TeamOverviewRowView, type: ApprovalLineType) {
      const ed = this.teamLineEditor;
      if (ed.open && ed.teamId === row.id && ed.type === type) {
        this.closeTeamLineEditor();
        return;
      }
      if (ed.saving) {
        return;
      }
      // 자리는 팀 관리 팝오버와 같다 — 페이지 헤더 바로 아래 오른쪽(.org-ward-pop · syncTeamWardPopTop 이 헤더 아래 끝을 잰다)
      const top = 0;
      this.teamLineEditor = {
        open: true,
        teamId: row.id,
        type,
        ctx: null,
        loading: true,
        saving: false,
        selected: [],
        alsoTypes: [],
        alsoTeamIds: [],
        teamQuery: "",
        shareOpen: false,
        top,
      };
      this.syncTeamWardPopTop('[data-testid="settings-team-line-pop"]');
      window.dispatchEvent(
        new CustomEvent(FLOATING_PANEL_OPEN_EVENT, { detail: TEAM_LINE_PANEL_ID }),
      );
      try {
        const ctx = (await this.sdk.approvalFlow.teamContext(row.id)) as TeamApprovalContextView;
        if (this.teamLineEditor.teamId === row.id) {
          this.teamLineEditor.ctx = ctx;
          this.teamLineEditor.selected = pickerSelectionOf(ctx, type);
        }
      } catch (e) {
        this.teamLineEditor.open = false;
        showAlert({ message: (e as Error)?.message || "결재자 후보를 불러오지 못했어요." });
      } finally {
        this.teamLineEditor.loading = false;
      }
    },

    closeTeamLineEditor() {
      if (!this.teamLineEditor.saving) {
        this.teamLineEditor.open = false;
      }
    },

    toggleTeamLineAlsoType(type: ApprovalLineType) {
      const list = this.teamLineEditor.alsoTypes;
      this.teamLineEditor.alsoTypes = list.includes(type)
        ? list.filter((t) => t !== type)
        : [...list, type];
    },

    toggleTeamLineAlsoTeam(teamId: string) {
      const list = this.teamLineEditor.alsoTeamIds;
      this.teamLineEditor.alsoTeamIds = list.includes(teamId)
        ? list.filter((t) => t !== teamId)
        : [...list, teamId];
    },

    /** 저장 — 고른 사람으로(비웠으면 비우기) 편집 칸 + 함께 고른 종류 · 팀 전부. 곳마다 서버가 후보를 검사한다 */
    async saveTeamLineEditor(clear = false) {
      const ed = this.teamLineEditor;
      const ids = clear ? [] : approverIdsFromSelection(ed.selected);
      const targets = applyTargets({
        teamId: ed.teamId,
        type: ed.type,
        alsoTypes: ed.alsoTypes,
        alsoTeamIds: ed.alsoTeamIds,
      });
      if (clear) {
        const ok = await askConfirm({
          message: `${this.teamLineEditorTeam()?.name ?? "이 팀"}의 ${APPROVAL_LINE_TYPE_LABEL[ed.type]} 결재선을 지울까요?\n지우면 상위 팀 · 병원 기본 · 자동 순으로 적용돼요. 이미 올린 문서는 바뀌지 않아요.`,
          confirmLabel: "지우기",
          tone: "danger",
        });
        if (!ok) {
          return;
        }
      }
      ed.saving = true;
      const names = new Map((this.teamLineOverview?.teams ?? []).map((t) => [t.id, t.name]));
      const failures: string[] = [];
      let okCount = 0;
      for (const t of targets) {
        try {
          if (ids.length === 0) {
            await this.sdk.approvalFlow.clearTeamTemplate(t.teamId, t.type as ApprovalRequestType);
          } else {
            await this.sdk.approvalFlow.saveTeamTemplate(
              t.teamId,
              t.type as ApprovalRequestType,
              ids,
            );
          }
          okCount += 1;
        } catch (e) {
          failures.push(
            `${names.get(t.teamId) ?? "팀"} · ${APPROVAL_LINE_TYPE_LABEL[t.type]}: ${(e as Error)?.message || "저장하지 못했어요."}`,
          );
        }
      }
      ed.saving = false;
      if (okCount > 0) {
        this.notify(applySummary(okCount, failures.length, ids.length === 0));
        ed.open = false;
      }
      if (failures.length > 0) {
        showAlert({
          message: applySummary(okCount, failures.length, ids.length === 0),
          details: failures,
        });
      }
      await this.loadTeamLineTab();
    },

    /* ── 변경 이력 모달 ── */
    async openTeamLineHistory(teamId?: string) {
      this.teamLineHistory = {
        open: true,
        teamId: teamId || this.teamLineOverview?.teams[0]?.id || "",
        loading: false,
        rows: [],
      };
      await this.loadTeamLineHistory();
    },

    async loadTeamLineHistory() {
      const teamId = this.teamLineHistory.teamId;
      if (!teamId) {
        return;
      }
      this.teamLineHistory.loading = true;
      try {
        const logs = (await this.sdk.approvalFlow.teamChangeLogs(teamId, 100)) as ChangeLogView[];
        const names = new Map(Object.entries(this.teamLineOverview?.names ?? {}));
        const store = Alpine.store("hereby");
        const fmt = (d: string | Date) => store.formatDateTime?.(d) ?? String(d);
        this.teamLineHistory.rows = logs.map((l) => ({
          id: l.id,
          ...changeLogLine(l, names, fmt),
        }));
      } catch {
        this.teamLineHistory.rows = [];
      } finally {
        this.teamLineHistory.loading = false;
      }
    },

    teamLinePopListenersBound: false,
    /** 결재선 페인 탭 고르기 — 팀별 결재선은 처음 열 때 표를 읽는다 */
    async selectApprovalLinePane(id: "org" | "teams") {
      this.approvalLinePane = id;
      this.teamLineEditor.open = false;
      if (id === "teams" && !this.teamLineOverview) {
        await this.loadTeamLineTab();
      }
    },
    /** 한 번만 — ESC(공용 ESC 층) · 채팅/AI 열기 · 다른 탭 · 다른 메뉴에 편집 팝오버를 닫는다(팀 관리 팝오버와 같은 규칙) */
    bindTeamLinePopListeners() {
      if (this.teamLinePopListenersBound) {
        return;
      }
      this.teamLinePopListenersBound = true;
      for (const key of ["activeTab", "settingsSubTab", "approvalLinePane"]) {
        // biome-ignore lint/suspicious/noExplicitAny: Alpine magic property
        (this as any).$watch(key, () => {
          this.teamLineEditor.open = false;
        });
      }
      registerEscLayer(
        () =>
          this.teamLineEditor.open && !this.teamLineEditor.saving
            ? document.querySelector<HTMLElement>('[data-testid="settings-team-line-pop"]')
            : null,
        () => this.closeTeamLineEditor(),
      );
      window.addEventListener(FLOATING_PANEL_OPEN_EVENT, (event: Event) => {
        if ((event as CustomEvent).detail !== TEAM_LINE_PANEL_ID) {
          this.closeTeamLineEditor();
        }
      });
      window.addEventListener(AI_ASSISTANT_OPEN_EVENT, () => this.closeTeamLineEditor());
      // 병원 기본 결재선 편집 팝오버도 같은 규칙
      for (const key of ["activeTab", "settingsSubTab", "approvalLinePane"]) {
        // biome-ignore lint/suspicious/noExplicitAny: Alpine magic property
        (this as any).$watch(key, () => {
          this.approvalTemplatePicker.kind = null;
        });
      }
      registerEscLayer(
        () =>
          this.approvalTemplatePicker.kind && !this.approvalTemplatePicker.saving
            ? document.querySelector<HTMLElement>('[data-testid="approval-template-modal"]')
            : null,
        () => this.closeApprovalTemplatePicker(),
      );
      window.addEventListener(FLOATING_PANEL_OPEN_EVENT, (event: Event) => {
        if ((event as CustomEvent).detail !== APPROVAL_TEMPLATE_PANEL_ID) {
          this.closeApprovalTemplatePicker();
        }
      });
      window.addEventListener(AI_ASSISTANT_OPEN_EVENT, () => this.closeApprovalTemplatePicker());
    },

    /** 설정 카드 「기본 출퇴근 기준시간」 — 저장값이 없으면 core 기본값(09:00 ~ 18:00). */
    attendanceDefaultTimesLabel(): string {
      const attendance = this.orgSettings?.attendance as
        | { defaultCheckIn?: string; defaultCheckOut?: string }
        | undefined;
      return `${attendance?.defaultCheckIn || ATTENDANCE_DEFAULT_CHECK_IN} ~ ${attendance?.defaultCheckOut || ATTENDANCE_DEFAULT_CHECK_OUT}`;
    },

    /** 보기 모드용 — 저장값(부분)을 해석한 직원 앱 첫화면 레이아웃. */
    employeeHomeLayoutView(): EmployeeHomeLayout {
      return resolveEmployeeHomeLayout(
        this.orgSettings?.employeeHome as EmployeeHomeLayoutConfig | undefined,
      );
    },

    employeeHomeSectionLabel(section: EmployeeHomeSection): string {
      return EMPLOYEE_HOME_SECTION_LABELS[section] ?? section;
    },

    /** 카드 순서 편집 — i번째 섹션을 위(-1)/아래(+1)로 한 칸. 끝에서는 아무것도 안 한다. */
    moveEmployeeHomeSection(index: number, delta: -1 | 1) {
      const order = this.orgSettingsForm.employeeHome.sectionOrder;
      const target = index + delta;
      if (index < 0 || index >= order.length || target < 0 || target >= order.length) {
        return;
      }
      const next = [...order];
      [next[index], next[target]] = [next[target], next[index]];
      this.orgSettingsForm.employeeHome.sectionOrder = next;
    },

    async saveOrgInfo() {
      // 기본 출퇴근 기준시간 — 서버 스키마도 막지만 그 오류는 검증 결과 원문(JSON)으로 떠서, 저장 전에 한국어로 먼저 알린다
      const { defaultCheckIn, defaultCheckOut } = this.orgSettingsForm;
      if (defaultCheckIn && defaultCheckIn === defaultCheckOut) {
        showAlert("기본 출근·퇴근 시각이 같아요. 서로 다르게 입력해 주세요.");
        return;
      }
      this.orgSaving = true;
      try {
        const orgId = this.orgInfo?.id as string;
        // 빈 값의 뜻이 필드마다 다르다. 대부분은 "안 건드림"(undefined → 전송에서 빠짐)이지만,
        // 지울 수 있는 이미지 두 개는 "지움"이므로 null 로 보내야 서버가 덮어쓴다.
        // undefined 로 보내면 JSON 에서 키째 빠져 라우터의 Object.assign 이 옛 값을 남긴다
        // — 로고를 지우고 저장해도 그대로 남던 원인이다(2026-08).
        const clearableFields = new Set(["logo", "sealImage"]);
        const cleanForm: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(this.orgForm)) {
          if (v !== "") {
            cleanForm[k] = v;
          } else if (clearableFields.has(k)) {
            cleanForm[k] = null;
          }
        }
        await this.sdk.organization.update(orgId, cleanForm);
        const allowedIps = (this.orgSettingsForm.allowedIpsText as string)
          .split("\n")
          .map((line: string) => line.trim())
          .filter((line: string) => line.length > 0);
        await this.sdk.organization.updateSettings({
          payroll: {
            payDay: this.orgSettingsForm.payDay,
            cutoffDay: this.orgSettingsForm.cutoffDay,
          },
          // 시트 — 신규 입사자 자동 추가(직원 기준 수동 시트에 행 자동 생성)
          sheets: {
            autoAddNewEmployees: this.orgSettingsForm.sheetsAutoAddNewEmployees === true,
            scheduledChangeApply:
              this.orgSettingsForm.sheetsScheduledChangeApply === "auto" ? "auto" : "confirm",
          },
          workHours: {
            weeklyLimit: this.orgSettingsForm.weeklyLimit,
            dailyStandard: this.orgSettingsForm.dailyStandard,
            weeklyLimitAlert: this.orgSettingsForm.weeklyLimitAlert,
          },
          attendance: {
            defaultMethod: this.orgSettingsForm.attendanceMethod,
            allowedIpAddresses: allowedIps,
            // 세콤 출근·퇴근 버튼 태그만 근태로(MED-970) — 끄면 모든 태그를 출퇴근으로 본다
            secomPunchTypesOnly: this.orgSettingsForm.secomPunchTypesOnly === true,
            // 근무 배정이 없는 직원의 출퇴근 기준 시각 — 비우면 서버가 09:00–18:00 으로 본다
            defaultCheckIn: this.orgSettingsForm.defaultCheckIn || undefined,
            defaultCheckOut: this.orgSettingsForm.defaultCheckOut || undefined,
          },
          // 신청 시간 단위 — 직원 앱 시각 선택기 간격(15/30/60분). 근태 보정(출퇴근 기록 수정)은 영향 없음
          requests: {
            timeStepMinutes: this.orgSettingsForm.requestTimeStepMinutes,
            // 연장 유형 단순화(MED-965) — 직원은 「시간외 근무」 하나로 신청, 분류는 서버가
            simplifiedOvertimeType: this.orgSettingsForm.simplifiedOvertimeType === true,
          },
          // 직원 앱 화면 캡처 제한 — 끄면 암전·녹화 가림·스크린샷 안내가 모두 빠진다
          screenCaptureProtection: this.orgSettingsForm.screenCaptureProtection !== false,
          // 전자서명 발신 기본값 — 빈 값은 미설정(API-account holder 폴백)
          sign: {
            name: (this.orgSettingsForm.signName as string).trim() || undefined,
            email: (this.orgSettingsForm.signEmail as string).trim() || undefined,
          },
          messageSender: {
            name: (this.orgSettingsForm.messageSenderName as string).trim() || undefined,
            email: (this.orgSettingsForm.messageSenderEmail as string).trim() || undefined,
          },
          selfPasswordReset: this.orgSettingsForm.selfPasswordReset,
          // 2단계 인증 필수(2026-10-13) — 로그인 응답 requires2FA 와 security.overview 가 읽는다
          security: { twoFactorRequired: !!this.orgSettingsForm.twoFactorRequired },
          // 비밀번호 정책 — 임시 비밀번호 형식·길이. 길이는 스키마 경계(4~20)로 클램프.
          passwordPolicy: {
            format: this.orgSettingsForm.pwFormat,
            length: Math.min(20, Math.max(4, Number(this.orgSettingsForm.pwLength) || 8)),
            // 고정값은 "fixed" 일 때만 보낸다 — 다른 형식으로 바꾸면 옛 고정값이 남지 않게.
            ...(this.orgSettingsForm.pwFormat === "fixed"
              ? { fixedValue: String(this.orgSettingsForm.pwFixedValue || "").trim() }
              : {}),
            // 유효기간은 1~90일만 보낸다 — 비우면 키 자체를 빼 기한 없음으로 저장(MED-922)
            ...(this.pwValidityDaysPayload() !== null
              ? { temporaryValidityDays: this.pwValidityDaysPayload() as number }
              : {}),
          },
          // 일반 비밀번호 정책 — 비밀번호 변경 시 서버 검증 규칙.
          userPasswordPolicy: {
            minLength: Math.min(20, Math.max(4, Number(this.orgSettingsForm.userPwMinLength) || 8)),
            requireUppercase: this.orgSettingsForm.userPwUpper,
            requireLowercase: this.orgSettingsForm.userPwLower,
            requireNumber: this.orgSettingsForm.userPwNumber,
            requireSpecial: this.orgSettingsForm.userPwSpecial,
          },
          selfEditableFields: this.orgSettingsForm.selfEditableFields,
          // 메신저 연락처 프로필의 전화번호 공개(병원 일괄)
          contactSharing: { phone: this.orgSettingsForm.sharePhone === true },
          // 직원 앱 첫화면 레이아웃 — 건수는 스키마 경계(1~10)로 클램프.
          employeeHome: {
            ...this.orgSettingsForm.employeeHome,
            noticeLimit: Math.min(
              EMPLOYEE_HOME_NOTICE_LIMIT_MAX,
              Math.max(
                EMPLOYEE_HOME_NOTICE_LIMIT_MIN,
                Math.trunc(Number(this.orgSettingsForm.employeeHome.noticeLimit)) ||
                  EMPLOYEE_HOME_NOTICE_LIMIT_DEFAULT,
              ),
            ),
            // 확인한 글을 남기는 일수(0~30) — 0 은 유효한 값(확인 즉시 빠짐)이라 `||` 로 기본값을 채우지 않는다.
            noticeKeepDays: (() => {
              const raw = Number(this.orgSettingsForm.employeeHome.noticeKeepDays);
              const n = Number.isFinite(raw)
                ? Math.trunc(raw)
                : EMPLOYEE_HOME_NOTICE_KEEP_DAYS_DEFAULT;
              return Math.min(
                EMPLOYEE_HOME_NOTICE_KEEP_DAYS_MAX,
                Math.max(EMPLOYEE_HOME_NOTICE_KEEP_DAYS_MIN, n),
              );
            })(),
          },
          // 용어 표시 — 명시한 낱말만 저장. 비우면 {} 가 최상위 병합으로 옛 낱말을 지운다(기본으로 복귀).
          terminology: compactTerminology({ role: this.orgSettingsForm.termRole }) ?? {},
          // 근로계약 기준 — 시트 식별 열이 한 달에 둘인 계약을 합치는 방식(MED-703)
          sheetContractBasis: this.orgSettingsForm.sheetContractBasis,
        });
        // 직원 앱 AI 토글은 settings.ai 블록 안이라 별도 프로시저로 — 바뀌었을 때만 부른다(마스터 꺼짐이면 서버가 거절)
        // biome-ignore lint/suspicious/noExplicitAny: nested JSONB settings
        const savedAi = (this.orgSettings as any)?.ai?.modules?.employeeApp?.enabled === true;
        if (this.orgSettingsForm.employeeAppAi !== savedAi) {
          await this.sdk.ai.updateEmployeeAppSettings({
            enabled: this.orgSettingsForm.employeeAppAi === true,
          });
        }
        this.orgEditMode = false;
        await this.loadOrgInfo();
      } catch (err) {
        showError(
          `${this._t("admin.save_failed")}: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
      this.orgSaving = false;
    },

    async saveSelfEditableFields() {
      try {
        await this.sdk.organization.updateSettings({
          selfEditableFields: { ...this.orgSettingsForm.selfEditableFields },
        });
        await this.loadOrgInfo();
      } catch (err) {
        showError(`저장 실패: ${err instanceof Error ? err.message : String(err)}`);
      }
    },

    async handleSealImageUpload(event: Event) {
      const input = event.target as HTMLInputElement;
      const file = input?.files?.[0];
      if (!file) {
        return;
      }
      if (!file.type.startsWith("image/")) {
        showAlert(this._t("admin.image_only"));
        return;
      }
      if (file.size > 500 * 1024) {
        showAlert(this._t("admin.file_size_500kb"));
        return;
      }
      const dataUrl = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(file);
      });
      this.orgForm.sealImage = dataUrl;
    },

    /** 병원 로고 업로드 — 직인과 동일 정책(이미지, 500KB, data URI 저장). LNB 코브랜딩에 표시. */
    async handleLogoImageUpload(event: Event) {
      const input = event.target as HTMLInputElement;
      const file = input?.files?.[0];
      if (!file) {
        return;
      }
      if (!file.type.startsWith("image/")) {
        showAlert(this._t("admin.image_only"));
        return;
      }
      if (file.size > 500 * 1024) {
        showAlert(this._t("admin.file_size_500kb"));
        return;
      }
      const dataUrl = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(file);
      });
      this.orgForm.logo = dataUrl;
    },

    // --- Item code CRUD ---
    openAddItemCode(type: "allowance" | "deduction") {
      this.itemCodeType = type;
      this.editingItemCodeId = "";
      this.itemCodeForm = { code: "", name: "", displayOrder: 0 };
      this.showItemCodeModal = true;
    },

    openEditItemCode(type: "allowance" | "deduction", item: Record<string, unknown>) {
      this.itemCodeType = type;
      this.editingItemCodeId = item.id as string;
      this.itemCodeForm = {
        code: (item.code as string) || "",
        name: (item.name as string) || "",
        displayOrder: (item.displayOrder as number) || 0,
      };
      this.showItemCodeModal = true;
    },

    async saveItemCode() {
      if (!(this.itemCodeForm.code && this.itemCodeForm.name)) {
        return;
      }
      this.itemCodeSaving = true;
      try {
        const isAllowance = this.itemCodeType === "allowance";
        if (this.editingItemCodeId) {
          const payload = { id: this.editingItemCodeId, ...this.itemCodeForm };
          if (isAllowance) {
            await this.sdk.itemCode.updateAllowanceCode(payload);
          } else {
            await this.sdk.itemCode.updateDeductionCode(payload);
          }
        } else if (isAllowance) {
          await this.sdk.itemCode.createAllowanceCode(this.itemCodeForm);
        } else {
          await this.sdk.itemCode.createDeductionCode(this.itemCodeForm);
        }
        const wasEditingItemCode = !!this.editingItemCodeId;
        this.showItemCodeModal = false;
        await this.loadItemCodes();
        this.notify(wasEditingItemCode ? "항목 코드를 수정했습니다" : "항목 코드를 추가했습니다");
      } catch (err) {
        showError(
          `${this._t("admin.save_failed")}: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
      this.itemCodeSaving = false;
    },

    async deleteItemCode(type: "allowance" | "deduction", id: string) {
      this.confirmMessage = this._t("admin.confirm_delete_item_code");
      this.confirmAction = async () => {
        try {
          if (type === "allowance") {
            await this.sdk.itemCode.deleteAllowanceCode(id);
          } else {
            await this.sdk.itemCode.deleteDeductionCode(id);
          }
          await this.loadItemCodes();
          this.notify("항목 코드를 삭제했습니다");
        } catch (err) {
          showError(
            `${this._t("admin.delete_failed")}: ${err instanceof Error ? err.message : String(err)}`,
          );
        }
      };
      this.showConfirm = true;
    },

    // --- Salary grade CRUD ---
    openAddGrade() {
      this.editingGradeId = "";
      this.gradeForm = {
        categoryId: this.salaryGradeFilter.categoryId || this.salaryGradeCategories[0]?.id || "",
        grade: 1,
        baseSalary: 0,
        hobongAmount: 0,
        totalSalary: 0,
        annualSalary: 0,
        effectiveYear: this.salaryGradeFilter.year || new Date().getFullYear(),
        isActive: true,
        notes: "",
      };
      this.showGradeModal = true;
    },

    openEditGrade(g: Record<string, unknown>) {
      this.editingGradeId = g.id as string;
      this.gradeForm = {
        categoryId: (g.categoryId as string) || "",
        grade: (g.grade as number) || 1,
        baseSalary: Number(g.baseSalary) || 0,
        hobongAmount: Number(g.hobongAmount) || 0,
        totalSalary: Number(g.totalSalary) || 0,
        annualSalary: Number(g.annualSalary) || 0,
        effectiveYear: (g.effectiveYear as number) || new Date().getFullYear(),
        isActive: (g.status as string) === "active",
        notes: (g.notes as string) || "",
      };
      this.showGradeModal = true;
    },

    async saveGrade() {
      // Auto-calculate derived fields (annualSalary is stored directly, not recalculated)
      const baseSalary = Math.floor(this.gradeForm.baseSalary || 0);
      const hobongAmount = Math.floor(this.gradeForm.hobongAmount || 0);
      const totalSalary = baseSalary + hobongAmount;
      const annualSalary = Math.floor(this.gradeForm.annualSalary || totalSalary * 12);
      const minimumWage = Math.round(totalSalary / 209);
      const status = this.gradeForm.isActive ? "active" : "inactive";

      this.gradeForm.baseSalary = baseSalary;
      this.gradeForm.hobongAmount = hobongAmount;
      this.gradeForm.totalSalary = totalSalary;

      this.gradeSaving = true;
      try {
        if (this.editingGradeId) {
          await this.sdk.salaryGrade.update({
            id: this.editingGradeId,
            baseSalary,
            hobongAmount,
            totalSalary,
            annualSalary,
            minimumWage,
            monthlyHours: 209,
            notes: this.gradeForm.notes || undefined,
            status,
          });
        } else {
          await this.sdk.salaryGrade.create({
            categoryId: this.gradeForm.categoryId,
            grade: this.gradeForm.grade,
            baseSalary,
            hobongAmount,
            totalSalary,
            annualSalary,
            minimumWage,
            monthlyHours: 209,
            effectiveYear: this.gradeForm.effectiveYear,
            notes: this.gradeForm.notes || undefined,
          });
        }
        const wasEditingGrade = !!this.editingGradeId;
        this.showGradeModal = false;
        await this.loadSalaryGrades();
        this.notify(wasEditingGrade ? "호봉을 수정했습니다" : "호봉을 추가했습니다");
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        if (message.includes("DUPLICATE_SALARY_GRADE") || message.includes("23505")) {
          showAlert(this._t("admin.duplicate_grade"));
        } else {
          showError(`${this._t("admin.save_failed")}: ${message}`);
        }
      }
      this.gradeSaving = false;
    },

    async deleteGrade(id: string) {
      this.confirmMessage = this._t("admin.confirm_delete_grade");
      this.confirmAction = async () => {
        try {
          await this.sdk.salaryGrade.delete(id);
          await this.loadSalaryGrades();
          this.notify("호봉을 삭제했습니다");
        } catch (err) {
          showError(
            `${this._t("admin.delete_failed")}: ${err instanceof Error ? err.message : String(err)}`,
          );
        }
      };
      this.showConfirm = true;
    },

    /* ──────────── helpers ──────────── */

    /* Registry of LNB iframe targets (external apps).
       dev·prod 모두 {origin}{prodPrefix}{path} — 같은 출처다. dev 포트는
       admin dev 서버의 프록시가 감추므로 여기에 없다 (src/embed-ports.ts). */
    analyticsEmbedModule(key: string): string | null {
      const modules: Record<string, string> = {
        "payroll-home": "hr_system",
        sheets: "hr_system",
        "ga-dashboard": "general_affairs",
        "ga-training": "general_affairs",
        "ga-tasks": "general_affairs",
        "ga-notices": "general_affairs",
        "mgmt-dashboard": "management_dashboard",
        drive: "drive",
        "doc-mgmt": "team_manager",
        "overtime-status": "team_manager",
        "overtime-approval": "team_manager",
        leave: "team_manager",
        "offsite-approval": "team_manager",
        incentive: "team_manager",
        "attendance-dashboard": "team_manager",
        "attendance-work": "team_manager",
        "attendance-overtime": "team_manager",
      };
      return modules[key] ?? null;
    },
    embedRegistry: {
      "doc-mgmt": {
        path: "/document-management",
        prodPrefix: "/team-manager",
        title: "문서 결재",
        icon: "ic-clipboard",
      },
      /* team-manager /work 통합 화면을 mode=status|approval 파라미터로
         연장근무 현황/결재 두 메뉴로 분리해 embed (기획서 §3, LNB 개편 시안). */
      "overtime-status": {
        path: "/work?mode=status",
        prodPrefix: "/team-manager",
        title: "연장근무 현황",
        icon: "ic-chart-bar",
      },
      "overtime-approval": {
        path: "/work?mode=approval",
        prodPrefix: "/team-manager",
        title: "연장근무 결재",
        icon: "ic-clipboard-check",
      },
      leave: {
        path: "/leave",
        prodPrefix: "/team-manager",
        title: "연차/휴가 결재",
        icon: "ic-clipboard-check",
      },
      /* 팀 기본 결재선(결재선 후속 ③) — 인사 관리 › 결재선 › 「팀 기본 결재선」 탭이 팀장 앱 「결재선 설정」을
         그대로 embed. 시스템 관리자는 팀 고르기에 모든 팀이 뜬다. */
      "approval-lines": {
        path: "/approval-lines",
        prodPrefix: "/team-manager",
        title: "팀 기본 결재선",
        icon: "ic-tree",
      },
      /* 외근 결재 — 외근 관리 페이지의 결재 서브탭에서 iframe 으로 사용 (기획서 §2.8).
         onlyOffsite=1: team-manager 통합 근무 화면에서 연장(+휴일) 구분 탭/목록을 숨겨
         외근 결재만 노출 (noOffsite 의 대칭 — 연장 테이블 중복 노출 방지). */
      "offsite-approval": {
        path: "/offsite?mode=approval&onlyOffsite=1",
        prodPrefix: "/team-manager",
        title: "외근 결재",
        icon: "ic-clipboard",
      },
      "payroll-home": {
        path: "/",
        prodPrefix: "/hr-system",
        title: "급여대장",
        icon: "ic-money",
      },
      // 연말정산(/year-end-settlement)·퇴직정산(/resignation-settlement)·
      // 호봉 관리(/base-salary)·호봉 승급(/grade-promotion)·고정 인센티브
      // 프리셋(/incentive-preset) embed는 hr-system 단독 페이지 제거(91d6c758)
      // 후 시트 기반으로 이전되어 정리했다.
      incentive: {
        path: "/treatment",
        prodPrefix: "/team-manager",
        title: "인센티브",
        icon: "ic-money",
      },
      sheets: {
        path: "/sheets",
        prodPrefix: "/hr-system",
        title: "시트",
        icon: "ic-table",
      },
      "attendance-dashboard": {
        path: "/",
        prodPrefix: "/team-manager",
        title: "근태 현황",
        icon: "ic-clock",
      },
      /* 근태 현황 > 근무 현황 탭 — team-manager 홈(overview)에서 출퇴근 현황 테이블만(att=1). */
      "attendance-work": {
        path: "/?att=1",
        prodPrefix: "/team-manager",
        title: "근무 현황",
        icon: "ic-clock",
      },
      /* 근태 현황 > 연장근무 현황 탭 — team-manager /work에서 외근 서브탭 제거(noOffsite=1). */
      "attendance-overtime": {
        path: "/work?noOffsite=1",
        prodPrefix: "/team-manager",
        title: "연장근무 현황",
        icon: "ic-clock-plus",
      },
      /* 총무(general-affairs) 병합 — 앱의 탭 라우트(/, /training, …)를 메뉴별로 embed. */
      "ga-dashboard": {
        path: "/",
        prodPrefix: "/general-affairs",
        title: "대시보드",
        icon: "ic-gauge",
      },
      "ga-training": {
        path: "/training",
        prodPrefix: "/general-affairs",
        title: "총무",
        icon: "ic-cap",
      },
      "ga-tasks": {
        path: "/tasks",
        prodPrefix: "/general-affairs",
        title: "관리",
        icon: "ic-list-check",
      },
      "ga-notices": {
        path: "/notices",
        prodPrefix: "/general-affairs",
        title: "공지",
        icon: "ic-bell",
      },
      // 총무 '설정'(앱 푸시 알림)은 admin 설정 > 기본정보 상단으로 이관됨 → embed 라우트도 비활성화
      //   (LNB 메뉴 embed:ga-settings 도 함께 숨김). 필요 시 되살리면 복구됨.
      // "ga-settings": {
      //   devPort: 4005,
      //   path: "/settings",
      //   prodPrefix: "/general-affairs",
      //   title: "설정",
      //   icon: "ic-setting",
      // },
      /* 경영 대시보드(management-dashboard) 병합 — 단일 페이지. */
      "mgmt-dashboard": {
        path: "/",
        prodPrefix: "/management-dashboard",
        title: "경영 대시보드",
        icon: "ic-chart-up",
      },
      /* 드라이브(drive) 병합 — 조직 공유 폴더·파일 보관함, 단일 페이지. */
      drive: {
        path: "/",
        prodPrefix: "/drive",
        title: "드라이브",
        icon: "ic-folder",
      },
    } as Record<string, { path: string; prodPrefix: string; title: string; icon: string }>,

    /** embedUrlFor 가 그 임베드 주소에 덧붙일 깊은 경로(openSheetInEmbed 가 쓴다) */
    embedSubPath: "",
    embedSubPathKey: "",

    /** 시트 임베드를 특정 시트로 연다 — hr-system 의 공유 링크 경로(/sheets/:id)를 그대로 쓴다. */
    openSheetInEmbed(sheetViewId: string): void {
      this.handleLnbSelect("embed:sheets");
      this.embedSubPath = `/${encodeURIComponent(sheetViewId)}`;
      this.embedSubPathKey = "sheets";
    },

    /** 「예약된 변경」을 한 건(알림)·직원·시트로 좁혀 연다. 페이지 컴포넌트가 FOCUS 이벤트를 받는다. */
    openScheduledChanges(scope: {
      id?: string;
      employeeId?: string;
      sheetSlug?: string;
      label?: string;
    }): void {
      this.switchTab("scheduled-changes");
      window.dispatchEvent(new CustomEvent(SCHEDULED_CHANGES_FOCUS_EVENT, { detail: scope }));
    },

    embedUrlFor(key: string): string {
      const base = this.embedRegistry[key];
      // 다른 화면이 연 깊은 경로(예: 시트 /sheets/<id>) — 그 임베드에만 붙인다. hr-system 이 경로로 시트를 연다.
      const cfg =
        base && this.embedSubPath && key === this.embedSubPathKey
          ? { ...base, path: `${base.path}${this.embedSubPath}` }
          : base;
      /* dev·prod 모두 같은 출처의 경로다. dev 에서는 admin dev 서버가
         `/hr-system/*` 등을 각 앱으로 프록시한다(vite.config 의 embedProxy,
         포트는 src/embed-ports.ts). 그래서 여기서 호스트·포트를 조립할 일이
         없고, LAN IP 로 접속해도 같은 주소가 나온다. */
      return embedUrl(cfg, {
        origin: window.location.origin,
        selectedTeamId: this.selectedTeamId,
        reloadKey: this.embedReloadKey,
        forceDemo: sessionStorage.getItem("hereby_demo") === "1",
      });
    },

    embedTitleFor(key: string): string {
      return this.embedRegistry[key]?.title || "";
    },

    embedIconFor(key: string): string {
      return this.embedRegistry[key]?.icon || "";
    },

    /* True when the current embed is a team-manager iframe (so the PageHeader
       should expose the team filter dropdown). hr-system iframes don't honor
       the admin team filter and shouldn't display the control. */
    embedShowsTeamFilter(key: string): boolean {
      const cfg = this.embedRegistry[key];
      return !!cfg && cfg.prodPrefix === "/team-manager";
    },

    /* Page-header [새로고침] handler for native tabs (dashboard.html header).
       Reloads the tab's native data via loadData(), AND — on 근태 현황
       (attendance) — force-reloads the embedded team-manager iframes
       (근무 현황 / 연장근무 현황) by bumping embedReloadKey. Those iframes'
       :src reads embedReloadKey (embedUrlFor → `_=N`), so loadData() alone
       never refreshed them: newly-registered overtime only appeared after
       switching tabs (which unloads/reloads the iframe). Bumping the key
       makes the button reload them in place. */
    refreshActiveView(): void {
      this.loadData();
      if (this.activeTab !== "attendance") {
        return;
      }
      // 근태 현황 서브탭별 갱신. 근무/연장근무 현황은 team-manager iframe이라
      // embedReloadKey를 올려 :src를 바꿔 리로드하지만, 연차·외근은 admin 네이티브
      // 패널이라 iframe reloadKey로는 갱신되지 않는다 → 각자의 로더를 재호출한다.
      if (this.attendanceTab === "work" || this.attendanceTab === "overtime") {
        this.embedReloadKey += 1;
      } else if (this.attendanceTab === "leave") {
        // 현재 연차 서브탭(현황/부여 기준/휴가 유형/연차·휴가 결재) 재로드
        this.switchLeaveTab(this.leaveSubTab);
      } else if (this.attendanceTab === "offsite") {
        this.loadOffsiteRecords();
        this.loadOffsiteTypes();
      } else if (this.attendanceTab === "closing") {
        // 근로시간 마감 — 현재 서브탭(마감 내역/개인별 마감 상태) 재로드
        this.refreshWorkTimeClosingView();
      } else if (this.attendanceTab === "correction") {
        this.loadCorrections();
      }
    },

    /* Refresh the iframe in the embed tab (bumps the reload key, which is
       appended to the iframe URL so Alpine sees a new src and forces reload). */
    refreshCurrentEmbed(): void {
      // Re-entrancy guard. The previous reload hasn't finished yet —
      // adding another src bump now would tear down the still-loading
      // iframe and start a third instance, multiplying the race against
      // refresh-token rotation. The iframe's `@load` clears the flag.
      if (this.embedRefreshing) {
        return;
      }
      this.embedRefreshing = true;
      this.embedReloadKey += 1;
      // Safety: if the iframe fails to fire `@load` (network error,
      // navigation away mid-load), don't strand the refresh button
      // disabled forever. 10s is long enough that any healthy reload
      // has finished and short enough that the user can recover.
      window.setTimeout(() => {
        this.embedRefreshing = false;
      }, 10_000);
    },

    /* Embed back-button visibility + title override. Mirrors the embedded
       app's in-app navigation depth via the `hereby:tm:nav` postMessage.
       Reset on every iframe load (new src) — the embedded app re-broadcasts
       its initial state right after init. When `embedCanGoBack` is true, the
       PageHeader shows `embedNavTitle` (the entered tab's label) instead of
       the registry title and hides the leading page icon. */
    embedCanGoBack: false,
    embedNavTitle: "",
    embedMessageListenerBound: false,
    // 임베드된 tm iframe(연장근무/외근 결재/연차 결재) 안에서 모달이 열렸는지 — 풀페이지 dim 표시용
    tmModalOpen: false,
    // 임베드된 hr-system 시트의 오른쪽 레일 드로어(셀 상세·열 설정·급여 반영, 320px)가
    // 열렸는지 — 채팅·AI FAB 를 레일 폭만큼 왼쪽으로 비켜 세우는 데 쓴다(FAB 래퍼의
    // .sheet-rail-open 클래스 → main.css --ds-fab-dodge).
    sheetRailOpen: false,
    // 그 레일의 폭(px, 0 = 닫힘/모름). 레일은 사용자가 끄는 칸이라 고정 320 이 아니다 —
    // 자식이 hereby:tm:rail 에 실어 보낸 값을 body 의 --ds-embed-rail-w 로 세운다.
    sheetRailWidth: 0,
    // 임베드된 hr-system 시트 안에서 **모달이 아닌 떠 있는 층**(열 필터 팝오버 등)이
    // 열렸는지 — 열려 있는 동안 채팅 FAB 를 감춘다. 그 층은 iframe 안에 있고 채팅 FAB 은
    // 이 문서에 있어, 안쪽에서 z 를 올려도 FAB 을 못 넘는다(2026-09-23 사용자 리포트).
    // 모달(tmModalOpen)과 갈라 두는 이유: 이쪽은 풀페이지 딤도 iframe z-raise 도 하지 않는다
    // — 팝오버는 가벼운 층이라 LNB·헤더까지 흐려지면 안 된다.
    sheetPopoverOpen: false,
    // 임베드된 hr-system 시트 안의 「시트 어시스턴트」가 도크로 열려 있을 때의 폭(px, 0 = 닫힘).
    // iframe 안 CSS 는 부모 FAB 에 닿지 않아 자식 위젯이 hereby:tm:ai-dock 으로 알린다 —
    // admin 은 body 의 --ds-embed-ai-dock-w 로 세우고 main.css 가 --ds-fab-dodge 에 더한다
    // (레일 320px 와 같은 축, 동시에 열릴 수 있어 더한다). 값은 setSheetAiDockWidth 로만 바꾼다.
    sheetAiDockWidth: 0,

    /* Bind the global postMessage listener once. Called from x-init on the
       embed tab wrapper so it ties into the existing Alpine lifecycle. */
    bindEmbedMessageListener(): void {
      if (this.embedMessageListenerBound) {
        return;
      }
      this.embedMessageListenerBound = true;
      window.addEventListener("message", (e: MessageEvent) => {
        // biome-ignore lint/suspicious/noExplicitAny: postMessage payload shape
        const data = e.data as any;
        if (typeof data?.type !== "string" || !data.type.startsWith("hereby:tm:")) {
          return;
        }
        // 임베드 앱이 모달을 화면 전체 가운데에 세우려고 iframe 바깥 여백(LNB 폭 · 헤더 높이 …)을 묻는다
        // (@hereby/components setupEmbedModalCentering). 묻는 iframe 에만 답한다.
        if (data.type === "hereby:tm:embed-gaps-request") {
          for (const frame of Array.from(document.querySelectorAll("iframe"))) {
            if (frame.contentWindow === e.source) {
              const r = frame.getBoundingClientRect();
              frame.contentWindow?.postMessage(
                {
                  type: "hereby:embed:gaps",
                  gaps: {
                    left: r.left,
                    top: r.top,
                    right: window.innerWidth - r.right,
                    bottom: window.innerHeight - r.bottom,
                  },
                },
                "*",
              );
            }
          }
        }
        if (data.type === "hereby:tm:nav") {
          this.embedCanGoBack = data.canGoBack === true;
          this.embedNavTitle = typeof data.activeTabLabel === "string" ? data.activeTabLabel : "";
        }
        // iframe(연장근무/외근 결재/연차 결재) 안에서 모달이 열리면 → 풀페이지 dim(tmModalOpen)을
        // LNB·탭 위에 깔고(LNB는 보이되 흐려짐), 그 iframe만 dim 위로 z-raise → 모달이 dim 위에 뜬다.
        // iframe을 확장하지 않아 LNB가 사라지지 않는다(admin 네이티브 모달과 동일한 풀페이지 경험).
        if (data.type === "hereby:tm:modal") {
          this.tmModalOpen = data.open === true;
          for (const frame of Array.from(document.querySelectorAll("iframe"))) {
            if (frame.contentWindow === e.source) {
              frame.classList.toggle("iframe-modal-raised", data.open === true);
            }
          }
        }
        // 시트 임베드 안의 팝오버(열 필터 …) 열림/닫힘 → 채팅 FAB 를 그동안만 감춘다.
        // 딤도 z-raise 도 하지 않는다 — 팝오버는 모달이 아니다.
        if (data.type === "hereby:tm:popover") {
          this.sheetPopoverOpen = data.open === true;
        }
        // hr-system 시트 임베드의 오른쪽 레일 드로어 열림/닫힘 → admin 의 채팅·AI FAB 를
        // 레일 폭만큼 왼쪽으로 비켜 세운다(FAB 래퍼의 .sheet-rail-open 클래스 바인딩).
        if (data.type === "hereby:tm:rail") {
          this.sheetRailOpen = data.open === true;
          // 레일 폭은 사용자가 끄는 값이라 고정 320 이 아니다 — 자식이 보낸 폭을 그대로
          // 세운다(없으면 옛 껍데기/옛 자식이라 main.css 의 기본값 320 으로 떨어진다).
          this.setSheetRailWidth(
            this.sheetRailOpen && typeof data.width === "number" ? data.width : 0,
          );
        }
        // 시트 임베드 안 「시트 어시스턴트」의 도크 열림/폭 — 레일과 같은 이유로 admin 의
        // 채팅 FAB 를 도크 폭만큼 비킨다(안 하면 채팅 FAB 이 도크 입력줄 위에 얹힌다).
        if (data.type === "hereby:tm:ai-dock") {
          this.setSheetAiDockWidth(
            data.open === true && typeof data.width === "number" ? data.width : 0,
          );
        }
        // 시트 임베드 안에서 누른 단축키(Ctrl/⌘+Shift+F)·ESC — iframe 의 keydown 은 부모까지 오지 않으므로 hr-system 이
        // 메시지로 넘긴다. ESC 는 iframe 안에 열린 창이 없었을 때만 전체화면 종료(창이 먼저, 전체화면은 마지막).
        if (data.type === "hereby:tm:fullscreen") {
          if (this.fullscreenAvailable()) {
            this.toggleFullscreen();
          }
        }
        if (data.type === "hereby:tm:escape" && data.hadOpenModal !== true) {
          this.handleFullscreenEscape();
        }
        // iframe(문서 결재)에서 승인/반려 발생 → LNB 대기 뱃지 즉시 갱신
        if (data.type === "hereby:tm:doc-approval-changed") {
          this.loadDocApprovalPending();
        }
        // iframe(팀장 앱 결재)에서 승인·반려·되돌리기 — 신호로만 쓰고 서버를 다시 읽는다(결재함 숫자 + 보이는 네이티브 목록)
        if (data.type === "hereby:tm:approval-changed") {
          this.loadApprovalInbox();
          if (this.activeTab === "attendance") {
            if (this.attendanceTab === "leave") {
              this.loadLeaveApprovals();
            } else if (this.attendanceTab === "offsite") {
              this.loadOffsiteRecords();
            }
          }
        }
        // 근태 대시보드 임베드의 [전체 보기] → 근태 현황 페이지의 해당 탭(결재)으로 부모 네비게이션
        if (data.type === "hereby:tm:navigate" && typeof data.route === "string") {
          // 시트 임베드의 예약 창 「모든 예약 보기」 → 「예약된 변경」을 그 직원·시트로 좁혀서
          if (data.route === "scheduled-changes") {
            this.openScheduledChanges({
              employeeId: typeof data.employeeId === "string" ? data.employeeId : undefined,
              sheetSlug: typeof data.sheetSlug === "string" ? data.sheetSlug : undefined,
              label: typeof data.label === "string" ? data.label : undefined,
            });
            return;
          }
          if (data.route === "offsite") {
            this.switchTab("attendance");
            this.switchAttendanceTab("offsite");
          } else if (data.route.startsWith("leave")) {
            this.switchTab("attendance");
            this.attendanceTab = "leave";
            this.switchLeaveTab("approval");
          } else if (data.route === "embed/overtime-status") {
            this.switchTab("attendance");
            this.attendanceTab = "overtime";
          }
        }
      });
    },

    /* Send a `back` command to the embedded iframe. The embedded app pops one
       level off its in-app stack and broadcasts an updated nav state, which
       in turn clears `embedCanGoBack` if the user is now at the root. */
    goBackInEmbed(): void {
      const iframe = document.querySelector(
        "iframe[data-embed-iframe]",
      ) as HTMLIFrameElement | null;
      const win = iframe?.contentWindow;
      if (!win) {
        return;
      }
      win.postMessage({ type: "hereby:tm:back" }, "*");
    },

    async loadMyTeams() {
      try {
        const result = await this.sdk.teamManager.myTeams();
        this.myTeams = (result || []) as typeof this.myTeams;
      } catch {
        this.myTeams = [];
      }
    },

    /* 시트 임베드 안 시트 어시스턴트 도크의 폭을 body 변수로 내보낸다(0 이면 거둔다).
       Alpine 바인딩이 아니라 body 인라인 변수인 이유는 위젯 자신의 --ds-ai-dock-w 와 같다 —
       FAB 래퍼뿐 아니라 딤 위 신고 버튼(.ds-report-fab)까지 --ds-fab-dodge 하나로 비킨다. */
    setSheetAiDockWidth(width: number): void {
      const next = Number.isFinite(width) && width > 0 ? Math.round(width) : 0;
      this.sheetAiDockWidth = next;
      if (next > 0) {
        document.body.style.setProperty("--ds-embed-ai-dock-w", `${next}px`);
      } else {
        document.body.style.removeProperty("--ds-embed-ai-dock-w");
      }
    },

    /* 시트 임베드 오른쪽 레일의 폭을 body 변수로 내보낸다(0 이면 거둔다).
       레일은 사용자가 폭을 끄는 칸이라 상수로 둘 수 없다 — 변수가 없으면 main.css 가
       기본값 320px 로 떨어지므로, 폭을 못 받은 옛 자식과도 예전과 똑같이 동작한다. */
    setSheetRailWidth(width: number): void {
      const next = Number.isFinite(width) && width > 0 ? Math.round(width) : 0;
      this.sheetRailWidth = next;
      if (next > 0) {
        document.body.style.setProperty("--ds-embed-rail-w", `${next}px`);
      } else {
        document.body.style.removeProperty("--ds-embed-rail-w");
      }
    },

    /* LNB dispatcher: routes composite ids ("settings:<sub>", "embed:<key>") and plain tab ids. */
    /**
     * LNB 로 메뉴를 옮기면 그 화면은 늘 처음 상태 — 하위 탭은 첫 칸, 스크롤은 맨 위 · 맨 왼쪽(페이지 · 표 · 목록,
     * iframe 으로 끼운 앱 화면 안까지). 화면들은 x-show 로 숨겨질 뿐 남아 있어서, 이게 없으면 돌아왔을 때
     * 예전 스크롤 위치 · 탭이 그대로 보였다(2026-10-20 사용자 결정: LNB 클릭마다 리셋).
     * 고른 병동 · 팀 · 시트처럼 일부러 기억하는 선택과 페이지 크기 · 필터 기억은 건드리지 않는다.
     */
    resetPageHistory(id: string) {
      this.closingSubTab = "list";
      this.approvalLinePane = "org";
      this.wardDetailTab = "people";
      // 격리 컴포넌트(예약된 변경 등)는 이 신호를 받아 자기 첫 탭으로 돌아간다
      window.dispatchEvent(new CustomEvent("hereby:page-reset", { detail: { id } }));
      Alpine.nextTick(() => {
        const main = document.querySelector("main");
        if (main) {
          resetScrollPositions(main);
        }
        for (const frame of Array.from(document.querySelectorAll("iframe"))) {
          frame.contentWindow?.postMessage({ type: EMBED_RESET_SCROLL_MESSAGE }, "*");
        }
      });
    },

    handleLnbSelect(id: string): void {
      if (id.startsWith("embed:")) {
        if (this.orgEditMode) {
          this.confirmLeaveOrgEdit(() => this.handleLnbSelect(id));
          return;
        }
        const embedKey = id.slice("embed:".length);
        // 제한 입장은 자기 결재 화면만 연다(병동 관리자는 임베드 메뉴가 없다)
        if (this.isRestrictedAdmin && !this.approverEmbeds.includes(embedKey)) {
          Alpine.store("toast")?.info?.(this.restrictedAdminNotice());
          return;
        }
        // LNB 로 들어오면 늘 목록 첫 화면 — 앞서 「시트에서 열기」로 붙인 깊은 경로를 지운다
        this.embedSubPath = "";
        this.embedSubPathKey = "";
        // 임베드끼리 옮길 때(시트 → 드라이브)는 switchTab 을 지나지 않으므로 시트 크롬
        // 비킴(레일·어시스턴트 도크)을 여기서 거둔다. 새 iframe 이 뜨면 postMessage 로 재설정된다.
        if (this.currentEmbed !== embedKey) {
          this.sheetRailOpen = false;
          this.setSheetRailWidth(0);
          this.setSheetAiDockWidth(0);
        }
        this.currentEmbed = embedKey;
        this.activeTab = "embed";
        this.resetPageHistory(id);
        this.loadApprovalInbox();
        // Encode the embed key in the hash so browser refresh (F5)
        // can restore which iframe page to load. Without this the
        // iframe falls back to its default (admin's own URL) and the
        // user sees a blank embed shell after refresh.
        Alpine.store("router").navigateTab("embed", embedKey);
        return;
      }
      // 근태 현황: 상단 탭 4개 단일 페이지(activeTab='attendance'). LNB 진입 시 항상 첫 탭(근무 현황)으로.
      if (id === "attendance") {
        this.attendanceTab = "work";
        this.resetPageHistory(id);
        this.switchTab("attendance");
        return;
      }
      // 연차 관리: 근태 현황 페이지의 'leave' 탭으로 진입(하위 첫 서브탭=연차 현황).
      //   (LNB 단독 항목은 제거됐지만, 외부 호출 대비 근태 현황 컨테이너로 라우팅한다.)
      if (id === "leave") {
        this.leaveSubTab = "balances";
        this.resetPageHistory(id);
        this.switchTab("attendance");
        this.switchAttendanceTab("leave");
        return;
      }
      // 외근 관리: 근태 현황 페이지의 'offsite' 탭으로 진입(현황·결재 통합 단일 화면).
      if (id === "offsite") {
        this.resetPageHistory(id);
        this.switchTab("attendance");
        this.switchAttendanceTab("offsite");
        return;
      }
      // 연장근무 유형: 인페이지 탭 대신 유형·기준 통합 관리 팝업을 연장근무 섹션으로 연다.
      if (id === "overtime-types") {
        this.openTypeManager("overtime");
        return;
      }
      if (id.startsWith("settings:")) {
        const sub = id.slice("settings:".length);
        if (this.activeTab === "settings") {
          if (this.orgEditMode) {
            this.confirmLeaveOrgEdit(() => this.handleLnbSelect(id));
            return;
          }
          this.resetPageHistory(id);
          this.switchSettingsSub(sub);
          return;
        }
        if (this.orgEditMode) {
          this.confirmLeaveOrgEdit(() => this.handleLnbSelect(id));
          return;
        }
        this.resetPageHistory(id);
        this.settingsSubTab = sub;
        this.switchTab("settings");
        return;
      }
      if (id === "documents") {
        // LNB "문서 결재" always opens the page on its first tab.
        this.docsTab = "approval";
        // 진입 시 대기 건수 뱃지 최신화
        this.loadDocApprovalPending();
      }
      this.resetPageHistory(id);
      this.switchTab(id);
    },

    lnbIsActive(id: string): boolean {
      if (id.startsWith("embed:")) {
        return this.activeTab === "embed" && this.currentEmbed === id.slice("embed:".length);
      }
      if (id.startsWith("settings:")) {
        const sub = id.slice("settings:".length);
        // 도입이 끝난 「직원 앱 도입」은 설정의 한 탭(내 정보 옆)이라 「설정」 항목이 선택 상태로 남는다
        if (sub === "org" && this.activeTab === "onboarding") {
          return this.appAdoptionFinished;
        }
        if (this.activeTab !== "settings") {
          return false;
        }
        // 조직 설정·내 정보는 '설정' 한 페이지의 2탭 — LNB에는 '설정'(settings:org)
        // 항목만 있으므로, 내 정보 탭에서도 그 항목이 선택 상태로 남는다.
        if (sub === "org") {
          return this.settingsSubTab === "org" || this.settingsSubTab === "myinfo";
        }
        return this.settingsSubTab === sub;
      }
      return this.activeTab === id;
    },

    switchSettingsSub(subTab: string) {
      if (this.orgEditMode) {
        this.confirmLeaveOrgEdit(() => this.switchSettingsSub(subTab));
        return;
      }
      this.settingsSubTab = subTab;
      Alpine.store("router").navigateTab("settings", subTab);
      this.loadSettingsSubTabData(subTab);
    },

    /* Switch the documents page sub-tab (문서 결재 / 자격증/면허). Checks
       the frontend permission gate first; if the user lacks it, shows the info
       popup instead of opening the tab. Lazy-loads each tab's data on entry. */
    switchDocsTab(tab: "approval" | "certs") {
      if (!this.docsTabAllowed(tab)) {
        const store = Alpine.store("hereby");
        const code = this.docsTabPerm[tab];
        this.docsPermPopup = { open: true, label: store.tPermission(code) || code };
        return;
      }
      this.docsTab = tab;
      if (tab === "certs") {
        this.loadCertifications();
        this.loadCertSummary();
        this.loadCertTypeConfigs();
      }
    },

    /* Frontend-only permission gate for the documents tabs. System admins bypass.
       ⚠️ UX ONLY — the underlying APIs must enforce permission on the backend.
       documentManagement.list does NOT yet (teamLeaderProcedure only).
       See PR notes / merge plan memo. */
    docsTabAllowed(tab: "approval" | "certs"): boolean {
      const store = Alpine.store("hereby");
      if (store.currentUser?.isSystemAdmin) {
        return true;
      }
      const need = this.docsTabPerm[tab];
      if (!need) {
        return true;
      }
      const perms = (store?.currentUser?.permissions as string[]) || [];
      return perms.some((p) => p === need || p.startsWith(`${need}.`));
    },

    /* Switch the 연차 관리 page sub-tab (연차 현황 / 부여 기준 / 휴가 유형 / 연차·휴가 결재).
       Lazy-loads each sub-tab's data on entry. */
    switchLeaveTab(tab: "balances" | "rules" | "reasons" | "attendance-rate" | "approval") {
      this.leaveSubTab = tab;
      if (tab === "balances") {
        this.loadLeaveBalances();
      } else if (tab === "rules") {
        this.loadEntitlementRules();
      } else if (tab === "reasons") {
        this.loadLeaveReasons();
      } else if (tab === "attendance-rate") {
        this.loadAttendanceRates();
      } else if (tab === "approval") {
        // 휴가 유형 라벨 해석을 위해 leaveReasons도 확보
        if (this.leaveReasons.length === 0) {
          this.loadLeaveReasons();
        }
        this.loadLeaveApprovals();
      }
    },

    /* ── 출근율 (전년도 출근율) ── */
    async loadAttendanceRates() {
      this.attendanceRateLoading = true;
      try {
        const res = await this.sdk.attendanceRate.list({
          year: this.attendanceRateFilter.year,
          search: this.attendanceRateFilter.search || undefined,
          teamIds: this.attendanceRateFilter.teamIds.length
            ? this.attendanceRateFilter.teamIds
            : undefined,
          result: this.attendanceRateFilter.result,
        });
        this.attendanceRates = res.items as Record<string, unknown>[];
        this.attendanceRateDefaults = res.defaults;
        this.attendanceRatePage = 1;
      } catch (_e) {
        this.attendanceRates = [];
      } finally {
        this.attendanceRateLoading = false;
      }
    },
    /** 산정 연도 옵션 — 전년도 기준(진행 중인 올해는 제외). 직전 연도부터 6년 전까지. */
    attendanceRateYearOptions() {
      const cur = new Date().getFullYear();
      const opts: number[] = [];
      for (let y = cur - 1; y >= cur - 6; y--) {
        opts.push(y);
      }
      return opts;
    },
    attendanceRatesPageItems() {
      const start = (this.attendanceRatePage - 1) * this.attendanceRateLimit;
      return this.attendanceRates.slice(start, start + this.attendanceRateLimit);
    },
    attendanceRatePageRange(): number[] {
      return this.pageWindow(this.attendanceRatePage, this.attendanceRateTotalPages());
    },

    attendanceRateTotalPages() {
      return Math.max(1, Math.ceil(this.attendanceRates.length / this.attendanceRateLimit));
    },
    /** 출근율 표시 "92.137%" — 계산 설정의 소수 자릿수 반영 */
    formatAttendanceRate(pct: unknown, precision: unknown) {
      const p = Math.min(4, Math.max(0, Number(precision) || 0));
      return `${(Number(pct) || 0).toFixed(p)}%`;
    },
    /** 일·시·분 표시 (0 단위 생략, 전부 0이면 "0일") */
    formatDHM(v: unknown) {
      const o = (v ?? {}) as { days?: number; hours?: number; minutes?: number };
      const parts: string[] = [];
      if (o.days) {
        parts.push(`${o.days}일`);
      }
      if (o.hours) {
        parts.push(`${o.hours}시간`);
      }
      if (o.minutes) {
        parts.push(`${o.minutes}분`);
      }
      return parts.length ? parts.join(" ") : "0일";
    },
    /* ── 소정근로일수 설정 모달 ── */
    async openAttendanceRateBulk() {
      this.attendanceRateModalMode = "bulk";
      this.arRowEmployee = null;
      this.arPickerSelectedIds = [];
      this.arPickerSearch = "";
      this.arPickerSort = { by: "hireDate", order: "asc" };
      this.arForm = {
        days: this.attendanceRateDefaults.fullYearWorkDays || 0,
        hours: 0,
        minutes: 0,
        roundingMode: AttendanceRateRoundingMode.ROUND,
        precision: 3,
        reason: "",
      };
      this.showAttendanceRateModal = true;
      await this.loadArPickerEmployees();
    },
    async loadArPickerEmployees() {
      this.arPickerLoading = true;
      try {
        const items = await fetchAllPages<Record<string, unknown>>(
          (page, limit) => this.sdk.employee.getEmployees({ page, limit }),
          1000,
        );
        this.arPickerEmployees = items
          .filter((e) => e.status === "ACTIVE")
          .map((e) => ({
            ...e,
            fullName: `${e.lastName ?? ""}${e.firstName ?? ""}`.trim(),
          }));
      } catch (_e) {
        this.arPickerEmployees = [];
      } finally {
        this.arPickerLoading = false;
      }
    },
    get arPickerFiltered() {
      const q = this.arPickerSearch.trim().toLowerCase();
      let list = this.arPickerEmployees;
      if (q) {
        list = list.filter(
          (e) =>
            `${e.fullName ?? ""}`.toLowerCase().includes(q) ||
            `${e.employeeNumber ?? ""}`.toLowerCase().includes(q),
        );
      }
      const { by, order } = this.arPickerSort;
      if (by) {
        list = [...list].sort((a, b) => {
          const av = `${a[by] ?? ""}`;
          const bv = `${b[by] ?? ""}`;
          if (!av && bv) {
            return 1;
          }
          if (av && !bv) {
            return -1;
          }
          return order === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
        });
      }
      return list;
    },
    toggleArPickerSort(col: string) {
      if (this.arPickerSort.by !== col) {
        this.arPickerSort = { by: col, order: "asc" };
      } else if (this.arPickerSort.order === "asc") {
        this.arPickerSort.order = "desc";
      } else {
        this.arPickerSort = { by: "", order: "asc" };
      }
    },
    get arPickerCount() {
      return this.arPickerEmployees.length;
    },
    get arPickerAllSelected() {
      const f = this.arPickerFiltered;
      return f.length > 0 && f.every((e) => this.arPickerSelectedIds.includes(e.id as string));
    },
    toggleArPickerSelect(id: string) {
      const i = this.arPickerSelectedIds.indexOf(id);
      if (i >= 0) {
        this.arPickerSelectedIds.splice(i, 1);
      } else {
        this.arPickerSelectedIds.push(id);
      }
    },
    toggleArPickerSelectAll() {
      const f = this.arPickerFiltered;
      if (this.arPickerAllSelected) {
        const ids = new Set(f.map((e) => e.id as string));
        this.arPickerSelectedIds = this.arPickerSelectedIds.filter((id) => !ids.has(id));
      } else {
        const merged = new Set(this.arPickerSelectedIds);
        for (const e of f) {
          merged.add(e.id as string);
        }
        this.arPickerSelectedIds = [...merged];
      }
    },
    async openAttendanceRateRow(row: Record<string, unknown>) {
      this.attendanceRateModalMode = "row";
      this.arPickerSelectedIds = [];
      try {
        const s = await this.sdk.attendanceRate.getSetting({
          employeeId: row.employeeId as string,
          year: this.attendanceRateFilter.year,
        });
        this.arRowEmployee = { ...s.employee, period: s.period };
        this.arForm = {
          days: s.prescribed.days,
          hours: s.prescribed.hours,
          minutes: s.prescribed.minutes,
          roundingMode: s.roundingMode,
          precision: s.precision,
          reason: s.reason || "",
        };
        this.showAttendanceRateModal = true;
      } catch (_e) {
        showError("소정근로일수 설정을 불러오지 못했어요.");
      }
    },
    async saveAttendanceRateSetting() {
      const employeeIds =
        this.attendanceRateModalMode === "row"
          ? [this.arRowEmployee?.employeeId as string].filter(Boolean)
          : [...this.arPickerSelectedIds];
      if (!employeeIds.length) {
        return;
      }
      this.attendanceRateSaving = true;
      try {
        await this.sdk.attendanceRate.saveSettings({
          year: this.attendanceRateFilter.year,
          employeeIds,
          prescribed: {
            days: Number(this.arForm.days) || 0,
            hours: Number(this.arForm.hours) || 0,
            minutes: Number(this.arForm.minutes) || 0,
          },
          roundingMode: this.arForm.roundingMode,
          precision: this.arForm.precision,
          reason: this.arForm.reason || undefined,
        });
        this.showAttendanceRateModal = false;
        await this.loadAttendanceRates();
      } catch (_e) {
        showError("소정근로일수 설정을 저장하지 못했어요.");
      } finally {
        this.attendanceRateSaving = false;
      }
    },

    /* ── 결재선(MED-900) 공통 — 행 상태 · 결재 열 · 결재함 숫자 · 결과/오류 · iframe 신호 ──
       규칙은 @hereby/core/constants(approval-state) 순수 함수 — 팀장 앱·직원 앱과 같은 판정. */
    approvalStateOf(item: Record<string, unknown> | null): ApprovalStateLike | null {
      return (item?.approvalState as ApprovalStateLike | undefined) ?? null;
    },
    approvalRowOf(item: Record<string, unknown> | null): ApprovalRowState {
      return approvalRowState(this.approvalStateOf(item), item?.status === "pending");
    },
    isOwnRequest(item: Record<string, unknown> | null): boolean {
      const me = Alpine.store("hereby").currentUser?.id;
      return !!me && !!item && item.employeeId === me;
    },
    /** 승인·반려를 누를 수 있나 — 결재선 밖 단계(보고서 대기)는 그대로, 대기 건은 내 차례·대신 처리·예전 방식만, 내 신청은 불가 */
    approvalCanAct(item: Record<string, unknown> | null): boolean {
      if (!item || this.isApprovalBusy(item.id as string)) {
        return false;
      }
      if (item.status !== "pending") {
        return true;
      }
      if (this.isOwnRequest(item)) {
        return false;
      }
      return approvalRowActionable(this.approvalRowOf(item));
    },
    /** 결재 열 라벨 — 관리자 목록은 결재자 이름을 넣지 않는다(「확인 중 · 1/2」). 이름은 줄을 눌러 여는 상세에서 */
    approvalCellLabel(item: Record<string, unknown>): string {
      return approvalProgressLabel(this.approvalStateOf(item), item.status === "pending", {
        withName: false,
      });
    },
    approvalCellClass(item: Record<string, unknown>): string {
      const row = this.approvalRowOf(item);
      if (row === "mine") {
        return "ds-label--primary";
      }
      if (row === "others" || row === "delegate") {
        return "ds-label--blue";
      }
      if (row === "legacy") {
        return "ds-label--gray";
      }
      const outcome = this.approvalStateOf(item)?.outcome;
      return outcome === "rejected"
        ? "ds-label--red"
        : outcome === "approved"
          ? "ds-label--green"
          : "ds-label--light-gray";
    },
    approvalCellTitle(item: Record<string, unknown> | null): string {
      if (!item) {
        return "";
      }
      if (item.status === "pending" && this.isOwnRequest(item)) {
        return "내 신청은 직접 결재할 수 없어요";
      }
      const row = this.approvalRowOf(item);
      if (row === "others") {
        return approvalOthersTurnMessage(this.approvalStateOf(item));
      }
      if (row === "delegate") {
        return "지정 결재자 대신 처리할 수 있어요";
      }
      return "";
    },
    approvalNextTag(item: Record<string, unknown>): string {
      return approvalNextLabel(this.approvalStateOf(item)) ?? "";
    },
    approveLabel(item: Record<string, unknown> | null): string {
      return this.approvalRowOf(item) === "delegate" ? "대신 승인" : "승인";
    },
    rejectLabel(item: Record<string, unknown> | null): string {
      return this.approvalRowOf(item) === "delegate" ? "대신 반려" : "반려";
    },
    isApprovalBusy(id: string): boolean {
      return this.approvalBusyIds.includes(id);
    },
    /** 결재선 패널의 「바꾸기」 — 공용 모달(approval-replace-modal.html)을 지금 열린 상세의 문서로 연다 */
    openApprovalReplace(step: ApprovalStepsStep) {
      if (this.detailApprovalRef) {
        requestApprovalReplace({ step, ref: this.detailApprovalRef });
      }
    },
    /** 공용 모달의 열림/닫힘(상세 모달 ESC 가드)과 결과(토스트 · 재조회 · 다른 창 신호)를 받는다. 한 번만 */
    bindApprovalReplaceEvents() {
      if (this.approvalReplaceBound) {
        return;
      }
      this.approvalReplaceBound = true;
      bindApprovalReplaceHost({
        onState: (open) => {
          this.approvalReplaceOpen = open;
        },
        onReplaced: (detail) => this.onApprovalReplaced(detail),
      });
    },
    /** 결재자를 바꾼 뒤(실패 뒤에도) — 상세 결재선 · 목록을 다시 읽는다 */
    async onApprovalReplaced(detail: ApprovalReplacedDetail) {
      if (detail.message) {
        this.notify(detail.message, "success");
      }
      const requestType = detail.ref.requestType as "leave" | "offsite_work";
      await this.loadDetailApproval(requestType, detail.ref.requestId);
      if (requestType === "leave") {
        await this.loadLeaveApprovals();
      } else {
        await this.loadOffsiteRecords();
      }
      this._notifyEmbedApprovalChanged(requestType, [detail.ref.requestId]);
    },
    /** 상세 모달을 열 때 결재선을 읽는다(approvalFlow.steps 한 번). 못 읽어도 상세는 열린다. */
    async loadDetailApproval(requestType: "leave" | "offsite_work", requestId: string) {
      this.detailApproval = null;
      this.detailApprovalRef = { requestType, requestId };
      this.detailApprovalLoading = true;
      try {
        this.detailApproval = (await this.sdk.approvalFlow.steps(
          requestType as ApprovalRequestType,
          requestId,
        )) as ApprovalStepsState;
      } catch {
        this.detailApproval = null;
      } finally {
        this.detailApprovalLoading = false;
      }
    },
    /** 결재함 숫자(내가 현 단계 결재자인 대기 건) — LNB 결재 메뉴 배지. 실패하면 0 */
    async loadApprovalInbox() {
      try {
        const summary = await this.sdk.approvalFlow.inboxSummary();
        const byType: Record<string, number> = {};
        for (const row of summary.byType) {
          byType[row.requestType] = row.count;
        }
        this.approvalInbox = { total: summary.total, byType };
      } catch {
        this.approvalInbox = { total: 0, byType: {} };
      }
    },
    /** 결재자 제한 입장의 첫 화면 — 대기가 있는 첫 결재 종류, 없으면 첫 메뉴 */
    firstApproverEmbed(): string {
      const typeOf: Record<string, string> = {
        leave: "leave",
        "overtime-approval": "overtime",
        "offsite-approval": "offsite_work",
        incentive: "incentive",
      };
      return (
        this.approverEmbeds.find(
          (key) => (this.approvalInbox.byType[typeOf[key] ?? ""] ?? 0) > 0,
        ) ?? this.approverEmbeds[0]
      );
    },
    /**
     * 승인·반려 한 번 — 요청 중 버튼 잠금 → 서버 message 토스트 → 재조회 → 결재함 숫자 → iframe 에 신호.
     * FORBIDDEN·CONFLICT·네트워크는 실패가 아니라 「상태가 바뀜」 — 공용 핸들러가 재조회 + 안내만 한다.
     */
    async runApprovalDecision(
      requestType: "leave" | "offsite_work",
      ids: string[],
      action: () => Promise<unknown>,
      reload: () => Promise<void>,
      fallbackMessage = "처리했어요.",
    ): Promise<boolean> {
      const fresh = ids.filter((id) => !this.approvalBusyIds.includes(id));
      if (fresh.length === 0) {
        return false;
      }
      this.approvalBusyIds.push(...fresh);
      try {
        const result = await action();
        this.notify(decisionResultMessage(result, fallbackMessage));
        await reload();
        this.loadApprovalInbox();
        this._notifyEmbedApprovalChanged(requestType, ids);
        return true;
      } catch (err) {
        await handleDecisionError(err, {
          refresh: async () => {
            await reload();
            this.loadApprovalInbox();
          },
        });
        return false;
      } finally {
        this.approvalBusyIds = this.approvalBusyIds.filter((id) => !fresh.includes(id));
      }
    },
    /** 네이티브에서 처리한 뒤 열려 있는 임베드(팀장 앱)에 알린다 — 자식은 신호로만 쓰고 서버를 다시 읽는다 */
    _notifyEmbedApprovalChanged(requestType: string, requestIds: string[]) {
      for (const frame of Array.from(document.querySelectorAll("iframe"))) {
        frame.contentWindow?.postMessage(
          { type: "hereby:admin:approval-changed", requestType, requestIds },
          "*",
        );
      }
    },

    /* ── 연차/휴가 결재 (네이티브) ── */
    async loadLeaveApprovals() {
      try {
        const filter: Record<string, unknown> = {};
        // 「취소 신청」(MED-959)은 상태값이 아니라 걸린 신청 기준 — cancelRequested 로 거른다
        if (this.leaveApprovalFilter.status === "cancel_requested") {
          filter.cancelRequested = true;
        } else if (this.leaveApprovalFilter.status) {
          filter.status = this.leaveApprovalFilter.status;
        }
        if (this.leaveApprovalFilter.dateFrom || this.leaveApprovalFilter.dateTo) {
          filter.dateRange = {
            from: this.leaveApprovalFilter.dateFrom || undefined,
            to: this.leaveApprovalFilter.dateTo || undefined,
          };
        }
        if (this.leaveApprovalFilter.search.trim()) {
          filter.search = this.leaveApprovalFilter.search.trim();
        }
        const result = await this.sdk.leaveRequest.list(
          // biome-ignore lint/suspicious/noExplicitAny: filter accepts org leaveType ids + status string
          filter as any,
          {
            page: this.leaveApprovalPaging.page,
            limit: this.leaveApprovalPaging.limit,
            // 명시 필수 — 공용 paginationSchema의 Zod 기본이 asc라, 생략하면 최신이 아래로 깔린다
            sortBy: "createdAt",
            sortOrder: this.leaveApprovalSortOrder,
          },
        );
        const r = result as {
          items?: Record<string, unknown>[];
          totalCount?: number;
          totalPages?: number;
        };
        this.leaveApprovals = r?.items || [];
        this.leaveApprovalPaging.totalCount = r?.totalCount || 0;
        this.leaveApprovalPaging.totalPages = r?.totalPages || 0;
      } catch {
        this.leaveApprovals = [];
        this.leaveApprovalPaging.totalCount = 0;
        this.leaveApprovalPaging.totalPages = 0;
      }
    },

    leaveApprovalGoPage(p: number) {
      const max = Math.max(1, this.leaveApprovalPaging.totalPages);
      this.leaveApprovalPaging.page = Math.min(Math.max(1, p), max);
      this.loadLeaveApprovals();
    },

    /** 신청일 컬럼 헤더 클릭 — 내림차순↔오름차순 토글 후 첫 페이지부터 재조회. */
    toggleLeaveApprovalSort() {
      this.leaveApprovalSortOrder = this.leaveApprovalSortOrder === "desc" ? "asc" : "desc";
      this.leaveApprovalGoPage(1);
    },

    // 자주 쓰는 기간 프리셋 — team-manager(연장/외근 결재)와 동일 사본 미러 (근태 현황 통일).
    // 본체는 attendance/date-preset.ts — tm의 date-preset-sync.test.ts가 동기화를 강제.
    resolveDatePresetRange(preset: string): { from: string; to: string } {
      return resolveDatePresetRange(preset);
    },

    // 연차/휴가 결재 기간 프리셋
    setLeaveApprovalDatePreset(preset: string) {
      const range = this.resolveDatePresetRange(preset);
      this.leaveApprovalDatePreset = preset;
      this.leaveApprovalFilter.dateFrom = range.from;
      this.leaveApprovalFilter.dateTo = range.to;
      this.leaveApprovalGoPage(1);
    },

    // 외근 현황 기간 프리셋
    setOffsiteRecDatePreset(preset: string) {
      const range = this.resolveDatePresetRange(preset);
      this.offsiteRecDatePreset = preset;
      this.offsiteRecFilter.dateFrom = range.from;
      this.offsiteRecFilter.dateTo = range.to;
      this.applyOffsiteRecFilter();
    },

    // 자격증/면허 신고 기한 기간 프리셋 (문서 결재 탭과 동일). 'all'이면 구간 해제.
    setCertDatePreset(preset: string) {
      const range = this.resolveDatePresetRange(preset);
      this.certDatePreset = preset;
      this.certFilter.deadlineFrom = range.from;
      this.certFilter.deadlineTo = range.to;
      this.loadCertifications();
    },

    leaveApprovalPageRange(): number[] {
      return this.pageWindow(this.leaveApprovalPaging.page, this.leaveApprovalPaging.totalPages);
    },

    /** 휴가 유형 id → 표시 이름. org 목록에 없으면 레거시 라벨, 삭제된 커스텀(lr_)은 '삭제' */
    leaveApprovalTypeLabel(type: unknown): string {
      const t = String(type ?? "");
      const found = this.leaveReasons.find((r) => r.id === t);
      if (found) {
        return found.name;
      }
      const legacy: Record<string, string> = {
        annual: "연차",
        sick: "병가",
        personal: "개인 사유",
        maternity: "출산 휴가",
        paternity: "배우자 출산",
        bereavement: "경조 휴가",
        military: "예비군/민방위",
        other: "기타",
      };
      if (legacy[t]) {
        return legacy[t];
      }
      return t.startsWith("lr_") ? "삭제" : t || "-";
    },

    /** 차감 일수 — 반차 0.5, 그 외 (종료-시작+1)일 */
    leaveDeductionDays(item: Record<string, unknown>): number {
      if (item.isHalfDay) {
        return 0.5;
      }
      const s = new Date(String(item.startDate).slice(0, 10));
      const e = new Date(String(item.endDate).slice(0, 10));
      if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) {
        return 0;
      }
      return Math.floor((e.getTime() - s.getTime()) / 86400000) + 1;
    },

    /** 차감 표시 라벨 — 시간 단위 신청(requestedMinutes)은 시간으로(여러 날이면 「3일 × 2시간」, 하루당), 그 외는 일수로 */
    leaveDeductionLabel(item: Record<string, unknown>): string {
      const mins = Number(item.requestedMinutes) || 0;
      if (mins > 0) {
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        const perDay = m === 0 ? `${h}시간` : h > 0 ? `${h}시간 ${m}분` : `${m}분`;
        const s = new Date(String(item.startDate).slice(0, 10));
        const e = new Date(String(item.endDate).slice(0, 10));
        const span =
          Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())
            ? 1
            : Math.floor((e.getTime() - s.getTime()) / 86400000) + 1;
        return span > 1 ? `${span}일 × ${perDay}` : perDay;
      }
      return `${this.leaveDeductionDays(item)}일`;
    },

    leaveStatusLabel(status: unknown): string {
      const map: Record<string, string> = {
        pending: "대기",
        approved: "승인",
        rejected: "반려",
        cancelled: "취소",
      };
      return map[String(status ?? "")] || String(status ?? "-");
    },

    /** 결재자/처리자 표기 `{성이름(사번)}` — 사번 없으면 이름만, 이름 없으면 '-' */
    leaveActorLabel(name: unknown, number: unknown): string {
      const n = String(name || "").trim();
      const num = String(number || "").trim();
      if (!n) {
        return "-";
      }
      return num ? `${n} (${num})` : n;
    },

    /** 취소 처리자 — 본인 취소면 '직원 본인', 아니면 {성이름(사번)} (연차 관리 기획 §3④) */
    leaveCancelActorLabel(r: Record<string, unknown> | null): string {
      if (!r?.cancelledBy) {
        return "-";
      }
      if (r.cancelledBy === r.employeeId) {
        return "직원 본인";
      }
      return this.leaveActorLabel(r.cancelledByName, r.cancelledByNumber);
    },

    /**
     * 휴가 상세 — 표 줄을 누르면 팀 관리 「스케줄러 미리보기」와 같은 자리(페이지 헤더 아래 오른쪽)에 팝오버로 뜬다
     * (2026-10-07 사용자 결정: 모달 → 팝오버). 같은 줄을 다시 누르면 닫힌다. 열 때 채팅 · 병동 팝오버를 접게 알린다.
     */
    openLeaveDetail(item: Record<string, unknown>) {
      if (this.showLeaveDetail && this.leaveDetailRecord?.id === item.id) {
        this.closeLeaveDetail();
        return;
      }
      this.leaveDetailRecord = item;
      this.showLeaveDetail = true;
      this.syncTeamWardPopTop('[data-testid="att-leave-detail-pop"]');
      window.dispatchEvent(
        new CustomEvent(FLOATING_PANEL_OPEN_EVENT, { detail: LEAVE_DETAIL_PANEL_ID }),
      );
      // 결재선 패널 데이터 — 상세를 연 뒤 한 번 읽는다
      this.loadDetailApproval("leave", item.id as string);
    },
    closeLeaveDetail() {
      this.showLeaveDetail = false;
    },
    /**
     * 한 번만 — ESC(공용 ESC 층: 위에 뜬 반려 사유 · 승인 취소 · 결재자 바꾸기 모달이 있으면 그쪽이 먼저) · 채팅/AI 열기 ·
     * 다른 탭 · 다른 서브탭 · 헤더 알림/프로필 메뉴에 닫힌다. 팀 관리 병동 팝오버(bindTeamWardPopListeners)와 같은 규칙.
     */
    bindLeaveDetailPopListeners() {
      if (this.leaveDetailPopListenersBound) {
        return;
      }
      this.leaveDetailPopListenersBound = true;
      for (const key of ["activeTab", "attendanceTab", "leaveSubTab"]) {
        // biome-ignore lint/suspicious/noExplicitAny: Alpine magic property
        (this as any).$watch(key, () => this.closeLeaveDetail());
      }
      registerEscLayer(
        () =>
          this.showLeaveDetail
            ? document.querySelector<HTMLElement>('[data-testid="att-leave-detail-pop"]')
            : null,
        () => this.closeLeaveDetail(),
      );
      window.addEventListener(FLOATING_PANEL_OPEN_EVENT, (event: Event) => {
        if ((event as CustomEvent).detail !== LEAVE_DETAIL_PANEL_ID) {
          this.closeLeaveDetail();
        }
      });
      window.addEventListener(AI_ASSISTANT_OPEN_EVENT, () => this.closeLeaveDetail());
      document.addEventListener(
        "click",
        (event) => {
          const target = event.target as Element | null;
          if (
            this.showLeaveDetail &&
            target?.closest?.(
              '.ds-page-header__toolbar [x-data="notificationBell"], .ds-page-header__toolbar .ds-page-header__profile',
            )
          ) {
            this.closeLeaveDetail();
          }
        },
        true,
      );
    },
    /** 표 줄의 「승인」 — 상세를 안 본 채 누르므로 다음 결재자 안내가 든 확인창을 먼저(팀장 앱 목록 줄과 같은 결) */
    /** 휴가 취소 신청 범위(MED-959) — 결재 시점 기준, 서버와 같은 규칙 */
    leaveCancelPreview(item: Record<string, unknown>): string {
      return leaveCancelPlanLabel(
        planLeaveCancellation({
          startDate: String(item.startDate ?? "").slice(0, 10),
          endDate: String(item.endDate ?? "").slice(0, 10),
          today: Alpine.store("hereby").todayLocal(),
          // 여러 날 시간 단위 신청은 일 단위처럼 남은 날만 취소 — 하루짜리는 start === end 가 잡는다
          singleUnit: item.isHalfDay === true,
          earliestFrom: item.cancelRequestFrom ? String(item.cancelRequestFrom).slice(0, 10) : null,
        }),
      );
    },

    /** 휴가 취소 신청 결재(MED-959) — 승인이면 전체/남은 날 취소·연차 복원·근무표 칸 정리, 거절이면 사유를 받아 그대로 */
    async resolveLeaveCancelRow(item: Record<string, unknown>, approve: boolean) {
      let rejectionReason: string | undefined;
      if (approve) {
        const ok = await askConfirm({
          message: `${item.employeeName} 님의 휴가 취소를 승인할까요?\n${this.leaveCancelPreview(item)} — 차감된 연차가 돌아가고 근무표의 그 날짜 휴가 칸이 비워져요.`,
          confirmLabel: "취소 승인",
        });
        if (!ok) {
          return;
        }
      } else {
        const typed = await showPrompt({
          title: "취소 거절 사유",
          message: `${item.employeeName} 님에게 보일 사유를 적어 주세요. 휴가는 그대로 유지돼요.`,
          placeholder: "예: 그날은 인력이 부족해요",
          requireValue: true,
        });
        if (typed === null || !typed.trim()) {
          return;
        }
        rejectionReason = typed.trim();
      }
      try {
        await this.sdk.leaveRequest.resolveCancelRequest(
          item.id as string,
          approve,
          rejectionReason,
        );
        Alpine.store("toast").success(
          approve ? "휴가 취소를 승인했어요." : "취소 신청을 거절했어요.",
        );
      } catch (e: unknown) {
        showError(e instanceof Error ? e.message : "처리하지 못했어요.");
      }
      await this.loadLeaveApprovals();
    },

    async confirmLeaveRowApproval(item: Record<string, unknown>) {
      const hint = approvalDecisionHint(this.approvalStateOf(item), "approved");
      const ok = await askConfirm({
        message: `${item.employeeName} 님의 휴가를 승인할까요?\n${hint}`,
        confirmLabel: this.approveLabel(item),
      });
      if (ok) {
        await this.approveLeaveById(item.id as string);
      }
    },

    /** 휴가 신청 id 하나를 바로 승인 — 상세 모달에서 온다(상세를 본 뒤라 확인창 없이 바로). 결재선 공용 처리. */
    async approveLeaveById(id: string) {
      await this.runApprovalDecision(
        "leave",
        [id],
        // 초과 사용 확인(MED-968) — 「결재자가 확인한 뒤 허용」 정책이면 확인창 뒤 confirmOveruse 로 다시 보낸다
        () =>
          withOveruseConfirm((confirmOveruse) =>
            this.sdk.leaveRequest.approve({ id, confirmOveruse: confirmOveruse || undefined }),
          ),
        () => this.loadLeaveApprovals(),
        "휴가를 승인했어요.",
      );
    },

    async approveLeaveDetail() {
      const r = this.leaveDetailRecord;
      if (!r?.id) {
        return;
      }
      this.showLeaveDetail = false;
      await this.approveLeaveById(r.id as string);
    },

    /** 승인 취소 가능 여부 — 승인 상태 && 휴가 기간(종료일)이 지나지 않음(당일 포함).
     *  기준일은 조직 타임존의 오늘. 서버 cancel 뮤테이션과 동일 정책(종료일 < 오늘이면 불가). */
    canCancelApprovedLeave(r: Record<string, unknown> | null): boolean {
      if (!r || r.status !== "approved") {
        return false;
      }
      const endStr = String(r.endDate ?? "").slice(0, 10);
      if (!endStr) {
        return false;
      }
      const today = Alpine.store("hereby").todayLocal() || new Date().toISOString().slice(0, 10);
      return endStr >= today;
    },

    /** 승인 취소 확인 모달 열기 (휴가 상세 위). */
    openLeaveApprovalCancel() {
      this.showLeaveCancelApproval = true;
    },

    /** 승인 취소 확정 — 서버 cancel 뮤테이션이 상태를 '취소'로 바꾸고 차감된 연차를 원장 상쇄로 복구한다. */
    async confirmLeaveApprovalCancel() {
      const r = this.leaveDetailRecord;
      if (!r?.id) {
        return;
      }
      try {
        await this.sdk.leaveRequest.cancel(r.id as string);
        this.showLeaveCancelApproval = false;
        this.showLeaveDetail = false;
        await this.loadLeaveApprovals();
        this.notify("휴가 승인을 취소했어요. 차감된 연차가 복구되었어요.");
        this.loadApprovalInbox();
        this._notifyEmbedApprovalChanged("leave", [r.id as string]);
      } catch (err) {
        this.showLeaveCancelApproval = false;
        this.notify(
          `${this._t("admin.save_failed")}: ${err instanceof Error ? err.message : String(err)}`,
          "error",
        );
      }
    },

    openLeaveReject(id: string) {
      this.leaveRejectId = id;
      this.leaveRejectReason = "";
      this.showLeaveReject = true;
    },

    async submitLeaveReject() {
      const reason = this.leaveRejectReason.trim();
      const id = this.leaveRejectId;
      if (!(id && reason)) {
        return;
      }
      const ok = await this.runApprovalDecision(
        "leave",
        [id],
        () => this.sdk.leaveRequest.reject({ id, rejectionReason: reason }),
        () => this.loadLeaveApprovals(),
        "휴가를 반려했어요.",
      );
      if (ok) {
        this.showLeaveReject = false;
        this.showLeaveDetail = false;
      }
    },

    async loadOffsiteRecords() {
      // 목록 재조회(필터·페이지 변경 포함) 시 이전 선택은 초기화 (스펙 §4: 현재 페이지 기준 선택)
      this.selectedOffsiteRecIds = [];
      try {
        const filter: Record<string, unknown> = {};
        if (this.offsiteRecFilter.status) {
          filter.status = this.offsiteRecFilter.status;
        }
        if (this.offsiteRecFilter.teamIds.length > 0) {
          filter.teamIds = this.offsiteRecFilter.teamIds;
        }
        if (this.offsiteRecFilter.dateFrom || this.offsiteRecFilter.dateTo) {
          filter.dateRange = {
            from: this.offsiteRecFilter.dateFrom || undefined,
            to: this.offsiteRecFilter.dateTo || undefined,
          };
        }
        if (this.offsiteRecFilter.search.trim()) {
          filter.search = this.offsiteRecFilter.search.trim();
        }
        const result = await this.sdk.offsiteWork.list(filter, {
          page: this.offsitePaging.page,
          limit: this.offsitePaging.limit,
          sortBy: "workDate",
          sortOrder: "desc",
        });
        this.offsiteRecords = result?.items || [];
        this.offsitePaging.totalCount = result?.totalCount || 0;
        this.offsitePaging.totalPages = result?.totalPages || 0;
        this.offsiteTotalHours = Number(result?.totalHours || 0);
      } catch {
        this.offsiteRecords = [];
        this.offsiteTotalHours = 0;
      } finally {
        this.loadedOnce.offsite = true;
      }
    },

    applyOffsiteRecFilter() {
      this.offsitePaging.page = 1;
      this.loadOffsiteRecords();
    },

    async loadOffsiteTypes() {
      try {
        const result = await this.sdk.offsiteWork.listAllTypes();
        // biome-ignore lint/suspicious/noExplicitAny: SDK returns unknown
        this.offsiteTypes = ((result as any)?.items || []) as typeof this.offsiteTypes;
      } catch {
        this.offsiteTypes = [];
      }
    },

    /** 상태 필터(전체/활성/비활성)를 적용한 목록 (기획서 유형관리 §1) */
    get filteredOffsiteTypes() {
      if (this.offsiteTypeFilter === "active") {
        return this.offsiteTypes.filter((t) => t.isActive !== false);
      }
      if (this.offsiteTypeFilter === "inactive") {
        return this.offsiteTypes.filter((t) => t.isActive === false);
      }
      return this.offsiteTypes;
    },

    openAddOffsiteType() {
      this.editingOffsiteTypeId = "";
      this.offsiteTypeForm = { name: "", description: "", isActive: true };
      this.showOffsiteTypeModal = true;
    },

    openEditOffsiteType(type: {
      id: string;
      name: string;
      description?: string | null;
      isActive?: boolean;
    }) {
      this.editingOffsiteTypeId = type.id;
      this.offsiteTypeForm = {
        name: type.name,
        description: type.description || "",
        isActive: type.isActive !== false,
      };
      this.showOffsiteTypeModal = true;
    },

    async saveOffsiteType() {
      if (!this.offsiteTypeForm.name?.trim()) {
        return;
      }
      try {
        const description = this.offsiteTypeForm.description?.trim() || undefined;
        // 상태 select(x-model + :value="true|false")이 일부 환경에서 문자열 "false"를 내보내
        // 백엔드 z.boolean() 검증에 막히는 것을 방지 — 실제 불리언으로 정규화한다.
        const rawActive = this.offsiteTypeForm.isActive as unknown as boolean | string;
        const isActive = rawActive !== false && rawActive !== "false";
        if (this.editingOffsiteTypeId) {
          await this.sdk.offsiteWork.updateType({
            id: this.editingOffsiteTypeId,
            name: this.offsiteTypeForm.name.trim(),
            description,
            isActive,
          });
        } else {
          await this.sdk.offsiteWork.createType({
            name: this.offsiteTypeForm.name.trim(),
            description,
            sortOrder: this.offsiteTypes.length,
            isActive,
          });
        }
        this.showOffsiteTypeModal = false;
        await this.loadOffsiteTypes();
        this.notify("저장되었습니다");
      } catch (err) {
        this.notify(
          `${this._t("admin.save_failed")}: ${err instanceof Error ? err.message : String(err)}`,
          "error",
        );
      }
    },

    /**
     * 외근 유형 삭제 (기획서 유형관리 §3) — 3-case:
     *  1) 활성 → 차단 안내 (먼저 비활성으로)
     *  2) 비활성 + 연결된 외근 내역 있음 → 삭제 시 해당 기록 유형 '삭제' 표시 경고
     *  3) 비활성 + 연결 없음 → 단순 삭제 확인
     */
    deleteOffsiteType(type: { id: string; name: string; isActive?: boolean; usageCount?: number }) {
      // 유형·기준 관리 패널(z-50) 안에서 호출되므로 확인창을 그 위(z-60)에 띄운다.
      this.confirmTop = true;
      // 활성 유형은 삭제 차단 — 안내 전용(취소 버튼 숨김) DS 모달.
      if (type.isActive !== false) {
        this.confirmTitle = "삭제";
        this.confirmMessage =
          "외근 유형을 삭제할 수 없어요. 먼저 비활성 상태로 변경한 뒤 삭제할 수 있어요.";
        this.confirmLabel = this._t("common.confirm");
        this.confirmHideCancel = true;
        this.confirmAction = null;
        this.showConfirm = true;
        return;
      }
      const hasRecords = (type.usageCount ?? 0) > 0;
      this.confirmTitle = "삭제";
      this.confirmMessage = hasRecords
        ? "이 유형을 사용한 외근 내역이 있어요. 삭제하면 해당 외근 내역의 유형은 '삭제'로 표시돼요."
        : `${type.name} 외근 유형을 삭제할까요?`;
      this.confirmLabel = this._t("common.confirm");
      this.confirmHideCancel = false;
      this.confirmAction = async () => {
        try {
          await this.sdk.offsiteWork.deleteType(type.id);
          await this.loadOffsiteTypes();
          this.notify("삭제되었습니다");
        } catch (err) {
          this.notify(
            `${this._t("admin.delete_failed")}: ${err instanceof Error ? err.message : String(err)}`,
            "error",
          );
        }
      };
      this.showConfirm = true;
    },

    /* ── 연장근무 유형 관리 ── */

    async loadOvertimeTypes() {
      try {
        const result = await this.sdk.overtime.listAllTypes();
        // biome-ignore lint/suspicious/noExplicitAny: SDK returns unknown
        this.overtimeTypes = ((result as any)?.items || []) as typeof this.overtimeTypes;
      } catch {
        this.overtimeTypes = [];
      }
    },

    /** 상태 필터(전체/활성/비활성) 적용 목록 */
    get filteredOvertimeTypes() {
      if (this.overtimeTypeFilter === "active") {
        return this.overtimeTypes.filter((t) => t.isActive !== false);
      }
      if (this.overtimeTypeFilter === "inactive") {
        return this.overtimeTypes.filter((t) => t.isActive === false);
      }
      return this.overtimeTypes;
    },

    openAddOvertimeType() {
      this.editingOvertimeTypeId = "";
      // 배율은 빈 값으로 시작(placeholder 노출) — 사용자가 직접 입력해야 저장 활성화.
      this.overtimeTypeForm = { name: "", description: "", multiplier: "", isActive: true };
      this.showOvertimeTypeModal = true;
    },

    openEditOvertimeType(type: {
      id: string;
      name: string;
      description?: string | null;
      multiplier?: number;
      isActive?: boolean;
    }) {
      this.editingOvertimeTypeId = type.id;
      this.overtimeTypeForm = {
        name: type.name,
        description: type.description || "",
        multiplier: Number(type.multiplier ?? 1.5),
        isActive: type.isActive !== false,
      };
      this.showOvertimeTypeModal = true;
    },

    async saveOvertimeType() {
      if (!this.overtimeTypeForm.name?.trim()) {
        return;
      }
      try {
        const description = this.overtimeTypeForm.description?.trim() || undefined;
        const multiplier = Number(this.overtimeTypeForm.multiplier) || 0;
        // 상태 select(x-model + :value="true|false")이 일부 환경에서 문자열 "false"를 내보내
        // 백엔드 z.boolean() 검증에 막히는 것을 방지 — 실제 불리언으로 정규화한다.
        const rawActive = this.overtimeTypeForm.isActive as unknown as boolean | string;
        const isActive = rawActive !== false && rawActive !== "false";
        if (this.editingOvertimeTypeId) {
          await this.sdk.overtime.updateType({
            id: this.editingOvertimeTypeId,
            name: this.overtimeTypeForm.name.trim(),
            description,
            multiplier,
            isActive,
          });
        } else {
          // 새 유형은 목록 상단에 표시(스펙). 실제 상단 정렬은 서버 createType이
          // 기존 sortOrder를 1씩 밀고 새 유형을 0으로 저장해 처리한다(sortOrder >= 0 유지).
          await this.sdk.overtime.createType({
            name: this.overtimeTypeForm.name.trim(),
            description,
            multiplier,
            sortOrder: 0,
            isActive,
          });
        }
        this.showOvertimeTypeModal = false;
        await this.loadOvertimeTypes();
        this.notify("저장되었습니다");
      } catch (err) {
        this.notify(
          `${this._t("admin.save_failed")}: ${err instanceof Error ? err.message : String(err)}`,
          "error",
        );
      }
    },

    /**
     * 연장근무 유형 삭제 (기획서 유형관리 §3) — 3-case:
     *  1) 활성 → 차단 (먼저 비활성으로)
     *  2) 비활성 + 연결된 연장근무 내역 있음 → 삭제 시 해당 기록 유형 '삭제' 표시 경고
     *  3) 비활성 + 연결 없음 → 단순 삭제 확인
     */
    deleteOvertimeType(type: {
      id: string;
      name: string;
      isActive?: boolean;
      usageCount?: number;
    }) {
      // 유형·기준 관리 패널(z-50) 안에서 호출되므로 확인창을 그 위(z-60)에 띄운다.
      this.confirmTop = true;
      // 활성 유형은 삭제 차단 — 안내 전용(취소 버튼 숨김) DS 모달.
      if (type.isActive !== false) {
        this.confirmTitle = "삭제";
        this.confirmMessage =
          "연장근무 유형을 삭제할 수 없어요. 먼저 비활성 상태로 변경한 뒤 삭제할 수 있어요.";
        this.confirmLabel = this._t("common.confirm");
        this.confirmHideCancel = true;
        this.confirmAction = null;
        this.showConfirm = true;
        return;
      }
      const hasRecords = (type.usageCount ?? 0) > 0;
      this.confirmTitle = "삭제";
      this.confirmMessage = hasRecords
        ? "이 유형을 사용한 연장근무 내역이 있어요. 삭제하면 해당 연장근무 내역의 유형은 '삭제'로 표시돼요."
        : `${type.name} 연장근무 유형을 삭제할까요?`;
      this.confirmLabel = this._t("common.confirm");
      this.confirmHideCancel = false;
      this.confirmAction = async () => {
        try {
          await this.sdk.overtime.deleteType(type.id);
          await this.loadOvertimeTypes();
          this.notify("삭제되었습니다");
        } catch (err) {
          this.notify(
            `${this._t("admin.delete_failed")}: ${err instanceof Error ? err.message : String(err)}`,
            "error",
          );
        }
      };
      this.showConfirm = true;
    },

    offsiteStatusLabel(s: unknown): string {
      const map: Record<string, string> = {
        pending: "대기",
        approved: "승인",
        rejected: "반려",
        cancelled: "취소",
        report_pending: "보고서 대기",
        report_approved: "보고서 승인",
        report_rejected: "보고서 반려",
      };
      return map[String(s || "")] || String(s || "-");
    },

    offsiteStatusBadge(s: unknown): string {
      // 상태 → ds-label 색 변형 (labels.md 의미색: 대기=yellow·승인=green·반려=red·취소=gray)
      const map: Record<string, string> = {
        pending: "ds-label--yellow",
        approved: "ds-label--green",
        rejected: "ds-label--red",
        cancelled: "ds-label--gray",
        report_pending: "ds-label--yellow",
        report_approved: "ds-label--green",
        report_rejected: "ds-label--red",
      };
      return map[String(s || "")] || "ds-label--gray";
    },

    /* ──────── 외근 관리 — [보기] 상세 팝업 결재(승인/반려/취소/보고서) + 일괄 결재 ──────── */
    showOffsiteDetail: false,
    showOffsiteRejectModal: false,
    showOffsiteCancelModal: false,
    offsiteSelectedRecord: null as Record<string, unknown> | null,
    offsiteHistory: [] as Array<{
      action: string;
      actorId: string;
      actorName: string;
      at: string;
      reason?: string;
    }>,
    offsiteRejectForm: { mode: "request" as "request" | "report", reason: "", ids: [] as string[] },
    offsiteCancelForm: { reason: "" },
    /** 외근 일괄 결재 선택 id (스펙 §4) — 대기/보고서 대기 단일 단계만 동시 선택. */
    selectedOffsiteRecIds: [] as string[],

    /** 「2026.05.30. 22:00 ~ 06:00」 형식 — 변경 사항(스펙) 적용. */
    offsiteWorkRange(r: Record<string, unknown>): string {
      // 근무시간 표기 간결화 — 날짜 1회 + HH:mm (중복 날짜·초 제거로 열 폭 축소 → 관리 열 확보)
      // 날짜는 화면 형식 「2026.05.30.」 — 근무일(date 컬럼 「YYYY-MM-DD」)은 시간대 변환 없이 옮긴다
      const date = r.workDate ? Alpine.store("hereby").formatDate(r.workDate as string | Date) : "";
      const s = String(r.startTime || "").slice(0, 5);
      const e = String(r.endTime || "").slice(0, 5);
      if (!(date && s && e)) {
        return "-";
      }
      // 익일 퇴근(종료 ≤ 시작)이면 "(익일 퇴근)" 접미 — 근무일 열과 동일 규칙.
      return `${date} ${s} ~ ${e}${this.nextDayWorkSuffix(r as { startTime?: string | null; endTime?: string | null })}`;
    },

    /** 외근/연장 항목이 자정을 넘겨 끝나면(종료 ≤ 시작) "(익일 퇴근)" 접미. */
    nextDayWorkSuffix(item: { startTime?: string | null; endTime?: string | null } | null): string {
      if (!(item?.startTime && item?.endTime)) {
        return "";
      }
      return String(item.endTime) <= String(item.startTime) ? "(익일 퇴근)" : "";
    },

    async openOffsiteDetail(r: Record<string, unknown>) {
      this.offsiteSelectedRecord = r;
      this.offsiteHistory = [];
      this.showOffsiteDetail = true;
      // 결재선 패널 데이터 — 상세를 연 뒤 한 번 읽는다
      this.loadDetailApproval("offsite_work", r.id as string);
      // 상태별 '내역'(결재자·일시·사유)을 auditLog 이력에서 조회 (스펙 §3)
      try {
        const result = await this.sdk.offsiteWork.history(r.id as string);
        this.offsiteHistory = result?.entries || [];
      } catch {
        this.offsiteHistory = [];
      }
    },

    /** 결재문서(서식) 미리보기 모달 상태 — [결재문서 보기] → 미리보기 → [인쇄하기] → 인쇄 */
    formPreview: { open: false, html: "", title: "" },
    /** 자격증/면허 파일 보기 팝업 — kind: image(<img>) | file(<iframe>) */
    certFilePreview: {
      open: false,
      title: "",
      blobUrl: "",
      kind: "file" as "image" | "file",
      loading: false,
      fs: false,
    },
    certFileFsExitAt: 0,
    /** 문서 양식 전체 화면 — 띄운 문서 HTML · 제목 · 켜짐 */
    docTplFs: { html: "", title: "", on: false },

    /** 결재 건을 서식(휴가신청서·외근신청서)으로 채워 미리보기 모달에 띄운다.
     *  각 화면의 권한 체계를 따른다: 휴가 leave:read · 외근 offsiteWork:read */
    async openFormPreview(kind: "leave" | "offsite", recordId: string) {
      try {
        const { html, templateName } =
          kind === "leave"
            ? await this.sdk.leaveRequest.renderForm(recordId)
            : await this.sdk.offsiteWork.renderForm(recordId);
        this.formPreview = { open: true, html, title: templateName };
      } catch (err) {
        this.notify(
          `결재문서를 불러오지 못했어요: ${err instanceof Error ? err.message : String(err)}`,
          "error",
        );
      }
    },

    /** 미리보기 화면의 [인쇄하기] — 브라우저 인쇄 대화상자로 넘어간다. */
    printFormPreview() {
      if (this.formPreview.html) {
        printHtmlDocument(this.formPreview.html);
      }
    },

    approveOffsiteRequest(r: Record<string, unknown>) {
      // 브라우저 confirm 대신 커스텀 확인 모달(공용 showConfirm) 사용 — 결재선: 다음 결재자 · 대신 처리 안내 한 줄
      this.confirmTitle = this.approvalRowOf(r) === "delegate" ? "외근 대신 승인" : "외근 승인";
      this.confirmMessage = `선택한 외근 신청을 승인할까요?\n${approvalDecisionHint(this.approvalStateOf(r), "approved")}`;
      this.confirmLabel = "승인";
      this.confirmAction = async () => {
        this.showOffsiteDetail = false;
        await this.runApprovalDecision(
          "offsite_work",
          [r.id as string],
          () => this.sdk.offsiteWork.approve([r.id as string]),
          () => this.loadOffsiteRecords(),
          "외근을 승인했어요.",
        );
      };
      this.showConfirm = true;
    },

    approveOffsiteReport(r: Record<string, unknown>) {
      this.confirmTitle = "보고서 승인";
      this.confirmMessage = "선택한 외근 보고서를 승인할까요? 승인 시 근태에 반영됩니다.";
      this.confirmLabel = "승인";
      this.confirmAction = async () => {
        try {
          await this.sdk.offsiteWork.approveReport([r.id as string]);
          this.showOffsiteDetail = false;
          await this.loadOffsiteRecords();
        } catch (err) {
          this.notify(
            `보고서 승인 실패: ${err instanceof Error ? err.message : String(err)}`,
            "error",
          );
        }
      };
      this.showConfirm = true;
    },

    /** "반려" 버튼 — `mode` 가 "request" 면 외근 신청 반려, "report" 면 보고서 반려. */
    openOffsiteReject(r: Record<string, unknown>, mode: "request" | "report") {
      this.offsiteSelectedRecord = r;
      this.offsiteRejectForm = { mode, reason: "", ids: [r.id as string] };
      this.showOffsiteRejectModal = true;
    },

    async confirmOffsiteReject() {
      const ids = this.offsiteRejectForm.ids;
      const reason = this.offsiteRejectForm.reason.trim();
      if (!(ids.length > 0 && reason)) {
        return;
      }
      if (this.offsiteRejectForm.mode !== "report") {
        // 신청 반려는 결재선 안 — 공용 처리(잠금 · 토스트 · 재조회 · 결재함 · iframe 신호 · 오류 안내)
        const ok = await this.runApprovalDecision(
          "offsite_work",
          ids,
          () => this.sdk.offsiteWork.reject(ids, reason),
          async () => {
            this.selectedOffsiteRecIds = [];
            await this.loadOffsiteRecords();
          },
          "외근을 반려했어요.",
        );
        if (ok) {
          this.showOffsiteRejectModal = false;
          this.showOffsiteDetail = false;
        }
        return;
      }
      try {
        // rejectReport 는 단건 API → 선택 건을 순회 (일괄 반려 동일 사유 적용)
        await Promise.all(ids.map((id) => this.sdk.offsiteWork.rejectReport(id, reason)));
        this.showOffsiteRejectModal = false;
        this.showOffsiteDetail = false;
        this.selectedOffsiteRecIds = [];
        await this.loadOffsiteRecords();
      } catch (err) {
        showError(`반려 실패: ${err instanceof Error ? err.message : String(err)}`);
      }
    },

    openOffsiteCancel(r: Record<string, unknown>) {
      this.offsiteSelectedRecord = r;
      this.offsiteCancelForm = { reason: "" };
      this.showOffsiteCancelModal = true;
    },

    async confirmOffsiteCancel() {
      const r = this.offsiteSelectedRecord;
      if (!(r && this.offsiteCancelForm.reason.trim())) {
        return;
      }
      try {
        await this.sdk.offsiteWork.cancelApproved(
          r.id as string,
          this.offsiteCancelForm.reason.trim(),
        );
        this.showOffsiteCancelModal = false;
        this.showOffsiteDetail = false;
        await this.loadOffsiteRecords();
      } catch (err) {
        showError(`취소 실패: ${err instanceof Error ? err.message : String(err)}`);
      }
    },

    offsiteAuditActionLabel(action: string): string {
      const map: Record<string, string> = {
        request: "신청",
        approve: "승인",
        reject: "반려",
        cancel_request: "신청 취소",
        cancel_approved: "취소",
        submit_report: "보고서 제출",
        approve_report: "보고서 승인",
        reject_report: "보고서 반려",
      };
      return map[action] || action;
    },

    /** 보기 모달 — 특정 처리 액션의 최신 이력 1건 (결재자/일시/사유). 스펙 §3 상태별 내역. */
    offsiteAudit(action: string): { actorName: string; at: string; reason?: string } | null {
      const hits = this.offsiteHistory.filter((e) => e.action === action);
      return hits.length ? hits[hits.length - 1] : null;
    },

    /* ── 외근 일괄 결재 (스펙 §4) — 대기/보고서 대기 단일 단계만 동시 처리 ── */

    /** 현재 선택된 건들의 단계(pending | report_pending). 빈 선택이면 "". */
    get offsiteRecSelectedStep(): string {
      return offsiteSelectedStep(this.offsiteRecords, this.selectedOffsiteRecIds);
    },

    /** 행 선택 가능 — 대기/보고서 대기만, 이미 선택된 단계와 동일해야 (단일 단계 규칙). 결재선: 대기 건은 내 차례(대신 처리 · 예전 방식 포함)만. */
    offsiteRecRowSelectable(r: Record<string, unknown>): boolean {
      if (r.status === "pending" && !this.approvalCanAct(r)) {
        return false;
      }
      return offsiteRowSelectable(r, this.offsiteRecords, this.selectedOffsiteRecIds);
    },

    /** 헤더 체크박스 — 현재 페이지의 선택 가능 행(단일 단계) 전체 선택/해제. */
    toggleAllOffsiteRec(ev: Event) {
      const checked = (ev.target as HTMLInputElement).checked;
      this.selectedOffsiteRecIds = checked
        ? offsiteSelectAllIds(this.offsiteRecords, this.offsiteRecSelectedStep).filter((id) => {
            const row = this.offsiteRecords.find((r) => r.id === id);
            return !row || row.status !== "pending" || this.approvalCanAct(row);
          })
        : [];
    },

    /** 일괄 승인 — 대기→승인 / 보고서 대기→보고서 승인 (공용 확인 모달). */
    bulkApproveOffsiteRec() {
      const ids = [...this.selectedOffsiteRecIds];
      if (ids.length === 0) {
        return;
      }
      const isReport = this.offsiteRecSelectedStep === "report_pending";
      this.confirmTitle = isReport ? "외근 보고서 일괄 승인" : "외근 일괄 승인";
      this.confirmMessage = isReport
        ? `선택한 ${ids.length}건의 외근 보고서를 승인할까요? 승인 시 근태에 반영됩니다.`
        : `선택한 ${ids.length}건의 외근 신청을 승인할까요?`;
      this.confirmLabel = "승인";
      this.confirmAction = async () => {
        if (!isReport) {
          // 신청 승인은 결재선 안 — 공용 처리(내 차례 아닌 건은 서버가 건너뛰고 message 로 알려 준다)
          await this.runApprovalDecision(
            "offsite_work",
            ids,
            () => this.sdk.offsiteWork.approve(ids),
            async () => {
              this.selectedOffsiteRecIds = [];
              await this.loadOffsiteRecords();
            },
          );
          return;
        }
        try {
          await this.sdk.offsiteWork.approveReport(ids);
          this.selectedOffsiteRecIds = [];
          await this.loadOffsiteRecords();
        } catch (err) {
          this.notify(
            `일괄 승인 실패: ${err instanceof Error ? err.message : String(err)}`,
            "error",
          );
        }
      };
      this.showConfirm = true;
    },

    /** 일괄 반려 — 반려 사유 모달을 선택 건 대상으로 연다 (동일 사유 적용). */
    openOffsiteRejectBulk() {
      const ids = [...this.selectedOffsiteRecIds];
      if (ids.length === 0) {
        return;
      }
      const mode = this.offsiteRecSelectedStep === "report_pending" ? "report" : "request";
      this.offsiteSelectedRecord = null;
      this.offsiteRejectForm = { mode, reason: "", ids };
      this.showOffsiteRejectModal = true;
    },

    /* ── 전체화면 — 스케줄러·시트처럼 넓은 화면에서 LNB·페이지 헤더를 걷어내고 본문만 채운다.
       브라우저 전체화면(Fullscreen API)도 함께 켠다. 대상 화면은 fullscreenAvailable() 하나가 정한다 —
       버튼 노출·단축키·탭 전환 시 자동 해제가 같은 판정을 쓴다.

       ESC 순서: 열린 창(모달·다이얼로그·iframe 모달)이 있으면 그 창이 먼저 닫히고, 전체화면은 **가장 마지막**에 끝난다.
       - 앱 셸 ESC 핸들러는 hasOpenOverlay() 면 손대지 않는다(창의 ESC 핸들러가 같은 keydown 을 받아 창을 닫는다).
       - 브라우저 전체화면에서는 ESC 를 브라우저가 가로채 페이지에 주지 않으므로, Chromium 에서는 키보드 잠금
         (navigator.keyboard.lock(["Escape"]))으로 ESC 를 페이지가 받게 한다(길게 누르면 브라우저가 빠져나온다).
       - 잠금이 없는 브라우저에서 ESC 로 브라우저 전체화면이 끝나도, 창이 열려 있었으면 앱 전체화면은 유지한다. */
    isFullscreen: false,
    fullscreenListenersBound: false,
    fullscreenAvailable(): boolean {
      if (this.activeTab === "scheduler") {
        return true;
      }
      return this.activeTab === "embed" && this.currentEmbed === "sheets";
    },
    /**
     * 지금 화면 위에 열린 창이 있는가 — 앱 상태(유형·기준 관리 팝업 · iframe 모달 · 확인창)와 DOM(보이는 모달 배경 ·
     * role=dialog)을 함께 본다. DOM 검사는 스케줄러(격리 컴포넌트)·공용 다이얼로그 스토어처럼 앱 상태 밖의 창을 잡는다.
     */
    hasOpenOverlay(): boolean {
      if (
        this.showTypeManagerModal ||
        this.tmModalOpen ||
        this.showConfirm ||
        this.closeConfirm.open
      ) {
        return true;
      }
      // 열린 창 감지용 선택자(마크업이 아님) — 레거시 .ds-mini-modal 도 「열린 창」으로 세야 ESC 순서가 맞다
      const nodes = document.querySelectorAll(
        '.ds-modal-backdrop, .ds-mini-modal, [role="dialog"], [role="alertdialog"]', // ds-modal-exempt: 열린 창 감지 선택자
      );
      for (const el of Array.from(nodes)) {
        if (el.getClientRects().length > 0) {
          return true;
        }
      }
      return false;
    },
    toggleFullscreen() {
      if (this.isFullscreen) {
        this.exitFullscreen();
      } else {
        this.enterFullscreen();
      }
    },
    enterFullscreen() {
      if (!this.fullscreenAvailable()) {
        return;
      }
      this.isFullscreen = true;
      const el = document.documentElement;
      if (!document.fullscreenElement && typeof el.requestFullscreen === "function") {
        // 브라우저가 거부해도(권한·iframe 정책) 앱 셸만 걷어낸 전체화면은 유지된다
        el.requestFullscreen()
          .then(() => this.lockEscapeKey())
          .catch(() => {
            /* 브라우저 거부(권한·정책) — 앱 셸 전체화면만으로 충분하다 */
          });
      }
    },
    exitFullscreen() {
      this.isFullscreen = false;
      this.unlockEscapeKey();
      if (document.fullscreenElement && typeof document.exitFullscreen === "function") {
        document.exitFullscreen().catch(() => {
          /* 이미 나온 상태 — 무시 */
        });
      }
    },
    /** Chromium 키보드 잠금 — 브라우저 전체화면에서 ESC 를 페이지가 먼저 받는다(창 닫기 → 마지막에 전체화면 종료). */
    lockEscapeKey() {
      // biome-ignore lint/suspicious/noExplicitAny: Keyboard Lock API 는 아직 lib.dom 에 없다
      const kb = (navigator as any).keyboard;
      if (kb && typeof kb.lock === "function") {
        kb.lock(["Escape"]).catch(() => {
          /* 미지원·거부 — 브라우저가 ESC 를 가져가는 기본 동작으로 둔다 */
        });
      }
    },
    unlockEscapeKey() {
      // biome-ignore lint/suspicious/noExplicitAny: Keyboard Lock API 는 아직 lib.dom 에 없다
      const kb = (navigator as any).keyboard;
      if (kb && typeof kb.unlock === "function") {
        kb.unlock();
      }
    },
    /** 앱 셸의 ESC — 열린 창이 있으면 그 창의 몫, 없을 때만 전체화면을 끈다. */
    handleFullscreenEscape() {
      if (!this.isFullscreen || this.hasOpenOverlay()) {
        return;
      }
      this.exitFullscreen();
    },
    /** 단축키 Ctrl/⌘+Shift+F — 대상 화면에서 전체화면 켜기/끄기. 입력 중(input·textarea·contenteditable)에는 반응하지 않는다. */
    handleFullscreenShortcut(e: KeyboardEvent): boolean {
      if (!((e.ctrlKey || e.metaKey) && e.shiftKey) || (e.key || "").toLowerCase() !== "f") {
        return false;
      }
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.isContentEditable ||
          target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT")
      ) {
        return false;
      }
      if (!this.fullscreenAvailable()) {
        return false;
      }
      e.preventDefault();
      this.toggleFullscreen();
      return true;
    },
    /** 브라우저 전체화면이 끝났을 때 — 창이 열려 있었으면 앱 전체화면은 남긴다(창 → 전체화면 순서 보장). */
    onBrowserFullscreenChange() {
      if (document.fullscreenElement) {
        return;
      }
      this.unlockEscapeKey();
      if (this.isFullscreen && !this.hasOpenOverlay()) {
        this.isFullscreen = false;
      }
    },

    async navigateGuidance(action: AiNavigationAction, signal: AbortSignal) {
      await navigateAdminGuidance(this, action, signal);
    },

    switchTab(tabId: string) {
      if (this.orgEditMode) {
        this.confirmLeaveOrgEdit(() => this.switchTab(tabId));
        return;
      }
      // 병동 페이지 관리자는 두 화면 밖으로 못 간다 — 주소창에 다른 해시를 쳐도 되돌린다.
      // 서버가 이미 막지만, 막힌 화면이 빈 표로 뜨는 것보다 아예 안 가는 쪽이 낫다(PM-01).
      if (this.isRestrictedAdmin && !this.restrictedAdminTabs.includes(tabId)) {
        Alpine.store("toast")?.info?.(this.restrictedAdminNotice());
        return;
      }
      // 전체화면은 대상 화면(스케줄러·시트)에서만 — 다른 탭으로 가면 LNB·헤더를 되살린다
      if (this.isFullscreen) {
        this.exitFullscreen();
      }
      // 임베드 iframe 모달이 열린 채 탭을 벗어나면 부모 풀페이지 dim이 남을 수 있어,
      // 탭 전환 시 항상 해제한다(안전망). 새 모달이 열리면 postMessage로 다시 켜진다.
      this.tmModalOpen = false;
      // 시트 레일 FAB 비킴도 같은 안전망 — 레일이 열린 채 다른 탭으로 가면
      // FAB 만 왼쪽에 남는다. 레일이 다시 열리면 postMessage 로 재설정된다.
      this.sheetRailOpen = false;
      this.setSheetRailWidth(0);
      // 팝오버가 열린 채 탭을 벗어나면 채팅 FAB 이 사라진 채로 남는다 — 같은 안전망.
      this.sheetPopoverOpen = false;
      this.setSheetAiDockWidth(0);
      this.activeTab = tabId;
      if (tabId !== "esign") {
        this.stopESignPolling();
      }
      // 하위 키 — 라우터가 이 탭의 하위 키를 이미 들고 있으면(주소창 입력·북마크·popstate 로 `#embed/sheets` 처럼
      // 해시가 먼저 바뀐 경우) 그 값을 앱 상태에 옮기고 해시에도 남긴다. 예전엔 `#embed` 로 해시를 덮어쓰고
      // currentEmbed 를 갱신하지 않아 빈 임베드 셸이 떴다(MED-695). LNB·앱 내 이동은 앱 상태가 먼저라 그대로 쓴다.
      Alpine.store("router").navigateTab(tabId, this.subTabForRoute(tabId));
      // 직접 진입·메뉴 이동·뒤로가기가 같은 데이터 준비 경로를 사용한다.
      this.loadData();
      if (tabId === "settings") {
        if (!this.settingsLoaded) {
          this.loadSettingsData();
        }
        this.loadSettingsSubTabData(this.settingsSubTab);
      }
      if (tabId === "leave") {
        this.loadLeaveBalances();
        this.loadEntitlementRules();
        this.loadLeaveReasons();
      }
      if (tabId === "offsite") {
        this.loadOffsiteRecords();
        this.loadOffsiteTypes();
      }
      if (tabId === "overtime-types") {
        this.loadOvertimeTypes();
      }
      if (tabId === "esign") {
        this.startESignPolling();
      }
      if (tabId === "parameters") {
        this.loadOrganizationParameters();
      }
      if (tabId === "onboarding") {
        window.dispatchEvent(new Event("app-onboarding-refresh"));
      }
      if (tabId === "organization") {
        this.resetOrgTreeCollapsed();
      }
    },

    /**
     * switchTab 이 해시에 남길 하위 키 — 라우터(해시)가 같은 탭의 하위 키를 들고 있으면 앱 상태를 그것에 맞추고 돌려준다.
     * settings·embed 는 앱 상태(settingsSubTab · currentEmbed)가 정본이라 라우터 값을 받아 적고, organization·scheduler 는
     * 화면이 라우터 값을 직접 읽으므로 해시만 보존한다.
     */
    subTabForRoute(tabId: string): string | undefined {
      const router = Alpine.store("router");
      const fromRoute = router.tab === tabId;
      if (tabId === "settings") {
        if (fromRoute && router.settingsSub) {
          this.settingsSubTab = router.settingsSub;
        }
        return this.settingsSubTab;
      }
      if (tabId === "embed") {
        if (fromRoute && router.embedSub) {
          this.currentEmbed = router.embedSub;
        }
        return this.currentEmbed || undefined;
      }
      if (tabId === "organization") {
        return fromRoute && router.orgSub ? router.orgSub : undefined;
      }
      if (tabId === "scheduler") {
        return fromRoute && router.schedulerSub ? router.schedulerSub : undefined;
      }
      // 병동 관리는 고르던 병동(또는 개요)을 해시에 싣는다 — 새로고침해도 같은 화면
      if (tabId === "wards") {
        if (fromRoute && router.wardSub) {
          return router.wardSub;
        }
        return this.wardSelectionRestored ? this.encodeWardSelection() : undefined;
      }
      return undefined;
    },

    /* ── organization parameter management ── */
    orgParameters: [] as {
      id: string;
      namespace: string;
      key: string;
      type: "number" | "string" | "boolean";
      value: number | string | boolean | null;
      label: string;
      description: string | null;
      unit: string | null;
      updatedAt: string | Date;
    }[],
    orgParametersLoading: false,
    orgParameterNamespaceFilter: "",
    orgParameterShowForm: false,
    orgParameterShowHistory: false,
    orgParameterHistoryRows: [] as Record<string, unknown>[],
    orgParameterHistoryParamId: "",
    orgParameterForm: {
      id: "",
      namespace: "",
      key: "",
      type: "number" as "number" | "string" | "boolean",
      value: "" as string,
      label: "",
      description: "",
      unit: "",
      reason: "",
    },
    get orgParametersGrouped() {
      const filter = this.orgParameterNamespaceFilter.trim().toLowerCase();
      // biome-ignore lint/suspicious/noExplicitAny: Alpine `this` widens types in getters
      const params = this.orgParameters as any[];
      // biome-ignore lint/suspicious/noExplicitAny: filtered shape is the same
      const visible = params.filter((p: any) => {
        if (!filter) {
          return true;
        }
        return (
          (p.namespace as string).toLowerCase().includes(filter) ||
          (p.key as string).toLowerCase().includes(filter) ||
          (p.label as string).toLowerCase().includes(filter)
        );
      });
      // biome-ignore lint/suspicious/noExplicitAny: Alpine `this` widens types in getters
      const groups = new Map<string, any[]>();
      for (const p of visible) {
        const arr = groups.get(p.namespace) ?? [];
        arr.push(p);
        groups.set(p.namespace, arr);
      }
      return Array.from(groups.entries())
        .map(([namespace, items]) => ({ namespace, items }))
        .sort((a, b) => a.namespace.localeCompare(b.namespace));
    },
    async loadOrganizationParameters() {
      this.orgParametersLoading = true;
      try {
        const list = await this.sdk.organizationParameter.list({});
        this.orgParameters = list as typeof this.orgParameters;
      } catch (e) {
        // biome-ignore lint/suspicious/noConsole: surface load errors
        console.error("loadOrganizationParameters", e);
        this.orgParameters = [];
      } finally {
        this.orgParametersLoading = false;
      }
    },
    openCreateOrgParameter() {
      this.orgParameterForm = {
        id: "",
        namespace: "",
        key: "",
        type: "number",
        value: "",
        label: "",
        description: "",
        unit: "",
        reason: "",
      };
      this.orgParameterShowForm = true;
    },
    openEditOrgParameter(p: {
      id: string;
      namespace: string;
      key: string;
      type: "number" | "string" | "boolean";
      value: number | string | boolean | null;
      label: string;
      description: string | null;
      unit: string | null;
    }) {
      this.orgParameterForm = {
        id: p.id,
        namespace: p.namespace,
        key: p.key,
        type: p.type,
        value: p.value === null || p.value === undefined ? "" : String(p.value),
        label: p.label,
        description: p.description ?? "",
        unit: p.unit ?? "",
        reason: "",
      };
      this.orgParameterShowForm = true;
    },
    /**
     * Parse the form's free-text value into the typed value the API
     * expects. Boolean accepts "true"/"false"/"1"/"0"; number requires
     * a finite Number(); string passes through.
     */
    _parseOrgParameterValue(): number | string | boolean | null {
      const raw = this.orgParameterForm.value;
      if (this.orgParameterForm.type === "boolean") {
        const t = raw.trim().toLowerCase();
        if (t === "true" || t === "1") {
          return true;
        }
        if (t === "false" || t === "0") {
          return false;
        }
        showAlert("boolean 값은 true/false (또는 1/0)이어야 합니다.");
        return null;
      }
      if (this.orgParameterForm.type === "number") {
        const n = Number(raw);
        if (!Number.isFinite(n)) {
          showAlert("number 값은 유효한 숫자여야 합니다.");
          return null;
        }
        return n;
      }
      return raw;
    },
    async submitOrgParameter() {
      const value = this._parseOrgParameterValue();
      if (value === null && this.orgParameterForm.type !== "string") {
        return;
      }
      const f = this.orgParameterForm;
      try {
        if (f.id) {
          await this.sdk.organizationParameter.update({
            id: f.id,
            value: value ?? f.value,
            label: f.label,
            description: f.description.trim().length > 0 ? f.description : null,
            unit: f.unit.trim().length > 0 ? f.unit : null,
            reason: f.reason.trim().length > 0 ? f.reason : undefined,
          });
        } else {
          await this.sdk.organizationParameter.create({
            namespace: f.namespace.trim(),
            key: f.key.trim(),
            type: f.type,
            value: value ?? f.value,
            label: f.label,
            description: f.description.trim().length > 0 ? f.description : null,
            unit: f.unit.trim().length > 0 ? f.unit : null,
            reason: f.reason.trim().length > 0 ? f.reason : undefined,
          });
        }
        this.orgParameterShowForm = false;
        await this.loadOrganizationParameters();
      } catch (e) {
        showError(`저장 실패: ${e instanceof Error ? e.message : e}`);
      }
    },
    async deleteOrgParameter(id: string) {
      if (
        !(await askConfirm({
          message: "이 파라미터를 삭제하시겠습니까? (이력은 보존됩니다)",
          tone: "danger",
        }))
      ) {
        return;
      }
      try {
        await this.sdk.organizationParameter.delete({ id });
        await this.loadOrganizationParameters();
      } catch (e) {
        showError(`삭제 실패: ${e instanceof Error ? e.message : e}`);
      }
    },
    async openOrgParameterHistory(id: string) {
      this.orgParameterHistoryParamId = id;
      this.orgParameterShowHistory = true;
      try {
        this.orgParameterHistoryRows = await this.sdk.organizationParameter.history({
          parameterId: id,
          limit: 100,
        });
      } catch (e) {
        // biome-ignore lint/suspicious/noConsole: surface load errors
        console.error("openOrgParameterHistory", e);
        this.orgParameterHistoryRows = [];
      }
    },

    /* ── ward management methods ── */
    /** 병동 목록 표시 순서 — 순서(displayOrder) → 최신 생성순. 새 병동은 순서 0 이라 그 안에서 맨 위로 온다(「추가된 병동은 상단」 정책).
     *  loadWards 와 ensureWardOptions 가 같이 쓴다 — 한쪽만 정렬하면 팀 목록을 다시 읽을 때마다 왼쪽 병동 목록 순서가 바뀐다. */
    sortWardList(
      items: readonly { displayOrder?: number | null; createdAt?: string | Date }[],
    ): Record<string, unknown>[] {
      return items
        .map((w) => ({ ...w }) as Record<string, unknown>)
        .sort(
          (a, b) =>
            Number(a.displayOrder ?? 0) - Number(b.displayOrder ?? 0) ||
            new Date(b.createdAt as string | Date).getTime() -
              new Date(a.createdAt as string | Date).getTime(),
        );
    },

    async loadWards() {
      try {
        // 스케줄러 목록은 표의 「스케줄러」 칸에 함께 보인다 — 실패해도 병동 목록은 그린다(칸만 「-」)
        const [result] = await Promise.all([
          this.sdk.nurseScheduler.listWards(),
          this.loadScheduleGroups(),
        ]);
        // API returns { items, totalCount }; fall back to array-shape for older versions
        const items = Array.isArray(result) ? result : result?.items || [];
        this.wardList = this.sortWardList(items);
        // 병동 이름 · 병상 수 · 상태가 바뀌면 개요 표와 상세 요약도 다시 센다
        this.refreshWardSummaries();
      } catch (err) {
        showError(`병동 목록 불러오기 실패: ${err instanceof Error ? err.message : String(err)}`);
        this.wardList = [];
      } finally {
        this.loadedOnce.wards = true;
      }
    },

    openCreateWard() {
      this.wardFormMode = "create";
      this.wardForm = {
        id: "",
        code: "",
        name: "",
        description: "",
        isActive: true,
        wardCareType: "",
        linkedTeamId: "",
        bedCount: "",
        displayOrder: "",
      };
      this.wardFormOriginal = {
        name: "",
        description: "",
        isActive: true,
        wardCareType: "",
        linkedTeamId: "",
      };
      this.wardShowForm = true;
    },

    /**
     * 병동 관리 표 안 수정(편집 아이콘) — 팝업 대신 그 줄 칸이 입력칸으로 바뀐다(2026-09-14 사용자 결정).
     * 한 번에 한 줄만. 다른 줄을 고치던 중 바뀐 내용이 있으면 버릴지 먼저 묻는다. 숫자 칸은 입력 그대로 문자열로 든다.
     */
    async startInlineWardEdit(wardId: string) {
      if (this.wardInlineEdit.id && this.wardInlineEdit.id !== wardId && this.wardInlineDirty()) {
        const ok = await askConfirm({
          message: "수정 중인 병동이 있어요.\n저장하지 않고 다른 병동을 수정할까요?",
          confirmLabel: "다른 병동 수정",
          cancelLabel: "계속 수정",
        });
        if (!ok) {
          return;
        }
      }
      const ward = (this.wardList as Record<string, unknown>[]).find((w) => w.id === wardId);
      if (!ward) {
        return;
      }
      const config = (ward.schedulingConfig ?? {}) as { wardCareType?: string; bedCount?: number };
      const form = {
        code: String(ward.code ?? ""),
        name: String(ward.name ?? ""),
        description: String(ward.description ?? ""),
        linkedTeamId: this.allTeams.find((t) => t.wardId === wardId)?.id ?? "",
        wardCareType: config.wardCareType ?? "",
        bedCount: config.bedCount == null ? "" : String(config.bedCount),
        displayOrder: String(ward.displayOrder ?? 0),
        isActive: ward.isActive !== false,
      };
      this.wardInlineEdit = { id: wardId, form, original: { ...form } };
    },

    cancelInlineWardEdit() {
      this.wardInlineEdit = {
        id: "",
        form: { ...EMPTY_WARD_INLINE_FORM },
        original: { ...EMPTY_WARD_INLINE_FORM },
      };
    },

    /** 표 안 수정에서 바뀐 게 있는지 — [저장] 활성 조건 */
    wardInlineDirty(): boolean {
      const { form, original } = this.wardInlineEdit;
      return JSON.stringify(form) !== JSON.stringify(original);
    },

    /** 저장 성공이면 true — 병동 정보 저장(saveWardInfo)이 이어서 책임자를 저장할지 판단한다 */
    async saveInlineWardEdit(): Promise<boolean> {
      const { id, form, original } = this.wardInlineEdit;
      if (!id || this.wardSaving || !this.wardInlineDirty()) {
        return false;
      }
      const code = form.code.trim();
      const name = form.name.trim();
      if (!(code && name)) {
        showAlert("병동 코드와 병동명을 입력해 주세요.");
        return false;
      }
      const bedCount = String(form.bedCount).trim() === "" ? null : Number(form.bedCount);
      const displayOrder = String(form.displayOrder).trim() === "" ? 0 : Number(form.displayOrder);
      if (
        (bedCount !== null && (!Number.isInteger(bedCount) || bedCount < 0)) ||
        !Number.isInteger(displayOrder) ||
        displayOrder < 0
      ) {
        showAlert("병상 수와 순서는 0 이상의 정수로 입력해 주세요.");
        return false;
      }
      this.wardSaving = true;
      try {
        await this.sdk.nurseScheduler.updateWard({
          id,
          code,
          name,
          description: form.description.trim(),
          displayOrder,
          isActive: form.isActive,
          // 연결 팀은 바뀐 경우에만 보낸다(빈 값 = 연결 끊기)
          ...(form.linkedTeamId !== original.linkedTeamId
            ? { linkedTeamId: form.linkedTeamId || null }
            : {}),
        });
        const careChanged = form.wardCareType !== original.wardCareType;
        // 병상 수는 비우면 바꾸지 않는다(설정 스키마가 비우기를 받지 않음)
        const bedChanged = bedCount !== null && String(form.bedCount) !== String(original.bedCount);
        if (careChanged || bedChanged) {
          await this.sdk.nurseScheduler.updateWardSettings({
            wardId: id,
            settings: {
              ...(careChanged
                ? { wardCareType: (form.wardCareType || null) as "general" | "rehab" | null }
                : {}),
              ...(bedChanged ? { bedCount: bedCount as number } : {}),
            },
          });
        }
        this.cancelInlineWardEdit();
        // 연결 팀이 바뀌면 팀 관리 트리 · 병동 현황도 달라진다 — 팀 목록도 다시 부른다
        await Promise.all([this.loadWards(), this.loadTeams()]);
        return true;
      } catch (err) {
        // 코드 중복 · 연결 팀 충돌처럼 서버가 한글로 알려 주는 입력 오류는 그대로, 그 밖은 공통 정책 팝업
        const errCode = (err as { data?: { code?: string } } | null)?.data?.code;
        if (err instanceof Error && errCode === "BAD_REQUEST") {
          showAlert(err.message);
        } else {
          this.showCommonError();
        }
        return false;
      } finally {
        this.wardSaving = false;
      }
    },

    /** 수정 모달의 [저장] 활성화 조건 — 원본 대비 변경이 있으면 true */
    wardFormDirty(): boolean {
      const o = this.wardFormOriginal;
      return (
        this.wardForm.name.trim() !== o.name.trim() ||
        (this.wardForm.description || "").trim() !== (o.description || "").trim() ||
        this.wardForm.isActive !== o.isActive ||
        this.wardForm.wardCareType !== o.wardCareType ||
        this.wardForm.linkedTeamId !== o.linkedTeamId
      );
    },

    async submitWardForm() {
      if (!this.wardForm.name.trim()) {
        return;
      }
      this.wardSaving = true;
      try {
        // "" = 미지정 → null 로 보내 키 삭제(서버 null-unset 규약).
        const careType = (this.wardForm.wardCareType || null) as "general" | "rehab" | null;
        if (this.wardFormMode === "edit") {
          // 연결 팀은 바뀐 경우에만 보낸다(빈 값 = 연결 끊기)
          const linkedChanged = this.wardForm.linkedTeamId !== this.wardFormOriginal.linkedTeamId;
          await this.sdk.nurseScheduler.updateWard({
            id: this.wardForm.id,
            name: this.wardForm.name.trim(),
            description: this.wardForm.description.trim() || undefined,
            isActive: this.wardForm.isActive !== false,
            ...(linkedChanged ? { linkedTeamId: this.wardForm.linkedTeamId || null } : {}),
          });
          await this.sdk.nurseScheduler.updateWardSettings({
            wardId: this.wardForm.id,
            settings: { wardCareType: careType },
          });
        } else {
          const bedCount =
            String(this.wardForm.bedCount).trim() === "" ? null : Number(this.wardForm.bedCount);
          const displayOrder =
            String(this.wardForm.displayOrder).trim() === ""
              ? 0
              : Number(this.wardForm.displayOrder);
          if (
            (bedCount !== null && (!Number.isInteger(bedCount) || bedCount < 0)) ||
            !Number.isInteger(displayOrder) ||
            displayOrder < 0
          ) {
            showAlert("병상 수와 순서는 0 이상의 정수로 입력해 주세요.");
            return;
          }
          const result = (await this.sdk.nurseScheduler.createWard({
            // 코드는 비우면 자동(WARD_숫자). 조직 안에서 겹치면 서버가 한글로 알린다
            code: this.wardForm.code.trim() || `WARD_${Date.now()}`,
            name: this.wardForm.name.trim(),
            description: this.wardForm.description.trim() || undefined,
            isActive: this.wardForm.isActive !== false,
            linkedTeamId: this.wardForm.linkedTeamId || undefined,
            displayOrder,
          })) as { data?: { id?: string } };
          if (result?.data?.id && (careType || bedCount !== null)) {
            await this.sdk.nurseScheduler.updateWardSettings({
              wardId: result.data.id,
              settings: {
                ...(careType ? { wardCareType: careType } : {}),
                ...(bedCount !== null ? { bedCount } : {}),
              },
            });
          }
        }
        this.wardShowForm = false;
        this.wardForm = {
          id: "",
          code: "",
          name: "",
          description: "",
          isActive: true,
          wardCareType: "",
          linkedTeamId: "",
          bedCount: "",
          displayOrder: "",
        };
        // 연결 팀이 바뀌면 팀 관리 트리 · 병동 현황 · 연결 팀 열도 달라진다 — 팀 목록도 다시 부른다
        await Promise.all([this.loadWards(), this.loadTeams()]);
      } catch (err) {
        // 연결 팀 충돌처럼 서버가 한글로 알려 주는 입력 오류는 그대로, 그 밖은 공통 정책 팝업(모달 위, z-60)
        const code = (err as { data?: { code?: string } } | null)?.data?.code;
        if (err instanceof Error && code === "BAD_REQUEST") {
          showAlert(err.message);
        } else {
          this.showCommonError();
        }
      } finally {
        this.wardSaving = false;
      }
    },

    /**
     * 병동 폼 「연결 팀」 선택지 — 팀 트리 순서(하위 팀 「— 」 들여쓰기), 필터 없는 전체 팀(allTeams) 기준.
     * 운영 중인 팀만, 지금 연결된 팀은 비활성이어도 남긴다. 팀 하나에는 병동 하나만이라 다른 병동에 연결된
     * 팀은 그 병동 이름과 함께 고를 수 없게 보인다(삭제된 병동을 가리키는 연결은 빈 연결로 본다 — 서버와 같음).
     */
    wardTeamOptions(
      // 병동 추가 팝업은 인자 없이, 표 안 수정은 그 줄 병동 id · 원래 연결 팀 id 를 넘긴다
      wardIdArg?: string,
      currentTeamIdArg?: string,
    ): {
      id: string;
      label: string;
      linkedWardName: string;
      isDepartment: boolean;
    }[] {
      const selfWardId = wardIdArg ?? this.wardForm.id;
      const current = currentTeamIdArg ?? this.wardForm.linkedTeamId;
      const wardNames = new Map(
        (this.wardList as Record<string, unknown>[]).map((w) => [
          w.id as string,
          (w.name as string) || "",
        ]),
      );
      const children = new Map<string, OrgTeamItem[]>();
      for (const team of this.allTeams) {
        const parentId = team.parentTeamId || "";
        const siblings = children.get(parentId) ?? [];
        siblings.push(team);
        children.set(parentId, siblings);
      }
      for (const siblings of children.values()) {
        siblings.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", "ko"));
      }
      const options: {
        id: string;
        label: string;
        linkedWardName: string;
        isDepartment: boolean;
      }[] = [];
      const walk = (parentId: string, depth: number) => {
        for (const team of children.get(parentId) ?? []) {
          if (team.isActive !== false || team.id === current) {
            const linkedWardName =
              team.wardId && team.wardId !== selfWardId ? (wardNames.get(team.wardId) ?? "") : "";
            options.push({
              id: team.id,
              label: "— ".repeat(depth) + (team.name ?? ""),
              linkedWardName,
              // 부서(최상위 + 하위 팀 있음)는 연결할 수 없다 — 트리 맥락을 위해 목록에는 비활성으로 남긴다
              isDepartment: isDepartmentTeam(this.allTeams, team.id),
            });
          }
          walk(team.id, depth + 1);
        }
      };
      walk("", 0);
      return options;
    },

    /** 병동에 연결된 팀이 부서인지 — 새 연결은 막았지만, 예전에 연결됐거나 연결 뒤 하위 팀이 생긴 경우를 표에서 알린다 */
    wardLinkedTeamIsDepartment(wardId: string): boolean {
      const team = this.allTeams.find((t) => t.wardId === wardId);
      return !!team && isDepartmentTeam(this.allTeams, team.id);
    },

    /** 병동 삭제 — 확인창에 무엇이 바뀌고 무엇이 남는지 적는다(연결 해제 · 근무표 · 남는 배정). 잠깐 멈추는 건 비활성 */
    async deleteWard(wardId: string) {
      const ward = (this.wardList as Record<string, unknown>[]).find((w) => w.id === wardId);
      const wardName = String(ward?.name || "병동");
      const linked = this.wardLinkedTeam(wardId);
      const assigned = this.wardHeadcountOf(wardId);
      // 되돌릴 수 없다는 문장은 굵게(emphasis) — 화면에 삭제한 병동을 되살리는 기능이 없다. 상세 병동 정보의 notice 와 같은 문구
      const irreversible = "병동을 삭제하면 되돌릴 수 없어요.";
      const lines = [`「${wardName}」 병동을 삭제할까요?`, irreversible];
      if (linked) {
        lines.push(`연결 팀(${linked.name})과의 연결이 끊겨요.`);
      }
      lines.push("이 병동의 스케줄러와 근무표를 더는 열 수 없어요.");
      if (assigned) {
        lines.push(`배정된 ${assigned}명은 병동 없음으로 보여요.`);
      }
      lines.push("잠시 멈추려면 삭제 대신 「비활성」으로 바꿔 주세요.");
      const ok = await askConfirm({
        message: lines.join("\n"),
        emphasis: irreversible,
        confirmLabel: "병동 삭제",
        tone: "danger",
      });
      if (!ok) {
        return;
      }
      try {
        await this.sdk.nurseScheduler.deleteWard(wardId);
        // 그 병동을 고치던 중이었으면(상세 병동 정보 · 개요 표 안 수정) 저장 안 한 내용은 버린다
        if (this.wardInlineEdit.id === wardId) {
          this.cancelWardInfoEdit();
        }
        if (this.wardSelected.type === "ward" && this.wardSelected.id === wardId) {
          this.selectWardOverview();
        }
        // 삭제하면 연결 팀도 풀린다 — 팀 목록까지 다시 부른다
        await Promise.all([this.loadWards(), this.loadTeams()]);
      } catch (_err) {
        this.showCommonError();
      }
    },

    /* ── 스케줄러(스케줄 그룹) 관리 — 병동 관리 표 「스케줄러」 칸 → 병동별 목록 팝업 → 추가/수정 폼 ──
       여기서는 이름 · 코드 · 근무 장소 · 설명 · 포함 직군 · 표시 순서 · 활성만 다룬다. 교대 매핑 · 교육 정책 · 지원 근무형태는
       스케줄러 화면의 설정에서 고친다. 기본 스케줄러(병동마다 하나)는 지우거나 비활성으로 못 바꾼다(서버도 막는다). */
    async loadScheduleGroups() {
      try {
        // 병동 표 · 팀 모달 · 배정 줄의 선택지 — 배경 조회라 권한이 없어도 팝업을 띄우지 않는다
        const result = await this.sdk.nurseScheduler.listScheduleGroups(undefined, {
          background: true,
        });
        this.scheduleGroupList = (result?.items ?? []).map((g) => ({ ...g }));
      } catch {
        // 못 읽으면(권한 없음 등) 병동 표의 스케줄러 칸은 「-」, 배정 줄에서는 스케줄러를 고를 수 없다
        this.scheduleGroupList = [];
      }
    },

    /** 병동의 스케줄러 — 서버 정렬 그대로(기본이 맨 앞, 그다음 표시 순서 · 이름) */
    wardScheduleGroups(wardId: string): ScheduleGroupItem[] {
      return this.scheduleGroupList.filter((g) => g.wardId === wardId);
    },

    /** 병동 표 「스케줄러」 칸 — 「기본 · 요양보호사 (2)」. 비활성은 이름 뒤에 (비활성). 없으면 빈 문자열 */
    wardSchedulerSummary(wardId: string): string {
      const groups = this.wardScheduleGroups(wardId);
      if (groups.length === 0) {
        return "";
      }
      const names = groups.map((g) => g.name + (g.isActive ? "" : "(비활성)"));
      return `${names.join(" · ")} (${groups.length})`;
    },

    openWardSchedulers(wardId: string) {
      this.wardSchedulers = { open: true, wardId };
    },

    closeWardSchedulers() {
      this.wardSchedulers = { open: false, wardId: "" };
    },

    wardSchedulersWardName(): string {
      const ward = (this.wardList as Record<string, unknown>[]).find(
        (w) => w.id === this.wardSchedulers.wardId,
      );
      return (ward?.name as string) || "";
    },

    jobCategoryLabel(value: string): string {
      return JOB_CATEGORY_LABELS[value as JobCategory] ?? value;
    },

    openCreateScheduler() {
      this.schedulerFormMode = "create";
      this.schedulerForm = { ...emptySchedulerForm(), wardId: this.wardSchedulers.wardId };
      this.schedulerFormOriginal = { ...emptySchedulerForm(), wardId: this.wardSchedulers.wardId };
      this.schedulerShowForm = true;
    },

    openEditScheduler(group: ScheduleGroupItem) {
      this.schedulerFormMode = "edit";
      const form = {
        id: group.id,
        wardId: group.wardId,
        name: group.name,
        code: group.code ?? "",
        location: group.location ?? "",
        description: group.description ?? "",
        displayOrder: String(group.displayOrder ?? 0),
        isActive: group.isActive !== false,
        isDefault: !!group.isDefault,
        jobCategories: [...(group.jobCategories ?? [])] as string[],
      };
      this.schedulerForm = form;
      this.schedulerFormOriginal = { ...form, jobCategories: [...form.jobCategories] };
      this.schedulerShowForm = true;
    },

    toggleSchedulerJobCategory(value: string) {
      const list = this.schedulerForm.jobCategories;
      const idx = list.indexOf(value);
      if (idx >= 0) {
        list.splice(idx, 1);
      } else {
        list.push(value);
      }
    },

    /** 수정 폼의 [저장] 활성 조건 — 원본 대비 변경이 있으면 true(직군은 순서 무관) */
    schedulerFormDirty(): boolean {
      const f = this.schedulerForm;
      const o = this.schedulerFormOriginal;
      return (
        f.name.trim() !== o.name.trim() ||
        f.code.trim() !== o.code.trim() ||
        f.location.trim() !== o.location.trim() ||
        f.description.trim() !== o.description.trim() ||
        String(f.displayOrder).trim() !== String(o.displayOrder).trim() ||
        f.isActive !== o.isActive ||
        [...f.jobCategories].sort().join(",") !== [...o.jobCategories].sort().join(",")
      );
    },

    /** 서버가 한글로 알려 주는 입력 오류(중복 이름 · 기본 스케줄러 · 인력이 남음)는 그대로, 그 밖은 공통 정책 팝업 */
    showSchedulerError(err: unknown) {
      const code = (err as { data?: { code?: string } } | null)?.data?.code;
      if (
        err instanceof Error &&
        (code === "BAD_REQUEST" || code === "CONFLICT" || code === "NOT_FOUND")
      ) {
        showError(err.message);
      } else {
        this.showCommonError();
      }
    },

    async submitSchedulerForm() {
      const f = this.schedulerForm;
      const name = f.name.trim();
      if (!name || this.schedulerSaving) {
        return;
      }
      const displayOrder = String(f.displayOrder).trim() === "" ? 0 : Number(f.displayOrder);
      if (!Number.isInteger(displayOrder) || displayOrder < 0) {
        showAlert("표시 순서는 0 이상의 정수로 입력해 주세요.");
        return;
      }
      const jobCategories = f.jobCategories as JobCategory[];
      this.schedulerSaving = true;
      try {
        if (this.schedulerFormMode === "edit") {
          await this.sdk.nurseScheduler.updateScheduleGroup({
            id: f.id,
            name,
            // 비우면 null 로 보내 지운다(서버 규약: null = 비우기)
            code: f.code.trim() || null,
            location: f.location.trim() || null,
            description: f.description.trim() || null,
            displayOrder,
            // 기본 스케줄러는 비활성으로 못 바꾼다 — 폼에서도 숨기고 값을 보내지 않는다
            ...(f.isDefault ? {} : { isActive: f.isActive }),
          });
          const before = [...this.schedulerFormOriginal.jobCategories].sort().join(",");
          if ([...jobCategories].sort().join(",") !== before) {
            await this.sdk.nurseScheduler.updateScheduleGroupSettings({
              scheduleGroupId: f.id,
              settings: { jobCategories },
            });
          }
        } else {
          await this.sdk.nurseScheduler.createScheduleGroup({
            wardId: f.wardId,
            name,
            code: f.code.trim() || undefined,
            location: f.location.trim() || undefined,
            description: f.description.trim() || undefined,
            displayOrder,
            isActive: f.isActive,
            ...(jobCategories.length > 0 ? { schedulingConfig: { jobCategories } } : {}),
          });
        }
        this.schedulerShowForm = false;
        await this.loadScheduleGroups();
      } catch (err) {
        this.showSchedulerError(err);
      } finally {
        this.schedulerSaving = false;
      }
    },

    /** 목록의 활성 스위치 — 실패하면 스위치를 원래 값으로 되돌린다 */
    async toggleSchedulerActive(group: ScheduleGroupItem, input: HTMLInputElement | null) {
      if (this.schedulerSaving || group.isDefault) {
        return;
      }
      const isActive = !group.isActive;
      this.schedulerSaving = true;
      try {
        await this.sdk.nurseScheduler.updateScheduleGroup({ id: group.id, isActive });
        await this.loadScheduleGroups();
      } catch (err) {
        if (input) {
          input.checked = group.isActive;
        }
        this.showSchedulerError(err);
      } finally {
        this.schedulerSaving = false;
      }
    },

    /** 삭제 — 기본 스케줄러이거나 인력 · 근무표가 남아 있으면 서버가 한글로 막는다(그 문구를 그대로 보여 준다) */
    async deleteScheduler(group: ScheduleGroupItem) {
      const ok = await askConfirm({
        message: `「${group.name}」 스케줄러를 삭제할까요?\n이 스케줄러의 인력과 근무표가 남아 있으면 삭제할 수 없어요.`,
        confirmLabel: "삭제",
        tone: "danger",
      });
      if (!ok) {
        return;
      }
      this.schedulerSaving = true;
      try {
        await this.sdk.nurseScheduler.deleteScheduleGroup(group.id);
        await this.loadScheduleGroups();
      } catch (err) {
        this.showSchedulerError(err);
      } finally {
        this.schedulerSaving = false;
      }
    },

    /* ── leave reason methods (synced with org.settings.leaveReasons) ── */
    _leaveReasonStorageKey(): string {
      const store = Alpine.store("hereby");
      const orgId = (store.currentUser?.organizationId as string) || "default";
      return `hereby_leave_reasons_${orgId}`;
    },

    async loadLeaveReasons() {
      try {
        // Read directly from org settings — the single source of truth.
        const org = await this.sdk.organization.getCurrent();
        const fromServer =
          ((org?.settings as Record<string, unknown> | undefined)?.leaveReasons as
            | typeof this.leaveReasons
            | undefined) || null;
        if (fromServer && fromServer.length > 0) {
          this.leaveReasons = fromServer;
          // Mirror to localStorage so the legacy fallback stays in sync.
          this._saveLeaveReasonsToStorage();
          // 근무표가 읽는 스토어 스냅샷도 서버 값으로 맞춘다(저장 실패 후 되돌린 경우 포함).
          // localStorage 폴백 경로에서는 하지 않는다 — 로컬에만 있는 값을 근무표로 흘리지 않는다.
          this._syncLeaveReasonsToStore();
          return;
        }
        // Fallback: read from localStorage (one-time migration to server happens
        // on the next save).
        const raw = localStorage.getItem(this._leaveReasonStorageKey());
        this.leaveReasons = raw ? JSON.parse(raw) : [];
      } catch (err) {
        // biome-ignore lint/suspicious/noConsole: surface the failure for debugging
        console.error("[admin] loadLeaveReasons failed:", err);
        // Network/permission failure — fall back to localStorage rather than
        // silently wiping the list.
        try {
          const raw = localStorage.getItem(this._leaveReasonStorageKey());
          this.leaveReasons = raw ? JSON.parse(raw) : [];
        } catch {
          this.leaveReasons = [];
        }
      }
    },

    _saveLeaveReasonsToStorage() {
      try {
        localStorage.setItem(this._leaveReasonStorageKey(), JSON.stringify(this.leaveReasons));
      } catch {
        /* storage may be full */
      }
    },

    async _saveLeaveReasonsToServer(): Promise<boolean> {
      try {
        await this.sdk.organization.updateSettings({ leaveReasons: this.leaveReasons });
        this._syncLeaveReasonsToStore();
        return true;
      } catch (err) {
        // biome-ignore lint/suspicious/noConsole: surface the failure for debugging
        console.error("[admin] Failed to persist leaveReasons to server:", err);
        return false;
      }
    },

    /**
     * 저장한 휴가 유형을 Alpine 스토어(currentUser.leaveReasons)에도 반영한다.
     *
     * 왜 필요한가 — 근무표(스케줄러)는 `scheduler.ts`의 `schedulerLeaveReasons()`로
     * 배정 목록을 만드는데, 그 함수가 읽는 곳이 org 설정 API가 아니라
     * `Alpine.store("hereby").currentUser.leaveReasons` 다. 이 값은 로그인/부팅 때
     * `auth.getCurrentUser()`로 한 번 채워지고 그 뒤로는 아무도 갱신하지 않는다.
     * 그래서 여기서 스토어를 같이 갱신하지 않으면, 「스케줄러 연동」을 켜고 저장해도
     * 재로그인(또는 새로고침) 전까지 근무표 우클릭 메뉴에 그 유형이 나타나지 않는다.
     *
     * 서버 반영에 성공한 뒤에만 부른다(_saveLeaveReasonsToServer) — 실패한 값을
     * 스토어에 남기면 화면만 반영된 것처럼 보인다.
     */
    _syncLeaveReasonsToStore() {
      const store = Alpine.store("hereby");
      if (store?.currentUser) {
        // 스토어는 반응형 프록시 — 이 대입만으로 근무표 우클릭 메뉴가 다시 그려진다.
        // 얕은 복사로 넘긴다: 같은 배열을 공유하면 저장 실패 시 되돌리기 전의
        // 낙관적 변경(unshift·슬롯 교체)이 스토어에 그대로 새어 들어간다.
        store.currentUser.leaveReasons = [...this.leaveReasons];
      }
    },

    /* ── 휴가 유형 표시/색상 헬퍼 ── */

    /** 상태 필터를 적용한 휴가 유형 목록 (전체/활성/비활성) */
    leaveReasonsFiltered(): typeof this.leaveReasons {
      const f = this.leaveReasonFilter.status;
      if (!f) {
        return this.leaveReasons;
      }
      return this.leaveReasons.filter((r) => (r.status || "active") === f);
    },

    /** 새 유형 생성 시 사용할 다음 색상 — 팔레트를 순차 사용 후 순환 */
    nextLeaveTypeColor(): string {
      const used = this.leaveReasons.map((r) => r.color).filter(Boolean) as string[];
      const free = this.leaveTypeColors.find((c) => !used.includes(c));
      return free || this.leaveTypeColors[used.length % this.leaveTypeColors.length];
    },

    /** 시간 설정 선택지 — 공통 규칙 30분 스텝(30분~8시간, 8시간=1일) */
    get leaveReasonDefaultMinuteOptions(): { value: number; label: string }[] {
      const out: { value: number; label: string }[] = [];
      for (let m = 30; m <= 480; m += 30) {
        const h = Math.floor(m / 60);
        const mm = m % 60;
        out.push({
          value: m,
          label: mm === 0 ? `${h}시간` : h > 0 ? `${h}시간 ${mm}분` : `${mm}분`,
        });
      }
      return out;
    },

    /** 저장값 → 시간 설정(분) 환산 — 레거시 유형(rate/hourly)도 새 폼으로 열린다.
     *  rate 유형은 배수×8시간, hourly 유형은 기본 사용 시간을 그대로 옮긴다(30분 스냅). */
    _leaveReasonUsageMinutes(r: (typeof this.leaveReasons)[number]): number {
      const snap = (m: number) => Math.min(480, Math.max(30, Math.round(m / 30) * 30));
      if (r.usageMinutes) {
        return snap(Number(r.usageMinutes));
      }
      if (r.deductionKind === "hourly") {
        return snap(Number(r.defaultMinutes ?? 240));
      }
      const rate = r.deductionRate ?? this._inferLeaveRate(r);
      const effective = Number.isFinite(Number(rate)) && Number(rate) > 0 ? Number(rate) : 1;
      return snap(effective * 480);
    },

    openAddLeaveReason() {
      this.editingLeaveReasonId = "";
      this.leaveReasonExtraOpen = false;
      this.leaveReasonPresetDraft = "";
      this.leaveReasonForm = {
        name: "",
        code: "",
        color: this.nextLeaveTypeColor(),
        description: "",
        annualDeduction: true,
        requestUnit: "day",
        usageMinutes: 480,
        allowOnOffDay: false,
        schedulerEnabled: true,
        countsAsAttendance: true,
        paidHours: true,
        breakChoiceAllowed: false,
        breakHoursPart: 0,
        breakMinutesPart: 30,
        specialLeave: false,
        privacyMasked: false,
        maskedName: "",
        evidenceRequired: false,
        status: "active",
        // 새 유형은 기본 리스트에서 시작 — 저장하면 명시 리스트로 굳는다
        reasonPresets: [...DEFAULT_LEAVE_REASON_PRESETS],
      };
      this.showLeaveReasonModal = true;
    },

    openEditLeaveReason(r: (typeof this.leaveReasons)[number]) {
      this.editingLeaveReasonId = r.id;
      // 추가 설정에 켜진 항목이 있으면 펼친 채로 연다 — 설정이 숨어 있으면 있는 줄 모른다
      this.leaveReasonExtraOpen = !!(r.specialLeave || r.privacyMasked || r.evidenceRequired);
      this.leaveReasonPresetDraft = "";
      const breakMin = Number(r.breakMinutes ?? 30);
      const legacyRate = r.deductionRate ?? this._inferLeaveRate(r);
      this.leaveReasonForm = {
        name: r.name,
        code: r.code ?? "",
        color: r.color || this.nextLeaveTypeColor(),
        description: r.description ?? "",
        // 연차 차감 Y/N — 레거시 '차감 없음'(rate 0)도 N으로 읽는다
        annualDeduction:
          r.deductsAnnualBalance !== false &&
          !(r.deductionKind !== "hourly" && r.usageMinutes == null && Number(legacyRate) === 0),
        // 저장된 차감 방식을 그대로 읽는다 — 예전엔 폼에 이 축이 없어 수정 저장만 해도
        // hourly 유형이 rate 로 덮어써져 시간 단위 신청 UI 가 조용히 사라졌다
        requestUnit: r.deductionKind === "hourly" ? "hourly" : "day",
        usageMinutes: this._leaveReasonUsageMinutes(r),
        allowOnOffDay: r.allowOnOffDay ?? false,
        // 기본 Y는 신규 유형에만 — 기존 유형은 저장값을 존중한다
        schedulerEnabled: r.schedulerEnabled ?? false,
        countsAsAttendance: r.countsAsAttendance !== false,
        // 레거시 무급(payKind=unpaid) 유형은 유급인정 시간 N으로 이관해 읽는다
        paidHours: r.paidHours !== false && r.payKind !== "unpaid",
        breakChoiceAllowed: r.breakChoiceAllowed ?? false,
        breakHoursPart: Math.floor(breakMin / 60),
        breakMinutesPart: breakMin % 60,
        specialLeave: r.specialLeave ?? false,
        privacyMasked: r.privacyMasked ?? false,
        maskedName: r.maskedName ?? "",
        evidenceRequired: r.evidenceRequired ?? false,
        status: r.status ?? "active",
        // undefined = 미관리 → 직원이 지금 보는 기본 칩을 프리필해 편집 시작값과 실화면을 일치시킨다.
        // 저장된 배열([] 포함)은 그대로 존중한다.
        reasonPresets: r.reasonPresets ? [...r.reasonPresets] : [...DEFAULT_LEAVE_REASON_PRESETS],
      };
      this.showLeaveReasonModal = true;
    },

    /** 추천 사유 추가 — 공백 정리·중복 차단·개수 상한(LEAVE_REASON_PRESET_MAX) */
    addLeaveReasonPreset() {
      const value = this.leaveReasonPresetDraft.trim();
      if (!value) {
        return;
      }
      if (this.leaveReasonForm.reasonPresets.includes(value)) {
        this.notify("이미 등록된 추천 사유예요.", "error");
        return;
      }
      if (this.leaveReasonForm.reasonPresets.length >= LEAVE_REASON_PRESET_MAX) {
        this.notify(`추천 사유는 최대 ${LEAVE_REASON_PRESET_MAX}개까지 등록할 수 있어요.`, "error");
        return;
      }
      this.leaveReasonForm.reasonPresets.push(value);
      this.leaveReasonPresetDraft = "";
    },

    removeLeaveReasonPreset(index: number) {
      this.leaveReasonForm.reasonPresets.splice(index, 1);
    },

    /* ══════ 연차 정책 플래그 ══════ */

    /** 서버 설정에서 정책 플래그를 읽어 폼에 채운다 (없는 키는 기본값 유지) */
    async loadLeavePolicy() {
      try {
        const org = (await this.sdk.organization.getCurrent()) as {
          settings?: Record<string, unknown>;
        };
        const saved = (org?.settings?.leavePolicy ?? {}) as Record<string, boolean | string>;
        for (const key of Object.keys(LEAVE_POLICY_DEFAULTS)) {
          this.leavePolicyForm[key] = saved[key] ?? LEAVE_POLICY_DEFAULTS[key];
        }
        // 초과 사용 처리(MED-968) — overuseMode 가 없는 옛 저장값은 enforceBalanceLimit · allowNegativeBalance 로 읽는다
        this.leavePolicyForm.overuseMode = resolveLeaveOveruseMode(saved);
        this.leavePolicyForm.enforceBalanceLimit = this.leavePolicyForm.overuseMode === "block";
        // 준법 강제: 5인 이상(기본)이면 서버(resolveLeavePolicy)가 공휴일 제외를 강제 ON한다.
        // 화면도 실제 동작값을 보여준다 — 저장값이 OFF여도 ON으로 표시(끌 수 없음).
        this.leavePolicyFiveOrMore = (org?.settings?.fiveOrMore as boolean | undefined) !== false;
        if (this.leavePolicyFiveOrMore) {
          this.leavePolicyForm.excludeWeekendsHolidays = true;
        }
        // 저장된 상태 스냅샷 — 저장 시 "차단을 새로 켰는가"를 이것과 비교해 판정한다
        this.leavePolicySaved = { ...this.leavePolicyForm };
        // 재직 직원 수 — "5인 미만 설정 + 실제 5인 이상" 경고용. 실패해도 정책 화면은 막지 않는다
        try {
          const employees = await this.sdk.employee.getEmployees({ page: 1, limit: 1 });
          this.leavePolicyActiveEmployeeCount = employees?.statusCounts?.ACTIVE || 0;
        } catch {
          this.leavePolicyActiveEmployeeCount = 0;
        }
      } catch (err) {
        // biome-ignore lint/suspicious/noConsole: surface the failure for debugging
        console.error("[admin] Failed to load leavePolicy:", err);
      }
    },

    /** 플랫폼 기본값과 다른 항목인가 — "기본값과 다름" 배지 표시용(저장 여부와 무관) */
    isLeavePolicyChanged(key: string): boolean {
      return this.leavePolicyForm[key] !== LEAVE_POLICY_DEFAULTS[key];
    },

    /** 상위 플래그가 꺼져 있어 이 선택이 아직 발효되지 않는가 */
    isLeavePolicyDormant(requires?: string): boolean {
      return !!requires && !this.leavePolicyForm[requires];
    },

    /** 법정 강제로 잠긴 선택인가 — 5인 이상 사업장의 공휴일 제외는 끌 수 없다(근기법 §55②) */
    isLeavePolicyLocked(key: string): boolean {
      return key === "excludeWeekendsHolidays" && this.leavePolicyFiveOrMore;
    },

    /** "5인 미만 사업장" 토글 — 켜면(fiveOrMore=false) 법정 강제 항목을 직접 선택할 수 있다.
     *  끄면(5인 이상 복귀) 공휴일 제외를 즉시 강제 ON으로 되돌린다(서버 resolveLeavePolicy와 동일). */
    toggleLeavePolicyUnderFive() {
      this.leavePolicyFiveOrMore = !this.leavePolicyFiveOrMore;
      if (this.leavePolicyFiveOrMore) {
        this.leavePolicyForm.excludeWeekendsHolidays = true;
      }
    },

    /** 경고 조건 — 5인 미만 설정인데 등록된 재직 직원이 5명 이상 */
    get leavePolicyUnderFiveMismatch(): boolean {
      return !this.leavePolicyFiveOrMore && this.leavePolicyActiveEmployeeCount >= 5;
    },

    /**
     * 유형별 차감·시간 단위 설정이 아직 발효되지 않은 상태인지.
     * 휴가 유형 폼에서 배수를 설정해도 이 플래그가 꺼져 있으면 반영되지 않으므로 안내한다.
     */
    get leaveTypeSettingsInactive(): boolean {
      return !this.leavePolicyForm.applyLeaveTypeDeduction;
    },

    /**
     * 저장 진입점 — 차단 검사를 새로 켰다면 먼저 영향을 확인하고 안내 팝업을 띄운다.
     *
     * 안내이지 차단이 아니다: 정책은 병원의 결정이고, 대기 신청 몇 건 때문에 정책 변경을
     * 막는 건 주객전도다. 다만 "저장 후에 승인 담당자가 이유 모르고 막히는" 상황은 피한다.
     * 미리보기가 실패해도 저장은 막지 않는다(부가 안내가 본 기능을 잠그면 안 된다).
     */
    /**
     * 저장 · 미리보기에 보내는 정책 — 초과 사용 처리(overuseMode)에서 예전 플래그 두 개를 파생한다
     * (서버 접수 가드와 옛 클라이언트가 enforceBalanceLimit · allowNegativeBalance 를 읽는다). 5인 이상이면 공휴일 제외는 늘 켬.
     */
    leavePolicyPayload() {
      const f = this.leavePolicyForm;
      const block = f.overuseMode === "block";
      return {
        ...f,
        enforceBalanceLimit: block,
        allowNegativeBalance: !block,
        excludeWeekendsHolidays: this.leavePolicyFiveOrMore ? true : f.excludeWeekendsHolidays,
      };
    },

    async saveLeavePolicy() {
      const blockNow = this.leavePolicyForm.overuseMode === "block";
      const blockWas = this.leavePolicySaved.overuseMode === "block";
      const willBlock =
        (this.leavePolicyForm.blockOverlappingRequests &&
          !this.leavePolicySaved.blockOverlappingRequests) ||
        (blockNow && !blockWas);
      if (willBlock) {
        this.leavePolicySaving = true;
        try {
          const res = await this.sdk.leaveRequest.previewPolicyImpact(
            this.leavePolicyPayload() as Parameters<
              typeof this.sdk.leaveRequest.previewPolicyImpact
            >[0],
          );
          if (res.blocked.length > 0) {
            this.leavePolicyImpact = res;
            this.showLeavePolicyImpact = true;
            this.leavePolicySaving = false;
            return;
          }
        } catch (err) {
          // biome-ignore lint/suspicious/noConsole: surface the failure for debugging
          console.error("[admin] Failed to preview leavePolicy impact:", err);
        }
        this.leavePolicySaving = false;
      }
      await this._persistLeavePolicy();
    },

    /** 캡처 기록 모달 열기 — 기본 기간은 최근 30일(MED-973) */
    async openScreenCaptureLog() {
      const today = Alpine.store("hereby").todayLocal();
      const from = new Date(`${today}T00:00:00`);
      from.setDate(from.getDate() - 30);
      this.screenCaptureLogFilter = {
        from: from.toISOString().slice(0, 10),
        to: today,
        screen: "",
        kind: "",
        search: "",
      };
      this.showScreenCaptureLog = true;
      await this.loadScreenCaptureLog(1);
    },

    async loadScreenCaptureLog(page: number) {
      this.screenCaptureLogLoading = true;
      try {
        const f = this.screenCaptureLogFilter;
        const res = await this.sdk.screenCapture.list({
          from: f.from || undefined,
          to: f.to || undefined,
          screen: (f.screen || undefined) as ScreenCaptureScreen | undefined,
          kind: (f.kind || undefined) as ScreenCaptureEventKind | undefined,
          search: f.search || undefined,
          page,
          limit: this.screenCaptureLog.limit,
        });
        this.screenCaptureLog = res;
      } catch (err) {
        showError(err instanceof Error ? err.message : "캡처 기록을 불러오지 못했어요.");
      } finally {
        this.screenCaptureLogLoading = false;
      }
    },

    screenCaptureScreenLabel(screen: string): string {
      return (
        (
          { payslip: "급여명세서", my_info: "내 정보", messenger: "메신저" } as Record<
            string,
            string
          >
        )[screen] ?? screen
      );
    },

    screenCaptureKindLabel(kind: string): string {
      return (
        (
          {
            screenshot: "스크린샷",
            recording_started: "녹화 시작",
            recording_stopped: "녹화 종료",
          } as Record<string, string>
        )[kind] ?? kind
      );
    },

    /** 안내 팝업에서 "이대로 저장"을 누른 경로 */
    async confirmLeavePolicyImpact() {
      this.showLeavePolicyImpact = false;
      this.tmChildClosedAt = Date.now();
      await this._persistLeavePolicy();
    },

    async _persistLeavePolicy() {
      this.leavePolicySaving = true;
      try {
        await this.sdk.organization.updateSettings({
          // 사업장 규모 — 5인 미만(false)이어야 공휴일 제외 OFF가 서버 정규화에 살아남는다
          fiveOrMore: this.leavePolicyFiveOrMore,
          leavePolicy: this.leavePolicyPayload(),
        });
        // 저장 성공 시에만 스냅샷을 갱신한다 — 다음 저장의 "새로 켬" 판정 기준이 된다
        this.leavePolicySaved = { ...this.leavePolicyForm };
        this.notify("연차 정책을 저장했어요.");
      } catch (err) {
        this.notify(
          `저장에 실패했어요: ${err instanceof Error ? err.message : String(err)}`,
          "error",
        );
      }
      this.leavePolicySaving = false;
    },

    /* ══════ 공휴일 ══════ */

    async loadHolidays() {
      this.holidayLoading = true;
      // 준법 상태(빈 달력 경고)는 목록과 병행 조회 — 실패해도 목록 표시는 막지 않는다
      this.sdk.organizationHoliday
        .complianceStatus(this.holidayYear)
        .then((s) => {
          this.holidayCompliance = s;
        })
        .catch(() => {
          this.holidayCompliance = null;
        });
      // 구분 라벨(krHolidayName)용 캐시 갱신 — 토글·등록 직후에도 최신을 반영하도록 무효화 후 적재
      invalidateHolidayYear(this.holidayYear);
      await ensureHolidayYears(this.sdk, [this.holidayYear]);
      try {
        const res = await this.sdk.organizationHoliday.list(this.holidayYear);
        // 같은 날짜에 병원 지정 휴일이 있으면 국가공휴일 행은 숨긴다 — '기본 공휴일 등록'으로
        // 복사한 날짜가 국가·병원 두 줄로 보이지 않게. 병원 행을 지우면 국가 행이 다시 나타난다
        // (차감은 국가공휴일 ∪ 병원휴일이라 삭제해도 국가 기준선은 유지됨을 화면이 그대로 보여준다).
        const orgDates = new Set(
          res.items.filter((h) => h.source === "organization").map((h) => h.date),
        );
        this.holidayItems = res.items.filter(
          (h) => h.source !== "national" || !orgDates.has(h.date),
        );
      } catch (err) {
        // biome-ignore lint/suspicious/noConsole: surface the failure for debugging
        console.error("[admin] Failed to load holidays:", err);
        this.holidayItems = [];
      }
      this.holidayLoading = false;
    },

    /**
     * 연도 드롭다운 선택지 — 올해 기준 앞뒤 5년.
     * 현재 선택값이 범위를 벗어나면(과거 데이터 조회 등) 그 값도 포함해 빈 select가 되지 않게 한다.
     */
    holidayYearOptions(): number[] {
      const now = new Date().getFullYear();
      const years = new Set<number>();
      for (let y = now - 5; y <= now + 5; y++) {
        years.add(y);
      }
      years.add(this.holidayYear);
      return [...years].sort((a, b) => a - b);
    },

    async changeHolidayYear(delta: number) {
      this.holidayYear += delta;
      await this.loadHolidays();
    },

    get holidayAddDisabled(): boolean {
      return this.holidaySaving || !(this.holidayForm.date && this.holidayForm.name.trim());
    },

    /** 기본 공휴일 등록 — 선택한 해의 국가공휴일을 일괄 등록 (기존 등록은 유지).
        레코드는 병원 휴일 테이블(organization_holidays)에 저장되지만,
        구분 라벨은 날짜 기준(krHolidayName)이라 화면에는 '국가'로 표시된다. */
    applyDefaultHolidays() {
      // 후보 = 이 조직에 적용 중인 국가 공휴일 (서버 목록 — 동기화분, opt-out 반영)
      const candidates = this.holidayItems
        .filter((h) => h.source === "national" && h.applied !== false)
        .map((h) => [h.date, h.name] as [string, string]);
      if (candidates.length === 0) {
        this.notify(`${this.holidayYear}년 국가공휴일 정보가 없어요.`, "info");
        return;
      }
      this.confirmTitle = "기본 공휴일 등록";
      this.confirmMessage = `${this.holidayYear}년 국가공휴일 ${candidates.length}건을 등록할까요? (이미 등록된 날짜는 유지돼요)`;
      this.confirmLabel = this._t("common.confirm");
      this.confirmAction = async () => {
        await this._applyDefaultHolidaysConfirmed(candidates);
      };
      // 유형·기준 관리 패널(z-50) 안에서 호출되므로 확인창을 그 위(z-60)에 띄운다.
      this.confirmTop = true;
      this.showConfirm = true;
    },

    async _applyDefaultHolidaysConfirmed(candidates: [string, string][]) {
      try {
        // 이미 병원 휴일이 있는 날짜는 건너뜀 — 기존 등록은 유지·덮어쓰지 않음.
        const existing = new Set(
          this.holidayItems.filter((h) => h.source === "organization").map((h) => h.date),
        );
        let added = 0;
        for (const [date, name] of candidates) {
          if (existing.has(date)) {
            continue;
          }
          try {
            await this.sdk.organizationHoliday.create({ date, name });
            added++;
          } catch {
            // 이미 등록된 날짜(CONFLICT)는 건너뜀
          }
        }
        await this.loadHolidays();
        this.notify(
          added > 0 ? `기본 공휴일 ${added}건을 등록했어요.` : "이미 모두 등록돼 있어요.",
        );
      } catch (err) {
        this.notify(
          `등록에 실패했어요: ${err instanceof Error ? err.message : String(err)}`,
          "error",
        );
      }
    },

    /** 휴일 추가 팝업 열기 — 다른 섹션(유형 추가)과 동일하게 입력은 중첩 폼 모달에서 받는다 */
    openAddHoliday() {
      this.holidayForm = { date: "", name: "" };
      this.showHolidayModal = true;
    },

    async addHoliday() {
      if (this.holidayAddDisabled) {
        return;
      }
      this.holidaySaving = true;
      try {
        await this.sdk.organizationHoliday.create({
          date: this.holidayForm.date,
          name: this.holidayForm.name.trim(),
        });
        this.holidayForm = { date: "", name: "" };
        // 성공 시에만 팝업을 닫는다 — 실패하면 입력값을 남겨 다시 시도할 수 있게.
        this.showHolidayModal = false;
        await this.loadHolidays();
        this.notify("휴일을 추가했어요.");
      } catch (err) {
        this.notify(
          `추가에 실패했어요: ${err instanceof Error ? err.message : String(err)}`,
          "error",
        );
      }
      this.holidaySaving = false;
    },

    /** 국가 공휴일의 조직별 적용/미적용 토글 — 노동절 등 공휴일을 일반 근무일로 쓰는 조직용 */
    async toggleHolidayApplied(h: { date: string; name: string; applied: boolean }) {
      try {
        if (!h.applied) {
          await this.sdk.organizationHoliday.setNationalHolidayApplied({
            date: h.date,
            applied: true,
          });
          await this.loadHolidays();
          this.notify(`'${h.name}'을(를) 다시 유급휴일로 적용했어요.`);
          return;
        }
        const ok = await askConfirm(
          `'${h.name}'(${Alpine.store("hereby").formatDate(h.date)})을(를) 이 병원에서 유급휴일로 적용하지 않을까요?\n미적용 공휴일은 일반 근무일이 돼요 — 연차 차감 제외·공휴일 신청 차단에서 빠져요.`,
        );
        if (!ok) {
          return;
        }
        const recurring = await askConfirm(
          `매년 ${h.date.slice(5)}을 반복해서 미적용할까요?\n확인: 매년 반복 (노동절 등 고정일) · 취소: ${Alpine.store("hereby").formatDate(h.date)} 하루만`,
        );
        await this.sdk.organizationHoliday.setNationalHolidayApplied({
          date: h.date,
          applied: false,
          recurring,
        });
        await this.loadHolidays();
        this.notify(`'${h.name}'을(를) 미적용으로 바꿨어요${recurring ? " (매년 반복)" : ""}.`);
      } catch (err) {
        this.notify(
          `변경에 실패했어요: ${err instanceof Error ? err.message : String(err)}`,
          "error",
        );
      }
    },

    deleteHoliday(id: string, name: string, date?: string) {
      // 국가공휴일 날짜의 행 삭제 = '기본 공휴일 등록' 해제. 자동 표시(읽기전용) 행으로 돌아갈 뿐
      // 차감 계산에는 계속 반영되므로, 병원 자체 휴일과 확인 문구를 가른다(거짓 안내 방지).
      const isNational = Boolean(date && this.krHolidayName(date));
      // 유형·기준 관리 패널(z-50) 안에서 호출되므로 확인창을 그 위(z-60)에 띄운다.
      this.confirmTop = true;
      this.confirmTitle = isNational ? "기본 공휴일 등록 해제" : "휴일 삭제";
      this.confirmMessage = isNational
        ? `'${name}' 등록을 해제할까요? 국가공휴일은 계속 목록에 표시되고 차감 계산에도 반영돼요.`
        : `'${name}'을(를) 삭제할까요? 앞으로의 연차 차감 계산에서 제외돼요. 이미 처리된 휴가에는 영향이 없어요.`;
      this.confirmLabel = this._t("common.delete");
      this.confirmAction = async () => {
        try {
          await this.sdk.organizationHoliday.delete(id);
          await this.loadHolidays();
          this.notify(isNational ? "기본 공휴일 등록을 해제했어요." : "휴일을 삭제했어요.");
        } catch (err) {
          this.notify(
            `삭제에 실패했어요: ${err instanceof Error ? err.message : String(err)}`,
            "error",
          );
        }
      };
      this.showConfirm = true;
    },

    /**
     * 목록의 코드 배지 표기 — 상한(leaveReasonCodeMax)을 넘으면 거기까지만 보여주고 말줄임한다.
     *
     * 신규 등록·재저장은 상한을 넘기면 막히지만(leaveReasonCodeError), 상한 도입 전에
     * 저장된 유형은 더 긴 코드를 그대로 갖고 있다 — 편집 없이 두면 그 값이 유지되므로
     * 목록은 계속 긴 코드를 만난다. 코드 배지는 근무표 셀처럼 좁은 자리에 들어가니
     * 저장값은 건드리지 않고 표시만 줄이고, 전체 값은 title 로 남겨 호버로 확인한다
     * (스케줄러 셀 메뉴의 (code || name).slice(0,3) 과 같은 상한).
     */
    leaveReasonCodeLabel(code?: string | null): string {
      const value = (code ?? "").trim();
      if (!value) {
        return "-";
      }
      return value.length > this.leaveReasonCodeMax
        ? `${value.slice(0, this.leaveReasonCodeMax)}…`
        : value;
    },

    /** 목록의 연차 차감 Y/N 판정 — 레거시 '차감 없음'(rate 0)도 N으로 읽는다 */
    leaveReasonDeductsAnnual(r: (typeof this.leaveReasons)[number]): boolean {
      if (r.deductsAnnualBalance === false) {
        return false;
      }
      if (r.usageMinutes != null) {
        return true;
      }
      // 레거시 rate 유형: 배수 0 = 차감 없음
      return !(
        r.deductionKind !== "hourly" && Number(r.deductionRate ?? this._inferLeaveRate(r)) === 0
      );
    },

    /** 레거시 필드(deductionDays·hours)에서 차감 배수를 유추 — 없으면 종일(1) */
    _inferLeaveRate(r: { deductionDays?: number; hours?: number }): number {
      if (r.deductionDays != null) {
        return r.deductionDays;
      }
      if (r.hours != null && r.hours > 0) {
        const std = Number(this.orgSettingsForm?.dailyStandard) || 8;
        return Math.round((r.hours / std) * 100) / 100;
      }
      return 1;
    },

    async saveLeaveReason() {
      const name = this.leaveReasonForm.name?.trim();
      const code = this.leaveReasonForm.code?.trim();
      if (!name) {
        this.notify("이름을 입력해 주세요.", "error");
        return;
      }
      // 저장 버튼도 막고 있지만(leaveReasonSaveDisabled) 여기서 한 번 더 —
      // 상한 도입 전 긴 코드가 편집 경로로 되살아나는 것과 중복 코드를 차단한다.
      if ((code?.length ?? 0) > this.leaveReasonCodeMax) {
        this.notify(`코드는 최대 ${this.leaveReasonCodeMax}글자예요.`, "error");
        return;
      }
      const dupOwner = code ? this.leaveReasonDuplicateCodeOwner(code) : null;
      if (dupOwner) {
        this.notify(`'${dupOwner}' 유형이 이미 이 코드를 쓰고 있어요.`, "error");
        return;
      }
      // 활성 → 비활성 전환 시 확인 팝업 (연차 관리 기획 휴가 유형 수정)
      if (this.editingLeaveReasonId && this.leaveReasonForm.status === "inactive") {
        const existing = this.leaveReasons.find((r) => r.id === this.editingLeaveReasonId);
        if (existing && (existing.status || "active") === "active") {
          this.confirmTitle = "휴가 유형 비활성화";
          this.confirmMessage =
            "이 휴가 유형을 비활성화할까요? 비활성화하면 Works에서 새 휴가 신청 시 선택할 수 없어요. 이미 신청·승인된 휴가는 그대로 유지돼요.";
          this.confirmLabel = this._t("common.confirm");
          this.confirmAction = async () => {
            await this._commitLeaveReason();
          };
          // 휴가 유형 폼(z=60) 위에서 뜨는 확인창 — 폼보다 위 레이어(.ds-modal-backdrop--top)로 올린다.
          this.confirmTop = true;
          this.showConfirm = true;
          return;
        }
      }
      await this._commitLeaveReason();
    },

    async _commitLeaveReason(): Promise<boolean> {
      const f = this.leaveReasonForm;
      const usageMinutes = Number(f.usageMinutes) || 480;
      const breakMinutes = (Number(f.breakHoursPart) || 0) * 60 + (Number(f.breakMinutesPart) || 0);
      // 레거시 hours/deductionDays는 저장하지 않는다.
      // 새 축(연차 차감 Y/N + 신청 단위 + 시간 설정)이 정본이고, 기존 차감 엔진(leave-deduction.calc)을
      // 위해 deductionRate(= 시간/8h, 차감 N이면 0)를 함께 파생해 둔다.
      // 신청 단위가 시간이면 deductionKind=hourly — works 가 사용 시간 select 를 띄우고 신청 분(requestedMinutes)이
      // 그대로 차감량이 된다. deductionRate 는 시간 없는 레거시 신청의 폴백으로 hourly 에도 남긴다.
      const hourly = f.requestUnit === "hourly";
      const payload = {
        name: f.name.trim(),
        code: f.code?.trim() || "",
        color: f.color || this.nextLeaveTypeColor(),
        description: f.description?.trim() || "",
        deductsAnnualBalance: !!f.annualDeduction,
        usageMinutes,
        deductionKind: (hourly ? "hourly" : "rate") as "rate" | "hourly",
        deductionRate: f.annualDeduction ? Math.min(1, usageMinutes / 480) : 0,
        // Works 신청 폼 초기값 — 시간 설정을 그대로 쓴다(시간 단위 유형에서 직원이 변경 가능)
        defaultMinutes: usageMinutes,
        // 유형별 최소 단위·사용 시간 범위는 두지 않는다(2026-07 결정 — 공통 규칙 30분 스텝).
        // undefined로 명시해 기존 저장값도 수정 저장 시 함께 지워지게 한다.
        minUnitMinutes: undefined,
        minHours: undefined,
        maxHours: undefined,
        // 유급 축은 UI에서 제거 — 유급인정 시간(paidHours)이 대체한다. 저장값도 지운다
        payKind: undefined,
        // 근무유형·팀 제한 UI는 제거(개인 할당 방식으로 대체 예정) — 저장 시 전체 허용으로 정규화
        allowedShiftIds: [] as string[],
        allowedTeamIds: [] as string[],
        allowOnOffDay: !!f.allowOnOffDay,
        schedulerEnabled: !!f.schedulerEnabled,
        countsAsAttendance: !!f.countsAsAttendance,
        paidHours: !!f.paidHours,
        breakChoiceAllowed: !!f.breakChoiceAllowed,
        breakMinutes,
        specialLeave: !!f.specialLeave,
        privacyMasked: !!f.privacyMasked,
        maskedName: f.privacyMasked ? f.maskedName?.trim() || "" : undefined,
        evidenceRequired: !!f.evidenceRequired,
        status: f.status,
        // 추천 사유 — 빈 값 정리 후 저장. 비어 있으면 []로 저장돼 칩이 꺼진다
        // (undefined = 미관리 폴백과 구분되는 값이다 — DEFAULT_LEAVE_REASON_PRESETS 주석 참조)
        reasonPresets: (f.reasonPresets ?? []).map((p) => p.trim()).filter(Boolean),
      };
      const wasEditingReason = !!this.editingLeaveReasonId;
      if (wasEditingReason) {
        const idx = this.leaveReasons.findIndex((r) => r.id === this.editingLeaveReasonId);
        if (idx >= 0) {
          this.leaveReasons[idx] = { ...this.leaveReasons[idx], ...payload };
        }
      } else {
        // 추가한 행은 목록 상단으로
        this.leaveReasons.unshift({
          id: `lr_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          ...payload,
        });
      }
      this._saveLeaveReasonsToStorage();
      const ok = await this._saveLeaveReasonsToServer();
      if (!ok) {
        // 서버 반영 실패 — 낙관적으로 반영한 로컬 변경을 서버 상태로 되돌리고 안내한다.
        await this.loadLeaveReasons();
        this.notify("휴가 유형 저장에 실패했어요. 잠시 후 다시 시도해 주세요.", "error");
        return false;
      }
      this.showLeaveReasonModal = false;
      this.notify(wasEditingReason ? "휴가 유형을 수정했어요." : "휴가 유형을 추가했어요.");
      return true;
    },

    /** 휴가 유형 삭제 — 상태별 분기 (연차 관리 기획 휴가 유형 §3).
     *  활성: 삭제 불가. 비활성+사용 내역: 내역 유형이 '삭제'로 표시됨 경고. 비활성+미사용: 일반 삭제. */
    /**
     * 휴가 유형을 비활성으로 바꾼다 — 삭제 확인창에서 바로 부르는 경로.
     * 낙관적으로 바꾸고 서버 반영이 실패하면 서버 상태로 되돌린다(삭제와 같은 패턴).
     */
    async _deactivateLeaveReason(id: string) {
      const idx = this.leaveReasons.findIndex((r) => r.id === id);
      if (idx < 0) {
        return;
      }
      const name = this.leaveReasons[idx].name;
      this.leaveReasons[idx] = { ...this.leaveReasons[idx], status: "inactive" };
      this._saveLeaveReasonsToStorage();
      const ok = await this._saveLeaveReasonsToServer();
      if (!ok) {
        await this.loadLeaveReasons();
        this.notify("휴가 유형 상태 변경에 실패했어요. 잠시 후 다시 시도해 주세요.", "error");
        return;
      }
      this.notify(`'${name}' 휴가 유형을 비활성으로 바꿨어요. 이제 삭제할 수 있어요.`);
    },

    async deleteLeaveReason(id: string) {
      const reason = this.leaveReasons.find((r) => r.id === id);
      if (!reason) {
        return;
      }
      // 유형·기준 관리 패널(z-50) 안에서 호출되므로 확인창을 그 위(z-60)에 띄운다.
      this.confirmTop = true;
      if ((reason.status || "active") === "active") {
        // 「먼저 비활성으로 바꾸세요」만 알려 주고 끝내면, 사용자는 수정 팝업 → 상태
        // 드롭다운 → 저장 → 비활성화 확인까지 직접 찾아가야 한다. 목록의 유형이 전부
        // 활성이면 어느 것도 지울 수 없어 "삭제가 안 된다"는 신고가 됐다
        // (CS-OKBEST-260917-0003). 「활성은 삭제 불가」 규칙(연차 관리 기획 휴가 유형
        // §3)은 그대로 두고, **그 자리에서** 비활성으로 바꿀 수 있게만 한다.
        // 비활성으로 바꾼 뒤 삭제를 한 번 더 누르게 해 두 단계는 유지한다 — 이 창에서
        // 곧바로 지워 버리면 "비활성으로 변경"을 누른 사람이 삭제까지 당한다.
        this.confirmTitle = "삭제";
        this.confirmMessage =
          `'${reason.name}' 휴가 유형은 활성 상태라 바로 삭제할 수 없어요.\n` +
          "지금 비활성으로 바꿀까요? 비활성으로 바꾸면 Works에서 새 휴가 신청 시 선택할 수 없고, 이미 신청·승인된 휴가는 그대로 유지돼요.\n" +
          "바꾼 뒤 삭제를 다시 누르면 삭제할 수 있어요.";
        this.confirmLabel = "비활성으로 변경";
        this.confirmHideCancel = false;
        this.confirmAction = async () => {
          await this._deactivateLeaveReason(id);
        };
        this.showConfirm = true;
        return;
      }
      // 사용 내역 존재 여부 — 이 유형으로 신청된 휴가가 있는지(상태 무관)
      let usedCount = 0;
      try {
        const res = await this.sdk.leaveRequest.list(
          // biome-ignore lint/suspicious/noExplicitAny: filter accepts org-configured leaveType id
          { leaveType: id } as any,
          { page: 1, limit: 1 },
        );
        usedCount = Number((res as { totalCount?: number })?.totalCount || 0);
      } catch {
        usedCount = 0;
      }
      this.confirmTitle = "삭제";
      this.confirmMessage =
        usedCount > 0
          ? "이 유형을 사용한 휴가 내역이 있어요. 삭제하면 해당 휴가 내역의 유형은 '삭제'로 표시돼요."
          : `${reason.name} 휴가 유형을 삭제할까요?`;
      this.confirmLabel = this._t("common.confirm");
      this.confirmAction = async () => {
        this.leaveReasons = this.leaveReasons.filter((r) => r.id !== id);
        this._saveLeaveReasonsToStorage();
        const ok = await this._saveLeaveReasonsToServer();
        if (!ok) {
          // 서버 반영 실패 — 낙관적으로 지운 항목을 서버 상태로 되돌리고 안내한다.
          await this.loadLeaveReasons();
          this.notify("휴가 유형 삭제에 실패했어요. 잠시 후 다시 시도해 주세요.", "error");
          return;
        }
        this.notify("휴가 유형을 삭제했어요.");
      };
      this.showConfirm = true;
    },

    async toggleWardStatus(wardId: string) {
      const ward = this.wardList.find((w: Record<string, unknown>) => w.id === wardId);
      if (!ward) {
        return;
      }
      try {
        await this.sdk.nurseScheduler.updateWard({
          id: wardId,
          isActive: !ward.isActive,
        });
        await this.loadWards();
      } catch (_err) {
        this.showCommonError();
      }
    },

    doLogout() {
      this.confirmTitle = this._t("auth.logout_confirm_title");
      this.confirmMessage = this._t("auth.logout_confirm_desc");
      this.confirmLabel = this._t("common.logout");
      this.confirmAction = async () => {
        await Alpine.store("hereby").logout();
        Alpine.store("router").navigate("/signin");
      };
      this.showConfirm = true;
    },

    statusLabel(s: string): string {
      const keyMap: Record<string, string> = {
        ACTIVE: "admin.status_active",
        INACTIVE: "admin.status_inactive",
        ON_LEAVE: "admin.status_on_leave",
        TERMINATED: "admin.status_terminated",
      };
      return keyMap[s] ? this._t(keyMap[s]) : s;
    },

    /** DS 라벨(ds-label) 색 모디파이어만 반환 — 조직/팀·직원 관리 탭 상태 배지를
     *  구형 .badge 대신 표준 ds-label 로 통일하기 위한 매핑. 옛 .badge 팔레트와
     *  동일 색(재직→green, 휴직→yellow, 퇴직→red, 그 외→gray)을 유지한다. */
    statusLabelClass(s: string): string {
      const map: Record<string, string> = {
        ACTIVE: "ds-label--green",
        INACTIVE: "ds-label--light-gray",
        ON_LEAVE: "ds-label--yellow",
        TERMINATED: "ds-label--red",
      };
      return map[s] || "ds-label--gray";
    },

    roleTypeLabel(t: string): string {
      const keyMap: Record<string, string> = {
        system: "admin.role_type_system",
        team: "admin.role_type_team",
        custom: "admin.role_type_custom",
      };
      return keyMap[t] ? this._t(keyMap[t]) : t;
    },

    roleTypeBadgeClass(t: string): string {
      const map: Record<string, string> = {
        system: "badge badge-system",
        team: "badge badge-team",
        custom: "badge badge-custom",
      };
      return map[t] || "badge";
    },

    empName(id: string): string {
      const emp = this.employees.find((e: Record<string, unknown>) => e.id === id);
      return ((emp as Record<string, unknown>)?.fullName as string) || "-";
    },

    /** 직원 이름 — 직원 관리 목록(현재 페이지)에 없으면 조직 전체 명단(allMembers)에서 찾는다.
     *  못 찾거나 id 가 비면 빈 문자열(empName 은 「-」를 돌려줘서 「팀장 -」로 보이던 문제). */
    orgEmployeeName(id: string): string {
      if (!id) {
        return "";
      }
      const emp = (this.employees as Record<string, unknown>[]).find((e) => e.id === id);
      if (emp?.fullName) {
        return emp.fullName as string;
      }
      const member = (this.allMembers as Record<string, unknown>[]).find(
        (m) => m.employeeId === id,
      );
      return (member?.employeeName as string) || "";
    },

    /* ──────────── e-sign (Hereby integration) ──────────── */
    resetESignFilter() {
      this.esignFilter = { search: "", filterStatus: "" };
      this.resetPage("esign");
      this.loadESignRecords();
    },

    async loadESignIntegrationStatus() {
      try {
        const result = await this.sdk.eSign.integrationStatus();
        this.esignIntegrationStatus = result;
        // 연동 상태가 정해진 뒤에만 「연동이 필요합니다」를 보인다(초깃값 unlinked 라 답 전에 잘못 떴다)
        this.loadedOnce.esignStatus = true;
        if (result.status === "linked") {
          if (this.esignTemplates.length === 0) {
            await this.loadESignTemplates();
          }
          await this.loadESignRecords();
          // 페이지 로드·탭 진입·헤더 [새로고침]은 모두 이 경로를 지난다. DB 만 다시 읽으면
          // 웹훅을 놓친 레코드의 상태가 관리 열의 개별 [새로고침]을 누르기 전까지 옛 값으로 남으므로,
          // 화면에 보이는 미완료 레코드를 Hereby 와 한 번 맞춘 뒤 바뀐 게 있으면 목록을 다시 그린다.
          await this.syncVisibleESignStatuses();
        } else {
          this.esignRecords = [];
          this.esignTemplates = [];
        }
      } catch (err) {
        // biome-ignore lint/suspicious/noConsole: browser-side failure diagnostics
        console.error("Failed to load e-sign integration status", err);
      } finally {
        this.loadedOnce.esignStatus = true;
      }
    },

    provisionESignIntegration() {
      if (this.esignProvisioning || this.showConfirm) {
        return;
      }
      const ownerEmail = this.esignIntegrationStatus.ownerEmailMasked || "등록된 조직 Owner";
      this.confirmTitle = "Hereby Sign 연결";
      this.confirmMessage =
        `조직 Owner(${ownerEmail})의 이름, 이메일 및 기업 정보를 Hereby Sign에 전달합니다. 해당 정보로 Hereby Sign 기업 대표 계정을 새로 만들거나 기존 계정과 연결합니다.\n\n` +
        "연결이 완료되면 전자서명 기능 사용에 필요한 인증 정보와 계약 상태 수신 설정이 자동으로 구성됩니다.\n\n" +
        "확인 후 연결하면 조직을 대표하여 이 연결을 진행할 권한이 있으며, Owner 정보 제공에 필요한 동의를 받았음을 확인합니다.";
      this.confirmLabel = "확인 후 연결";
      this.confirmHideCancel = false;
      this.confirmAction = async () => {
        await this.executeESignProvisioning();
      };
      this.showConfirm = true;
    },

    async executeESignProvisioning() {
      if (this.esignProvisioning) {
        return;
      }
      this.esignProvisioning = true;
      this.esignIntegrationStatus = {
        ...this.esignIntegrationStatus,
        status: "provisioning",
        errorMessage: null,
      };
      try {
        await this.sdk.eSign.provisionIntegration();
        await this.loadESignIntegrationStatus();
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        this.esignIntegrationStatus = {
          ...this.esignIntegrationStatus,
          status: "error",
          errorMessage: message,
        };
        showError(`전자서명 연동 실패: ${message}`);
      } finally {
        this.esignProvisioning = false;
      }
    },

    esignHealthDotClass(status: unknown): string {
      if (status === "healthy") {
        return "bg-status-success";
      }
      if (status === "error") {
        return "bg-status-error";
      }
      return "bg-status-warning";
    },

    /** 전자서명 상태 칩의 DS 라벨 색 변형(.ds-label--*). 골격·크기·모양은 마크업이 고정
     *  (`ds-label ds-label--sm ds-label--square`)하고 여기선 색 축만 고른다.
     *  색 정책: 오류/실패/비활성만 red — 나머지 진행 단계(완료·예약·대기)는 상태의 좋고 나쁨을
     *  단정하지 않으므로 전부 light-gray로 중립 표기한다(원색 칩 남용 방지). */
    esignBadgeClass(r: Record<string, unknown>): string {
      const filterStatus = String(r.herebyFilterStatus || "");
      const displayStatus = String(r.displayStatus || "");
      if (filterStatus === "inactive" || filterStatus === "failed" || displayStatus === "error") {
        return "ds-label--red";
      }
      return "ds-label--light-gray";
    },

    esignStatusText(r: Record<string, unknown>): string {
      if (String(r.herebyFilterStatus || "") === "reserved") {
        return "예약 전송";
      }
      return String(
        r.herebyStatusText ||
          this.esignLocalStatusLabel[String(r.displayStatus || "")] ||
          r.displayStatus ||
          "-",
      );
    },

    esignSentAtText(r: Record<string, unknown>): string {
      const sentAt = r.herebySentAt || r.sentAt;
      if (!sentAt) {
        return "-";
      }
      return Alpine.store("hereby").formatDateTime(sentAt as string | Date);
    },

    // Combobox helpers — case-insensitive substring filter on template name.
    filteredESignTemplates(): Record<string, unknown>[] {
      const q = this.esignTemplateQuery.trim().toLowerCase();
      if (!q) {
        return this.esignTemplates;
      }
      return this.esignTemplates.filter((t: Record<string, unknown>) => {
        const name = String((t as { name?: unknown }).name ?? "").toLowerCase();
        return name.includes(q);
      });
    },

    selectESignTemplate(t: Record<string, unknown> | undefined) {
      if (!t) {
        return;
      }
      this.esignForm.herebyTemplateId = Number((t as { templateId?: number }).templateId ?? 0);
      this.esignTemplateQuery = String((t as { name?: string }).name ?? "");
      this.esignTemplateOpen = false;
      this.esignTemplateHighlight = 0;
    },

    moveESignTemplateHighlight(delta: number) {
      const list = this.filteredESignTemplates();
      if (list.length === 0) {
        this.esignTemplateHighlight = 0;
        return;
      }
      this.esignTemplateOpen = true;
      const next = (this.esignTemplateHighlight + delta + list.length) % list.length;
      this.esignTemplateHighlight = next;
    },

    onESignTemplateInput() {
      // Typing invalidates any prior selection until a row is picked again.
      this.esignForm.herebyTemplateId = 0;
      this.esignTemplateOpen = true;
      this.esignTemplateHighlight = 0;
    },

    // Employee picker — server-driven combobox.
    async searchESignEmployees() {
      this.esignEmployeeLoading = true;
      try {
        const filter: Record<string, unknown> = { page: 1, limit: 20 };
        const q = this.esignEmployeeQuery.trim();
        if (q) {
          filter.search = q;
        }
        const result = await this.sdk.employee.getEmployees(filter);
        this.esignEmployeeOptions = ((result?.items || []) as Record<string, unknown>[]).map(
          (e: Record<string, unknown>) => ({
            ...e,
            fullName: `${e.lastName || ""}${e.firstName || ""}`.trim() || e.email,
          }),
        );
        this.esignEmployeeHighlight = 0;
      } catch (err) {
        // biome-ignore lint/suspicious/noConsole: browser-side failure diagnostics
        console.error("Failed to search employees for e-sign", err);
        this.esignEmployeeOptions = [];
      } finally {
        this.esignEmployeeLoading = false;
      }
    },

    selectESignEmployee(e: Record<string, unknown> | undefined) {
      if (!e) {
        return;
      }
      this.esignForm.employeeId = String((e as { id?: string }).id ?? "");
      this.esignSelectedEmployee = e;
      const label = String((e as { fullName?: string }).fullName ?? "");
      const empNo = String((e as { employeeNumber?: string }).employeeNumber ?? "");
      this.esignEmployeeQuery = empNo ? `${label} (${empNo})` : label;
      this.esignEmployeeOpen = false;
      this.esignEmployeeHighlight = 0;
    },

    moveESignEmployeeHighlight(delta: number) {
      const list = this.esignEmployeeOptions;
      if (list.length === 0) {
        this.esignEmployeeHighlight = 0;
        return;
      }
      this.esignEmployeeOpen = true;
      const next = (this.esignEmployeeHighlight + delta + list.length) % list.length;
      this.esignEmployeeHighlight = next;
    },

    onESignEmployeeInput() {
      // Typing invalidates the current selection until the user picks a row.
      this.esignForm.employeeId = "";
      this.esignSelectedEmployee = null;
      this.esignEmployeeOpen = true;
      this.searchESignEmployees();
    },

    async onESignEmployeeFocus() {
      this.esignEmployeeOpen = true;
      // Lazy-load initial 20 on first focus so the dropdown has something
      // to show before any keystroke.
      if (this.esignEmployeeOptions.length === 0 && !this.esignEmployeeLoading) {
        await this.searchESignEmployees();
      }
    },

    /** 계약서 템플릿 목록 모달 열기. 아직 못 불러온 상태(연동 직후·조회 실패)면 한 번 더 시도한다. */
    async openESignTemplatesModal() {
      this.showESignTemplatesModal = true;
      if (this.esignTemplates.length === 0) {
        await this.refreshESignTemplates();
      }
    },

    /** Hereby Sign 에서 템플릿을 새로 만든 뒤 눌러 목록을 다시 읽는다(세션당 1회 로드가 기본이라 필요). */
    async refreshESignTemplates() {
      if (this.esignTemplatesLoading) {
        return;
      }
      this.esignTemplatesLoading = true;
      try {
        await this.loadESignTemplates();
      } finally {
        this.esignTemplatesLoading = false;
      }
    },

    /** [계약서 추가하기] — Hereby Sign 템플릿 관리 화면을 새 창으로 연다. 주소가 없으면 버튼 자체가 숨겨진다. */
    openESignTemplateManage() {
      const url = this.esignIntegrationStatus.templateManageUrl;
      if (!url) {
        return;
      }
      window.open(url, "_blank", "noopener");
    },

    /** 템플릿의 직원 입력 항목 수 — 목록 모달의 칩 표기용. */
    esignTemplateFieldCount(t: Record<string, unknown>): number {
      const fields = (t as { fields?: unknown }).fields;
      return Array.isArray(fields) ? fields.length : 0;
    },

    /** 서명 요청 폼 초기화 — 모달을 열 때마다 이전 선택이 남지 않게 한다. */
    resetESignSendForm() {
      this.esignForm = { employeeId: "", herebyTemplateId: 0 };
      this.esignSelectedEmployee = null;
      this.esignTemplateQuery = "";
      this.esignTemplateOpen = false;
      this.esignTemplateHighlight = 0;
      this.esignEmployeeQuery = "";
      this.esignEmployeeOpen = false;
      this.esignEmployeeHighlight = 0;
    },

    /**
     * 서명 요청 보내기 모달 열기. 폼을 먼저 비운 뒤 여는 순서가 중요하다 — closeGuard 의
     * $watch 가 열리는 순간의 폼을 baseline 으로 찍으므로, 빈 폼이 기준이 되어야 "입력 없이
     * 닫기 = 바로 닫힘 / 골라 놓고 닫기 = 저장확인" 이 맞게 동작한다.
     */
    openESignSendModal() {
      this.resetESignSendForm();
      this.showESignSendModal = true;
    },

    /**
     * 피커 입력창의 Esc — 드롭다운이 열려 있으면 그것만 닫고 이벤트를 멈춘다(모달의
     * @keydown.escape.window 까지 올라가 모달이 같이 닫히는 것을 막음). 닫혀 있으면 그대로
     * 흘려보내 모달 닫기 동작을 따른다.
     */
    closeESignPicker(kind: "employee" | "template", event?: KeyboardEvent) {
      if (kind === "employee") {
        if (this.esignEmployeeOpen) {
          event?.stopPropagation();
          this.esignEmployeeOpen = false;
        }
        return;
      }
      if (this.esignTemplateOpen) {
        event?.stopPropagation();
        this.esignTemplateOpen = false;
      }
    },

    /** 직원·템플릿을 모두 골랐고, 직원에게 수신 이메일이 있을 때만 보낼 수 있다. */
    canSendESign(): boolean {
      if (this.esignSending) {
        return false;
      }
      if (!(this.esignForm.employeeId && this.esignForm.herebyTemplateId)) {
        return false;
      }
      const email = this.esignSelectedEmployee?.email;
      return typeof email === "string" && email.trim().length > 0;
    },

    startESignPolling() {
      if (this.esignPollingTimer) {
        return;
      }
      this.esignPollingTimer = setInterval(() => {
        this.pollESignRecords().catch(() => {
          // Polling errors are reflected by the list and health indicators.
        });
      }, ESIGN_POLLING_INTERVAL_MS);
    },

    stopESignPolling() {
      if (!this.esignPollingTimer) {
        return;
      }
      clearInterval(this.esignPollingTimer);
      this.esignPollingTimer = null;
      this.esignPollingInFlight = false;
    },

    async pollESignRecords() {
      if (
        shouldSkipESignPolling({
          activeTab: this.activeTab,
          documentHidden: document.hidden,
          esignEmployeeOpen: this.esignEmployeeOpen,
          esignOpening: this.esignOpening,
          esignPollingInFlight: this.esignPollingInFlight,
          esignSending: this.esignSending,
          esignSendModalOpen: this.showESignSendModal,
          esignTemplateOpen: this.esignTemplateOpen,
        })
      ) {
        return;
      }
      this.esignPollingInFlight = true;
      try {
        if (this.esignIntegrationStatus.status !== "linked") {
          await this.loadESignIntegrationStatus();
        } else {
          await this.loadESignRecords();
        }
      } finally {
        this.esignPollingInFlight = false;
      }
    },

    async loadESignTemplates() {
      try {
        // FIXME(타입 복구 중 발견): listHerebyTemplates 응답이 HerebyTemplate[]로 추론되지만 UI 상태는 Record<string, unknown>[]를 기대함. 응답 DTO 정렬 후 any 제거.
        // biome-ignore lint/suspicious/noExplicitAny: SDK response type mismatch during getter migration
        const result: any = await this.sdk.eSign.listHerebyTemplates();
        this.esignTemplates = Array.isArray(result) ? result : [];
      } catch (err) {
        // biome-ignore lint/suspicious/noConsole: browser-side failure diagnostics
        console.error("Failed to load Hereby templates", err);
        this.esignTemplates = [];
      }
    },

    async loadESignRecords() {
      // Reset per-record loading flags so stale "열기" disables don't
      // carry across reloads (guards against an opening promise that
      // errored before its finally branch ran).
      this.esignOpening = {};
      try {
        const p = this.paging.esign;
        // biome-ignore lint/suspicious/noExplicitAny: SDK dynamic types
        const input: any = {
          // 공통 paginationSchema 의 sortOrder 기본값은 asc 라 명시하지 않으면 오래된 건이 먼저 온다.
          // 최근 발송 건이 위에 오도록 생성일 내림차순을 고정한다.
          pagination: { page: p.page, limit: p.limit, sortBy: "createdAt", sortOrder: "desc" },
        };
        if (this.esignFilter.search) {
          input.search = this.esignFilter.search;
        }
        if (this.esignFilter.filterStatus) {
          input.filterStatus = this.esignFilter.filterStatus;
        }
        const result = await this.sdk.eSign.list(input);
        this.esignRecords = (result?.items || []) as Record<string, unknown>[];
        this.loadedOnce.esign = true;
        p.totalCount = result?.totalCount ?? 0;
        p.totalPages = result?.totalPages ?? 0;
        await this.loadESignIntegrationHealth();
      } catch (err) {
        // biome-ignore lint/suspicious/noConsole: browser-side failure diagnostics
        console.error("Failed to load e-sign records", err);
        this.esignRecords = [];
        this.paging.esign.totalCount = 0;
        this.paging.esign.totalPages = 0;
      } finally {
        this.loadedOnce.esign = true;
      }
    },

    /**
     * 화면에 보이는 미완료 레코드의 상태를 Hereby 와 일괄 동기화하고, 바뀐 건이 있으면 목록을 다시 읽는다.
     * 30초 폴링에서는 호출하지 않는다 — 새로고침(페이지 로드·탭 진입·헤더 버튼)에서만 Hereby 를 조회한다.
     * 실패는 조용히 넘긴다: 목록은 이미 그려졌고 연동 상태 표시등이 장애를 알린다.
     */
    async syncVisibleESignStatuses() {
      const ids = esignSyncCandidateIds(this.esignRecords);
      if (ids.length === 0) {
        return;
      }
      try {
        const result = await this.sdk.eSign.syncStatuses({ ids });
        if ((result?.updated ?? 0) > 0) {
          await this.loadESignRecords();
        }
      } catch (err) {
        // biome-ignore lint/suspicious/noConsole: browser-side failure diagnostics
        console.error("Failed to sync e-sign statuses", err);
      }
    },

    async loadESignIntegrationHealth() {
      try {
        const result = await this.sdk.eSign.integrationHealth();
        this.esignIntegrationHealth = result;
      } catch (err) {
        // biome-ignore lint/suspicious/noConsole: browser-side failure diagnostics
        console.error("Failed to load e-sign integration health", err);
      }
    },

    async sendESign() {
      if (!this.canSendESign()) {
        return;
      }
      this.esignSending = true;
      try {
        const result = await this.sdk.eSign.send({
          employeeId: this.esignForm.employeeId,
          herebyTemplateId: this.esignForm.herebyTemplateId,
        });
        // 성공: 모달을 닫고 폼을 비운다. 폼을 먼저 비워 closeGuard 가 "변경 있음"으로 오판하지 않게 한다.
        this.resetESignSendForm();
        this.showESignSendModal = false;
        this.notify("서명 요청을 보냈어요. 새 창에서 계약서 내용을 채워 발송해 주세요.");
        await this.loadESignRecords();
        // Open redirect URL in a new tab so admin can fill in the contract
        if (result?.herebyRedirectUrl) {
          window.open(result.herebyRedirectUrl, "_blank", "noopener");
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        showError(`전송 실패: ${message}`);
      } finally {
        this.esignSending = false;
      }
    },

    async syncESignStatus(id: string) {
      try {
        await this.sdk.eSign.syncStatus({ id });
        await this.loadESignRecords();
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        showError(`상태 확인 실패: ${message}`);
      }
    },

    // Phase 3.1: mint a fresh login-token redirect URL per click, then open.
    async openESign(id: string) {
      if (this.esignOpening[id]) {
        return;
      }
      this.esignOpening[id] = true;
      try {
        const result = await this.sdk.eSign.openContract(id);
        if (result?.herebyRedirectUrl) {
          window.open(result.herebyRedirectUrl, "_blank", "noopener");
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        showError(`열기 실패: ${message}`);
      } finally {
        this.esignOpening[id] = false;
      }
    },

    formatDate(d: string | Date | null): string {
      if (!d) {
        return "-";
      }
      return Alpine.store("hereby").formatDate(d);
    },

    /** 입사일 등 날짜 전용 표기 `YYYY.MM.DD.` (연차 관리 기획 컬럼 정의) */
    formatDateDot(d: string | Date | null): string {
      if (!d) {
        return "-";
      }
      // date 컬럼(startDate 등)은 "YYYY-MM-DD" 문자열로 오지만, timestamp(createdAt 등)는
      // superjson으로 Date 객체가 온다 → 문자열 정규식이 빗나가므로 조직 타임존 ISO로 먼저 정규화.
      let head = String(d).slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}/.test(head)) {
        head = Alpine.store("hereby").formatDateISO(d);
      }
      const m = head.match(/^(\d{4})-(\d{2})-(\d{2})/);
      return m ? `${m[1]}.${m[2]}.${m[3]}.` : this.formatDate(d);
    },

    leaveYearOptions(): number[] {
      const current = new Date().getFullYear();
      const years: number[] = [];
      for (let y = current; y >= current - 4; y--) {
        years.push(y);
      }
      return years;
    },

    leaveStats(): { totalEmployees: number; avgUsed: number; avgRemaining: number } {
      const items = this.leaveBalances;
      if (items.length === 0) {
        return { totalEmployees: 0, avgUsed: 0, avgRemaining: 0 };
      }
      let totalUsed = 0;
      let totalRemaining = 0;
      for (const b of items) {
        totalUsed += (b.used as number) || 0;
        totalRemaining += (b.remaining as number) || 0;
      }
      return {
        totalEmployees: items.length,
        avgUsed: Math.round((totalUsed / items.length) * 10) / 10,
        avgRemaining: Math.round((totalRemaining / items.length) * 10) / 10,
      };
    },

    permissionsByResource(): Record<string, Record<string, unknown>[]> {
      const grouped: Record<string, Record<string, unknown>[]> = {};
      for (const p of this.permissions) {
        const resource = (p.resource as string) || this._t("dept_type.other");
        if (!grouped[resource]) {
          grouped[resource] = [];
        }
        grouped[resource].push(p);
      }
      return grouped;
    },
  };
}
